<template>
    <div class="flow-main-container">
        <v-container fluid class="flow-container-fluid">
            <v-card class="card2">
                <v-btn class="floating-back-btn" @click="goBack" variant="elevated" color="white">
                    <v-icon color="primary" size="20">mdi-arrow-left</v-icon>
                    <span class="back-text">{{ $t('back') }}</span>
                </v-btn>
                    <!-- Undo/Redo buttons -->
                    <div class="undo-redo-container mr-2" v-if="!isViewMode && !isDebugMode">
                        <v-tooltip location="bottom">
                            <template v-slot:activator="{ props }">
                                <v-btn v-bind="props"
                                       @click="undo" 
                                       :disabled="!canUndo" 
                                       variant="outlined" 
                                       size="small" 
                                       color="primary"
                                       class="undo-btn mr-1">
                                    <v-icon size="16">mdi-undo</v-icon>
                                </v-btn>
                            </template>
                            <span>
                                Undo {{ historyIndex >= 0 ? `(${history[historyIndex]?.action || 'Unknown'})` : '(None)' }}
                                <br><small><kbd>Ctrl</kbd> + <kbd>Z</kbd></small>
                            </span>
                        </v-tooltip>
                        <v-tooltip location="bottom">
                            <template v-slot:activator="{ props }">
                                <v-btn v-bind="props"
                                       @click="redo" 
                                       :disabled="!canRedo" 
                                       variant="outlined" 
                                       size="small" 
                                       color="primary"
                                       class="redo-btn">
                                    <v-icon size="16">mdi-redo</v-icon>
                                </v-btn>
                            </template>
                            <span>
                                Redo {{ historyIndex + 1 < history.length ? `(${history[historyIndex + 1]?.action || 'Unknown'})` : '(None)' }}
                                <br><small><kbd>Ctrl</kbd> + <kbd>Y</kbd></small>
                            </span>
                        </v-tooltip>
                    </div>
                <!-- Flow Toolbar -->
                <div class="flow-toolbar mt-8" :class="{ 'flow-toolbar-debug': isDebugMode }">
                    <div class="toolbar-left">
                        <div class="flow-info">
                            <v-text-field v-model="flowName" :label="$t('flowName')" variant="outlined" density="compact"
                                hide-details="auto" class="modern-input flow-name-input" :readonly="isViewMode || isDebugMode"
                                @focus="handleFlowNameFocus" />
                            <div v-if="!isViewMode" class="flow-actions">
                                <!-- Additional Actions -->
                                <v-menu v-if="!isDebugMode" offset-y>
                                    <template v-slot:activator="{ props }">
                                        <v-btn v-bind="props" variant="text" icon size="small" class="more-actions-btn">
                                            <v-icon>mdi-dots-vertical</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-list density="compact" class="menu-list">
                                        <v-list-item @click="exportFlow" :disabled="!tempNode.length">
                                            <template v-slot:prepend>
                                                <v-icon>mdi-export</v-icon>
                                            </template>
                                            <v-list-item-title>{{ $t('exportFlow') }}</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="importFlow">
                                            <template v-slot:prepend>
                                                <v-icon>mdi-import</v-icon>
                                            </template>
                                            <v-list-item-title>{{ $t('importFlow') }}</v-list-item-title>
                                        </v-list-item>
                                        <div class="menu-divider"></div>
                                        <v-list-item @click="handleDuplicateSelectedNodes" 
                                                   :disabled="!selectedNodes.length" 
                                                   class="text-primary">
                                            <template v-slot:prepend>
                                                <v-icon color="primary">mdi-content-duplicate</v-icon>
                                            </template>
                                            <v-list-item-title>Duplicate Selected ({{ selectedNodes.length }})</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="handleCopySelectedNodes" 
                                                   :disabled="!selectedNodes.length" 
                                                   class="text-info">
                                            <template v-slot:prepend>
                                                <v-icon color="info">mdi-content-copy</v-icon>
                                            </template>
                                            <v-list-item-title>Copy Selected ({{ selectedNodes.length }})</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="handlePasteNodes" 
                                                   :disabled="!copiedNodes.length" 
                                                   class="text-info">
                                            <template v-slot:prepend>
                                                <v-icon color="info">mdi-content-paste</v-icon>
                                            </template>
                                            <v-list-item-title>Paste ({{ copiedNodes.length }})</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="handleDeleteSelectedNodes" 
                                                   :disabled="!selectedNodes.length" 
                                                   class="text-error">
                                            <template v-slot:prepend>
                                                <v-icon color="error">mdi-delete</v-icon>
                                            </template>
                                            <v-list-item-title>Delete Selected ({{ selectedNodes.length }})</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="clearFlow" :disabled="!tempNode.length" class="text-error">
                                            <template v-slot:prepend>
                                                <v-icon color="error">mdi-delete-sweep</v-icon>
                                            </template>
                                            <v-list-item-title>{{ $t('clearFlow') }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                                <!-- Save Button -->
                                <div v-if="!isDebugMode">
                                    <v-btn @click="openSaveDialog" :disabled="!tempNode.length" color="success"
                                        class="save-btn" size="small">
                                        <v-icon size="16">mdi-content-save</v-icon>
                                        <span class="btn-text">{{ $t('save') }}</span>
                                    </v-btn>
                                </div>
                                <!-- Debug Mode Toggle -->
                                <div class="ml-2">
                                    <v-btn @click="toggleDebugMode" :disabled="!tempNode.length || isViewMode"
                                        :color="isDebugMode ? 'warning' : 'default'"
                                        :variant="isDebugMode ? 'flat' : 'outlined'"
                                        class="debug-toggle-btn" size="small" icon>
                                        <v-icon size="18">{{ isDebugMode ? 'mdi-bug-check' : 'mdi-bug-play-outline' }}</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                            <div v-else class="ml-4">
                                <v-btn variant="text" size="small" class="view-mode-btn" readonly>
                                    <v-icon size="16">mdi-eye</v-icon>
                                    <span class="btn-text">{{ $t('viewMode') }}</span>
                                </v-btn>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Vue Flow Container -->
                <div class="flow-container" :class="{ 'debug-readonly-flow': isDebugMode }" @drop="onDrop" @dragover.prevent @dragenter.prevent @keydown="handleKeyDown" @click="focusFlowContainer" tabindex="-1" ref="flowContainer">
                    <VueFlow ref="vueFlow" :nodes="tempNode" v-model:edges="tempEdge" :default-viewport="{ zoom: 1.2 }"
                        :snap-to-grid="false" :min-zoom="0.2" :max-zoom="2" class="basic-flow" :pan-on-drag="[2]"
                        :selection-key-code="true" :multi-selection-key-code="true" :only-render-visible-elements="true" 
                        :nodes-draggable="!isDebugMode && !isViewMode" :nodes-connectable="!isDebugMode && !isViewMode"
                        :elements-selectable="!isDebugMode" :connect-on-click="!isDebugMode && !isViewMode"
                        @nodes-change="onNodesChange" @edges-change="onEdgesChange" @node-selection-change="onSelectionChange" 
                        @connect="onConnect" @edge-mouse-enter="onEdgeMouseEnter" @edge-mouse-leave="onEdgeMouseLeave"
                        @node-click="onNodeClick" @node-drag-start="onNodeDragStart" @node-drag-stop="onNodeDragStop"
                        @pane-click="onPaneClick"
                        :fitViewOnInit="true">

                        <template #node-flow-node="props">
                            <FlowNode :id="props.id" :data="props.data" :selected="props.selected" :key="props.id"
                                @delete-node="handleDeleteNode" @update:data="handleNodeDataUpdate"
                                @remove-outgoing-edge="handleRemoveOutgoingEdge"
                                @open-test="handleOpenNodeTest" />
                        </template>

                        <template #edge-flow-edge="buttonEdgeProps">
                            <FlowEdge v-bind="buttonEdgeProps" :id="buttonEdgeProps.id"
                                :source-x="buttonEdgeProps.sourceX" :source-y="buttonEdgeProps.sourceY"
                                :target-x="buttonEdgeProps.targetX" :target-y="buttonEdgeProps.targetY"
                                :source-position="buttonEdgeProps.sourcePosition"
                                :target-position="buttonEdgeProps.targetPosition"
                                :marker-end="buttonEdgeProps.markerEnd" :style="buttonEdgeProps.style" />
                        </template>

                        <Background patternColor="rgba(0, 0, 0, 0.4)" :gap="16" />
                        <Controls :show-interactive="false" v-if="!lockStatus" />
                        
                        <!-- Selection Info -->
                        <div v-if="selectedNodes.length > 0 && !isViewMode" class="selection-info">
                            <v-chip color="#0080ff7a" variant="elevated" size="small" class="ml-2">
                                <v-icon size="16" class="mr-1">mdi-cursor-default-click</v-icon>
                                {{ selectedNodes.length }} node{{ selectedNodes.length > 1 ? 's' : '' }} selected
                            </v-chip>
                            <v-chip color="primary" variant="text" size="small">
                                <v-icon size="16" class="mr-1">mdi-content-duplicate</v-icon>
                                Ctrl+D duplicate
                            </v-chip>
                            <v-chip color="error" variant="text" size="small">
                                <v-icon size="16" class="mr-1">mdi-delete</v-icon>
                                Del to remove
                            </v-chip>
                        </div>
                    </VueFlow>


                </div>
            </v-card>
        </v-container>

        <!-- Debug Mode Components -->
        <DebugInspector
            :visible="isDebugMode"
            :steps="debugSteps"
            :currentNodeId="debugCurrentNodeId"
            :currentVariables="debugVariables"
            :changedVariables="debugChangedVariables"
            :status="debugStatus"
            @focus-node="focusOnNode"
            @patch-variable="onDebugPatchVariable"
            @set-next="onDebugSetNext"
        />
        <DebugControlBar
            :visible="isDebugMode"
            :status="debugStatus"
            :pausedNodeName="debugPausedNodeName"
            :breakpointCount="debugBreakpoints.size"
            :stepCount="debugStepCount"
            :totalDuration="debugTotalDuration"
            :errorMessage="debugError"
            :message="debugMessage"
            :lastReply="debugStatus === 'completed' ? debugLastBotReply : ''"
            :setNextTargetName="getDebugSetNextTargetName()"
            :keepMemory="debugKeepMemory"
            :debugUserId="debugUserId"
            @send="startDebugExecution"
            @resume="resumeDebugExecution"
            @stop="stopDebugExecution"
            @update:message="val => debugMessage = val"
            @cancel-set-next="onDebugCancelSetNext"
            @update:keep-memory="onDebugKeepMemoryChange"
            @clear-all-breakpoints="clearAllBreakpoints"
        />

        <!-- Floating Node Panel -->
        <NodePanel v-if="!isViewMode && !isDebugMode" @add-node="addNewNode" class="node-panel" />

        <v-dialog v-model="flowToolEditorDialog" fullscreen scrollable persistent :retain-focus="false" :no-click-animation="true" scroll-strategy="none" @after-enter="onFlowToolDialogAfterEnter">
            <div class="flow-tool-editor-shell">
                <div class="internal-flow-toolbar">
                    <v-btn icon size="small" variant="text" color="white" @click="closeFlowToolEditor">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <div class="internal-flow-toolbar-title">
                        <v-icon size="20" class="me-2">mdi-sitemap</v-icon>
                        <span>Editing: {{ editingFlowToolNode?.data?.flowToolName || 'New Flow Tool' }}</span>
                    </div>
                    <v-chip size="small" :color="validateInternalFlow(internalFlowNodes, internalFlowEdges) ? 'success' : 'warning'" variant="flat" class="internal-flow-status-chip">
                        <v-icon size="14" class="me-1">{{ validateInternalFlow(internalFlowNodes, internalFlowEdges) ? 'mdi-check' : 'mdi-alert' }}</v-icon>
                        {{ validateInternalFlow(internalFlowNodes, internalFlowEdges) ? 'Ready' : 'Incomplete' }}
                    </v-chip>
                    <v-btn size="small" color="white" variant="flat" prepend-icon="mdi-content-save" @click="saveFlowToolInternalFlow" :disabled="isViewMode" class="internal-flow-save-btn">
                        Save & Exit
                    </v-btn>
                    <v-btn size="small" color="white" variant="outlined" @click="closeFlowToolEditor">
                        Cancel
                    </v-btn>
                </div>

                <NodePanel v-if="!isViewMode" @add-node="addInternalFlowNodeFromPanel" class="internal-flow-node-panel" :initial-collapsed="false" :excluded-node-types="internalFlowExcludedTypes" />

                <div class="internal-flow-canvas-wrap" @drop="onInternalFlowDrop" @dragover.prevent @dragenter.prevent>
                    <div class="internal-flow-info-banner">
                        <v-icon size="22" color="info">mdi-information</v-icon>
                        <span><strong>{{ $t('internalFlowColon') }}</strong> Drag nodes from panel ->{{ $t('connect') }}<strong>{{ $t('flowInput') }}</strong> -> your logic -> <strong>{{ $t('flowOutput') }}</strong></span>
                    </div>
                    <VueFlow ref="internalVueFlow" v-model:nodes="internalFlowNodes" v-model:edges="internalFlowEdges" :default-viewport="{ zoom: 1 }" :snap-to-grid="false" :min-zoom="0.2" :max-zoom="2" class="basic-flow internal-flow-canvas"
                        :nodes-draggable="!isViewMode" :nodes-connectable="!isViewMode" :elements-selectable="!isViewMode" :connect-on-click="!isViewMode" :delete-key-code="null"
                        @connect="onInternalFlowConnect" @edge-mouse-enter="onInternalEdgeMouseEnter" @edge-mouse-leave="onInternalEdgeMouseLeave"
                        @node-click="onInternalNodeClick" @pane-click="onInternalPaneClick">
                        <template #node-flow-node="props">
                            <FlowNode :id="props.id" :data="props.data" :selected="props.selected" :key="props.id" :flow-edges-override="internalFlowEdges"
                                @delete-node="handleDeleteInternalNode"
                                @update:data="handleInternalNodeDataUpdate"
                                @remove-outgoing-edge="handleInternalRemoveOutgoingEdge" />
                        </template>
                        <template #edge-flow-edge="buttonEdgeProps">
                            <FlowEdge v-bind="buttonEdgeProps" :id="buttonEdgeProps.id" :source-x="buttonEdgeProps.sourceX" :source-y="buttonEdgeProps.sourceY" :target-x="buttonEdgeProps.targetX" :target-y="buttonEdgeProps.targetY" :source-position="buttonEdgeProps.sourcePosition" :target-position="buttonEdgeProps.targetPosition" :marker-end="buttonEdgeProps.markerEnd" :style="buttonEdgeProps.style" />
                        </template>
                        <Background patternColor="rgba(79, 70, 229, 0.35)" :gap="16" />
                        <Controls :show-interactive="false" />
                    </VueFlow>
                </div>
            </div>
        </v-dialog>
        <!-- Floating Node Inspector Panel -->
        <!-- Shows for main flow when dialog is closed, or for internal flow node when dialog is open. -->
        <v-defaults-provider :defaults="inspectorOverlayDefaults">
        <div v-if="selectedNode && (isEditingInternalNode || (!flowToolEditorDialog && selectedNodes.length == 1))" class="floating-inspector"
            :class="{ 'inspector-expanded': inspectorExpanded, 'floating-inspector--internal': isEditingInternalNode }">
            <!-- Inspector Header -->
            <div class="inspector-header">
                <div class="inspector-title">
                    <v-icon class="mr-2" color="white">mdi-cog</v-icon>
                    <span class="text-h6">{{ $t('nodeProperties') }}</span>
                </div>
                <div class="inspector-controls">
                    <v-btn icon size="small" @click="inspectorExpanded = !inspectorExpanded" class="mr-1">
                        <v-icon color="white">{{ inspectorExpanded ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
                    </v-btn>
                    <v-btn icon size="small" @click="closeInspector">
                        <v-icon color="white">mdi-close</v-icon>
                    </v-btn>
                </div>
            </div>
            <!-- Inspector Content -->
            <div class="inspector-content">
                <v-form ref="inspectorForm">
                    
                    <!-- invisible dummy fields to trap browser autofill (Edge/Chrome) -->
                    <div style="position:absolute; left:-9999px; opacity:0; height:0; overflow:hidden;">
                        <input id="prevent-autofill-username-flow" type="text" name="prevent_autofill_username" autocomplete="username" tabindex="-1" aria-label="Prevent autofill username" aria-hidden="true" />
                        <input id="prevent-autofill-password-flow" type="password" name="prevent_autofill_password" autocomplete="current-password" tabindex="-1" aria-label="Prevent autofill password" aria-hidden="true" />
                    </div>

                    <!-- Node Type Badge -->
                    <div class="node-type-badge mb-4">
                        <v-chip :color="getNodeTypeColor(selectedNode.data.type)" variant="elevated" size="large"
                            class="text-subtitle-1 font-weight-bold header-chip">
                            <v-icon class="mr-2" size="20">{{ getNodeTypeIcon(selectedNode.data.type) }}</v-icon>
                            {{ getNodeTypeLabel(selectedNode.data.type) }}
                        </v-chip>
                    </div>

                    <!-- Node Type Specific Properties -->
                    <div v-if="selectedNode.data.type === 'messageInput'" class="form-section">
                        <h6 class="section-title">{{ $t('chatAiModelConfiguration') }}</h6>
                        <v-autocomplete v-model="nodeInspector.selectedModel" :items="uniqueAiModelOptions"
                            item-title="searchableText" item-value="value" :label="$t('flowAiModel')" variant="outlined"
                            density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('selectedModel', $event)" class="modern-input mb-3"
                            :clearable="!isViewMode" :readonly="isViewMode" chips multiple
                            :custom-filter="(value, query, item) => {
                                if (!query) return true;
                                const searchQuery = query.toLowerCase();
                                const itemValue = item?.raw?.value?.toString().toLowerCase() || '';
                                const itemName = item?.raw?.name?.toLowerCase() || '';
                                return itemValue.includes(searchQuery) || itemName.includes(searchQuery);
                            }">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props"
                                    :class="['model-option', { 'model-option-selected': Array.isArray(nodeInspector.selectedModel) && nodeInspector.selectedModel.includes(item.raw.value) }]"
                                    :title="item.raw.value + ': ' + item.raw.name">
                                    <template v-slot:prepend>
                                        <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                            <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                        </div>
                                    </template>
                                </v-list-item>
                            </template>
                            <template v-slot:chip="{ props, item }">
                                <v-chip v-bind="props" :text="item.raw.value + ': ' + item.raw.name"
                                    class="model-chip-hover model-chip-selected"></v-chip>
                            </template>
                        </v-autocomplete>
                    </div>

                    <div v-if="selectedNode.data.type === 'model'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-robot" size="24" color="primary" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold text-primary mb-0">{{ $t('aiModelConfiguration') }}</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">Configure your AI model settings and
                                parameters</p>
                        </div>

                        <!-- Action Buttons -->
                        <div class="action-buttons-container mb-4">
                            <!-- Load from Model Button (First Row) -->
                            <div class="load-model-button-wrapper mb-3">
                                <v-btn @click="openLoadFromModelDialog" color="success" variant="tonal" size="small"
                                    prepend-icon="mdi-download" :disabled="isViewMode" class="load-model-btn">
                                    Load from Model
                                </v-btn>
                            </div>

                            <!-- Use Base Model Prompt Toggle (Second Row) -->
                            <div class="base-prompt-toggle-section">
                                <v-card variant="outlined" class="pa-3" color="green-lighten-5" style="border-radius: 12px;">
                                    <div class="d-flex align-center">
                                        <v-avatar size="24"
                                            color="green"
                                            class="me-3">
                                            <v-icon
                                                color="white"
                                                size="12">mdi-text-box-check</v-icon>
                                        </v-avatar>
                                        <div class="flex-grow-1 me-3">
                                            <div class="text-subtitle-2"><span style="color:#4CAF50;font-weight: 500;">{{ $t('useBaseModelPrompt') }}</span></div>
                                            <div class="text-caption text-medium-emphasis">{{ $t('_combineWithDefaultModelPrompt') }}</div>
                                        </div>
                                        <v-switch v-model="nodeInspector.useBaseModelPrompt" color="green"
                                            hide-details density="compact"
                                            @update:model-value="updateNodeProperty('useBaseModelPrompt', $event)"
                                            :readonly="isViewMode" inset />
                                    </div>
                                </v-card>
                            </div>
                        </div>

                        <!-- Model Configuration Panels -->
                        <v-expansion-panels v-model="modelConfigPanels" multiple class="config-panels elevation-0"
                            variant="accordion">
                            <!-- Basic Information Panel -->
                            <v-expansion-panel value="basic" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4">
                                            <v-icon icon="mdi-information" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('basicInfo') }}</div>
                                            <div class="panel-subtitle">{{ $t('_modelAndServiceDetails') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <!-- Model -->
                                        <div class="config-section mb-6">
                                            <h6 class="section-label mb-3">{{ $t('serviceConfiguration') }}</h6>
                                            <v-row>
                                                 <div v-if="selectedNodeModelInfo" class="model-provider-details mt-2">
                                                        <div class="model-provider-detail">
                                                            <v-icon icon="mdi-server-network" size="16" color="primary" class="me-2"></v-icon>
                                                            <span class="model-provider-label">{{ $t('platform') }}</span>
                                                            <span class="model-provider-value">{{ selectedNodeModelInfo.platform || '-' }}</span>
                                                        </div>
                                                        <div class="model-provider-detail">
                                                            <v-icon icon="mdi-cloud-outline" size="16" color="primary" class="me-2"></v-icon>
                                                            <span class="model-provider-label">{{ $t('Provider') }}</span>
                                                            <span class="model-provider-value">{{ selectedNodeModelInfo.provider || '-' }}</span>
                                                        </div>
                                                    </div>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.model" :items="modelSelectionOptions"
                                                        item-title="modelDisplay" item-value="modelName" :label="$t('modelRequired')"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-brain"
                                                        @update:model-value="updateNodeProperty('model', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :hint="$t('_hintSelectModelVersion')" persistent-hint required clearable>
                                                        <template v-slot:item="{ props, item }">
                                                            <v-list-item v-bind="props" :title="getModelOptionTitle(item.raw)">
                                                                <template v-slot:subtitle>
                                                                    <div class="model-option-subtitle">
                                                                        <span class="model-option-meta">
                                                                            <span class="model-option-meta-value">{{ item.raw.platform || '-' }}</span>
                                                                        </span>
                                                                        <span class="model-option-meta-separator">/</span>
                                                                        <span class="model-option-meta">
                                                                            <span class="model-option-meta-value">{{ item.raw.provider || '-' }}</span>
                                                                        </span>
                                                                    </div>
                                                                </template>
                                                            </v-list-item>
                                                        </template>
                                                        <template v-slot:selection="{ item }">
                                                            <span class="model-selection-title">{{ getModelOptionTitle(item.raw) }}</span>
                                                        </template>
                                                    </v-autocomplete>
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <!-- Behavior Settings -->
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">{{ $t('behaviorSettings') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.gender"
                                                        :items="[{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }]"
                                                        item-title="name" item-value="id" :label="$t('gender')"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-account"
                                                        @update:model-value="updateNodeProperty('gender', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :hint="$t('_hintGenderPreference')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.typingSpeed"
                                                        :label="$t('typingSpeed')" type="number" min="0" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-keyboard-outline"
                                                        @update:model-value="updateNodeProperty('typingSpeed', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        :hint="$t('_hintCharsPerSecond')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.maxInputLength"
                                                        :label="$t('maxInputLength')" type="number" min="0" max="200000"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-outline"
                                                        @update:model-value="updateNodeProperty('maxInputLength', $event)"
                                                        @keypress="maxNumberpositive($event)"
                                                        @focusin="validateAndCorrectValue($event, 1, 200000)"
                                                        @focusout="validateAndCorrectValue($event, 1, 200000)"
                                                        @keydown.enter="validateAndCorrectValue($event, 1, 200000)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :hint="$t('_hintMaxCharsAllowed')"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Response Options Panel -->
                            <v-expansion-panel value="response" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                            <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('responseOptions') }}</div>
                                            <div class="panel-subtitle">{{ $t('_configureHowResponsesGenerated') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <!-- Stream Response -->
                                        <div class="switch-option-compact mb-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.streamResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.streamResponse ? 'primary' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.streamResponse ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-text-box-outline</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">{{ $t('streamResponse') }}</div>
                                                        <div class="switch-description">Progressive text generation
                                                        </div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.streamResponse" color="primary"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('streamResponse', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>

                                        <!-- Time Context -->
                                        <div class="switch-option-compact">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.enableTime }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.enableTime ? 'success' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.enableTime ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-clock-outline</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">{{ $t('timeContext') }}</div>
                                                        <div class="switch-description">{{ $t('enableTimeAwareness') }}</div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.enableTime" color="success"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('enableTime', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>

                                            <!-- Time Zone Dropdown (shown when enableTime is true) -->
                                            <div v-if="nodeInspector.enableTime" class="mt-3">
                                                <v-select v-model="nodeInspector.timeZone" :items="DDTimeZone"
                                                    item-title="name" item-value="value" :label="$t('timeZone')"
                                                    variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-earth"
                                                    @update:model-value="updateNodeProperty('timeZone', $event)"
                                                    class="config-input" :readonly="isViewMode"
                                                    :hint="$t('_selectYourTimezone')" persistent-hint
                                                    :placeholder="$t('_selectYourTimezone')">
                                                    <template v-slot:selection="{ item }">
                                                        <div class="d-flex align-center">
                                                            <v-icon small color="primary"
                                                                class="me-2">mdi-map-marker</v-icon>
                                                            <span class="font-weight-medium">{{ item.raw.name }}</span>
                                                            <v-chip size="x-small" color="grey-darken-1"
                                                                text-color="white" class="ml-2">
                                                                {{ item.raw.value.split('/')[1].replace('_', ' ') }}
                                                            </v-chip>
                                                        </div>
                                                    </template>
                                                </v-select>
                                            </div>
                                        </div>

                                        <!-- Json Response -->
                                        <div class="switch-option-compact mt-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.jsonResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.jsonResponse ? 'primary' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.jsonResponse ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-code-json</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">{{ $t('jsonResponse') }}</div>
                                                        <div class="switch-description">Return structured JSON format
                                                        </div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.jsonResponse" color="primary"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('jsonResponse', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Memory Settings Panel -->
                            <v-expansion-panel value="memory" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #00BCD4, #0097A7);">
                                            <v-icon icon="mdi-brain" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('memorySettings') }}</div>
                                            <div class="panel-subtitle">{{ $t('_configureConvMemoryContext') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('conversationMemory') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.memoryTurn"
                                                        :label="$t('turnCount')" type="number" min="0" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-history"
                                                        @update:model-value="updateNodeProperty('memoryTurn', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        :hint="$t('_hintTurnsRemember')"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.memoryDuration"
                                                        :label="$t('durationMinutes')" type="number" min="0"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-outline"
                                                        @update:model-value="updateNodeProperty('memoryDuration', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :hint="$t('_hintMemoryActive')"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('memoryTypes') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.shortTermMemory"
                                                        :label="$t('shortTermMemory')" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-memory"
                                                        @update:model-value="updateNodeProperty('shortTermMemory', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :hint="$t('_hintContextWindow')"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col>
                                                    <!-- Long-Term Memory Switch -->
                                                    <div class="switch-option-compact mb-4">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                            :class="{ 'switch-active-bg': nodeInspector.longTermMemory }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28"
                                                                    :color="nodeInspector.longTermMemory ? 'success' : 'grey-lighten-1'"
                                                                    class="me-3">
                                                                    <v-icon
                                                                        :color="nodeInspector.longTermMemory ? 'white' : 'grey-darken-1'"
                                                                        size="16">mdi-database</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">{{ $t('longTermMemory') }}</div>
                                                                    <div class="switch-description">Persistent storage
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <v-switch v-model="nodeInspector.longTermMemory"
                                                                color="success" hide-details density="compact"
                                                                @update:model-value="updateNodeProperty('longTermMemory', $event)"
                                                                :readonly="isViewMode" inset />
                                                        </div>
                                                    </div>
                                                </v-col>
                                            </v-row>

                                        </div>

                                        <!-- Multi-turn Input Configuration -->
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('multiTurnInputConfiguration') }}</h6>
                                            <v-row>
                                                <v-col>
                                                    <!-- Enable Multi-Turn Input Switch -->
                                                    <div class="switch-option-compact mb-3">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                            :class="{ 'switch-active-bg': nodeInspector.isChainMassage }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28"
                                                                    :color="nodeInspector.isChainMassage ? 'indigo' : 'grey-lighten-1'"
                                                                    class="me-3">
                                                                    <v-icon
                                                                        :color="nodeInspector.isChainMassage ? 'white' : 'grey-darken-1'"
                                                                        size="16">mdi-chat-processing</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">Enable Multi-Turn Input
                                                                    </div>
                                                                    <div class="switch-description">Allow continuous
                                                                        conversation</div>
                                                                </div>
                                                            </div>
                                                            <v-switch v-model="nodeInspector.isChainMassage"
                                                                color="indigo" hide-details density="compact"
                                                                @update:model-value="updateNodeProperty('isChainMassage', $event)"
                                                                :readonly="isViewMode" inset />
                                                        </div>
                                                    </div>
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.lenChainMassage"
                                                        :label="$t('sequence')" type="number" min="1" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-numeric"
                                                        @update:model-value="updateNodeProperty('lenChainMassage', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :disabled="!nodeInspector.isChainMassage"
                                                        :hint="$t('_hintTurnsInSequence')" persistent-hint
                                                        clearable />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.timeSecondChainMassage"
                                                        :label="$t('durationSeconds')" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer"
                                                        @update:model-value="updateNodeProperty('timeSecondChainMassage', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :disabled="!nodeInspector.isChainMassage"
                                                        :hint="$t('_hintMultiTurnDuration')" persistent-hint
                                                        clearable />
                                                </v-col>
                                            </v-row>
                                        </div>


                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Parameters Panel -->
                            <v-expansion-panel value="parameters" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #9C27B0, #7B1FA2);">
                                            <v-icon icon="mdi-cog" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('aiParameters') }}</div>
                                            <div class="panel-subtitle">{{ $t('_fineTuneModelBehavior') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('coreParameters') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.temperature"
                                                        :label="$t('temperatureRequired')" type="number" step="0.1" min="0" max="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-thermometer"
                                                        @update:model-value="updateNodeProperty('temperature', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        :hint="$t('_hintCreativityLevel')"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.topP" :label="$t('topP')"
                                                        type="number" step="0.1" min="0" max="1" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-filter-variant"
                                                        @update:model-value="updateNodeProperty('topP', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        :hint="$t('_hintTopP')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('penaltySettings') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.frequencyPenalty"
                                                        :label="$t('frequencyPenalty')" type="number" step="0.1" min="0"
                                                        max="2" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-repeat-off"
                                                        @update:model-value="updateNodeProperty('frequencyPenalty', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        :hint="$t('_hintReduceFrequentTokens')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.presencePenalty"
                                                        :label="$t('presencePenalty')" type="number" step="0.1" min="0"
                                                        max="2" variant="outlined" density="comfortable"
                                                        hide-details="auto"
                                                        prepend-inner-icon="mdi-minus-circle-outline"
                                                        @update:model-value="updateNodeProperty('presencePenalty', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :hint="$t('_hintReduceAnyTokens')"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section">
                                            <h6 class="section-label mb-3">{{ $t('outputControl') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.maxTokens"
                                                        :label="$t('maxTokensResponse')" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-check-outline"
                                                        @update:model-value="updateNodeProperty('maxTokens', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :hint="$t('_hintMaxResponseTokens')"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model="nodeInspector.stopSequence"
                                                        :label="$t('stopSequence')" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-stop-circle-outline"
                                                        @update:model-value="updateNodeProperty('stopSequence', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :hint="$t('_hintStopSequence')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row v-if="modelSupportsReasoning">
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.reasoningEffort"
                                                        :items="reasonOptions" item-title="text" item-value="value"
                                                        :label="$t('reasoningEffort')" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-brain"
                                                        clearable
                                                        @update:model-value="updateNodeProperty('reasoningEffort', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :hint="$t('_tipReasoningDepth')" persistent-hint>
                                                        <template v-slot:selection="{ item }">
                                                            <v-chip size="small" :color="getReasoningColor(item.raw.value)"
                                                                text-color="white" class="px-3">
                                                                {{ item.raw.text }}
                                                            </v-chip>
                                                        </template>
                                                    </v-select>
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Line Configuration Panel -->
                            <v-expansion-panel value="line" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #4CAF50, #388E3C);">
                                            <v-icon icon="mdi-message" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('lineConfiguration') }}</div>
                                            <div class="panel-subtitle">{{ $t('_lineMessagingPlatformSettings') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('displayOptions') }}</h6>

                                            <!-- Show Name -->
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.showLineName }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.showLineName ? 'info' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.showLineName ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-account-circle</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">{{ $t('showName') }}</div>
                                                            <div class="switch-description">{{ $t('displayUserName') }}</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.showLineName" color="info"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('showLineName', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>
                                            <!-- Loading Indicator -->
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.loadingBar }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.loadingBar ? 'success' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.loadingBar ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-progress-clock</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">{{ $t('loadingIndicator') }}</div>
                                                            <div class="switch-description">{{ $t('showTypingAnimation') }}</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.loadingBar" color="success"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('loadingBar', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>

                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('loadingAndDuration') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.loadingBarDuration"
                                                        :items="[10, 20, 30, 40, 50, 60]"
                                                        :label="$t('loadingDurationSeconds')" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-sand"
                                                        @update:model-value="updateNodeProperty('loadingBarDuration', $event)"
                                                        class="config-input" :disabled="!nodeInspector.loadingBar"
                                                        :readonly="isViewMode"
                                                        :hint="$t('_hintLoadingDuration')" persistent-hint
                                                        clearable />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('monitoring') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model="nodeInspector.monitorLineUserId"
                                                        :label="$t('monitorLineUserId')" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-monitor-eye"
                                                        @update:model-value="updateNodeProperty('monitorLineUserId', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :hint="$t('_hintLineUserId')"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.monitorSocialID"
                                                        :items="listMonitorLineName" :label="$t('monitorLineName')"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-chat" item-title="name" item-value="id"
                                                        @update:model-value="updateNodeProperty('monitorSocialID', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :hint="$t('_hintSelectMonitorLine')" persistent-hint clearable />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section">
                                            <h6 class="section-label mb-3">{{ $t('contentFiltering') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-toolbar-title style="font-size: 16px">
                                                        Forbidden word
                                                    </v-toolbar-title>
                                                </v-col>
                                            </v-row>
                                            <div class="pr-1" style="
                                                    border: #dfe0e2 1px solid;
                                                    background: #fff;
                                                    margin-top: 10px;
                                                    border-radius: 8px;
                                                ">
                                                <v-row v-for="(item, index) in nodeInspector.forbiddenWords || ['']"
                                                    :key="index" style="margin: 0px">
                                                    <v-col cols="12" style="padding: 0px">
                                                        <div style="display: flex; align-items: center">
                                                            <div style="
                                                                    height: 50px;
                                                                    background: #f8f8f8;
                                                                    padding: 14px 20px 0 20px;
                                                                ">
                                                                {{ index + 1 }}
                                                            </div>
                                                            <v-textarea variant="solo" style="
                                                                    overflow: hidden;
                                                                    overflow-wrap: break-word;
                                                                    resize: none;
                                                                    height: 50px;
                                                                    width: 98%;
                                                                    border-radius: 0px;
                                                                    font-family: Fira code, Fira Mono, Inconsolata,
                                                                        DejaVu Sans Mono, Courier, monospace;
                                                                    font-size: 14px;
                                                                "
                                                                :model-value="(nodeInspector.forbiddenWords && nodeInspector.forbiddenWords[index]) || ''"
                                                                :placeholder="$t('_phEnterWordPhrase')"
                                                                @keypress="isEnterForbidden($event, index)"
                                                                @keyup.delete="checkDeleteForbiddenWord(index, (nodeInspector.forbiddenWords && nodeInspector.forbiddenWords[index]) || '')"
                                                                @update:model-value="updateForbiddenWordProperty(index, $event)"
                                                                :readonly="isViewMode" />
                                                            <v-btn icon="mdi-attachment" variant="text"
                                                                v-show="(nodeInspector.forbiddenWords || []).length > 1 && index + 1 < (nodeInspector.forbiddenWords || []).length"
                                                                @click="removeForbiddenWordDirect(index)"
                                                                :disabled="isViewMode">
                                                                <v-tooltip location="top" :open-delay="500">
                                                                    <template v-slot:activator="{ props }">
                                                                        <v-icon color="error" style="cursor: pointer"
                                                                            v-bind="props">
                                                                            mdi-delete-forever
                                                                        </v-icon>
                                                                    </template>
                                                                    <span>{{ $t('delete') }}</span>
                                                                </v-tooltip>
                                                            </v-btn>
                                                        </div>
                                                    </v-col>
                                                </v-row>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- System Prompt Panel -->
                            <v-expansion-panel value="prompt" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #607D8B, #455A64);">
                                            <v-icon icon="mdi-message-text" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('systemPrompt') }}</div>
                                            <div class="panel-subtitle">{{ $t('_defineAiBehaviorPersonality') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="prompt-section">
                                            <div class="d-flex align-center mb-4">
                                                <v-avatar size="36" color="info" class="me-3">
                                                    <v-icon color="white" size="20">mdi-robot-outline</v-icon>
                                                </v-avatar>
                                                <div>
                                                    <h6 class="text-h6 mb-1">{{ $t('aiPersonalityInstructions') }}</h6>
                                                    <p class="text-body-2 text-medium-emphasis mb-0">
                                                        Define how your AI should behave, respond, and interact with
                                                        users
                                                    </p>
                                                </div>
                                            </div>
                                            <h3 class="text-h6 d-flex align-center mb-3" style="font-size: 16px !important;">
                                                <v-icon icon="mdi-pencil-outline" size="small"
                                                    class="me-2"></v-icon>
                                                Configure system message for your AI
                                            </h3>

                                            <!-- Prompt Template Header -->
                                            <div class="mb-2" style="display:flex;flex-direction: column;align-items: flex-end;">
                                                <div style="display: flex; align-items: center;">
                                                    <v-tooltip location="top">
                                                        <template v-slot:activator="{ props }">
                                                            <v-icon icon="mdi-information-outline" size="small"
                                                                color="grey" class="me-3" v-bind="props"></v-icon>
                                                        </template>
                                                        <span>{{ $t('_configureAddlPromptsTemplates') }}</span>
                                                    </v-tooltip>
                                                    <v-btn
                                                        v-if="!isViewMode && DDGroupPromptTemplate && DDGroupPromptTemplate.length > 0"
                                                        variant="outlined" color="primary"
                                                        prepend-icon="mdi-playlist-plus" @click="showPromptTemplate"
                                                        class="text-none font-weight-medium" size="small">
                                                        Show Templates
                                                    </v-btn>
                                                </div>
                                            </div>

                                            <!-- Main editor and templates layout -->
                                            <v-row>
                                                <!-- Editor area (full width) -->
                                                <v-col cols="12" class="prompt-editor-col">
                                                    <v-card variant="outlined" class="prompt-editor-card">
                                                        <v-card-text class="px-2 py-1">
                                                            <v-textarea v-model="nodeInspector.systemPrompt" label=""
                                                                variant="plain" counter auto-grow rows="25"
                                                                @update:model-value="updateNodeProperty('systemPrompt', $event)"
                                                                :readonly="isViewMode"
                                                                :placeholder="$t('_phEnterSystemMessage')"
                                                                class="prompt-textarea"
                                                                @contextmenu="onContextMenu"></v-textarea>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-col>
                                            </v-row>

                                            <!-- Context menu for prompt templates -->
                                            <v-menu v-model="showMenu" :activator="{ x, y }" location="bottom">
                                                <v-card min-width="250" elevation="3">
                                                    <v-list density="compact" class="py-0">
                                                        <v-list-subheader class="d-flex bg-grey-lighten-4 py-2">
                                                            <v-icon icon="mdi-text-box-plus-outline" class="me-2"
                                                                size="small"></v-icon>
                                                            Insert Template
                                                        </v-list-subheader>
                                                        <v-divider :thickness="1"
                                                            class="border-opacity-100"></v-divider>
                                                        <v-list-item v-for="(item, index) in filteredPromptTemplates"
                                                            :key="index"
                                                            @click="setSelectPromptTemplate(item); showMenu = false;"
                                                            :value="item.name" class="template-list-item">
                                                            <template v-slot:prepend>
                                                                <v-icon size="small"
                                                                    icon="mdi-text-box-outline"></v-icon>
                                                            </template>
                                                            <template v-slot:title>
                                                                <span>{{ item.name }}</span>
                                                            </template>
                                                        </v-list-item>
                                                    </v-list>
                                                </v-card>
                                            </v-menu>

                                            <!-- Dialog for showing all templates -->
                                            <v-dialog v-model="dialogShowAll" max-width="550" scrollable>
                                                <v-card>
                                                    <v-card-title class="bg-primary text-white d-flex align-center">
                                                        <v-icon icon="mdi-text-box-multiple-outline"
                                                            class="me-2"></v-icon>
                                                        Prompt Templates
                                                    </v-card-title>
                                                    <v-divider :thickness="1" class="border-opacity-100"></v-divider>
                                                    <v-card-text style="max-height: 500px" class="pt-4">
                                                        <v-text-field v-model="templateSearchQuery"
                                                            :label="$t('searchTemplates')" prepend-inner-icon="mdi-magnify"
                                                            variant="outlined" density="compact" hide-details
                                                            class="mb-3" clearable></v-text-field>

                                                        <v-list density="compact" class="template-list-all pa-0">
                                                            <v-list-item v-for="(item, i) in searchedTemplates" :key="i"
                                                                @click="setSelectPromptTemplate(item)"
                                                                class="template-list-item" :ripple="true">
                                                                <template v-slot:prepend>
                                                                    <v-icon size="small" color="primary"
                                                                        icon="mdi-text-box-outline"></v-icon>
                                                                </template>
                                                                <template v-slot:title>
                                                                    <span>{{ item.name }}</span>
                                                                </template>
                                                                <template v-slot:subtitle>
                                                                    <span class="text-caption text-truncate">
                                                                        {{ truncatePrompt(item.prompt) }}
                                                                    </span>
                                                                </template>
                                                            </v-list-item>
                                                        </v-list>
                                                    </v-card-text>
                                                    <v-card-actions>
                                                        <v-spacer></v-spacer>
                                                        <v-btn color="blue darken-1" variant="text"
                                                            @click="dialogShowAll = false">
                                                            Close
                                                        </v-btn>
                                                    </v-card-actions>
                                                </v-card>
                                            </v-dialog>

                                            <!-- Main Prompt Template Dialog -->
                                            <v-dialog v-model="promptTemplateDialog" max-width="800" scrollable>
                                                <v-card>
                                                    <v-card-title class="bg-primary text-white d-flex align-center">
                                                        <v-icon icon="mdi-text-box-multiple-outline" class="me-2"></v-icon>
                                                        Prompt Templates
                                                        <v-spacer></v-spacer>
                                                        <v-btn icon="mdi-close" variant="text" color="white" size="small" 
                                                            @click="promptTemplateDialog = false"></v-btn>
                                                    </v-card-title>
                                                    
                                                    <v-card-text class="pa-4">
                                                        <!-- Template Group Selection -->
                                                        <v-autocomplete 
                                                            :label="$t('selectTemplateGroup')" 
                                                            :items="DDGroupPromptTemplate" 
                                                            item-title="name"
                                                            item-value="id" 
                                                            density="compact" 
                                                            clearable
                                                            variant="outlined" 
                                                            v-model="groupPromptTemplate"
                                                            prepend-inner-icon="mdi-folder-outline" 
                                                            hide-details
                                                            class="mb-4">
                                                        </v-autocomplete>

                                                        <!-- Search Template -->
                                                        <v-text-field 
                                                            v-model="templateSearchQuery"
                                                            :label="$t('searchTemplates')" 
                                                            prepend-inner-icon="mdi-magnify"
                                                            variant="outlined" 
                                                            density="compact" 
                                                            hide-details
                                                            class="mb-4" 
                                                            clearable>
                                                        </v-text-field>

                                                        <!-- Templates List -->
                                                        <div style="max-height: 400px; overflow-y: auto;">
                                                            <div v-if="searchedTemplates.length === 0" 
                                                                class="pa-4 text-center text-grey-darken-1">
                                                                <v-icon icon="mdi-playlist-remove" size="large" 
                                                                    class="d-block mx-auto mb-2"></v-icon>
                                                                <span>{{ $t('noTemplatesFound') }}</span>
                                                            </div>

                                                            <v-list v-else density="compact" class="pa-0">
                                                                <v-list-item 
                                                                    v-for="(item, i) in searchedTemplates" 
                                                                    :key="i"
                                                                    @click="setSelectPromptTemplate(item)"
                                                                    class="template-list-item ma-1" 
                                                                    :ripple="true"
                                                                    rounded>
                                                                    <template v-slot:prepend>
                                                                        <v-icon size="small" color="primary" 
                                                                            icon="mdi-text-box-outline"></v-icon>
                                                                    </template>
                                                                    <template v-slot:title>
                                                                        <span class="font-weight-medium">{{ item.name }}</span>
                                                                    </template>
                                                                    <template v-slot:subtitle>
                                                                        <span class="text-caption text-grey">
                                                                            {{ truncatePrompt(item.prompt) }}
                                                                        </span>
                                                                    </template>
                                                                    <template v-slot:append>
                                                                        <v-btn icon="mdi-plus" size="small" variant="text" 
                                                                            color="primary" @click.stop="setSelectPromptTemplate(item)">
                                                                        </v-btn>
                                                                    </template>
                                                                </v-list-item>
                                                            </v-list>
                                                        </div>
                                                    </v-card-text>
                                                    
                                                    <v-card-actions>
                                                        <v-spacer></v-spacer>
                                                        <v-btn color="primary" variant="text" @click="promptTemplateDialog = false">
                                                            Close
                                                        </v-btn>
                                                    </v-card-actions>
                                                </v-card>
                                            </v-dialog>

                                            <div class="prompt-tips mt-4">
                                                <v-card class="tips-card" color="blue-grey-lighten-5" flat>
                                                    <v-card-text class="pa-4">
                                                        <div class="d-flex align-start">
                                                            <v-icon color="info" size="20"
                                                                class="me-2 mt-1">mdi-lightbulb-outline</v-icon>
                                                            <div>
                                                                <div class="text-subtitle-2 font-weight-medium mb-2">
                                                                    Tips for
                                                                    better prompts:</div>
                                                                <ul class="text-body-2 text-medium-emphasis">
                                                                    <li>Be specific about the AI's role and expertise
                                                                    </li>
                                                                    <li>{{ $t('_defineToneStyle') }}</li>
                                                                    <li>{{ $t('_includeRestrictionsGuidelines') }}</li>
                                                                    <li>{{ $t('_specifyHandleUnknownInfo') }}</li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </v-card-text>
                                                </v-card>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>

                    </div>

                    <div v-else-if="selectedNode.data.type === 'orchestrator'" class="form-section agent-architect-inspector orchestrator-config-inspector">
                        <div class="aa-config-hero aa-orchestrator-hero">
                            <v-icon size="24">mdi-hub</v-icon>
                            <div>
                                <div class="aa-hero-title">{{ $t('orchestratorConfiguration') }}</div>
                                <div class="aa-hero-subtitle">{{ $t('_configureMultiAgentOrchestration') }}</div>
                            </div>
                        </div>

                        <v-text-field v-model="nodeInspector.orchestratorName" :label="$t('orchestratorName')" variant="outlined" density="compact" hide-details="auto"
                            prepend-inner-icon="mdi-label" @update:model-value="updateNodeProperty('orchestratorName', $event)" :readonly="isViewMode" class="aa-field mb-4" />

                        <v-expansion-panels v-model="orchestratorConfigPanels" multiple class="config-panels elevation-0" variant="accordion">
                            <v-expansion-panel value="model" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #ec4899, #db2777);">
                                            <v-icon icon="mdi-brain" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('modelConfiguration') }}</div>
                                            <div class="panel-subtitle">{{ $t('_providerModelParameters') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-4">
                                            <v-autocomplete v-model="nodeInspector.model" :items="modelSelectionOptions" item-title="modelDisplay" item-value="modelName" :label="$t('model')" variant="outlined"
                                                density="comfortable" hide-details="auto" prepend-inner-icon="mdi-brain" clearable :readonly="isViewMode"
                                                @update:model-value="updateNodeProperty('model', $event)" class="config-input" :hint="$t('_hintModelAgentSelection')" persistent-hint>
                                                <template v-slot:item="{ props, item }">
                                                    <v-list-item v-bind="props" :title="getModelOptionTitle(item.raw)">
                                                        <template v-slot:subtitle>
                                                            <div class="model-option-subtitle">
                                                                <span class="model-option-meta-value">{{ item.raw.platform || '-' }}</span>
                                                                <span class="model-option-meta-separator">/</span>
                                                                <span class="model-option-meta-value">{{ item.raw.provider || '-' }}</span>
                                                            </div>
                                                        </template>
                                                    </v-list-item>
                                                </template>
                                            </v-autocomplete>
                                        </div>
                                        <div class="config-section mb-4">
                                            <h6 class="section-label mb-3">{{ $t('coreParameters') }}</h6>
                                            <v-row dense>
                                                <v-col cols="6">
                                                    <v-text-field v-model.number="nodeInspector.temperature" type="number" :label="$t('temperature')" min="0" max="2" step="0.1" variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-thermometer" @update:model-value="updateNodeProperty('temperature', $event)" :readonly="isViewMode" class="config-input" />
                                                </v-col>
                                                <v-col cols="6">
                                                    <v-text-field v-model.number="nodeInspector.maxTokens" type="number" :label="$t('maxTokens')" min="1" variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-check-outline" @update:model-value="updateNodeProperty('maxTokens', $event)" :readonly="isViewMode" class="config-input" />
                                                </v-col>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.topP" type="number" :label="$t('topP')" min="0" max="1" step="0.1" variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-filter-variant" @update:model-value="updateNodeProperty('topP', $event)" :readonly="isViewMode" class="config-input" />
                                                </v-col>
                                                <v-col cols="6">
                                                    <v-text-field v-model.number="nodeInspector.frequencyPenalty" type="number" :label="$t('frequencyTrunc')" min="0" max="2" step="0.1" variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-repeat-off" @update:model-value="updateNodeProperty('frequencyPenalty', $event)" :readonly="isViewMode" class="config-input" />
                                                </v-col>
                                                <v-col cols="6">
                                                    <v-text-field v-model.number="nodeInspector.presencePenalty" type="number" :label="$t('presencePerTrunc')" min="0" max="2" step="0.1" variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-minus-circle-outline" @update:model-value="updateNodeProperty('presencePenalty', $event)" :readonly="isViewMode" class="config-input" />
                                                </v-col>
                                                <v-col cols="12">
                                                    <v-text-field v-model="nodeInspector.stopSequence" :label="$t('stopSequence')" variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-stop-circle-outline" @update:model-value="updateNodeProperty('stopSequence', $event)" :readonly="isViewMode" class="config-input"
                                                        :hint="$t('_hintStopSequence')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">{{ $t('behaviorSettings') }}</h6>
                                            <v-row dense>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.gender" :items="[{ text: 'Male', value: 'male' }, { text: 'Female', value: 'female' }, { text: 'Neutral', value: 'neutral' }]"
                                                        item-title="text" item-value="value" :label="$t('gender')" variant="outlined" density="comfortable"
                                                        prepend-inner-icon="mdi-account" @update:model-value="updateNodeProperty('gender', $event)" :readonly="isViewMode" class="config-input"
                                                        :hint="$t('_hintGenderPreference')" persistent-hint />
                                                </v-col>
                                                <v-col cols="6">
                                                    <v-text-field v-model.number="nodeInspector.typingSpeed" type="number" :label="$t('typingSpeed')" min="0" variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-keyboard-outline" @update:model-value="updateNodeProperty('typingSpeed', $event)" :readonly="isViewMode" class="config-input"
                                                        :hint="$t('_hintCharsSec')" persistent-hint />
                                                </v-col>
                                                <v-col cols="6">
                                                    <v-text-field v-model.number="nodeInspector.maxInputLength" type="number" :label="$t('maxInputLength')" min="1" variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-outline" @update:model-value="updateNodeProperty('maxInputLength', $event)" :readonly="isViewMode" class="config-input"
                                                        :hint="$t('_hintMaxInputChars')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <v-expansion-panel value="response" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #ff9800, #f57c00);">
                                            <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('responseOptions') }}</div>
                                            <div class="panel-subtitle">{{ $t('_configureHowResponsesGenerated') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <div class="switch-option-compact mb-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg" :class="{ 'switch-active-bg': nodeInspector.streamResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28" :color="nodeInspector.streamResponse ? 'primary' : 'grey-lighten-1'" class="me-3">
                                                        <v-icon :color="nodeInspector.streamResponse ? 'white' : 'grey-darken-1'" size="16">mdi-text-box-outline</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">{{ $t('streamResponse') }}</div>
                                                        <div class="switch-description">{{ $t('_progressiveTextGeneration') }}</div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.streamResponse" color="primary" hide-details density="compact" inset @update:model-value="updateNodeProperty('streamResponse', $event)" :readonly="isViewMode" />
                                            </div>
                                        </div>
                                        <div class="switch-option-compact">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg" :class="{ 'switch-active-bg': nodeInspector.jsonResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28" :color="nodeInspector.jsonResponse ? 'primary' : 'grey-lighten-1'" class="me-3">
                                                        <v-icon :color="nodeInspector.jsonResponse ? 'white' : 'grey-darken-1'" size="16">mdi-code-json</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">{{ $t('jsonResponse') }}</div>
                                                        <div class="switch-description">{{ $t('_returnStructuredJson') }}</div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.jsonResponse" color="primary" hide-details density="compact" inset @update:model-value="updateNodeProperty('jsonResponse', $event)" :readonly="isViewMode" />
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <v-expansion-panel value="memory" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #00bcd4, #0097a7);">
                                            <v-icon icon="mdi-brain" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('memorySettings') }}</div>
                                            <div class="panel-subtitle">{{ $t('_configureConvMemoryContext') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <v-row>
                                            <v-col cols="6">
                                                <v-text-field v-model.number="nodeInspector.memoryTurn" type="number" :label="$t('turnCount')" min="0" variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-history" @update:model-value="updateNodeProperty('memoryTurn', $event)" :readonly="isViewMode" class="config-input" />
                                            </v-col>
                                            <v-col cols="6">
                                                <v-text-field v-model.number="nodeInspector.memoryDuration" type="number" :label="$t('durationMinutes')" min="0" variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-timer-outline" @update:model-value="updateNodeProperty('memoryDuration', $event)" :readonly="isViewMode" class="config-input" />
                                            </v-col>
                                            <v-col cols="12">
                                                <v-text-field v-model.number="nodeInspector.shortTermMemory" type="number" :label="$t('shortTermMemory')" min="1" variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-memory" @update:model-value="updateNodeProperty('shortTermMemory', $event)" :readonly="isViewMode" class="config-input" />
                                            </v-col>
                                        </v-row>
                                        <div class="switch-option-compact mt-2">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg" :class="{ 'switch-active-bg': nodeInspector.longTermMemory }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28" :color="nodeInspector.longTermMemory ? 'success' : 'grey-lighten-1'" class="me-3">
                                                        <v-icon :color="nodeInspector.longTermMemory ? 'white' : 'grey-darken-1'" size="16">mdi-database</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">{{ $t('longTermMemory') }}</div>
                                                        <div class="switch-description">{{ $t('_persistentMemoryOrchestration') }}</div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.longTermMemory" color="success" hide-details density="compact" inset @update:model-value="updateNodeProperty('longTermMemory', $event)" :readonly="isViewMode" />
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <v-expansion-panel value="system" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #7c4dff, #5e35b1);">
                                            <v-icon icon="mdi-text-box-edit" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('systemPrompt') }}</div>
                                            <div class="panel-subtitle">{{ $t('_instructionsAgentRouting') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <v-textarea v-model="nodeInspector.systemPrompt" :label="$t('systemPrompt')" variant="outlined" density="comfortable" hide-details="auto" rows="6"
                                            prepend-inner-icon="mdi-text-box-edit-outline" @update:model-value="updateNodeProperty('systemPrompt', $event)" :readonly="isViewMode" class="config-input" />
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <v-expansion-panel value="guardrail" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #ff9800, #ef6c00);">
                                            <v-icon icon="mdi-shield-check" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('guardrail') }}</div>
                                            <div class="panel-subtitle">{{ $t('_responseValidationBeforeOutput') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <v-textarea v-model="nodeInspector.guardrailPrompt" :label="$t('guardrailPrompt')" variant="outlined" density="comfortable" hide-details="auto" rows="5"
                                            prepend-inner-icon="mdi-shield-check" @update:model-value="updateNodeProperty('guardrailPrompt', $event)" :readonly="isViewMode" class="config-input mb-3" />
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <v-expansion-panel value="cache" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #ff8f00, #fb8c00);">
                                            <v-icon icon="mdi-sync" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('cacheSettings') }}</div>
                                            <div class="panel-subtitle">{{ $t('_configureResponseCaching') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('responseCache') }}</h6>
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-3 px-4 rounded-lg" :class="{ 'switch-active-bg': nodeInspector.enableCache }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="32" :color="nodeInspector.enableCache ? 'warning' : 'grey-lighten-1'" class="me-3">
                                                            <v-icon :color="nodeInspector.enableCache ? 'white' : 'grey-darken-1'" size="18">mdi-lightning-bolt</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">{{ $t('enableCache') }}</div>
                                                            <div class="switch-description">{{ $t('_cacheResponsesReduceCalls') }}</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableCache" color="warning" hide-details density="compact" inset @update:model-value="updateNodeProperty('enableCache', $event)" :readonly="isViewMode" />
                                                </div>
                                            </div>
                                            <v-text-field v-model.number="nodeInspector.cacheTTLMinutes" type="number" :label="$t('cacheTtlMinutes')" min="1" max="10080" variant="outlined" density="comfortable"
                                                prepend-inner-icon="mdi-timer-sand" :hint="$t('_hintCacheTtl')" persistent-hint
                                                @update:model-value="updateNodeProperty('cacheTTLMinutes', $event)" :readonly="isViewMode" :disabled="!nodeInspector.enableCache" class="config-input mb-3" />
                                            <v-select v-model="nodeInspector.cacheScope" :items="cacheScopes" item-title="title" item-value="value" :label="$t('cacheScope')" variant="outlined" density="comfortable" hide-details="auto"
                                                prepend-inner-icon="mdi-crosshairs-gps" @update:model-value="updateNodeProperty('cacheScope', $event)" :readonly="isViewMode" :disabled="!nodeInspector.enableCache" class="config-input" />
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <v-expansion-panel value="line" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #4caf50, #388e3c);">
                                            <v-icon icon="mdi-message" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('lineConfiguration') }}</div>
                                            <div class="panel-subtitle">{{ $t('_lineMessagingPlatformSettings') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-4">
                                            <h6 class="section-label mb-3">{{ $t('displayOptions') }}</h6>
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-3 px-4 rounded-lg" :class="{ 'switch-active-bg': nodeInspector.showLineName }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="32" :color="nodeInspector.showLineName ? 'info' : 'grey-lighten-1'" class="me-3">
                                                            <v-icon :color="nodeInspector.showLineName ? 'white' : 'grey-darken-1'" size="18">mdi-account-circle</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">{{ $t('showName') }}</div>
                                                            <div class="switch-description">{{ $t('displayUserName') }}</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.showLineName" color="info" hide-details density="compact" inset @update:model-value="updateNodeProperty('showLineName', $event)" :readonly="isViewMode" />
                                                </div>
                                            </div>
                                            <div class="switch-option-compact">
                                                <div class="d-flex align-center justify-space-between py-3 px-4 rounded-lg" :class="{ 'switch-active-bg': nodeInspector.loadingBar }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="32" :color="nodeInspector.loadingBar ? 'success' : 'grey-lighten-1'" class="me-3">
                                                            <v-icon :color="nodeInspector.loadingBar ? 'white' : 'grey-darken-1'" size="18">mdi-progress-clock</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">{{ $t('loadingIndicator') }}</div>
                                                            <div class="switch-description">{{ $t('showTypingAnimation') }}</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.loadingBar" color="success" hide-details density="compact" inset @update:model-value="updateNodeProperty('loadingBar', $event)" :readonly="isViewMode" />
                                                </div>
                                            </div>
                                        </div>

                                        <div class="config-section mb-4">
                                            <h6 class="section-label mb-3">{{ $t('loadingAndDuration') }}</h6>
                                            <v-select v-model="nodeInspector.loadingBarDuration" :items="[0, 10, 20, 30, 40, 50, 60]" :label="$t('loadingDurationSeconds')" variant="outlined" density="comfortable" hide-details="auto"
                                                prepend-inner-icon="mdi-timer-sand" @update:model-value="updateNodeProperty('loadingBarDuration', $event)" class="config-input" :disabled="!nodeInspector.loadingBar" :readonly="isViewMode" :hint="$t('_hintLoadingDuration')" persistent-hint clearable />
                                        </div>

                                        <div class="config-section mb-4">
                                            <h6 class="section-label mb-3">{{ $t('monitoring') }}</h6>
                                            <v-text-field v-model="nodeInspector.monitorLineUserId" :label="$t('monitorLineUserId')" variant="outlined" density="comfortable" hide-details="auto"
                                                prepend-inner-icon="mdi-monitor-eye" @update:model-value="updateNodeProperty('monitorLineUserId', $event)" class="config-input mb-3" :readonly="isViewMode" :hint="$t('_hintLineUserId')" persistent-hint />
                                            <v-autocomplete v-model="nodeInspector.monitorSocialID" :items="listMonitorLineName" item-title="name" item-value="id" :label="$t('monitorLineName')" variant="outlined" density="comfortable" hide-details="auto"
                                                prepend-inner-icon="mdi-account-eye" @update:model-value="updateNodeProperty('monitorSocialID', $event)" class="config-input" :readonly="isViewMode" clearable :hint="$t('_hintSelectMonitorLine')" persistent-hint />
                                        </div>

                                        <div class="config-section">
                                            <h6 class="section-label mb-3">{{ $t('contentFiltering') }}</h6>
                                            <div class="mb-2" style="font-size: 13px; color: #374151; font-weight: 500;">{{ $t('forbiddenWord') }}</div>
                                            <v-row v-for="(item, index) in nodeInspector.forbiddenWords || ['']" :key="`orchestrator-forbidden-${index}`" dense>
                                                <v-col cols="1" class="d-flex align-center justify-center">
                                                    <span style="font-size: 13px; color: #9ca3af; font-weight: 600;">{{ index + 1 }}</span>
                                                </v-col>
                                                <v-col cols="9">
                                                    <v-text-field :model-value="(nodeInspector.forbiddenWords && nodeInspector.forbiddenWords[index]) || ''" :placeholder="$t('_phEnterWordPhrase')" variant="outlined" density="compact" hide-details="auto"
                                                        @update:model-value="updateForbiddenWordProperty(index, $event)" @keypress="isEnterForbidden($event, index)" @keyup.delete="checkDeleteForbiddenWord(index, (nodeInspector.forbiddenWords && nodeInspector.forbiddenWords[index]) || '')" :readonly="isViewMode" />
                                                </v-col>
                                                <v-col cols="2" class="d-flex align-center">
                                                    <v-btn v-show="(nodeInspector.forbiddenWords || []).length > 1 && index + 1 < (nodeInspector.forbiddenWords || []).length" icon="mdi-close" variant="text" color="error" size="small" @click="removeForbiddenWordDirect(index)" />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <v-expansion-panel value="prompt" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #607d8b, #455a64);">
                                            <v-icon icon="mdi-cog-outline" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('promptSettings') }}</div>
                                            <div class="panel-subtitle">{{ $t('_timeContextTransactionFlags') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <div class="config-section mb-4">
                                            <h6 class="section-label mb-3">{{ $t('timeContext') }}</h6>
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-3 px-4 rounded-lg" :class="{ 'switch-active-bg': nodeInspector.enableTime }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="32" :color="nodeInspector.enableTime ? 'success' : 'grey-lighten-1'" class="me-3">
                                                            <v-icon :color="nodeInspector.enableTime ? 'white' : 'grey-darken-1'" size="18">mdi-clock-outline</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">{{ $t('timeContext') }}</div>
                                                            <div class="switch-description">{{ $t('_enableTimeAwarenessAi') }}</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableTime" color="success" hide-details density="compact" inset @update:model-value="updateNodeProperty('enableTime', $event)" :readonly="isViewMode" />
                                                </div>
                                            </div>
                                            <v-select v-if="nodeInspector.enableTime" v-model="nodeInspector.timeZone" :items="DDTimeZone" item-title="name" item-value="value" :label="$t('timeZone')" variant="outlined" density="comfortable" hide-details="auto"
                                                prepend-inner-icon="mdi-earth" @update:model-value="updateNodeProperty('timeZone', $event)" class="config-input" :readonly="isViewMode" clearable />
                                        </div>

                                        <div class="config-section mb-4">
                                            <h6 class="section-label mb-3">{{ $t('transactionIdContext') }}</h6>
                                            <div class="switch-option-compact">
                                                <div class="d-flex align-center justify-space-between py-3 px-4 rounded-lg" :class="{ 'switch-active-bg': nodeInspector.enableTransactionId }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="32" :color="nodeInspector.enableTransactionId ? 'primary' : 'grey-lighten-1'" class="me-3">
                                                            <v-icon :color="nodeInspector.enableTransactionId ? 'white' : 'grey-darken-1'" size="16">mdi-identifier</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">{{ $t('addTransactionIdToPrompt') }}</div>
                                                            <div class="switch-description">{{ $t('_includeTransactionIdAiContext') }}</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableTransactionId" color="primary" hide-details density="compact" inset @update:model-value="updateNodeProperty('enableTransactionId', $event)" :readonly="isViewMode" />
                                                </div>
                                            </div>
                                        </div>

                                        <div class="config-section">
                                            <h6 class="section-label mb-3">{{ $t('promptReplacementFlags') }}</h6>
                                            <div class="switch-option-compact mb-2">
                                                <div class="d-flex align-center justify-space-between py-3 px-4 rounded-lg" :class="{ 'switch-active-bg': nodeInspector.enableInputMessageReplace, 'flag-active-purple': nodeInspector.enableInputMessageReplace }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="32" :color="nodeInspector.enableInputMessageReplace ? 'deep-purple' : 'grey-lighten-1'" class="me-3">
                                                            <v-icon :color="nodeInspector.enableInputMessageReplace ? 'white' : 'grey-darken-1'" size="18">mdi-message-text-outline</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">{{ $t('inputMessageReplacing') }}</div>
                                                            <div class="switch-description">{{ $t('_replaceTagInputValue') }}</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableInputMessageReplace" color="deep-purple" hide-details density="compact" inset @update:model-value="updateNodeProperty('enableInputMessageReplace', $event)" :readonly="isViewMode" />
                                                </div>
                                                <div v-if="nodeInspector.enableInputMessageReplace" class="flag-snippet-row">
                                                    <span class="flag-snippet-code">&#123;&#123;InputMessage&#125;&#125;</span>
                                                    <v-btn icon="mdi-content-copy" variant="text" size="x-small" density="compact" @click="copyToClipboard('{{InputMessage}}')" />
                                                </div>
                                            </div>
                                            <div class="switch-option-compact mb-2">
                                                <div class="d-flex align-center justify-space-between py-3 px-4 rounded-lg" :class="{ 'switch-active-bg': nodeInspector.enableVoicePromptReplace, 'flag-active-orange': nodeInspector.enableVoicePromptReplace }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="32" :color="nodeInspector.enableVoicePromptReplace ? 'deep-orange' : 'grey-lighten-1'" class="me-3">
                                                            <v-icon :color="nodeInspector.enableVoicePromptReplace ? 'white' : 'grey-darken-1'" size="18">mdi-microphone-outline</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">{{ $t('voicePromptReplacing') }}</div>
                                                            <div class="switch-description">{{ $t('_replaceTagTtsPrompt') }}</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableVoicePromptReplace" color="deep-orange" hide-details density="compact" inset @update:model-value="updateNodeProperty('enableVoicePromptReplace', $event)" :readonly="isViewMode" />
                                                </div>
                                                <div v-if="nodeInspector.enableVoicePromptReplace" class="flag-snippet-row">
                                                    <span class="flag-snippet-code">&#123;&#123;PromptVoice&#125;&#125;</span>
                                                    <v-btn icon="mdi-content-copy" variant="text" size="x-small" density="compact" @click="copyToClipboard('{{PromptVoice}}')" />
                                                </div>
                                            </div>
                                            <div class="switch-option-compact">
                                                <div class="d-flex align-center justify-space-between py-3 px-4 rounded-lg" :class="{ 'switch-active-bg': nodeInspector.enableLanguageReplace, 'flag-active-teal': nodeInspector.enableLanguageReplace }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="32" :color="nodeInspector.enableLanguageReplace ? 'teal' : 'grey-lighten-1'" class="me-3">
                                                            <v-icon :color="nodeInspector.enableLanguageReplace ? 'white' : 'grey-darken-1'" size="18">mdi-translate</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">{{ $t('languageReplacing') }}</div>
                                                            <div class="switch-description">{{ $t('_replaceTagDetectedLanguage') }}</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableLanguageReplace" color="teal" hide-details density="compact" inset @update:model-value="updateNodeProperty('enableLanguageReplace', $event)" :readonly="isViewMode" />
                                                </div>
                                                <div v-if="nodeInspector.enableLanguageReplace" class="flag-snippet-row">
                                                    <span class="flag-snippet-code">&#123;&#123;Language&#125;&#125;</span>
                                                    <v-btn icon="mdi-content-copy" variant="text" size="x-small" density="compact" @click="copyToClipboard('{{Language}}')" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>

                        <v-card class="mt-4 pa-4 rounded-lg multi-agent-card" variant="outlined">
                            <div class="d-flex align-center justify-space-between">
                                <div class="d-flex align-center">
                                    <v-avatar size="32" :color="nodeInspector.enableMultiAgent ? 'pink' : 'grey-lighten-1'" class="me-3">
                                        <v-icon :color="nodeInspector.enableMultiAgent ? 'white' : 'grey-darken-1'" size="18">mdi-account-group</v-icon>
                                    </v-avatar>
                                    <div>
                                        <div class="switch-title">{{ $t('enableMultiAgent') }}</div>
                                        <div class="switch-description">{{ $t('_allowMultipleAgents') }}</div>
                                    </div>
                                </div>
                                <v-switch v-model="nodeInspector.enableMultiAgent" color="pink" hide-details density="compact" inset @update:model-value="updateNodeProperty('enableMultiAgent', $event)" :readonly="isViewMode" />
                            </div>
                            <div class="text-caption text-medium-emphasis mt-3">
                                When enabled, orchestrator can dispatch multiple agents.<br>
                                When disabled, uses single agent mode.
                            </div>
                            <v-select v-if="nodeInspector.enableMultiAgent" v-model="nodeInspector.multiAgentStrategy" :items="multiAgentStrategies" item-title="title" item-value="value" :label="$t('multiAgentStrategy')" variant="outlined" density="comfortable"
                                hide-details="auto" @update:model-value="updateNodeProperty('multiAgentStrategy', $event)" :readonly="isViewMode" class="config-input mt-3" />
                            <v-alert v-if="nodeInspector.enableMultiAgent" type="info" variant="tonal" density="compact" class="mt-3">
                                Orchestrator analyzes user input and routes to appropriate agents. Connect agents to the bottom handle for dispatching.
                            </v-alert>
                        </v-card>
                    </div>

                    <div v-else-if="selectedNode.data.type === 'agent'" class="form-section agent-architect-inspector agent-config-inspector">
                        <div class="aa-config-hero aa-agent-hero">
                            <v-icon size="24">mdi-robot</v-icon>
                            <div>
                                <div class="aa-hero-title">{{ $t('agentConfiguration') }}</div>
                                <div class="aa-hero-subtitle">{{ $t('_configureAutonomousAgent') }}</div>
                            </div>
                        </div>

                        <v-text-field v-model="nodeInspector.agentName" :label="$t('agentName')" variant="outlined" density="compact" hide-details="auto" prepend-inner-icon="mdi-label"
                            @update:model-value="updateNodeProperty('agentName', $event)" :readonly="isViewMode" class="aa-field mb-3" />
                        <v-textarea v-model="nodeInspector.agentDescription" :label="$t('agentDescription')" variant="outlined" density="compact" hide-details="auto" rows="3" auto-grow
                            @update:model-value="updateNodeProperty('agentDescription', $event)" :readonly="isViewMode" class="aa-field mb-1" />
                        <div class="aa-field-hint mb-4">{{ $t('_descHelpsOrchestrator') }}</div>
                        <v-textarea v-model="nodeInspector.personaPrompt" :label="$t('personaPrompt')" variant="outlined" density="compact" hide-details="auto" rows="5" auto-grow
                            @update:model-value="updateNodeProperty('personaPrompt', $event)" :readonly="isViewMode" class="aa-field mb-4" />

                        <div class="aa-switch-card mb-4" :class="{ 'aa-switch-card-active': nodeInspector.enableAgentToolDecision }">
                            <div class="aa-switch-main">
                                <v-avatar size="34" :color="nodeInspector.enableAgentToolDecision ? 'teal' : 'grey-lighten-1'" class="me-3">
                                    <svg
                                        class="aa-switch-icon-svg"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                        :style="{ color: nodeInspector.enableAgentToolDecision ? '#ffffff' : '#4b5563' }"
                                    >
                                        <path d="M0 0h24v24H0z" fill="none" />
                                        <path fill="currentColor" d="M13 3c3.88 0 7 3.14 7 7c0 2.8-1.63 5.19-4 6.31V21H9v-3H8c-1.11 0-2-.89-2-2v-3H4.5c-.42 0-.66-.5-.42-.81L6 9.66A7.003 7.003 0 0 1 13 3m0-2C8.42 1 4.61 4.43 4.06 8.91L2.5 11c-.58.72-.68 1.72-.26 2.59c.36.72 1 1.21 1.76 1.36V16c0 1.86 1.28 3.43 3 3.87V23h11v-5.53c2.5-1.64 4-4.41 4-7.47c0-4.96-4.04-9-9-9m3.1 8.42V9c0-.15 0-.24-.06-.38l.89-.66c.07-.04.07-.18.07-.28l-.82-1.36c-.05-.09-.18-.14-.28-.09l-.99.42c-.18-.19-.41-.33-.65-.42L14.1 5.2c-.03-.14-.1-.2-.22-.2h-1.59c-.1 0-.19.06-.19.2l-.14 1.03c-.23.09-.46.23-.66.42l-1.03-.42c-.09-.05-.17 0-.23.09l-.8 1.36c-.05.14-.05.24.05.28l.84.66c0 .14-.03.28-.03.38c0 .14.03.28.03.42l-.84.65c-.1.05-.1.14-.05.24l.8 1.4c.06.1.14.1.23.1l.99-.43c.24.19.42.29.7.38l.14 1.08c0 .09.09.16.19.16h1.59c.12 0 .19-.07.22-.16l.16-1.08c.24-.09.47-.19.65-.37l.99.42c.1 0 .23 0 .28-.1l.82-1.4c0-.1 0-.19-.07-.24zm-3 1.03c-.78 0-1.42-.66-1.42-1.45s.61-1.41 1.42-1.41c.78 0 1.44.61 1.44 1.41s-.66 1.45-1.44 1.45" />
                                    </svg>
                                </v-avatar>
                                <div class="aa-switch-text">
                                    <div class="aa-switch-title">{{ $t('enableToolDecision') }}</div>
                                    <div class="aa-switch-subtitle">{{ $t('_llmSelectsRelevantTools') }}</div>
                                </div>
                            </div>
                            <v-switch v-model="nodeInspector.enableAgentToolDecision" color="teal" hide-details density="compact" inset @update:model-value="handleAgentToolDecisionToggle" :readonly="isViewMode" />
                            <div class="aa-switch-help">{{ $t('_toolDecisionExplain') }}</div>
                            <v-alert v-if="getConnectedAgentDecisionResourceCount() <= 1" type="warning" variant="tonal" density="compact" class="mt-3 aa-switch-warning">
                                Connect at least 2 tools, flow tools, or knowledge sources to use this mode.
                            </v-alert>
                        </div>

                        <v-expansion-panels class="aa-flat-panels mb-4" variant="accordion">
                            <v-expansion-panel class="aa-flat-panel">
                                <v-expansion-panel-title class="aa-flat-panel-title">
                                    <div class="aa-panel-title-row">
                                        <v-avatar size="42" color="teal" class="aa-panel-avatar">
                                            <v-icon color="white" size="21">mdi-brain</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="aa-panel-title">{{ $t('knowledgeConnections') }}</div>
                                            <div class="aa-panel-subtitle">{{ $t('_connectKnowledgeToAgent') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="aa-flat-panel-content">
                                    <v-alert v-if="!getConnectedKnowledge().length" type="info" variant="tonal" density="compact" class="mb-3">
                                        Connect knowledge nodes from the Agent tools handle to describe how this agent should use them.
                                    </v-alert>
                                    <div v-for="knowledge in getConnectedKnowledge()" :key="knowledge.nodeId" class="aa-knowledge-card mb-3">
                                        <div class="aa-knowledge-title">{{ knowledge.label }}</div>
                                        <v-textarea :model-value="getKnowledgeConnectionDescription(knowledge)" :placeholder="getKnowledgePlaceholder(knowledge.type)" variant="outlined" density="compact" rows="2" hide-details="auto"
                                            @update:model-value="updateKnowledgeDescription(knowledge, $event)" :readonly="isViewMode" />
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>

                        <div class="aa-switch-card mb-4" :class="{ 'aa-switch-card-active': nodeInspector.useCustomAIModel }">
                            <div class="aa-switch-main">
                                <v-avatar size="34" :color="nodeInspector.useCustomAIModel ? 'teal' : 'grey-lighten-1'" class="me-3">
                                    <v-icon :color="nodeInspector.useCustomAIModel ? 'white' : 'grey-darken-1'" size="17">mdi-brain</v-icon>
                                </v-avatar>
                                <div class="aa-switch-text">
                                    <div class="aa-switch-title">{{ $t('customAiModel') }}</div>
                                    <div class="aa-switch-subtitle">{{ $t('_useAgentSpecificModel') }}</div>
                                </div>
                            </div>
                            <v-switch v-model="nodeInspector.useCustomAIModel" color="teal" hide-details density="compact" inset @update:model-value="updateNodeProperty('useCustomAIModel', $event)" :readonly="isViewMode" />
                            <div class="aa-switch-help">{{ $t('_customModelExplain') }}</div>
                        </div>

                        <v-expansion-panels v-if="nodeInspector.useCustomAIModel" v-model="agentConfigPanels" multiple class="config-panels elevation-0 mt-2" variant="accordion">
                            <!-- Basic Information -->
                            <v-expansion-panel value="basic" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4">
                                            <v-icon icon="mdi-information" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('basicInfo') }}</div>
                                            <div class="panel-subtitle">{{ $t('_modelAndServiceDetails') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-6">
                                            <h6 class="section-label mb-3">{{ $t('serviceConfiguration') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.agentModel" :items="modelSelectionOptions" item-title="modelDisplay" item-value="modelName" :label="$t('modelRequired')"
                                                        variant="outlined" density="comfortable" hide-details="auto" prepend-inner-icon="mdi-brain"
                                                        @update:model-value="updateNodeProperty('agentModel', $event)" class="config-input" :readonly="isViewMode"
                                                        :hint="$t('_hintSelectModelVersion')" persistent-hint required clearable>
                                                        <template v-slot:item="{ props, item }">
                                                            <v-list-item v-bind="props" :title="getModelOptionTitle(item.raw)">
                                                                <template v-slot:subtitle>
                                                                    <div class="model-option-subtitle">
                                                                        <span class="model-option-meta-value">{{ item.raw.platform || '-' }}</span>
                                                                        <span class="model-option-meta-separator">/</span>
                                                                        <span class="model-option-meta-value">{{ item.raw.provider || '-' }}</span>
                                                                    </div>
                                                                </template>
                                                            </v-list-item>
                                                        </template>
                                                        <template v-slot:selection="{ item }">
                                                            <span class="model-selection-title">{{ getModelOptionTitle(item.raw) }}</span>
                                                        </template>
                                                    </v-autocomplete>
                                                </v-col>
                                            </v-row>
                                        </div>
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">{{ $t('behaviorSettings') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select :model-value="nodeInspector.agentGender || 'male'" :items="[{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }]"
                                                        item-title="name" item-value="id" :label="$t('gender')" variant="outlined" density="comfortable" hide-details="auto" prepend-inner-icon="mdi-account"
                                                        @update:model-value="updateNodeProperty('agentGender', $event)" :readonly="isViewMode" class="config-input"
                                                        :hint="$t('_hintGenderPreference')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.agentTypingSpeed" type="number" min="0" :label="$t('typingSpeed')"
                                                        variant="outlined" density="comfortable" hide-details="auto" prepend-inner-icon="mdi-keyboard-outline"
                                                        @update:model-value="updateNodeProperty('agentTypingSpeed', $event)" :readonly="isViewMode" class="config-input"
                                                        :hint="$t('_hintCharsPerSecond')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.agentMaxInputLength" type="number" min="1" max="200000" :label="$t('maxInputLength')"
                                                        variant="outlined" density="comfortable" hide-details="auto" prepend-inner-icon="mdi-text-box-outline"
                                                        @update:model-value="updateNodeProperty('agentMaxInputLength', $event)" :readonly="isViewMode" class="config-input"
                                                        :hint="$t('_hintMaxCharsAllowed')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Response Options -->
                            <v-expansion-panel value="response" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                            <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('responseOptions') }}</div>
                                            <div class="panel-subtitle">{{ $t('_configureHowResponsesGenerated') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <div class="switch-option-compact mb-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28" color="grey-lighten-1" class="me-3">
                                                        <v-icon color="grey-darken-1" size="16">mdi-text-box-outline</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">{{ $t('streamResponse') }}</div>
                                                        <div class="switch-description" style="color: #FF9800;">{{ $t('controlledByOrchestrator') }}</div>
                                                    </div>
                                                </div>
                                                <v-switch disabled hide-details density="compact" inset />
                                            </div>
                                        </div>
                                        <div class="switch-option-compact">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28" color="grey-lighten-1" class="me-3">
                                                        <v-icon color="grey-darken-1" size="16">mdi-code-json</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">{{ $t('jsonResponse') }}</div>
                                                        <div class="switch-description" style="color: #FF9800;">{{ $t('controlledByOrchestrator') }}</div>
                                                    </div>
                                                </div>
                                                <v-switch disabled hide-details density="compact" inset />
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Memory Settings -->
                            <v-expansion-panel value="memory" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #00BCD4, #0097A7);">
                                            <v-icon icon="mdi-brain" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('memorySettings') }}</div>
                                            <div class="panel-subtitle">{{ $t('_configureConvMemoryContext') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('conversationMemory') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.agentMemoryTurn" type="number" min="0" :label="$t('turnCount')"
                                                        variant="outlined" density="comfortable" hide-details="auto" prepend-inner-icon="mdi-history"
                                                        @update:model-value="updateNodeProperty('agentMemoryTurn', $event)" :readonly="isViewMode" class="config-input"
                                                        :hint="$t('_hintTurnsRemember')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.agentMemoryDuration" type="number" min="0" :label="$t('durationMinutes')"
                                                        variant="outlined" density="comfortable" hide-details="auto" prepend-inner-icon="mdi-timer-outline"
                                                        @update:model-value="updateNodeProperty('agentMemoryDuration', $event)" :readonly="isViewMode" class="config-input"
                                                        :hint="$t('_hintMemoryActive')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('memoryTypes') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.agentShortTermMemory" type="number" min="1" :label="$t('shortTermMemory')"
                                                        variant="outlined" density="comfortable" hide-details="auto" prepend-inner-icon="mdi-memory"
                                                        @update:model-value="updateNodeProperty('agentShortTermMemory', $event)" :readonly="isViewMode" class="config-input"
                                                        :hint="$t('_hintContextWindow')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col>
                                                    <div class="switch-option-compact mb-4">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg" :class="{ 'switch-active-bg': nodeInspector.agentLongTermMemory }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28" :color="nodeInspector.agentLongTermMemory ? 'success' : 'grey-lighten-1'" class="me-3">
                                                                    <v-icon :color="nodeInspector.agentLongTermMemory ? 'white' : 'grey-darken-1'" size="16">mdi-database</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">{{ $t('longTermMemory') }}</div>
                                                                    <div class="switch-description">{{ $t('persistentStorage') }}</div>
                                                                </div>
                                                            </div>
                                                            <v-switch v-model="nodeInspector.agentLongTermMemory" color="success" hide-details density="compact" inset
                                                                @update:model-value="updateNodeProperty('agentLongTermMemory', $event)" :readonly="isViewMode" />
                                                        </div>
                                                    </div>
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- AI Parameters -->
                            <v-expansion-panel value="aiparams" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #9C27B0, #7B1FA2);">
                                            <v-icon icon="mdi-cog" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('aiParameters') }}</div>
                                            <div class="panel-subtitle">{{ $t('_fineTuneModelBehavior') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('coreParameters') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.agentTemperature" type="number" step="0.1" min="0" max="1" :label="$t('temperature')"
                                                        variant="outlined" density="comfortable" hide-details="auto" prepend-inner-icon="mdi-thermometer"
                                                        @update:model-value="updateNodeProperty('agentTemperature', $event)" :readonly="isViewMode" class="config-input"
                                                        :hint="$t('_hintCreativityLevel')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.agentTopP" type="number" step="0.1" min="0" max="1" :label="$t('topP')"
                                                        variant="outlined" density="comfortable" hide-details="auto" prepend-inner-icon="mdi-filter-variant"
                                                        @update:model-value="updateNodeProperty('agentTopP', $event)" :readonly="isViewMode" class="config-input"
                                                        :hint="$t('_hintTopP')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('penaltySettings') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.agentFrequencyPenalty" type="number" step="0.1" min="0" max="2" :label="$t('frequencyPenalty')"
                                                        variant="outlined" density="comfortable" hide-details="auto" prepend-inner-icon="mdi-repeat-off"
                                                        @update:model-value="updateNodeProperty('agentFrequencyPenalty', $event)" :readonly="isViewMode" class="config-input"
                                                        :hint="$t('_hintReduceFrequentTokens')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.agentPresencePenalty" type="number" step="0.1" min="0" max="2" :label="$t('presencePenalty')"
                                                        variant="outlined" density="comfortable" hide-details="auto" prepend-inner-icon="mdi-minus-circle-outline"
                                                        @update:model-value="updateNodeProperty('agentPresencePenalty', $event)" :readonly="isViewMode" class="config-input"
                                                        :hint="$t('_hintReduceAnyTokens')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">{{ $t('outputControl') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.agentMaxTokens" type="number" min="1" :label="$t('maxTokensResponse')"
                                                        variant="outlined" density="comfortable" hide-details="auto" prepend-inner-icon="mdi-text-box-check-outline"
                                                        @update:model-value="updateNodeProperty('agentMaxTokens', $event)" :readonly="isViewMode" class="config-input"
                                                        :hint="$t('_hintMaxResponseTokens')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model="nodeInspector.agentStopSequence" :label="$t('stopSequence')"
                                                        variant="outlined" density="comfortable" hide-details="auto" prepend-inner-icon="mdi-stop-circle-outline"
                                                        @update:model-value="updateNodeProperty('agentStopSequence', $event)" :readonly="isViewMode" class="config-input"
                                                        :hint="$t('_hintStopSequence')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Prompt Settings -->
                            <v-expansion-panel value="prompt" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #607d8b, #455a64);">
                                            <v-icon icon="mdi-message-text" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('promptSettings') }}</div>
                                            <div class="panel-subtitle">{{ $t('_timeContextTransactionOptions') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <div class="config-section mb-4">
                                            <h6 class="section-label mb-3">{{ $t('timeContext') }}</h6>
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-3 px-4 rounded-lg" :class="{ 'switch-active-bg': nodeInspector.agentEnableTime }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="32" :color="nodeInspector.agentEnableTime ? 'success' : 'grey-lighten-1'" class="me-3">
                                                            <v-icon :color="nodeInspector.agentEnableTime ? 'white' : 'grey-darken-1'" size="18">mdi-clock-outline</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">{{ $t('timeContext') }}</div>
                                                            <div class="switch-description">{{ $t('_enableTimeAwarenessAi') }}</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.agentEnableTime" color="success" hide-details density="compact" inset
                                                        @update:model-value="updateNodeProperty('agentEnableTime', $event)" :readonly="isViewMode" />
                                                </div>
                                            </div>
                                            <v-select v-if="nodeInspector.agentEnableTime" v-model="nodeInspector.agentTimeZone" :items="DDTimeZone" item-title="name" item-value="value" :label="$t('timeZone')"
                                                variant="outlined" density="comfortable" hide-details="auto" prepend-inner-icon="mdi-earth"
                                                @update:model-value="updateNodeProperty('agentTimeZone', $event)" :readonly="isViewMode" class="config-input" clearable />
                                        </div>
                                        <div class="config-section mb-4">
                                            <h6 class="section-label mb-3">{{ $t('transactionIdContext') }}</h6>
                                            <div class="switch-option-compact">
                                                <div class="d-flex align-center justify-space-between py-3 px-4 rounded-lg" :class="{ 'switch-active-bg': nodeInspector.agentEnableTransactionId }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="32" :color="nodeInspector.agentEnableTransactionId ? 'primary' : 'grey-lighten-1'" class="me-3">
                                                            <v-icon :color="nodeInspector.agentEnableTransactionId ? 'white' : 'grey-darken-1'" size="16">mdi-identifier</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">{{ $t('addTransactionIdToPrompt') }}</div>
                                                            <div class="switch-description">{{ $t('_includeTransactionIdAiContext') }}</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.agentEnableTransactionId" color="primary" hide-details density="compact" inset
                                                        @update:model-value="updateNodeProperty('agentEnableTransactionId', $event)" :readonly="isViewMode" />
                                                </div>
                                            </div>
                                        </div>
                                        <v-alert type="info" variant="tonal" density="compact" class="mt-2" style="font-size: 12px;">
                                            System prompt uses the <strong>{{ $t('personaPrompt') }}</strong> configured above. Time and TransactionID context will be appended automatically.
                                        </v-alert>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </div>

                    <div v-else-if="selectedNode.data.type === 'tool'" class="form-section agent-architect-inspector tool-config-inspector">
                        <div class="aa-config-hero aa-tool-hero">
                            <v-icon size="24">mdi-wrench</v-icon>
                            <div>
                                <div class="aa-hero-title">{{ $t('toolConfiguration') }}</div>
                                <div class="aa-hero-subtitle">{{ $t('_configureToolReact') }}</div>
                            </div>
                        </div>

                        <v-text-field v-model="nodeInspector.toolName" :label="$t('toolName')" variant="outlined" density="compact" hide-details="auto" prepend-inner-icon="mdi-label"
                            @update:model-value="updateNodeProperty('toolName', $event)" :readonly="isViewMode" class="aa-field mb-1" />
                        <div class="aa-field-hint mb-4">{{ $t('_shortDescNameTool') }}</div>
                        <v-textarea v-model="nodeInspector.toolDescription" :label="$t('toolDescription')" variant="outlined" density="compact" hide-details="auto" rows="4" auto-grow
                            @update:model-value="updateNodeProperty('toolDescription', $event)" :readonly="isViewMode" class="aa-field mb-1" />
                        <div class="aa-field-hint mb-3">{{ $t('_helpsAgentDecideTool') }}</div>
                        <v-select v-model="nodeInspector.toolType" :items="toolTypes" item-title="text" item-value="value" :label="$t('toolType')" variant="outlined" density="compact" hide-details="auto" prepend-inner-icon="mdi-cog"
                            @update:model-value="updateNodeProperty('toolType', $event)" :readonly="isViewMode" class="aa-field mb-4" />

                        <template v-if="nodeInspector.toolType === 'master'">
                            <div class="aa-section-heading mb-3">{{ $t('masterToolSelection') }}</div>
                            <v-autocomplete v-model="nodeInspector.masterToolId" :items="agentToolOptions" item-title="name" item-value="id" :label="$t('selectTool')" variant="outlined" density="compact" hide-details="auto" prepend-inner-icon="mdi-briefcase" clearable
                                :readonly="isViewMode" @update:model-value="updateAgentToolSelection" class="aa-field mb-4" />
                            <v-alert type="info" variant="tonal" density="compact" class="aa-info-alert">
                                <strong>{{ $t('toolUsageColon') }}</strong><br />
                                Connect this tool to an Agent node's bottom handle.<br />
                                The agent will decide when to use this tool.
                            </v-alert>
                        </template>

                        <template v-if="nodeInspector.toolType === 'api'">
                            <div class="aa-section-heading mb-3">{{ $t('customApiCall') }}</div>
                            <v-text-field v-model="nodeInspector.endpoint" :label="$t('apiEndpoint')" variant="outlined" density="compact" hide-details="auto" prepend-inner-icon="mdi-link-variant"
                                @update:model-value="updateNodeProperty('endpoint', $event)" :readonly="isViewMode" class="aa-field mb-3" />
                            <v-select v-model="nodeInspector.method" :items="['GET', 'POST', 'PUT', 'PATCH', 'DELETE']" :label="$t('httpMethod')" variant="outlined" density="compact" hide-details="auto" prepend-inner-icon="mdi-web"
                                @update:model-value="updateNodeProperty('method', $event)" :readonly="isViewMode" class="aa-field mb-3" />
                            <div class="d-flex align-center justify-space-between mb-2">
                                <div class="aa-section-heading mb-0">{{ $t('headersLabel') }}</div>
                                <v-btn size="small" variant="tonal" color="primary" @click="addToolHeader" :disabled="isViewMode" prepend-icon="mdi-plus">{{ $t('add') }}</v-btn>
                            </div>
                            <div v-for="(header, index) in nodeInspector.headers" :key="index" class="d-flex ga-2 mb-2">
                                <v-text-field v-model="header.key" :label="$t('Key')" variant="outlined" density="compact" hide-details="auto" @update:model-value="syncToolHeaders" :readonly="isViewMode" />
                                <v-text-field v-model="header.value" :label="$t('valueLabel')" variant="outlined" density="compact" hide-details="auto" @update:model-value="syncToolHeaders" :readonly="isViewMode" />
                                <v-btn icon size="small" variant="text" color="error" @click="removeToolHeader(index)" :disabled="isViewMode"><v-icon size="16">mdi-delete</v-icon></v-btn>
                            </div>
                            <v-textarea v-model="nodeInspector.body" :label="$t('jsonRequestBody')" variant="outlined" density="compact" hide-details="auto" rows="5" auto-grow prepend-inner-icon="mdi-code-json"
                                @update:model-value="updateNodeProperty('body', $event)" :readonly="isViewMode" class="aa-field" />
                        </template>

                        <template v-if="nodeInspector.toolType === 'text'">
                            <div class="aa-section-heading mb-3">{{ $t('textInformation') }}</div>
                            <v-textarea v-model="nodeInspector.textContent" :label="$t('textContent')" variant="outlined" density="compact" hide-details="auto" rows="6" auto-grow prepend-inner-icon="mdi-text-box"
                                @update:model-value="updateNodeProperty('textContent', $event)" :readonly="isViewMode" class="aa-field" />
                        </template>
                    </div>

                    <div v-else-if="selectedNode.data.type === 'flowTool'" class="form-section agent-architect-inspector flow-tool-config-inspector">
                        <div class="aa-config-hero aa-flow-tool-hero">
                            <v-icon size="24">mdi-sitemap</v-icon>
                            <div>
                                <div class="aa-hero-title">{{ $t('flowToolConfiguration') }}</div>
                                <div class="aa-hero-subtitle">{{ $t('_createCustomToolFlow') }}</div>
                            </div>
                        </div>

                        <v-text-field v-model="nodeInspector.flowToolName" :label="$t('flowToolName')" variant="outlined" density="compact" hide-details="auto" prepend-inner-icon="mdi-label"
                            @update:model-value="updateNodeProperty('flowToolName', $event)" :readonly="isViewMode" class="aa-field mb-1" />
                        <div class="aa-field-hint mb-4">{{ $t('_nameHelpsAgentDecide') }}</div>
                        <v-textarea v-model="nodeInspector.flowToolDescription" :label="$t('toolDescription')" variant="outlined" density="compact" hide-details="auto" rows="4" auto-grow
                            @update:model-value="updateNodeProperty('flowToolDescription', $event)" :readonly="isViewMode" class="aa-field mb-1" />
                        <div class="aa-field-hint mb-4">{{ $t('_helpsAgentUnderstandTool') }}</div>

                        <div class="aa-internal-flow-card mb-3">
                            <div class="d-flex align-center">
                                <v-icon color="indigo" size="24" class="me-3">mdi-chart-timeline-variant</v-icon>
                                <div>
                                    <div class="aa-internal-title">{{ $t('internalFlow') }}</div>
                                    <div class="aa-internal-subtitle">{{ getInternalProcessingNodeCount(nodeInspector.internalNodes) }} nodes configured</div>
                                </div>
                            </div>
                            <v-chip size="small" :color="isFlowToolValid() ? 'success' : 'warning'" variant="tonal" class="aa-internal-status">
                                <v-icon size="14" class="me-1">{{ isFlowToolValid() ? 'mdi-check' : 'mdi-alert' }}</v-icon>
                                {{ isFlowToolValid() ? 'Ready' : 'Incomplete' }}
                            </v-chip>
                        </div>

                        <v-btn block color="indigo" variant="flat" prepend-icon="mdi-pencil-box-outline" @click="openFlowToolEditor" :disabled="isViewMode" class="aa-edit-internal-flow-btn mb-4">
                            Edit Internal Flow
                        </v-btn>

                        <v-alert v-if="!isFlowToolValid()" type="warning" variant="tonal" density="compact" class="aa-warning-alert mb-4">
                            <strong>{{ $t('flowIncompleteColon') }}</strong><br />
                            The internal flow needs a complete path from Input to Output node.
                        </v-alert>

                        <v-alert type="info" variant="tonal" density="compact" class="aa-info-alert">
                            <strong>{{ $t('howFlowToolWorksColon') }}</strong><br />
                            - Agent receives user input and decides to use this tool<br />
                            - Input text is passed to the internal flow<br />
                            - Internal flow processes the input and returns the Flow Output result
                        </v-alert>
                    </div>

                    <div v-else-if="selectedNode.data.type === 'flowInput'" class="form-section">
                        <h6 class="section-title">{{ $t('flowInput') }}</h6>
                        <v-text-field v-model="nodeInspector.label" :label="$t('labelField')" variant="outlined" density="compact" hide-details="auto"
                            prepend-inner-icon="mdi-login-variant" @update:model-value="updateNodeProperty('label', $event)" :readonly="isViewMode" class="modern-input" />
                    </div>

                    <div v-else-if="selectedNode.data.type === 'flowOutput'" class="form-section">
                        <h6 class="section-title">{{ $t('flowOutput') }}</h6>
                        <v-textarea v-model="nodeInspector.outputTemplate" :label="$t('outputTemplate')" variant="outlined" density="compact" hide-details="auto" rows="4"
                            prepend-inner-icon="mdi-logout-variant" @update:model-value="updateNodeProperty('outputTemplate', $event)" :readonly="isViewMode" class="modern-input" />
                    </div>

                    <div v-else-if="selectedNode.data.type === 'intentAI'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-brain" size="24" color="primary" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold text-primary mb-0">{{ $t('intentAiModelConfiguration') }}</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">Configure your Intent AI model settings and
                                parameters</p>
                        </div>

                        <!-- Action Button -->
                        <div class="d-flex justify-end mb-4">
                            <v-btn @click="openLoadFromModelDialog" color="success" variant="tonal" size="small"
                                prepend-icon="mdi-download" :disabled="isViewMode" class="load-model-btn">
                                Load from Model
                            </v-btn>
                        </div>

                        <!-- Intent AI Model Configuration Panels -->
                        <v-expansion-panels v-model="intentaiModelConfigPanels" multiple class="config-panels elevation-0"
                            variant="accordion">
                            <!-- Basic Information Panel -->
                            <v-expansion-panel value="intentaibasic" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4">
                                            <v-icon icon="mdi-information" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('basicInfo') }}</div>
                                            <div class="panel-subtitle">{{ $t('_modelAndServiceDetails') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <!-- Model -->
                                        <div class="config-section mb-6">
                                            <h6 class="section-label mb-3">{{ $t('serviceConfiguration') }}</h6>
                                            <v-row>
                                                <div v-if="selectedIntentAiModelInfo" class="model-provider-details mt-2">
                                                    <div class="model-provider-detail">
                                                        <v-icon icon="mdi-server-network" size="16" color="primary" class="me-2"></v-icon>
                                                        <span class="model-provider-label">{{ $t('platform') }}</span>
                                                        <span class="model-provider-value">{{ selectedIntentAiModelInfo.platform || '-' }}</span>
                                                    </div>
                                                    <div class="model-provider-detail">
                                                        <v-icon icon="mdi-cloud-outline" size="16" color="primary" class="me-2"></v-icon>
                                                        <span class="model-provider-label">{{ $t('Provider') }}</span>
                                                        <span class="model-provider-value">{{ selectedIntentAiModelInfo.provider || '-' }}</span>
                                                    </div>
                                                </div>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.intentaiModel" :items="modelSelectionOptions"
                                                        item-title="modelDisplay" item-value="modelName" :label="$t('modelRequired')"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-brain"
                                                        @update:model-value="updateNodeProperty('intentaiModel', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :hint="$t('_hintSelectModelVersion')" persistent-hint required clearable>
                                                        <template v-slot:item="{ props, item }">
                                                            <v-list-item v-bind="props" :title="getModelOptionTitle(item.raw)">
                                                                <template v-slot:subtitle>
                                                                    <div class="model-option-subtitle">
                                                                        <span class="model-option-meta">
                                                                            <span class="model-option-meta-value">{{ item.raw.platform || '-' }}</span>
                                                                        </span>
                                                                        <span class="model-option-meta-separator">/</span>
                                                                        <span class="model-option-meta">
                                                                            <span class="model-option-meta-value">{{ item.raw.provider || '-' }}</span>
                                                                        </span>
                                                                    </div>
                                                                </template>
                                                            </v-list-item>
                                                        </template>
                                                        <template v-slot:selection="{ item }">
                                                            <span class="model-selection-title">{{ getModelOptionTitle(item.raw) }}</span>
                                                        </template>
                                                    </v-autocomplete>
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <!-- Behavior Settings -->
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">{{ $t('behaviorSettings') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.intentaiGender"
                                                        :items="[{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }]"
                                                        item-title="name" item-value="id" :label="$t('gender')"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-account"
                                                        @update:model-value="updateNodeProperty('intentaiGender', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :hint="$t('_hintGenderPreference')" persistent-hint
                                                        :model-value="nodeInspector.intentaiGender || 'male'" />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiTypingSpeed"
                                                        :label="$t('typingSpeed')" type="number" min="0" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-keyboard-outline"
                                                        @update:model-value="updateNodeProperty('intentaiTypingSpeed', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        :hint="$t('_hintCharsPerSecond')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiMaxInputLength"
                                                        :label="$t('maxInputLength')" type="number" min="0" max="200000"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-outline"
                                                        @update:model-value="updateNodeProperty('intentaiMaxInputLength', $event)"
                                                        @keypress="maxNumberpositive($event)"
                                                        @focusin="validateAndCorrectValue($event, 1, 200000)"
                                                        @focusout="validateAndCorrectValue($event, 1, 200000)"
                                                        @keydown.enter="validateAndCorrectValue($event, 1, 200000)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :hint="$t('_hintMaxCharsAllowed')"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Response Options Panel -->
                            <v-expansion-panel value="intentairesponse" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                            <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('responseOptions') }}</div>
                                            <div class="panel-subtitle">{{ $t('_configureHowResponsesGenerated') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <!-- Stream Response -->
                                        <div class="switch-option-compact mb-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.intentaiStreamResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.intentaiStreamResponse ? 'primary' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.intentaiStreamResponse ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-text-box-outline</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">{{ $t('streamResponse') }}</div>
                                                        <div class="switch-description">Progressive text generation
                                                        </div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.intentaiStreamResponse" color="primary"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('intentaiStreamResponse', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>

                                        <!-- Time Context -->
                                        <div class="switch-option-compact">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.intentaiEnableTime }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.intentaiEnableTime ? 'success' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.intentaiEnableTime ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-clock-outline</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">{{ $t('timeContext') }}</div>
                                                        <div class="switch-description">{{ $t('enableTimeAwareness') }}</div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.intentaiEnableTime" color="success"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('intentaiEnableTime', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>

                                            <!-- Time Zone Dropdown (shown when enableTime is true) -->
                                            <div v-if="nodeInspector.intentaiEnableTime" class="mt-3">
                                                <v-select v-model="nodeInspector.intentaiTimeZone" :items="DDTimeZone"
                                                    item-title="name" item-value="value" :label="$t('timeZone')"
                                                    variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-earth"
                                                    @update:model-value="updateNodeProperty('intentaiTimeZone', $event)"
                                                    class="config-input" :readonly="isViewMode"
                                                    :hint="$t('_selectYourTimezone')" persistent-hint
                                                    :placeholder="$t('_selectYourTimezone')">
                                                    <template v-slot:selection="{ item }">
                                                        <div class="d-flex align-center">
                                                            <v-icon small color="primary"
                                                                class="me-2">mdi-map-marker</v-icon>
                                                            <span class="font-weight-medium">{{ item.raw.name }}</span>
                                                            <v-chip size="x-small" color="grey-darken-1"
                                                                text-color="white" class="ml-2">
                                                                {{ item.raw.value.split('/')[1].replace('_', ' ') }}
                                                            </v-chip>
                                                        </div>
                                                    </template>
                                                </v-select>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Memory Settings Panel -->
                            <v-expansion-panel value="intentaimemory" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #00BCD4, #0097A7);">
                                            <v-icon icon="mdi-brain" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('memorySettings') }}</div>
                                            <div class="panel-subtitle">{{ $t('_configureConvMemoryContext') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('conversationMemory') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiMemoryTurn"
                                                        :label="$t('turnCount')" type="number" min="0" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-history"
                                                        @update:model-value="updateNodeProperty('intentaiMemoryTurn', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        :hint="$t('_hintTurnsRemember')"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiMemoryDuration"
                                                        :label="$t('durationMinutes')" type="number" min="0"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-outline"
                                                        @update:model-value="updateNodeProperty('intentaiMemoryDuration', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :hint="$t('_hintMemoryActive')"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('memoryTypes') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiShortTermMemory"
                                                        :label="$t('shortTermMemory')" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-memory"
                                                        @update:model-value="updateNodeProperty('intentaiShortTermMemory', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :hint="$t('_hintContextWindow')"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col>
                                                    <!-- Long-Term Memory Switch -->
                                                    <div class="switch-option-compact mb-4">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                            :class="{ 'switch-active-bg': nodeInspector.intentaiLongTermMemory }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28"
                                                                    :color="nodeInspector.intentaiLongTermMemory ? 'success' : 'grey-lighten-1'"
                                                                    class="me-3">
                                                                    <v-icon
                                                                        :color="nodeInspector.intentaiLongTermMemory ? 'white' : 'grey-darken-1'"
                                                                        size="16">mdi-database</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">{{ $t('longTermMemory') }}</div>
                                                                    <div class="switch-description">Persistent storage
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <v-switch v-model="nodeInspector.intentaiLongTermMemory"
                                                                color="success" hide-details density="compact"
                                                                @update:model-value="updateNodeProperty('intentaiLongTermMemory', $event)"
                                                                :readonly="isViewMode" inset />
                                                        </div>
                                                    </div>
                                                </v-col>
                                            </v-row>

                                        </div>

                                        <!-- Multi-turn Input Configuration -->
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('multiTurnInputConfiguration') }}</h6>
                                            <v-row>
                                                <v-col>
                                                    <!-- Enable Multi-Turn Input Switch -->
                                                    <div class="switch-option-compact mb-3">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                            :class="{ 'switch-active-bg': nodeInspector.intentaiIsChainMassage }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28"
                                                                    :color="nodeInspector.intentaiIsChainMassage ? 'indigo' : 'grey-lighten-1'"
                                                                    class="me-3">
                                                                    <v-icon
                                                                        :color="nodeInspector.intentaiIsChainMassage ? 'white' : 'grey-darken-1'"
                                                                        size="16">mdi-chat-processing</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">Enable Multi-Turn Input
                                                                    </div>
                                                                    <div class="switch-description">Allow continuous
                                                                        conversation</div>
                                                                </div>
                                                            </div>
                                                            <v-switch v-model="nodeInspector.intentaiIsChainMassage"
                                                                color="indigo" hide-details density="compact"
                                                                @update:model-value="updateNodeProperty('intentaiIsChainMassage', $event)"
                                                                :readonly="isViewMode" inset />
                                                        </div>
                                                    </div>
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiLenChainMassage"
                                                        :label="$t('sequence')" type="number" min="1" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-numeric"
                                                        @update:model-value="updateNodeProperty('intentaiLenChainMassage', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :disabled="!nodeInspector.intentaiIsChainMassage"
                                                        :hint="$t('_hintTurnsInSequence')" persistent-hint
                                                        clearable />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiTimeSecondChainMassage"
                                                        :label="$t('durationSeconds')" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer"
                                                        @update:model-value="updateNodeProperty('intentaiTimeSecondChainMassage', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :disabled="!nodeInspector.intentaiIsChainMassage"
                                                        :hint="$t('_hintMultiTurnDuration')" persistent-hint
                                                        clearable />
                                                </v-col>
                                            </v-row>
                                        </div>


                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Parameters Panel -->
                            <v-expansion-panel value="intentaiparameters" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #9C27B0, #7B1FA2);">
                                            <v-icon icon="mdi-cog" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('aiParameters') }}</div>
                                            <div class="panel-subtitle">{{ $t('_fineTuneModelBehavior') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('coreParameters') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiTemperature"
                                                        :label="$t('temperatureRequired')" type="number" step="0.1" min="0" max="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-thermometer"
                                                        @update:model-value="updateNodeProperty('intentaiTemperature', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        :hint="$t('_hintCreativityLevel')"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiTopP" :label="$t('topP')"
                                                        type="number" step="0.1" min="0" max="1" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-filter-variant"
                                                        @update:model-value="updateNodeProperty('intentaiTopP', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        :hint="$t('_hintTopP')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('penaltySettings') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiFrequencyPenalty"
                                                        :label="$t('frequencyPenalty')" type="number" step="0.1" min="0"
                                                        max="2" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-repeat-off"
                                                        @update:model-value="updateNodeProperty('intentaiFrequencyPenalty', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        :hint="$t('_hintReduceFrequentTokens')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiPresencePenalty"
                                                        :label="$t('presencePenalty')" type="number" step="0.1" min="0"
                                                        max="2" variant="outlined" density="comfortable"
                                                        hide-details="auto"
                                                        prepend-inner-icon="mdi-minus-circle-outline"
                                                        @update:model-value="updateNodeProperty('intentaiPresencePenalty', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :hint="$t('_hintReduceAnyTokens')"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section">
                                            <h6 class="section-label mb-3">{{ $t('outputControl') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiMaxTokens"
                                                        :label="$t('maxTokensResponse')" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-check-outline"
                                                        @update:model-value="updateNodeProperty('intentaiMaxTokens', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :hint="$t('_hintMaxResponseTokens')"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model="nodeInspector.intentaiStopSequence"
                                                        :label="$t('stopSequence')" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-stop-circle-outline"
                                                        @update:model-value="updateNodeProperty('intentaiStopSequence', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :hint="$t('_hintStopSequence')" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row v-if="intentaiModelSupportsReasoning">
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.intentaiReasoningEffort"
                                                        :items="reasonOptions" item-title="text" item-value="value"
                                                        :label="$t('reasoningEffort')" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-brain"
                                                        clearable
                                                        @update:model-value="updateNodeProperty('intentaiReasoningEffort', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :hint="$t('_tipReasoningDepth')" persistent-hint>
                                                        <template v-slot:selection="{ item }">
                                                            <v-chip size="small" :color="getReasoningColor(item.raw.value)"
                                                                text-color="white" class="px-3">
                                                                {{ item.raw.text }}
                                                            </v-chip>
                                                        </template>
                                                    </v-select>
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Line Configuration Panel -->
                            <v-expansion-panel value="intentailine" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #4CAF50, #388E3C);">
                                            <v-icon icon="mdi-message" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('lineConfiguration') }}</div>
                                            <div class="panel-subtitle">{{ $t('_lineMessagingPlatformSettings') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('displayOptions') }}</h6>

                                            <!-- Show Name -->
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.intentaiShowLineName }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.intentaiShowLineName ? 'info' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.intentaiShowLineName ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-account-circle</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">{{ $t('showName') }}</div>
                                                            <div class="switch-description">{{ $t('displayUserName') }}</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.intentaiShowLineName" color="info"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('intentaiShowLineName', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>
                                            <!-- Loading Indicator -->
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.intentaiLoadingBar }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.intentaiLoadingBar ? 'success' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.intentaiLoadingBar ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-progress-clock</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">{{ $t('loadingIndicator') }}</div>
                                                            <div class="switch-description">{{ $t('showTypingAnimation') }}</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.intentaiLoadingBar" color="success"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('intentaiLoadingBar', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>

                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('loadingAndDuration') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.intentaiLoadingBarDuration"
                                                        :items="[10, 20, 30, 40, 50, 60]"
                                                        :label="$t('loadingDurationSeconds')" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-sand"
                                                        @update:model-value="updateNodeProperty('intentaiLoadingBarDuration', $event)"
                                                        class="config-input" :disabled="!nodeInspector.intentaiLoadingBar"
                                                        :readonly="isViewMode"
                                                        :hint="$t('_hintLoadingDuration')" persistent-hint
                                                        clearable />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">{{ $t('monitoring') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model="nodeInspector.intentaiMonitorLineUserId"
                                                        :label="$t('monitorLineUserId')" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-monitor-eye"
                                                        @update:model-value="updateNodeProperty('intentaiMonitorLineUserId', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :hint="$t('_hintLineUserId')"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.intentaiMonitorSocialID"
                                                        :items="listMonitorLineName" :label="$t('monitorLineName')"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-chat" item-title="name" item-value="id"
                                                        @update:model-value="updateNodeProperty('intentaiMonitorSocialID', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :hint="$t('_hintSelectMonitorLine')" persistent-hint clearable />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section">
                                            <h6 class="section-label mb-3">{{ $t('contentFiltering') }}</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-toolbar-title style="font-size: 16px">
                                                        Forbidden word
                                                    </v-toolbar-title>
                                                </v-col>
                                            </v-row>
                                            <div class="pr-1" style="
                                                    border: #dfe0e2 1px solid;
                                                    background: #fff;
                                                    margin-top: 10px;
                                                    border-radius: 8px;
                                                ">
                                                <v-row v-for="(item, index) in nodeInspector.intentaiForbiddenWords || ['']"
                                                    :key="index" style="margin: 0px">
                                                    <v-col cols="12" style="padding: 0px">
                                                        <div style="display: flex; align-items: center">
                                                            <div style="
                                                                    height: 50px;
                                                                    background: #f8f8f8;
                                                                    padding: 14px 20px 0 20px;
                                                                ">
                                                                {{ index + 1 }}
                                                            </div>
                                                            <v-textarea variant="solo" style="
                                                                    overflow: hidden;
                                                                    overflow-wrap: break-word;
                                                                    resize: none;
                                                                    height: 50px;
                                                                    width: 98%;
                                                                    border-radius: 0px;
                                                                    font-family: Fira code, Fira Mono, Inconsolata,
                                                                        DejaVu Sans Mono, Courier, monospace;
                                                                    font-size: 14px;
                                                                "
                                                                :model-value="(nodeInspector.intentaiForbiddenWords && nodeInspector.intentaiForbiddenWords[index]) || ''"
                                                                :placeholder="$t('_phEnterWordPhrase')"
                                                                @keypress="isEnterIntentaiForbidden($event, index)"
                                                                @keyup.delete="checkDeleteIntentaiForbiddenWord(index, (nodeInspector.intentaiForbiddenWords && nodeInspector.intentaiForbiddenWords[index]) || '')"
                                                                @update:model-value="updateIntentaiForbiddenWordProperty(index, $event)"
                                                                :readonly="isViewMode" />
                                                            <v-btn icon="mdi-attachment" variant="text"
                                                                v-show="(nodeInspector.intentaiForbiddenWords || []).length > 1 && index + 1 < (nodeInspector.intentaiForbiddenWords || []).length"
                                                                @click="removeIntentaiForbiddenWordDirect(index)"
                                                                :disabled="isViewMode">
                                                                <v-tooltip location="top" :open-delay="500">
                                                                    <template v-slot:activator="{ props }">
                                                                        <v-icon color="error" style="cursor: pointer"
                                                                            v-bind="props">
                                                                            mdi-delete-forever
                                                                        </v-icon>
                                                                    </template>
                                                                    <span>{{ $t('delete') }}</span>
                                                                </v-tooltip>
                                                            </v-btn>
                                                        </div>
                                                    </v-col>
                                                </v-row>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- System Prompt Panel -->
                            <v-expansion-panel value="intentaiprompt" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #607D8B, #455A64);">
                                            <v-icon icon="mdi-message-text" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">{{ $t('systemPrompt') }}</div>
                                            <div class="panel-subtitle">{{ $t('_defineAiBehaviorPersonality') }}</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="prompt-section">
                                            <div class="d-flex align-center mb-4">
                                                <v-avatar size="36" color="info" class="me-3">
                                                    <v-icon color="white" size="20">mdi-robot-outline</v-icon>
                                                </v-avatar>
                                                <div>
                                                    <h6 class="text-h6 mb-1">{{ $t('aiPersonalityInstructions') }}</h6>
                                                    <p class="text-body-2 text-medium-emphasis mb-0">
                                                        Define how your AI should behave, respond, and interact with
                                                        users
                                                    </p>
                                                </div>
                                            </div>
                                            <h3 class="text-h6 d-flex align-center mb-3" style="font-size: 16px !important;">
                                                <v-icon icon="mdi-pencil-outline" size="small"
                                                    class="me-2"></v-icon>
                                                Configure system message for your AI
                                            </h3>

                                            <!-- Prompt Template Header -->
                                            <div class="mb-2" style="display:flex;flex-direction: column;align-items: flex-end;">
                                                <div style="display: flex; align-items: center;">
                                                    <v-tooltip location="top">
                                                        <template v-slot:activator="{ props }">
                                                            <v-icon icon="mdi-information-outline" size="small"
                                                                color="grey" class="me-3" v-bind="props"></v-icon>
                                                        </template>
                                                        <span>{{ $t('_configureAddlPromptsTemplates') }}</span>
                                                    </v-tooltip>
                                                    <v-btn
                                                        v-if="!isViewMode && DDGroupPromptTemplate && DDGroupPromptTemplate.length > 0"
                                                        variant="outlined" color="primary"
                                                        prepend-icon="mdi-playlist-plus" @click="showIntentaiPromptTemplate"
                                                        class="text-none font-weight-medium" size="small">
                                                        Show Templates
                                                    </v-btn>
                                                </div>
                                            </div>

                                            <!-- Main editor and templates layout -->
                                            <v-row>
                                                <!-- Editor area (full width) -->
                                                <v-col cols="12" class="prompt-editor-col">
                                                    <v-card variant="outlined" class="prompt-editor-card">
                                                        <v-card-text class="px-2 py-1">
                                                            <v-textarea v-model="nodeInspector.intentaiSystemPrompt" label=""
                                                                variant="plain" counter auto-grow rows="25"
                                                                @update:model-value="updateNodeProperty('intentaiSystemPrompt', $event)"
                                                                :readonly="isViewMode"
                                                                :placeholder="$t('_phEnterSystemMessage')"
                                                                class="prompt-textarea"
                                                                @contextmenu="onIntentaiContextMenu"></v-textarea>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-col>
                                            </v-row>

                                            <!-- Context menu for prompt templates -->
                                            <v-menu v-model="showIntentaiMenu" :activator="{ x, y }" location="bottom">
                                                <v-card min-width="250" elevation="3">
                                                    <v-list density="compact" class="py-0">
                                                        <v-list-subheader class="d-flex bg-grey-lighten-4 py-2">
                                                            <v-icon icon="mdi-text-box-plus-outline" class="me-2"
                                                                size="small"></v-icon>
                                                            Insert Template
                                                        </v-list-subheader>
                                                        <v-divider :thickness="1"
                                                            class="border-opacity-100"></v-divider>
                                                        <v-list-item v-for="(item, index) in filteredIntentaiPromptTemplates"
                                                            :key="index"
                                                            @click="setSelectIntentaiPromptTemplate(item); showIntentaiMenu = false;"
                                                            :value="item.name" class="template-list-item">
                                                            <template v-slot:prepend>
                                                                <v-icon size="small"
                                                                    icon="mdi-text-box-outline"></v-icon>
                                                            </template>
                                                            <template v-slot:title>
                                                                <span>{{ item.name }}</span>
                                                            </template>
                                                        </v-list-item>
                                                    </v-list>
                                                </v-card>
                                            </v-menu>

                                            <!-- Dialog for showing all templates -->
                                            <v-dialog v-model="dialogShowAllIntentai" max-width="550" scrollable>
                                                <v-card>
                                                    <v-card-title class="bg-primary text-white d-flex align-center">
                                                        <v-icon icon="mdi-text-box-multiple-outline"
                                                            class="me-2"></v-icon>
                                                        Prompt Templates
                                                    </v-card-title>
                                                    <v-divider :thickness="1" class="border-opacity-100"></v-divider>
                                                    <v-card-text style="max-height: 500px" class="pt-4">
                                                        <v-text-field v-model="intentaiTemplateSearchQuery"
                                                            :label="$t('searchTemplates')" prepend-inner-icon="mdi-magnify"
                                                            variant="outlined" density="compact" hide-details
                                                            class="mb-3" clearable></v-text-field>

                                                        <v-list density="compact" class="template-list-all pa-0">
                                                            <v-list-item v-for="(item, i) in searchedIntentaiTemplates" :key="i"
                                                                @click="setSelectIntentaiPromptTemplate(item)"
                                                                class="template-list-item" :ripple="true">
                                                                <template v-slot:prepend>
                                                                    <v-icon size="small" color="primary"
                                                                        icon="mdi-text-box-outline"></v-icon>
                                                                </template>
                                                                <template v-slot:title>
                                                                    <span>{{ item.name }}</span>
                                                                </template>
                                                                <template v-slot:subtitle>
                                                                    <span class="text-caption text-truncate">
                                                                        {{ truncatePrompt(item.prompt) }}
                                                                    </span>
                                                                </template>
                                                            </v-list-item>
                                                        </v-list>
                                                    </v-card-text>
                                                    <v-card-actions>
                                                        <v-spacer></v-spacer>
                                                        <v-btn color="blue darken-1" variant="text"
                                                            @click="dialogShowAllIntentai = false">
                                                            Close
                                                        </v-btn>
                                                    </v-card-actions>
                                                </v-card>
                                            </v-dialog>

                                            <!-- Main Prompt Template Dialog -->
                                            <v-dialog v-model="intentaiPromptTemplateDialog" max-width="800" scrollable>
                                                <v-card>
                                                    <v-card-title class="bg-primary text-white d-flex align-center">
                                                        <v-icon icon="mdi-text-box-multiple-outline" class="me-2"></v-icon>
                                                        Prompt Templates
                                                        <v-spacer></v-spacer>
                                                        <v-btn icon="mdi-close" variant="text" color="white" size="small" 
                                                            @click="intentaiPromptTemplateDialog = false"></v-btn>
                                                    </v-card-title>
                                                    
                                                    <v-card-text class="pa-4">
                                                        <!-- Template Group Selection -->
                                                        <v-autocomplete 
                                                            :label="$t('selectTemplateGroup')" 
                                                            :items="DDGroupPromptTemplate" 
                                                            item-title="name"
                                                            item-value="id" 
                                                            density="compact" 
                                                            clearable
                                                            variant="outlined" 
                                                            v-model="intentaiGroupPromptTemplate"
                                                            prepend-inner-icon="mdi-folder-outline" 
                                                            hide-details
                                                            class="mb-4">
                                                        </v-autocomplete>

                                                        <!-- Search Template -->
                                                        <v-text-field 
                                                            v-model="intentaiTemplateSearchQuery"
                                                            :label="$t('searchTemplates')" 
                                                            prepend-inner-icon="mdi-magnify"
                                                            variant="outlined" 
                                                            density="compact" 
                                                            hide-details
                                                            class="mb-4" 
                                                            clearable>
                                                        </v-text-field>

                                                        <!-- Templates List -->
                                                        <div style="max-height: 400px; overflow-y: auto;">
                                                            <div v-if="searchedIntentaiTemplates.length === 0" 
                                                                class="pa-4 text-center text-grey-darken-1">
                                                                <v-icon icon="mdi-playlist-remove" size="large" 
                                                                    class="d-block mx-auto mb-2"></v-icon>
                                                                <span>{{ $t('noTemplatesFound') }}</span>
                                                            </div>

                                                            <v-list v-else density="compact" class="pa-0">
                                                                <v-list-item 
                                                                    v-for="(item, i) in searchedIntentaiTemplates" 
                                                                    :key="i"
                                                                    @click="setSelectIntentaiPromptTemplate(item)"
                                                                    class="template-list-item ma-1" 
                                                                    :ripple="true"
                                                                    rounded>
                                                                    <template v-slot:prepend>
                                                                        <v-icon size="small" color="primary" 
                                                                            icon="mdi-text-box-outline"></v-icon>
                                                                    </template>
                                                                    <template v-slot:title>
                                                                        <span class="font-weight-medium">{{ item.name }}</span>
                                                                    </template>
                                                                    <template v-slot:subtitle>
                                                                        <span class="text-caption text-grey">
                                                                            {{ truncatePrompt(item.prompt) }}
                                                                        </span>
                                                                    </template>
                                                                    <template v-slot:append>
                                                                        <v-btn icon="mdi-plus" size="small" variant="text" 
                                                                            color="primary" @click.stop="setSelectIntentaiPromptTemplate(item)">
                                                                        </v-btn>
                                                                    </template>
                                                                </v-list-item>
                                                            </v-list>
                                                        </div>
                                                    </v-card-text>
                                                    
                                                    <v-card-actions>
                                                        <v-spacer></v-spacer>
                                                        <v-btn color="primary" variant="text" @click="intentaiPromptTemplateDialog = false">
                                                            Close
                                                        </v-btn>
                                                    </v-card-actions>
                                                </v-card>
                                            </v-dialog>

                                            <div class="prompt-tips mt-4">
                                                <v-card class="tips-card" color="blue-grey-lighten-5" flat>
                                                    <v-card-text class="pa-4">
                                                        <div class="d-flex align-start">
                                                            <v-icon color="info" size="20"
                                                                class="me-2 mt-1">mdi-lightbulb-outline</v-icon>
                                                            <div>
                                                                <div class="text-subtitle-2 font-weight-medium mb-2">
                                                                    Tips for
                                                                    better prompts:</div>
                                                                <ul class="text-body-2 text-medium-emphasis">
                                                                    <li>Be specific about the AI's role and expertise
                                                                    </li>
                                                                    <li>{{ $t('_defineToneStyle') }}</li>
                                                                    <li>{{ $t('_includeRestrictionsGuidelines') }}</li>
                                                                    <li>{{ $t('_specifyHandleUnknownInfo') }}</li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </v-card-text>
                                                </v-card>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>

                    </div>
                    <div v-else-if="selectedNode.data.type === 'intent'" class="form-section">
                        <h6 class="section-title">{{ $t('intentConfiguration') }}</h6>
                        <v-text-field v-model="nodeInspector.intentGroupName" :label="$t('intentHandlerName')"
                            variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('intentGroupName', $event)"
                            class="modern-input mb-4" :readonly="isViewMode" />

                        <!-- Intents Configuration -->
                        <div class="intents-section">
                            <div class="section-header mb-3">
                                <h6 class="section-title mb-0">{{ $t('intents') }}</h6>
                                <v-btn v-if="!isViewMode" size="small" color="primary" variant="tonal"
                                    @click="addInspectorIntent" class="add-btn">
                                    <v-icon left size="16">mdi-plus</v-icon>
                                    Add Intent
                                </v-btn>
                            </div>

                            <div v-for="(intent, intentIndex) in nodeInspector.intents" :key="intent.id"
                                class="intent-card mb-3">
                                <v-expansion-panels variant="accordion" class="intent-expansion">
                                    <v-expansion-panel>
                                        <v-expansion-panel-title>
                                            <div class="d-flex align-center w-100">
                                                <v-icon class="mr-2" color="primary" size="small">mdi-target</v-icon>
                                                <!-- Priority Number -->
                                                <span v-if="intent.priority || intent.priority === 0"
                                                    class="priority-number mr-2">
                                                    {{ intent.priority }}
                                                </span>
                                                <span class="font-weight-medium">
                                                    {{
                                                        intent.name || `Intent ${intentIndex
                                                        + 1}`
                                                    }}
                                                </span>
                                                <v-spacer></v-spacer>
                                                <v-btn v-if="!isViewMode" icon size="x-small" color="error"
                                                    variant="text" @click.stop="removeInspectorIntent(intentIndex)"
                                                    :disabled="nodeInspector.intents.length <= 1" class="ml-2">
                                                    <v-icon size="16">mdi-delete</v-icon>
                                                </v-btn>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <div class="intent-content pa-2">
                                                <!-- Intent Priority -->
                                                <v-text-field
                                                    v-model.number="nodeInspector.intents[intentIndex].priority"
                                                    :label="$t('priority')" type="number" variant="outlined" density="compact"
                                                    hide-details="auto" min="0" step="1" :placeholder="$t('enterPriority')"
                                                    @update:model-value="updateIntentData()"
                                                    @keypress="isNumberpositive($event)" class="modern-input mb-3"
                                                    :readonly="isViewMode">
                                                    <template v-slot:prepend-inner>
                                                        <v-icon size="small" color="orange">mdi-priority-high</v-icon>
                                                    </template>
                                                    <template v-slot:append-inner>
                                                        <v-tooltip location="top" max-width="200px">
                                                            <template v-slot:activator="{ props }">
                                                                <v-icon size="small" color="grey-darken-1"
                                                                    v-bind="props">
                                                                    mdi-information-outline
                                                                </v-icon>
                                                            </template>
                                                            <span>{{ $t('_priorityZeroHighest') }}</span>
                                                        </v-tooltip>
                                                    </template>
                                                </v-text-field>

                                                <!-- Intent Name -->
                                                <v-text-field v-model="nodeInspector.intents[intentIndex].name"
                                                    :label="$t('intentName')" variant="outlined" density="compact"
                                                    hide-details="auto" @update:model-value="updateIntentData()"
                                                    class="modern-input mb-3" :readonly="isViewMode" />

                                                <div
                                                    style="display: flex; align-items: center; justify-content: flex-start;margin-top: -15px;">
                                                    <v-switch v-model="nodeInspector.intents[intentIndex].useMemory"
                                                        hide-details density="compact" color="primary" inset
                                                        style="transform: scale(0.9); margin-left: 0px; margin-top: 10px;"
                                                        @update:model-value="updateIntentData()" class="mb-3"
                                                        :readonly="isViewMode" />
                                                    <span style="font-size: 16px;margin-left: 8px;">
                                                        Use memory
                                                    </span>
                                                </div>

                                                <div class="custom-divider"></div>

                                                <!-- Base Prompt Section -->
                                                <div style="margin-bottom: 36px;">
                                                    <!-- Base Prompt Toggle -->
                                                    <div
                                                        style="display: flex;align-items: center;justify-content: flex-end;">
                                                        <span
                                                            style="font-weight: 500;font-size: 12px;margin-right: 8px;">
                                                            Edit Base Prompt
                                                        </span>
                                                        <v-switch
                                                            v-model="nodeInspector.intents[intentIndex].enableBasePrompt"
                                                            color="primary" density="compact" hide-details
                                                            :readonly="isViewMode"
                                                            @update:model-value="updateIntentData()" inset
                                                            style="transform: scale(0.8);">
                                                        </v-switch>
                                                    </div>

                                                    <!-- Base Prompt Textarea -->
                                                    <v-textarea v-model="nodeInspector.intents[intentIndex].basePrompt"
                                                        :label="$t('basePrompt')" variant="outlined" density="compact"
                                                        rows="4" auto-grow hide-details="auto"
                                                        @update:model-value="updateIntentData()" class="modern-input"
                                                        :readonly="isViewMode || !nodeInspector.intents[intentIndex].enableBasePrompt"
                                                        :disabled="!nodeInspector.intents[intentIndex].enableBasePrompt"
                                                        placeholder="Find the Intent from the message of users.&#10;#Condition for Response&#10;1. If the user asks a question related to or similar in meaning to the #Group Intent below. You must response 1. Otherwise, 0" />
                                                </div>

                                                <!-- Intent Prompt -->
                                                <v-textarea v-model="nodeInspector.intents[intentIndex].prompt"
                                                    :label="$t('intentPrompt')" variant="outlined" density="compact" rows="2"
                                                    auto-grow hide-details="auto"
                                                    @update:model-value="updateIntentData()" class="modern-input mb-3"
                                                    :readonly="isViewMode" />

                                                <!-- Example Messages -->
                                                <div class="mb-3">
                                                    <div class="d-flex align-center mb-2">
                                                        <span class="text-subtitle-2 font-weight-medium">
                                                            Example Messages
                                                        </span>
                                                        <v-btn v-if="!isViewMode" size="x-small" color="primary"
                                                            variant="tonal"
                                                            @click="addInspectorIntentMessage(intentIndex)"
                                                            class="ml-auto">
                                                            <v-icon size="12">mdi-plus</v-icon>
                                                        </v-btn>
                                                    </div>
                                                    <div v-for="(message, messageIndex) in intent.messages"
                                                        :key="message.id" class="d-flex align-center mb-2">
                                                        <v-text-field
                                                            v-model="nodeInspector.intents[intentIndex].messages[messageIndex].text"
                                                            :placeholder="$t('_phExampleUserMessage')" variant="outlined"
                                                            density="compact" hide-details="auto"
                                                            @update:model-value="updateIntentData()"
                                                            class="modern-input flex-grow-1 mr-2"
                                                            :readonly="isViewMode" />
                                                        <v-btn v-if="!isViewMode" icon size="x-small" color="error"
                                                            variant="text"
                                                            @click="removeInspectorIntentMessage(intentIndex, messageIndex)"
                                                            :disabled="intent.messages.length <= 1">
                                                            <v-icon size="14">mdi-close</v-icon>
                                                        </v-btn>
                                                    </div>
                                                </div>

                                                <!-- Actions Section -->
                                                <div class="actions-section">
                                                    <!-- Main Action -->
                                                    <div class="mb-3">
                                                        <span class="text-subtitle-2 font-weight-medium">
                                                            Main
                                                            Action
                                                        </span>
                                                        <v-text-field
                                                            v-model="nodeInspector.intents[intentIndex].mainAction.label"
                                                            :placeholder="$t('_phMainActionLabel')" variant="outlined"
                                                            density="compact" hide-details="auto"
                                                            @update:model-value="updateIntentData()"
                                                            class="modern-input mt-2" :readonly="isViewMode" />
                                                    </div>

                                                    <!-- Parallel Actions -->
                                                    <div>
                                                        <div class="d-flex align-center mb-2">
                                                            <span class="text-subtitle-2 font-weight-medium">
                                                                Parallel
                                                                Actions
                                                            </span>
                                                            <v-btn v-if="!isViewMode" size="x-small" color="primary"
                                                                variant="tonal"
                                                                @click="addInspectorParallelAction(intentIndex)"
                                                                class="ml-auto">
                                                                <v-icon size="12">mdi-plus</v-icon>
                                                            </v-btn>
                                                        </div>
                                                        <div v-for="(parallel, parallelIndex) in intent.parallelActions"
                                                            :key="parallel.id" class="d-flex align-center mb-2">
                                                            <v-text-field
                                                                v-model="nodeInspector.intents[intentIndex].parallelActions[parallelIndex].label"
                                                                :placeholder="$t('_phParallelActionLabel')"
                                                                variant="outlined" density="compact" hide-details="auto"
                                                                @update:model-value="updateIntentData()"
                                                                class="modern-input flex-grow-1 mr-2"
                                                                :readonly="isViewMode" />
                                                            <v-btn v-if="!isViewMode" icon size="x-small" color="error"
                                                                variant="text"
                                                                @click="removeInspectorParallelAction(intentIndex, parallelIndex)">
                                                                <v-icon size="14">mdi-close</v-icon>
                                                            </v-btn>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </div>
                        </div>
                    </div>

                    <!-- Smart Intent Inspector Section -->
                    <div v-else-if="selectedNode.data.type === 'smartIntent'" class="form-section">
                        <h6 class="section-title">{{ $t('smartIntentConfiguration') }}</h6>
                        <v-text-field v-model="nodeInspector.smartIntentGroupName" :label="$t('smartIntentHandlerName')"
                            variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('smartIntentGroupName', $event)"
                            class="modern-input mb-4" :readonly="isViewMode" />

                        <!-- Smart Intents Configuration -->
                        <div class="smart-intents-section">
                            <div class="section-header mb-3">
                                <h6 class="section-title mb-0">{{ $t('smartIntents') }}</h6>
                                <div class="d-flex gap-2">
                                    <v-btn v-if="!isViewMode" size="small" color="primary" variant="tonal"
                                        @click="addInspectorSmartIntent" class="add-btn">
                                        <v-icon left size="16">mdi-plus</v-icon>
                                        Add Smart Intent
                                    </v-btn>
                                </div>
                            </div>

                            <div v-for="(smartIntent, smartIntentIndex) in nodeInspector.smartIntents" :key="smartIntent.id"
                                class="smart-intent-card mb-3">
                                <v-expansion-panels variant="accordion" class="smart-intent-expansion">
                                    <v-expansion-panel>
                                        <v-expansion-panel-title>
                                            <div class="d-flex align-center w-100">
                                                <v-icon class="mr-2" color="primary" size="small">mdi-brain-outline</v-icon>
                                                <!-- Priority Number -->
                                                <span v-if="smartIntent.priority || smartIntent.priority === 0"
                                                    class="priority-number mr-2">
                                                    {{ smartIntent.priority }}
                                                </span>
                                                <span class="font-weight-medium">
                                                    {{
                                                        smartIntent.name || `Smart Intent ${smartIntentIndex + 1}`
                                                    }}
                                                </span>
                                                <v-spacer></v-spacer>
                                                <v-btn v-if="!isViewMode" icon size="x-small" color="error"
                                                    variant="text" @click.stop="removeInspectorSmartIntent(smartIntentIndex)"
                                                    :disabled="nodeInspector.smartIntents.length <= 1" class="ml-2">
                                                    <v-icon size="16">mdi-delete</v-icon>
                                                </v-btn>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <div class="smart-intent-content pa-2">
                                                <!-- Smart Intent Priority -->
                                                <v-text-field
                                                    v-model.number="nodeInspector.smartIntents[smartIntentIndex].priority"
                                                    :label="$t('priority')" type="number" variant="outlined" density="compact"
                                                    hide-details="auto" min="0" step="1" :placeholder="$t('enterPriority')"
                                                    @update:model-value="updateSmartIntentData()"
                                                    @keypress="isNumberpositive($event)" class="modern-input mb-3"
                                                    :readonly="isViewMode">
                                                    <template v-slot:prepend-inner>
                                                        <v-icon size="small" color="orange">mdi-priority-high</v-icon>
                                                    </template>
                                                    <template v-slot:append-inner>
                                                        <v-tooltip location="top" max-width="200px">
                                                            <template v-slot:activator="{ props }">
                                                                <v-icon size="small" color="grey-darken-1"
                                                                    v-bind="props">
                                                                    mdi-information-outline
                                                                </v-icon>
                                                            </template>
                                                            <span>{{ $t('_priorityZeroHighest') }}</span>
                                                        </v-tooltip>
                                                    </template>
                                                </v-text-field>

                                                <!-- Smart Intent Name -->
                                                <v-text-field v-model="nodeInspector.smartIntents[smartIntentIndex].name"
                                                    :label="$t('smartIntentName')" variant="outlined" density="compact"
                                                    hide-details="auto" @update:model-value="updateSmartIntentData()"
                                                    class="modern-input mb-3" :readonly="isViewMode" />

                                                <!-- Use Memory Toggle -->
                                                <div style="display: flex; align-items: center; justify-content: flex-start;margin-top: -15px;">
                                                    <v-switch v-model="nodeInspector.smartIntents[smartIntentIndex].useMemory"
                                                        hide-details density="compact" color="primary" inset
                                                        style="transform: scale(0.9); margin-left: 0px; margin-top: 10px;"
                                                        @update:model-value="updateSmartIntentData()" class="mb-3"
                                                        :readonly="isViewMode" />
                                                    <span style="font-size: 16px;margin-left: 8px;">
                                                        Use memory
                                                    </span>
                                                </div>

                                                <div class="custom-divider"></div>

                                                <!-- Base Prompt Section -->
                                                <div style="margin-bottom: 36px;">
                                                    <!-- Base Prompt Toggle -->
                                                    <div style="display: flex;align-items: center;justify-content: flex-end;">
                                                        <span style="font-weight: 500;font-size: 12px;margin-right: 8px;">
                                                            Edit Base Prompt
                                                        </span>
                                                        <v-switch
                                                            v-model="nodeInspector.smartIntents[smartIntentIndex].enableBasePrompt"
                                                            color="primary" density="compact" hide-details
                                                            :readonly="isViewMode"
                                                            @update:model-value="updateSmartIntentData()" inset
                                                            style="transform: scale(0.8);">
                                                        </v-switch>
                                                    </div>

                                                    <!-- Base Prompt Textarea -->
                                                    <v-textarea v-model="nodeInspector.smartIntents[smartIntentIndex].basePrompt"
                                                        :label="$t('basePrompt')" variant="outlined" density="compact"
                                                        rows="4" auto-grow hide-details="auto"
                                                        @update:model-value="updateSmartIntentData()" class="modern-input"
                                                        :readonly="isViewMode || !nodeInspector.smartIntents[smartIntentIndex].enableBasePrompt"
                                                        :disabled="!nodeInspector.smartIntents[smartIntentIndex].enableBasePrompt"
                                                        placeholder="Find the Intent from the message of users.&#10;#Condition for Response&#10;1. If the user asks a question related to or similar in meaning to the #Group Intent below. You must response 1. Otherwise, 0" />
                                                </div>

                                                <!-- Smart Intent Prompt -->
                                                <v-textarea v-model="nodeInspector.smartIntents[smartIntentIndex].prompt"
                                                    :label="$t('smartIntentPrompt')" variant="outlined" density="compact" rows="2"
                                                    auto-grow hide-details="auto"
                                                    @update:model-value="updateSmartIntentData()" class="modern-input mb-3"
                                                    :readonly="isViewMode" />

                                                <!-- Example Messages -->
                                                <div class="mb-3">
                                                    <div class="d-flex align-center mb-2">
                                                        <span class="text-subtitle-2 font-weight-medium">
                                                            Example Messages
                                                        </span>
                                                        <v-btn v-if="!isViewMode" size="x-small" color="primary"
                                                            variant="tonal"
                                                            @click="addInspectorSmartIntentMessage(smartIntentIndex)"
                                                            class="ml-auto">
                                                            <v-icon size="12">mdi-plus</v-icon>
                                                        </v-btn>
                                                    </div>
                                                    <div v-for="(message, messageIndex) in smartIntent.messages"
                                                        :key="message.id" class="d-flex align-center mb-2">
                                                        <v-text-field
                                                            v-model="nodeInspector.smartIntents[smartIntentIndex].messages[messageIndex].text"
                                                            :placeholder="$t('_phExampleUserMessage')" variant="outlined"
                                                            density="compact" hide-details="auto"
                                                            @update:model-value="updateSmartIntentData()"
                                                            class="modern-input flex-grow-1 mr-2"
                                                            :readonly="isViewMode" />
                                                        <v-btn v-if="!isViewMode" icon size="x-small" color="error"
                                                            variant="text"
                                                            @click="removeInspectorSmartIntentMessage(smartIntentIndex, messageIndex)"
                                                            :disabled="smartIntent.messages.length <= 1">
                                                            <v-icon size="14">mdi-close</v-icon>
                                                        </v-btn>
                                                    </div>
                                                </div>

                                                <!-- Main Action Section with AI Configuration -->
                                                <div class="main-action-section mb-4">
                                                    <div class="d-flex align-center justify-space-between mb-2">
                                                        <span class="text-subtitle-2 font-weight-medium">{{ $t('actionLabel') }}</span>
                                                    </div>
                                                    
                                                    <!-- Action Label -->
                                                    <v-text-field
                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.label"
                                                        :placeholder="$t('_phMainActionLabel')" variant="outlined"
                                                        density="compact" hide-details="auto"
                                                        @update:model-value="updateSmartIntentData()"
                                                        class="modern-input mt-2 mb-3" :readonly="isViewMode" />

                                                    <!-- AI Toggle -->
                                                    <div class="ai-toggle-section mb-3">
                                                        <div style="display: flex; align-items: center; justify-content: space-between;">
                                                            <span class="text-subtitle-2 font-weight-medium">
                                                                <v-icon size="16" class="mr-2" color="primary">mdi-robot</v-icon>
                                                                Use AI Model
                                                            </span>
                                                            <div style="display: flex; align-items: center; gap: 8px;">
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.useAI"
                                                                        color="primary" density="compact" hide-details inset
                                                                        @update:model-value="updateSmartIntentData()"
                                                                        :readonly="isViewMode"
                                                                        style="transform: scale(0.8);" />
                                                                </div>
                                                            </div>
                                                    </div>

                                                    <!-- AI Configuration (when enabled) -->
                                                    <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.useAI" 
                                                         class="ai-config-section">
                                                         <div style="display: flex; align-items: center; justify-content: flex-end; margin-bottom: 8px;width: 100%;">  
                                                            <v-btn class="mb-3" v-if="!isViewMode && nodeInspector.smartIntents[smartIntentIndex].mainAction.useAI" @click="openSmartIntentLoadFromModelDialog(smartIntentIndex)" 
                                                                color="success" variant="text" size="small"
                                                                style="background-color: #e8f5e9;"
                                                                prepend-icon="mdi-download">
                                                                Load Config
                                                            </v-btn>
                                                         </div>
                                                        
                                                        <!-- AI Configuration Expandable Section -->
                                                        <v-expansion-panels variant="accordion" class="ai-main-config-expansion">
                                                            <v-expansion-panel>
                                                                <v-expansion-panel-title class="ai-config-title">
                                                                    <div class="d-flex align-center">
                                                                        <v-icon icon="mdi-brain" size="20" color="primary" class="me-3"></v-icon>
                                                                        <div>
                                                                            <div class="text-subtitle-1 font-weight-medium">{{ $t('aiModelConfiguration') }}</div>
                                                                            <div class="text-caption text-medium-emphasis">{{ $t('_providerParamsBehavior') }}</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text>
                                                                    <!-- AI Model Configuration Panels (exactly like AI Model Node) -->
                                                                    <v-expansion-panels multiple class="config-panels elevation-0" variant="accordion">
                                                            <!-- Basic Information Panel -->
                                                            <v-expansion-panel value="basic" class="panel-item mt-3">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4">
                                                                            <v-icon icon="mdi-information" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">{{ $t('basicInfo') }}</div>
                                                                            <div class="panel-subtitle">{{ $t('_providerModelBasic') }}</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content">
                                                                        <!-- Platform, Provider and Model -->
                                                                        <div class="config-section mb-6">
                                                                            <h6 class="section-label mb-3">{{ $t('serviceConfiguration') }}</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-autocomplete 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.platform"
                                                                                        :items="platformOptions" item-title="text" item-value="value"
                                                                                        :label="$t('platformRequired')" variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-server-network"
                                                                                        @update:model-value="changePlatformForSmartIntent(smartIntentIndex, $event)"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        :hint="$t('_hintSelectPlatform')" persistent-hint required />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-autocomplete 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.provider"
                                                                                        :items="listDDProvider" item-title="providerName" item-value="providerName"
                                                                                        :label="$t('providerRequired')" variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-cloud-outline"
                                                                                        @update:model-value="updateSmartIntentProviderChange(smartIntentIndex, $event)"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        :disabled="['Google', 'Anthropic'].includes(nodeInspector.smartIntents[smartIntentIndex].mainAction.platform)"
                                                                                        :hint="$t('_hintSelectProvider')" persistent-hint required />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-autocomplete 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.model"
                                                                                        :items="getModelsByProvider(nodeInspector.smartIntents[smartIntentIndex].mainAction.provider, nodeInspector.smartIntents[smartIntentIndex].mainAction.platform)"
                                                                                        item-title="modelDisplay" item-value="modelName" :label="$t('modelRequired')"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-brain"
                                                                                        @update:model-value="updateSmartIntentModelChange(smartIntentIndex, $event)"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        :disabled="!nodeInspector.smartIntents[smartIntentIndex].mainAction.provider"
                                                                                        :hint="$t('_hintSelectModelVersion')" persistent-hint required />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>

                                                                        <!-- Behavior Settings -->
                                                                        <div class="config-section">
                                                                            <h6 class="section-label mb-3">{{ $t('behaviorSettings') }}</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-select 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.gender"
                                                                                        :items="[{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }]"
                                                                                        item-title="name" item-value="id" :label="$t('gender')"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-account"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        :hint="$t('_hintGenderPreference')" persistent-hint
                                                                                        :model-value="nodeInspector.smartIntents[smartIntentIndex].mainAction.gender || 'male'" />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.typingSpeed"
                                                                                        :label="$t('typingSpeed')" type="number" min="0" variant="outlined"
                                                                                        density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-keyboard-outline"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode"
                                                                                        :hint="$t('_hintCharsPerSecond')" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.maxInputLength"
                                                                                        :label="$t('maxInputLength')" type="number" min="0" max="200000"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-text-box-outline"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="maxNumberpositive($event)"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        :hint="$t('_hintMaxCharsAllowed')" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>

                                                            <!-- Response Options Panel -->
                                                            <v-expansion-panel value="response" class="panel-item">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                                                            <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">{{ $t('responseOptions') }}</div>
                                                                            <div class="panel-subtitle">{{ $t('_configureHowResponsesGenerated') }}</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content pa-4">
                                                                        <!-- Stream Response -->
                                                                        <div class="switch-option-compact mb-3">
                                                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.streamResponse }">
                                                                                <div class="d-flex align-center">
                                                                                    <v-avatar size="28"
                                                                                        :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.streamResponse ? 'primary' : 'grey-lighten-1'"
                                                                                        class="me-3">
                                                                                        <v-icon
                                                                                            :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.streamResponse ? 'white' : 'grey-darken-1'"
                                                                                            size="16">mdi-text-box-outline</v-icon>
                                                                                    </v-avatar>
                                                                                    <div>
                                                                                        <div class="switch-title">{{ $t('streamResponse') }}</div>
                                                                                        <div class="switch-description">{{ $t('_progressiveTextGeneration') }}</div>
                                                                                    </div>
                                                                                </div>
                                                                                <v-switch 
                                                                                    v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.streamResponse" 
                                                                                    color="primary" hide-details density="compact"
                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                    :readonly="isViewMode" inset />
                                                                            </div>
                                                                        </div>

                                                                        <!-- Time Context -->
                                                                        <div class="switch-option-compact">
                                                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.enableTime }">
                                                                                <div class="d-flex align-center">
                                                                                    <v-avatar size="28"
                                                                                        :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableTime ? 'success' : 'grey-lighten-1'"
                                                                                        class="me-3">
                                                                                        <v-icon
                                                                                            :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableTime ? 'white' : 'grey-darken-1'"
                                                                                            size="16">mdi-clock-outline</v-icon>
                                                                                    </v-avatar>
                                                                                    <div>
                                                                                        <div class="switch-title">{{ $t('timeContext') }}</div>
                                                                                        <div class="switch-description">{{ $t('enableTimeAwareness') }}</div>
                                                                                    </div>
                                                                                </div>
                                                                                <v-switch 
                                                                                    v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableTime" 
                                                                                    color="success" hide-details density="compact"
                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                    :readonly="isViewMode" inset />
                                                                            </div>

                                                                            <!-- Time Zone Dropdown (shown when enableTime is true) -->
                                                                            <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableTime" class="mt-3">
                                                                                <v-select 
                                                                                    v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.timeZone" 
                                                                                    :items="DDTimeZone" item-title="name" item-value="value" 
                                                                                    :label="$t('timeZone')" variant="outlined" density="comfortable" hide-details="auto"
                                                                                    prepend-inner-icon="mdi-earth"
                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                    class="config-input" :readonly="isViewMode"
                                                                                    :hint="$t('_selectYourTimezone')" persistent-hint
                                                                                    :placeholder="$t('_selectYourTimezone')">
                                                                                    <template v-slot:selection="{ item }">
                                                                                        <div class="d-flex align-center">
                                                                                            <v-icon small color="primary" class="me-2">mdi-map-marker</v-icon>
                                                                                            <span class="font-weight-medium">{{ item.raw.name }}</span>
                                                                                            <v-chip size="x-small" color="grey-darken-1" text-color="white" class="ml-2">
                                                                                                {{ item.raw.value && item.raw.value.includes('/') ? item.raw.value.split('/')[1].replace('_', ' ') : item.raw.value }}
                                                                                            </v-chip>
                                                                                        </div>
                                                                                    </template>
                                                                                </v-select>
                                                                            </div>
                                                                        </div>

                                                                        <!-- JSON Response -->
                                                                        <div class="switch-option-compact mt-3">
                                                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.jsonResponse }">
                                                                                <div class="d-flex align-center">
                                                                                    <v-avatar size="28"
                                                                                        :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.jsonResponse ? 'primary' : 'grey-lighten-1'"
                                                                                        class="me-3">
                                                                                        <v-icon
                                                                                            :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.jsonResponse ? 'white' : 'grey-darken-1'"
                                                                                            size="16">mdi-code-json</v-icon>
                                                                                    </v-avatar>
                                                                                    <div>
                                                                                        <div class="switch-title">{{ $t('jsonResponse') }}</div>
                                                                                        <div class="switch-description">{{ $t('_returnStructuredJson') }}</div>
                                                                                    </div>
                                                                                </div>
                                                                                <v-switch 
                                                                                    v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.jsonResponse" 
                                                                                    color="primary" hide-details density="compact"
                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                    :readonly="isViewMode" inset />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>

                                                            <!-- Parameters Panel -->
                                                            <v-expansion-panel value="parameters" class="panel-item">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #9C27B0, #7B1FA2);">
                                                                            <v-icon icon="mdi-cog" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">{{ $t('aiParameters') }}</div>
                                                                            <div class="panel-subtitle">{{ $t('_fineTuneModelBehavior') }}</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content">
                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">{{ $t('coreParameters') }}</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.temperature"
                                                                                        :label="$t('temperatureRequired')" type="number" step="0.1" min="0" max="1"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-thermometer"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode"
                                                                                        :hint="$t('_hintCreativityLevel')" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.topP" 
                                                                                        :label="$t('topP')" type="number" step="0.1" min="0" max="1" 
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-filter-variant"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode"
                                                                                        :hint="$t('_hintTopP')" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>

                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">{{ $t('penaltySettings') }}</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.frequencyPenalty"
                                                                                        :label="$t('frequencyPenalty')" type="number" step="0.1" min="0" max="2" 
                                                                                        variant="outlined" density="comfortable" hide-details="auto" 
                                                                                        prepend-inner-icon="mdi-repeat-off"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode"
                                                                                        :hint="$t('_hintReduceFrequentTokens')" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.presencePenalty"
                                                                                        :label="$t('presencePenalty')" type="number" step="0.1" min="0" max="2" 
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-minus-circle-outline"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode" 
                                                                                        :hint="$t('_hintReduceAnyTokens')" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>

                                                                        <div class="config-section">
                                                                            <h6 class="section-label mb-3">{{ $t('outputControl') }}</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.maxTokens"
                                                                                        :label="$t('maxTokensResponse')" type="number" min="1"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-text-box-check-outline"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode" 
                                                                                        :hint="$t('_hintMaxResponseTokens')" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.stopSequence"
                                                                                        :label="$t('stopSequence')" variant="outlined" density="comfortable"
                                                                                        hide-details="auto" prepend-inner-icon="mdi-stop-circle-outline"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        :hint="$t('_hintStopSequence')" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>

                                                            <!-- Memory Settings Panel -->
                                                            <v-expansion-panel value="memory" class="panel-item">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4"
                                                                            style="background: linear-gradient(135deg, #00BCD4, #0097A7);">
                                                                            <v-icon icon="mdi-brain" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">{{ $t('memorySettings') }}</div>
                                                                            <div class="panel-subtitle">{{ $t('_configureConvMemoryContext') }}</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content">
                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">{{ $t('conversationMemory') }}</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.memoryTurn"
                                                                                        :label="$t('turnCount')" type="number" min="0" variant="outlined"
                                                                                        density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-history"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode"
                                                                                        :hint="$t('_hintTurnsRemember')"
                                                                                        persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.memoryDuration"
                                                                                        :label="$t('durationMinutes')" type="number" min="0"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-timer-outline"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode" :hint="$t('_hintMemoryActive')"
                                                                                        persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>

                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">{{ $t('memoryTypes') }}</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.shortTermMemory"
                                                                                        :label="$t('shortTermMemory')" type="number" min="1"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-memory"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode" :hint="$t('_hintContextWindow')"
                                                                                        persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col>
                                                                                    <!-- Long-Term Memory Switch -->
                                                                                    <div class="switch-option-compact mb-4">
                                                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                            :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.longTermMemory }">
                                                                                            <div class="d-flex align-center">
                                                                                                <v-avatar size="28"
                                                                                                    :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.longTermMemory ? 'success' : 'grey-lighten-1'"
                                                                                                    class="me-3">
                                                                                                    <v-icon
                                                                                                        :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.longTermMemory ? 'white' : 'grey-darken-1'"
                                                                                                        size="16">mdi-database</v-icon>
                                                                                                </v-avatar>
                                                                                                <div>
                                                                                                    <div class="switch-title">{{ $t('longTermMemory') }}</div>
                                                                                                    <div class="switch-description">Persistent storage
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <v-switch 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.longTermMemory"
                                                                                                color="success" hide-details density="compact"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode" inset />
                                                                                        </div>
                                                                                    </div>
                                                                                </v-col>
                                                                            </v-row>

                                                                        </div>

                                                                        <!-- Multi-turn Input Configuration -->
                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">{{ $t('multiTurnInputConfiguration') }}</h6>
                                                                            <v-row>
                                                                                <v-col>
                                                                                    <!-- Enable Multi-Turn Input Switch -->
                                                                                    <div class="switch-option-compact mb-3">
                                                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                            :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.isChainMassage }">
                                                                                            <div class="d-flex align-center">
                                                                                                <v-avatar size="28"
                                                                                                    :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.isChainMassage ? 'indigo' : 'grey-lighten-1'"
                                                                                                    class="me-3">
                                                                                                    <v-icon
                                                                                                        :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.isChainMassage ? 'white' : 'grey-darken-1'"
                                                                                                        size="16">mdi-chat-processing</v-icon>
                                                                                                </v-avatar>
                                                                                                <div>
                                                                                                    <div class="switch-title">Enable Multi-Turn Input
                                                                                                    </div>
                                                                                                    <div class="switch-description">Allow continuous
                                                                                                        conversation</div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <v-switch 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.isChainMassage"
                                                                                                color="indigo" hide-details density="compact"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode" inset />
                                                                                        </div>
                                                                                    </div>
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.lenChainMassage"
                                                                                        :label="$t('sequence')" type="number" min="1" variant="outlined"
                                                                                        density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-numeric"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode" :disabled="!nodeInspector.smartIntents[smartIntentIndex].mainAction.isChainMassage"
                                                                                        :hint="$t('_hintTurnsInSequence')" persistent-hint
                                                                                        clearable />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.timeSecondChainMassage"
                                                                                        :label="$t('durationSeconds')" type="number" min="1"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-timer"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode" :disabled="!nodeInspector.smartIntents[smartIntentIndex].mainAction.isChainMassage"
                                                                                        :hint="$t('_hintMultiTurnDuration')" persistent-hint
                                                                                        clearable />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>


                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>

                                                            <!-- Line Configuration Panel -->
                                                            <v-expansion-panel value="line" class="panel-item">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4"
                                                                            style="background: linear-gradient(135deg, #4CAF50, #388E3C);">
                                                                            <v-icon icon="mdi-message" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">{{ $t('lineConfiguration') }}</div>
                                                                            <div class="panel-subtitle">{{ $t('_lineMessagingPlatformSettings') }}</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content pa-4">
                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">{{ $t('displayOptions') }}</h6>

                                                                            <!-- Show Name -->
                                                                            <div class="switch-option-compact mb-3">
                                                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                    :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.showLineName }">
                                                                                    <div class="d-flex align-center">
                                                                                        <v-avatar size="28"
                                                                                            :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.showLineName ? 'info' : 'grey-lighten-1'"
                                                                                            class="me-3">
                                                                                            <v-icon
                                                                                                :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.showLineName ? 'white' : 'grey-darken-1'"
                                                                                                size="16">mdi-account-circle</v-icon>
                                                                                        </v-avatar>
                                                                                        <div>
                                                                                            <div class="switch-title">{{ $t('showName') }}</div>
                                                                                            <div class="switch-description">{{ $t('displayUserName') }}</div>
                                                                                        </div>
                                                                                    </div>
                                                                                    <v-switch 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.showLineName" 
                                                                                        color="info" hide-details density="compact"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        :readonly="isViewMode" inset />
                                                                                </div>
                                                                            </div>
                                                                            <!-- Loading Indicator -->
                                                                            <div class="switch-option-compact mb-3">
                                                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                    :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.loadingBar }">
                                                                                    <div class="d-flex align-center">
                                                                                        <v-avatar size="28"
                                                                                            :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.loadingBar ? 'success' : 'grey-lighten-1'"
                                                                                            class="me-3">
                                                                                            <v-icon
                                                                                                :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.loadingBar ? 'white' : 'grey-darken-1'"
                                                                                                size="16">mdi-progress-clock</v-icon>
                                                                                        </v-avatar>
                                                                                        <div>
                                                                                            <div class="switch-title">{{ $t('loadingIndicator') }}</div>
                                                                                            <div class="switch-description">{{ $t('showTypingAnimation') }}</div>
                                                                                        </div>
                                                                                    </div>
                                                                                    <v-switch 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.loadingBar" 
                                                                                        color="success" hide-details density="compact"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        :readonly="isViewMode" inset />
                                                                                </div>
                                                                            </div>

                                                                        </div>

                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">{{ $t('loadingAndDuration') }}</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-select 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.loadingBarDuration"
                                                                                        :items="[10, 20, 30, 40, 50, 60]"
                                                                                        :label="$t('loadingDurationSeconds')" variant="outlined"
                                                                                        density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-timer-sand"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        class="config-input" :disabled="!nodeInspector.smartIntents[smartIntentIndex].mainAction.loadingBar"
                                                                                        :readonly="isViewMode"
                                                                                        :hint="$t('_hintLoadingDuration')" persistent-hint
                                                                                        clearable />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>

                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">{{ $t('monitoring') }}</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.monitorLineUserId"
                                                                                        :label="$t('monitorLineUserId')" variant="outlined"
                                                                                        density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-monitor-eye"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        :hint="$t('_hintLineUserId')"
                                                                                        persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-autocomplete 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.monitorSocialID"
                                                                                        :items="listMonitorLineName" :label="$t('monitorLineName')"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-chat" item-title="name" item-value="id"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        :hint="$t('_hintSelectMonitorLine')" persistent-hint clearable />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>

                                                                        <div class="config-section">
                                                                            <h6 class="section-label mb-3">{{ $t('contentFiltering') }}</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-toolbar-title style="font-size: 16px">
                                                                                        Forbidden word
                                                                                    </v-toolbar-title>
                                                                                </v-col>
                                                                            </v-row>
                                                                            <div class="pr-1" style="
                                                                                    border: #dfe0e2 1px solid;
                                                                                    background: #fff;
                                                                                    margin-top: 10px;
                                                                                    border-radius: 8px;
                                                                                ">
                                                                                <v-row v-for="(item, forbiddenIndex) in (nodeInspector.smartIntents[smartIntentIndex].mainAction.forbiddenWords || [''])"
                                                                                    :key="forbiddenIndex" style="margin: 0px">
                                                                                    <v-col cols="12" style="padding: 0px">
                                                                                        <div style="display: flex; align-items: center">
                                                                                            <div style="
                                                                                                    height: 50px;
                                                                                                    background: #f8f8f8;
                                                                                                    padding: 14px 20px 0 20px;
                                                                                                ">
                                                                                                {{ forbiddenIndex + 1 }}
                                                                                            </div>
                                                                                            <v-textarea variant="solo" style="
                                                                                                    overflow: hidden;
                                                                                                    overflow-wrap: break-word;
                                                                                                    resize: none;
                                                                                                    height: 50px;
                                                                                                    width: 98%;
                                                                                                    border-radius: 0px;
                                                                                                    font-family: Fira code, Fira Mono, Inconsolata,
                                                                                                        DejaVu Sans Mono, Courier, monospace;
                                                                                                    font-size: 14px;
                                                                                                "
                                                                                                :model-value="((nodeInspector.smartIntents[smartIntentIndex].mainAction.forbiddenWords || [])[forbiddenIndex]) || ''"
                                                                                                :placeholder="$t('_phEnterWordPhrase')"
                                                                                                @keypress="isEnterSmartIntentForbidden($event, smartIntentIndex, forbiddenIndex)"
                                                                                                @keyup.delete="checkDeleteSmartIntentForbiddenWord(smartIntentIndex, forbiddenIndex, ((nodeInspector.smartIntents[smartIntentIndex].mainAction.forbiddenWords || [])[forbiddenIndex]) || '')"
                                                                                                @update:model-value="updateSmartIntentForbiddenWordProperty(smartIntentIndex, forbiddenIndex, $event)"
                                                                                                :readonly="isViewMode" />
                                                                                            <v-btn icon="mdi-attachment" variant="text"
                                                                                                v-show="(nodeInspector.smartIntents[smartIntentIndex].mainAction.forbiddenWords || []).length > 1 && forbiddenIndex + 1 < (nodeInspector.smartIntents[smartIntentIndex].mainAction.forbiddenWords || []).length"
                                                                                                @click="removeSmartIntentForbiddenWordDirect(smartIntentIndex, forbiddenIndex)"
                                                                                                :disabled="isViewMode">
                                                                                                <v-tooltip location="top" :open-delay="500">
                                                                                                    <template v-slot:activator="{ props }">
                                                                                                        <v-icon color="error" style="cursor: pointer"
                                                                                                            v-bind="props">
                                                                                                            mdi-delete-forever
                                                                                                        </v-icon>
                                                                                                    </template>
                                                                                                    <span>{{ $t('delete') }}</span>
                                                                                                </v-tooltip>
                                                                                            </v-btn>
                                                                                        </div>
                                                                                    </v-col>
                                                                                </v-row>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>

                                                            <!-- System Prompt Panel -->
                                                            <v-expansion-panel value="prompt" class="panel-item mb-3">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4"
                                                                            style="background: linear-gradient(135deg, #607D8B, #455A64);">
                                                                            <v-icon icon="mdi-message-text" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">{{ $t('systemPrompt') }}</div>
                                                                            <div class="panel-subtitle">{{ $t('_defineAiBehaviorPersonality') }}</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content">
                                                                        <div class="prompt-section">
                                                                            <div class="d-flex align-center mb-4">
                                                                                <v-avatar size="36" color="info" class="me-3">
                                                                                    <v-icon color="white" size="20">mdi-robot-outline</v-icon>
                                                                                </v-avatar>
                                                                                <div>
                                                                                    <h6 class="text-h6 mb-1">{{ $t('aiPersonalityInstructions') }}</h6>
                                                                                    <p class="text-body-2 text-medium-emphasis mb-0">
                                                                                        Define how your AI should behave, respond, and interact with
                                                                                        users
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                            <h3 class="text-h6 d-flex align-center mb-3" style="font-size: 16px !important;">
                                                                                <v-icon icon="mdi-pencil-outline" size="small"
                                                                                    class="me-2"></v-icon>
                                                                                Configure system message for your AI
                                                                            </h3>

                                                                            <!-- Prompt Template Header -->
                                                                            <div class="mb-2" style="display:flex;flex-direction: column;align-items: flex-end;">
                                                                                <div style="display: flex; align-items: center;">
                                                                                    <v-tooltip location="top">
                                                                                        <template v-slot:activator="{ props }">
                                                                                            <v-icon icon="mdi-information-outline" size="small"
                                                                                                color="grey" class="me-3" v-bind="props"></v-icon>
                                                                                        </template>
                                                                                        <span>{{ $t('_configureAddlPromptsTemplates') }}</span>
                                                                                    </v-tooltip>
                                                                                    <v-btn
                                                                                        v-if="!isViewMode && DDGroupPromptTemplate && DDGroupPromptTemplate.length > 0"
                                                                                        variant="outlined" color="primary"
                                                                                        prepend-icon="mdi-playlist-plus" @click="showSmartIntentPromptTemplate(smartIntentIndex)"
                                                                                        class="text-none font-weight-medium" size="small">
                                                                                        Show Templates
                                                                                    </v-btn>
                                                                                </div>
                                                                            </div>

                                                                            <!-- Main editor and templates layout -->
                                                                            <v-row>
                                                                                <!-- Editor area (full width) -->
                                                                                <v-col cols="12" class="prompt-editor-col">
                                                                                    <v-card variant="outlined" class="prompt-editor-card">
                                                                                        <v-card-text class="px-2 py-1">
                                                                                            <v-textarea 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.systemPrompt" 
                                                                                                label="" variant="plain" counter auto-grow rows="25"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode"
                                                                                                :placeholder="$t('_phEnterSystemMessage')"
                                                                                                class="prompt-textarea"
                                                                                                @contextmenu="onSmartIntentContextMenu(smartIntentIndex, $event)"></v-textarea>
                                                                                        </v-card-text>
                                                                                    </v-card>
                                                                                </v-col>
                                                                            </v-row>

                                                                            <div class="prompt-tips mt-4">
                                                                                <v-card class="tips-card" color="blue-grey-lighten-5" flat>
                                                                                    <v-card-text class="pa-4">
                                                                                        <div class="d-flex align-start">
                                                                                            <v-icon color="info" size="20"
                                                                                                class="me-2 mt-1">mdi-lightbulb-outline</v-icon>
                                                                                            <div>
                                                                                                <div class="text-subtitle-2 font-weight-medium mb-2">
                                                                                                    Tips for
                                                                                                    better prompts:</div>
                                                                                                <ul class="text-body-2 text-medium-emphasis">
                                                                                                    <li>Be specific about the AI's role and expertise
                                                                                                    </li>
                                                                                                    <li>{{ $t('_defineToneStyle') }}</li>
                                                                                                    <li>{{ $t('_includeRestrictionsGuidelines') }}</li>
                                                                                                    <li>{{ $t('_specifyHandleUnknownInfo') }}</li>
                                                                                                </ul>
                                                                                            </div>
                                                                                        </div>
                                                                                    </v-card-text>
                                                                                </v-card>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>

                                                            <!-- Knowledge Panel -->
                                                            <v-expansion-panel value="knowledge" class="panel-item mb-3">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4"
                                                                            style="background: linear-gradient(135deg, #FF6F00, #E65100);">
                                                                            <v-icon icon="mdi-database" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">{{ $t('knowledgeSources') }}</div>
                                                                            <div class="panel-subtitle">{{ $t('_configureKnowledgeContext') }}</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content">
                                                                        <!-- Knowledge Sources Selection -->
                                                                        <div class="config-section mb-4">
                                                                            <h6 class="section-label mb-3">{{ $t('enableKnowledgeSources') }}</h6>
                                                                            <div class="knowledge-sources-grid">
                                                                                <div class="knowledge-source-card mb-3">
                                                                                    <div class="knowledge-source-frame rounded-lg border">
                                                                                        <div class="knowledge-source-title">
                                                                                            <div class="d-flex align-center">
                                                                                                <v-avatar size="32" color="blue" class="me-3">
                                                                                                    <v-icon color="white" size="18">mdi-file-document</v-icon>
                                                                                                </v-avatar>
                                                                                                <div>
                                                                                                    <div class="text-subtitle-2 font-weight-medium">{{ $t('documents') }}</div>
                                                                                                    <div class="text-caption text-medium-emphasis">{{ $t('_vectorDocumentSearch') }}</div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <v-switch 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableDocuments"
                                                                                                color="blue" hide-details density="compact"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode" inset />
                                                                                        </div>
                                                                                        <!-- Documents Configuration -->
                                                                                        <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableDocuments" 
                                                                                            class="config-section my-4 knowledge-source-config">
                                                                                            <div class="d-flex align-center mb-3">
                                                                                                <h6 class="section-label mb-0">{{ $t('documentsConfiguration') }}</h6>
                                                                                            </div>
                                                                                            <!-- Document Selection -->
                                                                                            <v-autocomplete 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.document" 
                                                                                                :items="DDVector" item-title="name" item-value="name" 
                                                                                                :label="$t('ddDocument')" variant="outlined" density="comfortable" 
                                                                                                hide-details="auto" chips multiple 
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input mb-3"
                                                                                                :placeholder="$t('_phSelectDocuments')" :clearable="!isViewMode" :readonly="isViewMode">
                                                                                                <template v-slot:item="{ props, item }">
                                                                                                    <v-list-item v-bind="props" class="document-option">
                                                                                                        <template v-slot:prepend>
                                                                                                            <v-icon size="20" color="primary">mdi-file-document</v-icon>
                                                                                                        </template>
                                                                                                        <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                                                    </v-list-item>
                                                                                                </template>
                                                                                            </v-autocomplete>
                                                                                            
                                                                                            <!-- Top-N Chunks -->
                                                                                            <v-text-field 
                                                                                                v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.topNChunks" 
                                                                                                type="number" :label="$t('topNChunks')" variant="outlined" 
                                                                                                density="comfortable" hide-details="auto" :readonly="isViewMode" 
                                                                                                min="1" step="1" 
                                                                                                @focus="$event.target.select()"
                                                                                                @blur="validateAndFixSmartIntentTopNChunks($event, smartIntentIndex)"
                                                                                                @keydown.enter="validateAndFixSmartIntentTopNChunks($event, smartIntentIndex)"
                                                                                                class="config-input mb-3" 
                                                                                                :hint="$t('_hintDocChunks')" persistent-hint />
                                                                                            
                                                                                            <!-- Cosine Similarity -->
                                                                                            <v-text-field 
                                                                                                v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.cosineSimilarity" 
                                                                                                type="number" :label="$t('cosineSimilarity')" variant="outlined" 
                                                                                                density="comfortable" hide-details="auto" :readonly="isViewMode" 
                                                                                                min="0.0001" max="1" step="0.0001"
                                                                                                @focus="$event.target.select()"
                                                                                                @blur="validateAndFixSmartIntentCosineSimilarity($event, smartIntentIndex)"
                                                                                                @keydown.enter="validateAndFixSmartIntentCosineSimilarity($event, smartIntentIndex)"
                                                                                                class="config-input" 
                                                                                                :hint="$t('_hintSimilarityDoc')" persistent-hint />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                <div class="knowledge-source-card mb-3">
                                                                                    <div class="knowledge-source-frame rounded-lg border">
                                                                                        <div class="knowledge-source-title">
                                                                                            <div class="d-flex align-center">
                                                                                                <v-avatar size="32" color="green" class="me-3">
                                                                                                    <v-icon color="white" size="18">mdi-database</v-icon>
                                                                                                </v-avatar>
                                                                                                <div>
                                                                                                    <div class="text-subtitle-2 font-weight-medium">{{ $t('DatabaseIntegration_title') }}</div>
                                                                                                    <div class="text-caption text-medium-emphasis">{{ $t('_sqlDatabaseQueries') }}</div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <v-switch 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableDatabaseIntegration"
                                                                                                color="green" hide-details density="compact"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode" inset />
                                                                                        </div>
                                                                                        <!-- Database Integration Configuration -->
                                                                                        <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableDatabaseIntegration" 
                                                                                            class="config-section my-4 knowledge-source-config">
                                                                                            <div class="d-flex align-center mb-3">
                                                                                                <h6 class="section-label mb-0">{{ $t('databaseIntegrationConfiguration') }}</h6>
                                                                                            </div>
                                                                                            
                                                                                            <!-- Table Name Selection -->
                                                                                            <v-autocomplete 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.tableName" 
                                                                                                :items="listDataIntegration" item-title="dataName" item-value="id" 
                                                                                                :label="$t('tableName')" variant="outlined" density="comfortable" 
                                                                                                hide-details="auto" @update:model-value="updateSmartIntentData()" 
                                                                                                class="config-input mb-3" :placeholder="$t('_phSelectDatabaseTable')" 
                                                                                                :clearable="!isViewMode" :readonly="isViewMode">
                                                                                                <template v-slot:item="{ props, item }">
                                                                                                    <v-list-item v-bind="props" class="table-option">
                                                                                                        <template v-slot:prepend>
                                                                                                            <v-icon size="20" color="primary">mdi-table</v-icon>
                                                                                                        </template>
                                                                                                        <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                                                                                                        <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                                                    </v-list-item>
                                                                                                </template>
                                                                                            </v-autocomplete>

                                                                                            <!-- SQL Extraction Model Selection -->
                                                                                            <v-autocomplete 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.sqlExtractionModel" 
                                                                                                :items="listSqlExtract" item-title="name" item-value="modelDisplayID" 
                                                                                                :label="$t('sqlExtractionModel')" variant="outlined" density="comfortable" 
                                                                                                hide-details="auto" :clearable="!isViewMode" :readonly="isViewMode"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input mb-3" :placeholder="$t('_phSelectModelSql')">
                                                                                                <template v-slot:item="{ props, item }">
                                                                                                    <v-list-item v-bind="props" class="model-option">
                                                                                                        <template v-slot:prepend>
                                                                                                            <div class="model-icon-small mr-2" :style="{ backgroundColor: item.raw.color }">
                                                                                                                <v-icon size="14" color="green">mdi-robot</v-icon>
                                                                                                            </div>
                                                                                                        </template>
                                                                                                        <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                                                    </v-list-item>
                                                                                                </template>
                                                                                            </v-autocomplete>

                                                                                            <!-- Prompt for No Data -->
                                                                                            <v-textarea 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.promptSqlNoData" 
                                                                                                :label="$t('promptForNoData')" variant="outlined" density="comfortable" 
                                                                                                rows="3" auto-grow hide-details="auto"
                                                                                                :placeholder="$t('_phEnterNoDataMessage')"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input" :readonly="isViewMode" 
                                                                                                :hint="$t('_hintNoDbResults')" persistent-hint />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                <div class="knowledge-source-card mb-3">
                                                                                    <div class="knowledge-source-frame rounded-lg border">
                                                                                        <div class="knowledge-source-title">
                                                                                            <div class="d-flex align-center">
                                                                                                <v-avatar size="32" color="purple" class="me-3">
                                                                                                    <v-icon color="white" size="18">mdi-package-variant</v-icon>
                                                                                                </v-avatar>
                                                                                                <div>
                                                                                                    <div class="text-subtitle-2 font-weight-medium">{{ $t('products') }}</div>
                                                                                                    <div class="text-caption text-medium-emphasis">{{ $t('_productCatalogSearch') }}</div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <v-switch 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableProducts"
                                                                                                color="purple" hide-details density="compact"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode" inset />
                                                                                        </div>
                                                                                        <!-- Products Configuration -->
                                                                                        <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableProducts" 
                                                                                            class="config-section my-4 knowledge-source-config">
                                                                                            <div class="d-flex align-center mb-3">
                                                                                                <h6 class="section-label mb-0">{{ $t('productsConfiguration') }}</h6>
                                                                                            </div>
                                                                                            
                                                                                            <!-- Product Group List Selection -->
                                                                                            <v-autocomplete 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.productGroupList" 
                                                                                                :items="DDProductGroup" item-title="name" item-value="id" 
                                                                                                :label="$t('productGroupList')" variant="outlined" density="comfortable" 
                                                                                                hide-details="auto" chips multiple return-object
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input mb-3" :placeholder="$t('_phSelectProductGroup')" 
                                                                                                :clearable="!isViewMode" :readonly="isViewMode">
                                                                                                <template v-slot:item="{ props, item }">
                                                                                                    <v-list-item v-bind="props" class="product-group-option">
                                                                                                        <template v-slot:prepend>
                                                                                                            <v-icon size="20" color="primary">mdi-package-variant</v-icon>
                                                                                                        </template>
                                                                                                        <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                                                    </v-list-item>
                                                                                                </template>
                                                                                            </v-autocomplete>

                                                                                            <!-- Top-N Chunks -->
                                                                                            <v-text-field 
                                                                                                v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.productTopNChunks" 
                                                                                                type="number" :label="$t('topNChunks')" variant="outlined" 
                                                                                                density="comfortable" hide-details="auto" min="1" step="1"
                                                                                                @focus="$event.target.select()"
                                                                                                @blur="validateAndFixSmartIntentProductTopNChunks($event, smartIntentIndex)"
                                                                                                @keydown.enter="validateAndFixSmartIntentProductTopNChunks($event, smartIntentIndex)"
                                                                                                class="config-input mb-3" :placeholder="$t('_phEnterNumberChunks')" 
                                                                                                :readonly="isViewMode" 
                                                                                                :hint="$t('_hintProductChunks')" persistent-hint />

                                                                                            <!-- Cosine Similarity -->
                                                                                            <v-text-field 
                                                                                                v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.productCosineSimilarity"
                                                                                                type="number" :label="$t('cosineSimilarity')" variant="outlined" 
                                                                                                density="comfortable" hide-details="auto" min="0.0001" max="1" step="0.0001"
                                                                                                @focus="$event.target.select()"
                                                                                                @blur="validateAndFixSmartIntentProductCosineSimilarity($event, smartIntentIndex)"
                                                                                                @keydown.enter="validateAndFixSmartIntentProductCosineSimilarity($event, smartIntentIndex)"
                                                                                                class="config-input" :placeholder="$t('_phEnterSimilarityThreshold')"
                                                                                                :readonly="isViewMode" 
                                                                                                :hint="$t('_hintSimilarityProduct')" persistent-hint />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                <div class="knowledge-source-card mb-3">
                                                                                    <div class="knowledge-source-frame rounded-lg border">
                                                                                        <div class="knowledge-source-title">    
                                                                                            <div class="d-flex align-center">
                                                                                                <v-avatar size="32" color="orange" class="me-3">
                                                                                                    <v-icon color="white" size="18">mdi-rocket-launch</v-icon>
                                                                                                </v-avatar>
                                                                                                <div>
                                                                                                    <div class="text-subtitle-2 font-weight-medium">{{ $t('apiKnowledge') }}</div>
                                                                                                    <div class="text-caption text-medium-emphasis">{{ $t('_externalApiIntegration') }}</div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <v-switch 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableApiKnowledge"
                                                                                                color="orange" hide-details density="compact"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode" inset />
                                                                                        </div>
                                                                                        <!-- API Knowledge Configuration -->
                                                                                        <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableApiKnowledge" 
                                                                                            class="config-section my-4 knowledge-source-config">
                                                                                            <div class="d-flex align-center mb-3">
                                                                                                <h6 class="section-label mb-0">{{ $t('apiKnowledgeConfiguration') }}</h6>
                                                                                            </div>
                                                                                            
                                                                                            <!-- API URL -->
                                                                                            <v-text-field 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.knowledgeApiUrl" 
                                                                                                :label="$t('apiUrl')" variant="outlined" density="comfortable" 
                                                                                                hide-details="auto" placeholder="https://api.example.com/knowledge"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input mb-3" :readonly="isViewMode">
                                                                                                <template v-slot:prepend-inner>
                                                                                                    <v-icon size="20" color="primary">mdi-link</v-icon>
                                                                                                </template>
                                                                                            </v-text-field>

                                                                                            <!-- HTTP Method -->
                                                                                            <v-select 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.knowledgeHttpMethod" 
                                                                                                :items="['GET', 'POST']" :label="$t('httpMethod')" variant="outlined" 
                                                                                                density="comfortable" hide-details="auto"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input mb-3" :readonly="isViewMode">
                                                                                                <template v-slot:prepend-inner>
                                                                                                    <v-icon size="20" color="primary">mdi-code-brackets</v-icon>
                                                                                                </template>
                                                                                            </v-select>

                                                                                            <!-- Request Headers -->
                                                                                            <div class="headers-section mb-3">
                                                                                                <div class="section-header mb-2">
                                                                                                    <span class="section-title text-subtitle-2 font-weight-medium">{{ $t('requestHeaders') }}</span>
                                                                                                    <v-btn v-if="!isViewMode" @click="addSmartIntentKnowledgeAPIHeader(smartIntentIndex)" size="small" color="primary"
                                                                                                        variant="tonal" class="add-btn">
                                                                                                        <v-icon size="16">mdi-plus</v-icon>
                                                                                                        Add Header
                                                                                                    </v-btn>
                                                                                                </div>
                                                                                                <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.listHeaders" class="headers-list">
                                                                                                    <div v-for="(header, headerIndex) in nodeInspector.smartIntents[smartIntentIndex].mainAction.listHeaders" :key="headerIndex"
                                                                                                        class="header-row mb-2">
                                                                                                        <v-row no-gutters>
                                                                                                            <v-col cols="5">
                                                                                                                <v-text-field
                                                                                                                    v-model="header.key"
                                                                                                                    :label="$t('headerName')"
                                                                                                                    variant="outlined"
                                                                                                                    density="compact"
                                                                                                                    hide-details="auto"
                                                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                                                    class="header-input"
                                                                                                                    :readonly="isViewMode"
                                                                                                                    placeholder="Content-Type" />
                                                                                                            </v-col>
                                                                                                            <v-col cols="6" class="pl-2">
                                                                                                                <v-text-field
                                                                                                                    v-model="header.value"
                                                                                                                    :label="$t('headerValue')"
                                                                                                                    variant="outlined"
                                                                                                                    density="compact"
                                                                                                                    hide-details="auto"
                                                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                                                    class="header-input"
                                                                                                                    :readonly="isViewMode"
                                                                                                                    placeholder="application/json" />
                                                                                                            </v-col>
                                                                                                            <v-col cols="1" class="pl-2 d-flex align-center">
                                                                                                                <v-btn
                                                                                                                    v-if="!isViewMode"
                                                                                                                    @click="removeSmartIntentKnowledgeAPIHeader(smartIntentIndex, headerIndex)"
                                                                                                                    icon
                                                                                                                    size="small"
                                                                                                                    variant="text"
                                                                                                                    color="error">
                                                                                                                    <v-icon size="16">mdi-delete</v-icon>
                                                                                                                </v-btn>
                                                                                                            </v-col>
                                                                                                        </v-row>
                                                                                                    </div>
                                                                                                </div>
                                                                                                <div v-else class="text-caption text-medium-emphasis">
                                                                                                    No headers configured
                                                                                                </div>
                                                                                            </div>

                                                                                            <!-- Request Body (for POST requests) -->
                                                                                            <div class="mb-3">
                                                                                                <div class="section-header mb-2">
                                                                                                    <span class="section-title text-subtitle-2 font-weight-medium">{{ $t('requestBody') }}</span>
                                                                                                </div>
                                                                                                <v-textarea
                                                                                                    v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.knowledgeRequestBody"
                                                                                                    :label="$t('requestBodyJson')"
                                                                                                    variant="outlined"
                                                                                                    density="comfortable"
                                                                                                    rows="4"
                                                                                                    auto-grow
                                                                                                    hide-details="auto"
                                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                                    class="config-input"
                                                                                                    :readonly="isViewMode"
                                                                                                    placeholder='{"key": "value"}'
                                                                                                    hint="JSON payload for API request (use {{ variables }} for dynamic values)" 
                                                                                                    persistent-hint>
                                                                                                    <template v-slot:prepend-inner>
                                                                                                        <v-icon size="20" color="primary">mdi-code-json</v-icon>
                                                                                                    </template>
                                                                                                    <template v-slot:append-inner>
                                                                                                        <v-tooltip location="top">
                                                                                                            <template v-slot:activator="{ props }">
                                                                                                                <v-icon v-bind="props" size="small" color="grey-darken-1">
                                                                                                                    mdi-information-outline
                                                                                                                </v-icon>
                                                                                                            </template>
                                                                                                            <span>JSON payload for API request (use {{ variables }} for dynamic values)</span>
                                                                                                        </v-tooltip>
                                                                                                    </template>
                                                                                                </v-textarea>
                                                                                            </div>

                                                                                            <!-- Response Data Path -->
                                                                                            <v-text-field 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.knowledgeResponseDataPath" 
                                                                                                :label="$t('responseDataPath')" variant="outlined" density="comfortable" 
                                                                                                hide-details="auto" placeholder="data.knowledge"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input" :readonly="isViewMode">
                                                                                                <template v-slot:prepend-inner>
                                                                                                    <v-icon size="20" color="primary">mdi-code-json</v-icon>
                                                                                                </template>
                                                                                                <template v-slot:append-inner>
                                                                                                    <v-tooltip location="top">
                                                                                                        <template v-slot:activator="{ props }">
                                                                                                            <v-icon v-bind="props" size="small" color="grey-darken-1">
                                                                                                                mdi-information-outline
                                                                                                            </v-icon>
                                                                                                        </template>
                                                                                                        <span>{{ $t('_jsonPathExtractKnowledge') }}</span>
                                                                                                    </v-tooltip>
                                                                                                </template>
                                                                                            </v-text-field>

                                                                                            <!-- Token Authentication for Smart Intent mainAction -->
                                                                                            <v-divider class="my-3" />
                                                                                            <div class="d-flex align-center justify-space-between mb-2">
                                                                                                <div class="d-flex align-center">
                                                                                                    <v-icon size="18" color="orange" class="mr-2">mdi-key-variant</v-icon>
                                                                                                    <span class="text-subtitle-2 font-weight-medium">{{ $t('tokenAuthentication') }}</span>
                                                                                                </div>
                                                                                                <v-switch 
                                                                                                    v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableToken"
                                                                                                    color="orange" hide-details density="compact"
                                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                                    :readonly="isViewMode" inset />
                                                                                            </div>

                                                                                            <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableToken">
                                                                                                <v-radio-group 
                                                                                                    v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.tokenAuthMode" 
                                                                                                    inline hide-details
                                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                                    :readonly="isViewMode" class="mb-3">
                                                                                                    <v-radio :label="$t('getNewToken')" value="new" color="orange" />
                                                                                                    <v-radio :label="$t('useExistingToken')" value="existing" color="orange" />
                                                                                                </v-radio-group>

                                                                                                <!-- Get New Token -->
                                                                                                <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.tokenAuthMode === 'new'">
                                                                                                    <v-text-field 
                                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.tokenName"
                                                                                                        :label="$t('Token Name')" variant="outlined" density="compact" hide-details="auto"
                                                                                                        placeholder="e.g. my_erp_token"
                                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                                        class="config-input mb-3" :readonly="isViewMode"
                                                                                                        :hint="$t('_hintNameReuse')" persistent-hint>
                                                                                                        <template v-slot:prepend-inner>
                                                                                                            <v-icon size="18" color="orange">mdi-tag</v-icon>
                                                                                                        </template>
                                                                                                    </v-text-field>
                                                                                                    <v-text-field 
                                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.tokenUrl"
                                                                                                        :label="$t('tokenUrl')" variant="outlined" density="compact" hide-details="auto"
                                                                                                        placeholder="https://auth.example.com/oauth/token"
                                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                                        class="config-input mb-3" :readonly="isViewMode">
                                                                                                        <template v-slot:prepend-inner>
                                                                                                            <v-icon size="18" color="orange">mdi-link-lock</v-icon>
                                                                                                        </template>
                                                                                                    </v-text-field>
                                                                                                    <v-select 
                                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.tokenHttpMethod"
                                                                                                        :items="['GET', 'POST']" :label="$t('tokenMethod')" variant="outlined"
                                                                                                        density="compact" hide-details="auto"
                                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                                        class="config-input mb-3" :readonly="isViewMode">
                                                                                                        <template v-slot:prepend-inner>
                                                                                                            <v-icon size="18" color="orange">mdi-code-brackets</v-icon>
                                                                                                        </template>
                                                                                                    </v-select>
                                                                                                    <v-textarea 
                                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.tokenRequestBody"
                                                                                                        :label="$t('tokenRequestBody')" variant="outlined" density="compact"
                                                                                                        rows="3" auto-grow hide-details="auto"
                                                                                                        placeholder='{"client_id": "xxx", "grant_type": "client_credentials"}'
                                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                                        class="config-input mb-3" :readonly="isViewMode">
                                                                                                        <template v-slot:prepend-inner>
                                                                                                            <v-icon size="18" color="orange">mdi-code-json</v-icon>
                                                                                                        </template>
                                                                                                    </v-textarea>
                                                                                                    <v-text-field 
                                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.tokenResponsePath"
                                                                                                        :label="$t('tokenResponsePath')" variant="outlined" density="compact"
                                                                                                        hide-details="auto" placeholder="access_token"
                                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                                        class="config-input mb-3" :readonly="isViewMode">
                                                                                                        <template v-slot:prepend-inner>
                                                                                                            <v-icon size="18" color="orange">mdi-key-chain</v-icon>
                                                                                                        </template>
                                                                                                    </v-text-field>
                                                                                                </div>

                                                                                                <!-- Use Existing Token -->
                                                                                                <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.tokenAuthMode === 'existing'">
                                                                                                    <v-select 
                                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.useExistingTokenName"
                                                                                                        :items="availableTokenNames"
                                                                                                        :label="$t('selectExistingToken')" variant="outlined" density="compact"
                                                                                                        hide-details="auto"
                                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                                        class="config-input mb-3" :readonly="isViewMode"
                                                                                                        :no-data-text="'No tokens available'">
                                                                                                        <template v-slot:prepend-inner>
                                                                                                            <v-icon size="18" color="orange">mdi-key-link</v-icon>
                                                                                                        </template>
                                                                                                    </v-select>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <!-- Knowledge Integration Tips -->
                                                                        <div class="knowledge-tips mt-4">
                                                                            <v-card class="tips-card" color="orange-lighten-5" flat>
                                                                                <v-card-text class="pa-4">
                                                                                    <div class="d-flex align-start">
                                                                                        <v-icon color="orange-darken-1" size="20"
                                                                                            class="me-2 mt-1">mdi-lightbulb-outline</v-icon>
                                                                                        <div>
                                                                                            <div class="text-subtitle-2 font-weight-medium mb-2">
                                                                                                Knowledge Integration Tips:</div>
                                                                                            <ul class="text-body-2 text-medium-emphasis">
                                                                                                <li>{{ $t('_tipMultipleKnowledge') }}</li>
                                                                                                <li>{{ $t('_tipSimilarityThreshold') }}</li>
                                                                                                <li>{{ $t('_tipMultipleDocsProducts') }}</li>
                                                                                                <li>{{ $t('_tipTestApiBeforeDeploy') }}</li>
                                                                                                <li>{{ $t('_tipKnowledgeQueriedInOrder') }}</li>
                                                                                            </ul>
                                                                                        </div>
                                                                                    </div>
                                                                                </v-card-text>
                                                                            </v-card>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>
                                                        </v-expansion-panels>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>
                                                        </v-expansion-panels>
                                                    </div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </div>
                        </div>

                        <!-- Smart Intent Fallback Configuration -->
                        <div class="smart-intent-fallback-section mt-4">
                            <div class="d-flex align-center justify-space-between mb-3">
                                <h6 class="section-title mb-0">{{ $t('fallbackConfiguration') }}</h6>
                            </div>
                            
                            <!-- Fallback AI Toggle -->
                            <div class="ai-toggle-section mb-3">
                                <div style="display: flex; align-items: center; justify-content: space-between;">
                                    <span class="text-subtitle-2 font-weight-medium">
                                        <v-icon size="16" class="mr-2" color="orange">mdi-robot</v-icon>
                                        Use AI Model for Fallback
                                    </span>
                                    <v-switch 
                                        v-model="nodeInspector.smartIntentFallback.useAI"
                                        color="orange" density="compact" hide-details inset
                                        @update:model-value="updateSmartIntentFallbackData()"
                                        :readonly="isViewMode"
                                        style="transform: scale(0.8);" />
                                </div>
                            </div>

                            <!-- Fallback AI Configuration (when enabled) -->
                            <div v-if="nodeInspector.smartIntentFallback && nodeInspector.smartIntentFallback.useAI" 
                                 class="ai-config-section">
                                 <div style="display: flex; align-items: center; justify-content: flex-end; gap: 8px;width: 100%;">
                                    <v-btn v-if="!isViewMode && nodeInspector.smartIntentFallback.useAI" 
                                        @click="openSmartIntentLoadFromModelDialog('fallback')" 
                                        color="success" variant="text" size="small"
                                        style="background-color: #e8f5e9;"
                                        prepend-icon="mdi-download">
                                        Load Config
                                    </v-btn>
                                 </div>
                                 
                                <v-divider class="mb-3"></v-divider>
                                
                                <!-- Fallback AI Configuration Expandable Section -->
                                <v-expansion-panels variant="accordion" class="fallback-ai-config-expansion">
                                    <v-expansion-panel>
                                        <v-expansion-panel-title class="fallback-ai-config-title">
                                            <div class="d-flex align-center">
                                                <v-icon icon="mdi-backup-restore" size="20" color="orange" class="me-3"></v-icon>
                                                <div>
                                                    <div class="text-subtitle-1 font-weight-medium">{{ $t('fallbackAiConfiguration') }}</div>
                                                    <div class="text-caption text-medium-emphasis">{{ $t('_backupAiSettings') }}</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <!-- Model Configuration Panels (exactly like AI Model Node) -->
                                            <v-expansion-panels multiple class="config-panels elevation-0" variant="accordion">
                                    <!-- Basic Information Panel -->
                                    <v-expansion-panel value="basic" class="panel-item mt-3">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4">
                                                    <v-icon icon="mdi-information" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">{{ $t('basicInfo') }}</div>
                                                    <div class="panel-subtitle">{{ $t('_providerModelBasic') }}</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content">
                                                <!-- Platform, Provider and Model -->
                                                <div class="config-section mb-6">
                                                    <h6 class="section-label mb-3">{{ $t('serviceConfiguration') }}</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-autocomplete 
                                                                v-model="nodeInspector.smartIntentFallback.platform"
                                                                :items="platformOptions" item-title="text" item-value="value"
                                                                :label="$t('platformRequired')" variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-server-network"
                                                                @update:model-value="changePlatformForSmartIntentFallback($event)"
                                                                class="config-input" :readonly="isViewMode"
                                                                :hint="$t('_hintSelectPlatform')" persistent-hint required />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-autocomplete 
                                                                v-model="nodeInspector.smartIntentFallback.provider"
                                                                :items="listDDProvider" item-title="providerName" item-value="providerName"
                                                                :label="$t('providerRequired')" variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-cloud-outline"
                                                                @update:model-value="updateSmartIntentFallbackProviderChange($event)"
                                                                class="config-input" :readonly="isViewMode"
                                                                :disabled="['Google', 'Anthropic'].includes(nodeInspector.smartIntentFallback.platform)"
                                                                :hint="$t('_hintSelectProvider')" persistent-hint required />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-autocomplete 
                                                                v-model="nodeInspector.smartIntentFallback.model"
                                                                :items="getModelsByProvider(nodeInspector.smartIntentFallback.provider, nodeInspector.smartIntentFallback.platform)"
                                                                item-title="modelDisplay" item-value="modelName"
                                                                :label="$t('modelRequired')" variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-brain"
                                                                @update:model-value="updateSmartIntentFallbackModelChange($event)"
                                                                class="config-input" :readonly="isViewMode"
                                                                :disabled="!nodeInspector.smartIntentFallback.provider"
                                                                :hint="$t('_hintSelectModelVersion')" persistent-hint required />
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <!-- Behavior Settings -->
                                                <div class="config-section">
                                                    <h6 class="section-label mb-3">{{ $t('behaviorSettings') }}</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-select 
                                                                v-model="nodeInspector.smartIntentFallback.gender"
                                                                :items="[{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }]"
                                                                item-title="name" item-value="id" :label="$t('gender')"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-account"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                class="config-input" :readonly="isViewMode"
                                                                :hint="$t('_hintGenderPreference')" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.typingSpeed"
                                                                :label="$t('typingSpeed')" type="number" min="0" variant="outlined"
                                                                density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-keyboard-outline"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode"
                                                                :hint="$t('_hintCharsPerSecond')" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.maxInputLength"
                                                                :label="$t('maxInputLength')" type="number" min="0" max="200000"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-text-box-outline"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="maxNumberpositive($event)"
                                                                class="config-input" :readonly="isViewMode"
                                                                :hint="$t('_hintMaxCharsAllowed')" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>

                                    <!-- Response Options Panel -->
                                    <v-expansion-panel value="response" class="panel-item">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4"
                                                    style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                                    <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">{{ $t('responseOptions') }}</div>
                                                    <div class="panel-subtitle">{{ $t('_configureHowResponsesGenerated') }}</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content pa-4">
                                                <!-- Stream Response -->
                                                <div class="switch-option-compact mb-3">
                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                        :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.streamingEnabled }">
                                                        <div class="d-flex align-center">
                                                            <v-avatar size="28"
                                                                :color="nodeInspector.smartIntentFallback.streamingEnabled ? 'primary' : 'grey-lighten-1'"
                                                                class="me-3">
                                                                <v-icon
                                                                    :color="nodeInspector.smartIntentFallback.streamingEnabled ? 'white' : 'grey-darken-1'"
                                                                    size="16">mdi-text-box-outline</v-icon>
                                                            </v-avatar>
                                                            <div>
                                                                <div class="switch-title">{{ $t('streamResponse') }}</div>
                                                                <div class="switch-description">{{ $t('_progressiveTextGeneration') }}</div>
                                                            </div>
                                                        </div>
                                                        <v-switch 
                                                            v-model="nodeInspector.smartIntentFallback.streamingEnabled" 
                                                            color="primary" hide-details density="compact"
                                                            @update:model-value="updateSmartIntentFallbackData()"
                                                            :readonly="isViewMode" inset />
                                                    </div>
                                                </div>

                                                <!-- Time Context -->
                                                <div class="switch-option-compact">
                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                        :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.enableTime }">
                                                        <div class="d-flex align-center">
                                                            <v-avatar size="28"
                                                                :color="nodeInspector.smartIntentFallback.enableTime ? 'success' : 'grey-lighten-1'"
                                                                class="me-3">
                                                                <v-icon
                                                                    :color="nodeInspector.smartIntentFallback.enableTime ? 'white' : 'grey-darken-1'"
                                                                    size="16">mdi-clock-outline</v-icon>
                                                            </v-avatar>
                                                            <div>
                                                                <div class="switch-title">{{ $t('timeContext') }}</div>
                                                                <div class="switch-description">{{ $t('enableTimeAwareness') }}</div>
                                                            </div>
                                                        </div>
                                                        <v-switch 
                                                            v-model="nodeInspector.smartIntentFallback.enableTime" 
                                                            color="success" hide-details density="compact"
                                                            @update:model-value="updateSmartIntentFallbackData()"
                                                            :readonly="isViewMode" inset />
                                                    </div>

                                                    <!-- Time Zone Dropdown (shown when enableTime is true) -->
                                                    <div v-if="nodeInspector.smartIntentFallback.enableTime" class="mt-3">
                                                        <v-select 
                                                            v-model="nodeInspector.smartIntentFallback.timeZone" 
                                                            :items="DDTimeZone" item-title="name" item-value="value" 
                                                            :label="$t('timeZone')" variant="outlined" density="comfortable" 
                                                            hide-details="auto" prepend-inner-icon="mdi-earth"
                                                            @update:model-value="updateSmartIntentFallbackData()"
                                                            class="config-input" :readonly="isViewMode"
                                                            :hint="$t('_selectYourTimezone')" persistent-hint
                                                            :placeholder="$t('_selectYourTimezone')">
                                                        </v-select>
                                                    </div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>

                                    <!-- Memory Settings Panel -->
                                    <v-expansion-panel value="memory" class="panel-item">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4"
                                                    style="background: linear-gradient(135deg, #00BCD4, #0097A7);">
                                                    <v-icon icon="mdi-brain" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">{{ $t('memorySettings') }}</div>
                                                    <div class="panel-subtitle">{{ $t('_configureConvMemoryContext') }}</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content">
                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">{{ $t('conversationMemory') }}</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.memoryTurn"
                                                                :label="$t('turnCount')" type="number" min="0" variant="outlined"
                                                                density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-history"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode"
                                                                :hint="$t('_hintTurnsRemember')" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.memoryDuration"
                                                                :label="$t('durationMinutes')" type="number" min="0"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-timer-outline"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode" :hint="$t('_hintMemoryActive')"
                                                                persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">{{ $t('memoryTypes') }}</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.shortTermMemory"
                                                                :label="$t('shortTermMemory')" type="number" min="1"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-memory"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode" :hint="$t('_hintContextWindow')"
                                                                persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col>
                                                            <!-- Long-Term Memory Switch -->
                                                            <div class="switch-option-compact mb-4">
                                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                    :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.longTermMemory }">
                                                                    <div class="d-flex align-center">
                                                                        <v-avatar size="28"
                                                                            :color="nodeInspector.smartIntentFallback.longTermMemory ? 'success' : 'grey-lighten-1'"
                                                                            class="me-3">
                                                                            <v-icon
                                                                                :color="nodeInspector.smartIntentFallback.longTermMemory ? 'white' : 'grey-darken-1'"
                                                                                size="16">mdi-database</v-icon>
                                                                        </v-avatar>
                                                                        <div>
                                                                            <div class="switch-title">{{ $t('longTermMemory') }}</div>
                                                                            <div class="switch-description">{{ $t('persistentStorage') }}</div>
                                                                        </div>
                                                                    </div>
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntentFallback.longTermMemory"
                                                                        color="success" hide-details density="compact"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode" inset />
                                                                </div>
                                                            </div>
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <!-- Multi-turn Input Configuration -->
                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">{{ $t('multiTurnInputConfiguration') }}</h6>
                                                    <v-row>
                                                        <v-col>
                                                            <!-- Enable Multi-Turn Input Switch -->
                                                            <div class="switch-option-compact mb-3">
                                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                    :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.isChainMassage }">
                                                                    <div class="d-flex align-center">
                                                                        <v-avatar size="28"
                                                                            :color="nodeInspector.smartIntentFallback.isChainMassage ? 'indigo' : 'grey-lighten-1'"
                                                                            class="me-3">
                                                                            <v-icon
                                                                                :color="nodeInspector.smartIntentFallback.isChainMassage ? 'white' : 'grey-darken-1'"
                                                                                size="16">mdi-chat-processing</v-icon>
                                                                        </v-avatar>
                                                                        <div>
                                                                            <div class="switch-title">{{ $t('_InfoMultiTurnInput') }}</div>
                                                                            <div class="switch-description">{{ $t('_allowContinuousConversation') }}</div>
                                                                        </div>
                                                                    </div>
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntentFallback.isChainMassage"
                                                                        color="indigo" hide-details density="compact"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode" inset />
                                                                </div>
                                                            </div>
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.lenChainMassage"
                                                                :label="$t('sequence')" type="number" min="1" variant="outlined"
                                                                density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-numeric"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode" :disabled="!nodeInspector.smartIntentFallback.isChainMassage"
                                                                :hint="$t('_hintTurnsInSequence')" persistent-hint
                                                                clearable />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.timeSecondChainMassage"
                                                                :label="$t('durationSeconds')" type="number" min="1"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-timer"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode" :disabled="!nodeInspector.smartIntentFallback.isChainMassage"
                                                                :hint="$t('_hintMultiTurnDuration')" persistent-hint
                                                                clearable />
                                                        </v-col>
                                                    </v-row>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>

                                    <!-- Parameters Panel -->
                                    <v-expansion-panel value="parameters" class="panel-item">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4"
                                                    style="background: linear-gradient(135deg, #9C27B0, #7B1FA2);">
                                                    <v-icon icon="mdi-cog" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">{{ $t('aiParameters') }}</div>
                                                    <div class="panel-subtitle">{{ $t('_fineTuneModelBehavior') }}</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content">
                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">{{ $t('coreParameters') }}</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.temperature"
                                                                :label="$t('temperatureRequired')" type="number" step="0.1" min="0" max="1"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-thermometer"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode"
                                                                :hint="$t('_hintCreativityLevel')" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.topP" 
                                                                :label="$t('topP')" type="number" step="0.1" min="0" max="1" 
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-filter-variant"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode"
                                                                :hint="$t('_hintTopP')" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">{{ $t('penaltySettings') }}</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.frequencyPenalty"
                                                                :label="$t('frequencyPenalty')" type="number" step="0.1" min="0" max="2" 
                                                                variant="outlined" density="comfortable" hide-details="auto" 
                                                                prepend-inner-icon="mdi-repeat-off"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode"
                                                                :hint="$t('_hintReduceFrequentTokens')" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.presencePenalty"
                                                                :label="$t('presencePenalty')" type="number" step="0.1" min="0" max="2" 
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-minus-circle-outline"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode" :hint="$t('_hintReduceAnyTokens')"
                                                                persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <div class="config-section">
                                                    <h6 class="section-label mb-3">{{ $t('outputControl') }}</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.maxTokens"
                                                                :label="$t('maxTokensResponse')" type="number" min="1"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-text-box-check-outline"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode" :hint="$t('_hintMaxResponseTokens')"
                                                                persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model="nodeInspector.smartIntentFallback.stopSequence"
                                                                :label="$t('stopSequence')" variant="outlined" density="comfortable"
                                                                hide-details="auto" prepend-inner-icon="mdi-stop-circle-outline"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                class="config-input" :readonly="isViewMode"
                                                                :hint="$t('_hintStopSequence')" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>

                                    <!-- Line Configuration Panel -->
                                    <v-expansion-panel value="line" class="panel-item">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4"
                                                    style="background: linear-gradient(135deg, #4CAF50, #388E3C);">
                                                    <v-icon icon="mdi-message" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">{{ $t('lineConfiguration') }}</div>
                                                    <div class="panel-subtitle">{{ $t('_lineMessagingPlatformSettings') }}</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content pa-4">
                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">{{ $t('displayOptions') }}</h6>

                                                    <!-- Show Name -->
                                                    <div class="switch-option-compact mb-3">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                            :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.showLineName }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28"
                                                                    :color="nodeInspector.smartIntentFallback.showLineName ? 'info' : 'grey-lighten-1'"
                                                                    class="me-3">
                                                                    <v-icon
                                                                        :color="nodeInspector.smartIntentFallback.showLineName ? 'white' : 'grey-darken-1'"
                                                                        size="16">mdi-account-circle</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">{{ $t('showName') }}</div>
                                                                    <div class="switch-description">{{ $t('displayUserName') }}</div>
                                                                </div>
                                                            </div>
                                                            <v-switch 
                                                                v-model="nodeInspector.smartIntentFallback.showLineName" 
                                                                color="info" hide-details density="compact"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                :readonly="isViewMode" inset />
                                                        </div>
                                                    </div>

                                                    <!-- Loading Indicator -->
                                                    <div class="switch-option-compact mb-3">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                            :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.loadingBar }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28"
                                                                    :color="nodeInspector.smartIntentFallback.loadingBar ? 'success' : 'grey-lighten-1'"
                                                                    class="me-3">
                                                                    <v-icon
                                                                        :color="nodeInspector.smartIntentFallback.loadingBar ? 'white' : 'grey-darken-1'"
                                                                        size="16">mdi-progress-clock</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">{{ $t('loadingIndicator') }}</div>
                                                                    <div class="switch-description">{{ $t('showTypingAnimation') }}</div>
                                                                </div>
                                                            </div>
                                                            <v-switch 
                                                                v-model="nodeInspector.smartIntentFallback.loadingBar" 
                                                                color="success" hide-details density="compact"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                :readonly="isViewMode" inset />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">{{ $t('loadingAndDuration') }}</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-select 
                                                                v-model="nodeInspector.smartIntentFallback.loadingBarDuration"
                                                                :items="[10, 20, 30, 40, 50, 60]"
                                                                :label="$t('loadingDurationSeconds')" variant="outlined"
                                                                density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-timer-sand"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                class="config-input" :disabled="!nodeInspector.smartIntentFallback.loadingBar"
                                                                :readonly="isViewMode"
                                                                :hint="$t('_hintLoadingDuration')" persistent-hint
                                                                clearable />
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">{{ $t('monitoring') }}</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model="nodeInspector.smartIntentFallback.monitorLineUserId"
                                                                :label="$t('monitorLineUserId')" variant="outlined"
                                                                density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-monitor-eye"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                class="config-input" :readonly="isViewMode"
                                                                :hint="$t('_hintLineUserId')" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-autocomplete 
                                                                v-model="nodeInspector.smartIntentFallback.monitorSocialID"
                                                                :items="listMonitorLineName" :label="$t('monitorLineName')"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-chat" item-title="name" item-value="id"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                class="config-input" :readonly="isViewMode"
                                                                :hint="$t('_hintSelectMonitorLine')" persistent-hint clearable />
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <div class="config-section">
                                                    <h6 class="section-label mb-3">{{ $t('contentFiltering') }}</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-toolbar-title style="font-size: 16px">
                                                                Forbidden word
                                                            </v-toolbar-title>
                                                        </v-col>
                                                    </v-row>
                                                    <div class="pr-1" style="
                                                            border: #dfe0e2 1px solid;
                                                            background: #fff;
                                                            margin-top: 10px;
                                                            border-radius: 8px;
                                                        ">
                                                        <v-row v-for="(item, index) in nodeInspector.smartIntentFallback.forbiddenWords || ['']"
                                                            :key="`fallback-forbidden-${index}`" style="margin: 0px">
                                                            <v-col cols="12" style="padding: 0px">
                                                                <div style="display: flex; align-items: center">
                                                                    <div style="
                                                                            height: 50px;
                                                                            background: #f8f8f8;
                                                                            padding: 14px 20px 0 20px;
                                                                        ">
                                                                        {{ index + 1 }}
                                                                    </div>
                                                                    <v-textarea variant="solo" style="
                                                                            overflow: hidden;
                                                                            overflow-wrap: break-word;
                                                                            resize: none;
                                                                            height: 50px;
                                                                            width: 98%;
                                                                            border-radius: 0px;
                                                                            font-family: Fira code, Fira Mono, Inconsolata,
                                                                                DejaVu Sans Mono, Courier, monospace;
                                                                            font-size: 14px;
                                                                        "
                                                                        :model-value="(nodeInspector.smartIntentFallback.forbiddenWords && nodeInspector.smartIntentFallback.forbiddenWords[index]) || ''"
                                                                        :placeholder="$t('_phEnterWordPhrase')"
                                                                        @keypress="isEnterSmartIntentFallbackForbidden($event, index)"
                                                                        @keyup.delete="checkDeleteSmartIntentFallbackForbiddenWord(index, (nodeInspector.smartIntentFallback.forbiddenWords && nodeInspector.smartIntentFallback.forbiddenWords[index]) || '')"
                                                                        @update:model-value="updateSmartIntentFallbackForbiddenWord(index, $event)"
                                                                        :readonly="isViewMode" />
                                                                    <v-btn icon="mdi-attachment" variant="text"
                                                                        v-show="(nodeInspector.smartIntentFallback.forbiddenWords || []).length > 1 && index + 1 < (nodeInspector.smartIntentFallback.forbiddenWords || []).length"
                                                                        @click="removeSmartIntentFallbackForbiddenWord(index)"
                                                                        :disabled="isViewMode">
                                                                        <v-tooltip location="top" :open-delay="500">
                                                                            <template v-slot:activator="{ props }">
                                                                                <v-icon color="error" style="cursor: pointer"
                                                                                    v-bind="props">
                                                                                    mdi-delete-forever
                                                                                </v-icon>
                                                                            </template>
                                                                            <span>{{ $t('delete') }}</span>
                                                                        </v-tooltip>
                                                                    </v-btn>
                                                                </div>
                                                            </v-col>
                                                        </v-row>
                                                    </div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>

                                    <!-- System Prompt Panel -->
                                    <v-expansion-panel value="prompt" class="panel-item">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4"
                                                    style="background: linear-gradient(135deg, #607D8B, #455A64);">
                                                    <v-icon icon="mdi-message-text" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">{{ $t('systemPrompt') }}</div>
                                                    <div class="panel-subtitle">{{ $t('_defineAiBehaviorPersonality') }}</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content">
                                                <div class="prompt-section">
                                                    <div class="d-flex align-center mb-4">
                                                        <v-avatar size="36" color="info" class="me-3">
                                                            <v-icon color="white" size="20">mdi-robot-outline</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <h6 class="text-h6 mb-1">{{ $t('aiPersonalityInstructions') }}</h6>
                                                            <p class="text-body-2 text-medium-emphasis mb-0">
                                                                Define how your AI should behave, respond, and interact with users
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <h3 class="text-h6 d-flex align-center mb-3" style="font-size: 16px !important;">
                                                        <v-icon icon="mdi-pencil-outline" size="small"
                                                            class="me-2"></v-icon>
                                                        Configure system message for your AI
                                                    </h3>

                                                    <!-- Prompt Template Header for Fallback -->
                                                    <div class="mb-2" style="display:flex;flex-direction: column;align-items: flex-end;">
                                                        <div style="display: flex; align-items: center;">
                                                            <v-tooltip location="top">
                                                                <template v-slot:activator="{ props }">
                                                                    <v-icon icon="mdi-information-outline" size="small"
                                                                        color="grey" class="me-3" v-bind="props"></v-icon>
                                                                </template>
                                                                <span>{{ $t('_configureAddlPromptsTemplates') }}</span>
                                                            </v-tooltip>
                                                            <v-btn
                                                                v-if="!isViewMode && DDGroupPromptTemplate && DDGroupPromptTemplate.length > 0"
                                                                variant="outlined" color="orange"
                                                                prepend-icon="mdi-playlist-plus" @click="showSmartIntentFallbackPromptTemplate()"
                                                                class="text-none font-weight-medium" size="small">
                                                                Show Templates
                                                            </v-btn>
                                                        </div>
                                                    </div>

                                                    <!-- Main editor -->
                                                    <v-row>
                                                        <v-col cols="12" class="prompt-editor-col">
                                                            <v-card variant="outlined" class="prompt-editor-card">
                                                                <v-card-text class="px-2 py-1">
                                                                    <v-textarea 
                                                                        v-model="nodeInspector.smartIntentFallback.systemPrompt" 
                                                                        label="" variant="plain" counter auto-grow rows="10"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode"
                                                                        :placeholder="$t('_phEnterSystemMessage')"
                                                                        class="prompt-textarea" />
                                                                </v-card-text>
                                                            </v-card>
                                                        </v-col>
                                                    </v-row>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>

                                    <!-- Knowledge Panel for Fallback -->
                                    <v-expansion-panel value="knowledge" class="panel-item mb-3">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4"
                                                    style="background: linear-gradient(135deg, #FF6F00, #E65100);">
                                                    <v-icon icon="mdi-database" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">{{ $t('knowledgeSources') }}</div>
                                                    <div class="panel-subtitle">{{ $t('_configureKnowledgeFallback') }}</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content">
                                                <!-- Knowledge Sources Selection -->
                                                <div class="config-section mb-4">
                                                    <h6 class="section-label mb-3">{{ $t('enableKnowledgeSources') }}</h6>
                                                    <div class="knowledge-sources-grid">
                                                        <div class="knowledge-source-card mb-3">
                                                            <div class="knowledge-source-frame rounded-lg border">
                                                                <div class="knowledge-source-title">    
                                                                    <div class="d-flex align-center">
                                                                        <v-avatar size="32" color="blue" class="me-3">
                                                                            <v-icon color="white" size="18">mdi-file-document</v-icon>
                                                                        </v-avatar>
                                                                        <div>
                                                                            <div class="text-subtitle-2 font-weight-medium">{{ $t('documents') }}</div>
                                                                            <div class="text-caption text-medium-emphasis">{{ $t('_vectorDocumentSearch') }}</div>
                                                                        </div>
                                                                    </div>
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntentFallback.enableDocuments"
                                                                        color="blue" hide-details density="compact"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode" inset />
                                                                </div>
                                                                <!-- Documents Configuration -->
                                                                <div v-if="nodeInspector.smartIntentFallback.enableDocuments" 
                                                                    class="config-section mb-4 knowledge-source-config">
                                                                    <div class="d-flex align-center mb-3">
                                                                        <h6 class="section-label mb-0">{{ $t('documentsConfiguration') }}</h6>
                                                                    </div>
                                                                    <!-- Document Selection -->
                                                                    <v-autocomplete 
                                                                        v-model="nodeInspector.smartIntentFallback.document" 
                                                                        :items="DDVector" item-title="name" item-value="name" 
                                                                        :label="$t('ddDocument')" variant="outlined" density="comfortable" 
                                                                        hide-details="auto" chips multiple 
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input mb-3"
                                                                        :placeholder="$t('_phSelectDocuments')" :clearable="!isViewMode" :readonly="isViewMode">
                                                                        <template v-slot:item="{ props, item }">
                                                                            <v-list-item v-bind="props" class="document-option">
                                                                                <template v-slot:prepend>
                                                                                    <v-icon size="20" color="primary">mdi-file-document</v-icon>
                                                                                </template>
                                                                                <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                            </v-list-item>
                                                                        </template>
                                                                    </v-autocomplete>
                                                                    
                                                                    <!-- Top-N Chunks -->
                                                                    <v-text-field 
                                                                        v-model.number="nodeInspector.smartIntentFallback.topNChunks" 
                                                                        type="number" :label="$t('topNChunks')" variant="outlined" 
                                                                        density="comfortable" hide-details="auto" :readonly="isViewMode" 
                                                                        min="1" step="1" 
                                                                        @focus="$event.target.select()"
                                                                        @blur="validateAndFixSmartIntentFallbackTopNChunks($event)"
                                                                        @keydown.enter="validateAndFixSmartIntentFallbackTopNChunks($event)"
                                                                        class="config-input mb-3" 
                                                                        :hint="$t('_hintDocChunks')" persistent-hint />
                                                                    
                                                                    <!-- Cosine Similarity -->
                                                                    <v-text-field 
                                                                        v-model.number="nodeInspector.smartIntentFallback.cosineSimilarity" 
                                                                        type="number" :label="$t('cosineSimilarity')" variant="outlined" 
                                                                        density="comfortable" hide-details="auto" :readonly="isViewMode" 
                                                                        min="0.0001" max="1" step="0.0001"
                                                                        @focus="$event.target.select()"
                                                                        @blur="validateAndFixSmartIntentFallbackCosineSimilarity($event)"
                                                                        @keydown.enter="validateAndFixSmartIntentFallbackCosineSimilarity($event)"
                                                                        class="config-input" 
                                                                        :hint="$t('_hintSimilarityDoc')" persistent-hint />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div class="knowledge-source-card mb-3">
                                                            <div class="knowledge-source-frame rounded-lg border">
                                                                <div class="knowledge-source-title">
                                                                    <div class="d-flex align-center">
                                                                        <v-avatar size="32" color="green" class="me-3">
                                                                            <v-icon color="white" size="18">mdi-database</v-icon>
                                                                        </v-avatar>
                                                                        <div>
                                                                            <div class="text-subtitle-2 font-weight-medium">{{ $t('DatabaseIntegration_title') }}</div>
                                                                            <div class="text-caption text-medium-emphasis">{{ $t('_sqlDatabaseQueries') }}</div>
                                                                        </div>
                                                                    </div>
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntentFallback.enableDatabaseIntegration"
                                                                        color="green" hide-details density="compact"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode" inset />
                                                                </div>
                                                                <!-- Database Integration Configuration -->
                                                                <div v-if="nodeInspector.smartIntentFallback.enableDatabaseIntegration" 
                                                                    class="config-section mb-4 knowledge-source-config">
                                                                    <div class="d-flex align-center mb-3">
                                                                        <h6 class="section-label mb-0">{{ $t('databaseIntegrationConfiguration') }}</h6>
                                                                    </div>
                                                                    
                                                                    <!-- Table Name Selection -->
                                                                    <v-autocomplete 
                                                                        v-model="nodeInspector.smartIntentFallback.tableName" 
                                                                        :items="listDataIntegration" item-title="dataName" item-value="id" 
                                                                        :label="$t('tableName')" variant="outlined" density="comfortable" 
                                                                        hide-details="auto" @update:model-value="updateSmartIntentFallbackData()" 
                                                                        class="config-input mb-3" :placeholder="$t('_phSelectDatabaseTable')" 
                                                                        :clearable="!isViewMode" :readonly="isViewMode">
                                                                        <template v-slot:item="{ props, item }">
                                                                            <v-list-item v-bind="props" class="table-option">
                                                                                <template v-slot:prepend>
                                                                                    <v-icon size="20" color="primary">mdi-table</v-icon>
                                                                                </template>
                                                                                <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                                                                                <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                            </v-list-item>
                                                                        </template>
                                                                    </v-autocomplete>

                                                                    <!-- SQL Extraction Model Selection -->
                                                                    <v-autocomplete 
                                                                        v-model="nodeInspector.smartIntentFallback.sqlExtractionModel" 
                                                                        :items="listSqlExtract" item-title="name" item-value="modelDisplayID" 
                                                                        :label="$t('sqlExtractionModel')" variant="outlined" density="comfortable" 
                                                                        hide-details="auto" :clearable="!isViewMode" :readonly="isViewMode"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input mb-3" :placeholder="$t('_phSelectModelSql')">
                                                                        <template v-slot:item="{ props, item }">
                                                                            <v-list-item v-bind="props" class="model-option">
                                                                                <template v-slot:prepend>
                                                                                    <div class="model-icon-small mr-2" :style="{ backgroundColor: item.raw.color }">
                                                                                        <v-icon size="14" color="green">mdi-robot</v-icon>
                                                                                    </div>
                                                                                </template>
                                                                                <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                            </v-list-item>
                                                                        </template>
                                                                    </v-autocomplete>

                                                                    <!-- Prompt for No Data -->
                                                                    <v-textarea 
                                                                        v-model="nodeInspector.smartIntentFallback.promptSqlNoData" 
                                                                        :label="$t('promptForNoData')" variant="outlined" density="comfortable" 
                                                                        rows="3" auto-grow hide-details="auto"
                                                                        :placeholder="$t('_phEnterNoDataMessage')"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input" :readonly="isViewMode" 
                                                                        :hint="$t('_hintNoDbResults')" persistent-hint />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div class="knowledge-source-card mb-3">
                                                            <div class="knowledge-source-frame rounded-lg border">
                                                                <div class="knowledge-source-title">
                                                                    <div class="d-flex align-center">
                                                                        <v-avatar size="32" color="purple" class="me-3">
                                                                            <v-icon color="white" size="18">mdi-package-variant</v-icon>
                                                                        </v-avatar>
                                                                        <div>
                                                                            <div class="text-subtitle-2 font-weight-medium">{{ $t('products') }}</div>
                                                                            <div class="text-caption text-medium-emphasis">{{ $t('_productCatalogSearch') }}</div>
                                                                        </div>
                                                                    </div>
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntentFallback.enableProducts"
                                                                        color="purple" hide-details density="compact"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode" inset />
                                                                </div>
                                                                <!-- Products Configuration -->
                                                                <div v-if="nodeInspector.smartIntentFallback.enableProducts" 
                                                                    class="config-section mb-4 knowledge-source-config">
                                                                    <div class="d-flex align-center mb-3">
                                                                        <h6 class="section-label mb-0">{{ $t('productsConfiguration') }}</h6>
                                                                    </div>
                                                                    
                                                                    <!-- Product Group List Selection -->
                                                                    <v-autocomplete 
                                                                        v-model="nodeInspector.smartIntentFallback.productGroupList" 
                                                                        :items="DDProductGroup" item-title="name" item-value="id" 
                                                                        :label="$t('productGroupList')" variant="outlined" density="comfortable" 
                                                                        hide-details="auto" chips multiple return-object
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input mb-3" :placeholder="$t('_phSelectProductGroup')" 
                                                                        :clearable="!isViewMode" :readonly="isViewMode">
                                                                        <template v-slot:item="{ props, item }">
                                                                            <v-list-item v-bind="props" class="product-group-option">
                                                                                <template v-slot:prepend>
                                                                                    <v-icon size="20" color="primary">mdi-package-variant</v-icon>
                                                                                </template>
                                                                                <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                            </v-list-item>
                                                                        </template>
                                                                    </v-autocomplete>

                                                                    <!-- Top-N Chunks -->
                                                                    <v-text-field 
                                                                        v-model.number="nodeInspector.smartIntentFallback.productTopNChunks" 
                                                                        type="number" :label="$t('topNChunks')" variant="outlined" 
                                                                        density="comfortable" hide-details="auto" min="1" step="1"
                                                                        @focus="$event.target.select()"
                                                                        @blur="validateAndFixSmartIntentFallbackProductTopNChunks($event)"
                                                                        @keydown.enter="validateAndFixSmartIntentFallbackProductTopNChunks($event)"
                                                                        class="config-input mb-3" :placeholder="$t('_phEnterNumberChunks')" 
                                                                        :readonly="isViewMode" 
                                                                        :hint="$t('_hintProductChunks')" persistent-hint />

                                                                    <!-- Cosine Similarity -->
                                                                    <v-text-field 
                                                                        v-model.number="nodeInspector.smartIntentFallback.productCosineSimilarity"
                                                                        type="number" :label="$t('cosineSimilarity')" variant="outlined" 
                                                                        density="comfortable" hide-details="auto" min="0.0001" max="1" step="0.0001"
                                                                        @focus="$event.target.select()"
                                                                        @blur="validateAndFixSmartIntentFallbackProductCosineSimilarity($event)"
                                                                        @keydown.enter="validateAndFixSmartIntentFallbackProductCosineSimilarity($event)"
                                                                        class="config-input" :placeholder="$t('_phEnterSimilarityThreshold')"
                                                                        :readonly="isViewMode" 
                                                                        :hint="$t('_hintSimilarityProduct')" persistent-hint />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div class="knowledge-source-card mb-3">
                                                            <div class="knowledge-source-frame rounded-lg border">
                                                                <div class="knowledge-source-title">
                                                                    <div class="d-flex align-center">
                                                                        <v-avatar size="32" color="orange" class="me-3">
                                                                            <v-icon color="white" size="18">mdi-rocket-launch</v-icon>
                                                                        </v-avatar>
                                                                        <div>
                                                                            <div class="text-subtitle-2 font-weight-medium">{{ $t('apiKnowledge') }}</div>
                                                                            <div class="text-caption text-medium-emphasis">{{ $t('_externalApiIntegration') }}</div>
                                                                        </div>
                                                                    </div>
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntentFallback.enableApiKnowledge"
                                                                        color="orange" hide-details density="compact"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode" inset />
                                                                </div>
                                                                <!-- API Knowledge Configuration -->
                                                                <div v-if="nodeInspector.smartIntentFallback.enableApiKnowledge" 
                                                                    class="config-section mb-4 knowledge-source-config">
                                                                    <div class="d-flex align-center mb-3">
                                                                        <h6 class="section-label mb-0">{{ $t('apiKnowledgeConfiguration') }}</h6>
                                                                    </div>
                                                                    
                                                                    <!-- API URL -->
                                                                    <v-text-field 
                                                                        v-model="nodeInspector.smartIntentFallback.knowledgeApiUrl" 
                                                                        :label="$t('apiUrl')" variant="outlined" density="comfortable" 
                                                                        hide-details="auto" placeholder="https://api.example.com/knowledge"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input mb-3" :readonly="isViewMode">
                                                                        <template v-slot:prepend-inner>
                                                                            <v-icon size="20" color="primary">mdi-link</v-icon>
                                                                        </template>
                                                                    </v-text-field>

                                                                    <!-- HTTP Method -->
                                                                    <v-select 
                                                                        v-model="nodeInspector.smartIntentFallback.knowledgeHttpMethod" 
                                                                        :items="['GET', 'POST']" :label="$t('httpMethod')" variant="outlined" 
                                                                        density="comfortable" hide-details="auto"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input mb-3" :readonly="isViewMode">
                                                                        <template v-slot:prepend-inner>
                                                                            <v-icon size="20" color="primary">mdi-code-brackets</v-icon>
                                                                        </template>
                                                                    </v-select>

                                                                    <!-- Request Headers -->
                                                                    <div class="headers-section mb-3">
                                                                        <div class="section-header mb-2">
                                                                            <span class="section-title text-subtitle-2 font-weight-medium">{{ $t('requestHeaders') }}</span>
                                                                            <v-btn v-if="!isViewMode" @click="addSmartIntentFallbackKnowledgeAPIHeader()" size="small" color="primary"
                                                                                variant="tonal" class="add-btn">
                                                                                <v-icon size="16">mdi-plus</v-icon>
                                                                                Add Header
                                                                            </v-btn>
                                                                        </div>
                                                                        <div v-if="nodeInspector.smartIntentFallback.listHeaders" class="headers-list">
                                                                            <div v-for="(header, headerIndex) in nodeInspector.smartIntentFallback.listHeaders" :key="headerIndex"
                                                                                class="header-row mb-2">
                                                                                <v-row no-gutters>
                                                                                    <v-col cols="5">
                                                                                        <v-text-field
                                                                                            v-model="header.key"
                                                                                            :label="$t('headerName')"
                                                                                            variant="outlined"
                                                                                            density="compact"
                                                                                            hide-details="auto"
                                                                                            @update:model-value="updateSmartIntentFallbackData()"
                                                                                            class="header-input"
                                                                                            :readonly="isViewMode"
                                                                                            placeholder="Content-Type" />
                                                                                    </v-col>
                                                                                    <v-col cols="6" class="pl-2">
                                                                                        <v-text-field
                                                                                            v-model="header.value"
                                                                                            :label="$t('headerValue')"
                                                                                            variant="outlined"
                                                                                            density="compact"
                                                                                            hide-details="auto"
                                                                                            @update:model-value="updateSmartIntentFallbackData()"
                                                                                            class="header-input"
                                                                                            :readonly="isViewMode"
                                                                                            placeholder="application/json" />
                                                                                    </v-col>
                                                                                    <v-col cols="1" class="pl-2 d-flex align-center">
                                                                                        <v-btn
                                                                                            v-if="!isViewMode"
                                                                                            @click="removeSmartIntentFallbackKnowledgeAPIHeader(headerIndex)"
                                                                                            icon
                                                                                            size="small"
                                                                                            variant="text"
                                                                                            color="error">
                                                                                            <v-icon size="16">mdi-delete</v-icon>
                                                                                        </v-btn>
                                                                                    </v-col>
                                                                                </v-row>
                                                                            </div>
                                                                        </div>
                                                                        <div v-else class="text-caption text-medium-emphasis">
                                                                            No headers configured
                                                                        </div>
                                                                    </div>

                                                                    <!-- Request Body -->
                                                                    <div class="mb-3">
                                                                        <div class="section-header mb-2">
                                                                            <span class="section-title text-subtitle-2 font-weight-medium">{{ $t('requestBody') }}</span>
                                                                        </div>
                                                                        <v-textarea
                                                                            v-model="nodeInspector.smartIntentFallback.knowledgeRequestBody"
                                                                            :label="$t('requestBodyJson')"
                                                                            variant="outlined"
                                                                            density="comfortable"
                                                                            rows="4"
                                                                            auto-grow
                                                                            hide-details="auto"
                                                                            @update:model-value="updateSmartIntentFallbackData()"
                                                                            class="config-input"
                                                                            :readonly="isViewMode"
                                                                            placeholder='{"query": "{{userMessage}}", "limit": 10}'
                                                                            hint="JSON payload for API request (use {{ variables }} for dynamic values)" 
                                                                            persistent-hint>
                                                                            <template v-slot:prepend-inner>
                                                                                <v-icon size="20" color="primary">mdi-code-json</v-icon>
                                                                            </template>
                                                                            <template v-slot:append-inner>
                                                                                <v-tooltip location="top">
                                                                                    <template v-slot:activator="{ props }">
                                                                                        <v-icon v-bind="props" size="small" color="grey-darken-1">
                                                                                            mdi-information-outline
                                                                                        </v-icon>
                                                                                    </template>
                                                                                    <span>JSON payload for API request (use {{ variables }} for dynamic values)</span>
                                                                                </v-tooltip>
                                                                            </template>
                                                                        </v-textarea>
                                                                    </div>

                                                                    <!-- Response Data Path -->
                                                                    <v-text-field 
                                                                        v-model="nodeInspector.smartIntentFallback.knowledgeResponseDataPath" 
                                                                        :label="$t('responseDataPath')" variant="outlined" density="comfortable" 
                                                                        hide-details="auto" placeholder="data.knowledge"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input" :readonly="isViewMode">
                                                                        <template v-slot:prepend-inner>
                                                                            <v-icon size="20" color="primary">mdi-code-json</v-icon>
                                                                        </template>
                                                                        <template v-slot:append-inner>
                                                                            <v-tooltip location="top">
                                                                                <template v-slot:activator="{ props }">
                                                                                    <v-icon v-bind="props" size="small" color="grey-darken-1">
                                                                                        mdi-information-outline
                                                                                    </v-icon>
                                                                                </template>
                                                                                <span>{{ $t('_jsonPathExtractKnowledge') }}</span>
                                                                            </v-tooltip>
                                                                        </template>
                                                                    </v-text-field>

                                                                    <!-- Token Authentication for Fallback -->
                                                                    <v-divider class="my-3" />
                                                                    <div class="d-flex align-center justify-space-between mb-2">
                                                                        <div class="d-flex align-center">
                                                                            <v-icon size="18" color="orange" class="mr-2">mdi-key-variant</v-icon>
                                                                            <span class="text-subtitle-2 font-weight-medium">{{ $t('tokenAuthentication') }}</span>
                                                                        </div>
                                                                        <v-switch 
                                                                            v-model="nodeInspector.smartIntentFallback.enableToken"
                                                                            color="orange" hide-details density="compact"
                                                                            @update:model-value="updateSmartIntentFallbackData()"
                                                                            :readonly="isViewMode" inset />
                                                                    </div>

                                                                    <div v-if="nodeInspector.smartIntentFallback.enableToken">
                                                                        <v-radio-group 
                                                                            v-model="nodeInspector.smartIntentFallback.tokenAuthMode" 
                                                                            inline hide-details
                                                                            @update:model-value="updateSmartIntentFallbackData()"
                                                                            :readonly="isViewMode" class="mb-3">
                                                                            <v-radio :label="$t('getNewToken')" value="new" color="orange" />
                                                                            <v-radio :label="$t('useExistingToken')" value="existing" color="orange" />
                                                                        </v-radio-group>

                                                                        <!-- Get New Token -->
                                                                        <div v-if="nodeInspector.smartIntentFallback.tokenAuthMode === 'new'">
                                                                            <v-text-field 
                                                                                v-model="nodeInspector.smartIntentFallback.tokenName"
                                                                                :label="$t('Token Name')" variant="outlined" density="compact" hide-details="auto"
                                                                                placeholder="e.g. my_erp_token"
                                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                                class="config-input mb-3" :readonly="isViewMode"
                                                                                :hint="$t('_hintNameReuse')" persistent-hint>
                                                                                <template v-slot:prepend-inner>
                                                                                    <v-icon size="18" color="orange">mdi-tag</v-icon>
                                                                                </template>
                                                                            </v-text-field>
                                                                            <v-text-field 
                                                                                v-model="nodeInspector.smartIntentFallback.tokenUrl"
                                                                                :label="$t('tokenUrl')" variant="outlined" density="compact" hide-details="auto"
                                                                                placeholder="https://auth.example.com/oauth/token"
                                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                                class="config-input mb-3" :readonly="isViewMode">
                                                                                <template v-slot:prepend-inner>
                                                                                    <v-icon size="18" color="orange">mdi-link-lock</v-icon>
                                                                                </template>
                                                                            </v-text-field>
                                                                            <v-select 
                                                                                v-model="nodeInspector.smartIntentFallback.tokenHttpMethod"
                                                                                :items="['GET', 'POST']" :label="$t('tokenMethod')" variant="outlined"
                                                                                density="compact" hide-details="auto"
                                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                                class="config-input mb-3" :readonly="isViewMode">
                                                                                <template v-slot:prepend-inner>
                                                                                    <v-icon size="18" color="orange">mdi-code-brackets</v-icon>
                                                                                </template>
                                                                            </v-select>
                                                                            <v-textarea 
                                                                                v-model="nodeInspector.smartIntentFallback.tokenRequestBody"
                                                                                :label="$t('tokenRequestBody')" variant="outlined" density="compact"
                                                                                rows="3" auto-grow hide-details="auto"
                                                                                placeholder='{"client_id": "xxx", "grant_type": "client_credentials"}'
                                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                                class="config-input mb-3" :readonly="isViewMode">
                                                                                <template v-slot:prepend-inner>
                                                                                    <v-icon size="18" color="orange">mdi-code-json</v-icon>
                                                                                </template>
                                                                            </v-textarea>
                                                                            <v-text-field 
                                                                                v-model="nodeInspector.smartIntentFallback.tokenResponsePath"
                                                                                :label="$t('tokenResponsePath')" variant="outlined" density="compact"
                                                                                hide-details="auto" placeholder="access_token"
                                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                                class="config-input mb-3" :readonly="isViewMode">
                                                                                <template v-slot:prepend-inner>
                                                                                    <v-icon size="18" color="orange">mdi-key-chain</v-icon>
                                                                                </template>
                                                                            </v-text-field>
                                                                        </div>

                                                                        <!-- Use Existing Token -->
                                                                        <div v-if="nodeInspector.smartIntentFallback.tokenAuthMode === 'existing'">
                                                                            <v-select 
                                                                                v-model="nodeInspector.smartIntentFallback.useExistingTokenName"
                                                                                :items="availableTokenNames"
                                                                                :label="$t('selectExistingToken')" variant="outlined" density="compact"
                                                                                hide-details="auto"
                                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                                class="config-input mb-3" :readonly="isViewMode"
                                                                                :no-data-text="'No tokens available'">
                                                                                <template v-slot:prepend-inner>
                                                                                    <v-icon size="18" color="orange">mdi-key-link</v-icon>
                                                                                </template>
                                                                            </v-select>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>





                                                <!-- Knowledge Integration Tips -->
                                                <div class="knowledge-tips mt-4">
                                                    <v-card class="tips-card" color="orange-lighten-5" flat>
                                                        <v-card-text class="pa-4">
                                                            <div class="d-flex align-start">
                                                                <v-icon color="orange-darken-1" size="20"
                                                                    class="me-2 mt-1">mdi-lightbulb-outline</v-icon>
                                                                <div>
                                                                    <div class="text-subtitle-2 font-weight-medium mb-2">
                                                                        Fallback Knowledge Integration Tips:</div>
                                                                    <ul class="text-body-2 text-medium-emphasis">
                                                                        <li>{{ $t('_tipFallbackKnowledgeUsed') }}</li>
                                                                        <li>{{ $t('_tipBroaderKnowledge') }}</li>
                                                                        <li>{{ $t('_tipUseGeneralDocs') }}</li>
                                                                        <li>{{ $t('_tipTestFallbackResponses') }}</li>
                                                                    </ul>
                                                                </div>
                                                            </div>
                                                        </v-card-text>
                                                    </v-card>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </div>
                        </div>
                        <!-- Smart Intent Prompt Template Dialog -->
                        <v-dialog v-model="smartIntentPromptTemplateDialog" max-width="800" scrollable>
                            <v-card>
                                <v-card-title class="bg-secondary text-white d-flex align-center">
                                    <v-icon icon="mdi-brain" class="me-2"></v-icon>
                                    Smart Intent Prompt Templates
                                    <v-spacer></v-spacer>
                                    <v-btn icon="mdi-close" variant="text" color="white" size="small" 
                                        @click="smartIntentPromptTemplateDialog = false"></v-btn>
                                </v-card-title>
                                
                                <v-card-text class="pa-4">
                                    <!-- Template Group Selection -->
                                    <v-autocomplete 
                                        :label="$t('selectTemplateGroup')" 
                                        :items="DDGroupPromptTemplate" 
                                        item-title="name"
                                        item-value="id" 
                                        density="compact" 
                                        clearable
                                        variant="outlined" 
                                        v-model="smartIntentGroupPromptTemplate"
                                        prepend-inner-icon="mdi-folder-outline" 
                                        hide-details
                                        class="mb-4">
                                    </v-autocomplete>
                                    <!-- Search Template -->
                                    <v-text-field 
                                        v-model="smartIntentTemplateSearchQuery"
                                        :label="$t('searchTemplates')" 
                                        prepend-inner-icon="mdi-magnify"
                                        variant="outlined" 
                                        density="compact" 
                                        hide-details
                                        class="mb-4" 
                                        clearable>
                                    </v-text-field>
                                    <!-- Templates List -->
                                    <div style="max-height: 400px; overflow-y: auto;">
                                        <div v-if="smartIntentSearchedTemplates.length === 0" 
                                            class="pa-4 text-center text-grey-darken-1">
                                            <v-icon icon="mdi-playlist-remove" size="large" 
                                                class="d-block mx-auto mb-2"></v-icon>
                                            <span>{{ $t('noTemplatesFound') }}</span>
                                        </div>
                                        <v-list v-else density="compact" class="pa-0">
                                            <v-list-item 
                                                v-for="(item, i) in smartIntentSearchedTemplates" 
                                                :key="i"
                                                @click="setSelectSmartIntentPromptTemplate(item)"
                                                class="template-list-item ma-1" 
                                                :ripple="true"
                                                rounded>
                                                <template v-slot:prepend>
                                                    <v-icon size="small" color="secondary" 
                                                        icon="mdi-brain"></v-icon>
                                                </template>
                                                <template v-slot:title>
                                                    <span class="font-weight-medium">{{ item.name }}</span>
                                                </template>
                                                <template v-slot:subtitle>
                                                    <span class="text-caption text-grey">
                                                        {{ truncatePrompt(item.prompt) }}
                                                    </span>
                                                </template>
                                                <template v-slot:append>
                                                    <v-btn icon="mdi-plus" size="small" variant="text" 
                                                        color="secondary" @click.stop="setSelectSmartIntentPromptTemplate(item)">
                                                    </v-btn>
                                                </template>
                                            </v-list-item>
                                        </v-list>
                                    </div>
                                </v-card-text>
                                
                                <v-card-actions>
                                    <v-spacer></v-spacer>
                                    <v-btn color="secondary" variant="text" @click="smartIntentPromptTemplateDialog = false">
                                        Close
                                    </v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-dialog>

                        <!-- Smart Intent Fallback Prompt Template Dialog -->
                        <v-dialog v-model="smartIntentFallbackPromptTemplateDialog" max-width="800" scrollable>
                            <v-card>
                                <v-card-title class="bg-orange text-white d-flex align-center">
                                    <v-icon icon="mdi-shield-alert" class="me-2"></v-icon>
                                    Fallback Prompt Templates
                                    <v-spacer></v-spacer>
                                    <v-btn icon="mdi-close" variant="text" color="white" size="small" 
                                        @click="smartIntentFallbackPromptTemplateDialog = false"></v-btn>
                                </v-card-title>
                                
                                <v-card-text class="pa-4">
                                    <!-- Template Group Selection -->
                                    <v-autocomplete 
                                        :label="$t('selectTemplateGroup')" 
                                        :items="DDGroupPromptTemplate" 
                                        item-title="name"
                                        item-value="id" 
                                        density="compact" 
                                        clearable
                                        variant="outlined" 
                                        v-model="smartIntentFallbackGroupPromptTemplate"
                                        prepend-inner-icon="mdi-folder-outline" 
                                        hide-details
                                        class="mb-4">
                                    </v-autocomplete>

                                    <!-- Search Template -->
                                    <v-text-field 
                                        v-model="smartIntentFallbackTemplateSearchQuery"
                                        :label="$t('searchTemplates')" 
                                        prepend-inner-icon="mdi-magnify"
                                        variant="outlined" 
                                        density="compact" 
                                        hide-details
                                        class="mb-4" 
                                        clearable>
                                    </v-text-field>

                                    <!-- Templates List -->
                                    <div style="max-height: 400px; overflow-y: auto;">
                                        <div v-if="smartIntentFallbackSearchedTemplates.length === 0" 
                                            class="pa-4 text-center text-grey-darken-1">
                                            <v-icon icon="mdi-playlist-remove" size="large" 
                                                class="d-block mx-auto mb-2"></v-icon>
                                            <span>{{ $t('noTemplatesFound') }}</span>
                                        </div>

                                        <v-list v-else density="compact" class="pa-0">
                                            <v-list-item 
                                                v-for="(item, i) in smartIntentFallbackSearchedTemplates" 
                                                :key="i"
                                                @click="setSelectSmartIntentFallbackPromptTemplate(item)"
                                                class="template-list-item ma-1" 
                                                :ripple="true"
                                                rounded>
                                                <template v-slot:prepend>
                                                    <v-icon size="small" color="orange" 
                                                        icon="mdi-shield-alert"></v-icon>
                                                </template>
                                                <template v-slot:title>
                                                    <span class="font-weight-medium">{{ item.name }}</span>
                                                </template>
                                                <template v-slot:subtitle>
                                                    <span class="text-caption text-grey">
                                                        {{ truncatePrompt(item.prompt) }}
                                                    </span>
                                                </template>
                                                <template v-slot:append>
                                                    <v-btn icon="mdi-plus" size="small" variant="text" 
                                                        color="orange" @click.stop="setSelectSmartIntentFallbackPromptTemplate(item)">
                                                    </v-btn>
                                                </template>
                                            </v-list-item>
                                        </v-list>
                                    </div>
                                </v-card-text>
                                
                                <v-card-actions>
                                    <v-spacer></v-spacer>
                                    <v-btn color="orange" variant="text" @click="smartIntentFallbackPromptTemplateDialog = false">
                                        Close
                                    </v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-dialog>
                    </div>

                    <!-- Advanced Router Inspector Section -->
                    <div v-else-if="selectedNode.data.type === 'advancedRouter'" class="form-section">
                        <h6 class="section-title">{{ $t('advancedRouterConfiguration') }}</h6>

                        <v-text-field v-model="nodeInspector.advancedRouterName" :label="$t('routerName')"
                            variant="outlined" density="compact" hide-details="auto" :readonly="isViewMode"
                            @update:model-value="updateAdvancedRouterData()" class="modern-input mb-3" />

                        <div class="d-flex align-center mb-2 adv-router-prompt-toolbar">
                            <span class="text-caption font-weight-medium">{{ $t('additionalInstructions') }}</span>
                            <v-spacer></v-spacer>
                            <v-btn v-if="!isViewMode" size="x-small" variant="tonal" color="purple"
                                prepend-icon="mdi-auto-fix" @click="generateAdvancedRouterPrompt()"
                                :disabled="!nodeInspector.advancedRouterOutputs || nodeInspector.advancedRouterOutputs.length === 0"
                                data-testid="adv-router-generate-prompt">
                                Generate from intents
                            </v-btn>
                        </div>
                        <v-textarea v-model="nodeInspector.advancedRouterPrompt"
                            :placeholder="$t('_phCustomClassifierInstructions')"
                            variant="outlined" density="compact" hide-details="auto" rows="6" :readonly="isViewMode"
                            @update:model-value="updateAdvancedRouterData()" class="modern-input mb-3"
                            data-testid="adv-router-prompt" />

                        <div class="d-flex align-center mb-1">
                            <span class="text-caption font-weight-medium">{{ $t('confidenceThreshold') }}</span>
                            <v-spacer></v-spacer>
                            <span class="text-caption">{{ Math.round((nodeInspector.advancedRouterConfidenceThreshold ?? 0.5) * 100) }}%</span>
                        </div>
                        <v-slider v-model="nodeInspector.advancedRouterConfidenceThreshold" min="0" max="1"
                            step="0.05" thumb-label hide-details density="compact" color="purple" class="mb-3"
                            :disabled="isViewMode" @update:model-value="updateAdvancedRouterData()" />

                        <div class="d-flex align-center mb-1">
                            <span class="text-caption font-weight-medium">{{ $t('maxDepth') }}</span>
                            <v-spacer></v-spacer>
                            <span class="text-caption">L{{ nodeInspector.advancedRouterMaxDepth || 2 }}</span>
                        </div>
                        <v-slider v-model="nodeInspector.advancedRouterMaxDepth" min="1" max="4" step="1"
                            thumb-label hide-details density="compact" color="indigo" class="mb-4"
                            :disabled="isViewMode" @update:model-value="updateAdvancedRouterData()" />

                        <!-- Schema Field Mapping -->
                        <v-expansion-panels variant="accordion" class="mb-3">
                            <v-expansion-panel>
                                <v-expansion-panel-title>
                                    <span class="font-weight-medium">{{ $t('customJsonSchemaAdvanced') }}</span>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text>
                                    <v-switch v-model="nodeInspector.advancedRouterCustomSchemaEnabled"
                                        :label="$t('enableCustomFieldMapping')" color="purple" density="compact"
                                        hide-details inset :disabled="isViewMode"
                                        @update:model-value="updateAdvancedRouterData()" class="mb-2" />
                                    <v-text-field v-model="nodeInspector.advancedRouterIntentField"
                                        :label="$t('intentFieldName')" variant="outlined" density="compact"
                                        hide-details="auto" :readonly="isViewMode"
                                        :disabled="!nodeInspector.advancedRouterCustomSchemaEnabled"
                                        placeholder="capability_intent"
                                        @update:model-value="updateAdvancedRouterData()" class="modern-input mb-2" />
                                    <v-text-field v-model="nodeInspector.advancedRouterConfidenceField"
                                        :label="$t('confidenceFieldName')" variant="outlined" density="compact"
                                        hide-details="auto" :readonly="isViewMode"
                                        :disabled="!nodeInspector.advancedRouterCustomSchemaEnabled"
                                        placeholder="confidence"
                                        @update:model-value="updateAdvancedRouterData()" class="modern-input mb-2" />
                                    <v-text-field v-model="nodeInspector.advancedRouterSubIntentsField"
                                        :label="$t('subIntentsFieldName')" variant="outlined" density="compact"
                                        hide-details="auto" :readonly="isViewMode"
                                        :disabled="!nodeInspector.advancedRouterCustomSchemaEnabled"
                                        placeholder="intents"
                                        @update:model-value="updateAdvancedRouterData()" class="modern-input mb-2" />
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>

                        <!-- Intent Tree Builder -->
                        <div class="d-flex align-center mb-3">
                            <h6 class="section-title mb-0">{{ $t('intentTree') }}</h6>
                            <v-spacer></v-spacer>
                            <v-btn v-if="!isViewMode" size="small" color="purple" variant="tonal"
                                prepend-icon="mdi-plus"
                                @click="addAdvancedRouterOutput()" class="add-btn">
                                Add Intent
                            </v-btn>
                        </div>

                        <div v-if="!nodeInspector.advancedRouterOutputs || nodeInspector.advancedRouterOutputs.length === 0"
                            class="adv-router-empty-state">
                            <v-icon size="28" color="grey-lighten-1">mdi-sitemap-outline</v-icon>
                            <span class="text-caption text-grey-darken-1">{{ $t('_noIntentsConfigured') }}<br/>{{ $t('_clickAddIntentToStart') }}</span>
                        </div>

                        <div v-for="(output, outputIndex) in nodeInspector.advancedRouterOutputs" :key="output.id"
                            class="adv-router-card adv-router-card--l1">
                            <div class="adv-router-card-row">
                                <v-chip size="x-small" color="purple" variant="flat" class="adv-router-depth-chip">L1</v-chip>
                                <v-text-field v-model="output.intentName" :label="$t('intentName')" variant="outlined"
                                    density="compact" hide-details placeholder="e.g. Loan" :readonly="isViewMode"
                                    @update:model-value="updateAdvancedRouterData()" class="modern-input flex-1" />
                                <v-btn v-if="!isViewMode" icon="mdi-delete-outline" size="x-small" color="error"
                                    variant="text" class="adv-router-delete-btn"
                                    @click="removeAdvancedRouterOutput(outputIndex)" />
                            </div>
                            <v-text-field v-model="output.description" :label="$t('descriptionOptional')" variant="outlined"
                                density="compact" hide-details :placeholder="$t('_phHelpLlmIntent')"
                                :readonly="isViewMode" @update:model-value="updateAdvancedRouterData()"
                                class="modern-input adv-router-description" />

                            <!-- L2 sub-intents -->
                            <div v-if="(nodeInspector.advancedRouterMaxDepth || 2) >= 2"
                                class="adv-router-children">
                                <div class="adv-router-children-header">
                                    <v-chip size="x-small" color="indigo" variant="tonal">L2</v-chip>
                                    <span class="adv-router-children-label">{{ $t('subIntents') }}</span>
                                    <v-spacer></v-spacer>
                                    <v-btn v-if="!isViewMode" size="x-small" color="indigo" variant="text"
                                        prepend-icon="mdi-plus"
                                        @click="addAdvancedRouterSubIntent(outputIndex)">
                                        Add
                                    </v-btn>
                                </div>
                                <div v-for="(sub, subIndex) in output.subIntents" :key="sub.id"
                                    class="adv-router-card adv-router-card--l2">
                                    <div class="adv-router-card-row">
                                        <v-chip size="x-small" color="indigo" variant="flat" class="adv-router-depth-chip">L2</v-chip>
                                        <v-text-field v-model="sub.intentName" :label="$t('subIntentName')"
                                            variant="outlined" density="compact" hide-details
                                            :readonly="isViewMode"
                                            @update:model-value="updateAdvancedRouterData()"
                                            class="modern-input flex-1" />
                                        <v-btn v-if="!isViewMode" icon="mdi-delete-outline" size="x-small" color="error"
                                            variant="text" class="adv-router-delete-btn"
                                            @click="removeAdvancedRouterSubIntent(outputIndex, subIndex)" />
                                    </div>

                                    <!-- L3 -->
                                    <div v-if="(nodeInspector.advancedRouterMaxDepth || 2) >= 3"
                                        class="adv-router-children">
                                        <div class="adv-router-children-header">
                                            <v-chip size="x-small" color="deep-purple" variant="tonal">L3</v-chip>
                                            <span class="adv-router-children-label">{{ $t('subIntents') }}</span>
                                            <v-spacer></v-spacer>
                                            <v-btn v-if="!isViewMode" size="x-small" color="deep-purple"
                                                variant="text" prepend-icon="mdi-plus"
                                                @click="addAdvancedRouterSubIntent(outputIndex, subIndex)">
                                                Add
                                            </v-btn>
                                        </div>
                                        <div v-for="(sub2, sub2Index) in sub.subIntents" :key="sub2.id"
                                            class="adv-router-card adv-router-card--l3">
                                            <div class="adv-router-card-row">
                                                <v-chip size="x-small" color="deep-purple" variant="flat" class="adv-router-depth-chip">L3</v-chip>
                                                <v-text-field v-model="sub2.intentName" :label="$t('l3Name')"
                                                    variant="outlined" density="compact" hide-details
                                                    :readonly="isViewMode"
                                                    @update:model-value="updateAdvancedRouterData()"
                                                    class="modern-input flex-1" />
                                                <v-btn v-if="!isViewMode" icon="mdi-delete-outline" size="x-small"
                                                    color="error" variant="text" class="adv-router-delete-btn"
                                                    @click="removeAdvancedRouterSubIntent(outputIndex, subIndex, sub2Index)" />
                                            </div>

                                            <!-- L4 -->
                                            <div v-if="(nodeInspector.advancedRouterMaxDepth || 2) >= 4"
                                                class="adv-router-children">
                                                <div class="adv-router-children-header">
                                                    <v-chip size="x-small" color="pink" variant="tonal">L4</v-chip>
                                                    <span class="adv-router-children-label">{{ $t('subIntents') }}</span>
                                                    <v-spacer></v-spacer>
                                                    <v-btn v-if="!isViewMode" size="x-small" color="pink"
                                                        variant="text" prepend-icon="mdi-plus"
                                                        @click="addAdvancedRouterSubIntent(outputIndex, subIndex, sub2Index)">
                                                        Add
                                                    </v-btn>
                                                </div>
                                                <div v-for="(sub3, sub3Index) in sub2.subIntents" :key="sub3.id"
                                                    class="adv-router-card adv-router-card--l4">
                                                    <div class="adv-router-card-row">
                                                        <v-chip size="x-small" color="pink" variant="flat" class="adv-router-depth-chip">L4</v-chip>
                                                        <v-text-field v-model="sub3.intentName"
                                                            :label="$t('l4Name')" variant="outlined" density="compact"
                                                            hide-details :readonly="isViewMode"
                                                            @update:model-value="updateAdvancedRouterData()"
                                                            class="modern-input flex-1" />
                                                        <v-btn v-if="!isViewMode" icon="mdi-delete-outline"
                                                            size="x-small" color="error" variant="text"
                                                            class="adv-router-delete-btn"
                                                            @click="removeAdvancedRouterSubIntent(outputIndex, subIndex, sub2Index, sub3Index)" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="selectedNode.data.type === 'apiTrigger'" class="form-section">
                        <h6 class="section-title">{{ $t('apiConfiguration') }}</h6>
                        <v-text-field v-model="nodeInspector.apiUrl" :label="$t('apiUrl')" variant="outlined"
                            density="compact" hide-details="auto" :readonly="isViewMode"
                            @update:model-value="updateNodeProperty('apiUrl', $event)" class="modern-input mb-3" />
                        <v-select v-model="nodeInspector.httpMethod" :items="['GET', 'POST', 'PUT', 'DELETE']"
                            :label="$t('httpMethod')" variant="outlined" density="compact" hide-details="auto"
                            :readonly="isViewMode" @update:model-value="updateNodeProperty('httpMethod', $event)"
                            class="modern-input mb-3" />

                        <!-- JSON Validation -->
                        <h6 class="section-title mt-4">{{ $t('jsonRequestBody') }}</h6>
                        <v-autocomplete v-model="nodeInspector.modelJsonValidator" :items="uniqueAiModelOptions"
                            item-title="name" item-value="value" :label="$t('jsonValidatorModel')" variant="outlined" density="compact"
                            hide-details="auto" :readonly="isViewMode" :clearable="!isViewMode"
                            @update:model-value="updateNodeProperty('modelJsonValidator', $event)"
                            class="modern-input mb-3">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="model-option"
                                    :title="item.raw.value + ': ' + item.raw.name">
                                    <template v-slot:prepend>
                                        <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                            <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                        </div>
                                    </template>
                                </v-list-item>
                            </template>
                            <template v-slot:selection="{ item }">
                                <v-chip variant="text" size="medium"
                                    :text="item.raw.value + ': ' + item.raw.name"></v-chip>
                            </template>
                        </v-autocomplete>

                        <!-- JSON Editor with Variables - Updated Version -->
                        <div class="json-editor-container mb-3">
                            <!-- Variables Panel Header -->
                            <div class="json-variables-header" v-if="!isViewMode">
                                <div class="variables-header-content">
                                    <div class="variables-title">
                                        <span class="text-subtitle-2">Json</span>
                                        <v-chip size="x-small" variant="tonal" color="primary" class="ml-2 variables-toggle-chip"
                                            :class="{ 'is-open': variablesPanelExpanded }"
                                            @click.stop="variablesPanelExpanded = !variablesPanelExpanded">
                                            <v-icon start size="12">
                                                {{ variablesPanelExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                            </v-icon>
                                            {{ filteredVariables.length }} available variables
                                        </v-chip>
                                    </div>

                                    <!-- Category Filter -->
                                    <v-menu offset-y>
                                        <template v-slot:activator="{ props }">
                                            <v-btn v-bind="props" variant="text" size="small"
                                                class="category-filter-btn"
                                                :class="{ 'filter-active': selectedVariableCategory !== 'All' }">
                                                <v-icon size="14">mdi-filter-variant</v-icon>
                                                <span class="ml-1">{{ selectedVariableCategory }}</span>
                                                <v-icon size="12" class="ml-1">mdi-chevron-down</v-icon>
                                            </v-btn>
                                        </template>
                                        <v-list density="compact" class="category-menu">
                                            <v-list-item v-for="category in variableCategories" :key="category"
                                                @click="selectedVariableCategory = category"
                                                :class="{ 'v-list-item--active': selectedVariableCategory === category }">
                                                <v-list-item-title>{{ category }}</v-list-item-title>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>

                                    <!-- Beautify Button -->
                                    <v-btn variant="text" size="small"
                                        class="beautify-btn"
                                        :title="$t('_titleFormatJson')"
                                        @click.stop="beautifyTriggerJson">
                                        <v-icon size="14">mdi-magic-staff</v-icon>
                                        <span class="ml-1">{{ $t('beautify') }}</span>
                                    </v-btn>
                                </div>
                            </div>

                            <!-- Variables Grid - Collapsible -->
                            <v-expand-transition>
                                <div v-show="variablesPanelExpanded" class="json-variables-content">
                                    <!-- Quick Insert Buttons -->
                                    <div class="variables-quick-grid">
                                        <v-tooltip v-for="variable in filteredVariables" :key="variable.key"
                                            :text="variable.description" location="top">
                                            <template v-slot:activator="{ props }">
                                                <v-chip v-bind="props" class="variable-chip-compact"
                                                    @dragstart="onDragStart($event, variable.key)"
                                                    @click="insertVariable(variable.key)" draggable="true"
                                                    :color="getCategoryColor(variable.category)" variant="tonal"
                                                    size="small">
                                                    <v-icon start size="12">{{ variable.icon }}</v-icon>
                                                    <span class="variable-label">{{ variable.label }}</span>
                                                    <v-icon end size="10" class="drag-handle">mdi-drag-vertical</v-icon>
                                                </v-chip>
                                            </template>
                                        </v-tooltip>
                                    </div>
                                </div>
                            </v-expand-transition>
                            <!-- JSON Textarea -->
                            <div class="json-textarea-container">
                                <v-textarea v-model="nodeInspector.jsonValidate" :label="$t('jsonValidationSchema')"
                                    variant="outlined" density="compact" rows="8" auto-grow :readonly="isViewMode"
                                    hide-details="auto" placeholder='{"Name": "Value"}'
                                    @update:model-value="updateNodeProperty('jsonValidate', $event)"
                                    class="modern-json-editor" @drop.stop.prevent="onJsonDrop($event)"
                                    @dragover.stop.prevent="onJsonDragOver($event)"
                                    @dragleave.stop="onJsonDragLeave($event)" ref="jsonTextarea" />
                            </div>

                            <!-- Insert Helper (footer, no longer overlays editor) -->
                            <div class="insert-helper-footer" v-if="!isViewMode">
                                <v-icon size="12" class="mr-1">mdi-information-outline</v-icon>
                                <span class="helper-text">
                                    Click variables to insert or drag &amp; drop into editor
                                </span>
                            </div>
                        </div>

                        <!-- Headers Configuration -->
                        <h6 class="section-title mt-4">{{ $t('headersConfiguration') }}</h6>
                        <div v-if="nodeInspector.listHeadersAPITrigger" class="headers-section">
                            <div v-for="(header, headerIndex) in nodeInspector.listHeadersAPITrigger" :key="headerIndex"
                                class="header-row mb-3">
                                <div class="d-flex gap-2">
                                    <v-text-field v-model="header.key" :label="$t('Key')" variant="outlined" density="compact"
                                        hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateHeaderProperty(headerIndex, 'key', $event)"
                                        class="modern-input" />
                                    <v-text-field v-model="header.value" :label="$t('valueLabel')" variant="outlined"
                                        density="compact" hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateHeaderProperty(headerIndex, 'value', $event)"
                                        class="modern-input" />
                                    <v-btn icon size="small" color="error" variant="text" v-if="!isViewMode"
                                        @click="deleteHeader(headerIndex)">
                                        <v-icon size="16">mdi-delete</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                            <v-btn size="small" color="primary" variant="tonal" @click="addHeader" v-if="!isViewMode"
                                class="add-btn">
                                <v-icon left size="16">mdi-plus</v-icon>
                                Add Header
                            </v-btn>
                        </div>

                        <!-- Validation Configuration -->
                        <h6 class="section-title mt-4">{{ $t('validationConfiguration') }}</h6>
                        <div v-if="nodeInspector.listValidation" class="validation-section">
                            <div v-for="(validation, validationIndex) in nodeInspector.listValidation"
                                :key="validationIndex" class="validation-row mb-3">
                                <div class="d-flex gap-2 mb-2">
                                    <v-text-field v-model="validation.key" :label="$t('Key')" variant="outlined"
                                        density="compact" hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateValidationProperty(validationIndex, 'key', $event)"
                                        class="modern-input" />
                                    <v-select v-model="validation.typeValidation" :items="DDTypeValidation"
                                        item-title="name" item-value="value" :label="$t('validationType')" variant="outlined"
                                        density="compact" hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateValidationProperty(validationIndex, 'typeValidation', $event)"
                                        class="modern-input" />
                                    <v-btn icon size="small" color="error" variant="text" v-if="!isViewMode"
                                        @click="deleteValidation(validationIndex)">
                                        <v-icon size="16">mdi-delete</v-icon>
                                    </v-btn>
                                </div>
                                <div class="d-flex gap-2 mb-2">
                                    <v-text-field v-if="validation.typeValidation === 'MCV'"
                                        v-model="validation.numberOfCharacter" :label="$t('numberOfCharacters')"
                                        type="number" variant="outlined" density="compact" hide-details="auto"
                                        :readonly="isViewMode"
                                        @update:model-value="updateValidationProperty(validationIndex, 'numberOfCharacter', $event)"
                                        @keypress="isNumberpositive($event)" class="modern-input" />
                                    <v-text-field v-if="validation.typeValidation === 'RV'" v-model="validation.regExp"
                                        :label="$t('regularExpression')" variant="outlined" density="compact"
                                        hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateValidationProperty(validationIndex, 'regExp', $event)"
                                        class="modern-input" />
                                </div>
                                <v-textarea v-model="validation.promptMessage" :label="$t('errorMessage')" variant="outlined"
                                    density="compact" rows="2" auto-grow hide-details="auto"
                                    :placeholder="$t('_phErrorMessageValidation')" :readonly="isViewMode"
                                    @update:model-value="updateValidationProperty(validationIndex, 'promptMessage', $event)"
                                    class="modern-input" />
                            </div>
                            <v-btn size="small" color="primary" variant="tonal" @click="addValidation"
                                v-if="!isViewMode" class="add-btn">
                                <v-icon left size="16">mdi-plus</v-icon>
                                Add Validation
                            </v-btn>
                        </div>

                        <!-- Text Response -->
                        <h6 class="section-title mt-4">{{ $t('textResponse') }}</h6>
                        <v-textarea v-model="nodeInspector.textResponse" :label="$t('responseMessage')" variant="outlined"
                            density="compact" rows="3" auto-grow hide-details="auto"
                            :placeholder="$t('_phEnterResponseMessage')" :readonly="isViewMode"
                            @update:model-value="updateNodeProperty('textResponse', $event)"
                            class="modern-input mb-3" />
                    </div>

                    <div v-else-if="selectedNode.data.type === 'contactAgent'" class="form-section">
                        <h6 class="section-title">{{ $t('configuration') }}</h6>

                        <!-- Contact Agent Message -->
                        <v-textarea v-model="nodeInspector.contactAgentMessage" :label="$t('contactAgentMessage')"
                            variant="outlined" density="compact" rows="3" auto-grow hide-details="auto"
                            :placeholder="$t('_phEnterAgentHandoff')"
                            @update:model-value="updateNodeProperty('contactAgentMessage', $event)"
                            :readonly="isViewMode" class="modern-input mb-3" />

                        <!-- Bot Disable Time -->
                        <div class="time-field-container mb-3">
                            <v-text-field v-model="nodeInspector.botDisableTime" :label="$t('botDisableTimeMinutes')"
                                variant="outlined" density="compact" hide-details="auto"
                                :placeholder="$t('_phEnterTimeMinutes')" type="number" min="1"
                                @update:model-value="validateAndUpdateBotDisableTime($event)"
                                @keypress="isNumberpositive($event)" :readonly="isViewMode" class="modern-input">
                                <template v-slot:append-inner>
                                    <v-tooltip location="top" max-width="300px">
                                        <template v-slot:activator="{ props }">
                                            <v-icon size="small" color="grey-darken-1" class="info-icon" v-bind="props">
                                                mdi-information-outline
                                            </v-icon>
                                        </template>
                                        <span>Specify the duration for bot deactivation (in minutes) when transferring
                                            to an agent. Minimum value is 1 minute. If
                                            left blank, the bot will remain active.</span>
                                    </v-tooltip>
                                </template>
                            </v-text-field>
                        </div>
                    </div>

                    <!-- Merge Node Configuration -->
                    <div v-else-if="selectedNode.data.type === 'merge'" class="form-section">
                        <h6 class="section-title">{{ $t('mergeConfiguration') }}</h6>
                        
                        <!-- Info Alert -->
                        <v-alert type="info" variant="tonal" density="compact" class="mb-4">
                            <template v-slot:prepend>
                                <v-icon>mdi-information</v-icon>
                            </template>
                            <div class="text-caption">
                                Combine outputs from multiple parallel AI Model nodes into a single result by joining all responses.
                            </div>
                        </v-alert>

                        <!-- Output Settings -->
                        <h6 class="section-title">{{ $t('outputSettings') }}</h6>
                        
                        <!-- Output Format -->
                        <v-select 
                            v-model="nodeInspector.mergeOutputFormat" 
                            :items="[
                                { title: 'Plain Text', value: 'text' },
                                { title: 'JSON', value: 'json' }
                            ]" 
                            item-title="title" 
                            item-value="value"
                            :label="$t('outputFormat')" 
                            variant="outlined" 
                            density="compact" 
                            hide-details="auto"
                            @update:model-value="updateNodeProperty('mergeOutputFormat', $event)"
                            class="modern-input mb-3" 
                            :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="small" color="primary">mdi-format-text</v-icon>
                            </template>
                        </v-select>

                        <!-- Text Separator (only show when output format is text) -->
                        <v-select 
                            v-if="nodeInspector.mergeOutputFormat === 'text'"
                            v-model="nodeInspector.mergeSeparator" 
                            :items="[
                                { title: 'New Line (\\n)', value: 'newline' },
                                { title: 'Double New Line (\\n\\n)', value: 'double_newline' },
                                { title: 'Horizontal Line (---)', value: 'hr' },
                                { title: 'Comma (,)', value: 'comma' },
                                { title: 'Semicolon (;)', value: 'semicolon' },
                                { title: 'Pipe (|)', value: 'pipe' },
                                { title: 'Space', value: 'space' },
                                { title: 'No Separator', value: 'none' },
                                { title: 'Custom', value: 'custom' }
                            ]" 
                            item-title="title" 
                            item-value="value"
                            :label="$t('textSeparator')" 
                            variant="outlined" 
                            density="compact" 
                            hide-details="auto"
                            @update:model-value="updateNodeProperty('mergeSeparator', $event)"
                            class="modern-input mb-3" 
                            :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="small" color="primary">mdi-format-line-spacing</v-icon>
                            </template>
                        </v-select>

                        <!-- Custom Separator (only show when separator is custom) -->
                        <v-text-field 
                            v-if="nodeInspector.mergeOutputFormat === 'text' && nodeInspector.mergeSeparator === 'custom'"
                            v-model="nodeInspector.mergeCustomSeparator" 
                            :label="$t('customSeparator')" 
                            variant="outlined"
                            density="compact" 
                            hide-details="auto" 
                            :placeholder="$t('_phEnterCustomSeparator')"
                            @update:model-value="updateNodeProperty('mergeCustomSeparator', $event)" 
                            class="modern-input mb-3"
                            :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="small" color="primary">mdi-pencil</v-icon>
                            </template>
                        </v-text-field>

                        <!-- Include Labels Toggle (only show when output format is text) -->
                        <v-switch 
                            v-if="nodeInspector.mergeOutputFormat === 'text'"
                            v-model="nodeInspector.mergeIncludeLabels" 
                            :label="$t('includeResponseLabels')"
                            hide-details 
                            density="compact"
                            color="primary"
                            inset
                            @update:model-value="updateNodeProperty('mergeIncludeLabels', $event)"
                            class="mb-3" 
                            :readonly="isViewMode" />

                        <!-- Label Format (only show when Include Labels is enabled) -->
                        <v-text-field 
                            v-if="nodeInspector.mergeOutputFormat === 'text' && nodeInspector.mergeIncludeLabels"
                            v-model="nodeInspector.mergeLabelFormat" 
                            :label="$t('labelFormatOptional')" 
                            variant="outlined"
                            density="compact" 
                            hide-details="auto" 
                            placeholder="Ex: Response ###{n}###"
                            @update:model-value="updateNodeProperty('mergeLabelFormat', $event)" 
                            class="modern-input mb-3"
                            :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="small" color="primary">mdi-label</v-icon>
                            </template>
                        </v-text-field>

                        
                        <h6 class="section-title">{{ $t('usageSetting') }}</h6>
                            <!-- Prompt Usage Type -->
                        <v-select 
                            v-model="nodeInspector.mergePromptUsage" 
                            :items="[
                                { title: 'Use as System Prompt', value: 'system' },
                                { title: 'Use as User Prompt', value: 'user' }
                            ]" 
                            item-title="title" 
                            item-value="value"
                            :label="$t('promptUsageType')" 
                            variant="outlined" 
                            density="compact" 
                            hide-details="auto"
                            @update:model-value="updateNodeProperty('mergePromptUsage', $event)"
                            class="modern-input mb-3" 
                            :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="small" color="primary">mdi-message-cog</v-icon>
                            </template>
                        </v-select>
                    </div>

                    <!-- Email Node Configuration -->
                    <div v-else-if="selectedNode.data.type === 'email'" class="form-section">
                        <h6 class="section-title">{{ $t('mailConfigSource') }}</h6>

                        <v-radio-group
                            v-model="nodeInspector.useCentralConfig"
                            inline density="compact" hide-details="auto"
                            @update:model-value="toggleMailConfigSource($event)"
                            :readonly="isViewMode" class="mb-2">
                            <v-radio :value="true" :label="$t('useCentralConfig')"></v-radio>
                            <v-radio :value="false" :label="$t('customConfig')"></v-radio>
                        </v-radio-group>

                        <v-alert
                            v-if="nodeInspector.useCentralConfig"
                            type="info" variant="tonal" density="compact" class="mb-3">
                            <div class="text-caption">
                                Using mail configuration from <strong>Setting → Core Configurations → Mail Config</strong>.
                                To change provider or credentials, edit them centrally in Setup.
                            </div>
                        </v-alert>

                        <!-- Custom Mail Config -->
                        <div v-if="!nodeInspector.useCentralConfig" class="custom-mail-config mb-3">
                            <v-select
                                v-model="nodeInspector.mailConfig.emailSendingMethod"
                                :items="[
                                    { title: 'SMTP', value: 'SMTP' },
                                    { title: 'Mailgun', value: 'Mailgun' }
                                ]"
                                item-title="title" item-value="value"
                                :label="$t('emailSendingMethodRequired')" variant="outlined" density="compact" hide-details="auto"
                                @update:model-value="updateMailConfigProperty('emailSendingMethod', $event)"
                                class="modern-input mb-3" :readonly="isViewMode" />

                            <!-- SMTP -->
                            <div v-if="nodeInspector.mailConfig.emailSendingMethod === 'SMTP'">
                                <v-row dense>
                                    <v-col cols="6">
                                        <v-select
                                            v-model="nodeInspector.mailConfig.smtpProvider"
                                            :items="[
                                                { title: 'Office 365', value: 'Office365' },
                                                { title: 'Gmail', value: 'Gmail' },
                                                { title: 'Other', value: 'Other' }
                                            ]"
                                            item-title="title" item-value="value"
                                            :label="$t('smtpProviderRequired')" variant="outlined" density="compact" hide-details="auto"
                                            @update:model-value="onSmtpProviderChange($event)"
                                            class="modern-input mb-2" :readonly="isViewMode" />
                                    </v-col>
                                    <v-col cols="6">
                                        <v-select
                                            v-model="nodeInspector.mailConfig.smtpAuthMethod"
                                            :items="[
                                                { title: 'Basic', value: 'Basic' },
                                                { title: 'OAuth2', value: 'OAuth2' }
                                            ]"
                                            item-title="title" item-value="value"
                                            :label="$t('authMethod')" variant="outlined" density="compact" hide-details="auto"
                                            :disabled="nodeInspector.mailConfig.smtpProvider === 'Gmail'"
                                            @update:model-value="updateMailConfigProperty('smtpAuthMethod', $event)"
                                            class="modern-input mb-2" :readonly="isViewMode" />
                                    </v-col>
                                </v-row>

                                <v-row dense>
                                    <v-col cols="6">
                                        <v-text-field
                                            v-model="nodeInspector.mailConfig.smtpHost"
                                            :label="$t('smtpHostRequired')" variant="outlined" density="compact" hide-details="auto"
                                            :placeholder="nodeInspector.mailConfig.smtpProvider === 'Gmail' ? 'smtp.gmail.com' : 'smtp.office365.com'"
                                            :disabled="nodeInspector.mailConfig.smtpProvider !== 'Other'"
                                            @update:model-value="updateMailConfigProperty('smtpHost', $event)"
                                            class="modern-input mb-2" :readonly="isViewMode" />
                                    </v-col>
                                    <v-col cols="3">
                                        <v-text-field
                                            v-model.number="nodeInspector.mailConfig.smtpPort"
                                            type="number" :label="$t('portRequired')" variant="outlined" density="compact" hide-details="auto"
                                            placeholder="587"
                                            :disabled="nodeInspector.mailConfig.smtpProvider !== 'Other'"
                                            @update:model-value="updateMailConfigProperty('smtpPort', Number($event))"
                                            class="modern-input mb-2" :readonly="isViewMode" />
                                    </v-col>
                                    <v-col cols="3">
                                        <v-switch
                                            v-model="nodeInspector.mailConfig.smtpEnableSsl"
                                            color="success" hide-details density="compact" inset
                                            :label="$t('ssl')"
                                            :disabled="nodeInspector.mailConfig.smtpProvider !== 'Other'"
                                            @update:model-value="updateMailConfigProperty('smtpEnableSsl', $event)"
                                            :readonly="isViewMode" />
                                    </v-col>
                                </v-row>

                                <v-row dense>
                                    <v-col cols="6">
                                        <v-text-field
                                            v-model="nodeInspector.mailConfig.emailHost"
                                            :label="$t('fromEmailRequired')" variant="outlined" density="compact" hide-details="auto"
                                            placeholder="sender@example.com"
                                            @update:model-value="updateMailConfigProperty('emailHost', $event)"
                                            class="modern-input mb-2" :readonly="isViewMode" />
                                    </v-col>
                                    <v-col cols="6">
                                        <v-text-field
                                            v-model="nodeInspector.mailConfig.hostName"
                                            :label="$t('displayNameRequired')" variant="outlined" density="compact" hide-details="auto"
                                            placeholder="Your Company"
                                            @update:model-value="updateMailConfigProperty('hostName', $event)"
                                            class="modern-input mb-2" :readonly="isViewMode" />
                                    </v-col>
                                </v-row>

                                <!-- SMTP Basic -->
                                <v-text-field
                                    v-if="nodeInspector.mailConfig.smtpAuthMethod !== 'OAuth2'"
                                    v-model="nodeInspector.mailConfig.password"
                                    :type="nodeInspector.showMailConfigSecret.password ? 'text' : 'password'"
                                    :label="$t('passwordAppPasswordRequired')" variant="outlined" density="compact" hide-details="auto"
                                    :placeholder="isEncryptedSecret(nodeInspector.mailConfig.password) ? '(saved — type to change)' : 'Enter password'"
                                    @update:model-value="onMailConfigSecretChange('password', $event)"
                                    class="modern-input mb-2" :readonly="isViewMode">
                                    <template v-slot:append-inner>
                                        <v-btn icon size="x-small" variant="text" density="compact"
                                            @click="nodeInspector.showMailConfigSecret.password = !nodeInspector.showMailConfigSecret.password">
                                            <v-icon size="small">{{ nodeInspector.showMailConfigSecret.password ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}</v-icon>
                                        </v-btn>
                                    </template>
                                </v-text-field>

                                <!-- SMTP OAuth2 -->
                                <div v-if="nodeInspector.mailConfig.smtpAuthMethod === 'OAuth2'">
                                    <v-text-field
                                        v-model="nodeInspector.mailConfig.smtpOAuth2TenantId"
                                        :type="nodeInspector.showMailConfigSecret.smtpOAuth2TenantId ? 'text' : 'password'"
                                        :label="$t('tenantIdRequired')" variant="outlined" density="compact" hide-details="auto"
                                        :placeholder="isEncryptedSecret(nodeInspector.mailConfig.smtpOAuth2TenantId) ? '(saved — type to change)' : 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'"
                                        @update:model-value="onMailConfigSecretChange('smtpOAuth2TenantId', $event)"
                                        class="modern-input mb-2" :readonly="isViewMode">
                                        <template v-slot:append-inner>
                                            <v-btn icon size="x-small" variant="text" density="compact"
                                                @click="nodeInspector.showMailConfigSecret.smtpOAuth2TenantId = !nodeInspector.showMailConfigSecret.smtpOAuth2TenantId">
                                                <v-icon size="small">{{ nodeInspector.showMailConfigSecret.smtpOAuth2TenantId ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}</v-icon>
                                            </v-btn>
                                        </template>
                                    </v-text-field>
                                    <v-text-field
                                        v-model="nodeInspector.mailConfig.smtpOAuth2ClientId"
                                        :type="nodeInspector.showMailConfigSecret.smtpOAuth2ClientId ? 'text' : 'password'"
                                        :label="$t('clientIdRequired')" variant="outlined" density="compact" hide-details="auto"
                                        :placeholder="isEncryptedSecret(nodeInspector.mailConfig.smtpOAuth2ClientId) ? '(saved — type to change)' : 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'"
                                        @update:model-value="onMailConfigSecretChange('smtpOAuth2ClientId', $event)"
                                        class="modern-input mb-2" :readonly="isViewMode">
                                        <template v-slot:append-inner>
                                            <v-btn icon size="x-small" variant="text" density="compact"
                                                @click="nodeInspector.showMailConfigSecret.smtpOAuth2ClientId = !nodeInspector.showMailConfigSecret.smtpOAuth2ClientId">
                                                <v-icon size="small">{{ nodeInspector.showMailConfigSecret.smtpOAuth2ClientId ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}</v-icon>
                                            </v-btn>
                                        </template>
                                    </v-text-field>
                                    <v-text-field
                                        v-model="nodeInspector.mailConfig.smtpOAuth2ClientSecret"
                                        :type="nodeInspector.showMailConfigSecret.smtpOAuth2ClientSecret ? 'text' : 'password'"
                                        :label="$t('clientSecretRequired')" variant="outlined" density="compact" hide-details="auto"
                                        :placeholder="isEncryptedSecret(nodeInspector.mailConfig.smtpOAuth2ClientSecret) ? '(saved — type to change)' : 'Enter client secret'"
                                        @update:model-value="onMailConfigSecretChange('smtpOAuth2ClientSecret', $event)"
                                        class="modern-input mb-2" :readonly="isViewMode">
                                        <template v-slot:append-inner>
                                            <v-btn icon size="x-small" variant="text" density="compact"
                                                @click="nodeInspector.showMailConfigSecret.smtpOAuth2ClientSecret = !nodeInspector.showMailConfigSecret.smtpOAuth2ClientSecret">
                                                <v-icon size="small">{{ nodeInspector.showMailConfigSecret.smtpOAuth2ClientSecret ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}</v-icon>
                                            </v-btn>
                                        </template>
                                    </v-text-field>
                                </div>
                            </div>

                            <!-- Mailgun -->
                            <div v-if="nodeInspector.mailConfig.emailSendingMethod === 'Mailgun'">
                                <v-text-field
                                    v-model="nodeInspector.mailConfig.mailgunApiUrl"
                                    :label="$t('mailgunApiUrlRequired')" variant="outlined" density="compact" hide-details="auto"
                                    placeholder="https://api.mailgun.net/v3/your-domain/messages"
                                    @update:model-value="updateMailConfigProperty('mailgunApiUrl', $event)"
                                    class="modern-input mb-2" :readonly="isViewMode" />
                                <v-text-field
                                    v-model="nodeInspector.mailConfig.mailgunApiKey"
                                    :type="nodeInspector.showMailConfigSecret.mailgunApiKey ? 'text' : 'password'"
                                    :label="$t('mailgunApiKeyRequired')" variant="outlined" density="compact" hide-details="auto"
                                    :placeholder="isEncryptedSecret(nodeInspector.mailConfig.mailgunApiKey) ? '(saved — type to change)' : 'Enter Mailgun API key'"
                                    @update:model-value="onMailConfigSecretChange('mailgunApiKey', $event)"
                                    class="modern-input mb-2" :readonly="isViewMode">
                                    <template v-slot:append-inner>
                                        <v-btn icon size="x-small" variant="text" density="compact"
                                            @click="nodeInspector.showMailConfigSecret.mailgunApiKey = !nodeInspector.showMailConfigSecret.mailgunApiKey">
                                            <v-icon size="small">{{ nodeInspector.showMailConfigSecret.mailgunApiKey ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}</v-icon>
                                        </v-btn>
                                    </template>
                                </v-text-field>
                                <v-text-field
                                    v-model="nodeInspector.mailConfig.mailFrom"
                                    :label="$t('fromEmailRequired')" variant="outlined" density="compact" hide-details="auto"
                                    placeholder="Support <support@yourdomain.com>"
                                    @update:model-value="updateMailConfigProperty('mailFrom', $event)"
                                    class="modern-input mb-2" :readonly="isViewMode" />
                            </div>
                        </div>

                        <!-- Test Connection -->
                        <v-btn v-if="!isViewMode" size="small" color="primary" variant="tonal"
                            :loading="emailTestLoading" class="mb-3"
                            @click="testEmailNodeConnection">
                            <v-icon size="14" class="mr-1">mdi-lan-connect</v-icon>
                            Test Connection
                        </v-btn>
                        <v-alert v-if="emailTestResult" :type="emailTestResult.success ? 'success' : 'error'"
                            variant="tonal" density="compact" class="mb-3">
                            <div class="text-caption">
                                {{ emailTestResult.message }}
                                <span v-if="emailTestResult.elapsedMs != null"> ({{ emailTestResult.elapsedMs }} ms)</span>
                            </div>
                        </v-alert>

                        <!-- Recipient Configuration -->
                        <h6 class="section-title mt-4">{{ $t('recipientConfiguration') }}</h6>
                        
                        <!-- TO Email -->
                        <v-text-field 
                            v-model="nodeInspector.toEmail" 
                            :label="$t('toEmailRequired')" 
                            variant="outlined"
                            density="compact" 
                            hide-details="auto" 
                            placeholder="recipient@example.com"
                            @update:model-value="updateNodeProperty('toEmail', $event)" 
                            class="modern-input mb-3"
                            :readonly="isViewMode"
                            required>
                            <template v-slot:prepend-inner>
                                <v-icon size="small" color="primary">mdi-email</v-icon>
                            </template>
                        </v-text-field>

                        <!-- CC Emails -->
                        <div class="email-list-section mb-3">
                            <div class="d-flex align-center mb-2">
                                <span class="text-subtitle-2 font-weight-medium">{{ $t('ccEmailsOptional') }}</span>
                                <v-btn v-if="!isViewMode" size="x-small" color="primary" variant="tonal"
                                    @click="addEmailToList('ccEmails')" class="ml-auto">
                                    <v-icon size="12">mdi-plus</v-icon>
                                </v-btn>
                            </div>
                            <div v-for="(email, emailIndex) in nodeInspector.ccEmails || []" :key="emailIndex" 
                                class="d-flex align-center mb-2">
                                <v-text-field 
                                    v-model="nodeInspector.ccEmails[emailIndex]"
                                    placeholder="cc@example.com" 
                                    variant="outlined"
                                    density="compact" 
                                    hide-details="auto"
                                    @update:model-value="updateEmailList('ccEmails')"
                                    class="modern-input flex-grow-1 mr-2"
                                    :readonly="isViewMode" />
                                <v-btn v-if="!isViewMode" icon size="x-small" color="error" variant="text"
                                    @click="removeEmailFromList('ccEmails', emailIndex)">
                                    <v-icon size="14">mdi-close</v-icon>
                                </v-btn>
                            </div>
                        </div>

                        <!-- BCC Emails hide for issues now -->

                        <!-- Email Content -->
                        <h6 class="section-title mt-4">{{ $t('emailContent') }}</h6>
                        
                        <!-- Subject -->
                        <v-text-field 
                            v-model="nodeInspector.subject" 
                            :label="$t('subject')" 
                            variant="outlined"
                            density="compact" 
                            hide-details="auto" 
                            :placeholder="$t('_phEmailSubject')"
                            @update:model-value="updateNodeProperty('subject', $event)" 
                            class="modern-input mb-3"
                            :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="small" color="primary">mdi-format-title</v-icon>
                            </template>
                        </v-text-field>

                        <!-- Email Template Selection -->
                        <v-select 
                            v-model="nodeInspector.emailTemplate" 
                            :items="[
                                { title: 'Custom (Manual Content)', value: 'custom' },
                                { title: 'Registration', value: 'register' },
                                { title: 'Reset Password', value: 'resetPassword' }
                            ]" 
                            item-title="title" 
                            item-value="value"
                            :label="$t('emailTemplate')" 
                            variant="outlined" 
                            density="compact" 
                            hide-details="auto"
                            @update:model-value="updateNodeProperty('emailTemplate', $event)"
                            class="modern-input mb-3" 
                            :readonly="isViewMode"  style="display:none"/>

                        <!-- Email Body (only show for custom template) -->
                        <v-textarea 
                            v-if="!nodeInspector.emailTemplate || nodeInspector.emailTemplate === 'custom'"
                            v-model="nodeInspector.emailBody" 
                            :label="$t('emailBody')" 
                            variant="outlined"
                            density="compact" 
                            rows="6" 
                            auto-grow 
                            hide-details="auto"
                            :placeholder="$t('_phEnterEmailContent')"
                            @update:model-value="updateNodeProperty('emailBody', $event)"
                            class="modern-input mb-3" 
                            :readonly="isViewMode" />

                        <!-- Template Info (show for non-custom templates) -->
                        <v-alert 
                            v-if="nodeInspector.emailTemplate && nodeInspector.emailTemplate !== 'custom'"
                            type="info" 
                            variant="tonal" 
                            density="compact" 
                            class="mb-3">
                            <template v-slot:prepend>
                                <v-icon>mdi-information</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>{{ $t('templateSelectedColon') }}</strong> {{ nodeInspector.emailTemplate }}<br>
                                This template will automatically generate the email content based on context variables.
                            </div>
                        </v-alert>
                    </div>

                    <div v-else-if="selectedNode.data.type === 'switch'" class="form-section">
                        <h6 class="section-title">{{ $t('switchConfiguration') }}</h6>
                        <v-text-field v-model="nodeInspector.switchName" :label="$t('switchName')" variant="outlined"
                            density="compact" hide-details="auto" placeholder="Conditional Switch"
                            @update:model-value="updateNodeProperty('switchName', $event)" class="modern-input mb-3"
                            :readonly="isViewMode" />

                        <!-- Switch Outputs Management -->
                        <div class="switch-outputs-section">
                            <div class="section-header">
                                <h6 class="section-title">{{ $t('outputPaths') }}</h6>
                                <v-btn v-if="!isViewMode" size="small" color="primary" variant="tonal" @click="addInspectorSwitchOutput"
                                    class="add-btn">
                                    <v-icon left size="16">mdi-plus</v-icon>
                                    Add Output
                                </v-btn>
                            </div>

                            <div v-for="(output, outputIndex) in nodeInspector.switchOutputs" :key="output.id"
                                class="switch-output-card mb-3">
                                <v-expansion-panels variant="accordion" class="switch-output-expansion">
                                    <v-expansion-panel>
                                        <v-expansion-panel-title>
                                            <div class="d-flex align-center w-100">
                                                <v-icon class="mr-2" color="primary"
                                                    size="small">mdi-arrow-decision</v-icon>
                                                <span class="font-weight-medium">
                                                    {{
                                                        output.name || `Output ${outputIndex
                                                        + 1}`
                                                    }}
                                                </span>
                                                <v-spacer></v-spacer>
                                                <v-btn v-if="!isViewMode" icon size="x-small" color="error" variant="text"
                                                    @click.stop="removeInspectorSwitchOutput(outputIndex)"
                                                    :disabled="nodeInspector.switchOutputs.length <= 2" class="ml-2">
                                                    <v-icon size="16">mdi-delete</v-icon>
                                                </v-btn>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <div class="switch-output-content pa-2">
                                                <!-- Output Name -->
                                                <v-text-field v-model="nodeInspector.switchOutputs[outputIndex].name"
                                                    :label="$t('outputName')" variant="outlined" density="compact"
                                                    hide-details="auto" @update:model-value="updateSwitchData()"
                                                    class="modern-input mb-3" :readonly="isViewMode" />

                                                <!-- Condition Builder -->
                                                <div class="condition-builder-section mb-3">
                                                    <div class="d-flex align-center mb-2">
                                                        <h6 class="condition-section-title">{{ $t('conditionBuilder') }}</h6>
                                                        <v-spacer></v-spacer>
                                                        <v-btn-toggle
                                                            v-model="nodeInspector.switchOutputs[outputIndex].conditionMode"
                                                            mandatory density="compact" variant="outlined"
                                                            class="condition-mode-toggle" :disabled="isViewMode">
                                                            <v-btn value="simple" size="small" :disabled="isViewMode">
                                                                <v-icon size="14" class="mr-1">mdi-cog</v-icon>
                                                                Simple
                                                            </v-btn>
                                                            <v-btn value="advanced" size="small" :disabled="isViewMode">
                                                                <v-icon size="14" class="mr-1">mdi-code-braces</v-icon>
                                                                Advanced
                                                            </v-btn>
                                                        </v-btn-toggle>
                                                    </div>

                                                    <!-- Simple Condition Builder -->
                                                    <div v-if="!nodeInspector.switchOutputs[outputIndex].conditionMode || nodeInspector.switchOutputs[outputIndex].conditionMode === 'simple'"
                                                        class="simple-condition-builder" :class="{ 'view-mode-disabled': isViewMode }">

                                                        <!-- Condition Type -->
                                                        <v-select
                                                            v-model="nodeInspector.switchOutputs[outputIndex].conditionType"
                                                            :items="[
                                                                { title: 'Text Contains', value: 'text_contains' },
                                                                { title: 'Text Equals', value: 'text_equals' },
                                                                { title: 'Number Comparison', value: 'number_comparison' },
                                                                { title: 'Boolean Check', value: 'boolean_check' },
                                                                { title: 'Regex Match', value: 'regex_match' },
                                                                { title: 'Length Check', value: 'length_check' }
                                                            ]" item-title="title" item-value="value"
                                                            :label="$t('conditionType')" variant="outlined" density="compact"
                                                            hide-details="auto" @update:model-value="updateSwitchData()"
                                                            class="modern-input mb-2" :readonly="isViewMode" />

                                                        <!-- Dynamic Condition Fields -->
                                                        <div v-if="nodeInspector.switchOutputs[outputIndex].conditionType === 'text_contains'"
                                                            class="condition-fields">
                                                            <div class="d-flex gap-2">
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].sourceField"
                                                                    :label="$t('sourceField')" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="input.text"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].searchValue"
                                                                    :label="$t('containsText')" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="keyword"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                            </div>
                                                            <v-checkbox
                                                                v-model="nodeInspector.switchOutputs[outputIndex].caseSensitive"
                                                                :label="$t('caseSensitive')" density="compact" hide-details
                                                                @update:model-value="updateSwitchData()" class="mt-1" />
                                                        </div>

                                                        <div v-else-if="nodeInspector.switchOutputs[outputIndex].conditionType === 'text_equals'"
                                                            class="condition-fields">
                                                            <div class="d-flex gap-2">
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].sourceField"
                                                                    :label="$t('sourceField')" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="input.text"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].compareValue"
                                                                    :label="$t('equalsTo')" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="exact text"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                            </div>
                                                            <v-checkbox
                                                                v-model="nodeInspector.switchOutputs[outputIndex].caseSensitive"
                                                                :label="$t('caseSensitive')" density="compact" hide-details
                                                                @update:model-value="updateSwitchData()" class="mt-1" />
                                                        </div>

                                                        <div v-else-if="nodeInspector.switchOutputs[outputIndex].conditionType === 'number_comparison'"
                                                            class="condition-fields">
                                                            <div class="d-flex gap-2 mb-2">
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].sourceField"
                                                                    :label="$t('numberSource')" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="input.value"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-select
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].operator"
                                                                    :items="[
                                                                        { title: 'Greater than (>)', value: 'gt' },
                                                                        { title: 'Less than (<)', value: 'lt' },
                                                                        { title: 'Equal (=)', value: 'eq' },
                                                                        { title: 'Greater or equal (>=)', value: 'gte' },
                                                                        { title: 'Less or equal (<=)', value: 'lte' },
                                                                        { title: 'Not equal (!=)', value: 'ne' }
                                                                    ]" item-title="title" item-value="value"
                                                                    :label="$t('_Operator')" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].compareValue"
                                                                    :label="$t('compareValue')" type="number"
                                                                    variant="outlined" density="compact"
                                                                    hide-details="auto"
                                                                    @update:model-value="updateSwitchData()"
                                                                    @keypress="isNumberpositive($event)"
                                                                    class="modern-input" />
                                                            </div>
                                                        </div>

                                                        <div v-else-if="nodeInspector.switchOutputs[outputIndex].conditionType === 'boolean_check'"
                                                            class="condition-fields">
                                                            <div class="d-flex gap-2">
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].sourceField"
                                                                    :label="$t('booleanSource')" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="user.isLoggedIn"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-select
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].expectedValue"
                                                                    :items="[
                                                                        { title: 'True', value: true },
                                                                        { title: 'False', value: false }
                                                                    ]" item-title="title" item-value="value"
                                                                    :label="$t('expectedValue')" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                            </div>
                                                        </div>

                                                        <div v-else-if="nodeInspector.switchOutputs[outputIndex].conditionType === 'regex_match'"
                                                            class="condition-fields">
                                                            <div class="d-flex gap-2">
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].sourceField"
                                                                    :label="$t('sourceField')" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="input.text"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].regexPattern"
                                                                    :label="$t('regexPattern')" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="^[0-9]+$"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                            </div>
                                                            <v-text-field
                                                                v-model="nodeInspector.switchOutputs[outputIndex].regexFlags"
                                                                :label="$t('regexFlagsOptional')" variant="outlined"
                                                                density="compact" hide-details="auto" placeholder="gi"
                                                                @update:model-value="updateSwitchData()"
                                                                class="modern-input mt-2" />
                                                        </div>

                                                        <div v-else-if="nodeInspector.switchOutputs[outputIndex].conditionType === 'length_check'"
                                                            class="condition-fields">
                                                            <div class="d-flex gap-2">
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].sourceField"
                                                                    :label="$t('sourceField')" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="input.text"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-select
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].lengthOperator"
                                                                    :items="[
                                                                        { title: 'Greater than', value: 'gt' },
                                                                        { title: 'Less than', value: 'lt' },
                                                                        { title: 'Equal to', value: 'eq' },
                                                                        { title: 'Between', value: 'between' }
                                                                    ]" item-title="title" item-value="value"
                                                                    :label="$t('lengthOperator')" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].lengthValue"
                                                                    :label="$t('lengthValue')" type="number"
                                                                    variant="outlined" density="compact"
                                                                    hide-details="auto"
                                                                    @update:model-value="updateSwitchData()"
                                                                    @keypress="isNumberpositive($event)"
                                                                    class="modern-input" />
                                                            </div>
                                                            <v-text-field
                                                                v-if="nodeInspector.switchOutputs[outputIndex].lengthOperator === 'between'"
                                                                v-model="nodeInspector.switchOutputs[outputIndex].lengthValueMax"
                                                                :label="$t('maxLength')" type="number" variant="outlined"
                                                                density="compact" hide-details="auto"
                                                                @update:model-value="updateSwitchData()"
                                                                @keypress="isNumberpositive($event)"
                                                                class="modern-input mt-2" />
                                                        </div>

                                                        <!-- Generated Condition Preview -->
                                                        <v-alert
                                                            v-if="nodeInspector.switchOutputs[outputIndex].conditionType"
                                                            type="info" variant="tonal" density="compact" class="mt-3">
                                                            <template v-slot:prepend>
                                                                <v-icon>mdi-code-braces</v-icon>
                                                            </template>
                                                            <div class="text-caption">
                                                                <strong>{{ $t('generatedConditionColon') }}</strong><br>
                                                                <code class="condition-preview">
                                                            {{
                                                                generateConditionPreview(nodeInspector.switchOutputs[outputIndex])
                                                            }}
                                                        </code>
                                                            </div>
                                                        </v-alert>
                                                    </div>

                                                    <!-- Advanced Condition Builder -->
                                                    <div v-else class="advanced-condition-builder" :class="{ 'view-mode-disabled': isViewMode }">
                                                        <v-textarea
                                                            v-model="nodeInspector.switchOutputs[outputIndex].condition"
                                                            :label="$t('customConditionExpression')" variant="outlined"
                                                            density="compact" rows="3" auto-grow hide-details="auto"
                                                            placeholder="input.text.toLowerCase().includes('hello') && input.confidence > 0.8"
                                                            @update:model-value="updateSwitchData()"
                                                            class="modern-input" :readonly="isViewMode" />

                                                        <div v-if="!isViewMode" class="condition-hints mt-2">
                                                            <v-chip-group column>
                                                                <v-chip size="small" variant="outlined"
                                                                    @click="insertConditionHelper(outputIndex, 'input.text')"
                                                                    class="hint-chip">input.text</v-chip>
                                                                <v-chip size="small" variant="outlined"
                                                                    @click="insertConditionHelper(outputIndex, 'input.confidence')"
                                                                    class="hint-chip">input.confidence</v-chip>
                                                                <v-chip size="small" variant="outlined"
                                                                    @click="insertConditionHelper(outputIndex, 'user.data')"
                                                                    class="hint-chip">user.data</v-chip>
                                                                <v-chip size="small" variant="outlined"
                                                                    @click="insertConditionHelper(outputIndex, 'context.variables')"
                                                                    class="hint-chip">context.variables</v-chip>
                                                            </v-chip-group>
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- Output Description -->
                                                <v-textarea
                                                    v-model="nodeInspector.switchOutputs[outputIndex].description"
                                                    :label="$t('description')" variant="outlined" density="compact" rows="1"
                                                    auto-grow hide-details="auto" :placeholder="$t('_phOptionalDesc')"
                                                    @update:model-value="updateSwitchData()" class="modern-input" />
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="selectedNode.data.type === 'jsonCondition'" class="form-section json-condition-form">
                        <h6 class="section-title json-condition-form-title">{{ $t('jsonConditionConfiguration') }}</h6>

                        <v-text-field v-model="nodeInspector.jsonConditionName" :label="$t('conditionName')"
                            variant="outlined" density="compact" hide-details="auto" :readonly="isViewMode"
                            @update:model-value="updateJsonConditionData()"
                            class="modern-input modern-input--lg json-condition-input mb-4" />

                        <v-select v-model="nodeInspector.matchMode" :items="jsonConditionMatchModes"
                            :menu-props="{ contentClass: 'json-condition-match-mode-menu' }" item-title="title"
                            item-value="value" :label="$t('matchMode')" variant="outlined"
                            density="compact" hide-details="auto" :readonly="isViewMode"
                            @update:model-value="updateJsonConditionData()"
                            class="modern-input modern-input--lg json-condition-input mb-4" />

                        <div class="section-header json-condition-section-header mb-3">
                            <h6 class="section-title json-condition-section-title mb-0">Rules ({{ nodeInspector.jsonConditions.length }})</h6>
                            <v-btn v-if="!isViewMode" size="small" color="teal" variant="text" @click="addJsonConditionRule"
                                class="json-condition-add-rule-btn">
                                <v-icon size="16" class="mr-1">mdi-plus</v-icon>
                                Add Rule
                            </v-btn>
                        </div>

                        <v-expansion-panels v-model="jsonConditionExpandedPanel" variant="accordion"
                            class="json-condition-rule-panels mb-4">
                            <v-expansion-panel v-for="(condition, conditionIndex) in nodeInspector.jsonConditions"
                                :key="condition.id || conditionIndex" class="json-condition-rule-panel">
                                <v-expansion-panel-title class="json-condition-rule-title">
                                    <div class="json-condition-rule-heading">
                                        Rule {{ conditionIndex + 1 }}: {{ getJsonConditionRuleSummary(condition) }}
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text>
                                    <div class="json-condition-rule-body">
                                        <v-text-field v-model="nodeInspector.jsonConditions[conditionIndex].jsonPath"
                                            :label="$t('jsonPath')" variant="outlined" density="compact" hide-details="auto"
                                            placeholder="matches[0].score" :readonly="isViewMode"
                                            @update:model-value="updateJsonConditionData()"
                                            class="modern-input modern-input--lg json-condition-input mb-4" />

                                        <v-select v-model="nodeInspector.jsonConditions[conditionIndex].operator"
                                            :items="jsonConditionOperators" item-title="title" item-value="value"
                                            :menu-props="{ contentClass: 'json-condition-operator-menu' }"
                                            :item-props="getJsonConditionOperatorProps" :label="$t('_Operator')" variant="outlined"
                                            density="compact" hide-details="auto" :readonly="isViewMode"
                                            @update:model-value="updateJsonConditionData()"
                                            class="modern-input modern-input--lg json-condition-input mb-4">
                                            <template v-slot:item="{ props, item }">
                                                <v-list-item v-if="item.raw.header" class="json-condition-operator-header" disabled :ripple="false">
                                                    <v-list-item-title>— {{ item.raw.title }} —</v-list-item-title>
                                                </v-list-item>
                                                <v-list-item v-else v-bind="props" :title="item.raw.title" />
                                            </template>
                                        </v-select>

                                        <v-text-field v-if="jsonConditionNeedsCompareValue(nodeInspector.jsonConditions[conditionIndex].operator)"
                                            v-model="nodeInspector.jsonConditions[conditionIndex].compareValue"
                                            :label="nodeInspector.jsonConditions[conditionIndex].operator === 'regex' ? 'Regex Pattern' : 'Compare Value'"
                                            :placeholder="nodeInspector.jsonConditions[conditionIndex].operator === 'regex' ? '^[^@]+@[^@]+\.[^@]+$' : ''"
                                            :hint="nodeInspector.jsonConditions[conditionIndex].operator === 'regex' ? 'Use raw regex here, for example ^[^@]+@[^@]+\.[^@]+$. JSON serialization will display the same pattern as \\.' : ''"
                                            :persistent-hint="nodeInspector.jsonConditions[conditionIndex].operator === 'regex'"
                                            variant="outlined" density="compact" hide-details="auto"
                                            :readonly="isViewMode" @update:model-value="updateJsonConditionData()"
                                            class="modern-input modern-input--lg json-condition-input" />

                                        <div v-if="!isViewMode && nodeInspector.jsonConditions.length > 1"
                                            class="json-condition-rule-actions mt-3">
                                            <v-btn color="error" variant="text"
                                                class="json-condition-remove-rule-btn"
                                                @click="removeJsonConditionRule(conditionIndex)">
                                                Remove Rule
                                            </v-btn>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>

                        <div class="json-condition-output-section">
                            <h6 class="section-title json-condition-section-title mb-3">{{ $t('outputLabels') }}</h6>
                            <div class="json-condition-output-grid">
                                <v-text-field v-model="nodeInspector.matchLabel" :label="$t('matchLabel')"
                                    variant="outlined" density="compact" hide-details="auto" :readonly="isViewMode"
                                    @update:model-value="updateJsonConditionData()"
                                    class="modern-input modern-input--lg json-condition-input" />

                                <v-text-field v-model="nodeInspector.fallbackLabel" :label="$t('fallbackLabel')"
                                    variant="outlined" density="compact" hide-details="auto" :readonly="isViewMode"
                                    @update:model-value="updateJsonConditionData()"
                                    class="modern-input modern-input--lg json-condition-input" />
                            </div>
                        </div>
                    </div>

                    <div v-else-if="selectedNode.data.type === 'condition'" class="form-section">
                        <h6 class="section-title">{{ $t('conditionConfiguration') }}</h6>

                        <!-- Condition Type -->
                        <v-select v-model="nodeInspector.conditionType" :items="[
                            { title: 'Value Comparison', value: 'value_comparison' },
                            { title: 'Text Contains', value: 'text_contains' },
                            { title: 'Number Range', value: 'number_range' },
                            { title: 'Boolean Check', value: 'boolean_check' },
                            { title: 'Custom Expression', value: 'custom_expression' }
                        ]" item-title="title" item-value="value" :label="$t('conditionType')" variant="outlined"
                            density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('conditionType', $event)"
                            class="modern-input mb-3" />

                        <!-- Condition Builder based on type -->
                        <div v-if="nodeInspector.conditionType === 'value_comparison'" class="condition-builder mb-3">
                            <div class="row-container">
                                <v-text-field v-model="nodeInspector.leftOperand" :label="$t('leftValue')" variant="outlined"
                                    density="compact" hide-details="auto" placeholder="e.g., input.text"
                                    @update:model-value="updateNodeProperty('leftOperand', $event)"
                                    class="modern-input mb-2" />

                                <v-select v-model="nodeInspector.operator" :items="[
                                    { title: 'Equals', value: 'equals' },
                                    { title: 'Not Equals', value: 'not_equals' },
                                    { title: 'Greater Than', value: 'greater_than' },
                                    { title: 'Less Than', value: 'less_than' },
                                    { title: 'Greater or Equal', value: 'greater_equal' },
                                    { title: 'Less or Equal', value: 'less_equal' }
                                ]" item-title="title" item-value="value" :label="$t('_Operator')" variant="outlined"
                                    density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('operator', $event)"
                                    class="modern-input mb-2" />

                                <v-text-field v-model="nodeInspector.rightOperand" :label="$t('rightValue')"
                                    variant="outlined" density="compact" hide-details="auto" placeholder="e.g., 'hello'"
                                    @update:model-value="updateNodeProperty('rightOperand', $event)"
                                    class="modern-input mb-2" />
                            </div>
                        </div>

                        <div v-else-if="nodeInspector.conditionType === 'text_contains'" class="condition-builder mb-3">
                            <v-text-field v-model="nodeInspector.leftOperand" :label="$t('textSource')" variant="outlined"
                                density="compact" hide-details="auto" placeholder="e.g., input.text"
                                @update:model-value="updateNodeProperty('leftOperand', $event)"
                                class="modern-input mb-2" />

                            <v-text-field v-model="nodeInspector.rightOperand" :label="$t('containsText')" variant="outlined"
                                density="compact" hide-details="auto" placeholder="e.g., 'keyword'"
                                @update:model-value="updateNodeProperty('rightOperand', $event)"
                                class="modern-input mb-2" />
                        </div>

                        <div v-else-if="nodeInspector.conditionType === 'number_range'" class="condition-builder mb-3">
                            <v-text-field v-model="nodeInspector.leftOperand" :label="$t('numberSource')" variant="outlined"
                                density="compact" hide-details="auto" placeholder="e.g., input.value"
                                @update:model-value="updateNodeProperty('leftOperand', $event)"
                                class="modern-input mb-2" />

                            <div class="d-flex gap-2">
                                <v-text-field v-model="nodeInspector.minValue" :label="$t('minValue')" type="number"
                                    variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('minValue', $event)"
                                    @keypress="isNumberpositive($event)" class="modern-input" />

                                <v-text-field v-model="nodeInspector.maxValue" :label="$t('maxValue')" type="number"
                                    variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('maxValue', $event)"
                                    @keypress="isNumberpositive($event)" class="modern-input" />
                            </div>
                        </div>

                        <div v-else-if="nodeInspector.conditionType === 'boolean_check'" class="condition-builder mb-3">
                            <v-text-field v-model="nodeInspector.leftOperand" :label="$t('booleanSource')" variant="outlined"
                                density="compact" hide-details="auto" placeholder="e.g., user.isLoggedIn"
                                @update:model-value="updateNodeProperty('leftOperand', $event)"
                                class="modern-input mb-2" />

                            <v-switch v-model="nodeInspector.expectedValue" :label="$t('expectedValue')" color="primary"
                                hide-details @update:model-value="updateNodeProperty('expectedValue', $event)"
                                class="modern-switch" />
                        </div>

                        <div v-else-if="nodeInspector.conditionType === 'custom_expression'"
                            class="condition-builder mb-3">
                            <v-textarea v-model="nodeInspector.conditionExpression" :label="$t('customExpression')"
                                variant="outlined" density="compact" rows="2" auto-grow hide-details="auto"
                                placeholder="e.g., input.text.length > 5 && input.text.includes('hello')"
                                @update:model-value="updateNodeProperty('conditionExpression', $event)"
                                class="modern-input" />
                        </div>

                        <!-- Output Labels -->
                        <div class="output-labels-section">
                            <h6 class="section-title mb-2">{{ $t('outputLabels') }}</h6>
                            <div class="d-flex gap-2">
                                <v-text-field v-model="nodeInspector.trueLabel" :label="$t('trueOutputLabel')"
                                    variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('trueLabel', $event)"
                                    class="modern-input" />

                                <v-text-field v-model="nodeInspector.falseLabel" :label="$t('falseOutputLabel')"
                                    variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('falseLabel', $event)"
                                    class="modern-input" />
                            </div>
                        </div>

                        <!-- Description -->
                        <v-textarea v-model="nodeInspector.description" :label="$t('description')" variant="outlined"
                            density="compact" rows="2" auto-grow hide-details="auto"
                            :placeholder="$t('_phOptionalDescCondition')"
                            @update:model-value="updateNodeProperty('description', $event)" class="modern-input mt-3" />
                    </div>

                    <div v-else-if="selectedNode.data.type === 'delay'" class="form-section">
                        <h6 class="section-title">{{ $t('delayConfiguration') }}</h6>

                        <!-- Delay Type -->
                        <v-select v-model="nodeInspector.delayType" :items="[
                            { title: 'Fixed Duration', value: 'fixed' },
                            { title: 'Random Range', value: 'random' },
                            { title: 'Variable Delay', value: 'variable' }
                        ]" item-title="title" item-value="value" :label="$t('delayType')" variant="outlined"
                            density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('delayType', $event)" class="modern-input mb-3"
                            :readonly="isViewMode" />

                        <!-- Fixed Duration Configuration -->
                        <div v-if="nodeInspector.delayType === 'fixed'" class="delay-config-section mb-3">
                            <div class="d-flex gap-2">
                                <v-text-field v-model="nodeInspector.delayValue" :label="$t('duration')" type="number" min="0"
                                    step="0.1" variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('delayValue', $event)"
                                    @keypress="isNumberpositive($event)" class="modern-input flex-grow-1"
                                    :readonly="isViewMode" />

                                <v-select v-model="nodeInspector.delayUnit" :items="[
                                    { title: 'Seconds', value: 'seconds' },
                                    { title: 'Minutes', value: 'minutes' },
                                    { title: 'Hours', value: 'hours' }
                                ]" item-title="title" item-value="value" :label="$t('unit')" variant="outlined"
                                    density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('delayUnit', $event)" class="modern-input"
                                    style="min-width: 100px;" :readonly="isViewMode" />
                            </div>
                        </div>

                        <!-- Random Range Configuration -->
                        <div v-else-if="nodeInspector.delayType === 'random'" class="delay-config-section mb-3">
                            <div class="d-flex gap-2 mb-2">
                                <v-text-field v-model="nodeInspector.randomMin" :label="$t('minDuration')" type="number"
                                    min="0" step="0.1" variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('randomMin', $event)"
                                    class="modern-input" :readonly="isViewMode" />

                                <v-text-field v-model="nodeInspector.randomMax" :label="$t('maxDuration')" type="number"
                                    min="0" step="0.1" variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('randomMax', $event)"
                                    class="modern-input" :readonly="isViewMode" />
                            </div>

                            <v-select v-model="nodeInspector.delayUnit" :items="[
                                { title: 'Seconds', value: 'seconds' },
                                { title: 'Minutes', value: 'minutes' },
                                { title: 'Hours', value: 'hours' }
                            ]" item-title="title" item-value="value" :label="$t('unit')" variant="outlined" density="compact"
                                hide-details="auto" @update:model-value="updateNodeProperty('delayUnit', $event)"
                                class="modern-input" :readonly="isViewMode" />
                        </div>

                        <!-- Variable Delay Configuration -->
                        <div v-else-if="nodeInspector.delayType === 'variable'" class="delay-config-section mb-3">
                            <v-text-field v-model="nodeInspector.delayExpression" :label="$t('delayExpression')"
                                variant="outlined" density="compact" hide-details="auto"
                                placeholder="e.g., input.length * 0.5"
                                @update:model-value="updateNodeProperty('delayExpression', $event)"
                                class="modern-input mb-2" :readonly="isViewMode" />

                            <v-select v-model="nodeInspector.delayUnit" :items="[
                                { title: 'Seconds', value: 'seconds' },
                                { title: 'Minutes', value: 'minutes' },
                                { title: 'Hours', value: 'hours' }
                            ]" item-title="title" item-value="value" :label="$t('unit')" variant="outlined" density="compact"
                                hide-details="auto" @update:model-value="updateNodeProperty('delayUnit', $event)"
                                class="modern-input" :readonly="isViewMode" />
                        </div>

                        <!-- Delay Message -->
                        <v-text-field v-model="nodeInspector.delayMessage" :label="$t('delayMessageOptional')"
                            variant="outlined" density="compact" hide-details="auto"
                            :placeholder="$t('_phPleaseWaitProcessing')"
                            @update:model-value="updateNodeProperty('delayMessage', $event)"
                            class="modern-input mb-3" :readonly="isViewMode" />

                        <!-- Progress Indicator -->
                        <v-switch v-model="nodeInspector.showProgress" :label="$t('showProgressIndicator')" color="primary"
                            hide-details @update:model-value="updateNodeProperty('showProgress', $event)"
                            class="modern-switch mb-3" :readonly="isViewMode" />

                        <!-- Duration Preview -->
                        <div class="duration-preview">
                            <v-alert type="info" variant="tonal" density="compact" class="mb-2">
                                <template v-slot:prepend>
                                    <v-icon>mdi-clock-outline</v-icon>
                                </template>
                                <div class="text-caption">
                                    <strong>{{ $t('durationPreviewColon') }}</strong><br>
                                    <span v-if="nodeInspector.delayType === 'fixed'">
                                        {{ nodeInspector.delayValue }} {{ nodeInspector.delayUnit }}
                                    </span>
                                    <span v-else-if="nodeInspector.delayType === 'random'">
                                        {{ nodeInspector.randomMin }} - {{ nodeInspector.randomMax }} {{
                                            nodeInspector.delayUnit
                                        }}
                                    </span>
                                    <span v-else-if="nodeInspector.delayType === 'variable'">
                                        Variable based on expression
                                    </span>
                                </div>
                            </v-alert>
                        </div>

                        <!-- Description -->
                        <v-textarea v-model="nodeInspector.description" :label="$t('description')" variant="outlined"
                            density="compact" rows="2" auto-grow hide-details="auto"
                            :placeholder="$t('_phOptionalDescDelay')"
                            @update:model-value="updateNodeProperty('description', $event)" class="modern-input"
                            :readonly="isViewMode" />
                    </div>

                    <!-- Documents Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'documents'" class="form-section">
                        <h6 class="section-title">{{ $t('documentsConfiguration') }}</h6>
                        <!-- Document Selection -->
                        <v-autocomplete v-model="nodeInspector.document" :items="DDVector" item-title="name"
                            item-value="name" :label="$t('ddDocument')" variant="outlined" density="compact" hide-details="auto"
                            chips multiple @update:model-value="updateNodeProperty('document', $event);"
                            @click:clear="resetDocumentDataAndSelection" class="modern-input mb-3"
                            :placeholder="$t('_phSelectDocuments')" :clearable="!isViewMode" :readonly="isViewMode">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="document-option">
                                    <template v-slot:prepend>
                                        <v-icon size="20" color="primary">mdi-file-document</v-icon>
                                    </template>
                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                </v-list-item>
                            </template>
                        </v-autocomplete>

                        <!-- Document Data Selection Section -->
                        <div v-if="nodeInspector.document && nodeInspector.document.length > 0"
                            class="document-data-section mb-3">
                            <!-- Document Data Selection Header with Toggle -->
                            <div style="display: flex;align-items: center;justify-content: space-between;">
                                <!-- Enable Specific Data Selection Switch -->
                                <div style="display: flex; align-items: center;">
                                    <v-switch v-model="nodeInspector.enableSpecificDataSelection" color="primary"
                                        hide-details density="compact"
                                        @update:model-value="onSpecificDataSelectionChange($event)"
                                        :readonly="isViewMode" class="mr-2" inset style="transform: scale(0.8);">
                                    </v-switch>
                                    <span class="text-subtitle-2">
                                        Use Specific Data Selection
                                    </span>
                                </div>
                                <!-- Expand/Collapse Button -->
                                <v-btn v-if="nodeInspector.enableSpecificDataSelection"
                                    @click="documentDataExpanded = !documentDataExpanded" icon variant="text"
                                    size="small" color="primary">
                                    <v-icon>{{ documentDataExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                </v-btn>
                            </div>

                            <!-- Expandable Content -->
                            <v-expand-transition>
                                <div v-show="documentDataExpanded && nodeInspector.enableSpecificDataSelection"
                                    class="document-data-content pa-3 bg-grey-lighten-5 rounded-lg">

                                    <h6 class="section-title mb-0">
                                        <v-icon size="20" class="mr-2 mb-4" color="primary">mdi-file-search</v-icon>
                                        Select Document Data
                                    </h6>
                                    <!-- Vector Name Selection for Document Data -->
                                    <v-autocomplete v-if="!isViewMode" v-model="nodeInspector.documentVectorName"
                                        :items="nodeInspector.document" :label="$t('selectDocumentForData')"
                                        variant="outlined" density="compact" hide-details="auto"
                                        @update:model-value="updateNodeProperty('documentVectorName', $event); getDocumentData();"
                                        class="modern-input mb-3" clearable :readonly="isViewMode">
                                        <template v-slot:prepend-inner>
                                            <v-icon size="20" color="primary">mdi-file-document-outline</v-icon>
                                        </template>
                                        <template v-slot:append-inner>
                                            <v-tooltip location="top">
                                                <template v-slot:activator="{ props }">
                                                    <v-icon v-bind="props" size="small" color="grey-darken-1">
                                                        mdi-information-outline
                                                    </v-icon>
                                                </template>
                                                <span>{{ $t('_selectDocumentToView') }}</span>
                                            </v-tooltip>
                                        </template>
                                    </v-autocomplete>

                                    <!-- Selected Document Data Display -->
                                    <div class="selected-document-data mb-4"
                                        v-if="nodeInspector.selectedDocumentData && nodeInspector.selectedDocumentData.length > 0">
                                        <div class="mb-2">
                                            <span class="text-subtitle-2 font-weight-medium text-primary">
                                                <v-icon size="16" class="mr-1">mdi-check-circle</v-icon>
                                                Selected Document Data:
                                            </span>
                                        </div>
                                        <div class="d-flex flex-wrap ga-2">
                                            <v-chip v-for="(item, dataIndex) in nodeInspector.selectedDocumentData"
                                                :key="dataIndex + '_DocumentData'" :closable="!isViewMode"
                                                @click:close="deleteSelectedDocumentData(item.id)" color="primary"
                                                variant="elevated" size="small" class="selected-document-data-chip">
                                                <v-tooltip location="top" open-delay="500">
                                                    <template v-slot:activator="{ props }">
                                                        <span v-bind="props" class="chip-text">{{
                                                            item.textORG.length > 50 ? item.textORG.substring(0, 50) +
                                                                '...' : item.textORG
                                                        }}</span>
                                                    </template>
                                                    <span>{{ item.textORG }}</span>
                                                </v-tooltip>
                                            </v-chip>
                                        </div>
                                    </div>

                                    <div v-if="loadingDocumentDataTable && nodeInspector.documentVectorName"
                                        class="text-center mb-4">
                                        <v-progress-circular indeterminate color="primary"
                                            size="24"></v-progress-circular>

                                    </div>

                                    <!-- Document Data Table -->
                                    <v-data-table
                                        v-show="showDocumentDataTable && nodeInspector.enableSpecificDataSelection"
                                        :items-per-page="documentDataItemsPerPage"
                                        :server-items-length="documentDataTotalTable"
                                        v-model:options="documentDataOptions" :items="documentDataTable"
                                        :headers="documentDataHeaders" loading-text="Loading document data..."
                                        fixed-header class="elevation-2 document-data-table mb-4 rounded-lg"
                                        density="comfortable">
                                        <template v-slot:item="{ item }">
                                            <tr>
                                                <td class="text-center pa-2">
                                                    <v-checkbox v-model="item.selected"
                                                        @change="selectDocumentData(item.selected, item.id, item.textORG)"
                                                        hide-details density="compact" color="primary">
                                                    </v-checkbox>
                                                </td>
                                                <td class="pa-2">
                                                    <div class="text-container">
                                                        <span
                                                            :class="[item.show ? 'text-content-show' : 'text-content']"
                                                            v-html="formatText(item.textORG)">
                                                        </span>
                                                    </div>
                                                </td>
                                                <td class="pa-2">
                                                    <div class="text-container">
                                                        <span
                                                            :class="[item.show ? 'text-content-show' : 'text-content']"
                                                            v-html="formatText(item.textEN)">
                                                        </span>
                                                        <v-btn @click="item.show = !item.show" icon size="small"
                                                            variant="text" class="ml-2">
                                                            <v-icon>{{
                                                                item.show ? "mdi-chevron-up" : "mdi-chevron-down"
                                                            }}</v-icon>
                                                        </v-btn>
                                                    </div>
                                                </td>
                                            </tr>
                                        </template>
                                    </v-data-table>

                                </div>
                            </v-expand-transition>
                        </div>
                        <v-divider class="my-4"></v-divider>

                        <!-- Text Replacement -->
                        <v-autocomplete v-model="nodeInspector.textReplacement" :items="DDGroupReplace"
                            item-title="name" item-value="id" :label="$t('textReplacement')" variant="outlined"
                            density="compact" hide-details="auto" chips multiple
                            @update:model-value="updateNodeProperty('textReplacement', $event)"
                            class="modern-input mb-3" :placeholder="$t('_phSelectTextReplacement')"
                            :clearable="!isViewMode" :readonly="isViewMode">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="text-replacement-option">
                                    <template v-slot:prepend>
                                        <v-icon size="20" color="primary">mdi-find-replace</v-icon>
                                    </template>
                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                </v-list-item>
                            </template>
                        </v-autocomplete>
                        <!-- Top-N Chunks -->
                        <v-text-field v-model="nodeInspector.topNChunks" type="number" :label="$t('topNChunks')"
                            variant="outlined" density="compact" hide-details="auto" :readonly="isViewMode" min="1"
                            step="1" @focus="$event.target.select()" 
                            @blur="validateAndFixTopNChunks($event)"
                            @keydown.enter="validateAndFixTopNChunks($event)"
                            class="modern-input mb-3" />
                        <!-- Cosine Similarity -->
                        <v-text-field v-model="nodeInspector.cosineSimilarity" type="number" :label="$t('cosineSimilarity')"
                            :hint="$t('_cosineSimilarityHint')" persistent-hint
                            variant="outlined" density="compact" hide-details="auto" :readonly="isViewMode" min="0.0001"
                            max="1" step="0.0001"
                            @focus="$event.target.select()"
                            @blur="validateAndFixCosineSimilarity($event)"
                            @keydown.enter="validateAndFixCosineSimilarity($event)"
                            class="modern-input" />
                    </div>

                    <!-- DB Integration Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'databaseIntegration'" class="form-section">
                        <h6 class="section-title">{{ $t('databaseIntegrationConfiguration') }}</h6>

                        <!-- Table Name Selection -->
                        <v-autocomplete v-model="nodeInspector.tableName" :items="listDataIntegration"
                            item-title="dataName" item-value="id" :label="$t('tableName')" variant="outlined"
                            density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('tableName', $event)" class="modern-input mb-3"
                            :placeholder="$t('_phSelectDatabaseTable')" :clearable="!isViewMode" :readonly="isViewMode">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="table-option">
                                    <template v-slot:prepend>
                                        <v-icon size="20" color="primary">mdi-table</v-icon>
                                    </template>
                                    <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                </v-list-item>
                            </template>
                        </v-autocomplete>

                        <!-- SQL Extraction Model Selection -->
                        <v-autocomplete v-model="nodeInspector.sqlExtractionModel" :items="listSqlExtract"
                            item-title="name" item-value="modelDisplayID" :label="$t('sqlExtractionModel')"
                            variant="outlined" density="compact" hide-details="auto" :clearable="!isViewMode"
                            :readonly="isViewMode"
                            @update:model-value="updateNodeProperty('sqlExtractionModel', $event)"
                            class="modern-input mb-3" :placeholder="$t('_phSelectModelSql')">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="model-option">
                                    <template v-slot:prepend>
                                        <div class="model-icon-small mr-2" :style="{ backgroundColor: item.raw.color }">
                                            <v-icon size="14" color="green">mdi-robot</v-icon>
                                        </div>
                                    </template>
                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                </v-list-item>
                            </template>
                        </v-autocomplete>

                        <!-- Prompt for No Data -->
                        <v-textarea v-model="nodeInspector.promptSqlNoData" :label="$t('promptForNoData')"
                            variant="outlined" density="compact" rows="3" auto-grow hide-details="auto"
                            :placeholder="$t('_phEnterNoDataMessage')"
                            @update:model-value="updateNodeProperty('promptSqlNoData', $event)"
                            class="modern-input mb-3" :readonly="isViewMode" />
                    </div>

                    <!-- Knowledge Graph Node Properties (S15-P1 · plugin ใต้ AI Model · ค่าเป็น graphKey) -->
                    <div v-else-if="selectedNode.data.type === 'knowledgeGraph'" class="form-section">
                        <h6 class="section-title">{{ $t('flowpg_nodeTypeKnowledgeGraph') }}</h6>

                        <v-autocomplete v-model="nodeInspector.knowledgeGraphIds" data-testid="kg-node-graphs"
                            :items="DDKnowledgeGraphs" item-title="name" item-value="graphKey" :loading="loadingKnowledgeGraphs"
                            :label="$t('graphRagModelSection')" :hint="$t('flowpg_kgSelectGraphsHint')" persistent-hint
                            :no-data-text="$t('graphRagModelNoGraphs')"
                            variant="outlined" density="compact" hide-details="auto" chips :closable-chips="!isViewMode" multiple
                            :clearable="!isViewMode" :readonly="isViewMode" class="modern-input mb-3"
                            @update:model-value="updateNodeProperty('knowledgeGraphIds', $event)">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" :title="item.raw.name" :subtitle="item.raw.graphKey" />
                            </template>
                        </v-autocomplete>

                        <!-- empty state: ไม่มีกราฟให้เลือก / โหลดล้ม → บอกทางไปสร้าง (tab ใหม่ ไม่ทิ้ง flow ที่ยังไม่ save) · ไม่ toast -->
                        <div v-if="!loadingKnowledgeGraphs && DDKnowledgeGraphs.length === 0" class="text-caption text-medium-emphasis mb-3" data-testid="kg-node-no-graphs">
                            {{ $t('flowpg_kgNoGraphsGoCreate') }}
                            <a href="/KnowledgeGraphs" target="_blank" rel="noopener">{{ $t('flowpg_kgOpenGraphManager') }} ↗</a>
                        </div>

                        <!-- Advanced Retrieval Settings — โผล่เมื่อเลือก ≥ 1 กราฟ · เริ่มพับ · ใช้ label/hint ชุด graphRagModel* เดียวกับ Model Creator -->
                        <v-expansion-panels v-if="(nodeInspector.knowledgeGraphIds || []).length > 0" variant="accordion" class="mt-2 mb-2">
                            <v-expansion-panel>
                                <v-expansion-panel-title data-testid="kg-node-advanced">{{ $t('graphRagModelAdvanced') }}</v-expansion-panel-title>
                                <v-expansion-panel-text>
                                    <span class="text-caption text-medium-emphasis">{{ $t('graphRagModelAdvancedHint') }}</span>

                                    <v-switch v-model="nodeInspector.kgRetrievalConfig.UseCitationContext" data-testid="kg-node-citation"
                                        :label="$t('graphRagModelUseCitation')" color="primary" hide-details density="compact" :readonly="isViewMode"
                                        @update:model-value="updateNodeProperty('kgRetrievalConfig.UseCitationContext', $event)" />
                                    <span class="text-caption text-medium-emphasis d-block mb-2">{{ $t('graphRagModelUseCitationHint') }}</span>

                                    <!-- ✏️ 2026-10-20 ตัดช่อง Citation Prompt ออก (ผู้ใช้ขอ) — เปิด citation = ใช้กติกามาตรฐานเสมอ (normalizeKgRetrievalConfig) -->

                                    <v-row dense>
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="nodeInspector.kgRetrievalConfig.MaxHops" data-testid="kg-node-max-hops"
                                                type="number" min="1" max="10" :label="$t('graphRagModelMaxHops')" :hint="$t('graphRagModelMaxHopsHint')" persistent-hint
                                                :rules="[kgRangeRule('graphRagModelMaxHops', 1, 10)]" density="compact" variant="outlined" :readonly="isViewMode"
                                                @update:model-value="updateKgRetrievalNumber('MaxHops', $event)" />
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-select v-model="nodeInspector.kgRetrievalConfig.SearchMode" data-testid="kg-node-search-mode"
                                                :items="kgSearchModes" :label="$t('graphRagModelSearchMode')" :hint="$t('graphRagModelSearchModeHint')" persistent-hint
                                                density="compact" variant="outlined" :readonly="isViewMode"
                                                @update:model-value="updateNodeProperty('kgRetrievalConfig.SearchMode', $event)" />
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="nodeInspector.kgRetrievalConfig.FuzzyThreshold" data-testid="kg-node-fuzzy"
                                                type="number" min="0" max="100" :label="$t('graphRagModelFuzzyThreshold')" :hint="$t('graphRagModelFuzzyThresholdHint')" persistent-hint
                                                :rules="[kgRangeRule('graphRagModelFuzzyThreshold', 0, 100)]" density="compact" variant="outlined" :readonly="isViewMode"
                                                @update:model-value="updateKgRetrievalNumber('FuzzyThreshold', $event)" />
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="nodeInspector.kgRetrievalConfig.SemanticThreshold" data-testid="kg-node-semantic"
                                                type="number" min="0" max="1" step="0.01" :label="$t('graphRagModelSemanticThreshold')" :hint="$t('graphRagModelSemanticThresholdHint')" persistent-hint
                                                :rules="[kgRangeRule('graphRagModelSemanticThreshold', 0, 1)]" density="compact" variant="outlined" :readonly="isViewMode"
                                                @update:model-value="updateKgRetrievalNumber('SemanticThreshold', $event)" />
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="nodeInspector.kgRetrievalConfig.MaxContextNodes" data-testid="kg-node-max-nodes"
                                                type="number" min="1" max="200" :label="$t('graphRagModelMaxContextNodes')" :hint="$t('graphRagModelMaxContextNodesHint')" persistent-hint
                                                :rules="[kgRangeRule('graphRagModelMaxContextNodes', 1, 200)]" density="compact" variant="outlined" :readonly="isViewMode"
                                                @update:model-value="updateKgRetrievalNumber('MaxContextNodes', $event)" />
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="nodeInspector.kgRetrievalConfig.MaxContextEdges" data-testid="kg-node-max-edges"
                                                type="number" min="1" max="400" :label="$t('graphRagModelMaxContextEdges')" :hint="$t('graphRagModelMaxContextEdgesHint')" persistent-hint
                                                :rules="[kgRangeRule('graphRagModelMaxContextEdges', 1, 400)]" density="compact" variant="outlined" :readonly="isViewMode"
                                                @update:model-value="updateKgRetrievalNumber('MaxContextEdges', $event)" />
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="nodeInspector.kgRetrievalConfig.BaselineTopK" data-testid="kg-node-baseline-topk"
                                                type="number" min="1" max="100" :clearable="!isViewMode" :label="$t('graphRagModelBaselineTopK')" :hint="$t('graphRagModelBaselineTopKHint')" persistent-hint
                                                :rules="[kgRangeRule('graphRagModelBaselineTopK', 1, 100)]" density="compact" variant="outlined" :readonly="isViewMode"
                                                @update:model-value="updateKgRetrievalNumber('BaselineTopK', $event)" />
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="nodeInspector.kgRetrievalConfig.BaselineMinSimilarity" data-testid="kg-node-baseline-minsim"
                                                type="number" min="0" max="1" step="0.01" :clearable="!isViewMode" :label="$t('graphRagModelBaselineMinSim')" :hint="$t('graphRagModelBaselineMinSimHint')" persistent-hint
                                                :rules="[kgRangeRule('graphRagModelBaselineMinSim', 0, 1)]" density="compact" variant="outlined" :readonly="isViewMode"
                                                @update:model-value="updateKgRetrievalNumber('BaselineMinSimilarity', $event)" />
                                        </v-col>
                                    </v-row>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </div>

                    <!-- Waterfall Search Node Properties (S15-P1 · identifier nexaWaterfallSearch ตาม ajt · ไม่มีชื่อ vendor บนจอ — D7) -->
                    <div v-else-if="selectedNode.data.type === 'nexaWaterfallSearch'" class="form-section">
                        <h6 class="section-title">{{ $t('flowpg_nodeTypeWaterfallSearch') }}</h6>
                        <p class="text-caption text-medium-emphasis mb-3">{{ $t('flowpg_wfDescription') }}</p>

                        <div class="d-flex align-center justify-space-between mb-2">
                            <span class="text-subtitle-2">{{ $t('flowpg_wfTiersTitle') }} ({{ (nodeInspector.graphTiers || []).length }}/10)</span>
                            <!-- @click guard ด้วย predicate เดียวกับ :disabled (กฎ CLAUDE.md) + addWaterfallTier กันซ้ำอีกชั้น -->
                            <v-btn size="small" color="primary" variant="tonal" data-testid="wf-node-add-tier"
                                :disabled="isViewMode || (nodeInspector.graphTiers || []).length >= 10"
                                @click="!(isViewMode || (nodeInspector.graphTiers || []).length >= 10) && addWaterfallTier()">
                                <v-icon start size="16">mdi-plus</v-icon>{{ $t('flowpg_wfAddTier') }}
                            </v-btn>
                        </div>

                        <div v-if="!nodeInspector.graphTiers || nodeInspector.graphTiers.length === 0" class="text-caption text-medium-emphasis mb-3" data-testid="wf-node-no-tiers">
                            {{ $t('flowpg_wfNoTiersHint') }}
                        </div>

                        <!-- :key = _key runtime ของ tier (ไม่ใช่ index — §1.4) · testid ยังใช้ index เพราะสเปกกำหนด wf-node-tier-{i} -->
                        <v-card v-for="(tier, i) in nodeInspector.graphTiers" :key="tier._key" variant="outlined" class="mb-3 pa-3" :data-testid="'wf-node-tier-' + i">
                            <div class="d-flex align-center justify-space-between mb-2">
                                <span class="text-subtitle-2">{{ $t('flowpg_wfTier') }} {{ i + 1 }}</span>
                                <div>
                                    <v-btn icon size="x-small" variant="text" :data-testid="'wf-node-tier-' + i + '-up'"
                                        :disabled="isViewMode || i === 0" @click="!(isViewMode || i === 0) && moveWaterfallTierUp(i)"
                                        :title="$t('flowpg_moveUp')" :aria-label="$t('flowpg_moveUp')">
                                        <v-icon size="16">mdi-arrow-up</v-icon>
                                    </v-btn>
                                    <v-btn icon size="x-small" variant="text" :data-testid="'wf-node-tier-' + i + '-down'"
                                        :disabled="isViewMode || i === nodeInspector.graphTiers.length - 1" @click="!(isViewMode || i === nodeInspector.graphTiers.length - 1) && moveWaterfallTierDown(i)"
                                        :title="$t('flowpg_moveDown')" :aria-label="$t('flowpg_moveDown')">
                                        <v-icon size="16">mdi-arrow-down</v-icon>
                                    </v-btn>
                                    <v-btn icon size="x-small" variant="text" color="error" :data-testid="'wf-node-tier-' + i + '-remove'"
                                        :disabled="isViewMode" @click="!isViewMode && removeWaterfallTier(i)"
                                        :title="$t('flowpg_wfRemoveTier')" :aria-label="$t('flowpg_wfRemoveTier')">
                                        <v-icon size="16">mdi-delete</v-icon>
                                    </v-btn>
                                </div>
                            </div>

                            <!-- one-way binding (:model-value) — ค่าจริงอยู่ที่ node.data.graphTiers ผ่าน updateWaterfallTierProperty (ส่ง array ทั้งก้อน) -->
                            <v-autocomplete :model-value="tier.graphId" :data-testid="'wf-node-tier-' + i + '-graph'"
                                :items="DDKnowledgeGraphs" item-title="name" item-value="graphKey" :loading="loadingKnowledgeGraphs"
                                :no-data-text="$t('graphRagModelNoGraphs')" :label="$t('graphRagModelSection')"
                                :rules="[v => !!v || $t('flowpg_wfTierGraphRequired')]"
                                variant="outlined" density="compact" hide-details="auto" class="mb-2"
                                :clearable="!isViewMode" :readonly="isViewMode"
                                @update:model-value="updateWaterfallTierProperty(i, 'graphId', $event)">
                                <template v-slot:item="{ props, item }">
                                    <v-list-item v-bind="props" :title="item.raw.name" :subtitle="item.raw.graphKey" />
                                </template>
                            </v-autocomplete>

                            <v-row dense>
                                <v-col cols="12" sm="6">
                                    <v-select :model-value="tier.searchMode" :items="kgSearchModes" :label="$t('graphRagModelSearchMode')"
                                        density="compact" variant="outlined" hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateWaterfallTierProperty(i, 'searchMode', $event)" />
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-text-field :model-value="tier.maxHops" type="number" min="1" max="10" :label="$t('graphRagModelMaxHops')"
                                        :rules="[kgRangeRule('graphRagModelMaxHops', 1, 10)]" density="compact" variant="outlined" hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateWaterfallTierProperty(i, 'maxHops', $event)" />
                                </v-col>
                                <v-col cols="12">
                                    <v-switch :model-value="tier.citationEnabled" :label="$t('graphRagModelUseCitation')" color="primary" hide-details density="compact" :readonly="isViewMode"
                                        @update:model-value="updateWaterfallTierProperty(i, 'citationEnabled', $event)" />
                                </v-col>
                            </v-row>

                            <v-expansion-panels variant="accordion" class="mt-2">
                                <v-expansion-panel>
                                    <v-expansion-panel-title>{{ $t('graphRagModelAdvanced') }}</v-expansion-panel-title>
                                    <v-expansion-panel-text>
                                        <v-row dense>
                                            <v-col cols="12" sm="6">
                                                <v-text-field :model-value="tier.fuzzyThreshold" type="number" min="0" max="100" :label="$t('graphRagModelFuzzyThreshold')"
                                                    :rules="[kgRangeRule('graphRagModelFuzzyThreshold', 0, 100)]" density="compact" variant="outlined" hide-details="auto" :readonly="isViewMode"
                                                    @update:model-value="updateWaterfallTierProperty(i, 'fuzzyThreshold', $event)" />
                                            </v-col>
                                            <v-col cols="12" sm="6">
                                                <v-text-field :model-value="tier.semanticThreshold" type="number" min="0" max="1" step="0.01" :label="$t('graphRagModelSemanticThreshold')"
                                                    :rules="[kgRangeRule('graphRagModelSemanticThreshold', 0, 1)]" density="compact" variant="outlined" hide-details="auto" :readonly="isViewMode"
                                                    @update:model-value="updateWaterfallTierProperty(i, 'semanticThreshold', $event)" />
                                            </v-col>
                                            <v-col cols="12" sm="6">
                                                <v-text-field :model-value="tier.maxContextNodes" type="number" min="1" max="200" :label="$t('graphRagModelMaxContextNodes')"
                                                    :rules="[kgRangeRule('graphRagModelMaxContextNodes', 1, 200)]" density="compact" variant="outlined" hide-details="auto" :readonly="isViewMode"
                                                    @update:model-value="updateWaterfallTierProperty(i, 'maxContextNodes', $event)" />
                                            </v-col>
                                            <v-col cols="12" sm="6">
                                                <v-text-field :model-value="tier.maxContextEdges" type="number" min="1" max="400" :label="$t('graphRagModelMaxContextEdges')"
                                                    :rules="[kgRangeRule('graphRagModelMaxContextEdges', 1, 400)]" density="compact" variant="outlined" hide-details="auto" :readonly="isViewMode"
                                                    @update:model-value="updateWaterfallTierProperty(i, 'maxContextEdges', $event)" />
                                            </v-col>
                                            <v-col cols="12" sm="6">
                                                <v-text-field :model-value="tier.anchorFinalTopK" type="number" min="1" max="100" :label="$t('flowpg_wfAnchorTopK')"
                                                    :rules="[kgRangeRule('flowpg_wfAnchorTopK', 1, 100)]" density="compact" variant="outlined" hide-details="auto" :readonly="isViewMode"
                                                    @update:model-value="updateWaterfallTierProperty(i, 'anchorFinalTopK', $event)" />
                                            </v-col>
                                            <v-col cols="12" sm="6">
                                                <v-text-field :model-value="tier.baselineTopK" type="number" min="1" max="100" :clearable="!isViewMode" :label="$t('graphRagModelBaselineTopK')"
                                                    :rules="[kgRangeRule('graphRagModelBaselineTopK', 1, 100)]" density="compact" variant="outlined" hide-details="auto" :readonly="isViewMode"
                                                    @update:model-value="updateWaterfallTierProperty(i, 'baselineTopK', $event)" />
                                            </v-col>
                                            <v-col cols="12" sm="6">
                                                <v-text-field :model-value="tier.baselineMinSimilarity" type="number" min="0" max="1" step="0.01" :clearable="!isViewMode" :label="$t('graphRagModelBaselineMinSim')"
                                                    :rules="[kgRangeRule('graphRagModelBaselineMinSim', 0, 1)]" density="compact" variant="outlined" hide-details="auto" :readonly="isViewMode"
                                                    @update:model-value="updateWaterfallTierProperty(i, 'baselineMinSimilarity', $event)" />
                                            </v-col>
                                        </v-row>
                                    </v-expansion-panel-text>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </v-card>

                        <!-- ✏️ 2026-10-20 ตัดช่อง Citation Prompt ระดับ node ออก (ผู้ใช้ขอ) — เปิด/ปิด citation ที่สวิตช์ราย tier · ใช้กติกามาตรฐานเสมอ -->
                    </div>

                    <!-- Knowledge API Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'apiknowledge'" class="form-section">

                        <!-- Token Authentication Section (First) -->
                        <div class="d-flex align-center justify-space-between mb-3">
                            <div class="d-flex align-center">
                                <v-icon size="20" color="orange" class="mr-2">mdi-key-variant</v-icon>
                                <h6 class="section-title mb-0">{{ $t('tokenAuthentication') }}</h6>
                            </div>
                            <v-switch v-model="nodeInspector.enableToken" color="orange" hide-details density="compact"
                                @update:model-value="updateNodeProperty('enableToken', $event)"
                                :readonly="isViewMode" inset />
                        </div>

                        <div v-if="nodeInspector.enableToken" class="token-config-section">
                            <!-- Token Mode -->
                            <v-radio-group v-model="nodeInspector.tokenAuthMode" inline hide-details
                                @update:model-value="updateNodeProperty('tokenAuthMode', $event)"
                                :readonly="isViewMode" class="mb-3">
                                <v-radio :label="$t('getNewToken')" value="new" color="orange" />
                                <v-radio :label="$t('useExistingToken')" value="existing" color="orange" />
                            </v-radio-group>

                            <!-- Get New Token Config -->
                            <div v-if="nodeInspector.tokenAuthMode === 'new'">
                                <!-- Token Name -->
                                <v-text-field v-model="nodeInspector.tokenName" :label="$t('Token Name')" variant="outlined"
                                    density="compact" hide-details="auto" placeholder="e.g. my_erp_token"
                                    @update:model-value="updateNodeProperty('tokenName', $event)"
                                    class="modern-input mb-3" :readonly="isViewMode"
                                    :hint="$t('_hintTokenNameReuse')" persistent-hint>
                                    <template v-slot:prepend-inner>
                                        <v-icon size="20" color="orange">mdi-tag</v-icon>
                                    </template>
                                </v-text-field>

                                <!-- Token URL -->
                                <v-text-field v-model="nodeInspector.tokenUrl" :label="$t('tokenUrl')" variant="outlined"
                                    density="compact" hide-details="auto" placeholder="https://auth.example.com/oauth/token"
                                    @update:model-value="updateNodeProperty('tokenUrl', $event)"
                                    class="modern-input mb-3" :readonly="isViewMode">
                                    <template v-slot:prepend-inner>
                                        <v-icon size="20" color="orange">mdi-link-lock</v-icon>
                                    </template>
                                </v-text-field>

                                <!-- Token HTTP Method -->
                                <v-select v-model="nodeInspector.tokenHttpMethod" :items="['GET', 'POST', 'PUT', 'PATCH', 'DELETE']"
                                    :label="$t('tokenHttpMethod')" variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('tokenHttpMethod', $event)"
                                    class="modern-input mb-3" :readonly="isViewMode">
                                    <template v-slot:prepend-inner>
                                        <v-icon size="20" color="orange">mdi-code-brackets</v-icon>
                                    </template>
                                </v-select>

                                <!-- Token Headers -->
                                <div class="headers-section mb-3">
                                    <div class="section-header mb-2">
                                        <span class="section-title">{{ $t('tokenHeaders') }}</span>
                                        <v-btn v-if="!isViewMode" @click="addTokenHeader" size="small" color="orange"
                                            variant="tonal" class="add-btn">
                                            <v-icon size="16">mdi-plus</v-icon>
                                            Add Header
                                        </v-btn>
                                    </div>
                                    <div v-if="nodeInspector.tokenHeaders" class="headers-list">
                                        <div v-for="(header, headerIndex) in nodeInspector.tokenHeaders" :key="headerIndex"
                                            class="header-row mb-2">
                                            <v-text-field v-model="header.key" :label="$t('headerName')" variant="outlined"
                                                density="compact" hide-details="auto" class="modern-input mr-2 mb-2"
                                                @update:model-value="updateTokenHeaderProperty(headerIndex, 'key', $event)"
                                                :readonly="isViewMode" />
                                            <v-text-field v-model="header.value" :label="$t('headerValue')" variant="outlined"
                                                density="compact" hide-details="auto" class="modern-input mr-2"
                                                @update:model-value="updateTokenHeaderProperty(headerIndex, 'value', $event)"
                                                :readonly="isViewMode" />
                                            <div style="display:flex; justify-content:flex-end; width:100%;">
                                                <v-btn v-if="!isViewMode" @click="deleteTokenHeader(headerIndex)"
                                                    size="small" color="error" variant="text" icon>
                                                    <v-icon size="16">mdi-delete</v-icon>
                                                </v-btn>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Token Request Body -->
                                <v-textarea v-if="['POST','PUT','PATCH'].includes((nodeInspector.tokenHttpMethod || 'POST').toUpperCase())"
                                    v-model="nodeInspector.tokenRequestBody" :label="$t('tokenRequestBodyJson')"
                                    variant="outlined" density="compact" rows="4" auto-grow hide-details="auto"
                                    placeholder='{"client_id": "xxx", "client_secret": "yyy", "grant_type": "client_credentials"}'
                                    @update:model-value="updateNodeProperty('tokenRequestBody', $event)"
                                    class="modern-input mb-3" :readonly="isViewMode">
                                    <template v-slot:prepend-inner>
                                        <v-icon size="20" color="orange">mdi-code-json</v-icon>
                                    </template>
                                </v-textarea>

                                <!-- Token Response Path -->
                                <v-text-field v-model="nodeInspector.tokenResponsePath" :label="$t('tokenResponsePath')"
                                    variant="outlined" density="compact" hide-details="auto"
                                    placeholder="access_token (JSON path to extract token value)"
                                    @update:model-value="updateNodeProperty('tokenResponsePath', $event)"
                                    class="modern-input mb-3" :readonly="isViewMode">
                                    <template v-slot:prepend-inner>
                                        <v-icon size="20" color="orange">mdi-key-chain</v-icon>
                                    </template>
                                </v-text-field>
                            </div>

                            <!-- Use Existing Token -->
                            <div v-if="nodeInspector.tokenAuthMode === 'existing'">
                                <v-select v-model="nodeInspector.useExistingTokenName"
                                    :items="availableTokenNames"
                                    :label="$t('selectExistingToken')" variant="outlined" density="compact"
                                    hide-details="auto"
                                    @update:model-value="updateNodeProperty('useExistingTokenName', $event)"
                                    class="modern-input mb-3" :readonly="isViewMode"
                                    :no-data-text="'No tokens defined in this flow. Create a token in another API Knowledge node first.'">
                                    <template v-slot:prepend-inner>
                                        <v-icon size="20" color="orange">mdi-key-link</v-icon>
                                    </template>
                                </v-select>
                            </div>

                            <!-- How to use Token - Guide -->
                            <v-expansion-panels class="mb-4" variant="accordion">
                                <v-expansion-panel bg-color="blue-grey-lighten-5" elevation="0" style="border: 1px solid #CFD8DC;">
                                    <v-expansion-panel-title class="py-2 px-3 text-subtitle-2 font-weight-medium" style="min-height: 48px; color: #455A64;">
                                        <div class="d-flex align-center">
                                            <v-icon size="20" color="info" class="mr-2" style="opacity: 0.9;">mdi-information-outline</v-icon>
                                            How to use Token
                                        </div>
                                    </v-expansion-panel-title>
                                    <v-expansion-panel-text class="pt-0 pb-2 px-1">
                                        <div class="text-caption text-medium-emphasis" style="line-height: 1.6;">
                                            <div class="mb-2 mt-2">After getting the token, you can use it in the <strong>{{ $t('requestHeaders') }}</strong> section below by referencing the Token Name with curly braces:</div>
                                            <v-sheet color="white" rounded border class="pa-3 mb-3" style="font-family: monospace; font-size: 13px;">
                                                <div class="mb-1"><strong class="text-grey-darken-3">Header Name:</strong> <span class="text-primary">Authorization</span></div>
                                                <div><strong class="text-grey-darken-3">Header Value:</strong> <span class="text-primary">Bearer {<span>{{ nodeInspector.tokenName || 'TOKEN_NAME' }}</span>}</span></div>
                                            </v-sheet>
                                            <div class="mb-2 d-flex align-start"><v-icon size="16" color="success" class="mr-2 mt-1">mdi-check-circle</v-icon> <span>Use <code style="background: #e3f2fd; padding: 2px 6px; border-radius: 4px; color: #1565c0; font-weight: 600;">{TOKEN_NAME}</code> in Header Value, API URL, or Request Body.</span></div>
                                            <div class="mb-2 d-flex align-start"><v-icon size="16" color="success" class="mr-2 mt-1">mdi-check-circle</v-icon> <span>If no <code style="background: #e3f2fd; padding: 2px 6px; border-radius: 4px; color: #1565c0; font-weight: 600;">{TOKEN_NAME}</code> is found, an <code>Authorization: Bearer</code> header is added automatically.</span></div>
                                            <div class="d-flex align-start"><v-icon size="16" color="success" class="mr-2 mt-1">mdi-check-circle</v-icon> <span>Other nodes can reuse this token by selecting its name in "Use Existing Token".</span></div>
                                        </div>
                                    </v-expansion-panel-text>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </div>

                        <v-divider v-if="nodeInspector.enableToken" class="mb-4" />

                        <h6 class="section-title">{{ $t('knowledgeApiConfiguration') }}</h6>

                        <!-- API URL -->
                        <v-text-field v-model="nodeInspector.knowledgeApiUrl" :label="$t('apiUrl')" variant="outlined"
                            density="compact" hide-details="auto" placeholder="https://api.example.com/knowledge"
                            @update:model-value="updateNodeProperty('knowledgeApiUrl', $event)"
                            class="modern-input mb-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="primary">mdi-link</v-icon>
                            </template>
                        </v-text-field>

                        <!-- HTTP Method -->
                        <v-select v-model="nodeInspector.knowledgeHttpMethod" :items="['GET', 'POST', 'PUT', 'PATCH', 'DELETE']"
                            :label="$t('httpMethod')" variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('knowledgeHttpMethod', $event)"
                            class="modern-input mb-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="primary">mdi-code-brackets</v-icon>
                            </template>
                        </v-select>

                        <!-- Request Headers -->
                        <div class="headers-section mb-3">
                            <div class="section-header mb-2">
                                <span class="section-title">{{ $t('requestHeaders') }}</span>
                                <v-btn v-if="!isViewMode" @click="addKnowledgeAPIHeader" size="small" color="primary"
                                    variant="tonal" class="add-btn">
                                    <v-icon size="16">mdi-plus</v-icon>
                                    Add Header
                                </v-btn>
                            </div>
                            <div v-if="nodeInspector.listHeaders" class="headers-list">
                                <div v-for="(header, headerIndex) in nodeInspector.listHeaders" :key="headerIndex"
                                    class="header-row mb-2">
                                    <v-text-field v-model="header.key" :label="$t('headerName')" variant="outlined"
                                        density="compact" hide-details="auto" class="modern-input mr-2 mb-2"
                                        @update:model-value="updateKnowledgeAPIHeaderProperty(headerIndex, 'key', $event)"
                                        :readonly="isViewMode" />

                                    <v-text-field v-model="header.value" :label="$t('headerValue')" variant="outlined"
                                        density="compact" hide-details="auto" class="modern-input mr-2"
                                        @update:model-value="updateKnowledgeAPIHeaderProperty(headerIndex, 'value', $event)"
                                        :readonly="isViewMode" />
                                    <div style="display:flex; justify-content:flex-end; width:100%;">
                                        <v-btn v-if="!isViewMode" @click="deleteKnowledgeAPIHeader(headerIndex)"
                                            size="small" color="error" variant="text" icon>
                                            <v-icon size="16">mdi-delete</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Query Params -->
                        <div class="headers-section mb-3">
                            <div class="section-header mb-2">
                                <span class="section-title">{{ $t('queryParams') }}</span>
                                <v-btn v-if="!isViewMode" @click="addKnowledgeAPIParam" size="small" color="primary"
                                    variant="tonal" class="add-btn">
                                    <v-icon size="16">mdi-plus</v-icon>
                                    Add Param
                                </v-btn>
                            </div>
                            <div v-if="nodeInspector.listParams && nodeInspector.listParams.length" class="headers-list">
                                <div v-for="(param, paramIndex) in nodeInspector.listParams" :key="paramIndex"
                                    class="header-row mb-2">
                                    <v-text-field v-model="param.key" :label="$t('paramName')" variant="outlined"
                                        density="compact" hide-details="auto" class="modern-input mr-2 mb-2"
                                        @update:model-value="updateKnowledgeAPIParamProperty(paramIndex, 'key', $event)"
                                        :readonly="isViewMode" />

                                    <v-text-field v-model="param.value" :label="$t('paramValue')" variant="outlined"
                                        density="compact" hide-details="auto" class="modern-input mr-2"
                                        placeholder="Supports {userMessage}, {sessionID}, ..."
                                        @update:model-value="updateKnowledgeAPIParamProperty(paramIndex, 'value', $event)"
                                        :readonly="isViewMode" />
                                    <div style="display:flex; justify-content:flex-end; width:100%;">
                                        <v-btn v-if="!isViewMode" @click="deleteKnowledgeAPIParam(paramIndex)"
                                            size="small" color="error" variant="text" icon>
                                            <v-icon size="16">mdi-delete</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                            <div class="text-caption text-medium-emphasis mt-1">
                                <v-icon size="12" class="mr-1">mdi-information-outline</v-icon>
                                Values are URL-encoded automatically. Use <code>{userMessage}</code> and other variables to inject runtime values.
                            </div>
                        </div>

                        <!-- Request Body with Variables -->
                        <div v-if="['POST','PUT','PATCH'].includes((nodeInspector.knowledgeHttpMethod || 'GET').toUpperCase())">
                        <h6 class="section-title mt-4">{{ $t('requestBodyJson') }}</h6>

                        <!-- JSON Validator Model (Optional, LLM-driven body generation) -->
                        <v-autocomplete v-model="nodeInspector.knowledgeModelJsonValidator" :items="uniqueAiModelOptions"
                            item-title="name" item-value="value" :label="$t('jsonValidatorModelOptional')" variant="outlined" density="compact"
                            hide-details="auto" :readonly="isViewMode" :clearable="!isViewMode"
                            @update:model-value="updateNodeProperty('knowledgeModelJsonValidator', $event)"
                            class="modern-input mb-3"
                            :placeholder="$t('_phLeaveEmptyJson')">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="primary">mdi-robot</v-icon>
                            </template>
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="model-option"
                                    :title="item.raw.value + ': ' + item.raw.name">
                                    <template v-slot:prepend>
                                        <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                            <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                        </div>
                                    </template>
                                </v-list-item>
                            </template>
                            <template v-slot:selection="{ item }">
                                <v-chip variant="text" size="medium"
                                    :text="item.raw.value + ': ' + item.raw.name"></v-chip>
                            </template>
                            <template v-slot:append-inner>
                                <v-tooltip location="top" max-width="320px">
                                    <template v-slot:activator="{ props }">
                                        <v-icon v-bind="props" size="small" color="grey-darken-1">
                                            mdi-information-outline
                                        </v-icon>
                                    </template>
                                    <span>
                                        If set, this model will generate the request body from the user's message
                                        following the JSON template below as the schema. Leave empty to send the
                                        JSON below as-is (with placeholder substitution only).
                                    </span>
                                </v-tooltip>
                            </template>
                        </v-autocomplete>

                        <div class="json-editor-container mb-3">
                            <!-- Variables Panel Header -->
                            <div class="json-variables-header" v-if="!isViewMode">
                                <div class="variables-header-content">
                                    <div class="variables-title">
                                        <span class="text-subtitle-2">Json</span>
                                        <v-chip size="x-small" variant="tonal" color="primary" class="ml-2 variables-toggle-chip"
                                            :class="{ 'is-open': variablesPanelExpanded }"
                                            @click.stop="variablesPanelExpanded = !variablesPanelExpanded">
                                            <v-icon start size="12">
                                                {{ variablesPanelExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                            </v-icon>
                                            {{ filteredVariables.length }} available variables
                                        </v-chip>
                                    </div>

                                    <!-- Category Filter -->
                                    <v-menu offset-y>
                                        <template v-slot:activator="{ props }">
                                            <v-btn v-bind="props" variant="text" size="small"
                                                class="category-filter-btn"
                                                :class="{ 'filter-active': selectedVariableCategory !== 'All' }">
                                                <v-icon size="14">mdi-filter-variant</v-icon>
                                                <span class="ml-1">{{ selectedVariableCategory }}</span>
                                                <v-icon size="12" class="ml-1">mdi-chevron-down</v-icon>
                                            </v-btn>
                                        </template>
                                        <v-list density="compact" class="category-menu">
                                            <v-list-item v-for="category in variableCategories" :key="category"
                                                @click="selectedVariableCategory = category"
                                                :class="{ 'v-list-item--active': selectedVariableCategory === category }">
                                                <v-list-item-title>{{ category }}</v-list-item-title>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>

                                    <!-- Beautify Button -->
                                    <v-btn variant="text" size="small"
                                        class="beautify-btn"
                                        :title="$t('_titleFormatJson')"
                                        @click.stop="beautifyKnowledgeJson">
                                        <v-icon size="14">mdi-magic-staff</v-icon>
                                        <span class="ml-1">{{ $t('beautify') }}</span>
                                    </v-btn>
                                </div>
                            </div>

                            <!-- Variables Grid - Collapsible -->
                            <v-expand-transition>
                                <div v-show="variablesPanelExpanded" class="json-variables-content">
                                    <!-- Quick Insert Buttons -->
                                    <div class="variables-quick-grid">
                                        <v-tooltip v-for="variable in filteredVariables" :key="variable.key"
                                            :text="variable.description" location="top">
                                            <template v-slot:activator="{ props }">
                                                <v-chip v-bind="props" class="variable-chip-compact"
                                                    @dragstart="onDragStart($event, variable.key)"
                                                    @click="insertKnowledgeVariable(variable.key)" draggable="true"
                                                    :color="getCategoryColor(variable.category)" variant="tonal"
                                                    size="small">
                                                    <v-icon start size="12">{{ variable.icon }}</v-icon>
                                                    <span class="variable-label">{{ variable.label }}</span>
                                                    <v-icon end size="10" class="drag-handle">mdi-drag-vertical</v-icon>
                                                </v-chip>
                                            </template>
                                        </v-tooltip>
                                    </div>
                                </div>
                            </v-expand-transition>

                            <!-- JSON Textarea -->
                            <div class="json-textarea-container">
                                <v-textarea v-model="nodeInspector.knowledgeRequestBody" :label="$t('requestBodyJson')"
                                    variant="outlined" density="compact" rows="8" auto-grow hide-details="auto"
                                    placeholder='{"query": "{{userMessage}}", "limit": 10}'
                                    @update:model-value="updateNodeProperty('knowledgeRequestBody', $event)"
                                    class="modern-json-editor" :readonly="isViewMode"
                                    @drop.stop.prevent="onKnowledgeJsonDrop($event)"
                                    @dragover.stop.prevent="onJsonDragOver($event)"
                                    @dragleave.stop="onJsonDragLeave($event)"
                                    ref="knowledgeJsonTextarea">
                                    <template v-slot:prepend-inner>
                                        <v-icon size="20" color="primary">mdi-code-json</v-icon>
                                    </template>
                                </v-textarea>
                            </div>

                            <!-- Insert Helper (moved to footer, no longer overlays content) -->
                            <div class="insert-helper-footer" v-if="!isViewMode">
                                <v-icon size="12" class="mr-1">mdi-information-outline</v-icon>
                                <span class="helper-text">
                                    Click variables to insert or drag &amp; drop into editor
                                </span>
                            </div>
                        </div>
                        </div>

                        <!-- Response Processing -->
                        <v-text-field v-model="nodeInspector.responseDataPath" :label="$t('responseDataPath')"
                            variant="outlined" density="compact" hide-details="auto"
                            placeholder="data.results (JSON path to extract knowledge)"
                            @update:model-value="updateNodeProperty('responseDataPath', $event)"
                            class="modern-input mb-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="primary">mdi-format-list-bulleted</v-icon>
                            </template>
                            <template v-slot:append-inner>
                                <v-tooltip location="top">
                                    <template v-slot:activator="{ props }">
                                        <v-icon v-bind="props" size="small" color="grey-darken-1">
                                            mdi-information-outline
                                        </v-icon>
                                    </template>
                                    <span>{{ $t('_jsonPathExtractKnowledge') }}</span>
                                </v-tooltip>
                            </template>
                        </v-text-field>
                    </div>

                    <!-- Products Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'products'" class="form-section">
                        <h6 class="section-title">{{ $t('productsConfiguration') }}</h6>

                        <!-- Product Group List Selection -->
                        <v-autocomplete v-model="nodeInspector.productGroupList" :items="DDProductGroup"
                            item-title="name" item-value="id" :label="$t('productGroupList')" variant="outlined"
                            density="compact" hide-details="auto" chips multiple return-object
                            @update:model-value="updateNodeProperty('productGroupList', $event)"
                            class="modern-input mb-3" :placeholder="$t('_phSelectProductGroup')" :clearable="!isViewMode"
                            :readonly="isViewMode">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="product-group-option">
                                    <template v-slot:prepend>
                                        <v-icon size="20" color="primary">mdi-package-variant</v-icon>
                                    </template>
                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                </v-list-item>
                            </template>
                        </v-autocomplete>

                        <!-- Top-N Chunks -->
                        <v-text-field v-model="nodeInspector.productTopNChunks" type="number" :label="$t('topNChunks')"
                            variant="outlined" density="compact" hide-details="auto" min="1" step="1"
                            @focus="$event.target.select()"
                            @blur="validateAndFixProductTopNChunks($event)"
                            @keydown.enter="validateAndFixProductTopNChunks($event)"
                            class="modern-input mb-3" :placeholder="$t('_phEnterNumberChunks')" :readonly="isViewMode" />

                        <!-- Cosine Similarity -->
                        <v-text-field v-model="nodeInspector.productCosineSimilarity" type="number"
                            :label="$t('cosineSimilarity')" :hint="$t('_cosineSimilarityHint')" persistent-hint variant="outlined" density="compact" hide-details="auto"
                            min="0.0001" max="1" step="0.0001"
                            @focus="$event.target.select()"
                            @blur="validateAndFixProductCosineSimilarity($event)"
                            @keydown.enter="validateAndFixProductCosineSimilarity($event)"
                            class="modern-input" :placeholder="$t('_phEnterSimilarityThreshold')"
                            :readonly="isViewMode" />
                    </div>

                    <!-- SharePoint Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'sharepoint'" class="form-section">
                        <h6 class="section-title">{{ $t('sharePointConfiguration') }}</h6>
                        
                        <!-- File Location Section -->
                        <div class="subsection">
                            <div class="subsection-header mb-3">
                                <v-icon size="20" color="info" class="mr-2">mdi-file-search</v-icon>
                                <span class="subsection-title">{{ $t('fileLocation') }}</span>
                            </div>
                            
                            <!-- Filename -->
                            <v-text-field 
                                v-model="nodeInspector.sharepoint.filename" 
                                :label="$t('filename')" 
                                variant="outlined" 
                                density="compact" 
                                hide-details="auto"
                                @update:model-value="updateNodeProperty('sharepoint.filename', $event)"
                                class="modern-input mb-3" 
                                placeholder="example.docx"
                                :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="18" color="info">mdi-file-document</v-icon>
                                </template>
                            </v-text-field>

                            <!-- Folder Path -->
                            <v-text-field 
                                v-model="nodeInspector.sharepoint.folderPath" 
                                :label="$t('subfolderPath')"
                                variant="outlined" 
                                density="compact" 
                                hide-details="auto"
                                @update:model-value="updateNodeProperty('sharepoint.folderPath', $event)"
                                class="modern-input" 
                                placeholder="/SubfolderName (optional)"
                                :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="18" color="info">mdi-folder</v-icon>
                                </template>
                                <template v-slot:append-inner>
                                    <v-tooltip location="top">
                                        <template v-slot:activator="{ props }">
                                            <v-icon v-bind="props" size="16" color="grey-darken-1">mdi-help-circle-outline</v-icon>
                                        </template>
                                        <span>{{ $t('_subfolderPathSharePoint') }}<br><strong>Root: /Documents</strong><br>Example: /Data will become /Documents/Data</span>
                                    </v-tooltip>
                                </template>
                            </v-text-field>
                        </div>
                        
                        <!-- Connection Settings Section -->
                        <div class="subsection mb-4">
                            <div class="subsection-header mb-3">
                                <v-icon size="20" color="primary" class="mr-2">mdi-connection</v-icon>
                                <span class="subsection-title">{{ $t('connectionSettings') }}</span>
                            </div>
                            
                            <!-- Site URL -->
                            <v-text-field 
                                v-model="nodeInspector.sharepoint.siteUrl" 
                                :label="$t('siteUrl')" 
                                variant="outlined" 
                                density="compact" 
                                hide-details="auto"
                                @update:model-value="updateNodeProperty('sharepoint.siteUrl', $event)"
                                class="modern-input mb-3" 
                                placeholder="https://yourtenant.sharepoint.com/sites/yoursite"
                                :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="18" color="primary">mdi-web</v-icon>
                                </template>
                            </v-text-field>

                            <!-- Tenant ID -->
                            <v-text-field 
                                v-model="nodeInspector.sharepoint.tenantId" 
                                :label="$t('tenantId')" 
                                variant="outlined" 
                                density="compact" 
                                hide-details="auto"
                                @update:model-value="updateNodeProperty('sharepoint.tenantId', $event)"
                                class="modern-input mb-3" 
                                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                autocomplete="off"
                                :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="18" color="primary">mdi-domain</v-icon>
                                </template>
                            </v-text-field>
                        </div>

                        <!-- Authentication Section -->
                        <div class="subsection mb-4">
                            <div class="subsection-header mb-3">
                                <v-icon size="20" color="success" class="mr-2">mdi-shield-lock</v-icon>
                                <span class="subsection-title">{{ $t('authentication') }}</span>
                            </div>
                            
                            <!-- Client ID -->
                            <v-text-field 
                                v-model="nodeInspector.sharepoint.clientId" 
                                :label="$t('clientId')" 
                                variant="outlined" 
                                density="compact" 
                                hide-details="auto"
                                @update:model-value="updateNodeProperty('sharepoint.clientId', $event)"
                                class="modern-input mb-3" 
                                placeholder="Application (client) ID"
                                :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="18" color="success">mdi-key-variant</v-icon>
                                </template>
                            </v-text-field>

                            <!-- Client Secret -->
                            <v-text-field 
                                v-model="nodeInspector.sharepoint.clientSecret" 
                                :label="$t('clientSecret')" 
                                variant="outlined" 
                                density="compact" 
                                hide-details="auto"
                                type="password"
                                @update:model-value="updateNodeProperty('sharepoint.clientSecret', $event)"
                                class="modern-input" 
                                autocomplete="off"
                                :placeholder="$t('enterClientSecret')"
                                :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="18" color="success">mdi-shield-key</v-icon>
                                </template>
                            </v-text-field>
                        </div>

                    </div>

                </v-form>
            </div>
        </div>
        </v-defaults-provider>
        <!-- Loading Dialog -->
        <v-dialog v-model="dialog" persistent width="300">
            <v-card color="#1976D2" dark>
                <v-card-text class="white--text">
                    Loading Please Wait...
                    <v-progress-linear indeterminate color="#FAA91B" class="mb-0"></v-progress-linear>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Save Confirmation Dialog -->
        <v-dialog v-model="saveDialog" max-width="500" persistent>
            <v-card class="save-dialog">
                <v-card-title class="dialog-header">
                    <div class="header-content">
                        <v-icon class="mr-3" color="white" size="28">mdi-content-save</v-icon>
                        <span class="text-h5 font-weight-bold">{{ isEditMode ? 'Update Flow' : 'Save Flow' }}</span>
                    </div>
                </v-card-title>

                <v-card-text class="dialog-content">
                    <div class="confirmation-content">
                        <!-- Flow Name Display -->
                        <div class="flow-name-display">
                            <v-icon class="mr-3" color="primary" size="32">mdi-chart-timeline-variant-shimmer</v-icon>
                            <div>
                                <div class="flow-name">{{ flowName || 'Untitled Flow' }}</div>
                                <div class="flow-subtitle">{{ isEditMode ? 'Update this flow?' : 'Save this flow?' }}
                                </div>
                            </div>
                        </div>

                        <!-- Warning for validation errors -->
                        <v-alert v-if="getValidationErrors().length > 0" type="warning" variant="tonal"
                            density="compact" class="mt-4">
                            <template v-slot:prepend>
                                <v-icon>mdi-alert</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>{{ $t('warningColon') }}</strong> Flow has {{ getValidationErrors().length }} validation
                                issue(s).
                                <br>You can still save, but the flow may not work correctly.
                            </div>
                        </v-alert>

                        <!-- Success message for no issues -->
                        <v-alert v-else type="success" variant="tonal" density="compact" class="mt-4">
                            <template v-slot:prepend>
                                <v-icon>mdi-check-circle</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>{{ $t('readyToSaveColon') }}</strong> Flow validation passed successfully.
                            </div>
                        </v-alert>
                    </div>
                </v-card-text>

                <v-card-actions class="dialog-actions">
                    <v-spacer></v-spacer>
                    <v-btn @click="closeSaveDialog" variant="outlined" class="mr-2" :disabled="saving"
                        style="padding: 0 15px; border-radius: 8px;">
                        Cancel
                    </v-btn>
                    <v-btn @click="confirmSave" :loading="saving" color="success" variant="elevated"
                        style="padding: 0 15px; border-radius: 8px;">
                        <v-icon class="mr-2">{{ isEditMode ? 'mdi-content-save-edit' : 'mdi-content-save' }}</v-icon>
                        {{ isEditMode ? 'Update Flow' : 'Save Flow' }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Load from Model Dialog -->
        <v-dialog v-model="loadFromModelDialog" max-width="600" persistent>
            <v-card class="load-model-dialog">
                <v-card-title class="dialog-header">
                    <div class="header-content">
                        <v-icon class="mr-3" color="white" size="28">mdi-download</v-icon>
                        <span class="text-h5 font-weight-bold">{{ $t('loadFromExistingModel') }}</span>
                    </div>
                </v-card-title>

                <v-card-text class="dialog-content">
                    <div class="load-model-content">
                        <!-- Model Selection -->
                        <div class="model-selection-section">
                            <div class="section-title mb-3">{{ $t('selectModelToLoad') }}</div>
                            <v-autocomplete v-model="selectedModelToLoad" :items="uniqueAiModelOptions"
                                item-title="searchableText" item-value="modelId" :label="$t('selectModel')" variant="outlined"
                                density="comfortable" hide-details="auto" prepend-inner-icon="mdi-robot" class="mb-4"
                                :placeholder="$t('_phChooseModelLoadConfig')"
                                :loading="loadingAvailableModels" clearable
                                :custom-filter="(value, query, item) => {
                                    if (!query) return true;
                                    const searchQuery = query.toLowerCase();
                                    const itemValue = item?.raw?.value?.toString().toLowerCase() || '';
                                    const itemName = item?.raw?.name?.toLowerCase() || '';
                                    return itemValue.includes(searchQuery) || itemName.includes(searchQuery);
                                }">
                                <template v-slot:item="{ props, item }">
                                    <v-list-item v-bind="props" class="model-option"
                                        :title="item.raw.value + ': ' + item.raw.name">
                                        <template v-slot:prepend>
                                            <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                                <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                            </div>
                                        </template>
                                    </v-list-item>
                                </template>

                                <template v-slot:selection="{ item }">
                                    <v-chip variant="text" size="medium"
                                        :text="item.raw.value + ': ' + item.raw.name"></v-chip>
                                </template>
                            </v-autocomplete>
                        </div>

                        <!-- Configuration Preview -->
                        <div v-if="selectedModelToLoad && modelConfigPreview" class="config-preview-section">
                            <div class="section-title mb-3">{{ $t('configurationPreview') }}</div>
                            <v-card variant="outlined" class="config-preview-card">
                                <v-card-text class="pa-3">
                                    <div class="preview-grid">
                                        <div class="preview-item">
                                            <span class="preview-label">{{ $t('providerColon') }}</span>
                                            <span class="preview-value">{{ modelConfigPreview.provider }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">{{ $t('modelColon') }}</span>
                                            <span class="preview-value">{{ (modelConfigPreview.model ||
                                                '').split('|')[0]
                                                }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">{{ $t('temperatureColon') }}</span>
                                            <span class="preview-value">{{ modelConfigPreview.temperature }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">{{ $t('maxTokensColon') }}</span>
                                            <span class="preview-value">{{ modelConfigPreview.maxTokens }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">{{ $t('topPColon') }}</span>
                                            <span class="preview-value">{{ modelConfigPreview.topP }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">{{ $t('frequencyPenaltyColon') }}</span>
                                            <span class="preview-value">{{ modelConfigPreview.frequencyPenalty }}</span>
                                        </div>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </div>

                        <!-- Warning -->
                        <v-alert v-if="selectedModelToLoad" type="warning" variant="tonal" density="compact"
                            class="mt-4">
                            <template v-slot:prepend>
                                <v-icon>mdi-alert</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>{{ $t('noteColon') }}</strong> Loading configuration will overwrite current AI model settings in
                                this
                                node.
                            </div>
                        </v-alert>
                    </div>
                </v-card-text>

                <v-card-actions class="dialog-actions">
                    <v-btn @click="closeLoadFromModelDialog" variant="outlined" class="mr-2"
                        :disabled="loadingModelConfig">
                        Cancel
                    </v-btn>
                    <v-btn @click="confirmLoadFromModel" :loading="loadingModelConfig" :disabled="!selectedModelToLoad"
                        color="success" variant="elevated">
                        <v-icon class="mr-2">mdi-download</v-icon>
                        Load Configuration
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Smart Intent Load from Model Dialog -->
        <v-dialog v-model="smartIntentLoadFromModelDialog" max-width="600" persistent>
            <v-card class="load-model-dialog">
                <v-card-title class="dialog-header">
                    <div class="header-content">
                        <v-icon class="mr-3" color="white" size="28">mdi-download</v-icon>
                        <span class="text-h5 font-weight-bold">
                            Load Config for {{ getLoadConfigTitle() }}
                        </span>
                    </div>
                </v-card-title>

                <v-card-text class="dialog-content">
                    <div class="load-model-content">
                        <!-- Model Selection -->
                        <div class="model-selection-section">
                            <div class="section-title mb-3">{{ $t('selectModelToLoad') }}</div>
                            <v-autocomplete v-model="selectedSmartIntentModelToLoad" :items="uniqueAiModelOptions"
                                item-title="searchableText" item-value="modelId" :label="$t('selectModel')" variant="outlined"
                                density="comfortable" hide-details="auto" prepend-inner-icon="mdi-robot" class="mb-4"
                                :placeholder="$t('_phChooseModelLoadConfig')"
                                :loading="loadingAvailableModels" clearable
                                :custom-filter="(value, query, item) => {
                                    if (!query) return true;
                                    const searchQuery = query.toLowerCase();
                                    const itemValue = item?.raw?.value?.toString().toLowerCase() || '';
                                    const itemName = item?.raw?.name?.toLowerCase() || '';
                                    return itemValue.includes(searchQuery) || itemName.includes(searchQuery);
                                }">
                                <template v-slot:item="{ props, item }">
                                    <v-list-item v-bind="props" class="model-option"
                                        :title="item.raw.value + ': ' + item.raw.name">
                                        <template v-slot:prepend>
                                            <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                                <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                            </div>
                                        </template>
                                    </v-list-item>
                                </template>

                                <template v-slot:selection="{ item }">
                                    <v-chip variant="text" size="medium"
                                        :text="item.raw.value + ': ' + item.raw.name"></v-chip>
                                </template>
                            </v-autocomplete>
                        </div>

                        <!-- Configuration Preview -->
                        <div v-if="selectedSmartIntentModelToLoad && smartIntentModelConfigPreview" class="config-preview-section">
                            <div class="section-title mb-3">{{ $t('configurationPreview') }}</div>
                            <v-card variant="outlined" class="config-preview-card">
                                <v-card-text class="pa-3">
                                    <div class="preview-grid">
                                        <div class="preview-item">
                                            <span class="preview-label">{{ $t('providerColon') }}</span>
                                            <span class="preview-value">{{ smartIntentModelConfigPreview.provider }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">{{ $t('modelColon') }}</span>
                                            <span class="preview-value">{{ (smartIntentModelConfigPreview.model ||
                                                '').split('|')[0]
                                                }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">{{ $t('temperatureColon') }}</span>
                                            <span class="preview-value">{{ smartIntentModelConfigPreview.temperature }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">{{ $t('maxTokensColon') }}</span>
                                            <span class="preview-value">{{ smartIntentModelConfigPreview.maxTokens }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">{{ $t('topPColon') }}</span>
                                            <span class="preview-value">{{ smartIntentModelConfigPreview.topP }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">{{ $t('frequencyPenaltyColon') }}</span>
                                            <span class="preview-value">{{ smartIntentModelConfigPreview.frequencyPenalty }}</span>
                                        </div>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </div>

                        <!-- Warning -->
                        <v-alert v-if="selectedSmartIntentModelToLoad" type="warning" variant="tonal" density="compact"
                            class="mt-4">
                            <template v-slot:prepend>
                                <v-icon>mdi-alert</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>{{ $t('noteColon') }}</strong> Loading configuration will overwrite current AI settings for {{ getLoadConfigTitle() }}.
                            </div>
                        </v-alert>
                    </div>
                </v-card-text>

                <v-card-actions class="dialog-actions">
                    <v-btn @click="closeSmartIntentLoadFromModelDialog" variant="outlined" class="mr-2"
                        :disabled="loadingSmartIntentModelConfig">
                        Cancel
                    </v-btn>
                    <v-btn @click="confirmSmartIntentLoadFromModel" :loading="loadingSmartIntentModelConfig" :disabled="!selectedSmartIntentModelToLoad"
                        color="success" variant="elevated">
                        <v-icon class="mr-2">mdi-download</v-icon>
                        Load Configuration
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Success Snackbar -->
        <v-snackbar v-model="showSnackbar" :timeout="3000" :color="snackBarColor" location="bottom center">
            <v-icon class="me-2">mdi-check-circle</v-icon>
            {{ snackbarMessage }}
        </v-snackbar>

        <!-- Success Dialog -->
        <v-dialog v-model="successDialog" max-width="500" persistent>
            <v-card class="success-dialog">
                <v-card-title class="success-dialog-header">
                    <div class="success-header-content">
                        <v-icon class="success-icon" size="32">mdi-check-circle</v-icon>
                        <span class="success-title">{{ $t('successExcl') }}</span>
                    </div>
                </v-card-title>

                <v-card-text class="success-dialog-content">
                    <div class="success-message">
                        {{ successMessage }}
                    </div>

                    <div class="success-details">
                        <div class="success-detail-item">
                            <v-icon size="16" class="mr-2">mdi-chart-timeline-variant</v-icon>
                            <span>Flow: {{ flowName }}</span>
                        </div>
                        <div class="success-detail-item">
                            <v-icon size="16" class="mr-2">mdi-clock-outline</v-icon>
                            <span>{{ new Date().toLocaleString() }}</span>
                        </div>
                    </div>
                </v-card-text>

                <v-card-actions class="success-dialog-actions">
                    <v-spacer></v-spacer>
                    <v-btn @click="successDialog = false" color="success" variant="elevated" class="success-ok-btn">
                        <v-icon class="mr-2">mdi-check</v-icon>
                        OK
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Confirmation Exit Dialog -->
        <v-dialog v-model="confirmExitDialog" persistent max-width="500">
            <v-card class="confirmation-dialog">
                <v-card-title class="dialog-header d-flex align-center">
                    <v-icon color="warning" size="28" class="mr-3">mdi-alert-circle</v-icon>
                    <div class="header-content">
                        <h3 class="text-h5 mb-0">{{ $t('confirmNavigation') }}</h3>
                    </div>
                </v-card-title>

                <v-card-text class="dialog-content pt-6">
                    <div class="confirmation-content">
                        <p class="text-body-1 mb-1">
                            Are you sure you want to leave?
                        </p>
                        <p class="text-body-1 mb-4">
                            Any unsaved changes will be lost.
                        </p>
                        
                        <v-alert type="warning" variant="tonal" class="mb-0">
                            <template v-slot:prepend>
                                <v-icon>mdi-content-save-alert</v-icon>
                            </template>
                            <strong>{{ $t('unsavedChangesColon') }}</strong> Your current flow modifications have not been saved.
                        </v-alert>
                    </div>
                </v-card-text>

                <v-card-actions class="dialog-actions pa-6">
                    <v-btn @click="confirmExit" color="warning" variant="elevated" class="elevation-2">
                        <v-icon class="mr-2" style="transform: scaleX(-1);">mdi-exit-to-app</v-icon>
                        Leave Anyway
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn @click="cancelExit" variant="outlined" color="primary" class="mr-3">
                        Stay Here
                        <v-icon class="ml-2">mdi-arrow-right</v-icon>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Advanced Router Generate Prompt (AI) -->
        <v-dialog v-model="advRouterGenerateDialog" persistent max-width="760" scrollable>
            <v-card class="generate-prompt-dialog">
                <v-card-title class="gp-header">
                    <div class="gp-header-icon">
                        <v-icon size="22" color="white">mdi-auto-fix</v-icon>
                    </div>
                    <div class="gp-header-text">
                        <h3 class="gp-title">{{ $t('generateClassifierPrompt') }}</h3>
                        <span class="gp-subtitle">{{ $t('_letLlmDraftClassifier') }}</span>
                    </div>
                </v-card-title>

                <v-card-text class="gp-content">
                    <v-alert v-if="advRouterGenerateHasExistingPrompt" type="warning" variant="tonal" density="compact" class="mb-5 gp-alert">
                        <template v-slot:prepend>
                            <v-icon size="18">mdi-content-save-alert</v-icon>
                        </template>
                        Your current prompt will be replaced when generation succeeds.
                    </v-alert>

                    <section class="gp-section">
                        <h6 class="gp-section-label">{{ $t('model') }}</h6>
                        <v-row>
                            <div v-if="selectedAdvRouterGenerateModelInfo" class="model-provider-details mt-2">
                                <div class="model-provider-detail">
                                    <v-icon icon="mdi-server-network" size="16" color="primary" class="me-2"></v-icon>
                                    <span class="model-provider-label">{{ $t('platform') }}</span>
                                    <span class="model-provider-value">{{ selectedAdvRouterGenerateModelInfo.platform || '-' }}</span>
                                </div>
                                <div class="model-provider-detail">
                                    <v-icon icon="mdi-cloud-outline" size="16" color="primary" class="me-2"></v-icon>
                                    <span class="model-provider-label">{{ $t('Provider') }}</span>
                                    <span class="model-provider-value">{{ selectedAdvRouterGenerateModelInfo.provider || '-' }}</span>
                                </div>
                            </div>
                            <v-col cols="12">
                                <v-autocomplete v-model="advRouterGenerateForm.model"
                                    :items="modelSelectionOptions" item-title="modelDisplay" item-value="modelName"
                                    :label="$t('model')" variant="outlined" density="comfortable" hide-details
                                    prepend-inner-icon="mdi-brain"
                                    @update:model-value="onAdvRouterGenerateModelChange($event)">
                                    <template v-slot:item="{ props, item }">
                                        <v-list-item v-bind="props" :title="getModelOptionTitle(item.raw)">
                                            <template v-slot:subtitle>
                                                <div class="model-option-subtitle">
                                                    <span class="model-option-meta">
                                                        <span class="model-option-meta-value">{{ item.raw.platform || '-' }}</span>
                                                    </span>
                                                    <span class="model-option-meta-separator">/</span>
                                                    <span class="model-option-meta">
                                                        <span class="model-option-meta-value">{{ item.raw.provider || '-' }}</span>
                                                    </span>
                                                </div>
                                            </template>
                                        </v-list-item>
                                    </template>
                                    <template v-slot:selection="{ item }">
                                        <span class="model-selection-title">{{ getModelOptionTitle(item.raw) }}</span>
                                    </template>
                                </v-autocomplete>
                            </v-col>
                        </v-row>
                    </section>

                    <section class="gp-section">
                        <div class="gp-section-header">
                            <h6 class="gp-section-label">{{ $t('intents') }}<span class="gp-optional">{{ $t('optionalLower') }}</span></h6>
                            <span class="gp-section-hint">{{ advRouterGenerateIntents.length }} {{ advRouterGenerateIntents.length === 1 ? 'intent' : 'intents' }}</span>
                        </div>
                        <p class="gp-section-help">
                            Add context for any intent below. Leave blank and the LLM will infer triggers from the name.
                        </p>

                        <div v-if="advRouterGenerateIntents.length === 0" class="gp-empty-state">
                            <v-icon size="28" color="grey-lighten-1">mdi-sitemap-outline</v-icon>
                            <span>{{ $t('_noIntentsToDescribe') }}</span>
                        </div>

                        <div class="gp-intents-list">
                            <div v-for="row in advRouterGenerateIntents" :key="row.path"
                                class="gp-intent-card"
                                :class="'gp-intent-card--l' + Math.min(row.level, 4)"
                                :style="{ marginLeft: ((row.level - 1) * 14) + 'px' }">
                                <div class="gp-intent-head">
                                    <v-chip size="x-small" :color="advRouterLevelColor(row.level)" variant="flat" class="gp-level-chip">
                                        L{{ row.level }}
                                    </v-chip>
                                    <span class="gp-intent-name">{{ row.name || '(unnamed)' }}</span>
                                </div>
                                <v-text-field v-model="advRouterGenerateForm.descriptions[row.path]"
                                    :placeholder="$t('_phDescribeKeyPhrasings')"
                                    variant="outlined" density="comfortable" hide-details class="gp-intent-input" />
                            </div>
                        </div>
                    </section>

                    <section class="gp-section">
                        <h6 class="gp-section-label">{{ $t('additionalNotes') }}<span class="gp-optional">{{ $t('optionalLower') }}</span></h6>
                        <v-textarea v-model="advRouterGenerateForm.additionalInstructions"
                            :placeholder="$t('_phDomainContext')"
                            variant="outlined" density="comfortable" hide-details rows="3" class="gp-textarea" />
                    </section>

                    <v-alert v-if="advRouterGenerateError" type="error" variant="tonal" density="compact" class="mt-4 gp-alert">
                        {{ advRouterGenerateError }}
                    </v-alert>
                </v-card-text>

                <v-card-actions class="gp-actions">
                    <v-spacer></v-spacer>
                    <v-btn @click="cancelAdvancedRouterGenerate" variant="text" class="gp-btn-cancel"
                        :disabled="advRouterGenerateLoading"
                        data-testid="adv-router-generate-cancel">
                        Cancel
                    </v-btn>
                    <div class="gp-btn-generate-wrap" :class="{ 'gp-btn-generate-wrap--loading': advRouterGenerateLoading }">
                        <v-btn @click="confirmAdvancedRouterGenerate" variant="flat" class="gp-btn-generate"
                            :disabled="!advRouterGenerateForm.model || advRouterGenerateLoading"
                            data-testid="adv-router-generate-confirm">
                            <v-icon size="18" class="mr-2">mdi-auto-fix</v-icon>
                            Generate with AI
                        </v-btn>
                    </div>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Advanced Router Test Prompt -->
        <v-dialog v-model="advRouterTestDialog" persistent max-width="820" scrollable>
            <v-card class="test-prompt-dialog">
                <v-card-title class="tp-header">
                    <div class="tp-header-icon">
                        <v-icon size="22" color="white">mdi-test-tube</v-icon>
                    </div>
                    <div class="tp-header-text">
                        <h3 class="tp-title">{{ $t('testClassifierPrompt') }}</h3>
                        <span class="tp-subtitle">{{ $t('_runSampleClassifier') }}</span>
                    </div>
                </v-card-title>

                <v-card-text class="tp-content">
                    <!-- Connected IntentAI warning -->
                    <v-alert v-if="!advRouterTestModelInfo" type="error" variant="tonal" density="compact" class="mb-4 tp-alert">
                        <template v-slot:prepend>
                            <v-icon size="18">mdi-alert-circle-outline</v-icon>
                        </template>
                        Connect an Intent AI node to this Advanced Router before testing — the test will use the same model the flow will use at runtime.
                    </v-alert>

                    <!-- Model info chip -->
                    <section v-if="advRouterTestModelInfo" class="tp-model-info">
                        <v-icon size="18" color="teal">mdi-brain</v-icon>
                        <span class="tp-model-info-label">{{ $t('_usingModelFromIntentAi') }}</span>
                        <span class="tp-model-info-name">{{ advRouterTestModelInfo.modelDisplay }}</span>
                        <v-chip size="x-small" variant="tonal" color="teal" class="tp-model-info-chip">
                            {{ advRouterTestModelInfo.platform || advRouterTestModelInfo.provider }}
                        </v-chip>
                    </section>

                    <!-- Test message input -->
                    <section class="tp-section">
                        <div class="tp-section-row">
                            <h6 class="tp-section-label">{{ $t('userMessage') }}</h6>
                            <span class="tp-section-hint">{{ $t('_enterToRunHint') }}</span>
                        </div>
                        <v-textarea v-model="advRouterTestForm.message"
                            :placeholder="$t('_phTypeSampleMessageLoan')"
                            variant="outlined" density="comfortable" hide-details rows="3" class="tp-textarea"
                            :disabled="!advRouterTestModelInfo || advRouterTestLoading"
                            @keydown="onAdvRouterTestKeydown"
                            data-testid="adv-router-test-message" />
                    </section>

                    <v-alert v-if="advRouterTestError" type="error" variant="tonal" density="compact" class="mt-4 tp-alert">
                        {{ advRouterTestError }}
                    </v-alert>

                    <!-- Results -->
                    <section v-if="advRouterTestResult" class="tp-section tp-results">
                        <div class="tp-results-header">
                            <h6 class="tp-section-label tp-section-label--lg">{{ $t('Result') }}</h6>
                            <div class="tp-results-meta">
                                <span class="tp-meta-item" v-if="advRouterTestResult.responseTimeMs != null">
                                    <v-icon size="14">mdi-timer-outline</v-icon>
                                    {{ Math.round(advRouterTestResult.responseTimeMs) }} ms
                                </span>
                                <span class="tp-meta-item" v-if="advRouterTestResult.totalTokens">
                                    <v-icon size="14">mdi-counter</v-icon>
                                    {{ advRouterTestResult.totalTokens }} tokens
                                    <span class="tp-meta-sub" v-if="advRouterTestResult.promptTokens != null">
                                        ({{ advRouterTestResult.promptTokens }} in / {{ advRouterTestResult.completionTokens }} out)
                                    </span>
                                </span>
                            </div>
                        </div>

                        <!-- Matched intent card -->
                        <div v-if="advRouterTestResult.isMatched" class="tp-match-card tp-match-card--success">
                            <div class="tp-match-header">
                                <v-icon size="20" color="success">mdi-check-circle</v-icon>
                                <span class="tp-match-title">{{ $t('matched') }}</span>
                                <v-chip size="x-small" variant="flat" color="success" class="tp-confidence-chip">
                                    {{ Math.round((advRouterTestResult.matchedConfidence || 0) * 100) }}%
                                </v-chip>
                            </div>
                            <div class="tp-match-path">
                                <template v-for="(seg, i) in advRouterTestResult.matchedIntentSegments" :key="i">
                                    <v-chip size="small" :color="advRouterLevelColor(i + 1)" variant="flat" class="tp-path-chip">
                                        L{{ i + 1 }} · {{ seg }}
                                    </v-chip>
                                    <v-icon v-if="i < advRouterTestResult.matchedIntentSegments.length - 1" size="14" class="tp-path-arrow">mdi-chevron-right</v-icon>
                                </template>
                            </div>
                            <div class="tp-match-footnote">
                                Depth L{{ advRouterTestResult.matchedDepth }} · threshold {{ Math.round((nodeInspector.advancedRouterConfidenceThreshold ?? 0.5) * 100) }}%
                            </div>
                        </div>

                        <div v-else class="tp-match-card tp-match-card--fallback">
                            <div class="tp-match-header">
                                <v-icon size="20" class="tp-fallback-icon">mdi-information-outline</v-icon>
                                <span class="tp-match-title">{{ $t('_noMatchFallbackRoute') }}</span>
                            </div>
                            <div class="tp-match-footnote">
                                No intent met the confidence threshold of {{ Math.round((nodeInspector.advancedRouterConfidenceThreshold ?? 0.5) * 100) }}%.
                                Review the score breakdown below or refine your prompt to improve detection.
                            </div>
                        </div>

                        <!-- Score breakdown -->
                        <div v-if="advRouterTestResult.allScores && advRouterTestResult.allScores.length > 0"
                            class="tp-scores">
                            <div class="tp-scores-header">
                                <span class="tp-section-label tp-section-label--sm">{{ $t('intentScores') }}</span>
                                <span class="tp-scores-hint">{{ advRouterTestResult.allScores.length }} returned</span>
                            </div>
                            <div class="tp-scores-list">
                                <div v-for="score in advRouterTestResult.allScores" :key="score.fullPath"
                                    class="tp-score-row" :class="{ 'tp-score-row--matched': score.isMatched }">
                                    <v-chip size="x-small" :color="advRouterLevelColor(score.level)" variant="flat" class="tp-score-level">
                                        L{{ score.level }}
                                    </v-chip>
                                    <span class="tp-score-path">{{ score.fullPath }}</span>
                                    <div class="tp-score-bar-wrap">
                                        <div class="tp-score-bar" :style="{ width: Math.round((score.confidence || 0) * 100) + '%' }"
                                            :class="advRouterTestScoreBarClass(score.confidence)"></div>
                                    </div>
                                    <span class="tp-score-pct">{{ Math.round((score.confidence || 0) * 100) }}%</span>
                                </div>
                            </div>
                        </div>

                        <!-- Raw response -->
                        <div class="tp-raw">
                            <div class="tp-raw-header" @click="advRouterTestRawCollapsed = !advRouterTestRawCollapsed">
                                <v-icon size="16">{{ advRouterTestRawCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-down' }}</v-icon>
                                <span class="tp-section-label tp-section-label--sm">{{ $t('rawLlmResponse') }}</span>
                            </div>
                            <pre v-if="!advRouterTestRawCollapsed" class="tp-raw-body">{{ advRouterTestResult.rawResponse || '(empty)' }}</pre>
                        </div>
                    </section>
                </v-card-text>

                <v-card-actions class="tp-actions">
                    <v-spacer></v-spacer>
                    <v-btn @click="closeAdvancedRouterTest" variant="text" class="tp-btn-cancel"
                        :disabled="advRouterTestLoading"
                        data-testid="adv-router-test-close">
                        Close
                    </v-btn>
                    <div class="tp-btn-run-wrap" :class="{ 'tp-btn-run-wrap--loading': advRouterTestLoading }">
                        <v-btn @click="runAdvancedRouterTest" variant="flat" class="tp-btn-run"
                            :disabled="!advRouterTestModelInfo || !advRouterTestForm.message || !advRouterTestForm.message.trim() || advRouterTestLoading"
                            data-testid="adv-router-test-run">
                            <v-icon size="18" class="mr-2">mdi-play</v-icon>
                            Run Test
                        </v-btn>
                    </div>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Intent Router Test Prompt -->
        <v-dialog v-model="intentRouterTestDialog" persistent max-width="820" scrollable>
            <v-card class="test-prompt-dialog">
                <v-card-title class="tp-header">
                    <div class="tp-header-icon">
                        <v-icon size="22" color="white">mdi-test-tube</v-icon>
                    </div>
                    <div class="tp-header-text">
                        <h3 class="tp-title">{{ $t('testIntentRouter') }}</h3>
                        <span class="tp-subtitle">{{ $t('_runSampleIntentRouter') }}</span>
                    </div>
                </v-card-title>

                <v-card-text class="tp-content">
                    <v-alert v-if="!intentRouterTestModelInfo" type="error" variant="tonal" density="compact" class="mb-4 tp-alert">
                        <template v-slot:prepend>
                            <v-icon size="18">mdi-alert-circle-outline</v-icon>
                        </template>
                        Connect an Intent AI node to this Intent Router before testing.
                    </v-alert>

                    <section v-if="intentRouterTestModelInfo" class="tp-model-info">
                        <v-icon size="18" color="teal">mdi-brain</v-icon>
                        <span class="tp-model-info-label">{{ $t('_usingModelFromIntentAi') }}</span>
                        <span class="tp-model-info-name">{{ intentRouterTestModelInfo.modelDisplay }}</span>
                        <v-chip size="x-small" variant="tonal" color="teal" class="tp-model-info-chip">
                            {{ intentRouterTestModelInfo.platform || intentRouterTestModelInfo.provider }}
                        </v-chip>
                    </section>

                    <section class="tp-section">
                        <div class="tp-section-row">
                            <h6 class="tp-section-label">{{ $t('userMessage') }}</h6>
                            <span class="tp-section-hint">{{ $t('_enterToRunHint') }}</span>
                        </div>
                        <v-textarea v-model="intentRouterTestForm.message"
                            :placeholder="$t('_phTypeSampleMessage')"
                            variant="outlined" density="comfortable" hide-details rows="3" class="tp-textarea"
                            :disabled="!intentRouterTestModelInfo || intentRouterTestLoading"
                            @keydown="onIntentRouterTestKeydown"
                            data-testid="intent-router-test-message" />
                    </section>

                    <v-alert v-if="intentRouterTestError" type="error" variant="tonal" density="compact" class="mt-4 tp-alert">
                        {{ intentRouterTestError }}
                    </v-alert>

                    <section v-if="intentRouterTestResult" class="tp-section tp-results">
                        <div class="tp-results-header">
                            <h6 class="tp-section-label tp-section-label--lg">{{ $t('Result') }}</h6>
                            <div class="tp-results-meta">
                                <span class="tp-meta-item" v-if="intentRouterTestResult.responseTimeMs != null">
                                    <v-icon size="14">mdi-timer-outline</v-icon>
                                    {{ Math.round(intentRouterTestResult.responseTimeMs) }} ms
                                </span>
                                <span class="tp-meta-item" v-if="intentRouterTestResult.totalTokens">
                                    <v-icon size="14">mdi-counter</v-icon>
                                    {{ intentRouterTestResult.totalTokens }} tokens
                                    <span class="tp-meta-sub" v-if="intentRouterTestResult.promptTokens != null">
                                        ({{ intentRouterTestResult.promptTokens }} in / {{ intentRouterTestResult.completionTokens }} out)
                                    </span>
                                </span>
                            </div>
                        </div>

                        <div v-if="!intentRouterTestResult.isFallback" class="tp-match-card tp-match-card--success">
                            <div class="tp-match-header">
                                <v-icon size="20" color="success">mdi-check-circle</v-icon>
                                <span class="tp-match-title">Best intent: {{ intentRouterTestResult.bestIntentName }}</span>
                                <v-chip size="x-small" variant="flat" color="success" class="tp-confidence-chip">1</v-chip>
                            </div>
                            <div class="tp-match-footnote">Selected route: {{ intentRouterTestResult.matchedFullPath }}</div>
                        </div>

                        <div v-else class="tp-match-card tp-match-card--intent-fallback">
                            <div class="tp-match-header">
                                <v-icon size="20" class="tp-intent-fallback-icon">mdi-alert-circle-outline</v-icon>
                                <span class="tp-match-title">{{ $t('_bestIntentFallback') }}</span>
                                <v-chip size="x-small" variant="flat" color="warning" class="tp-confidence-chip">1</v-chip>
                            </div>
                            <div class="tp-match-footnote">{{ $t('_noIntentScore1') }}</div>
                        </div>

                        <div v-if="intentRouterTestResult.allScores && intentRouterTestResult.allScores.length > 0" class="tp-intent-tree">
                            <div class="tp-scores-header">
                                <span class="tp-section-label tp-section-label--sm">{{ $t('allIntentResult') }}</span>
                                <span class="tp-scores-hint">{{ $t('_routeScoresOnly') }}</span>
                            </div>
                            <div class="tp-tree-list">
                                <div v-for="score in intentRouterTestResult.allScores" :key="score.fullPath || score.intentName"
                                    class="tp-tree-row" :class="{ 'tp-tree-row--matched': score.isMatched && !score.isFallback, 'tp-tree-row--fallback': score.isFallback }">
                                    <v-icon size="17" :color="score.isFallback ? 'warning' : (score.isMatched ? 'success' : 'grey-lighten-1')">
                                        {{ score.isFallback ? 'mdi-alert-circle-outline' : (score.isMatched ? 'mdi-check-circle' : 'mdi-circle-outline') }}
                                    </v-icon>
                                    <v-chip size="x-small" :color="score.isFallback ? 'warning' : 'teal'" variant="flat" class="tp-score-level">
                                        {{ score.isFallback ? 'FB' : 'L1' }}
                                    </v-chip>
                                    <div class="tp-tree-main">
                                        <div class="tp-tree-name">{{ score.intentName || score.fullPath }}</div>
                                        <div class="tp-tree-route">Route: {{ score.outputLabel || score.fullPath }}</div>
                                        <div v-if="score.errorMessage" class="tp-tree-error">{{ score.errorMessage }}</div>
                                    </div>
                                    <v-chip size="small" :color="intentRouterScoreColor(score)" variant="flat" class="tp-binary-score-chip">
                                        {{ score.score }}
                                    </v-chip>
                                </div>
                            </div>
                        </div>
                    </section>
                </v-card-text>

                <v-card-actions class="tp-actions">
                    <v-spacer></v-spacer>
                    <v-btn @click="closeIntentRouterTest" variant="text" class="tp-btn-cancel"
                        :disabled="intentRouterTestLoading"
                        data-testid="intent-router-test-close">
                        Close
                    </v-btn>
                    <div class="tp-btn-run-wrap" :class="{ 'tp-btn-run-wrap--loading': intentRouterTestLoading }">
                        <v-btn @click="runIntentRouterTest" variant="flat" class="tp-btn-run"
                            :disabled="!intentRouterTestModelInfo || !intentRouterTestForm.message || !intentRouterTestForm.message.trim() || intentRouterTestLoading"
                            data-testid="intent-router-test-run">
                            <v-icon size="18" class="mr-2">mdi-play</v-icon>
                            Run Test
                        </v-btn>
                    </div>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- AI Model Test Prompt -->
        <v-dialog v-model="aiModelTestDialog" persistent max-width="820" scrollable>
            <v-card class="test-prompt-dialog">
                <v-card-title class="tp-header">
                    <div class="tp-header-icon">
                        <v-icon size="22" color="white">mdi-test-tube</v-icon>
                    </div>
                    <div class="tp-header-text">
                        <h3 class="tp-title">{{ $t('testAiModel') }}</h3>
                        <span class="tp-subtitle">{{ $t('_sendUserMessageReview') }}</span>
                    </div>
                </v-card-title>

                <v-card-text class="tp-content">
                    <v-alert v-if="!aiModelTestModelInfo" type="error" variant="tonal" density="compact" class="mb-4 tp-alert">
                        <template v-slot:prepend>
                            <v-icon size="18">mdi-alert-circle-outline</v-icon>
                        </template>
                        Select a model before testing this AI Model node.
                    </v-alert>

                    <section v-if="aiModelTestModelInfo" class="tp-model-info">
                        <v-icon size="18" color="teal">mdi-brain</v-icon>
                        <span class="tp-model-info-label">{{ $t('_usingModelFromAiModel') }}</span>
                        <span class="tp-model-info-name">{{ aiModelTestModelInfo.modelDisplay }}</span>
                        <v-chip size="x-small" variant="tonal" color="teal" class="tp-model-info-chip">
                            {{ aiModelTestModelInfo.platform || aiModelTestModelInfo.provider }}
                        </v-chip>
                    </section>

                    <section class="tp-section">
                        <div class="tp-section-row">
                            <h6 class="tp-section-label">{{ $t('userMessage') }}</h6>
                            <span class="tp-section-hint">{{ $t('_enterToRunHint') }}</span>
                        </div>
                        <v-textarea v-model="aiModelTestForm.userMessage"
                            :placeholder="$t('_phTypeSampleAiModel')"
                            variant="outlined" density="comfortable" hide-details rows="3" class="tp-textarea"
                            :disabled="!aiModelTestModelInfo || aiModelTestLoading"
                            @keydown="onAIModelTestKeydown"
                            data-testid="ai-model-test-user-message" />
                    </section>

                    <v-alert v-if="aiModelTestError" type="error" variant="tonal" density="compact" class="mt-4 tp-alert">
                        {{ aiModelTestError }}
                    </v-alert>

                    <section v-if="aiModelTestResult" class="tp-section tp-results">
                        <div class="tp-results-header">
                            <h6 class="tp-section-label tp-section-label--lg">{{ $t('Result') }}</h6>
                            <div class="tp-results-meta">
                                <span class="tp-meta-item" v-if="aiModelTestResult.responseTimeMs != null">
                                    <v-icon size="14">mdi-timer-outline</v-icon>
                                    {{ Math.round(aiModelTestResult.responseTimeMs) }} ms
                                </span>
                                <span class="tp-meta-item" v-if="aiModelTestResult.totalTokens">
                                    <v-icon size="14">mdi-counter</v-icon>
                                    {{ aiModelTestResult.totalTokens }} tokens
                                    <span class="tp-meta-sub" v-if="aiModelTestResult.promptTokens != null">
                                        ({{ aiModelTestResult.promptTokens }} in / {{ aiModelTestResult.completionTokens }} out)
                                    </span>
                                </span>
                            </div>
                        </div>

                        <div class="tp-model-response-card">
                            <div class="tp-model-response-header">
                                <v-icon size="18" color="teal">mdi-message-reply-text</v-icon>
                                <span class="tp-section-label tp-section-label--sm">{{ $t('modelResponse') }}</span>
                            </div>
                            <div class="tp-model-response-body">
                                <template v-if="isImageUrl(aiModelTestResult.responseText)">
                                    <a :href="extractImageUrl(aiModelTestResult.responseText)" target="_blank" rel="noopener noreferrer">
                                        <img :src="extractImageUrl(aiModelTestResult.responseText)" alt="Model response" style="max-width:100%;max-height:400px;display:block;cursor:pointer;" />
                                    </a>
                                </template>
                                <template v-else>{{ aiModelTestResult.responseText || '(empty)' }}</template>
                            </div>
                        </div>
                    </section>
                </v-card-text>

                <v-card-actions class="tp-actions">
                    <v-spacer></v-spacer>
                    <v-btn @click="closeAIModelTest" variant="text" class="tp-btn-cancel"
                        :disabled="aiModelTestLoading"
                        data-testid="ai-model-test-close">
                        Close
                    </v-btn>
                    <div class="tp-btn-run-wrap" :class="{ 'tp-btn-run-wrap--loading': aiModelTestLoading }">
                        <v-btn @click="runAIModelTest" variant="flat" class="tp-btn-run"
                            :disabled="!aiModelTestModelInfo || !aiModelTestForm.userMessage || !aiModelTestForm.userMessage.trim() || aiModelTestLoading"
                            data-testid="ai-model-test-run">
                            <v-icon size="18" class="mr-2">mdi-play</v-icon>
                            Run Test
                        </v-btn>
                    </div>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { MarkerType, VueFlow } from '@vue-flow/core'
import { Background } from '@vue-flow/background'
import { Controls } from '@vue-flow/controls'
import { ref, computed, toRef, reactive } from "vue";

// Import styles
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'
import '@vue-flow/controls/dist/style.css'

import FlowNode from '@/components/FlowComponents/FlowNode.vue'
import FlowEdge from '@/components/FlowComponents/FlowEdge.vue'
import NodePanel from '@/components/FlowComponents/NodePanel.vue'
import DebugInspector from '@/components/FlowComponents/DebugInspector.vue'
import DebugControlBar from '@/components/FlowComponents/DebugControlBar.vue'
import {
    getFlowNodeTypeColor, getFlowNodeTypeIcon, getFlowNodeTypeLabel, getJsonConditionOperatorToken,
    KG_SEARCH_MODES, defaultKgRetrievalConfig, normalizeKgRetrievalConfig, defaultWaterfallTier, normalizeWaterfallTier,
} from '@/utils/flowUtils';
import providerModelOptionsMixin from '@/mixins/providerModelOptionsMixin.js';

// S15-P1 — ช่วงค่าที่ backend รับ (GraphRagUserSettings.Validate) · ใช้ทั้ง :rules และตัวกันไม่ emit ค่านอกช่วงเข้า node
// key ของ Knowledge Graph node เป็น PascalCase (kgRetrievalConfig) · ของ tier เป็น camelCase — ตาม spec §Node data contract
const KG_CONFIG_RANGES = Object.freeze({
    MaxHops: [1, 10],
    FuzzyThreshold: [0, 100],
    SemanticThreshold: [0, 1],
    MaxContextNodes: [1, 200],
    MaxContextEdges: [1, 400],
    BaselineTopK: [1, 100],
    BaselineMinSimilarity: [0, 1],
});
const WF_TIER_NUMERIC_RANGES = Object.freeze({
    maxHops: [1, 10],
    fuzzyThreshold: [0, 100],
    semanticThreshold: [0, 1],
    maxContextNodes: [1, 200],
    maxContextEdges: [1, 400],
    anchorFinalTopK: [1, 100],
    baselineTopK: [1, 100],
    baselineMinSimilarity: [0, 1],
});
const WF_MAX_TIERS = 10;

export default {
    name: "Flow",
    inject: ["flagNav"],
    mixins: [providerModelOptionsMixin],
    components: {
        VueFlow,
        Background,
        Controls,
        FlowNode,
        FlowEdge,
        NodePanel,
        DebugInspector,
        DebugControlBar
    },
    provide() {
        return {
            lockFlowCreatorStatus: computed(() => this.lockStatus),
            aiModelOptions: computed(() => this.aiModelOptions),
            availableAgentTools: computed(() => this.agentToolOptions),
            flowEdges: computed(() => this.tempEdge),
            // Debug mode injects for child components
            isDebugMode: computed(() => this.isDebugMode),
            debugNodeStates: computed(() => this.debugNodeStates),
            debugBreakpoints: computed(() => this.debugBreakpoints),
            debugStatus: computed(() => this.debugStatus),
            debugOverrideNextNodeId: computed(() => this.debugOverrideNextNodeId),
            debugHoveredNodeId: computed(() => this.debugHoveredNodeId),
            debugCurrentNodeId: computed(() => this.debugCurrentNodeId),
            debugEdgeStates: computed(() => this.debugEdgeStates),
            toggleDebugBreakpoint: this.toggleDebugBreakpoint,
            clearAllBreakpoints: this.clearAllBreakpoints,
            setDebugHoveredNodeId: (id) => { this.debugHoveredNodeId = id; },
        };
    },
    data() {
        return {
            interactiveControls: true,
            unableToAccessModel: [],
            // Edit mode properties
            isEditMode: false,
            isViewMode: false,
            editFlowId: null,
            // Save/Load UI state
            saveDialog: false,
            saveFormValid: false,
            saving: false,
            loading: false,
            successDialog: false,
            // Load from Model state
            loadFromModelDialog: false,
            selectedModelToLoad: null,
            availableModels: [],
            modelConfigPreview: null,
            loadingAvailableModels: false,
            loadingModelConfig: false,
            // Smart Intent Load Config properties (separate from AI Model)
            smartIntentLoadFromModelDialog: false,
            selectedSmartIntentModelToLoad: null,
            smartIntentModelConfigPreview: null,
            loadingSmartIntentModelConfig: false,
            currentLoadingSmartIntentIndex: null, // Track which intent is being loaded
            // Confirmation dialog properties
            confirmExitDialog: false,
            pendingNavigation: null,
            isNavigatingAway: false, // Flag to prevent double confirmation
            advRouterGenerateDialog: false,
            advRouterGenerateLoading: false,
            advRouterGenerateError: '',
            advRouterGenerateForm: {
                platform: null,
                provider: null,
                model: null,
                descriptions: {},
                additionalInstructions: '',
            },
            advRouterGenerateIntents: [],
            // Test prompt dialog state
            advRouterTestDialog: false,
            advRouterTestLoading: false,
            advRouterTestError: '',
            advRouterTestRawCollapsed: false,
            advRouterTestForm: {
                message: '',
            },
            advRouterTestModelInfo: null,
            advRouterTestResult: null,
            intentRouterTestDialog: false,
            intentRouterTestLoading: false,
            intentRouterTestError: '',
            intentRouterTestRawCollapsed: false,
            intentRouterTestForm: {
                message: '',
            },
            intentRouterTestModelInfo: null,
            intentRouterTestResult: null,
            aiModelTestDialog: false,
            aiModelTestLoading: false,
            aiModelTestError: '',
            aiModelTestRawCollapsed: false,
            aiModelTestForm: {
                systemMessage: '',
                userMessage: '',
            },
            aiModelTestModelInfo: null,
            aiModelTestResult: null,
            // Save form data
            saveFlowData: {
                name: '',
                saveAsTemplate: false,
                createBackup: true,
                publishFlow: false
            },
            // Flow metadata
            flowId: null,
            flowName: 'Untitled Flow',
            flowCreatedAt: null,

            isDraggingNode: false,
            dialog: false,
            showSnackbar: false,
            snackBarColor: 'success',
            snackbarMessage: '',
            inspectorExpanded: false,
            selectedNode: null,
            selectedNodes: [], // Track multiple selected nodes

            // Undo/Redo system
            history: [], // Stack for storing flow states
            historyIndex: -1, // Current position in history
            maxHistorySize: 50, // Maximum number of undo states
            isPerformingUndoRedo: false, // Flag to prevent infinite loops
            propertyUpdateTimer: null, // Timer for debouncing property updates
            isDraggingNodes: false, // Flag to track node dragging
            dragStartPositions: {}, // Store initial positions before drag

            // Copy & Paste system
            copiedNodes: [], // Store copied nodes data
            pasteOffset: 100, // Offset for pasted nodes

            DDVector: [],
            listDataIntegration: [],
            // S15-P1 — รายการกราฟให้ dropdown ของ node knowledgeGraph / nexaWaterfallSearch (envelope res.data.data — G8)
            DDKnowledgeGraphs: [],
            loadingKnowledgeGraphs: false,
            kgSearchModes: KG_SEARCH_MODES,
            DDGroupReplace: [],
            DDProductGroup: [],
            listDDModel: [],
            listDDIntentGroup: [],
            listMonitorLineName: [],
            listDDProvider: [],
            agentToolOptions: [],
            toolTypes: [
                { text: 'Master Tool', value: 'master', icon: 'mdi-toolbox', color: 'blue' },
                { text: 'Custom API Call', value: 'api', icon: 'mdi-api', color: 'orange' },
                { text: 'Text Information', value: 'text', icon: 'mdi-text-box', color: 'green' }
            ],
            multiAgentStrategies: [
                { title: 'Pipeline (Sequential)', value: 'pipeline' },
                { title: 'Verify (Parallel + Cross-check)', value: 'verify' }
            ],
            cacheScopes: [
                { title: 'Flow Level (All Users)', value: 'flow' },
                { title: 'User Level', value: 'user' },
                { title: 'Company Level', value: 'company' }
            ],
            flowToolEditorDialog: false,
            internalFlowNodes: [],
            internalFlowEdges: [],
            selectedInternalNode: null,
            isEditingInternalNode: false,
            editingFlowToolNode: null,
            internalFlowExcludedTypes: ['messageInput', 'response', 'flowTool'],
            platformOptions: [
                { text: 'Azure', value: 'Azure' },
                { text: 'Google Vertex AI', value: 'Google' },
                { text: 'Anthropic Direct', value: 'Anthropic' },
                { text: 'Other', value: 'Other' },
            ],
            reasonOptions: [
                { text: 'Low', value: 'low' },
                { text: 'Medium', value: 'medium' },
                { text: 'High', value: 'high' },
            ],
            DDModelShow: [],
            DDTimeZone: [],
            modelID: null,
            LLmsModel: {
                provider: null,
                model: null
            },
            // Document data properties (similar to IntentSessionDetail.vue)
            documentDataTable: [],
            documentDataHeaders: [
                {
                    title: "",
                    key: "selected",
                    align: "center",
                    sortable: false,
                    width: "10px",
                },
                {
                    title: "Text Original",
                    key: "textORG",
                    align: "start",
                    sortable: true,
                    width: "50%",
                },
                {
                    title: "Text English",
                    key: "textEN",
                    align: "start",
                    sortable: true,
                    width: "50%",
                }
            ],
            documentDataItemsPerPage: 10,
            documentDataTotalTable: 0,
            documentDataOptions: {},
            loadingDocumentDataTable: false,
            showDocumentDataTable: false,
            selectedDocumentData: [],
            documentDataExpanded: false, // Control expand/collapse state
            // Model configuration panels control
            modelConfigPanels: ['basic'], // Default to basic panel open
            orchestratorConfigPanels: [],
            agentConfigPanels: [],
            toolConfigPanels: [],
            flowToolConfigPanels: [],
            intentaiModelConfigPanels: ['intentaibasic'], // Default to basic panel open

            // Prompt template properties
            showPromptTem: false,
            promptTemplateDialog: false,
            promptTemplates: [],
            promptTemplateGroups: [],
            groupPromptTemplate: null,
            showMenu: false,
            x: 0,
            y: 0,
            
            // Smart Intent Prompt Template properties
            smartIntentPromptTemplateDialog: false,
            smartIntentCurrentIndex: null,
            smartIntentGroupPromptTemplate: null,
            smartIntentTemplateSearchQuery: '',
            
            // Smart Intent Fallback Prompt Template properties
            smartIntentFallbackPromptTemplateDialog: false,
            smartIntentFallbackGroupPromptTemplate: null,
            smartIntentFallbackTemplateSearchQuery: '',
            
            dialogShowAll: false,
            templateSearchQuery: '',

            // IntentAI Prompt template properties
            intentaiPromptTemplateDialog: false,
            showIntentaiMenu: false,
            dialogShowAllIntentai: false,
            intentaiTemplateSearchQuery: '',
            intentaiGroupPromptTemplate: null,
            jsonConditionExpandedPanel: 0,
            jsonConditionMatchModes: [
                { title: 'ALL conditions must be true (AND)', value: 'all' },
                { title: 'ANY condition must be true (OR)', value: 'any' }
            ],
            jsonConditionOperators: [
                { title: 'Comparison', value: '__comparison', header: true },
                { title: 'Equals (=)', value: 'equals' },
                { title: 'Not Equals (≠)', value: 'not_equals' },
                { title: 'Greater Than (>)', value: 'greater_than' },
                { title: 'Less Than (<)', value: 'less_than' },
                { title: 'Greater or Equal (≥)', value: 'greater_equal' },
                { title: 'Less or Equal (≤)', value: 'less_equal' },
                { title: 'String', value: '__string', header: true },
                { title: 'Contains', value: 'contains' },
                { title: 'Not Contains', value: 'not_contains' },
                { title: 'Starts With', value: 'starts_with' },
                { title: 'Ends With', value: 'ends_with' },
                { title: 'Regex', value: 'regex' },
                { title: 'Existence', value: '__existence', header: true },
                { title: 'Is Empty', value: 'is_empty' },
                { title: 'Is Not Empty', value: 'is_not_empty' },
                { title: 'Exists', value: 'exists' },
                { title: 'Not Exists', value: 'not_exists' },
                { title: 'Array', value: '__array', header: true },
                { title: 'Array Empty', value: 'array_empty' },
                { title: 'Array Not Empty', value: 'array_not_empty' },
                { title: 'Array Contains', value: 'array_contains' },
                { title: 'Array Length Min (≥)', value: 'array_length_min' },
                { title: 'Array Length Max (≤)', value: 'array_length_max' },
                { title: 'Array Length Equals (=)', value: 'array_length_equals' },
                { title: 'Array All Unique', value: 'array_all_unique' },
                { title: 'Boolean', value: '__boolean', header: true },
                { title: 'Is True', value: 'is_true' },
                { title: 'Is False', value: 'is_false' },
                { title: 'Schema', value: '__schema', header: true },
                { title: 'Type Is', value: 'type_is' },
                { title: 'One Of', value: 'one_of' },
                { title: 'Not One Of', value: 'not_one_of' },
                { title: 'Format (email/uri/uuid/date)', value: 'format' },
                { title: 'Cross-Field', value: '__cross_field', header: true },
                { title: 'Equals Field', value: 'equals_field' },
                { title: 'Greater Than Field', value: 'greater_than_field' },
                { title: 'Less Than Field', value: 'less_than_field' }
            ],
            nodeInspector: {
                label: '',
                color: '#1976D2',
                placeholderText: '',
                required: false,
                selectedModel: null,
                systemPrompt: '',
                temperature: null,
                maxTokens: null,
                // AI Model Configuration properties
                provider: null,
                model: null,
                orchestratorName: '',
                confidenceThreshold: 0.7,
                enableMultiAgent: false,
                multiAgentStrategy: 'pipeline',
                enableCache: false,
                cacheTTLMinutes: 1440,
                cacheScope: 'flow',
                enableSemanticCache: false,
                similarityThreshold: 0.95,
                enableTransactionId: false,
                enableInputMessageReplace: false,
                enableVoicePromptReplace: false,
                enableLanguageReplace: false,
                agentName: '',
                agentDescription: '',
                personaPrompt: '',
                selectedTools: [],
                enableAgentToolDecision: false,
                useCustomAIModel: false,
                agentProvider: null,
                agentModel: null,
                agentTemperature: 0,
                agentMaxTokens: null,
                agentTopP: 1,
                agentFrequencyPenalty: null,
                agentPresencePenalty: null,
                agentStopSequence: null,
                agentStreamResponse: false,
                agentJsonResponse: false,
                agentGender: 'male',
                agentTypingSpeed: null,
                agentMaxInputLength: 200000,
                agentMemoryTurn: null,
                agentMemoryDuration: null,
                agentShortTermMemory: 200,
                agentLongTermMemory: false,
                agentEnableTime: false,
                agentTimeZone: null,
                agentEnableTransactionId: false,
                knowledgeConnections: this.createDefaultKnowledgeConnections(),
                executionMode: 'direct',
                goal: '',
                flowId: '',
                flowParameters: {},
                autoExecuteTools: false,
                masterToolId: null,
                toolName: '',
                toolDescription: '',
                toolType: 'master',
                endpoint: '',
                method: 'GET',
                headers: [],
                body: '',
                textContent: '',
                flowToolName: '',
                flowToolDescription: '',
                internalNodes: [],
                internalEdges: [],
                internalNodeCount: 0,
                showWarning: true,
                outputTemplate: '{currentResponse}',
                useBaseModelPrompt: false,
                gender: 'male',
                typingSpeed: null,
                maxInputLength: 200000,
                streamResponse: false,
                enableTime: null,
                timeZone: null,
                jsonResponse: false,
                memoryTurn: null,
                memoryDuration: null,
                shortTermMemory: null,
                longTermMemory: false,
                isChainMassage: false,
                lenChainMassage: null,
                timeSecondChainMassage: null,
                topP: null,
                frequencyPenalty: null,
                presencePenalty: null,
                stopSequence: null,
                reasoningEffort: null,
                intentaiReasoningEffort: null,
                loadingBar: false,
                loadingBarDuration: 0,
                monitorLineUserId: null,
                monitorSocialID: null,
                showLineName: false,
                forbiddenWords: [''],
                intentGroupName: '',
                intents: [],
                // Guardrail AI properties
                guardrailType: 'content_safety',
                guardrailModel: 'gpt-4',
                guardrailPrompt: '',
                safetyThreshold: 80,
                enableLogging: true,
                // API Trigger properties
                apiUrl: '',
                httpMethod: 'POST',
                requestBody: '',
                headers: '',
                textResponse: '',
                jsonValidate: '{\n   "Name":"Value"\n}',
                modelJsonValidator: null,
                listHeadersAPITrigger: [
                    {
                        key: '',
                        value: '',
                    },
                ],
                listValidation: [
                    {
                        key: '',
                        typeValidation: '',
                        numberOfCharacter: '',
                        regExp: '',
                        promptMessage: '',
                    },
                ],
                // Contact Agent properties
                contactAgentMessage: '',
                botDisableTime: null,
                // Switch properties
                switchName: '',
                switchOutputs: [],
                // JSON Condition properties
                jsonConditionName: 'New JSON Condition',
                matchMode: 'all',
                jsonConditions: [],
                matchLabel: 'Match',
                fallbackLabel: 'Fallback',
                // Condition properties
                conditionType: 'value_comparison',
                leftOperand: '',
                operator: 'equals',
                rightOperand: '',
                conditionExpression: '',
                trueLabel: 'True',
                falseLabel: 'False',
                description: '',
                minValue: 0,
                maxValue: 100,
                expectedValue: true,
                // Delay properties
                delayType: 'fixed',
                delayValue: 5,
                delayUnit: 'seconds',
                randomMin: 1,
                randomMax: 10,
                delayMessage: '',
                showProgress: true,
                delayExpression: '',
                // Documents properties
                document: [],
                textReplacement: [],
                topNChunks: 5,
                cosineSimilarity: 0.4,
                selectedDocumentData: [],
                // Database Integration properties
                tableName: null,
                sqlExtractionModel: null,
                promptSqlNoData: '',
                // Knowledge Graph node properties (S15-P1)
                knowledgeGraphIds: [],
                kgRetrievalConfig: defaultKgRetrievalConfig(),
                // Waterfall Search node properties (S15-P1)
                graphTiers: [],
                // Products properties
                productGroupList: [],
                productTopNChunks: 5,
                productCosineSimilarity: 0.4,
                // Knowledge API properties
                knowledgeApiUrl: '',
                knowledgeHttpMethod: 'GET',
                knowledgeRequestBody: '',
                knowledgeModelJsonValidator: null,
                listHeaders: [
                    {
                        key: '',
                        value: '',
                    },
                ],
                listParams: [],
                responseDataPath: '',
                // Knowledge API Token properties
                enableToken: false,
                tokenAuthMode: 'new',
                tokenName: '',
                useExistingTokenName: '',
                tokenUrl: '',
                tokenHttpMethod: 'POST',
                tokenRequestBody: '',
                tokenHeaders: [{ key: '', value: '' }],
                tokenParams: [],
                tokenResponsePath: 'access_token',
                // SharePoint properties
                sharepoint: {
                    siteUrl: '',
                    tenantId: '',
                    clientId: '',
                    clientSecret: '',
                    folderPath: '',
                    filename: ''
                },
                // Email properties
                emailProvider: 'mailgun',
                toEmail: '',
                ccEmails: [],
                bccEmails: [],
                subject: '',
                emailTemplate: 'custom',
                emailBody: '',
                isHtml: false,
                // Smart Intent properties
                smartIntentGroupName: '',
                smartIntents: null,
                // Advanced Router properties
                advancedRouterName: '',
                advancedRouterPrompt: '',
                advancedRouterConfidenceThreshold: 0.5,
                advancedRouterMaxDepth: 2,
                advancedRouterOutputs: [],
                advancedRouterCustomSchemaEnabled: false,
                advancedRouterIntentField: 'capability_intent',
                advancedRouterConfidenceField: 'confidence',
                advancedRouterSubIntentsField: 'intents',
                smartIntentFallback: {
                        useAI: false,
                        provider: '',
                        model: '',
                        temperature: 0.7,
                        maxTokens: 150,
                        topP: 1.0,
                        presencePenalty: 0.0,
                        frequencyPenalty: 0.0,
                        streamingEnabled: true,
                        lineFlexMessage: false,
                        systemPrompt: '',
                        forbiddenWords: []
                    },
                useCentralConfig: true,
                mailConfig: null,
                showMailConfigSecret: {
                    password: false,
                    smtpOAuth2TenantId: false,
                    smtpOAuth2ClientId: false,
                    smtpOAuth2ClientSecret: false,
                    mailgunApiKey: false,
                },
            },
            emailTestLoading: false,
            emailTestResult: null,
            tempNode: [],
            tempEdge: [],
            uniqueAiModelOptions: [],
            aiModelOptions: [],
            DDTypeValidation: [
                {
                    name: "Phone",
                    value: "PV",
                },
                {
                    name: "Email",
                    value: "EV",
                },
                {
                    name: "Max Character",
                    value: "MCV",
                },
                {
                    name: "Regular Expression",
                    value: "RV",
                },
            ],

            // Variables list for JSON editor
            availableVariables: [
                {
                    key: '{transactionID}',
                    label: 'Transaction ID',
                    description: 'Unique transaction identifier',
                    icon: 'mdi-identifier',
                    category: 'System'
                },
                {
                    key: '{channel}',
                    label: 'Channel',
                    description: 'Current communication channel',
                    icon: 'mdi-forum',
                    category: 'System'
                },
                {
                    key: '{channelID}',
                    label: 'Channel ID',
                    description: 'Channel identifier',
                    icon: 'mdi-pound',
                    category: 'System'
                },
                {
                    key: '{userChannel}',
                    label: 'User Channel',
                    description: 'User specific channel',
                    icon: 'mdi-account-circle',
                    category: 'User'
                },
                {
                    key: '{sessionID}',
                    label: 'Session ID',
                    description: 'User session identifier',
                    icon: 'mdi-clock-outline',
                    category: 'Session'
                },
                {
                    key: '{userMessage}',
                    label: 'User Message',
                    description: 'Current message in the chain (may be rewritten by previous AI / Query nodes)',
                    icon: 'mdi-message-text',
                    category: 'Message'
                },
                {
                    key: '{message}',
                    label: 'Original Message',
                    description: "User's original raw message",
                    icon: 'mdi-message-outline',
                    category: 'Message'
                },
                {
                    key: '{userId}',
                    label: 'User ID',
                    description: 'Authenticated user identifier',
                    icon: 'mdi-account-key',
                    category: 'User'
                },
                {
                    key: '{timestamp}',
                    label: 'Timestamp',
                    description: 'Current UTC timestamp (ISO 8601)',
                    icon: 'mdi-clock-time-eight',
                    category: 'System'
                }
            ],

            // Variables panel state
                        variablesPanelExpanded: false,
                        selectedVariableCategory: 'All',
            
                        // ===== DEBUG MODE STATE =====
                        isDebugMode: false,
                        debugStatus: 'ready',        // ready | running | paused | completed | error | stopped
                        debugSessionId: '',
                        debugSteps: [],              // Timeline of all debug events
                        debugNodeStates: {},         // { nodeId: 'idle'|'running'|'success'|'error'|'paused' }
                        debugEdgeStates: {},         // { edgeId: 'active'|'completed'|'inactive' }
                        debugBreakpoints: new Set(), // Set of node IDs with breakpoints
                        debugMessage: '',
                        debugError: '',
                        debugCurrentNodeId: '',
                        debugPreviousNodeId: '',
                        debugPausedNodeName: '',
                        debugStepCount: 0,
                        debugTotalDuration: 0,
                        debugVariables: {},          // Current context variables snapshot
                        debugChangedVariables: new Set(),
                        debugLastBotReply: '',
                        debugSseReader: null,        // ReadableStream reader reference for cancellation
                        debugPatchVariables: {},     // Collected variable edits (sent on resume)
                        debugOverrideNextNodeId: '', // "Set Next Statement" target node ID
                        debugOverrideNextStepIndex: null,
                        debugStepsBeforeSetNext: null,
                        debugHoveredNodeId: '',      // Node currently hovered (for set-next overlay)
                        debugKeepMemory: false,      // Persist userId across debug runs
                        debugUserId: '',             // Generated GUID for chat history isolation
        }
    },
    computed: {
        inspectorOverlayDefaults() {
            if (!this.isEditingInternalNode) {
                return {};
            }

            return {};
        },
        modelSelectionOptions() {
            return Array.isArray(this.listDDModel) ? this.listDDModel : [];
        },
        selectedNodeModelInfo() {
            return this.findModelOption(this.nodeInspector?.model, this.nodeInspector?.platform, this.nodeInspector?.provider);
        },
        selectedIntentAiModelInfo() {
            return this.findModelOption(this.nodeInspector?.intentaiModel, this.nodeInspector?.intentaiPlatform, this.nodeInspector?.intentaiProvider);
        },
        selectedAdvRouterGenerateModelInfo() {
            return this.findModelOption(this.advRouterGenerateForm?.model, this.advRouterGenerateForm?.platform, this.advRouterGenerateForm?.provider);
        },
        advRouterGenerateHasExistingPrompt() {
            return !!(this.nodeInspector?.advancedRouterPrompt || '').trim();
        },
        modelSupportsReasoning() {
            const name = this.nodeInspector?.model;
            if (!name) return false;
            const ddm = this.findModelOption(name, this.nodeInspector?.platform, this.nodeInspector?.provider);
            return !!(ddm && ddm.useReasoningEffort);
        },
        intentaiModelSupportsReasoning() {
            const name = this.nodeInspector?.intentaiModel;
            if (!name) return false;
            const ddm = this.findModelOption(name, this.nodeInspector?.intentaiPlatform, this.nodeInspector?.intentaiProvider);
            return !!(ddm && ddm.useReasoningEffort);
        },

        // Computed: Get all token names defined in apiknowledge nodes in the current flow
        availableTokenNames() {
            if (!this.tempNode || !Array.isArray(this.tempNode)) return [];
            const currentNodeId = this.selectedNode?.id;
            return this.tempNode
                .filter(n => n.data && n.data.type === 'apiknowledge' && n.data.enableToken && n.data.tokenAuthMode === 'new' && n.data.tokenName && n.data.tokenName.trim() !== '' && n.id !== currentNodeId)
                .map(n => n.data.tokenName);
        },

        // uniqueAiModelOptions() {
        //     const seen = new Set();
        //     return this.aiModelOptions.filter(option => {
        //         const duplicate = seen.has(option.value);
        //         seen.add(option.value);
        //         return !duplicate;
        //     });
        // },
        
        uniqueProviderOptions() {
            // Extract unique providers from AI model options
            const providers = new Set();
            this.uniqueAiModelOptions.forEach(option => {
                if (option.value && option.value.includes('|')) {
                    const provider = option.value.split('|')[0];
                    providers.add(provider);
                } else if (option.name) {
                    // Fallback to extracting from name
                    const parts = option.name.split(' ');
                    if (parts.length > 0) {
                        providers.add(parts[0]);
                    }
                }
            });
            return Array.from(providers).sort();
        },
        
        filteredPromptTemplates() {

            if (this.groupPromptTemplate === null) {
                return [];
            }
            return this.DDPromptTemplate.filter(
                (item) => item.groupPromptTemplateID === this.groupPromptTemplate
            );
        },

        searchedTemplates() {
            if (!this.templateSearchQuery) {
                return this.filteredPromptTemplates;
            }

            const query = this.templateSearchQuery.toLowerCase();
            return this.filteredPromptTemplates.filter(template =>
                template.name.toLowerCase().includes(query) ||
                (template.prompt && template.prompt.toLowerCase().includes(query))
            );
        },

        // IntentAI Prompt Template computed properties
        filteredIntentaiPromptTemplates() {
            if (this.intentaiGroupPromptTemplate === null) {
                return [];
            }
            return this.DDPromptTemplate.filter(
                (item) => item.groupPromptTemplateID === this.intentaiGroupPromptTemplate
            );
        },

        searchedIntentaiTemplates() {
            if (!this.intentaiTemplateSearchQuery) {
                return this.filteredIntentaiPromptTemplates;
            }

            const query = this.intentaiTemplateSearchQuery.toLowerCase();
            return this.filteredIntentaiPromptTemplates.filter(template =>
                template.name.toLowerCase().includes(query) ||
                (template.prompt && template.prompt.toLowerCase().includes(query))
            );
        },

        // Smart Intent Prompt Template computed properties
        filteredSmartIntentPromptTemplates() {
            if (this.smartIntentGroupPromptTemplate === null) {
                return [];
            }
            return this.DDPromptTemplate.filter(
                (item) => item.groupPromptTemplateID === this.smartIntentGroupPromptTemplate
            );
        },

        smartIntentSearchedTemplates() {
            if (!this.smartIntentTemplateSearchQuery) {
                return this.filteredSmartIntentPromptTemplates;
            }

            const query = this.smartIntentTemplateSearchQuery.toLowerCase();
            return this.filteredSmartIntentPromptTemplates.filter(template =>
                template.name.toLowerCase().includes(query) ||
                (template.prompt && template.prompt.toLowerCase().includes(query))
            );
        },

        // Smart Intent Fallback Prompt Template computed properties
        filteredSmartIntentFallbackPromptTemplates() {
            if (this.smartIntentFallbackGroupPromptTemplate === null) {
                return [];
            }
            return this.DDPromptTemplate.filter(
                (item) => item.groupPromptTemplateID === this.smartIntentFallbackGroupPromptTemplate
            );
        },

        smartIntentFallbackSearchedTemplates() {
            if (!this.smartIntentFallbackTemplateSearchQuery) {
                return this.filteredSmartIntentFallbackPromptTemplates;
            }

            const query = this.smartIntentFallbackTemplateSearchQuery.toLowerCase();
            return this.filteredSmartIntentFallbackPromptTemplates.filter(template =>
                template.name.toLowerCase().includes(query) ||
                (template.prompt && template.prompt.toLowerCase().includes(query))
            );
        },

        // Filter variables by category
        filteredVariables() {
            if (this.selectedVariableCategory === 'All') {
                return this.availableVariables;
            }
            return this.availableVariables.filter(v => v.category === this.selectedVariableCategory);
        },

        // Get unique categories
        variableCategories() {
            const categories = ['All', ...new Set(this.availableVariables.map(v => v.category))];
            return categories;
        },
        lockStatus() {
            // ตรวจสอบว่า instance ของ vueFlow พร้อมใช้งานหรือไม่
            if (this.$refs.vueFlow) {
                // ปุ่ม lock จะควบคุมค่า nodesDraggable เราจึงใช้ค่านี้เป็นตัวชี้วัดสถานะ
                // ถ้า nodesDraggable เป็น false หรือ undefined แสดงว่า "Locked"
                const nodesDraggable = this.$refs.vueFlow.nodesDraggable;
                return nodesDraggable === false;
            }
            // ค่าเริ่มต้นก่อนที่ component จะ mount (unlocked)
            return false;
        },
        DDTimeZone() {
            return [
                {
                    value: "Asia/Bangkok",
                    name: "Thailand",
                },
                {
                    value: "Asia/Hong_Kong",
                    name: "Hong Kong",
                },
                {
                    value: "Asia/Tokyo",
                    name: "Japan",
                },
                {
                    value: "America/New_York",
                    name: "USA",
                },
                {
                    value: "Europe/London",
                    name: "England",
                },
                {
                    value: "Australia/Sydney",
                    name: "Australia",
                },
            ];
        },
        // Undo/Redo computed properties
        canUndo() {
            return this.historyIndex > 0;
        },
        canRedo() {
            return this.historyIndex < this.history.length - 1;
        }
    },
    watch: {
        // Watch for changes in document selection to fetch document data
        'nodeInspector.documentVectorName'(newValue) {
            if (newValue && this.nodeInspector.enableSpecificDataSelection) {
                this.getDocumentData();
            } else {
                this.resetDocumentDataTable();
            }
        },

        // Watch for model selection to show preview
        selectedModelToLoad(newValue) {
            if (newValue) {
                this.loadModelConfigPreview(newValue);
            } else {
                this.modelConfigPreview = null;
            }
        },

        // Watch for Smart Intent model selection to show preview
        selectedSmartIntentModelToLoad(newValue) {
            if (newValue) {
                this.loadSmartIntentModelConfigPreview(newValue);
            } else {
                this.smartIntentModelConfigPreview = null;
            }
        },

        // Watch for uniqueAiModelOptions to be loaded and filter messageInput models if needed
        uniqueAiModelOptions(newValue) {
            if (newValue && newValue.length > 0 && this.tempNode && this.tempNode.length > 0) {
                // Check if there are messageInput nodes that need filtering
                let hasUnfilteredMessageInput = this.tempNode.some(node => 
                    node.data && 
                    node.data.type === 'messageInput' && 
                    node.data.selectedModel && 
                    node.data.selectedModel.length > 0
                );

                if (hasUnfilteredMessageInput) {
                    console.log('AI Models loaded, applying filter to messageInput nodes');
                    this.filterMessageInputModels();
                }
            }
        },

        internalFlowEdges() {
            if (!this.flowToolEditorDialog) return;
            this.$nextTick(() => {
                this.checkAllInternalNodesValidation();
            });
        }
    },
    methods: {
        async checkRouteMode() {
            const { id, mode, importData } = this.$route.query;

            if (importData) {
                // Handle imported flow data
                await this.handleImportedData(importData);
            } else if (id) {
                // Handle edit/view mode
                this.editFlowId = id;
                this.isEditMode = mode === 'edit';
                this.isViewMode = mode === 'view';

                // Load the flow data
                await this.loadFlowForEdit(id);

            }
        },
        goBack() {
                // Use custom confirmation dialog if in edit mode with unsaved changes
            this.showExitConfirmation(() => {
                this.$router.push('/FlowList');
            });
        },
        generateGuid() {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                const r = Math.random() * 16 | 0;
                const v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        },

        // Undo/Redo System Methods
        saveStateToHistory(action = 'Unknown Action') {
            if (this.isPerformingUndoRedo) return;

            // Create a deep copy of current state
            const currentState = {
                nodes: JSON.parse(JSON.stringify(this.tempNode)),
                edges: JSON.parse(JSON.stringify(this.tempEdge)),
                action: action,
                timestamp: new Date().toISOString()
            };

            // Remove any history entries after current index (if we're not at the end)
            if (this.historyIndex < this.history.length - 1) {
                this.history = this.history.slice(0, this.historyIndex + 1);
            }

            // Add new state to history
            this.history.push(currentState);

            // Limit history size
            if (this.history.length > this.maxHistorySize) {
                this.history.shift();
            } else {
                this.historyIndex++;
            }
        },

        undo() {
            if (!this.canUndo) return;

            this.isPerformingUndoRedo = true;
            
            try {
                this.historyIndex--;
                const previousState = this.history[this.historyIndex];
                
                // Restore previous state
                this.tempNode = JSON.parse(JSON.stringify(previousState.nodes));
                this.tempEdge = JSON.parse(JSON.stringify(previousState.edges));
                
                // Clear selection when undoing
                this.selectedNode = null;
                this.selectedNodes = [];
                this.closeInspector();
                
                this.showSuccess(this.$t('_undone', { action: previousState.action }));
                
            } finally {
                this.isPerformingUndoRedo = false;
            }
        },

        redo() {
            if (!this.canRedo) return;

            this.isPerformingUndoRedo = true;
            
            try {
                this.historyIndex++;
                const nextState = this.history[this.historyIndex];
                
                // Restore next state
                this.tempNode = JSON.parse(JSON.stringify(nextState.nodes));
                this.tempEdge = JSON.parse(JSON.stringify(nextState.edges));
                
                // Clear selection when redoing
                this.selectedNode = null;
                this.selectedNodes = [];
                this.closeInspector();
                
                this.showSuccess(this.$t('_redone', { action: nextState.action }));
                
            } finally {
                this.isPerformingUndoRedo = false;
            }
        },

        initializeHistory() {
            // Save initial state
            this.saveStateToHistory('Initial State');
        },
        // Load GPT Models (async version)
        loadGPTModels() {
            return new Promise((resolve, reject) => {
                this.$call.DDGPTModel(
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            this.aiModelOptions = dataResponse.data.map(m => ({
                                name: m.name,
                                value: m.modelDisplayID,
                                modelId: m.id,
                                icon: 'mdi-robot',
                                color: '#10a37f',
                                searchableText: `${m.modelDisplayID}: ${m.name}` // สำหรับการค้นหา
                            }));
                            this.uniqueAiModelOptions = this.aiModelOptions.filter((option, index, self) =>
                                index === self.findIndex((t) => (
                                    t.value === option.value
                                ))
                            );
                        } else {
                            this.aiModelOptions = [];
                        }
                        resolve();
                    },
                    (err) => {
                        this.aiModelOptions = [];
                        reject(err);
                    }
                );
            });
        },
        // Load Model Management Data (async version)
        GetAllDDModelManagement() {
            return new Promise((resolve, reject) => {
                let self = this;
                self.$call.GetAllDDModelManagement(
                    0,
                    (dataResponse) => {
                        if (dataResponse.status == 0) {
                            self.DDVector = dataResponse.data.vectorDBName;
                            self.listDataIntegration = dataResponse.data.dataIntegration;
                            self.DDGroupReplace = dataResponse.data.groupReplace;
                            self.DDProductGroup = dataResponse.data.productGroup;
                            self.listDDModel = dataResponse.data.modelData;
                            self.listDDIntentGroup = dataResponse.data.intentGroup;
                            self.listSqlExtract = dataResponse.data.modelPublih.filter((e) => e.typeName == 'Bot');
                            self.listMonitorLineName = dataResponse.data.monitorSocialLine;
                            self.DDPromptTemplate = dataResponse.data.promptTemplate.listData;
                            self.DDGroupPromptTemplate = dataResponse.data.groupPromptTemplate;
                            self.listDDProvider = dataResponse.data.provider;
                        } else {
                            self.DDVector = [];
                            self.listDataIntegration = [];
                            self.listSqlExtract = [];
                            // self.listGuardRailModel = [];
                            self.DDGroupReplace = [];
                            self.DDProductGroup = [];
                            self.DDPromptTemplate = [];
                            self.listDDProvider = [];
                            self.listDDModel = [];
                        }
                        resolve();
                    },
                    (errorResource) => {
                        console.error(errorResource);
                        reject(errorResource);
                    }
                );
            });
        },
        loadAgentTools() {
            return new Promise((resolve) => {
                this.$call.GetAgentToolDropdown(
                    (dataResponse) => {
                        const tools = dataResponse?.data || dataResponse?.Data || [];
                        this.agentToolOptions = tools.map(tool => ({
                            id: tool.id ?? tool.ID,
                            name: tool.name ?? tool.Name,
                            description: tool.description ?? tool.Description,
                            httpMethod: tool.httpMethod ?? tool.HttpMethod,
                            url: tool.apiEndpoint ?? tool.ApiEndpoint ?? tool.url ?? tool.Url,
                        })).filter(tool => tool.id);
                        resolve();
                    },
                    () => {
                        this.agentToolOptions = [];
                        resolve();
                    }
                );
            });
        },
        // Method to filter messageInput models after uniqueAiModelOptions is loaded
        filterMessageInputModels() {
            if (!this.uniqueAiModelOptions || this.uniqueAiModelOptions.length === 0) {
                return;
            }

            if (!this.tempNode || this.tempNode.length === 0) {
                return;
            }

            let messageInputNodes = this.tempNode.filter(n => n.data && n.data.type === 'messageInput');
            
            if (messageInputNodes.length > 0) {
                messageInputNodes.forEach(messageInputNode => {
                    if (messageInputNode.data.selectedModel && Array.isArray(messageInputNode.data.selectedModel) && messageInputNode.data.selectedModel.length > 0) {
                        this.unableToAccessModel = messageInputNode.data.selectedModel;
                        let tempUniqueModels = this.uniqueAiModelOptions.map(option => option.value);
                        this.unableToAccessModel = this.unableToAccessModel.filter(model => !tempUniqueModels.includes(model));
                        
                        // Update the specific node
                        let filteredModels = messageInputNode.data.selectedModel.filter(model => !this.unableToAccessModel.includes(model));
                        
                        // Update tempNode array
                        this.tempNode = this.tempNode.map(node => {
                            if (node.id === messageInputNode.id) {
                                return {
                                    ...node,
                                    data: {
                                        ...node.data,
                                        selectedModel: filteredModels
                                    }
                                };
                            }
                            return node;
                        });
                        
                        if (this.unableToAccessModel.length > 0) {
                            console.log(`Filtered out inaccessible models from node ${messageInputNode.id}: ${this.unableToAccessModel.join(', ')}`);
                        }
                    }
                });
            }
        },
        // Reload all models and data (useful for refresh actions)
        async reloadModels() {
            try {
                await Promise.all([
                    this.loadGPTModels(),
                    this.GetAllDDModelManagement(),
                    this.loadAgentTools()
                ]);
                this.showSuccess(this.$t('_modelsReloaded'));
            } catch (error) {
                console.error('Failed to reload models:', error);
                this.showError(this.$t('_failedReloadModels'));
            }
        },
        clearHistory() {
            this.history = [];
            this.historyIndex = -1;
            this.saveStateToHistory('Flow Saved');
            // Show info message about history being cleared
            this.showInfo(this.$t('_undoRedoCleared'));
            console.log('History cleared after save');
        },
        onSelectionChange({ nodes }) {
            // When editing an internal flow node, ignore main flow selection changes
            // to prevent overwriting selectedNode set by onInternalNodeClick
            if (this.isEditingInternalNode) return;

            // event นี้จะทำงานหลัง onSelectionDragEnd

            // เช็คว่าเพิ่งลากเสร็จ และมี node ที่ถูกเลือกแค่ตัวเดียวหรือไม่
            if (this.wasJustDragSelecting && nodes.length === 1) {
                this.selectedNodes = []; // ยกเลิกการเลือก
            }

            // รีเซ็ตค่าธงเสมอ เพื่อให้การคลิกครั้งต่อไปทำงานปกติ
            this.wasJustDragSelecting = false;

            // อัปเดต selectedNodes
            this.selectedNodes = nodes;

            // อัปเดต selectedNode สำหรับ inspector panel
            if (this.selectedNodes.length === 1) {
                this.selectedNode = this.selectedNodes[0];
            } else {
                this.selectedNode = null;
            }
        },
        onNodeClick({ node }) {
            if (this.isDebugMode) return;
            if (this.flowToolEditorDialog) return;
            this.openNodeInspector(node);
        },
        openNodeInspector(node) {
            if (node.data?.type === 'agent') {
                this.syncAgentToolDecisionAvailability([node.id]);
                node = this.tempNode.find(tempNode => tempNode.id === node.id) || node;
            }

            this.selectedNode = node;

            // Default Base Prompt text
            const defaultBasePrompt = `Find the Intent from the message of users.
#Condition for Response
1. If the user asks a question related to or similar in meaning to the #Group Intent below. You must response 1. Otherwise, 0`;

            // Initialize inspector values from node data
            this.nodeInspector = {
                label: node.data.label || '',
                color: node.data.color || '#1976D2',
                placeholderText: node.data.placeholderText || '',
                required: node.data.required || false,
                selectedModel: Array.isArray(node.data.selectedModel)
                    ? node.data.selectedModel
                    : (node.data.selectedModel ? [node.data.selectedModel] : []),
                systemPrompt: node.data.systemPrompt || '',
                temperature: node.data.temperature || null,
                maxTokens: node.data.maxTokens || null,
                // AI Model Configuration properties (matching ModelManagement.vue defaults)
                platform: node.data.platform || null,
                provider: node.data.provider || null,
                apiVersion: node.data.apiVersion || null,
                model: node.data.model || null,
                useBaseModelPrompt: node.data.useBaseModelPrompt !== undefined ? node.data.useBaseModelPrompt : false,
                orchestratorName: node.data.orchestratorName || 'New Orchestrator',
                confidenceThreshold: node.data.confidenceThreshold !== undefined ? node.data.confidenceThreshold : 0.7,
                enableMultiAgent: node.data.enableMultiAgent !== undefined ? node.data.enableMultiAgent : false,
                multiAgentStrategy: node.data.multiAgentStrategy || 'pipeline',
                enableCache: node.data.enableCache !== undefined ? node.data.enableCache : false,
                cacheTTLMinutes: node.data.cacheTTLMinutes || 1440,
                cacheScope: node.data.cacheScope || 'flow',
                enableSemanticCache: node.data.enableSemanticCache !== undefined ? node.data.enableSemanticCache : false,
                similarityThreshold: node.data.similarityThreshold !== undefined ? node.data.similarityThreshold : 0.95,
                enableTransactionId: node.data.enableTransactionId !== undefined ? node.data.enableTransactionId : false,
                enableInputMessageReplace: node.data.enableInputMessageReplace !== undefined ? node.data.enableInputMessageReplace : false,
                enableVoicePromptReplace: node.data.enableVoicePromptReplace !== undefined ? node.data.enableVoicePromptReplace : false,
                enableLanguageReplace: node.data.enableLanguageReplace !== undefined ? node.data.enableLanguageReplace : false,
                agentName: node.data.agentName || 'New Agent',
                agentDescription: node.data.agentDescription || '',
                personaPrompt: node.data.personaPrompt || '',
                selectedTools: Array.isArray(node.data.selectedTools) ? node.data.selectedTools : [],
                enableAgentToolDecision: node.data.enableAgentToolDecision !== undefined ? node.data.enableAgentToolDecision : false,
                useCustomAIModel: node.data.useCustomAIModel !== undefined ? node.data.useCustomAIModel : false,
                agentProvider: node.data.agentProvider || null,
                agentModel: node.data.agentModel || null,
                agentTemperature: node.data.agentTemperature !== undefined ? node.data.agentTemperature : 0,
                agentMaxTokens: node.data.agentMaxTokens || null,
                agentTopP: node.data.agentTopP !== undefined ? node.data.agentTopP : 1,
                agentFrequencyPenalty: node.data.agentFrequencyPenalty || null,
                agentPresencePenalty: node.data.agentPresencePenalty || null,
                agentStopSequence: node.data.agentStopSequence || null,
                agentStreamResponse: node.data.agentStreamResponse !== undefined ? node.data.agentStreamResponse : false,
                agentJsonResponse: node.data.agentJsonResponse !== undefined ? node.data.agentJsonResponse : false,
                agentGender: node.data.agentGender || 'male',
                agentTypingSpeed: node.data.agentTypingSpeed || null,
                agentMaxInputLength: node.data.agentMaxInputLength || 200000,
                agentMemoryTurn: node.data.agentMemoryTurn || null,
                agentMemoryDuration: node.data.agentMemoryDuration || null,
                agentShortTermMemory: node.data.agentShortTermMemory || 200,
                agentLongTermMemory: node.data.agentLongTermMemory !== undefined ? node.data.agentLongTermMemory : false,
                agentEnableTime: node.data.agentEnableTime !== undefined ? node.data.agentEnableTime : false,
                agentTimeZone: node.data.agentTimeZone || null,
                agentEnableTransactionId: node.data.agentEnableTransactionId !== undefined ? node.data.agentEnableTransactionId : false,
                knowledgeConnections: {
                    ...this.createDefaultKnowledgeConnections(),
                    ...(node.data.knowledgeConnections || {})
                },
                executionMode: node.data.executionMode || 'direct',
                goal: node.data.goal || '',
                flowId: node.data.flowId || '',
                flowParameters: node.data.flowParameters || {},
                autoExecuteTools: node.data.autoExecuteTools !== undefined ? node.data.autoExecuteTools : false,
                masterToolId: node.data.masterToolId || null,
                toolName: node.data.toolName || 'New Tool',
                toolDescription: node.data.toolDescription || '',
                toolType: node.data.toolType || 'master',
                endpoint: node.data.endpoint || '',
                method: node.data.method || 'GET',
                headers: Array.isArray(node.data.headers) ? node.data.headers : [],
                body: node.data.body || '',
                textContent: node.data.textContent || '',
                flowToolName: node.data.flowToolName || 'New Flow Tool',
                flowToolDescription: node.data.flowToolDescription || '',
                internalNodes: Array.isArray(node.data.internalNodes) ? JSON.parse(JSON.stringify(node.data.internalNodes)) : [],
                internalEdges: Array.isArray(node.data.internalEdges) ? JSON.parse(JSON.stringify(node.data.internalEdges)) : [],
                internalNodeCount: node.data.internalNodeCount || this.getInternalProcessingNodeCount(node.data.internalNodes || []),
                showWarning: node.data.showWarning !== undefined ? node.data.showWarning : true,
                
                // Parallel execution settings
                isParallel: node.data.isParallel !== undefined ? node.data.isParallel : false,
                
                gender: node.data.gender || 'male',
                typingSpeed: node.data.typingSpeed || null,
                maxInputLength: node.data.maxInputLength || 200000,
                streamResponse: node.data.streamResponse !== undefined ? node.data.streamResponse : false,
                jsonResponse: node.data.jsonResponse !== undefined ? node.data.jsonResponse : false,
                enableTime: node.data.enableTime || null,
                timeZone: node.data.timeZone || null,
                memoryTurn: node.data.memoryTurn || null,
                memoryDuration: node.data.memoryDuration || null,
                shortTermMemory: node.data.shortTermMemory || null,
                longTermMemory: node.data.longTermMemory !== undefined ? node.data.longTermMemory : false,
                isChainMassage: node.data.isChainMassage !== undefined ? node.data.isChainMassage : false,
                lenChainMassage: node.data.lenChainMassage || null,
                timeSecondChainMassage: node.data.timeSecondChainMassage || null,
                topP: node.data.topP || null,
                frequencyPenalty: node.data.frequencyPenalty || null,
                presencePenalty: node.data.presencePenalty || null,
                stopSequence: node.data.stopSequence || null,
                reasoningEffort: node.data.reasoningEffort || null,
                intentaiReasoningEffort: node.data.intentaiReasoningEffort || null,
                loadingBar: node.data.loadingBar !== undefined ? node.data.loadingBar : false,
                loadingBarDuration: node.data.loadingBarDuration || 0,
                monitorLineUserId: node.data.monitorLineUserId || null,
                monitorSocialID: node.data.monitorSocialID || null,
                showLineName: node.data.showLineName !== undefined ? node.data.showLineName : false,
                forbiddenWords: node.data.forbiddenWords || [''],
                intentGroupName: node.data.intentGroupName || '',
                intents: node.data.type === 'intent'
                    ? (node.data.intents || [
                        {
                            id: `intent-${this.generateGuid()}`,
                            name: 'Intent Name',
                            priority: 0, // Default priority
                            prompt: '',
                            basePrompt: defaultBasePrompt, // Use default base prompt
                            enableBasePrompt: false, // Disable base prompt by default
                            messages: [
                                { id: `msg-${this.generateGuid()}`, text: '' }
                            ],
                            mainAction: { id: `main-${this.generateGuid()}`, label: 'Main Action' },
                            parallelActions: []
                        }
                    ])
                    : [],
                examples: node.data.examples || '',
                // Initialize actions and outputs for intent nodes
                actions: node.data.type === 'intent'
                    ? (node.data.actions || [
                        {
                            id: `action-${this.generateGuid()}`,
                            label: 'Action 1',
                            parallels: [
                                { id: `parallel-${this.generateGuid()}`, label: 'Parallel Action 1' }
                            ]
                        }
                    ])
                    : [],
                outputs: node.data.type === 'intent'
                    ? (node.data.outputs || [])
                    : [],
                // Smart Intent properties
                smartIntentGroupName: node.data.smartIntentGroupName || '',
                smartIntents: node.data.type === 'smartIntent'
                    ? (node.data.smartIntents || [
                        {
                            id: `smart-intent-${this.generateGuid()}`,
                            name: 'Smart Intent Name',
                            priority: 0,
                            prompt: '',
                            basePrompt: defaultBasePrompt,
                            enableBasePrompt: false,
                            useMemory: false,
                            messages: [
                                { id: `msg-${this.generateGuid()}`, text: '' }
                            ],
                            mainAction: {
                                id: `main-${this.generateGuid()}`,
                                label: 'Main Action',
                                useAI: true,
                                // Basic Information - match AI Model defaults
                                platform: null,
                                provider: null,
                                model: null,
                                apiVersion: null,
                                gender: 'male',
                                typingSpeed: null,
                                maxInputLength: 200000,
                                // Response Options - match AI Model defaults
                                streamResponse: false,
                                enableTime: null,
                                timeZone: null,
                                jsonResponse: false,
                                // AI Parameters - match AI Model defaults
                                temperature: null,
                                topP: null,
                                frequencyPenalty: null,
                                presencePenalty: null,
                                maxTokens: null,
                                stopSequence: null,
                                // Memory Settings - match AI Model defaults
                                memoryTurn: null,
                                memoryDuration: null,
                                shortTermMemory: null,
                                longTermMemory: false,
                                // Multi-turn Input Configuration - match AI Model defaults
                                isChainMassage: false,
                                lenChainMassage: null,
                                timeSecondChainMassage: null,
                                // Forbidden Words - match AI Model defaults
                                forbiddenWords: [''],
                                // Knowledge Configuration
                                knowledgeType: '',
                                // Knowledge Sources Enable Flags
                                enableDocuments: false,
                                enableDatabaseIntegration: false,
                                enableProducts: false,
                                enableApiKnowledge: false,
                                // Documents properties
                                document: [],
                                topNChunks: 5,
                                cosineSimilarity: 0.5,
                                // Database Integration properties
                                tableName: null,
                                sqlExtractionModel: null,
                                promptSqlNoData: '',
                                // Products properties
                                productGroupList: [],
                                productTopNChunks: 5,
                                productCosineSimilarity: 0.5,
                                // API Knowledge properties
                                knowledgeApiUrl: '',
                                knowledgeHttpMethod: 'GET',
                                listParams: [],
                                knowledgeResponseDataPath: '',
                                // API Knowledge Token properties
                                enableToken: false,
                                tokenAuthMode: 'new',
                                tokenName: '',
                                useExistingTokenName: '',
                                tokenUrl: '',
                                tokenHttpMethod: 'POST',
                                tokenRequestBody: '',
                                tokenParams: [],
                                tokenResponsePath: 'access_token'
                            }
                        }
                    ])
                    : [],
                smartIntentFallback: node.data.type === 'smartIntent'
                    ? (node.data.smartIntentFallback || {
                        useAI: false,
                        // Basic Information - match AI Model defaults
                        platform: null,
                        provider: null,
                        model: null,
                        apiVersion: null,
                        gender: 'male',
                        typingSpeed: null,
                        maxInputLength: 200000,
                        // Response Options - match AI Model defaults
                        streamResponse: false,
                        enableTime: null,
                        timeZone: null,
                        // AI Parameters - match AI Model defaults
                        temperature: null,
                        topP: null,
                        frequencyPenalty: null,
                        presencePenalty: null,
                        maxTokens: null,
                        stopSequence: null,
                        // Memory Settings - match AI Model defaults
                        memoryTurn: null,
                        memoryDuration: null,
                        shortTermMemory: null,
                        longTermMemory: false,
                        // Multi-turn Input Configuration - match AI Model defaults
                        isChainMassage: false,
                        lenChainMassage: null,
                        timeSecondChainMassage: null,
                        // Forbidden Words - match AI Model defaults
                        forbiddenWords: ['']
                    })
                    : {},
                // Advanced Router properties
                advancedRouterName: node.data.advancedRouterName || '',
                advancedRouterPrompt: node.data.advancedRouterPrompt || '',
                advancedRouterConfidenceThreshold: node.data.advancedRouterConfidenceThreshold != null ? node.data.advancedRouterConfidenceThreshold : 0.5,
                advancedRouterMaxDepth: node.data.advancedRouterMaxDepth || 2,
                advancedRouterOutputs: node.data.type === 'advancedRouter'
                    ? (Array.isArray(node.data.advancedRouterOutputs) ? JSON.parse(JSON.stringify(node.data.advancedRouterOutputs)) : [])
                    : [],
                advancedRouterCustomSchemaEnabled: !!node.data.customSchemaEnabled,
                advancedRouterIntentField: (node.data.schemaFieldMapping && node.data.schemaFieldMapping.intentField) || 'capability_intent',
                advancedRouterConfidenceField: (node.data.schemaFieldMapping && node.data.schemaFieldMapping.confidenceField) || 'confidence',
                advancedRouterSubIntentsField: (node.data.schemaFieldMapping && node.data.schemaFieldMapping.subIntentsField) || 'intents',
                // JSON Condition properties
                jsonConditionName: node.data.jsonConditionName || 'Rule-Based',
                matchMode: node.data.matchMode || 'all',
                jsonConditions: node.data.type === 'jsonCondition'
                    ? (Array.isArray(node.data.conditions) && node.data.conditions.length > 0
                        ? JSON.parse(JSON.stringify(node.data.conditions))
                        : [{ id: `jc-${this.generateGuid()}`, jsonPath: '', operator: '', compareValue: '' }])
                    : [],
                matchLabel: node.data.matchLabel || 'Match',
                fallbackLabel: node.data.fallbackLabel || 'Fallback',
                // Guardrail AI properties
                guardrailType: node.data.guardrailType || 'content_safety',
                guardrailModel: node.data.guardrailModel || 'gpt-4',
                guardrailPrompt: node.data.guardrailPrompt || '',
                safetyThreshold: node.data.safetyThreshold || 80,
                enableLogging: node.data.enableLogging !== undefined ? node.data.enableLogging : true,
                // Intent AI properties
                intentaiModel: Array.isArray(node.data.intentaiModel)
                    ? (node.data.intentaiModel.length > 0 ? node.data.intentaiModel[0] : null)
                    : (node.data.intentaiModel || (Array.isArray(node.data.intentModel) ? (node.data.intentModel.length > 0 ? node.data.intentModel[0] : null) : node.data.intentModel) || null),
                intentaiPlatform: node.data.intentaiPlatform || node.data.platform || '',
                intentAiModelApiVersion: node.data.intentAiModelApiVersion || null,
                intentaiProvider: node.data.intentaiProvider || node.data.provider || '',
                intentaiGender: node.data.intentaiGender || node.data.gender || '',
                intentaiTypingSpeed: node.data.intentaiTypingSpeed || node.data.typingSpeed || 0,
                intentaiMaxInputLength: node.data.intentaiMaxInputLength || node.data.maxInputLength || 4000,
                intentaiStreamResponse: node.data.intentaiStreamResponse !== undefined ? node.data.intentaiStreamResponse : (node.data.streamResponse !== undefined ? node.data.streamResponse : true),
                intentaiJsonResponse: node.data.intentaiJsonResponse !== undefined ? node.data.intentaiJsonResponse : (node.data.jsonResponse !== undefined ? node.data.jsonResponse : false),
                intentaiEnableTime: node.data.intentaiEnableTime !== undefined ? node.data.intentaiEnableTime : (node.data.enableTime !== undefined ? node.data.enableTime : false),
                intentaiTimeZone: node.data.intentaiTimeZone || node.data.timeZone || '',
                intentaiMemoryTurn: node.data.intentaiMemoryTurn || node.data.memoryTurn || 5,
                intentaiMemoryDuration: node.data.intentaiMemoryDuration || node.data.memoryDuration || 60,
                intentaiShortTermMemory: node.data.intentaiShortTermMemory || node.data.shortTermMemory || 3,
                intentaiLongTermMemory: node.data.intentaiLongTermMemory !== undefined ? node.data.intentaiLongTermMemory : (node.data.longTermMemory !== undefined ? node.data.longTermMemory : false),
                intentaiIsChainMassage: node.data.intentaiIsChainMassage !== undefined ? node.data.intentaiIsChainMassage : (node.data.isChainMassage !== undefined ? node.data.isChainMassage : false),
                intentaiLenChainMassage: node.data.intentaiLenChainMassage || node.data.lenChainMassage || 5,
                intentaiTimeSecondChainMassage: node.data.intentaiTimeSecondChainMassage || node.data.timeSecondChainMassage || 30,
                intentaiTemperature: node.data.intentaiTemperature || node.data.temperature || 0.7,
                intentaiTopP: node.data.intentaiTopP || node.data.topP || 1.0,
                intentaiFrequencyPenalty: node.data.intentaiFrequencyPenalty || node.data.frequencyPenalty || 0,
                intentaiPresencePenalty: node.data.intentaiPresencePenalty || node.data.presencePenalty || 0,
                intentaiMaxTokens: node.data.intentaiMaxTokens || node.data.maxTokens || 1000,
                intentaiSystemPrompt: node.data.intentaiSystemPrompt || node.data.intentSystemPrompt || '',
                intentaiExamples: node.data.intentaiExamples || node.data.intentExamples || '',
                intentaiConfidence: node.data.intentaiConfidence || node.data.intentConfidence || 75,
                intentaiFallbackIntent: node.data.intentaiFallbackIntent || node.data.fallbackIntent || 'unknown',
                intentaiShowLineName: node.data.intentaiShowLineName !== undefined ? node.data.intentaiShowLineName : (node.data.showLineName !== undefined ? node.data.showLineName : false),
                intentaiLoadingBar: node.data.intentaiLoadingBar !== undefined ? node.data.intentaiLoadingBar : (node.data.loadingBar !== undefined ? node.data.loadingBar : false),
                intentaiLoadingBarDuration: node.data.intentaiLoadingBarDuration || node.data.loadingBarDuration || 0,
                intentaiMonitorLineUserId: node.data.intentaiMonitorLineUserId || node.data.monitorLineUserId || null,
                intentaiMonitorSocialID: node.data.intentaiMonitorSocialID || node.data.monitorSocialID || null,
                intentaiForbiddenWords: node.data.intentaiForbiddenWords || node.data.forbiddenWords || [''],
                // API Trigger properties
                apiUrl: node.data.apiUrl || '',
                httpMethod: node.data.httpMethod || 'POST',
                headers: node.data.type === 'tool' ? (Array.isArray(node.data.headers) ? node.data.headers : []) : (node.data.headers || ''),
                textResponse: node.data.textResponse || '',
                jsonValidate: node.data.jsonValidate || '{\n   "Name":"Value"\n}',
                modelJsonValidator: node.data.modelJsonValidator || null,
                listHeadersAPITrigger: node.data.listHeadersAPITrigger || [
                    {
                        key: '',
                        value: '',
                    },
                ],
                listValidation: node.data.listValidation || [
                    {
                        key: '',
                        typeValidation: '',
                        numberOfCharacter: '',
                        regExp: '',
                        promptMessage: '',
                    },
                ],
                // Contact Agent properties
                departmentName: node.data.departmentName || '',
                contactAgentMessage: node.data.contactAgentMessage || '',
                handoffMessage: node.data.handoffMessage || '',
                botDisableTime: node.data.botDisableTime || null,
                disableBot: node.data.disableBot !== undefined ? node.data.disableBot : true,
                // Switch properties
                switchName: node.data.switchName || '',
                switchOutputs: node.data.type === 'switch'
                    ? (node.data.switchOutputs || [
                        {
                            id: `switch-output-${this.generateGuid()}`, // Use GUID
                            name: 'Output 1',
                            condition: ''
                        },
                        {
                            id: `switch-output-${this.generateGuid()}`, // Use GUID
                            name: 'Output 2',
                            condition: ''
                        }
                    ])
                    : [],
                // Condition properties
                conditionType: node.data.conditionType || 'value_comparison',
                leftOperand: node.data.leftOperand || '',
                operator: node.data.operator || 'equals',
                rightOperand: node.data.rightOperand || '',
                conditionExpression: node.data.conditionExpression || '',
                trueLabel: node.data.trueLabel || 'True',
                falseLabel: node.data.falseLabel || 'False',
                minValue: node.data.minValue || 0,
                maxValue: node.data.maxValue || 100,
                expectedValue: node.data.expectedValue !== undefined ? node.data.expectedValue : true,
                // Delay properties
                delayType: node.data.delayType || 'fixed',
                delayValue: node.data.delayValue || 5,
                delayUnit: node.data.delayUnit || 'seconds',
                randomMin: node.data.randomMin || 1,
                randomMax: node.data.randomMax || 10,
                delayMessage: node.data.delayMessage || '',
                showProgress: node.data.showProgress !== undefined ? node.data.showProgress : true,
                delayExpression: node.data.delayExpression || '',
                // Documents properties
                document: node.data.document || [],
                selectedDocumentData: node.data.selectedDocumentData || [],
                enableSpecificDataSelection: node.data.enableSpecificDataSelection !== undefined ? node.data.enableSpecificDataSelection : false,
                documentVectorName: node.data.documentVectorName || '',
                textReplacement: node.data.textReplacement || [],
                topNChunks: node.data.topNChunks || 5,
                cosineSimilarity: node.data.cosineSimilarity || 0.4,
                // Database Integration properties
                tableName: node.data.tableName || null,
                sqlExtractionModel: node.data.sqlExtractionModel || null,
                promptSqlNoData: node.data.promptSqlNoData || '',
                // Knowledge Graph node properties (S15-P1) — ค่าเป็น graphKey · config PascalCase ครบ 10 คีย์เสมอ
                knowledgeGraphIds: Array.isArray(node.data.knowledgeGraphIds) ? [...node.data.knowledgeGraphIds] : [],
                kgRetrievalConfig: normalizeKgRetrievalConfig(node.data.kgRetrievalConfig),
                // Waterfall Search node properties (S15-P1) — tier แต่ละตัวมี _key runtime สำหรับ v-for (ไม่ถูก serialize · normalizeWaterfallTier ตัดทิ้งตอน save)
                graphTiers: Array.isArray(node.data.graphTiers)
                    ? node.data.graphTiers.map(t => ({ ...normalizeWaterfallTier(t), _key: t?._key || this.generateGuid() }))
                    : [],
                // Products properties
                productGroupList: node.data.productGroupList || [],
                productTopNChunks: node.data.productTopNChunks || 5,
                productCosineSimilarity: node.data.productCosineSimilarity || 0.4,
                // Knowledge API properties
                knowledgeApiUrl: node.data.knowledgeApiUrl || '',
                knowledgeHttpMethod: node.data.knowledgeHttpMethod || 'GET',
                listHeaders: node.data.listHeaders || [
                    {
                        key: '',
                        value: '',
                    },
                ],
                listParams: node.data.listParams || [],
                knowledgeRequestBody: this.methodAllowsRequestBody(node.data.knowledgeHttpMethod || 'GET') ? (node.data.knowledgeRequestBody || '') : '',
                knowledgeModelJsonValidator: node.data.knowledgeModelJsonValidator || null,
                responseDataPath: node.data.responseDataPath || '',
                // Knowledge API Token properties
                enableToken: node.data.enableToken || false,
                tokenAuthMode: node.data.tokenAuthMode || 'new',
                tokenName: node.data.tokenName || '',
                useExistingTokenName: node.data.useExistingTokenName || '',
                tokenUrl: node.data.tokenUrl || '',
                tokenHttpMethod: node.data.tokenHttpMethod || 'POST',
                tokenRequestBody: this.methodAllowsRequestBody(node.data.tokenHttpMethod || 'POST') ? (node.data.tokenRequestBody || '') : '',
                tokenHeaders: node.data.tokenHeaders || [{ key: '', value: '' }],
                tokenParams: node.data.tokenParams || [],
                tokenResponsePath: node.data.tokenResponsePath || 'access_token',
                // SharePoint properties
                sharepoint: {
                    siteUrl: node.data.sharepoint?.siteUrl || '',
                    tenantId: node.data.sharepoint?.tenantId || '',
                    clientId: node.data.sharepoint?.clientId || '',
                    clientSecret: node.data.sharepoint?.clientSecret || '',
                    folderPath: node.data.sharepoint?.folderPath || '',
                    filename: node.data.sharepoint?.filename || ''
                },
                // Email properties
                emailProvider: node.data.emailProvider || 'mailgun',
                toEmail: node.data.toEmail || '',
                ccEmails: node.data.ccEmails || [],
                bccEmails: node.data.bccEmails || [],
                subject: node.data.subject || '',
                emailTemplate: node.data.emailTemplate || 'custom',
                emailBody: node.data.emailBody || '',
                isHtml: node.data.isHtml !== undefined ? node.data.isHtml : false,
                useCentralConfig: node.data.useCentralConfig !== undefined ? node.data.useCentralConfig : true,
                mailConfig: this.createEmptyMailConfig(node.data.mailConfig),
                showMailConfigSecret: {
                    password: false,
                    smtpOAuth2TenantId: false,
                    smtpOAuth2ClientId: false,
                    smtpOAuth2ClientSecret: false,
                    mailgunApiKey: false,
                },
                // Merge properties
                mergeStrategy: node.data.mergeStrategy || 'concatenate',
                mergePrompt: node.data.mergePrompt || '',
                mergeSeparator: node.data.mergeSeparator || 'hr',
                mergeCustomSeparator: node.data.mergeCustomSeparator || '',
                mergeIncludeLabels: node.data.mergeIncludeLabels !== undefined ? node.data.mergeIncludeLabels : true,
                mergeLabelFormat: node.data.mergeLabelFormat || '',
                mergeOutputFormat: node.data.mergeOutputFormat || 'text',
                mergePromptUsage: node.data.mergePromptUsage || 'system',
                summaryModel: node.data.summaryModel || null,
                summaryPrompt: node.data.summaryPrompt || 'Summarize the following AI responses into a single coherent response:\n\n{responses}',
            };
            
            // Process smartIntent forbiddenWords to ensure empty item at the end (similar to AI model logic)
            if (node.data.type === 'smartIntent') {
                // Handle smartIntents array
                if (this.nodeInspector.smartIntents && this.nodeInspector.smartIntents.length > 0) {
                    this.nodeInspector.smartIntents.forEach(smartIntent => {
                        if (smartIntent.mainAction) {
                            if (smartIntent.mainAction.platform === undefined) {
                                smartIntent.mainAction.platform = null;
                            }
                            if (smartIntent.mainAction.apiVersion === undefined) {
                                smartIntent.mainAction.apiVersion = null;
                            }
                        }
                        if (smartIntent.mainAction && smartIntent.mainAction.forbiddenWords) {
                            const lastItem = smartIntent.mainAction.forbiddenWords[smartIntent.mainAction.forbiddenWords.length - 1];
                            if (lastItem !== '') {
                                smartIntent.mainAction.forbiddenWords.push('');
                            }
                        } else if (smartIntent.mainAction) {
                            smartIntent.mainAction.forbiddenWords = [''];
                        }
                    });
                }
                
                // Handle smartIntentFallback forbiddenWords
                if (this.nodeInspector.smartIntentFallback) {
                    if (this.nodeInspector.smartIntentFallback.platform === undefined) {
                        this.nodeInspector.smartIntentFallback.platform = null;
                    }
                    if (this.nodeInspector.smartIntentFallback.apiVersion === undefined) {
                        this.nodeInspector.smartIntentFallback.apiVersion = null;
                    }
                }
                if (this.nodeInspector.smartIntentFallback && this.nodeInspector.smartIntentFallback.forbiddenWords) {
                    const lastItem = this.nodeInspector.smartIntentFallback.forbiddenWords[this.nodeInspector.smartIntentFallback.forbiddenWords.length - 1];
                    if (lastItem !== '') {
                        this.nodeInspector.smartIntentFallback.forbiddenWords.push('');
                    }
                } else if (this.nodeInspector.smartIntentFallback) {
                    this.nodeInspector.smartIntentFallback.forbiddenWords = [''];
                }
            }
            
            if (node.data.type === 'model') {
                this.syncCurrentModelMetadataFromSelection(false);
            }
            
            if (node.data.type === 'intentAI') {
                this.syncCurrentModelMetadataFromSelection(true);
            }
            
            // Show inspector as floating panel instead of dialog
            // this.inspectorDialog = true;

            // Set initial expand state for document data selection if enabled
            if (node.data.type === 'documents' && this.nodeInspector.enableSpecificDataSelection) {
                this.documentDataExpanded = true;
            } else {
                this.documentDataExpanded = false;
            }

            // Check for validation warnings when node is selected
            this.checkNodeValidation(node);
        },
        validateSingleNode(node, context = {}) {
            let hasWarning = false;
            const edgeList = Array.isArray(context.edges) ? context.edges : this.tempEdge;
            const isInternal = context.isInternal === true;
            const nodeType = node?.data?.type;

            if (isInternal && (nodeType === 'flowInput' || nodeType === 'flowOutput')) {
                return false;
            }

            // Validation for messageInput node
            if (nodeType === 'messageInput') {
                const selectedModel = node.data.selectedModel;
                if (!selectedModel || node.data.selectedModel === undefined || (Array.isArray(selectedModel) && selectedModel.length === 0)) {
                    hasWarning = true;
                }
            }

            if (nodeType === 'model') {
                if (!node.data.model) {
                    hasWarning = true;
                }
            }

            if (nodeType === 'agent') {
                if (node.data.useCustomAIModel && !node.data.agentModel) {
                    hasWarning = true;
                }
            }

            if (nodeType === 'tool') {
                if (node.data.toolType === 'master' && !node.data.masterToolId) {
                    hasWarning = true;
                }
                if (node.data.toolType === 'api' && !node.data.endpoint) {
                    hasWarning = true;
                }
                if (node.data.toolType === 'text' && !node.data.textContent) {
                    hasWarning = true;
                }
            }

            if (nodeType === 'flowTool') {
                hasWarning = !this.validateInternalFlow(node.data.internalNodes || [], node.data.internalEdges || []);
            }

            if (nodeType === 'intent') {
                // เช็คว่า Intent มีการกำหนด fallback intent หรือไม่
                let fallbackEdges = edgeList.filter(edge => edge.source === node.id && edge.sourceHandle === 'intent-fallback');

                // เช็คว่า Intent มีการต่อไปยัง node intentAI หรือไม่ จาก output-bottom
                let intentAIEdges = edgeList.filter(edge => edge.source === node.id && edge.sourceHandle === 'output-bottom');

                if (fallbackEdges.length === 0 || intentAIEdges.length === 0) {
                    hasWarning = true; // Missing fallback intent or intentAI connection
                }
            }

            if (nodeType === 'smartIntent') {
                // เช็คว่า Smart Intent มีการกำหนด fallback intent หรือไม่
                let fallbackEdges = edgeList.filter(edge => edge.source === node.id && edge.sourceHandle === 'smart-intent-fallback');

                // เช็คว่า Smart Intent มีการต่อไปยัง node intentAI หรือไม่ จาก output-bottom
                let intentAIEdges = edgeList.filter(edge => edge.source === node.id && edge.sourceHandle === 'output-bottom');

                if (fallbackEdges.length === 0 || intentAIEdges.length === 0) {
                    hasWarning = true; // Missing fallback intent or intentAI connection
                }

                // Validate Smart Intent knowledge sources
                if (node.data.smartIntents && Array.isArray(node.data.smartIntents)) {
                    node.data.smartIntents.forEach(smartIntent => {
                        if (smartIntent.mainAction && smartIntent.mainAction.useAI) {
                            // Validate Documents
                            if (smartIntent.mainAction.enableDocuments) {
                                if (!smartIntent.mainAction.document || (Array.isArray(smartIntent.mainAction.document) && smartIntent.mainAction.document.length === 0)) {
                                    hasWarning = true;
                                }
                            }

                            // Validate Database Integration
                            if (smartIntent.mainAction.enableDatabaseIntegration) {
                                if (!smartIntent.mainAction.tableName) {
                                    hasWarning = true;
                                }
                            }

                            // Validate Products
                            if (smartIntent.mainAction.enableProducts) {
                                if (!smartIntent.mainAction.productGroupList || smartIntent.mainAction.productGroupList.length === 0) {
                                    hasWarning = true;
                                }
                            }

                            // Validate API Knowledge
                            if (smartIntent.mainAction.enableApiKnowledge) {
                                if (!smartIntent.mainAction.knowledgeApiUrl || smartIntent.mainAction.knowledgeApiUrl.trim() === '') {
                                    hasWarning = true;
                                }
                                // Validate Token config
                                if (smartIntent.mainAction.enableToken) {
                                    if (smartIntent.mainAction.tokenAuthMode === 'new') {
                                        if (!smartIntent.mainAction.tokenName || !smartIntent.mainAction.tokenUrl) {
                                            hasWarning = true;
                                        }
                                    } else if (smartIntent.mainAction.tokenAuthMode === 'existing') {
                                        if (!smartIntent.mainAction.useExistingTokenName) {
                                            hasWarning = true;
                                        }
                                    }
                                }
                            }
                        }
                    });
                }

                // Validate Smart Intent Fallback knowledge sources
                if (node.data.smartIntentFallback && node.data.smartIntentFallback.useAI) {
                    // Validate Documents in Fallback
                    if (node.data.smartIntentFallback.enableDocuments) {
                        if (!node.data.smartIntentFallback.document || (Array.isArray(node.data.smartIntentFallback.document) && node.data.smartIntentFallback.document.length === 0)) {
                            hasWarning = true;
                        }
                    }

                    // Validate Database Integration in Fallback
                    if (node.data.smartIntentFallback.enableDatabaseIntegration) {
                        if (!node.data.smartIntentFallback.tableName) {
                            hasWarning = true;
                        }
                    }

                    // Validate Products in Fallback
                    if (node.data.smartIntentFallback.enableProducts) {
                        if (!node.data.smartIntentFallback.productGroupList || node.data.smartIntentFallback.productGroupList.length === 0) {
                            hasWarning = true;
                        }
                    }

                    // Validate API Knowledge in Fallback
                    if (node.data.smartIntentFallback.enableApiKnowledge) {
                        if (!node.data.smartIntentFallback.knowledgeApiUrl || node.data.smartIntentFallback.knowledgeApiUrl.trim() === '') {
                            hasWarning = true;
                        }
                        // Validate Token config in Fallback
                        if (node.data.smartIntentFallback.enableToken) {
                            if (node.data.smartIntentFallback.tokenAuthMode === 'new') {
                                if (!node.data.smartIntentFallback.tokenName || !node.data.smartIntentFallback.tokenUrl) {
                                    hasWarning = true;
                                }
                            } else if (node.data.smartIntentFallback.tokenAuthMode === 'existing') {
                                if (!node.data.smartIntentFallback.useExistingTokenName) {
                                    hasWarning = true;
                                }
                            }
                        }
                    }
                }
            }

            if (nodeType === 'intentAI') {
                if (!node.data.intentaiModel) {
                    hasWarning = true;
                }
            }

            if (nodeType === 'queryRewriter') {
                if (!node.data.queryRewriterProvider || !node.data.queryRewriterModel) {
                    hasWarning = true;
                }
            }

            if (nodeType === 'advancedRouter') {
                const fallbackEdges = edgeList.filter(edge => edge.source === node.id && edge.sourceHandle === 'advrouter-fallback');
                const intentAIEdges = edgeList.filter(edge => edge.source === node.id && edge.sourceHandle === 'output-bottom');
                if (fallbackEdges.length === 0 || intentAIEdges.length === 0) {
                    hasWarning = true;
                }
                if (!Array.isArray(node.data.advancedRouterOutputs) || node.data.advancedRouterOutputs.length === 0) {
                    hasWarning = true;
                } else {
                    const allHaveName = node.data.advancedRouterOutputs.every(o => o && typeof o.intentName === 'string' && o.intentName.trim().length > 0);
                    if (!allHaveName) hasWarning = true;
                }
            }

            if (nodeType === 'jsonCondition') {
                const conditions = Array.isArray(node.data.conditions) ? node.data.conditions : [];
                if (conditions.length === 0) {
                    hasWarning = true;
                } else {
                    const hasIncompleteRule = conditions.some(condition =>
                        !condition ||
                        !condition.jsonPath || condition.jsonPath.trim() === '' ||
                        !condition.operator || condition.operator.trim() === ''
                    );
                    if (hasIncompleteRule) hasWarning = true;
                }
            }

            if (nodeType === 'apiTrigger') {
                // Check if API URL is provided
                if (!node.data.apiUrl || node.data.apiUrl.trim() === '' || node.data.apiUrl === undefined) {
                    hasWarning = true;
                }
            }

            if (nodeType === 'contactAgent') {
                if (!node.data.botDisableTime || node.data.botDisableTime == 0 || node.data.contactAgentMessage.trim() === '') {
                    hasWarning = true; // Invalid date format
                }
            }

            if (nodeType === 'documents') {
                // Check if document vector name is provided
                if (!node.data.document || (Array.isArray(node.data.document) && node.data.document.length === 0)) {
                    hasWarning = true;
                }
            }

            if (nodeType === 'databaseIntegration') {
                // Check if table name is provided
                if (!node.data.tableName) {
                    hasWarning = true;
                }
            }

            if (nodeType === 'knowledgeGraph') {
                // S15-P1 — ยังไม่เลือกกราฟ = warning badge (ไม่บล็อก Save — G2 / OQ-3)
                if (!Array.isArray(node.data.knowledgeGraphIds) || node.data.knowledgeGraphIds.filter(Boolean).length === 0) {
                    hasWarning = true;
                }
            }

            if (nodeType === 'nexaWaterfallSearch') {
                // S15-P1 — ต้องมี tier ที่เลือกกราฟแล้วอย่างน้อย 1 (tier ที่ graphId ว่างนับว่าไม่มี — TC-22)
                if (!Array.isArray(node.data.graphTiers) || !node.data.graphTiers.some(t => t && t.graphId)) {
                    hasWarning = true;
                }
            }

            if (nodeType === 'apiknowledge') {
                // Check if Knowledge API URL is provided
                if (!node.data.knowledgeApiUrl || node.data.knowledgeApiUrl.trim() === '' || node.data.knowledgeApiUrl === undefined) {
                    hasWarning = true;
                }
                // Check token config validity
                if (node.data.enableToken) {
                    if (node.data.tokenAuthMode === 'new') {
                        if (!node.data.tokenName || node.data.tokenName.trim() === '' ||
                            !node.data.tokenUrl || node.data.tokenUrl.trim() === '') {
                            hasWarning = true;
                        }
                    } else if (node.data.tokenAuthMode === 'existing') {
                        if (!node.data.useExistingTokenName || node.data.useExistingTokenName.trim() === '') {
                            hasWarning = true;
                        }
                    }
                }
            }

            if (nodeType === 'products') {
                // Check if product group list is provided
                if (!node.data.productGroupList || node.data.productGroupList.length === 0) {
                    hasWarning = true;
                }
            }

            if (nodeType === 'sharepoint') {
                // Check required fields for SharePoint node
                if (!node.data.sharepoint || 
                    !node.data.sharepoint.siteUrl || node.data.sharepoint.siteUrl.trim() === '' ||
                    !node.data.sharepoint.tenantId || node.data.sharepoint.tenantId.trim() === '' ||
                    !node.data.sharepoint.clientId || node.data.sharepoint.clientId.trim() === '' ||
                    !node.data.sharepoint.clientSecret || node.data.sharepoint.clientSecret.trim() === '' ||
                    !node.data.sharepoint.filename || node.data.sharepoint.filename.trim() === '') {
                    hasWarning = true;
                }
            }

            if (nodeType === 'email') {
                // Check required fields for Email node
                if (!node.data.toEmail || node.data.toEmail.trim() === '') {
                    hasWarning = true;
                } else {
                    // Validate email format
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(node.data.toEmail.trim())) {
                        hasWarning = true;
                    }
                }
                
                if (!node.data.subject || node.data.subject.trim() === '') {
                    hasWarning = true;
                }
                
                if (node.data.emailTemplate === 'custom' && (!node.data.emailBody || node.data.emailBody.trim() === '')) {
                    hasWarning = true;
                }

                if(!node.data.emailBody || node.data.emailBody.trim() === '' || node.data.emailBody.length > 10000) {
                    hasWarning = true;
                }
            }

            if (nodeType === 'merge') {
                // Check if merge node has at least 2 input connections
                const inputEdges = edgeList.filter(edge => edge.target === node.id && edge.targetHandle === 'input');
                if (inputEdges.length < 2) {
                    hasWarning = true;
                }
                
                // Check strategy-specific requirements
                if (node.data.mergeStrategy === 'summary') {
                    if (!node.data.summaryModel || !node.data.summaryPrompt) {
                        hasWarning = true;
                    }
                }
            }

            return hasWarning;
        },
        checkNodeValidation(node) {
            const hasWarning = this.validateSingleNode(node);

            this.$nextTick(() => {
                // Update node data directly if warning state changes
                if (node.data.showWarning !== hasWarning) {
                    const nodeIndex = this.tempNode.findIndex(n => n.id === node.id);
                    if (nodeIndex !== -1) {
                        this.tempNode[nodeIndex].data.showWarning = hasWarning;
                    }
                }
            });
        },
        updateNodeWarningState(collectionName, nodeId, hasWarning) {
            const nodeCollection = collectionName === 'internalFlowNodes' ? this.internalFlowNodes : this.tempNode;
            const nodeIndex = nodeCollection.findIndex(n => n.id === nodeId);
            if (nodeIndex === -1) return;
            if (nodeCollection[nodeIndex].data.showWarning === hasWarning) return;

            const updatedNode = {
                ...nodeCollection[nodeIndex],
                data: {
                    ...nodeCollection[nodeIndex].data,
                    showWarning: hasWarning,
                },
            };

            if (collectionName === 'internalFlowNodes') {
                this.internalFlowNodes = [
                    ...this.internalFlowNodes.slice(0, nodeIndex),
                    updatedNode,
                    ...this.internalFlowNodes.slice(nodeIndex + 1),
                ];

                if (this.selectedNode?.id === nodeId) this.selectedNode = updatedNode;
                if (this.selectedInternalNode?.id === nodeId) this.selectedInternalNode = updatedNode;
                if (this.nodeInspector?.id === nodeId || this.selectedNode?.id === nodeId) {
                    this.nodeInspector.showWarning = hasWarning;
                }
                return;
            }

            this.tempNode = [
                ...this.tempNode.slice(0, nodeIndex),
                updatedNode,
                ...this.tempNode.slice(nodeIndex + 1),
            ];
        },
        checkInternalNodeValidation(node) {
            if (!node) return;
            const hasWarning = this.validateSingleNode(node, {
                edges: this.internalFlowEdges,
                isInternal: true,
            });
            this.$nextTick(() => {
                this.updateNodeWarningState('internalFlowNodes', node.id, hasWarning);
            });
        },
        checkAffectedNodesValidation(nodeIds) {
            // Check validation only for specified nodes
            nodeIds.forEach(nodeId => {
                const node = this.tempNode.find(n => n.id === nodeId);
                if (!node) return;

                const hasWarning = this.validateSingleNode(node);

                // Update node data directly if warning state changes
                if (node.data.showWarning !== hasWarning) {
                    const nodeIndex = this.tempNode.findIndex(n => n.id === node.id);
                    if (nodeIndex !== -1) {
                        this.tempNode[nodeIndex].data.showWarning = hasWarning;
                    }
                }
            });
        },
        checkAffectedInternalNodesValidation(nodeIds) {
            nodeIds.forEach(nodeId => {
                const node = this.internalFlowNodes.find(n => n.id === nodeId);
                if (!node) return;

                const hasWarning = this.validateSingleNode(node, {
                    edges: this.internalFlowEdges,
                    isInternal: true,
                });

                this.updateNodeWarningState('internalFlowNodes', node.id, hasWarning);
            });
        },
        checkAllNodesValidation() {
            // Check validation for all nodes in the flow
            this.tempNode.forEach(node => {
                const hasWarning = this.validateSingleNode(node);

                // Update node data directly if warning state changes
                if (node.data.showWarning !== hasWarning) {
                    const nodeIndex = this.tempNode.findIndex(n => n.id === node.id);
                    if (nodeIndex !== -1) {
                        this.tempNode[nodeIndex].data.showWarning = hasWarning;
                    }
                }
            });
        },
        checkAllInternalNodesValidation() {
            this.internalFlowNodes.forEach(node => {
                const hasWarning = this.validateSingleNode(node, {
                    edges: this.internalFlowEdges,
                    isInternal: true,
                });

                this.updateNodeWarningState('internalFlowNodes', node.id, hasWarning);
            });
        },
        closeInspector() {
            this.selectedNode = null;
            this.inspectorExpanded = false;
            if (this.isEditingInternalNode) {
                this.selectedInternalNode = null;
                this.isEditingInternalNode = false;
            }
        },
        clearSelection() {
            // Clear all node selections
            this.tempNode.forEach(node => {
                node.selected = false;
            });
            this.selectedNodes = [];
            this.selectedNode = null;
        },
        methodAllowsRequestBody(method) {
            return ['POST', 'PUT', 'PATCH'].includes((method || '').toString().toUpperCase());
        },
        createDefaultKnowledgeConnections() {
            return {
                documents: { nodeId: null, description: '' },
                databaseIntegration: { nodeId: null, description: '' },
                knowledgeApi: { nodeId: null, description: '' },
                products: { nodeId: null, description: '' },
                sharepoint: { nodeId: null, description: '' },
                dynamicKnowledge: { nodeId: null, description: '' }
            };
        },
        getKnowledgeConnectionKey(type) {
            const map = {
                apiknowledge: 'knowledgeApi'
            };
            return map[type] || type;
        },
        getConnectedAgentTools(agentNode = this.selectedNode) {
            if (!agentNode || agentNode.data?.type !== 'agent') return [];

            const toolTypes = ['tool', 'flowTool'];

            return this.tempEdge
                .filter(edge => edge.source === agentNode.id && edge.sourceHandle === 'tools-bottom')
                .map(edge => this.tempNode.find(node => node.id === edge.target))
                .filter(node => node && toolTypes.includes(node.data?.type))
                .filter((node, index, nodes) => nodes.findIndex(item => item.id === node.id) === index);
        },
        getConnectedAgentToolCount(agentNode = this.selectedNode) {
            return this.getConnectedAgentTools(agentNode).length;
        },
        getConnectedAgentDecisionResources(agentNode = this.selectedNode) {
            if (!agentNode || agentNode.data?.type !== 'agent') return [];

            const decisionResourceTypes = ['tool', 'flowTool', 'documents', 'dynamicKnowledge', 'databaseIntegration', 'apiknowledge', 'products', 'sharepoint'];

            return this.tempEdge
                .filter(edge => edge.source === agentNode.id && edge.sourceHandle === 'tools-bottom')
                .map(edge => this.tempNode.find(node => node.id === edge.target))
                .filter(node => node && decisionResourceTypes.includes(node.data?.type))
                .filter((node, index, nodes) => nodes.findIndex(item => item.id === node.id) === index);
        },
        getConnectedAgentDecisionResourceCount(agentNode = this.selectedNode) {
            return this.getConnectedAgentDecisionResources(agentNode).length;
        },
        canEnableAgentToolDecision(agentNode = this.selectedNode) {
            return this.getConnectedAgentDecisionResourceCount(agentNode) > 1;
        },
        getAgentToolDecisionOwnerIdFromEdge(edge) {
            if (!edge || edge.sourceHandle !== 'tools-bottom') return null;

            const sourceNode = this.tempNode.find(node => node.id === edge.source);
            return sourceNode?.data?.type === 'agent' ? sourceNode.id : null;
        },
        syncAgentToolDecisionAvailability(agentNodeIds = []) {
            if (!Array.isArray(agentNodeIds) || agentNodeIds.length === 0) return;

            const uniqueAgentNodeIds = [...new Set(agentNodeIds.filter(Boolean))];
            if (uniqueAgentNodeIds.length === 0) return;

            let nextSelectedNode = this.selectedNode;
            let shouldSyncInspector = false;
            let hasUpdates = false;

            this.tempNode = this.tempNode.map(node => {
                if (!uniqueAgentNodeIds.includes(node.id) || node.data?.type !== 'agent' || !node.data?.enableAgentToolDecision) {
                    return node;
                }

                if (this.canEnableAgentToolDecision(node)) {
                    return node;
                }

                hasUpdates = true;
                const updatedNode = {
                    ...node,
                    data: {
                        ...node.data,
                        enableAgentToolDecision: false
                    }
                };

                if (nextSelectedNode?.id === node.id) {
                    nextSelectedNode = updatedNode;
                    shouldSyncInspector = true;
                }

                return updatedNode;
            });

            if (!hasUpdates) return;

            if (nextSelectedNode?.id === this.selectedNode?.id) {
                this.selectedNode = nextSelectedNode;
            }

            if (shouldSyncInspector && this.nodeInspector) {
                this.nodeInspector.enableAgentToolDecision = false;
            }
        },
        handleAgentToolDecisionToggle(value) {
            if (!this.selectedNode || this.selectedNode.data?.type !== 'agent') return;

            if (!value) {
                this.updateNodeProperty('enableAgentToolDecision', false);
                return;
            }

            if (!this.canEnableAgentToolDecision()) {
                this.nodeInspector.enableAgentToolDecision = false;
                this.updateNodeProperty('enableAgentToolDecision', false);
                this.showWarning(this.$t('_connectAtLeast2Tools'));
                return;
            }

            this.updateNodeProperty('enableAgentToolDecision', true);
        },
        getConnectedKnowledge() {
            if (!this.selectedNode || this.selectedNode.data?.type !== 'agent') return [];
            const knowledgeTypes = ['documents', 'dynamicKnowledge', 'databaseIntegration', 'apiknowledge', 'products', 'sharepoint'];
            const labels = {
                documents: 'Documents',
                dynamicKnowledge: 'Dynamic Knowledge',
                databaseIntegration: 'Database Integration',
                apiknowledge: 'API Knowledge',
                products: 'Products',
                sharepoint: 'SharePoint'
            };
            return this.tempEdge
                .filter(edge => edge.source === this.selectedNode.id && edge.sourceHandle === 'tools-bottom')
                .map(edge => this.tempNode.find(node => node.id === edge.target))
                .filter(node => node && knowledgeTypes.includes(node.data?.type))
                .map(node => ({
                    nodeId: node.id,
                    type: node.data.type,
                    label: labels[node.data.type] || node.data.label || node.data.type
                }));
        },
        getKnowledgeConnectionDescription(knowledge) {
            const key = this.getKnowledgeConnectionKey(knowledge.type);
            return this.nodeInspector.knowledgeConnections?.[key]?.description || '';
        },
        updateKnowledgeDescription(knowledge, description) {
            const key = this.getKnowledgeConnectionKey(knowledge.type);
            const connections = {
                ...this.createDefaultKnowledgeConnections(),
                ...(this.nodeInspector.knowledgeConnections || {})
            };
            connections[key] = { nodeId: knowledge.nodeId, description };
            this.updateNodeProperty('knowledgeConnections', connections);
        },
        getKnowledgePlaceholder(type) {
            const placeholders = {
                documents: 'Describe when this Agent should use these documents',
                dynamicKnowledge: 'Describe when this Agent should use this dynamic knowledge',
                databaseIntegration: 'Describe which database questions this Agent can answer',
                apiknowledge: 'Describe when this API knowledge is relevant',
                products: 'Describe product knowledge usage',
                sharepoint: 'Describe SharePoint file usage'
            };
            return placeholders[type] || 'Describe how this knowledge should be used';
        },
        addToolHeader() {
            this.nodeInspector.headers = [...(this.nodeInspector.headers || []), { key: '', value: '' }];
            this.syncToolHeaders();
        },
        removeToolHeader(index) {
            this.nodeInspector.headers = (this.nodeInspector.headers || []).filter((_, itemIndex) => itemIndex !== index);
            this.syncToolHeaders();
        },
        syncToolHeaders() {
            this.updateNodeProperty('headers', this.nodeInspector.headers || []);
        },
        // ── S15-P1 · Knowledge Graph / Waterfall Search node ─────────────────────────────
        /**
         * โหลดรายการกราฟให้ dropdown ของทั้ง 2 node (ลอก ModelManagement.LoadKnowledgeGraphsForPicker)
         * GraphRagGraph/GetAll คืน paging envelope { page, perPage, total, data: [...] } → ใช้ res.data.data (G8) ไม่ใช่ items
         * คืน Promise ที่ resolve เสมอ — โหลดล้มต้องไม่ทำ mounted() ล้ม และไม่เด้ง toast (TC-15)
         */
        loadKnowledgeGraphs() {
            return new Promise((resolve) => {
                const self = this;
                self.loadingKnowledgeGraphs = true;
                self.$call.GraphRagGetGraphs(
                    '',
                    (res) => {
                        self.loadingKnowledgeGraphs = false;
                        self.DDKnowledgeGraphs = (res && res.status === 0 && Array.isArray(res.data?.data)) ? res.data.data : [];
                        resolve();
                    },
                    () => {
                        self.loadingKnowledgeGraphs = false;
                        self.DDKnowledgeGraphs = [];
                        resolve();
                    }
                );
            });
        },
        /**
         * ช่องว่าง = ไม่ override (ผ่านเสมอ) · มีค่าต้องเป็นตัวเลขในช่วงที่ backend รับ
         * สำเนาจาก ModelManagement.kgRangeRule (ผูก this.$t จึงไม่ย้ายไป utils ในสไลซ์นี้ — spec :938)
         */
        kgRangeRule(labelKey, min, max) {
            return (value) => {
                if (value === null || value === undefined || value === '') return true;
                const num = Number(value);
                if (Number.isNaN(num) || num < min || num > max) {
                    return `${this.$t(labelKey)}: ${min} - ${max}`;
                }
                return true;
            };
        },
        /** '' / null → null · ที่เหลือ → number (v-text-field type=number คืน string) */
        kgNumberOrNull(value) {
            if (value === null || value === undefined || value === '') return null;
            const num = Number(value);
            return Number.isNaN(num) ? null : num;
        },
        /**
         * ช่องตัวเลขของ kgRetrievalConfig: แปลงเป็น Number ก่อน emit · ค่านอกช่วง "ไม่ emit เข้า node" (TC-11)
         * error text แสดงจาก :rules ของช่องอยู่แล้ว · node คงค่าที่ถูกต้องล่าสุด · ไม่บล็อก Save
         */
        updateKgRetrievalNumber(key, value) {
            const num = this.kgNumberOrNull(value);
            const range = KG_CONFIG_RANGES[key];
            if (num !== null && range && (num < range[0] || num > range[1])) return;
            this.updateNodeProperty(`kgRetrievalConfig.${key}`, num);
        },
        /** สำเนา tier ทุกตัว (object ใหม่ · array ใหม่) — updateNodeProperty ต้องได้ reference ใหม่ถึงจะ re-render + เข้า history */
        cloneWaterfallTiers() {
            return (Array.isArray(this.nodeInspector.graphTiers) ? this.nodeInspector.graphTiers : []).map(t => ({ ...t }));
        },
        addWaterfallTier() {
            if (this.isViewMode) return;
            const tiers = this.cloneWaterfallTiers();
            if (tiers.length >= WF_MAX_TIERS) return;
            tiers.push({ ...defaultWaterfallTier(), _key: this.generateGuid() });
            this.updateNodeProperty('graphTiers', tiers);
        },
        removeWaterfallTier(index) {
            if (this.isViewMode) return;
            const tiers = this.cloneWaterfallTiers();
            if (index < 0 || index >= tiers.length) return;
            tiers.splice(index, 1);
            this.updateNodeProperty('graphTiers', tiers);
        },
        moveWaterfallTierUp(index) {
            if (this.isViewMode) return;
            const tiers = this.cloneWaterfallTiers();
            if (index <= 0 || index >= tiers.length) return;
            [tiers[index - 1], tiers[index]] = [tiers[index], tiers[index - 1]];
            this.updateNodeProperty('graphTiers', tiers);
        },
        moveWaterfallTierDown(index) {
            if (this.isViewMode) return;
            const tiers = this.cloneWaterfallTiers();
            if (index < 0 || index >= tiers.length - 1) return;
            [tiers[index + 1], tiers[index]] = [tiers[index], tiers[index + 1]];
            this.updateNodeProperty('graphTiers', tiers);
        },
        /**
         * แก้ 1 field ของ tier — dotted path ลึก 2 ระดับ (graphTiers[i].x) updateNodeProperty ไม่รองรับ จึงส่ง array ทั้งก้อน
         * field ตัวเลข: แปลงเป็น Number · นอกช่วง = ไม่ emit (กติกาเดียวกับ updateKgRetrievalNumber)
         */
        updateWaterfallTierProperty(index, key, value) {
            if (this.isViewMode) return;
            const tiers = this.cloneWaterfallTiers();
            if (!tiers[index]) return;
            let next = value;
            const range = WF_TIER_NUMERIC_RANGES[key];
            if (range) {
                next = this.kgNumberOrNull(value);
                if (next !== null && (next < range[0] || next > range[1])) return;
            }
            tiers[index] = { ...tiers[index], [key]: next };
            this.updateNodeProperty('graphTiers', tiers);
        },
        // ── end S15-P1 ─────────────────────────────────────────────────────────────────
        getInternalProcessingNodeCount(nodes) {
            return (Array.isArray(nodes) ? nodes : []).filter(node => !['flowInput', 'flowOutput'].includes(node?.data?.type)).length;
        },
        isFlowToolValid(flowToolNode = null) {
            const data = flowToolNode?.data || this.nodeInspector;
            return this.validateInternalFlow(data.internalNodes || [], data.internalEdges || []);
        },
        openFlowToolEditor() {
            if (!this.selectedNode || this.selectedNode.data?.type !== 'flowTool') return;
            this.editingFlowToolNode = this.selectedNode;
            // Open dialog with empty canvas first — nodes/edges are loaded in onFlowToolDialogAfterEnter
            // after the dialog animation completes, so Vue Flow measures handle positions in a stable container
            this.internalFlowNodes = [];
            this.internalFlowEdges = [];
            this.flowToolEditorDialog = true;
        },
        onFlowToolDialogAfterEnter() {
            if (!this.editingFlowToolNode) return;
            const pendingEdges = this.normalizeInternalFlowEdges(JSON.parse(JSON.stringify(this.editingFlowToolNode.data.internalEdges || [])));
            // Strip cached dimensions so Vue Flow re-measures from DOM instead of using stale stored values
            this.internalFlowNodes = JSON.parse(JSON.stringify(this.editingFlowToolNode.data.internalNodes || [])).map(node => {
                this.applyNodeDefaults(node);
                delete node.width;
                delete node.height;
                delete node.measured;
                return node;
            });
            this.initializeFlowToolEditor();
            this.checkAllInternalNodesValidation();
            // Set edges after two animation frames so Vue Flow finishes measuring node dimensions first
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.internalFlowEdges = pendingEdges;
                });
            });
        },
        normalizeInternalFlowEdge(edge) {
            return {
                ...edge,
                sourceHandle: edge?.sourceHandle || 'output',
                targetHandle: edge?.targetHandle || 'input',
                type: edge?.type || 'flow-edge',
                markerEnd: edge?.markerEnd || MarkerType.ArrowClosed,
                animated: edge?.animated ?? false,
                showButton: edge?.showButton ?? false
            };
        },
        normalizeInternalFlowEdges(edges) {
            return (Array.isArray(edges) ? edges : []).map(edge => this.normalizeInternalFlowEdge(edge));
        },
        createScopedValidationNodes(nodes, isInternal = false) {
            return (Array.isArray(nodes) ? nodes : []).map(node => ({
                ...node,
                data: {
                    ...node.data,
                    type: isInternal
                        ? (node.data?.type === 'flowInput'
                            ? 'messageInput'
                            : node.data?.type === 'flowOutput'
                                ? 'response'
                                : node.data?.type)
                        : node.data?.type,
                },
            }));
        },
        validateConnectionForGraph(connection, { nodes, edges, isInternal = false } = {}) {
            const normalizedConnection = {
                ...connection,
                sourceHandle: connection?.sourceHandle || 'output',
                targetHandle: connection?.targetHandle || 'input',
            };

            const originalNodes = this.tempNode;
            const originalEdges = this.tempEdge;

            try {
                this.tempNode = this.createScopedValidationNodes(nodes, isInternal);
                this.tempEdge = Array.isArray(edges) ? edges : [];
                return this.validateConnection(normalizedConnection);
            } finally {
                this.tempNode = originalNodes;
                this.tempEdge = originalEdges;
            }
        },
        initializeFlowToolEditor() {
            const hasInput = this.internalFlowNodes.some(node => node.data?.type === 'flowInput');
            const hasOutput = this.internalFlowNodes.some(node => node.data?.type === 'flowOutput');
            if (!hasInput) {
                this.internalFlowNodes.unshift({
                    id: `flow-input-${this.generateGuid()}`,
                    type: 'flow-node',
                    position: { x: 100, y: 260 },
                    data: { type: 'flowInput', label: 'Flow Input', isFixed: true }
                });
            }
            if (!hasOutput) {
                this.internalFlowNodes.push({
                    id: `flow-output-${this.generateGuid()}`,
                    type: 'flow-node',
                    position: { x: 760, y: 260 },
                    data: { type: 'flowOutput', label: 'Flow Output', isFixed: true }
                });
            }
        },
        addInternalFlowNodeDirect(nodeType = 'model') {
            this.createInternalFlowNode({
                data: {
                    label: this.getNodeTypeLabel(nodeType),
                    type: nodeType,
                    selectedModel: [],
                    model: null
                }
            });
        },
        addInternalFlowNodeFromPanel(nodeData) {
            this.createInternalFlowNode(nodeData);
        },
        isInternalFlowNodeAllowed(nodeType) {
            return nodeType && !this.internalFlowExcludedTypes.includes(nodeType);
        },
        createInternalFlowNode(nodeData, position = null) {
            const nodeType = nodeData?.data?.type || nodeData?.type;
            if (!this.isInternalFlowNodeAllowed(nodeType)) {
                this.showWarning(this.$t('_nodeTypeNotInFlowTool'));
                return;
            }

            const fallbackPosition = {
                x: 360 + (this.getInternalProcessingNodeCount(this.internalFlowNodes) * 50),
                y: 240
            };
            const data = {
                ...(nodeData?.data || {}),
                label: nodeData?.data?.label || nodeData?.name || this.getNodeTypeLabel(nodeType),
                type: nodeType
            };
            const newNode = {
                id: this.generateGuid(),
                type: 'flow-node',
                position: position || fallbackPosition,
                data
            };

            this.applyNodeDefaults(newNode);
            this.internalFlowNodes = [...this.internalFlowNodes, newNode];
            this.selectedInternalNode = newNode;
            this.checkInternalNodeValidation(newNode);

            // Auto-open inspector for the new node (mirrors main flow addNewNode behavior)
            this.$nextTick(() => {
                if (!this.flowToolEditorDialog) return;
                const addedNode = this.internalFlowNodes.find(n => n.id === newNode.id);
                if (addedNode) {
                    this.isEditingInternalNode = true;
                    this.openNodeInspector(addedNode);
                }
            });
        },
        getInternalFlowDropPosition(event) {
            const vueFlowInstance = this.$refs.internalVueFlow;
            if (vueFlowInstance && typeof vueFlowInstance.screenToFlowPosition === 'function') {
                const flowPosition = vueFlowInstance.screenToFlowPosition({ x: event.clientX, y: event.clientY });
                return { x: flowPosition.x - 75, y: flowPosition.y - 25 };
            }

            const flowElement = vueFlowInstance?.$el?.querySelector('.vue-flow__viewport') || vueFlowInstance?.$el;
            if (flowElement) {
                const flowBounds = flowElement.getBoundingClientRect();
                const viewport = vueFlowInstance?.getViewport ? vueFlowInstance.getViewport() : { x: 0, y: 0, zoom: 1 };
                const relativeX = event.clientX - flowBounds.left;
                const relativeY = event.clientY - flowBounds.top;
                return {
                    x: (relativeX - viewport.x) / viewport.zoom - 75,
                    y: (relativeY - viewport.y) / viewport.zoom - 25
                };
            }

            return null;
        },
        onInternalFlowDrop(event) {
            event.preventDefault();
            if (this.isViewMode) return;

            const nodeData = event.dataTransfer.getData('application/reactflow');
            if (!nodeData) return;

            const nodeTemplate = JSON.parse(nodeData);
            this.createInternalFlowNode(nodeTemplate, this.getInternalFlowDropPosition(event));
        },
        onInternalFlowConnect(connection) {
            const normalizedConnection = {
                ...connection,
                sourceHandle: connection?.sourceHandle || 'output',
                targetHandle: connection?.targetHandle || 'input',
            };
            const sourceNode = this.internalFlowNodes.find(node => node.id === normalizedConnection.source);
            const targetNode = this.internalFlowNodes.find(node => node.id === normalizedConnection.target);
            if (!sourceNode || !targetNode || connection.source === connection.target) {
                this.showWarning(this.$t('_invalidInternalFlowConn'));
                return;
            }
            if (!this.isInternalFlowNodeAllowed(sourceNode.data?.type) || !this.isInternalFlowNodeAllowed(targetNode.data?.type)) {
                this.showWarning(this.$t('_nodeTypeNotInFlowTool'));
                return;
            }

            const validationResult = this.validateConnectionForGraph(normalizedConnection, {
                nodes: this.internalFlowNodes,
                edges: this.internalFlowEdges,
                isInternal: true,
            });
            if (!validationResult.isValid) {
                this.showWarning(validationResult.message);
                return;
            }

            const exists = this.internalFlowEdges.some(edge => edge.source === normalizedConnection.source && edge.target === normalizedConnection.target && edge.sourceHandle === normalizedConnection.sourceHandle && edge.targetHandle === normalizedConnection.targetHandle);
            if (exists) return;
            this.internalFlowEdges = [
                ...this.internalFlowEdges,
                this.normalizeInternalFlowEdge({
                    id: `e${normalizedConnection.source}-${normalizedConnection.target}-${this.generateGuid()}`,
                    source: normalizedConnection.source,
                    target: normalizedConnection.target,
                    sourceHandle: normalizedConnection.sourceHandle,
                    targetHandle: normalizedConnection.targetHandle,
                })
            ];

            this.$nextTick(() => {
                this.checkAffectedInternalNodesValidation([normalizedConnection.source, normalizedConnection.target]);
            });
        },
        onInternalNodeClick({ node }) {
            this.selectedInternalNode = node;
            this.isEditingInternalNode = true;
            this.openNodeInspector(node);
        },
        onInternalPaneClick() {
            if (this.isEditingInternalNode) {
                this.selectedInternalNode = null;
                this.isEditingInternalNode = false;
                this.selectedNode = null;
            }
        },
        handleDeleteInternalNode(nodeId) {
            const node = this.internalFlowNodes.find(item => item.id === nodeId);
            if (!node || node.data?.isFixed) return;
            this.internalFlowNodes = this.internalFlowNodes.filter(item => item.id !== nodeId);
            this.internalFlowEdges = this.internalFlowEdges.filter(edge => edge.source !== nodeId && edge.target !== nodeId);
            this.$nextTick(() => {
                this.checkAllInternalNodesValidation();
            });
        },
        onInternalEdgeMouseEnter({ edge }) {
            edge.style = { strokeWidth: 1.6 };
        },
        onInternalEdgeMouseLeave({ edge }) {
            edge.style = { strokeWidth: 1.5 };
        },
        handleInternalNodeDataUpdate(nodeId, newData) {
            const idx = this.internalFlowNodes.findIndex(n => n.id === nodeId);
            if (idx === -1) return;
            this.internalFlowNodes = [
                ...this.internalFlowNodes.slice(0, idx),
                { ...this.internalFlowNodes[idx], data: { ...this.internalFlowNodes[idx].data, ...newData } },
                ...this.internalFlowNodes.slice(idx + 1)
            ];
            this.$nextTick(() => {
                const updatedNode = this.internalFlowNodes.find(n => n.id === nodeId);
                this.checkInternalNodeValidation(updatedNode);
            });
        },
        handleInternalRemoveOutgoingEdge(edgeId) {
            this.internalFlowEdges = this.internalFlowEdges.filter(e => e.id !== edgeId);
            this.$nextTick(() => {
                this.checkAllInternalNodesValidation();
            });
        },
        saveFlowToolInternalFlow() {
            if (!this.editingFlowToolNode) return;
            const nodeIndex = this.tempNode.findIndex(node => node.id === this.editingFlowToolNode.id);
            if (nodeIndex === -1) return;
            const internalNodes = JSON.parse(JSON.stringify(this.internalFlowNodes));
            const internalEdges = this.normalizeInternalFlowEdges(JSON.parse(JSON.stringify(this.internalFlowEdges)));
            const showWarning = !this.validateInternalFlow(internalNodes, internalEdges);
            const updatedNode = {
                ...this.tempNode[nodeIndex],
                data: {
                    ...this.tempNode[nodeIndex].data,
                    internalNodes,
                    internalEdges,
                    internalNodeCount: this.getInternalProcessingNodeCount(internalNodes),
                    showWarning
                }
            };
            this.tempNode = [
                ...this.tempNode.slice(0, nodeIndex),
                updatedNode,
                ...this.tempNode.slice(nodeIndex + 1)
            ];
            this.selectedNode = updatedNode;
            this.nodeInspector.internalNodes = internalNodes;
            this.nodeInspector.internalEdges = internalEdges;
            this.nodeInspector.internalNodeCount = updatedNode.data.internalNodeCount;
            this.nodeInspector.showWarning = showWarning;
            this.saveStateToHistory('Edit Flow Tool Internal Flow');
            this.closeFlowToolEditor();
        },
        closeFlowToolEditor() {
            const toolNode = this.editingFlowToolNode;
            const wasEditing = this.isEditingInternalNode;
            this.flowToolEditorDialog = false;
            this.internalFlowNodes = [];
            this.internalFlowEdges = [];
            this.selectedInternalNode = null;
            this.isEditingInternalNode = false;
            this.editingFlowToolNode = null;
            // Restore selectedNode to the FlowTool node when canceling while an internal node was selected
            if (wasEditing && toolNode && this.selectedNode?.id !== toolNode.id) {
                this.selectedNode = this.tempNode.find(n => n.id === toolNode.id) || null;
                if (this.selectedNode) this.openNodeInspector(this.selectedNode);
            }
        },
        validateFlowToolNode(flowToolNode) {
            if (!flowToolNode || flowToolNode.data?.type !== 'flowTool') return false;
            const showWarning = !this.validateInternalFlow(flowToolNode.data.internalNodes || [], flowToolNode.data.internalEdges || []);
            flowToolNode.data.showWarning = showWarning;
            flowToolNode.data.internalNodeCount = this.getInternalProcessingNodeCount(flowToolNode.data.internalNodes || []);
            return showWarning;
        },
        validateInternalFlow(nodes, edges) {
            const nodeList = Array.isArray(nodes) ? nodes : [];
            const edgeList = this.normalizeInternalFlowEdges(Array.isArray(edges) ? edges : []);
            const inputNode = nodeList.find(node => node.data?.type === 'flowInput');
            const outputNode = nodeList.find(node => node.data?.type === 'flowOutput');
            if (!inputNode || !outputNode) return false;
            const visited = new Set();
            const queue = [inputNode.id];
            let hasPathToOutput = false;
            while (queue.length > 0) {
                const currentId = queue.shift();
                if (currentId === outputNode.id) {
                    hasPathToOutput = true;
                    break;
                }
                if (visited.has(currentId)) continue;
                visited.add(currentId);
                edgeList.filter(edge => edge.source === currentId).forEach(edge => queue.push(edge.target));
            }
            if (!hasPathToOutput) return false;

            return !nodeList.some(node => this.validateSingleNode(node, {
                edges: edgeList,
                isInternal: true,
            }));
        },
        updateAgentToolSelection(toolId) {
            const selectedTool = this.agentToolOptions.find(tool => tool.id === toolId);
            this.updateNodeProperty('masterToolId', toolId);
            this.updateNodeProperty('toolName', selectedTool ? selectedTool.name : '');
        },
        // Real-time update methods
        updateNodeProperty(property, value) {
            if (!this.selectedNode) return;

            if (property === 'llmTimeout' || property === 'intentaiLlmTimeout' || property === 'queryRewriterLlmTimeout') {
                value = this.normalizeTimeoutValue(value, 1, 3600);
            }

            const bodyPropertyToClear = property === 'knowledgeHttpMethod'
                ? 'knowledgeRequestBody'
                : property === 'tokenHttpMethod'
                    ? 'tokenRequestBody'
                    : null;

            // When editing a node inside the internal flow editor, update internalFlowNodes instead of tempNode
            if (this.isEditingInternalNode) {
                const idx = this.internalFlowNodes.findIndex(n => n.id === this.selectedNode.id);
                if (idx === -1) return;
                let updatedData = property.includes('.')
                    ? (() => { const [pk, ck] = property.split('.'); return { ...this.internalFlowNodes[idx].data, [pk]: { ...this.internalFlowNodes[idx].data[pk], [ck]: value } }; })()
                    : { ...this.internalFlowNodes[idx].data, [property]: value };

                if (bodyPropertyToClear && !this.methodAllowsRequestBody(value)) {
                    updatedData[bodyPropertyToClear] = '';
                }

                this.internalFlowNodes = [
                    ...this.internalFlowNodes.slice(0, idx),
                    { ...this.internalFlowNodes[idx], data: updatedData },
                    ...this.internalFlowNodes.slice(idx + 1),
                ];
                this.selectedNode = this.internalFlowNodes[idx];
                this.selectedInternalNode = this.internalFlowNodes[idx];
                if (property.includes('.')) {
                    const [pk, ck] = property.split('.');
                    if (!this.nodeInspector[pk]) this.nodeInspector[pk] = {};
                    this.nodeInspector[pk][ck] = value;
                } else {
                    this.nodeInspector[property] = value;
                }

                if (bodyPropertyToClear && !this.methodAllowsRequestBody(value)) {
                    this.nodeInspector[bodyPropertyToClear] = '';
                }

                this.$nextTick(() => {
                    this.applyDerivedPropertyUpdates(property, value);
                    const updatedInternalNode = this.internalFlowNodes.find(n => n.id === this.selectedNode.id);
                    this.checkInternalNodeValidation(updatedInternalNode);
                });
                return;
            }

            // Find the node in our tempNode array
            const nodeIndex = this.tempNode.findIndex(node => node.id === this.selectedNode.id);
            if (nodeIndex === -1) return;

            // Update the node data immediately
            let updatedData;
            
            // Handle nested properties (e.g., sharepoint.siteUrl)
            if (property.includes('.')) {
                const [parentKey, childKey] = property.split('.');
                updatedData = {
                    ...this.tempNode[nodeIndex].data,
                    [parentKey]: {
                        ...this.tempNode[nodeIndex].data[parentKey],
                        [childKey]: value
                    }
                };
            } else {
                updatedData = {
                    ...this.tempNode[nodeIndex].data,
                    [property]: value
                };
            }

            if (bodyPropertyToClear && !this.methodAllowsRequestBody(value)) {
                updatedData[bodyPropertyToClear] = '';
            }

            // Replace the entire node to ensure reactivity
            this.tempNode = [
                ...this.tempNode.slice(0, nodeIndex),
                {
                    ...this.tempNode[nodeIndex],
                    data: updatedData
                },
                ...this.tempNode.slice(nodeIndex + 1)
            ];

            // Update selectedNode reference
            this.selectedNode = this.tempNode[nodeIndex];

            // Update nodeInspector to keep it in sync
            if (property.includes('.')) {
                const [parentKey, childKey] = property.split('.');
                if (!this.nodeInspector[parentKey]) {
                    this.nodeInspector[parentKey] = {};
                }
                this.nodeInspector[parentKey][childKey] = value;
            } else {
                this.nodeInspector[property] = value;
            }

            if (bodyPropertyToClear && !this.methodAllowsRequestBody(value)) {
                this.nodeInspector[bodyPropertyToClear] = '';
            }

            this.$nextTick(() => {
                this.applyDerivedPropertyUpdates(property, value);
            });

            this.checkNodeValidation(this.tempNode[nodeIndex]);

            // Debounce history saving for property updates
            this.debouncedSavePropertyChange(property);
        },

        getReasoningColor(level) {
            switch (level) {
                case 'low': return 'blue-grey';
                case 'medium': return 'blue-darken-1';
                case 'high': return 'indigo-darken-2';
                default: return 'grey';
            }
        },

        // Debounced method to save property changes to history
        debouncedSavePropertyChange(property) {
            // Clear existing timer
            if (this.propertyUpdateTimer) {
                clearTimeout(this.propertyUpdateTimer);
            }
            
            // Set new timer to save after 1 second of inactivity
            this.propertyUpdateTimer = setTimeout(() => {
                if (!this.isPerformingUndoRedo) {
                    this.saveStateToHistory(`Update ${property}`);
                }
            }, 1000);
        },
        updateValidatedNodeProperty(property, value, minValue = null, maxValue = null) {
            // Validate the value
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue)) {
                return; // Don't update if not a valid number
            }

            // Apply min/max constraints
            if (minValue !== null && validatedValue < minValue) {
                validatedValue = minValue;
            }
            if (maxValue !== null && validatedValue > maxValue) {
                validatedValue = maxValue;
            }

            // Round cosine similarity values to 4 decimal places
            if (property === 'cosineSimilarity' || property === 'productCosineSimilarity') {
                validatedValue = Math.round(validatedValue * 10000) / 10000;
            }

            // Update the node property with validated value
            this.updateNodeProperty(property, validatedValue);

            // Update the nodeInspector to reflect the validated value
            this.nodeInspector[property] = validatedValue;
        },

        // Validation methods for blur events
        validateAndFixTopNChunks(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseInt(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue) || validatedValue < 1) {
                validatedValue = 1; // Default minimum value
            }

            // Update the property
            this.updateNodeProperty('topNChunks', validatedValue);
            this.nodeInspector.topNChunks = validatedValue;
        },

        validateAndFixCosineSimilarity(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number and apply constraints
            if (isNaN(validatedValue) || validatedValue < 0.0001) {
                validatedValue = 0.0001; // Default minimum value
            }
            if (validatedValue > 1) {
                validatedValue = 1; // Default maximum value
            }

            // Round to 4 decimal places
            validatedValue = Math.round(validatedValue * 10000) / 10000;

            // Update the property
            this.updateNodeProperty('cosineSimilarity', validatedValue);
            this.nodeInspector.cosineSimilarity = validatedValue;
        },

        validateAndFixProductTopNChunks(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseInt(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue) || validatedValue < 1) {
                validatedValue = 1; // Default minimum value
            }

            // Update the property
            this.updateNodeProperty('productTopNChunks', validatedValue);
            this.nodeInspector.productTopNChunks = validatedValue;
        },

        validateAndFixProductCosineSimilarity(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number and apply constraints
            if (isNaN(validatedValue) || validatedValue < 0.0001) {
                validatedValue = 0.0001; // Default minimum value
            }
            if (validatedValue > 1) {
                validatedValue = 1; // Default maximum value
            }

            // Round to 4 decimal places
            validatedValue = Math.round(validatedValue * 10000) / 10000;

            // Update the property
            this.updateNodeProperty('productCosineSimilarity', validatedValue);
            this.nodeInspector.productCosineSimilarity = validatedValue;
        },

        // Validation methods for Smart Intent fields
        validateAndFixSmartIntentTopNChunks(event, smartIntentIndex) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseInt(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue) || validatedValue < 1) {
                validatedValue = 1; // Default minimum value
            }

            // Update the nested property
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction.topNChunks = validatedValue;
            this.updateSmartIntentProperty(smartIntentIndex, 'topNChunks', validatedValue);
        },

        validateAndFixSmartIntentCosineSimilarity(event, smartIntentIndex) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number and apply constraints
            if (isNaN(validatedValue) || validatedValue < 0.0001) {
                validatedValue = 0.0001; // Default minimum value
            }
            if (validatedValue > 1) {
                validatedValue = 1; // Default maximum value
            }

            // Round to 4 decimal places
            validatedValue = Math.round(validatedValue * 10000) / 10000;

            // Update the nested property
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction.cosineSimilarity = validatedValue;
            this.updateSmartIntentProperty(smartIntentIndex, 'cosineSimilarity', validatedValue);
        },

        validateAndFixSmartIntentProductTopNChunks(event, smartIntentIndex) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseInt(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue) || validatedValue < 1) {
                validatedValue = 1; // Default minimum value
            }

            // Update the nested property
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction.productTopNChunks = validatedValue;
            this.updateSmartIntentProperty(smartIntentIndex, 'productTopNChunks', validatedValue);
        },

        validateAndFixSmartIntentProductCosineSimilarity(event, smartIntentIndex) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number and apply constraints
            if (isNaN(validatedValue) || validatedValue < 0.0001) {
                validatedValue = 0.0001; // Default minimum value
            }
            if (validatedValue > 1) {
                validatedValue = 1; // Default maximum value
            }

            // Round to 4 decimal places
            validatedValue = Math.round(validatedValue * 10000) / 10000;

            // Update the nested property
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction.productCosineSimilarity = validatedValue;
            this.updateSmartIntentProperty(smartIntentIndex, 'productCosineSimilarity', validatedValue);
        },

        // Validation methods for Smart Intent Fallback fields
        validateAndFixSmartIntentFallbackProductTopNChunks(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseInt(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue) || validatedValue < 1) {
                validatedValue = 1; // Default minimum value
            }

            // Update the nested property
            this.nodeInspector.smartIntentFallback.productTopNChunks = validatedValue;
            this.updateSmartIntentFallbackProperty('productTopNChunks', validatedValue);
        },

        validateAndFixSmartIntentFallbackProductCosineSimilarity(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number and apply constraints
            if (isNaN(validatedValue) || validatedValue < 0.0001) {
                validatedValue = 0.0001; // Default minimum value
            }
            if (validatedValue > 1) {
                validatedValue = 1; // Default maximum value
            }

            // Round to 4 decimal places
            validatedValue = Math.round(validatedValue * 10000) / 10000;

            // Update the nested property
            this.nodeInspector.smartIntentFallback.productCosineSimilarity = validatedValue;
            this.updateSmartIntentFallbackProperty('productCosineSimilarity', validatedValue);
        },

        // Additional validation methods for Smart Intent Fallback Documents fields
        validateAndFixSmartIntentFallbackTopNChunks(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseInt(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue) || validatedValue < 1) {
                validatedValue = 1; // Default minimum value
            }

            // Update the nested property
            this.nodeInspector.smartIntentFallback.topNChunks = validatedValue;
            this.updateSmartIntentFallbackProperty('topNChunks', validatedValue);
        },

        validateAndFixSmartIntentFallbackCosineSimilarity(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number and apply constraints
            if (isNaN(validatedValue) || validatedValue < 0.0001) {
                validatedValue = 0.0001; // Default minimum value
            }
            if (validatedValue > 1) {
                validatedValue = 1; // Default maximum value
            }

            // Round to 4 decimal places
            validatedValue = Math.round(validatedValue * 10000) / 10000;

            // Update the nested property
            this.nodeInspector.smartIntentFallback.cosineSimilarity = validatedValue;
            this.updateSmartIntentFallbackProperty('cosineSimilarity', validatedValue);
        },

        // Validation method specifically for Smart Intent properties
        updateValidatedSmartIntentProperty(smartIntentIndex, property, value, minValue = null, maxValue = null) {
            // Validate the value
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue)) {
                return; // Don't update if not a valid number
            }

            // Apply min/max constraints
            if (minValue !== null && validatedValue < minValue) {
                validatedValue = minValue;
            }
            if (maxValue !== null && validatedValue > maxValue) {
                validatedValue = maxValue;
            }

            // Round cosine similarity values to 4 decimal places
            if (property === 'cosineSimilarity' || property === 'productCosineSimilarity') {
                validatedValue = Math.round(validatedValue * 10000) / 10000;
            }

            // Update the smart intent property with validated value
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction[property] = validatedValue;
            
            // Call updateSmartIntentData to save the changes and trigger validation
            this.updateSmartIntentData();
        },

        // Validation method specifically for Smart Intent Fallback properties
        updateValidatedSmartIntentFallbackProperty(property, value, minValue = null, maxValue = null) {
            // Validate the value
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue)) {
                return; // Don't update if not a valid number
            }

            // Apply min/max constraints
            if (minValue !== null && validatedValue < minValue) {
                validatedValue = minValue;
            }
            if (maxValue !== null && validatedValue > maxValue) {
                validatedValue = maxValue;
            }

            // Round cosine similarity values to 4 decimal places
            if (property === 'cosineSimilarity' || property === 'productCosineSimilarity') {
                validatedValue = Math.round(validatedValue * 10000) / 10000;
            }

            // Update the smart intent fallback property with validated value
            this.nodeInspector.smartIntentFallback[property] = validatedValue;
            
            // Call updateSmartIntentFallbackData to save the changes and trigger validation
            this.updateSmartIntentFallbackData();
        },

        // Special validation for Bot Disable Time
        validateAndUpdateBotDisableTime(value) {
            // Allow empty value (bot remains active)
            if (!value || value === '') {
                this.updateNodeProperty('botDisableTime', '');
                this.nodeInspector.botDisableTime = '';
                return;
            }

            // Convert to number
            let numValue = parseFloat(value);

            // Check if it's a valid number
            if (isNaN(numValue)) {
                return; // Don't update if not a valid number
            }

            // Ensure minimum value is 1
            if (numValue < 1) {
                numValue = 1;
                // Show warning to user
                this.showWarning(this.$t('_botDisableMin1'));
            }

            // Update with validated value
            this.updateNodeProperty('botDisableTime', numValue);
            this.nodeInspector.botDisableTime = numValue;
        },
        updateIntentData() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'intent') return;

            this.updateNodeProperty('intents', this.nodeInspector.intents);
        },
        // API Trigger methods
        updateHeaderProperty(index, property, value) {
            if (!this.nodeInspector.listHeadersAPITrigger[index]) return;
            this.nodeInspector.listHeadersAPITrigger[index][property] = value;
            this.updateNodeProperty('listHeadersAPITrigger', this.nodeInspector.listHeadersAPITrigger);
        },
        updateValidationProperty(index, property, value) {
            if (!this.nodeInspector.listValidation[index]) return;
            this.nodeInspector.listValidation[index][property] = value;
            this.updateNodeProperty('listValidation', this.nodeInspector.listValidation);
        },
        addHeader() {
            if (!this.nodeInspector.listHeadersAPITrigger) {
                this.nodeInspector.listHeadersAPITrigger = [];
            }
            this.nodeInspector.listHeadersAPITrigger.push({
                key: '',
                value: '',
            });
            this.updateNodeProperty('listHeadersAPITrigger', this.nodeInspector.listHeadersAPITrigger);
        },
        addValidation() {
            if (!this.nodeInspector.listValidation) {
                this.nodeInspector.listValidation = [];
            }
            this.nodeInspector.listValidation.push({
                key: '',
                typeValidation: '',
                numberOfCharacter: '',
                regExp: '',
                promptMessage: '',
            });
            this.updateNodeProperty('listValidation', this.nodeInspector.listValidation);
        },
        deleteHeader(index) {
            if (this.nodeInspector.listHeadersAPITrigger && this.nodeInspector.listHeadersAPITrigger.length > 0) {
                this.nodeInspector.listHeadersAPITrigger.splice(index, 1);
                this.updateNodeProperty('listHeadersAPITrigger', this.nodeInspector.listHeadersAPITrigger);
            }
        },
        deleteValidation(index) {
            if (this.nodeInspector.listValidation && this.nodeInspector.listValidation.length > 0) {
                this.nodeInspector.listValidation.splice(index, 1);
                this.updateNodeProperty('listValidation', this.nodeInspector.listValidation);
            }
        },
        // Knowledge API Header methods
        updateKnowledgeAPIHeaderProperty(index, property, value) {
            if (!this.nodeInspector.listHeaders[index]) return;
            this.nodeInspector.listHeaders[index][property] = value;
            this.updateNodeProperty('listHeaders', this.nodeInspector.listHeaders);
        },
        addKnowledgeAPIHeader() {
            if (!this.nodeInspector.listHeaders) {
                this.nodeInspector.listHeaders = [];
            }
            this.nodeInspector.listHeaders.push({
                key: '',
                value: '',
            });
            this.updateNodeProperty('listHeaders', this.nodeInspector.listHeaders);
        },
        deleteKnowledgeAPIHeader(index) {
            if (this.nodeInspector.listHeaders && this.nodeInspector.listHeaders.length > 0) {
                this.nodeInspector.listHeaders.splice(index, 1);
                this.updateNodeProperty('listHeaders', this.nodeInspector.listHeaders);
            }
        },
        // Knowledge API Param (query string) methods
        updateKnowledgeAPIParamProperty(index, property, value) {
            if (!this.nodeInspector.listParams || !this.nodeInspector.listParams[index]) return;
            this.nodeInspector.listParams[index][property] = value;
            this.updateNodeProperty('listParams', this.nodeInspector.listParams);
        },
        addKnowledgeAPIParam() {
            if (!this.nodeInspector.listParams) {
                this.nodeInspector.listParams = [];
            }
            this.nodeInspector.listParams.push({ key: '', value: '' });
            this.updateNodeProperty('listParams', this.nodeInspector.listParams);
        },
        deleteKnowledgeAPIParam(index) {
            if (this.nodeInspector.listParams && this.nodeInspector.listParams.length > 0) {
                this.nodeInspector.listParams.splice(index, 1);
                this.updateNodeProperty('listParams', this.nodeInspector.listParams);
            }
        },
        // Token header management methods
        addTokenHeader() {
            if (!this.nodeInspector.tokenHeaders) {
                this.nodeInspector.tokenHeaders = [];
            }
            this.nodeInspector.tokenHeaders.push({ key: '', value: '' });
            this.updateNodeProperty('tokenHeaders', this.nodeInspector.tokenHeaders);
        },
        deleteTokenHeader(index) {
            if (this.nodeInspector.tokenHeaders && this.nodeInspector.tokenHeaders.length > 0) {
                this.nodeInspector.tokenHeaders.splice(index, 1);
                this.updateNodeProperty('tokenHeaders', this.nodeInspector.tokenHeaders);
            }
        },
        updateTokenHeaderProperty(index, prop, value) {
            if (this.nodeInspector.tokenHeaders && this.nodeInspector.tokenHeaders[index]) {
                this.nodeInspector.tokenHeaders[index][prop] = value;
                this.updateNodeProperty('tokenHeaders', this.nodeInspector.tokenHeaders);
            }
        },
        // Get category color
        getCategoryColor(category) {
            const colors = {
                'System': 'blue',
                'User': 'green',
                'Session': 'purple',
                'Input': 'orange',
                'Context': 'teal'
            };
            return colors[category] || 'grey';
        },
        // Utility methods for node type styling
        getNodeTypeColor(type) {
            return getFlowNodeTypeColor(type);
        },
        getNodeTypeIcon(type) {
            return getFlowNodeTypeIcon(type);
        },
        getNodeTypeLabel(type) {
            return getFlowNodeTypeLabel(type);
        },
        // Forbidden words management methods (ลอกมาจาก ModelManagement.vue)
        isEnterForbidden(evt, index) {
            let charCode = evt.which ? evt.which : evt.keyCode;
            if (charCode === 13) {
                evt.preventDefault();
            } else {
                this.checkNewForbiddenWord(index);
                return true;
            }
        },
        checkNewForbiddenWord(index) {
            // Initialize forbiddenWords array if it doesn't exist
            if (!this.nodeInspector.forbiddenWords) {
                this.nodeInspector.forbiddenWords = [''];
            }

            // Add new empty word if we're at the last index
            if (this.nodeInspector.forbiddenWords.length === index + 1) {
                this.nodeInspector.forbiddenWords.push('');
                this.updateNodeProperty('forbiddenWords', this.nodeInspector.forbiddenWords);
            }
            return true;
        },
        checkDeleteForbiddenWord(index, value) {
            if (!this.nodeInspector.forbiddenWords) return;
            if (value === '' && this.nodeInspector.forbiddenWords.length > 1) {
                this.nodeInspector.forbiddenWords.splice(index, 1);
                this.updateNodeProperty('forbiddenWords', this.nodeInspector.forbiddenWords);
            }
        },
        updateForbiddenWordProperty(index, value) {
            // Initialize forbiddenWords array if it doesn't exist
            if (!this.nodeInspector.forbiddenWords) {
                this.nodeInspector.forbiddenWords = [''];
            }

            // Ensure the array has enough elements
            while (this.nodeInspector.forbiddenWords.length <= index) {
                this.nodeInspector.forbiddenWords.push('');
            }

            this.nodeInspector.forbiddenWords[index] = value;
            this.updateNodeProperty('forbiddenWords', this.nodeInspector.forbiddenWords);
        },
        removeForbiddenWordDirect(index) {
            if (!this.nodeInspector.forbiddenWords || this.nodeInspector.forbiddenWords.length <= 1) return;
            this.nodeInspector.forbiddenWords.splice(index, 1);
            this.updateNodeProperty('forbiddenWords', this.nodeInspector.forbiddenWords);
        },
        copyToClipboard(text) {
            navigator.clipboard.writeText(text).catch(() => {});
        },
        // เก็บ methods เดิมไว้เผื่อใช้ที่อื่น
        updateForbiddenWord(index, value) {
            this.updateForbiddenWordProperty(index, value);
            this.checkDeleteForbiddenWord(index, value);
        },
        removeForbiddenWord(index) {
            this.removeForbiddenWordDirect(index);
        },
        
        // Smart Intent Forbidden words management methods
        updateSmartIntentForbiddenWord(smartIntentIndex, index, value) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction) {
                smartIntent.mainAction = {};
            }
            if (!smartIntent.mainAction.forbiddenWords) {
                smartIntent.mainAction.forbiddenWords = [''];
            }
            
            // Ensure the array has enough elements
            while (smartIntent.mainAction.forbiddenWords.length <= index) {
                smartIntent.mainAction.forbiddenWords.push('');
            }

            smartIntent.mainAction.forbiddenWords[index] = value;
            this.updateSmartIntentData();
        },
        
        removeSmartIntentForbiddenWord(smartIntentIndex, index) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction?.forbiddenWords || smartIntent.mainAction.forbiddenWords.length <= 1) return;
            smartIntent.mainAction.forbiddenWords.splice(index, 1);
            this.updateSmartIntentData();
        },
        
        isEnterSmartIntentForbidden(evt, smartIntentIndex, index) {
            let charCode = evt.which ? evt.which : evt.keyCode;
            if (charCode === 13) {
                evt.preventDefault();
            } else {
                this.checkNewSmartIntentForbiddenWord(smartIntentIndex, index);
                return true;
            }
        },
        
        checkNewSmartIntentForbiddenWord(smartIntentIndex, index) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction) {
                smartIntent.mainAction = {};
            }
            if (!smartIntent.mainAction.forbiddenWords) {
                smartIntent.mainAction.forbiddenWords = [''];
            }

            // Add new empty word if we're at the last index
            if (smartIntent.mainAction.forbiddenWords.length === index + 1) {
                smartIntent.mainAction.forbiddenWords.push('');
                this.updateSmartIntentData();
            }
            return true;
        },
        
        checkDeleteSmartIntentForbiddenWord(smartIntentIndex, index, value) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction?.forbiddenWords) return;
            if (value === '' && smartIntent.mainAction.forbiddenWords.length > 1) {
                smartIntent.mainAction.forbiddenWords.splice(index, 1);
                this.updateSmartIntentData();
            }
        },
        
        updateSmartIntentForbiddenWordProperty(smartIntentIndex, index, value) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction) {
                smartIntent.mainAction = {};
            }
            if (!smartIntent.mainAction.forbiddenWords) {
                smartIntent.mainAction.forbiddenWords = [''];
            }

            // Ensure the array has enough elements
            while (smartIntent.mainAction.forbiddenWords.length <= index) {
                smartIntent.mainAction.forbiddenWords.push('');
            }

            smartIntent.mainAction.forbiddenWords[index] = value;
            
            // Add new empty word if we're typing in the last field and it's not empty
            if (smartIntent.mainAction.forbiddenWords.length === index + 1 && value !== '') {
                smartIntent.mainAction.forbiddenWords.push('');
            }
            
            this.updateSmartIntentData();
        },
        
        removeSmartIntentForbiddenWordDirect(smartIntentIndex, index) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction?.forbiddenWords || smartIntent.mainAction.forbiddenWords.length <= 1) return;
            smartIntent.mainAction.forbiddenWords.splice(index, 1);
            this.updateSmartIntentData();
        },
        
        // Smart Intent Fallback Forbidden words management methods
        updateSmartIntentFallbackForbiddenWord(index, value) {
            if (!this.nodeInspector.smartIntentFallback.forbiddenWords) {
                this.nodeInspector.smartIntentFallback.forbiddenWords = [''];
            }
            
            // Ensure the array has enough elements
            while (this.nodeInspector.smartIntentFallback.forbiddenWords.length <= index) {
                this.nodeInspector.smartIntentFallback.forbiddenWords.push('');
            }

            this.nodeInspector.smartIntentFallback.forbiddenWords[index] = value;
            this.updateSmartIntentFallbackData();
        },
        
        removeSmartIntentFallbackForbiddenWord(index) {
            if (!this.nodeInspector.smartIntentFallback.forbiddenWords || this.nodeInspector.smartIntentFallback.forbiddenWords.length <= 1) return;
            this.nodeInspector.smartIntentFallback.forbiddenWords.splice(index, 1);
            this.updateSmartIntentFallbackData();
        },
        
        isEnterSmartIntentFallbackForbidden(evt, index) {
            let charCode = evt.which ? evt.which : evt.keyCode;
            if (charCode === 13) {
                evt.preventDefault();
            } else {
                this.checkNewSmartIntentFallbackForbiddenWord(index);
                return true;
            }
        },
        
        checkNewSmartIntentFallbackForbiddenWord(index) {
            if (!this.nodeInspector.smartIntentFallback.forbiddenWords) {
                this.nodeInspector.smartIntentFallback.forbiddenWords = [''];
            }

            // Add new empty word if we're at the last index
            if (this.nodeInspector.smartIntentFallback.forbiddenWords.length === index + 1) {
                this.nodeInspector.smartIntentFallback.forbiddenWords.push('');
            }
        },
        
        checkDeleteSmartIntentFallbackForbiddenWord(index, value) {
            if (!value && this.nodeInspector.smartIntentFallback.forbiddenWords && this.nodeInspector.smartIntentFallback.forbiddenWords.length > 1) {
                this.removeSmartIntentFallbackForbiddenWord(index);
            }
        },
        
        // IntentAI Forbidden words management methods
        isEnterIntentaiForbidden(evt, index) {
            let charCode = evt.which ? evt.which : evt.keyCode;
            if (charCode === 13) {
                evt.preventDefault();
            } else {
                this.checkNewIntentaiForbiddenWord(index);
                return true;
            }
        },
        checkNewIntentaiForbiddenWord(index) {
            // Initialize intentaiForbiddenWords array if it doesn't exist
            if (!this.nodeInspector.intentaiForbiddenWords) {
                this.nodeInspector.intentaiForbiddenWords = [''];
            }

            // Add new empty word if we're at the last index
            if (this.nodeInspector.intentaiForbiddenWords.length === index + 1) {
                this.nodeInspector.intentaiForbiddenWords.push('');
                this.updateNodeProperty('intentaiForbiddenWords', this.nodeInspector.intentaiForbiddenWords);
            }
            return true;
        },
        checkDeleteIntentaiForbiddenWord(index, value) {
            if (!this.nodeInspector.intentaiForbiddenWords) return;
            if (value === '' && this.nodeInspector.intentaiForbiddenWords.length > 1) {
                this.nodeInspector.intentaiForbiddenWords.splice(index, 1);
                this.updateNodeProperty('intentaiForbiddenWords', this.nodeInspector.intentaiForbiddenWords);
            }
        },
        updateIntentaiForbiddenWordProperty(index, value) {
            // Initialize intentaiForbiddenWords array if it doesn't exist
            if (!this.nodeInspector.intentaiForbiddenWords) {
                this.nodeInspector.intentaiForbiddenWords = [''];
            }

            // Ensure the array has enough elements
            while (this.nodeInspector.intentaiForbiddenWords.length <= index) {
                this.nodeInspector.intentaiForbiddenWords.push('');
            }

            this.nodeInspector.intentaiForbiddenWords[index] = value;
            this.updateNodeProperty('intentaiForbiddenWords', this.nodeInspector.intentaiForbiddenWords);
        },
        removeIntentaiForbiddenWordDirect(index) {
            if (!this.nodeInspector.intentaiForbiddenWords || this.nodeInspector.intentaiForbiddenWords.length <= 1) return;
            this.nodeInspector.intentaiForbiddenWords.splice(index, 1);
            this.updateNodeProperty('intentaiForbiddenWords', this.nodeInspector.intentaiForbiddenWords);
        },
        // Provider and Model management methods
        normalizeLookupValue(value) {
            return typeof value === 'string' ? value.trim().toLowerCase() : '';
        },
        matchesProvider(providerName, selectedProvider) {
            const normalizedProviderName = this.normalizeLookupValue(providerName);
            const normalizedSelectedProvider = this.normalizeLookupValue(selectedProvider);
            return normalizedProviderName !== '' && normalizedProviderName === normalizedSelectedProvider;
        },
        getModelOptionTitle(model) {
            if (!model) return '';
            if (model.modelDisplay) return model.modelDisplay;

            const modelName = model.modelName || '';
            const parts = modelName.split('|').filter(Boolean);
            return parts.length > 1 ? parts[parts.length - 1] : modelName;
        },
        findModelOption(modelName, platform = null, provider = null) {
            if (!modelName || !Array.isArray(this.listDDModel)) return null;

            const modelText = String(modelName);
            const modelTail = modelText.includes('|') ? modelText.split('|').filter(Boolean).slice(-1)[0] : modelText;
            const exactMatches = this.listDDModel.filter((model) => model.modelName === modelText);
            const matches = exactMatches.length > 0
                ? exactMatches
                : this.listDDModel.filter((model) => {
                    const optionModelName = model.modelName || '';
                    const optionTail = optionModelName.includes('|') ? optionModelName.split('|').filter(Boolean).slice(-1)[0] : optionModelName;
                    return model.modelDisplay === modelText || optionModelName === modelTail || optionTail === modelTail;
                });
            if (matches.length === 0) return null;

            const normalizedPlatform = this.normalizeLookupValue(platform);
            const normalizedProvider = this.normalizeLookupValue(provider);

            if (normalizedPlatform || normalizedProvider) {
                const contextualMatch = matches.find((model) => {
                    const platformMatches = !normalizedPlatform || this.normalizeLookupValue(model.platform) === normalizedPlatform;
                    const providerMatches = !normalizedProvider || this.normalizeLookupValue(model.provider) === normalizedProvider;
                    return platformMatches && providerMatches;
                });

                if (contextualMatch) return contextualMatch;
            }

            return matches[0];
        },
        resolveModelSelectionValue(modelValue, platform = null, provider = null) {
            if (!modelValue || !Array.isArray(this.listDDModel)) return modelValue || null;

            const exactMatch = this.findModelOption(modelValue, platform, provider);
            if (exactMatch) return exactMatch.modelName;

            const normalizedPlatform = this.normalizeLookupValue(platform);
            const normalizedProvider = this.normalizeLookupValue(provider);
            const candidates = this.listDDModel.filter((model) => {
                const platformMatches = !normalizedPlatform || this.normalizeLookupValue(model.platform) === normalizedPlatform;
                const providerMatches = !normalizedProvider || this.normalizeLookupValue(model.provider) === normalizedProvider;
                return platformMatches && providerMatches;
            });

            const modelText = String(modelValue);
            const modelTail = modelText.includes('|') ? modelText.split('|').filter(Boolean).slice(-1)[0] : modelText;
            const displayMatch = candidates.find((model) => model.modelDisplay === modelTail || model.modelName === modelTail || model.modelName?.endsWith(`|${modelTail}`));

            return displayMatch?.modelName || modelValue;
        },
        setSelectedNodeDerivedProperty(property, value) {
            if (!this.selectedNode) return;

            this.nodeInspector[property] = value;

            if (this.isEditingInternalNode) {
                const idx = this.internalFlowNodes.findIndex(n => n.id === this.selectedNode.id);
                if (idx === -1) return;
                this.internalFlowNodes[idx].data[property] = value;
                this.selectedNode.data[property] = value;
                return;
            }

            const nodeIndex = this.tempNode.findIndex((node) => node.id === this.selectedNode.id);
            if (nodeIndex === -1) return;

            this.selectedNode.data[property] = value;
            this.tempNode[nodeIndex].data[property] = value;
        },
        applyDerivedPropertyUpdates(property, value) {
            if (property === 'model') {
                const selectedModel = this.findModelOption(value, this.nodeInspector.platform, this.nodeInspector.provider);
                if (selectedModel) {
                    this.setSelectedNodeDerivedProperty('platform', selectedModel.platform || null);
                    this.setSelectedNodeDerivedProperty('provider', selectedModel.provider || null);
                    this.setSelectedNodeDerivedProperty('apiVersion', selectedModel.apiVersion || null);
                } else if (!value) {
                    this.setSelectedNodeDerivedProperty('platform', null);
                    this.setSelectedNodeDerivedProperty('provider', null);
                    this.setSelectedNodeDerivedProperty('apiVersion', null);
                }

                if (!(selectedModel && selectedModel.useReasoningEffort) && this.nodeInspector.reasoningEffort) {
                    this.updateNodeProperty('reasoningEffort', null);
                }

                return;
            }

            if (property === 'intentaiModel') {
                const selectedModel = this.findModelOption(value, this.nodeInspector.intentaiPlatform, this.nodeInspector.intentaiProvider);
                if (selectedModel) {
                    this.setSelectedNodeDerivedProperty('intentaiPlatform', selectedModel.platform || null);
                    this.setSelectedNodeDerivedProperty('intentaiProvider', selectedModel.provider || null);
                    this.setSelectedNodeDerivedProperty('intentAiModelApiVersion', selectedModel.apiVersion || null);
                } else if (!value) {
                    this.setSelectedNodeDerivedProperty('intentaiPlatform', null);
                    this.setSelectedNodeDerivedProperty('intentaiProvider', null);
                    this.setSelectedNodeDerivedProperty('intentAiModelApiVersion', null);
                }

                if (!(selectedModel && selectedModel.useReasoningEffort) && this.nodeInspector.intentaiReasoningEffort) {
                    this.updateNodeProperty('intentaiReasoningEffort', null);
                }
            }
        },
        syncCurrentModelMetadataFromSelection(isIntentAI = false) {
            const modelProperty = isIntentAI ? 'intentaiModel' : 'model';
            const platformProperty = isIntentAI ? 'intentaiPlatform' : 'platform';
            const providerProperty = isIntentAI ? 'intentaiProvider' : 'provider';
            const apiVersionProperty = isIntentAI ? 'intentAiModelApiVersion' : 'apiVersion';
            const selectedModel = this.findModelOption(this.nodeInspector?.[modelProperty], this.nodeInspector?.[platformProperty], this.nodeInspector?.[providerProperty]);

            if (!selectedModel) return;

            this.setSelectedNodeDerivedProperty(platformProperty, selectedModel.platform || null);
            this.setSelectedNodeDerivedProperty(providerProperty, selectedModel.provider || null);
            this.setSelectedNodeDerivedProperty(apiVersionProperty, selectedModel.apiVersion || null);
        },
        applyLoadedModelSelection(modelConfig, isIntentAI = false) {
            const modelValue = this.resolveModelSelectionValue(modelConfig?.model, modelConfig?.platform, modelConfig?.provider);
            const selectedModel = this.findModelOption(modelValue, modelConfig?.platform, modelConfig?.provider);
            const modelProperty = isIntentAI ? 'intentaiModel' : 'model';
            const platformProperty = isIntentAI ? 'intentaiPlatform' : 'platform';
            const providerProperty = isIntentAI ? 'intentaiProvider' : 'provider';
            const apiVersionProperty = isIntentAI ? 'intentAiModelApiVersion' : 'apiVersion';

            this.updateNodeProperty(modelProperty, modelValue || null);
            this.updateNodeProperty(platformProperty, selectedModel?.platform || modelConfig?.platform || null);
            this.updateNodeProperty(providerProperty, selectedModel?.provider || modelConfig?.provider || null);
            this.updateNodeProperty(apiVersionProperty, selectedModel?.apiVersion || modelConfig?.apiVersion || null);
        },
        getFixedProviderByPlatform(platform) {
            const map = { Google: 'Google', Anthropic: 'Anthropic' };
            return map[platform] ?? null;
        },
        changePlatformForNode(value) {
            const fixed = this.getFixedProviderByPlatform(value);
            this.nodeInspector.model = null;
            this.updateNodeProperty('platform', value);
            this.updateNodeProperty('model', null);
            if (!value) {
                this.nodeInspector.provider = null;
                this.updateNodeProperty('provider', null);
                this.DDModelShow = [];
                return;
            }
            if (fixed) {
                this.nodeInspector.provider = fixed;
                this.updateNodeProperty('provider', fixed);
                this.changeProvider(fixed, value);
                return;
            }
            this.nodeInspector.provider = null;
            this.updateNodeProperty('provider', null);
            this.DDModelShow = [];
        },
        changePlatformForIntentAI(value) {
            const fixed = this.getFixedProviderByPlatform(value);
            this.nodeInspector.intentaiModel = null;
            this.updateNodeProperty('intentaiPlatform', value);
            this.updateNodeProperty('intentaiModel', null);
            if (!value) {
                this.nodeInspector.intentaiProvider = null;
                this.updateNodeProperty('intentaiProvider', null);
                this.DDModelShow = [];
                return;
            }
            if (fixed) {
                this.nodeInspector.intentaiProvider = fixed;
                this.updateNodeProperty('intentaiProvider', fixed);
                this.changeProvider(fixed, value);
                return;
            }
            this.nodeInspector.intentaiProvider = null;
            this.updateNodeProperty('intentaiProvider', null);
            this.DDModelShow = [];
        },
        changePlatformForSmartIntent(smartIntentIndex, value) {
            const fixed = this.getFixedProviderByPlatform(value);
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction.model = null;
            if (!value) {
                this.nodeInspector.smartIntents[smartIntentIndex].mainAction.provider = null;
                this.updateSmartIntentData();
                return;
            }
            if (fixed) {
                this.nodeInspector.smartIntents[smartIntentIndex].mainAction.provider = fixed;
            } else {
                this.nodeInspector.smartIntents[smartIntentIndex].mainAction.provider = null;
            }
            this.updateSmartIntentData();
        },
        changePlatformForSmartIntentFallback(value) {
            const fixed = this.getFixedProviderByPlatform(value);
            this.nodeInspector.smartIntentFallback.model = '';
            this.nodeInspector.smartIntentFallback.apiVersion = null;
            if (!value) {
                this.nodeInspector.smartIntentFallback.provider = null;
                this.updateSmartIntentFallbackData();
                return;
            }
            if (fixed) {
                this.nodeInspector.smartIntentFallback.provider = fixed;
            } else {
                this.nodeInspector.smartIntentFallback.provider = null;
            }
            this.updateSmartIntentFallbackData();
        },
        async changeProvider(value, platform) {
            return new Promise((resolve) => {
                console.log('Provider changed to:', value, 'Platform:', platform);
                if (!value) {
                    resolve();
                    return;
                }

                this.DDModelShow = this.listDDModel.filter((e) => {
                    if (e.provider !== value) return false;
                    if (platform) return e.platform === platform;
                    return true;
                });

                // Clear model when provider changes (only if not loading from model config)
                if (!this.loadingModelConfig) {
                    // Check if this is intentAI node
                    if (this.selectedNode && this.selectedNode.data.type === 'intentAI') {
                        if (this.nodeInspector.intentaiModel) {
                            this.nodeInspector.intentaiModel = null;
                            this.updateNodeProperty('intentaiModel', null);
                        }
                    } else {
                        // For other node types
                        if (this.nodeInspector.model) {
                            this.nodeInspector.model = null;
                            this.updateNodeProperty('model', null);
                        }
                    }
                }

                // Give Vue time to update the dropdown
                this.$nextTick(() => {
                    resolve();
                });
            });
        },
        toggleLockStatus() {
            if (this.$refs.vueFlow) {
                const flow = this.$refs.vueFlow;

                // Get current state - if nodesDraggable is undefined, default to true (unlocked)
                const isCurrentlyLocked = flow.nodesDraggable === false;
                const shouldLock = !isCurrentlyLocked; // Toggle the state
                // Apply new state
                flow.nodesDraggable = !shouldLock;
                flow.nodesConnectable = !shouldLock;
                flow.elementsSelectable = !shouldLock;
                flow.zoomOnScroll = !shouldLock;
                flow.zoomOnDoubleClick = !shouldLock;
                // flow.panOnDrag = !shouldLock ? [1, 2] : false;

                // Update interactiveControls to match lock status
                this.interactiveControls = !shouldLock;

                // Force Vue to re-render
                this.$forceUpdate();
            } else {
                console.warn('VueFlow ref not available');
            }
        },
        
        // ==================== Parallel Flow Helper Functions ====================
        
        /**
         * Check if a node can be a parallel parent
         * @param {Object} node - The node to check
         * @returns {boolean} - True if node can be parallel parent (AI Model or messageInput)
         */
        canBeParallelParent(node) {
            if (!node || !node.data) return false;
            const nodeType = node.data.type;
            return nodeType === 'model' || nodeType === 'messageInput' || nodeType === 'intent' || nodeType === 'smartIntent' || nodeType === 'advancedRouter';
        },
        
        /**
         * Update all outgoing edges from a source node to set isParallel flag
         * For Intent/Smart Intent: only set isParallel on edges from handles with 2+ connections
         * @param {string} sourceNodeId - The ID of the source node
         * @param {boolean} isParallel - The parallel state to set
         */
        updateOutgoingEdgesParallelFlag(sourceNodeId, isParallel) {
            const node = this.tempNode.find(n => n.id === sourceNodeId);
            if (!node) return;
            
            // For nodes with multiple output handles, need to check each handle separately
            if (node.data.type === 'intent' || node.data.type === 'smartIntent' || node.data.type === 'advancedRouter') {
                const fallbackHandle = node.data.type === 'intent'
                    ? 'intent-fallback'
                    : node.data.type === 'smartIntent'
                        ? 'smart-intent-fallback'
                        : 'advrouter-fallback';
                
                // Group edges by sourceHandle
                const edgesByHandle = {};
                this.tempEdge.forEach(edge => {
                    if (edge.source === sourceNodeId && edge.sourceHandle) {
                        const isMainAction = node.data.type === 'advancedRouter'
                            ? edge.sourceHandle.startsWith('advrouter-')
                            : edge.sourceHandle.endsWith('-main');
                        const isFallback = edge.sourceHandle === fallbackHandle;
                        
                        if (isMainAction || isFallback) {
                            const targetNode = this.tempNode.find(n => n.id === edge.target);
                            if (targetNode && (targetNode.data.type === 'model' || targetNode.data.type === 'merge')) {
                                if (!edgesByHandle[edge.sourceHandle]) {
                                    edgesByHandle[edge.sourceHandle] = [];
                                }
                                edgesByHandle[edge.sourceHandle].push(edge);
                            }
                        }
                    }
                });
                
                // Update isParallel flag based on each handle's connection count
                Object.entries(edgesByHandle).forEach(([handle, edges]) => {
                    const handleIsParallel = edges.length >= 2;
                    edges.forEach(edge => {
                        edge.data = edge.data || {};
                        edge.data.isParallel = handleIsParallel;
                    });
                });
            } else {
                // For messageInput and AI Model: update all edges from 'output' handle
                this.tempEdge.forEach(edge => {
                    if (edge.source === sourceNodeId && edge.sourceHandle === 'output') {
                        edge.data = edge.data || {};
                        edge.data.isParallel = isParallel;
                    }
                });
            }
        },
        
        /**
         * Check and update parallel mode for a node based on its connections
         * @param {string} nodeId - The ID of the node to check
         * @returns {boolean} - True if parallel mode was changed
         */
        checkAndUpdateParallelMode(nodeId) {
            const node = this.tempNode.find(n => n.id === nodeId);
            if (!node || !this.canBeParallelParent(node)) {
                return false;
            }
            
            // Count outgoing connections to AI Model or Merge nodes
            // For Intent, Smart Intent, and Advanced Router nodes, we need to check each output handle SEPARATELY
            // Parallel mode is only enabled if a SINGLE output handle has 2+ connections to AI Models
            let hasParallelOutput = false;
            
            if (node.data.type === 'intent') {
                // For intent, group edges by sourceHandle and check each handle separately
                const edgesByHandle = {};
                
                this.tempEdge.forEach(edge => {
                    if (edge.source === nodeId && edge.sourceHandle) {
                        const isMainAction = edge.sourceHandle.endsWith('-main');
                        const isFallback = edge.sourceHandle === 'intent-fallback';
                        
                        if (isMainAction || isFallback) {
                            const targetNode = this.tempNode.find(n => n.id === edge.target);
                            if (targetNode && (targetNode.data.type === 'model' || targetNode.data.type === 'merge')) {
                                if (!edgesByHandle[edge.sourceHandle]) {
                                    edgesByHandle[edge.sourceHandle] = [];
                                }
                                edgesByHandle[edge.sourceHandle].push(edge);
                            }
                        }
                    }
                });
                
                // Check if ANY single handle has 2+ connections (that's when it's truly parallel)
                hasParallelOutput = Object.values(edgesByHandle).some(edges => edges.length >= 2);
                
            } else if (node.data.type === 'smartIntent') {
                // For smart intent, group edges by sourceHandle and check each handle separately
                const edgesByHandle = {};
                
                this.tempEdge.forEach(edge => {
                    if (edge.source === nodeId && edge.sourceHandle) {
                        const isMainAction = edge.sourceHandle.endsWith('-main');
                        const isFallback = edge.sourceHandle === 'smart-intent-fallback';
                        
                        if (isMainAction || isFallback) {
                            const targetNode = this.tempNode.find(n => n.id === edge.target);
                            if (targetNode && (targetNode.data.type === 'model' || targetNode.data.type === 'merge')) {
                                if (!edgesByHandle[edge.sourceHandle]) {
                                    edgesByHandle[edge.sourceHandle] = [];
                                }
                                edgesByHandle[edge.sourceHandle].push(edge);
                            }
                        }
                    }
                });
                
                // Check if ANY single handle has 2+ connections (that's when it's truly parallel)
                hasParallelOutput = Object.values(edgesByHandle).some(edges => edges.length >= 2);
                
            } else if (node.data.type === 'advancedRouter') {
                // For Advanced Router, group edges by advrouter handle and check each handle separately
                const edgesByHandle = {};

                this.tempEdge.forEach(edge => {
                    if (edge.source === nodeId && edge.sourceHandle && edge.sourceHandle.startsWith('advrouter-')) {
                        const targetNode = this.tempNode.find(n => n.id === edge.target);
                        if (targetNode && (targetNode.data.type === 'model' || targetNode.data.type === 'merge')) {
                            if (!edgesByHandle[edge.sourceHandle]) {
                                edgesByHandle[edge.sourceHandle] = [];
                            }
                            edgesByHandle[edge.sourceHandle].push(edge);
                        }
                    }
                });

                // Check if ANY single handle has 2+ connections (that's when it's truly parallel)
                hasParallelOutput = Object.values(edgesByHandle).some(edges => edges.length >= 2);

            } else {
                // For messageInput and AI Model: check 'output' handle (single output, so count all)
                const outgoingToModelOrMerge = this.tempEdge.filter(edge => {
                    if (edge.source === nodeId && edge.sourceHandle === 'output') {
                        const targetNode = this.tempNode.find(n => n.id === edge.target);
                        return targetNode && (targetNode.data.type === 'model' || targetNode.data.type === 'merge');
                    }
                    return false;
                });
                hasParallelOutput = outgoingToModelOrMerge.length >= 2;
            }
            
            const nodeIndex = this.tempNode.findIndex(n => n.id === nodeId);
            let modeChanged = false;
            
            // Enable parallel mode if any single output has 2+ connections to model/merge
            if (hasParallelOutput) {
                // Only AI Model nodes get isParallel property (messageInput doesn't need it)
                if (node.data.type === 'model' && node.data.isParallel !== true) {
                    this.tempNode[nodeIndex].data.isParallel = true;
                    modeChanged = true;
                    
                    // Update inspector if this is the selected node
                    if (this.selectedNode && this.selectedNode.id === nodeId) {
                        this.nodeInspector.isParallel = true;
                    }
                }
                
                // Update all outgoing edges to have isParallel = true
                this.updateOutgoingEdgesParallelFlag(nodeId, true);
                
            } else {
                // Disable parallel mode if less than 2 connections
                if (node.data.type === 'model' && node.data.isParallel === true) {
                    this.tempNode[nodeIndex].data.isParallel = false;
                    modeChanged = true;
                    
                    // Update inspector if this is the selected node
                    if (this.selectedNode && this.selectedNode.id === nodeId) {
                        this.nodeInspector.isParallel = false;
                    }
                }
                
                // Update all outgoing edges to have isParallel = false
                this.updateOutgoingEdgesParallelFlag(nodeId, false);
            }
            
            return modeChanged;
        },
        
        // ==================== End Parallel Flow Helper Functions ====================
        
        onEdgeMouseEnter({ edge: hoveredEdge }) {
            hoveredEdge.style = { strokeWidth: 1.6 };
        },
        onEdgeMouseLeave({ edge: hoveredEdge }) {
            hoveredEdge.style = { strokeWidth: 1.5 };
        },

        // Handle clicking on the pane (background) to clear selection
        onPaneClick() {
            if (this.flowToolEditorDialog) return;
            // Clear node selection and close inspector
            this.selectedNodes = [];
            this.selectedNode = null;
            this.closeInspector();
        },

        // Node drag event handlers for undo/redo
        onNodeDragStart({ nodes }) {
            if (this.isDebugMode) return;
            this.isDraggingNodes = true;
            // Store initial positions of all selected nodes
            this.dragStartPositions = {};
            nodes.forEach(node => {
                this.dragStartPositions[node.id] = {
                    x: node.position.x,
                    y: node.position.y
                };
            });
        },

        onNodeDragStop({ nodes }) {
            if (this.isDebugMode) return;
            this.isDraggingNodes = false;
            
            // Check if any node actually moved
            let hasMovement = false;
            for (const node of nodes) {
                const startPos = this.dragStartPositions[node.id];
                if (startPos && (Math.abs(startPos.x - node.position.x) > 1 || Math.abs(startPos.y - node.position.y) > 1)) {
                    hasMovement = true;
                    break;
                }
            }
            
            // Only save to history if there was actual movement
            if (hasMovement && !this.isPerformingUndoRedo) {
                const nodeCount = nodes.length;
                this.saveStateToHistory(`Move ${nodeCount} Node${nodeCount > 1 ? 's' : ''}`);
            }
            
            // Clear stored positions
            this.dragStartPositions = {};
        },
        // Optimized node changes to reduce re-renders & expensive work during drag
        onNodesChange(changes) {
            if (this.isDebugMode) return;
            // Build index map once (O(n)) and reuse (O(1) lookups)
            const indexMap = new Map(this.tempNode.map((n, i) => [n.id, i]));

            let anySelectionChanged = false;
            const affectedAgentToolDecisionNodeIds = new Set();

            changes.forEach(change => {
                if (change.type === 'position' && change.position) {
                    const nodeIndex = indexMap.get(change.id);
                    if (nodeIndex !== undefined) {
                        const node = this.tempNode[nodeIndex];
                        // Mutate position directly (cheaper than replacing whole node)
                        node.position.x = change.position.x;
                        node.position.y = change.position.y;
                    }
                } else if (change.type === 'remove') {
                    this.tempEdge
                        .filter(edge => edge.source === change.id || edge.target === change.id)
                        .forEach(edge => {
                            const agentNodeId = this.getAgentToolDecisionOwnerIdFromEdge(edge);
                            if (agentNodeId) {
                                affectedAgentToolDecisionNodeIds.add(agentNodeId);
                            }
                        });

                    this.tempNode = this.tempNode.filter(node => node.id !== change.id);
                    this.tempEdge = this.tempEdge.filter(edge => edge.source !== change.id && edge.target !== change.id);
                } else if (change.type === 'select') {
                    const nodeIndex = indexMap.get(change.id);
                    if (nodeIndex !== undefined) {
                        this.tempNode[nodeIndex].selected = change.selected;
                        anySelectionChanged = true;
                    }
                }
            });

            if (anySelectionChanged) {
                this.selectedNodes = this.tempNode.filter(node => node.selected);
            }

            if (affectedAgentToolDecisionNodeIds.size > 0) {
                this.syncAgentToolDecisionAvailability(Array.from(affectedAgentToolDecisionNodeIds));
            }
        },
        onEdgesChange(changes) {
            if (this.isDebugMode) return;
            // Collect affected node IDs from edge changes
            const affectedNodeIds = new Set();
            const affectedAgentToolDecisionNodeIds = new Set();

            changes.forEach(change => {
                if (change.type === 'add') {
                    // Handle new edge
                    this.tempEdge.push(change.item);
                    affectedNodeIds.add(change.item.source);
                    affectedNodeIds.add(change.item.target);
                } else if (change.type === 'remove') {
                    // Find the edge before removing to get source/target
                    const edgeToRemove = this.tempEdge.find(edge => edge.id === change.id);
                    if (edgeToRemove) {
                        affectedNodeIds.add(edgeToRemove.source);
                        affectedNodeIds.add(edgeToRemove.target);
                        const agentNodeId = this.getAgentToolDecisionOwnerIdFromEdge(edgeToRemove);
                        if (agentNodeId) {
                            affectedAgentToolDecisionNodeIds.add(agentNodeId);
                        }
                        
                        // Check if target is Merge node and needs validation update
                        const targetNode = this.tempNode.find(node => node.id === edgeToRemove.target);
                        if (targetNode && targetNode.data.type === 'merge') {
                            affectedNodeIds.add(edgeToRemove.target);
                        }
                    }
                    // Handle edge removal (remove first, then check parallel mode)
                    this.tempEdge = this.tempEdge.filter(edge => edge.id !== change.id);
                    
                    // After removal, check and update parallel mode for source node
                    if (edgeToRemove) {
                        const sourceNode = this.tempNode.find(node => node.id === edgeToRemove.source);
                        if (sourceNode && this.canBeParallelParent(sourceNode)) {
                            this.checkAndUpdateParallelMode(sourceNode.id);
                        }
                    }
                }
            });

            if (affectedAgentToolDecisionNodeIds.size > 0) {
                this.syncAgentToolDecisionAvailability(Array.from(affectedAgentToolDecisionNodeIds));
            }

            // Check validation only for affected nodes
            if (affectedNodeIds.size > 0) {
                this.$nextTick(() => {
                    this.checkAffectedNodesValidation(Array.from(affectedNodeIds));
                });
            }
        },
        onConnect(connection) {
            if (this.isDebugMode) return;

            // Validation rules for connections
            const validationResult = this.validateConnection(connection);
            if (!validationResult.isValid) {
                this.showWarning(validationResult.message);
                return;
            }

            // Check if edge already exists to prevent duplicates
            const existingEdge = this.tempEdge.find(edge =>
                edge.source === connection.source &&
                edge.target === connection.target &&
                edge.sourceHandle === connection.sourceHandle &&
                edge.targetHandle === connection.targetHandle
            );

            if (existingEdge) {
                console.warn('Edge already exists between these nodes and handles');
                this.showWarning(this.$t('_connectionExists'));
                return;
            }

            // Create new edge
            // Get source and target nodes first to check isParallel
            const sourceNode = this.tempNode.find(node => node.id === connection.source);
            const targetNode = this.tempNode.find(node => node.id === connection.target);
            
            const newEdge = {
                id: `e${connection.source}-${connection.target}-${this.generateGuid()}`,
                source: connection.source,
                target: connection.target,
                sourceHandle: connection.sourceHandle,
                targetHandle: connection.targetHandle,
                type: 'flow-edge',
                markerEnd: MarkerType.ArrowClosed,
                animated: false,
                showButton: false,
                data: {
                    isParallel: sourceNode && sourceNode.data?.type === 'model' && sourceNode.data?.isParallel === true
                }
            };

            this.tempEdge.push(newEdge);
            
            // Check and update parallel mode for source node (if it's AI Model or messageInput)
            if (sourceNode && this.canBeParallelParent(sourceNode)) {
                this.checkAndUpdateParallelMode(sourceNode.id);
            }

            // Save state to history
            const sourceName = sourceNode ? sourceNode.data.label || 'Node' : 'Node';
            const targetName = targetNode ? targetNode.data.label || 'Node' : 'Node';
            this.saveStateToHistory(`Connect ${sourceName} → ${targetName}`);

            // Check validation only for source and target nodes
            this.$nextTick(() => {
                this.checkAffectedNodesValidation([connection.source, connection.target]);
            });
        },
        validateConnection(connection) {
            const sourceNode = this.tempNode.find(node => node.id === connection.source);
            const targetNode = this.tempNode.find(node => node.id === connection.target);

            if (!sourceNode || !targetNode) {
                return {
                    isValid: false,
                    message: 'Source or target node not found'
                };
            }

            // Rule 1: ห้ามเชื่อมต่อกับตัวเอง
            if (connection.source === connection.target) {
                return {
                    isValid: false,
                    message: 'Cannot connect node to itself'
                };
            }

            // Rule 2: ห้ามลากจาก input handle
            if (connection.sourceHandle === 'input') {
                return {
                    isValid: false,
                    message: 'Cannot drag from input handle. Input handles can only receive connections.'
                };
            }

            // Rule 3: ห้ามลากไป output handle
            if (connection.targetHandle && connection.targetHandle !== 'input') {
                return {
                    isValid: false,
                    message: 'Cannot connect to output handle. Output handles can only send connections.'
                };
            }

            // Rule 4: ตรวจสอบการเชื่อมต่อตาม node type
            const sourceNodeType = sourceNode.data.type;
            const targetNodeType = targetNode.data.type;

            // Rule 4a: Message Input node ต้องเป็น start node (ไม่รับ input)
            if (targetNodeType === 'messageInput') {
                return {
                    isValid: false,
                    message: 'Message Input node cannot receive connections as it is a start node'
                };
            }

            // Rule 4b: Response node ต้องเป็น end node (ไม่ส่ง output ต่อ)
            if (sourceNodeType === 'response') {
                return {
                    isValid: false,
                    message: 'Response node cannot send connections as it is an end node'
                };
            }

            // Rule 5: ตรวจสอบ handle types ที่เฉพาะเจาะจง
            const handleValidation = this.validateHandleTypes(sourceNode, targetNode, connection);
            if (!handleValidation.isValid) {
                return handleValidation;
            }

            // Rule 6: ห้ามสร้าง circular reference
            const circularValidation = this.checkCircularReference(connection);
            if (!circularValidation.isValid) {
                return circularValidation;
            }

            // Rule 7: จำกัดจำนวน input connections
            const inputLimitValidation = this.validateInputLimit(sourceNode, targetNode, connection);
            if (!inputLimitValidation.isValid) {
                return inputLimitValidation;
            }

            // Rule 8: จำกัดจำนวน output connections สำหรับ messageInput node
            const outputLimitValidation = this.validateOutputLimit(sourceNode, targetNode, connection);
            if (!outputLimitValidation.isValid) {
                return outputLimitValidation;
            }

            // Rule 9: ป้องกันการต่อ edge ไปยัง AI Model node ที่มี input จาก parallel parent แล้ว
            // (เพื่อป้องกัน case เช่น Intent A และ Fallback ต่อไป AI Model เดียวกัน)
            if (targetNode.data.type === 'model' && this.canBeParallelParent(sourceNode)) {
                const parallelParentValidation = this.validateNoExistingParallelParentInput(sourceNode, targetNode, connection);
                if (!parallelParentValidation.isValid) {
                    return parallelParentValidation;
                }
            }

            return { isValid: true };
        },
        // Validate specific handle types based on node types
        validateHandleTypes(sourceNode, targetNode, connection) {
            const sourceType = sourceNode.data.type;
            const targetType = targetNode.data.type;
            const sourceHandle = connection.sourceHandle;
            const targetHandle = connection.targetHandle;
            
            // Helper function to check if a node has any descendants (children, grandchildren, etc.)
            // EXCLUDING connections to Merge nodes (since Merge is the expected end of parallel branches)
            const hasDescendants = (nodeId, sourceHandle = null) => {
                const outgoingEdges = this.tempEdge.filter(edge => {
                    if (sourceHandle) {
                        return edge.source === nodeId && edge.sourceHandle === sourceHandle;
                    }
                    return edge.source === nodeId;
                });
                
                if (outgoingEdges.length === 0) return false;
                
                // Check if any outgoing edge goes to a non-Merge node
                // Connections to Merge nodes are allowed in parallel mode, so they don't count as "descendants"
                const hasNonMergeDescendants = outgoingEdges.some(edge => {
                    const targetNode = this.tempNode.find(n => n.id === edge.target);
                    // If target is Merge node, this doesn't count as a descendant
                    if (targetNode && targetNode.data.type === 'merge') {
                        return false;
                    }
                    // Any other connection counts as a descendant
                    return true;
                });
                
                return hasNonMergeDescendants;
            };

            if (sourceType === 'messageInput') {
                // **Check if messageInput is in parallel mode (connecting to multiple AI Models)**
                const existingOutputs = this.tempEdge.filter(edge =>
                    edge.source === connection.source && edge.sourceHandle === 'output'
                );
                
                // **NEW: Check if messageInput already has descendants - if yes, block parallel mode**
                if (existingOutputs.length > 0 && targetType === 'model') {
                    // Check if trying to create parallel mode (2nd connection to AI Model)
                    const existingModelConnections = existingOutputs.filter(edge => {
                        const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                        return existingTargetNode && existingTargetNode.data.type === 'model';
                    });
                    
                    if (existingModelConnections.length > 0) {
                        // Trying to add 2nd AI Model connection (parallel mode)
                        
                        // Check 1: If any existing AI Model child has descendants
                        const hasChildWithDescendants = existingModelConnections.some(edge => {
                            return hasDescendants(edge.target, 'output');
                        });
                        
                        if (hasChildWithDescendants) {
                            return {
                                isValid: false,
                                message: 'Cannot create parallel flow: Message Input node already has descendants through existing AI Model connections. Parallel mode is only allowed if AI Model children have no further connections.'
                            };
                        }
                        
                        // Check 2: If the target AI Model (new connection) has descendants
                        const targetHasDescendants = hasDescendants(connection.target, 'output');
                        if (targetHasDescendants) {
                            return {
                                isValid: false,
                                message: 'Cannot create parallel flow: The target AI Model already has descendants. Parallel mode is only allowed if AI Model children have no further connections.'
                            };
                        }
                    }
                }
                
                // Check if messageInput already connected to a parallel parent AI Model
                const hasConnectionToParallelParent = existingOutputs.some(edge => {
                    const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                    return existingTargetNode && existingTargetNode.data.type === 'model' && existingTargetNode.data.isParallel === true;
                });
                
                // If messageInput is already connected to a parallel parent, block any additional connections
                if (hasConnectionToParallelParent) {
                    return {
                        isValid: false,
                        message: 'Message Input node cannot have additional connections when connected to a parallel parent AI Model'
                    };
                }
                
                // Check if messageInput is already in parallel mode
                const isMessageInputInParallelMode = existingOutputs.length > 0 && existingOutputs.every(edge => {
                    const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                    return existingTargetNode && existingTargetNode.data.type === 'model';
                });

                // **Check if target is AI Model that's already in use**
                if (targetType === 'model') {
                    // If trying to connect to a parallel parent and already have other connections, block it
                    if (targetNode.data.isParallel === true && existingOutputs.length > 0) {
                        return {
                            isValid: false,
                            message: 'Message Input node cannot connect to a parallel parent AI Model when it already has other connections'
                        };
                    }
                    
                    // ONLY check if messageInput is in parallel mode
                    if (isMessageInputInParallelMode) {
                        // Check if target AI Model is already a parallel parent
                        if (targetNode.data.isParallel === true) {
                            return {
                                isValid: false,
                                message: 'Cannot connect to this AI Model node because it is already a parallel parent node'
                            };
                        }
                    }

                    // ALWAYS check if target AI Model is already a child of a parallel parent (ห้ามทุกกรณี)
                    const hasParallelParent = this.tempEdge.some(edge => {
                        if (edge.target === connection.target && edge.sourceHandle === 'output') {
                            // Check if edge is marked as parallel
                            if (edge.data?.isParallel === true) return true;
                            
                            // Check if parent node is a parallel parent
                            const parentNode = this.tempNode.find(n => n.id === edge.source);
                            if (parentNode && parentNode.data.type === 'model' && parentNode.data.isParallel === true) {
                                return true;
                            }
                            
                            // Check if parent is messageInput with multiple model outputs
                            if (parentNode && parentNode.data.type === 'messageInput') {
                                const parentOutputs = this.tempEdge.filter(e =>
                                    e.source === parentNode.id && e.sourceHandle === 'output'
                                );
                                if (parentOutputs.length >= 2) {
                                    const allToModels = parentOutputs.every(e => {
                                        const t = this.tempNode.find(n => n.id === e.target);
                                        return t && t.data.type === 'model';
                                    });
                                    if (allToModels) return true;
                                }
                            }
                        }
                        return false;
                    });

                    if (hasParallelParent) {
                        return {
                            isValid: false,
                            message: 'Cannot connect to this AI Model node because it is already a child of a parallel parent node'
                        };
                    }
                }

                // If messageInput is in parallel mode
                if (isMessageInputInParallelMode) {
                    // Parallel mode: only allow connection to model nodes
                    if (targetType !== 'model') {
                        return {
                            isValid: false,
                            message: 'Message Input node in parallel mode can only connect to AI Model nodes'
                        };
                    }
                    return { isValid: true };
                }
                
                // Check if messageInput has existing connection to non-AI Model nodes
                const hasConnectionToNonModel = existingOutputs.some(edge => {
                    const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                    return existingTargetNode && existingTargetNode.data.type !== 'model';
                });
                
                // If messageInput is already connected to a non-AI Model node, block any additional connections
                if (hasConnectionToNonModel) {
                    return {
                        isValid: false,
                        message: 'Message Input node cannot have additional connections when connected to Intent or Smart Intent nodes'
                    };
                }
                
                // If trying to connect to non-AI Model and already have other connections, block it
                if (targetType !== 'model' && existingOutputs.length > 0) {
                    return {
                        isValid: false,
                        message: 'Message Input node cannot connect to Intent or Smart Intent nodes when it already has other connections'
                    };
                }

                // Normal mode: allow connection to model, intent, smartIntent
                const allowedTargets = ['model', 'intent', 'smartIntent', 'advancedRouter', 'jsonCondition', 'orchestrator', 'agent', 'queryRewriter'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'Message Input node can only connect to AI Model nodes or Intent Handler nodes'
                    };
                }

            }

            if (sourceType === 'api') {
                const allowedTargets = ['model', 'intent', 'smartIntent', 'advancedRouter', 'jsonCondition', 'orchestrator', 'agent', 'queryRewriter'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'API Call Input node can only connect to AI Model nodes or Intent Handler nodes'
                    };
                }

            }

            // Intent node handle validation
            if (sourceType === 'intent') {
                if (sourceHandle == 'output-bottom') {
                    const allowedTargets = ['intentAI'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Source model of Intent node can only connect to Intent AI nodes'
                        };
                    }
                }
                // Check if this is a main action handle (format: ${intent.id}-main)
                else if (sourceHandle && sourceHandle.endsWith('-main')) {
                    // Main action output - similar logic to messageInput
                    // Get all edges from this specific main action handle
                    const existingOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    );
                    
                    // **NEW: Check if this main action already has descendants - if yes, block parallel mode**
                    if (existingOutputs.length > 0 && targetType === 'model') {
                        // Check if trying to create parallel mode (2nd connection to AI Model)
                        const existingModelConnections = existingOutputs.filter(edge => {
                            const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                            return existingTargetNode && existingTargetNode.data.type === 'model';
                        });
                        
                        if (existingModelConnections.length > 0) {
                            // Trying to add 2nd AI Model connection (parallel mode)
                            
                            // Check 1: If any existing AI Model child has descendants
                            const hasChildWithDescendants = existingModelConnections.some(edge => {
                                return hasDescendants(edge.target, 'output');
                            });
                            
                            if (hasChildWithDescendants) {
                                return {
                                    isValid: false,
                                    message: 'Cannot create parallel flow: Intent main action already has descendants through existing AI Model connections. Parallel mode is only allowed if AI Model children have no further connections.'
                                };
                            }
                            
                            // Check 2: If the target AI Model (new connection) has descendants
                            const targetHasDescendants = hasDescendants(connection.target, 'output');
                            if (targetHasDescendants) {
                                return {
                                    isValid: false,
                                    message: 'Cannot create parallel flow: The target AI Model already has descendants. Parallel mode is only allowed if AI Model children have no further connections.'
                                };
                            }
                        }
                    }
                    
                    // Check if this main action already connected to a parallel parent AI Model
                    const hasConnectionToParallelParent = existingOutputs.some(edge => {
                        const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                        return existingTargetNode && existingTargetNode.data.type === 'model' && existingTargetNode.data.isParallel === true;
                    });
                    
                    // If this main action is already connected to a parallel parent, block any additional connections
                    if (hasConnectionToParallelParent) {
                        return {
                            isValid: false,
                            message: 'Intent main action cannot have additional connections when connected to a parallel parent AI Model'
                        };
                    }
                    
                    // Check if this main action is already in parallel mode (2+ AI Models)
                    const isMainActionInParallelMode = existingOutputs.length > 0 && existingOutputs.every(edge => {
                        const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                        return existingTargetNode && existingTargetNode.data.type === 'model';
                    });

                    // **Check if target is AI Model that's already in use**
                    if (targetType === 'model') {
                        // If trying to connect to a parallel parent and already have other connections, block it
                        if (targetNode.data.isParallel === true && existingOutputs.length > 0) {
                            return {
                                isValid: false,
                                message: 'Intent main action cannot connect to a parallel parent AI Model when it already has other connections'
                            };
                        }
                        
                        // ONLY check if main action is in parallel mode
                        if (isMainActionInParallelMode) {
                            // Check if target AI Model is already a parallel parent
                            if (targetNode.data.isParallel === true) {
                                return {
                                    isValid: false,
                                    message: 'Cannot connect to this AI Model node because it is already a parallel parent node'
                                };
                            }
                        }

                        // ALWAYS check if target AI Model is already a child of a parallel parent (ห้ามทุกกรณี)
                        const hasParallelParent = this.tempEdge.some(edge => {
                            if (edge.target === connection.target && edge.sourceHandle === 'output') {
                                // Check if edge is marked as parallel
                                if (edge.data?.isParallel === true) return true;
                                
                                // Check if parent node is a parallel parent
                                const parentNode = this.tempNode.find(n => n.id === edge.source);
                                if (parentNode && parentNode.data.type === 'model' && parentNode.data.isParallel === true) {
                                    return true;
                                }
                                
                                // Check if parent is messageInput with multiple model outputs
                                if (parentNode && parentNode.data.type === 'messageInput') {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === 'output'
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                
                                // Check if parent is intent main action with multiple model outputs from that specific handle
                                if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                    // Check if this specific main action handle has multiple connections to AI Models
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                
                                // Check if parent is intent fallback with multiple model outputs
                                if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle === 'intent-fallback') {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === 'intent-fallback'
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                
                                // Check if parent is smart intent main action with multiple model outputs
                                if (parentNode && parentNode.data.type === 'smartIntent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                
                                // Check if parent is smart intent fallback with multiple model outputs
                                if (parentNode && parentNode.data.type === 'smartIntent' && edge.sourceHandle === 'smart-intent-fallback') {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === 'smart-intent-fallback'
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                            }
                            return false;
                        });

                        if (hasParallelParent) {
                            return {
                                isValid: false,
                                message: 'Cannot connect to this AI Model node because it is already a child of a parallel parent node'
                            };
                        }
                    }

                    // If main action is in parallel mode
                    if (isMainActionInParallelMode) {
                        // Parallel mode: only allow connection to model nodes
                        if (targetType !== 'model') {
                            return {
                                isValid: false,
                                message: 'Intent main action in parallel mode can only connect to AI Model nodes'
                            };
                        }
                        return { isValid: true };
                    }
                    
                    // Check if main action has existing connection to non-AI Model nodes
                    const hasConnectionToNonModel = existingOutputs.some(edge => {
                        const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                        return existingTargetNode && existingTargetNode.data.type !== 'model';
                    });
                    
                    // If main action is already connected to a non-AI Model node, block any additional connections
                    if (hasConnectionToNonModel) {
                        return {
                            isValid: false,
                            message: 'Intent main action cannot have additional connections when connected to non-AI Model nodes'
                        };
                    }
                    
                    // If trying to connect to non-AI Model and already have other connections, block it
                    if (targetType !== 'model' && existingOutputs.length > 0) {
                        return {
                            isValid: false,
                            message: 'Intent main action cannot connect to non-AI Model nodes when it already has other connections'
                        };
                    }

                    // Normal mode: allow connection to allowed targets
                    const allowedTargets = ['intent', 'model', 'response', 'apiTrigger', 'contactAgent', 'email', 'smartIntent', 'advancedRouter', 'jsonCondition'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Intent node can only connect to Intent, AI Model, API Trigger, Contact Agent nodes or Response'
                        };
                    }
                }
                // Check if this is a fallback handle (format: intent-fallback)
                else if (sourceHandle === 'intent-fallback') {
                    // Fallback output - similar logic to messageInput and main action
                    // Get all edges from this fallback handle
                    const existingOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === 'intent-fallback'
                    );
                    
                    // **NEW: Check if this fallback already has descendants - if yes, block parallel mode**
                    if (existingOutputs.length > 0 && targetType === 'model') {
                        // Check if trying to create parallel mode (2nd connection to AI Model)
                        const existingModelConnections = existingOutputs.filter(edge => {
                            const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                            return existingTargetNode && existingTargetNode.data.type === 'model';
                        });
                        
                        if (existingModelConnections.length > 0) {
                            // Trying to add 2nd AI Model connection (parallel mode)
                            
                            // Check 1: If any existing AI Model child has descendants
                            const hasChildWithDescendants = existingModelConnections.some(edge => {
                                return hasDescendants(edge.target, 'output');
                            });
                            
                            if (hasChildWithDescendants) {
                                return {
                                    isValid: false,
                                    message: 'Cannot create parallel flow: Intent fallback already has descendants through existing AI Model connections. Parallel mode is only allowed if AI Model children have no further connections.'
                                };
                            }
                            
                            // Check 2: If the target AI Model (new connection) has descendants
                            const targetHasDescendants = hasDescendants(connection.target, 'output');
                            if (targetHasDescendants) {
                                return {
                                    isValid: false,
                                    message: 'Cannot create parallel flow: The target AI Model already has descendants. Parallel mode is only allowed if AI Model children have no further connections.'
                                };
                            }
                        }
                    }
                    
                    // Check if this fallback already connected to a parallel parent AI Model
                    const hasConnectionToParallelParent = existingOutputs.some(edge => {
                        const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                        return existingTargetNode && existingTargetNode.data.type === 'model' && existingTargetNode.data.isParallel === true;
                    });
                    
                    // If this fallback is already connected to a parallel parent, block any additional connections
                    if (hasConnectionToParallelParent) {
                        return {
                            isValid: false,
                            message: 'Intent fallback cannot have additional connections when connected to a parallel parent AI Model'
                        };
                    }
                    
                    // Check if this fallback is already in parallel mode (2+ AI Models)
                    const isFallbackInParallelMode = existingOutputs.length > 0 && existingOutputs.every(edge => {
                        const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                        return existingTargetNode && existingTargetNode.data.type === 'model';
                    });

                    // **Check if target is AI Model that's already in use**
                    if (targetType === 'model') {
                        // If trying to connect to a parallel parent and already have other connections, block it
                        if (targetNode.data.isParallel === true && existingOutputs.length > 0) {
                            return {
                                isValid: false,
                                message: 'Intent fallback cannot connect to a parallel parent AI Model when it already has other connections'
                            };
                        }
                        
                        // ONLY check if fallback is in parallel mode
                        if (isFallbackInParallelMode) {
                            // Check if target AI Model is already a parallel parent
                            if (targetNode.data.isParallel === true) {
                                return {
                                    isValid: false,
                                    message: 'Cannot connect to this AI Model node because it is already a parallel parent node'
                                };
                            }
                        }

                        // ALWAYS check if target AI Model is already a child of a parallel parent (ห้ามทุกกรณี)
                        const hasParallelParent = this.tempEdge.some(edge => {
                            if (edge.target === connection.target && edge.sourceHandle === 'output') {
                                // Check if edge is marked as parallel
                                if (edge.data?.isParallel === true) return true;
                                
                                // Check if parent node is a parallel parent
                                const parentNode = this.tempNode.find(n => n.id === edge.source);
                                if (parentNode && parentNode.data.type === 'model' && parentNode.data.isParallel === true) {
                                    return true;
                                }
                                
                                // Check if parent is messageInput with multiple model outputs
                                if (parentNode && parentNode.data.type === 'messageInput') {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === 'output'
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                
                                // Check if parent is intent main action with multiple model outputs from that specific handle
                                if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                    // Check if this specific main action handle has multiple connections to AI Models
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                
                                // Check if parent is intent fallback with multiple model outputs
                                if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle === 'intent-fallback') {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === 'intent-fallback'
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                
                                // Check if parent is smart intent main action with multiple model outputs
                                if (parentNode && parentNode.data.type === 'smartIntent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                
                                // Check if parent is smart intent fallback with multiple model outputs
                                if (parentNode && parentNode.data.type === 'smartIntent' && edge.sourceHandle === 'smart-intent-fallback') {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === 'smart-intent-fallback'
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                            }
                            return false;
                        });

                        if (hasParallelParent) {
                            return {
                                isValid: false,
                                message: 'Cannot connect to this AI Model node because it is already a child of a parallel parent node'
                            };
                        }
                    }

                    // If fallback is in parallel mode
                    if (isFallbackInParallelMode) {
                        // Parallel mode: only allow connection to model nodes
                        if (targetType !== 'model') {
                            return {
                                isValid: false,
                                message: 'Intent fallback in parallel mode can only connect to AI Model nodes'
                            };
                        }
                        return { isValid: true };
                    }
                    
                    // Check if fallback has existing connection to non-AI Model nodes
                    const hasConnectionToNonModel = existingOutputs.some(edge => {
                        const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                        return existingTargetNode && existingTargetNode.data.type !== 'model';
                    });
                    
                    // If fallback is already connected to a non-AI Model node, block any additional connections
                    if (hasConnectionToNonModel) {
                        return {
                            isValid: false,
                            message: 'Intent fallback cannot have additional connections when connected to non-AI Model nodes'
                        };
                    }
                    
                    // If trying to connect to non-AI Model and already have other connections, block it
                    if (targetType !== 'model' && existingOutputs.length > 0) {
                        return {
                            isValid: false,
                            message: 'Intent fallback cannot connect to non-AI Model nodes when it already has other connections'
                        };
                    }

                    // Normal mode: allow connection to allowed targets
                    const allowedTargets = ['intent', 'model', 'response', 'apiTrigger', 'contactAgent', 'email', 'smartIntent', 'advancedRouter', 'jsonCondition'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Intent fallback can only connect to Intent, AI Model, API Trigger, Contact Agent nodes or Response'
                        };
                    }
                }
                // Parallel action handles or other handles - use default allowed targets
                else {
                    const allowedTargets = ['intent', 'model', 'response', 'apiTrigger', 'contactAgent', 'email', 'smartIntent', 'advancedRouter', 'jsonCondition'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Intent node can only connect to Intent, AI Model, API Trigger, Contact Agent nodes or Response'
                        };
                    }
                }
            }

            // Smart Intent node handle validation
            if (sourceType === 'smartIntent') {
                if (sourceHandle == 'output-bottom') {
                    const allowedTargets = ['intentAI'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Source model of Smart Intent node can only connect to Intent AI nodes'
                        };
                    }
                }
                // Check if this is a main action handle (format: ${smartIntent.id}-main)
                else if (sourceHandle && sourceHandle.endsWith('-main')) {
                    // Main action output - similar logic to Intent main action
                    // Get all edges from this specific main action handle
                    const existingOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    );
                    
                    // **NEW: Check if this main action already has descendants - if yes, block parallel mode**
                    if (existingOutputs.length > 0 && targetType === 'model') {
                        // Check if trying to create parallel mode (2nd connection to AI Model)
                        const existingModelConnections = existingOutputs.filter(edge => {
                            const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                            return existingTargetNode && existingTargetNode.data.type === 'model';
                        });
                        
                        if (existingModelConnections.length > 0) {
                            // Trying to add 2nd AI Model connection (parallel mode)
                            
                            // Check 1: If any existing AI Model child has descendants
                            const hasChildWithDescendants = existingModelConnections.some(edge => {
                                return hasDescendants(edge.target, 'output');
                            });
                            
                            if (hasChildWithDescendants) {
                                return {
                                    isValid: false,
                                    message: 'Cannot create parallel flow: Smart Intent main action already has descendants through existing AI Model connections. Parallel mode is only allowed if AI Model children have no further connections.'
                                };
                            }
                            
                            // Check 2: If the target AI Model (new connection) has descendants
                            const targetHasDescendants = hasDescendants(connection.target, 'output');
                            if (targetHasDescendants) {
                                return {
                                    isValid: false,
                                    message: 'Cannot create parallel flow: The target AI Model already has descendants. Parallel mode is only allowed if AI Model children have no further connections.'
                                };
                            }
                        }
                    }
                    
                    // Check if this main action already connected to a parallel parent AI Model
                    const hasConnectionToParallelParent = existingOutputs.some(edge => {
                        const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                        return existingTargetNode && existingTargetNode.data.type === 'model' && existingTargetNode.data.isParallel === true;
                    });
                    
                    // If this main action is already connected to a parallel parent, block any additional connections
                    if (hasConnectionToParallelParent) {
                        return {
                            isValid: false,
                            message: 'Smart Intent main action cannot have additional connections when connected to a parallel parent AI Model'
                        };
                    }
                    
                    // Check if this main action is already in parallel mode (2+ AI Models)
                    const isMainActionInParallelMode = existingOutputs.length > 0 && existingOutputs.every(edge => {
                        const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                        return existingTargetNode && existingTargetNode.data.type === 'model';
                    });

                    // **Check if target is AI Model that's already in use**
                    if (targetType === 'model') {
                        // If trying to connect to a parallel parent and already have other connections, block it
                        if (targetNode.data.isParallel === true && existingOutputs.length > 0) {
                            return {
                                isValid: false,
                                message: 'Smart Intent main action cannot connect to a parallel parent AI Model when it already has other connections'
                            };
                        }
                        
                        // ONLY check if main action is in parallel mode
                        if (isMainActionInParallelMode) {
                            // Check if target AI Model is already a parallel parent
                            if (targetNode.data.isParallel === true) {
                                return {
                                    isValid: false,
                                    message: 'Cannot connect to this AI Model node because it is already a parallel parent node'
                                };
                            }
                        }

                        // ALWAYS check if target AI Model is already a child of a parallel parent (ห้ามทุกกรณี)
                        const hasParallelParent = this.tempEdge.some(edge => {
                            if (edge.target === connection.target && edge.sourceHandle === 'output') {
                                // Check if edge is marked as parallel
                                if (edge.data?.isParallel === true) return true;
                                
                                // Check if parent node is a parallel parent
                                const parentNode = this.tempNode.find(n => n.id === edge.source);
                                if (parentNode && parentNode.data.type === 'model' && parentNode.data.isParallel === true) {
                                    return true;
                                }
                                
                                // Check if parent is messageInput with multiple model outputs
                                if (parentNode && parentNode.data.type === 'messageInput') {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === 'output'
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                
                                // Check if parent is intent main action with multiple model outputs from that specific handle
                                if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                    // Check if this specific main action handle has multiple connections to AI Models
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                
                                // Check if parent is intent fallback with multiple model outputs
                                if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle === 'intent-fallback') {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === 'intent-fallback'
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                
                                // Check if parent is smart intent main action with multiple model outputs from that specific handle
                                if (parentNode && parentNode.data.type === 'smartIntent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                    // Check if this specific main action handle has multiple connections to AI Models
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                            }
                            return false;
                        });

                        if (hasParallelParent) {
                            return {
                                isValid: false,
                                message: 'Cannot connect to this AI Model node because it is already a child of a parallel parent node'
                            };
                        }
                    }

                    // If main action is in parallel mode
                    if (isMainActionInParallelMode) {
                        // Parallel mode: only allow connection to model nodes
                        if (targetType !== 'model') {
                            return {
                                isValid: false,
                                message: 'Smart Intent main action in parallel mode can only connect to AI Model nodes'
                            };
                        }
                        return { isValid: true };
                    }
                    
                    // Check if main action has existing connection to non-AI Model nodes
                    const hasConnectionToNonModel = existingOutputs.some(edge => {
                        const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                        return existingTargetNode && existingTargetNode.data.type !== 'model';
                    });
                    
                    // If main action is already connected to a non-AI Model node, block any additional connections
                    if (hasConnectionToNonModel) {
                        return {
                            isValid: false,
                            message: 'Smart Intent main action cannot have additional connections when connected to non-AI Model nodes'
                        };
                    }
                    
                    // If trying to connect to non-AI Model and already have other connections, block it
                    if (targetType !== 'model' && existingOutputs.length > 0) {
                        return {
                            isValid: false,
                            message: 'Smart Intent main action cannot connect to non-AI Model nodes when it already has other connections'
                        };
                    }

                    // Normal mode: allow connection to allowed targets
                    const allowedTargets = ['intent', 'model', 'response', 'apiTrigger', 'contactAgent', 'email', 'smartIntent', 'advancedRouter', 'jsonCondition'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Smart Intent main action can only connect to Intent, AI Model, API Trigger, Contact Agent nodes or Response'
                        };
                    }
                }
                else if (sourceHandle === 'smart-intent-fallback') {
                    // Fallback output - similar logic to Intent fallback
                    // Get all edges from this fallback handle
                    const existingOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === 'smart-intent-fallback'
                    );
                    
                    // **NEW: Check if this fallback already has descendants - if yes, block parallel mode**
                    if (existingOutputs.length > 0 && targetType === 'model') {
                        // Check if trying to create parallel mode (2nd connection to AI Model)
                        const existingModelConnections = existingOutputs.filter(edge => {
                            const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                            return existingTargetNode && existingTargetNode.data.type === 'model';
                        });
                        
                        if (existingModelConnections.length > 0) {
                            // Trying to add 2nd AI Model connection (parallel mode)
                            
                            // Check 1: If any existing AI Model child has descendants
                            const hasChildWithDescendants = existingModelConnections.some(edge => {
                                return hasDescendants(edge.target, 'output');
                            });
                            
                            if (hasChildWithDescendants) {
                                return {
                                    isValid: false,
                                    message: 'Cannot create parallel flow: Smart Intent fallback already has descendants through existing AI Model connections. Parallel mode is only allowed if AI Model children have no further connections.'
                                };
                            }
                            
                            // Check 2: If the target AI Model (new connection) has descendants
                            const targetHasDescendants = hasDescendants(connection.target, 'output');
                            if (targetHasDescendants) {
                                return {
                                    isValid: false,
                                    message: 'Cannot create parallel flow: The target AI Model already has descendants. Parallel mode is only allowed if AI Model children have no further connections.'
                                };
                            }
                        }
                    }
                    
                    // Check if this fallback already connected to a parallel parent AI Model
                    const hasConnectionToParallelParent = existingOutputs.some(edge => {
                        const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                        return existingTargetNode && existingTargetNode.data.type === 'model' && existingTargetNode.data.isParallel === true;
                    });
                    
                    // If this fallback is already connected to a parallel parent, block any additional connections
                    if (hasConnectionToParallelParent) {
                        return {
                            isValid: false,
                            message: 'Smart Intent fallback cannot have additional connections when connected to a parallel parent AI Model'
                        };
                    }
                    
                    // Check if this fallback is already in parallel mode (2+ AI Models)
                    const isFallbackInParallelMode = existingOutputs.length > 0 && existingOutputs.every(edge => {
                        const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                        return existingTargetNode && existingTargetNode.data.type === 'model';
                    });

                    // **Check if target is AI Model that's already in use**
                    if (targetType === 'model') {
                        // If trying to connect to a parallel parent and already have other connections, block it
                        if (targetNode.data.isParallel === true && existingOutputs.length > 0) {
                            return {
                                isValid: false,
                                message: 'Smart Intent fallback cannot connect to a parallel parent AI Model when it already has other connections'
                            };
                        }
                        
                        // ONLY check if fallback is in parallel mode
                        if (isFallbackInParallelMode) {
                            // Check if target AI Model is already a parallel parent
                            if (targetNode.data.isParallel === true) {
                                return {
                                    isValid: false,
                                    message: 'Cannot connect to this AI Model node because it is already a parallel parent node'
                                };
                            }
                        }

                        // ALWAYS check if target AI Model is already a child of a parallel parent (ห้ามทุกกรณี)
                        const hasParallelParent = this.tempEdge.some(edge => {
                            if (edge.target === connection.target && edge.sourceHandle === 'output') {
                                // Check if edge is marked as parallel
                                if (edge.data?.isParallel === true) return true;
                                
                                // Check if parent node is a parallel parent
                                const parentNode = this.tempNode.find(n => n.id === edge.source);
                                if (parentNode && parentNode.data.type === 'model' && parentNode.data.isParallel === true) {
                                    return true;
                                }
                                
                                // Check if parent is messageInput with multiple model outputs
                                if (parentNode && parentNode.data.type === 'messageInput') {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === 'output'
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                
                                // Check if parent is intent main action with multiple model outputs from that specific handle
                                if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                    // Check if this specific main action handle has multiple connections to AI Models
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                
                                // Check if parent is intent fallback with multiple model outputs
                                if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle === 'intent-fallback') {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === 'intent-fallback'
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                
                                // Check if parent is smart intent main action with multiple model outputs
                                if (parentNode && parentNode.data.type === 'smartIntent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                
                                // Check if parent is smart intent fallback with multiple model outputs
                                if (parentNode && parentNode.data.type === 'smartIntent' && edge.sourceHandle === 'smart-intent-fallback') {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === 'smart-intent-fallback'
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                            }
                            return false;
                        });

                        if (hasParallelParent) {
                            return {
                                isValid: false,
                                message: 'Cannot connect to this AI Model node because it is already a child of a parallel parent node'
                            };
                        }
                    }

                    // If fallback is in parallel mode
                    if (isFallbackInParallelMode) {
                        // Parallel mode: only allow connection to model nodes
                        if (targetType !== 'model') {
                            return {
                                isValid: false,
                                message: 'Smart Intent fallback in parallel mode can only connect to AI Model nodes'
                            };
                        }
                        return { isValid: true };
                    }
                    
                    // Check if fallback has existing connection to non-AI Model nodes
                    const hasConnectionToNonModel = existingOutputs.some(edge => {
                        const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                        return existingTargetNode && existingTargetNode.data.type !== 'model';
                    });
                    
                    // If fallback is already connected to a non-AI Model node, block any additional connections
                    if (hasConnectionToNonModel) {
                        return {
                            isValid: false,
                            message: 'Smart Intent fallback cannot have additional connections when connected to non-AI Model nodes'
                        };
                    }
                    
                    // If trying to connect to non-AI Model and already have other connections, block it
                    if (targetType !== 'model' && existingOutputs.length > 0) {
                        return {
                            isValid: false,
                            message: 'Smart Intent fallback cannot connect to non-AI Model nodes when it already has other connections'
                        };
                    }

                    // Normal mode: allow connection to allowed targets
                    const allowedTargets = ['intent', 'model', 'response', 'apiTrigger', 'contactAgent', 'email', 'smartIntent', 'advancedRouter', 'jsonCondition'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Smart Intent fallback can only connect to Intent, AI Model, API Trigger, Contact Agent nodes or Response'
                        };
                    }
                }
                else {
                    const allowedTargets = ['intent', 'model', 'response', 'apiTrigger', 'contactAgent', 'email', 'smartIntent', 'advancedRouter', 'jsonCondition'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Smart Intent node can only connect to Intent, AI Model, API Trigger, Contact Agent nodes or Response'
                        };
                    }
                }
            }

            // Advanced Router node handle validation
            if (sourceType === 'advancedRouter') {
                if (sourceHandle === 'output-bottom') {
                    const allowedTargets = ['intentAI'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Source model of Advanced Router node can only connect to Intent AI nodes'
                        };
                    }
                } else if (sourceHandle && sourceHandle.startsWith('advrouter-')) {
                    const existingOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    );
                    const outputLabel = sourceHandle === 'advrouter-fallback'
                        ? 'Advanced Router fallback'
                        : 'Advanced Router output';

                    if (existingOutputs.length > 0 && targetType === 'model') {
                        const existingModelConnections = existingOutputs.filter(edge => {
                            const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                            return existingTargetNode && existingTargetNode.data.type === 'model';
                        });

                        if (existingModelConnections.length > 0) {
                            const hasChildWithDescendants = existingModelConnections.some(edge => {
                                return hasDescendants(edge.target, 'output');
                            });

                            if (hasChildWithDescendants) {
                                return {
                                    isValid: false,
                                    message: `Cannot create parallel flow: ${outputLabel} already has descendants through existing AI Model connections. Parallel mode is only allowed if AI Model children have no further connections.`
                                };
                            }

                            const targetHasDescendants = hasDescendants(connection.target, 'output');
                            if (targetHasDescendants) {
                                return {
                                    isValid: false,
                                    message: 'Cannot create parallel flow: The target AI Model already has descendants. Parallel mode is only allowed if AI Model children have no further connections.'
                                };
                            }
                        }
                    }

                    const hasConnectionToParallelParent = existingOutputs.some(edge => {
                        const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                        return existingTargetNode && existingTargetNode.data.type === 'model' && existingTargetNode.data.isParallel === true;
                    });

                    if (hasConnectionToParallelParent) {
                        return {
                            isValid: false,
                            message: `${outputLabel} cannot have additional connections when connected to a parallel parent AI Model`
                        };
                    }

                    const isOutputInParallelMode = existingOutputs.length > 0 && existingOutputs.every(edge => {
                        const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                        return existingTargetNode && existingTargetNode.data.type === 'model';
                    });

                    if (targetType === 'model') {
                        if (targetNode.data.isParallel === true && existingOutputs.length > 0) {
                            return {
                                isValid: false,
                                message: `${outputLabel} cannot connect to a parallel parent AI Model when it already has other connections`
                            };
                        }

                        if (isOutputInParallelMode && targetNode.data.isParallel === true) {
                            return {
                                isValid: false,
                                message: 'Cannot connect to this AI Model node because it is already a parallel parent node'
                            };
                        }
                    }

                    if (isOutputInParallelMode) {
                        if (targetType !== 'model') {
                            return {
                                isValid: false,
                                message: `${outputLabel} in parallel mode can only connect to AI Model nodes`
                            };
                        }
                        return { isValid: true };
                    }

                    const hasConnectionToNonModel = existingOutputs.some(edge => {
                        const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                        return existingTargetNode && existingTargetNode.data.type !== 'model';
                    });

                    if (hasConnectionToNonModel) {
                        return {
                            isValid: false,
                            message: `${outputLabel} cannot have additional connections when connected to non-AI Model nodes`
                        };
                    }

                    if (targetType !== 'model' && existingOutputs.length > 0) {
                        return {
                            isValid: false,
                            message: `${outputLabel} cannot connect to non-AI Model nodes when it already has other connections`
                        };
                    }

                    const allowedTargets = ['intent', 'model', 'response', 'apiTrigger', 'contactAgent', 'email', 'smartIntent', 'advancedRouter', 'jsonCondition'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Advanced Router output can only connect to Intent, AI Model, API Trigger, Contact Agent nodes or Response'
                        };
                    }
                }
            }

            if (sourceType === 'jsonCondition') {
                const validHandles = ['output-match', 'output-fallback'];
                const isValidHandle = !sourceHandle || validHandles.includes(sourceHandle);

                if (!isValidHandle) {
                    return {
                        isValid: false,
                        message: 'Invalid output handle for JSON Condition node'
                    };
                }

                // serviceFallback / apiRequest ถูกถอดออกแล้ว — ไม่มีในพาเลท ไม่มี node component
                // และไม่มี NodeProcessor ฝั่ง backend จึงเป็นสตริงตายที่ลากมาต่อไม่ได้อยู่แล้ว
                const allowedTargets = [
                    'model', 'intent', 'smartIntent', 'response', 'apiTrigger', 'contactAgent', 'email',
                    'orchestrator', 'flowOutput', 'queryRewriter', 'interestOptimizer', 'loanInterest',
                    'interestOptimizerV2', 'loanInterestV2', 'jsonCondition',
                    'delay', 'switch', 'condition', 'orchestrator', 'agent', 'flowOutput'
                ];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'JSON Condition output can only connect to processing, routing, action, or response nodes'
                    };
                }
            }

            // AI Model node handle validation
            if (sourceType === 'model') {
                if (sourceHandle === 'output-bottom') {
                    // S15-P1 — Knowledge Graph / Waterfall Search เป็น knowledge-source plugin ใต้ AI Model (ajt Flow.vue:12648)
                    const allowedTargets = ['databaseIntegration', 'documents', 'products', 'apiknowledge', 'sharepoint', 'knowledgeGraph', 'nexaWaterfallSearch'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'AI Model node knowledge souce can only connect to knowledge nodes;'
                        };
                    }
                } else if (sourceHandle === 'output') {
                    // **Check 1: If source node is a parent in parallel mode (isParallel = true)**
                    // Parent node can ONLY connect to AI Model nodes (to create parallel branches)
                    if (sourceNode.data.isParallel === true) {
                        // **NEW: Check if AI Model parallel parent already has descendants - if yes, block additional parallel connections**
                        const existingParallelOutputs = this.tempEdge.filter(edge =>
                            edge.source === connection.source && edge.sourceHandle === 'output'
                        );
                        
                        if (existingParallelOutputs.length > 0) {
                            // Check 1: If any existing parallel child has descendants
                            const hasChildWithDescendants = existingParallelOutputs.some(edge => {
                                const childNode = this.tempNode.find(n => n.id === edge.target);
                                if (childNode && childNode.data.type === 'model') {
                                    return hasDescendants(edge.target, 'output');
                                }
                                return false;
                            });
                            
                            if (hasChildWithDescendants) {
                                return {
                                    isValid: false,
                                    message: 'Cannot add more parallel connections: AI Model parallel parent already has descendants through existing child AI Models. Parallel mode is only allowed if child AI Models have no further connections.'
                                };
                            }
                            
                            // Check 2: If the target AI Model (new connection) has descendants
                            const targetHasDescendants = hasDescendants(connection.target, 'output');
                            if (targetHasDescendants) {
                                return {
                                    isValid: false,
                                    message: 'Cannot add more parallel connections: The target AI Model already has descendants. Parallel mode is only allowed if child AI Models have no further connections.'
                                };
                            }
                        }
                        
                        if (targetType !== 'model') {
                            return {
                                isValid: false,
                                message: 'AI Model node with parallel mode enabled can only connect to other AI Model nodes. Child AI Model nodes must then connect to a Merge node.'
                            };
                        }
                        
                        // Check if target AI Model is already a parallel parent
                        if (targetNode.data.isParallel === true) {
                            return {
                                isValid: false,
                                message: 'Cannot connect to this AI Model node because it is already a parallel parent node'
                            };
                        }

                        // Check if target AI Model is already a child of a parallel parent
                        const targetHasParallelParent = this.tempEdge.some(edge => {
                            // Check for 'output' handle (AI Model, messageInput) OR main action handles (Intent) OR fallback handle
                            const isValidSourceHandle = edge.sourceHandle === 'output' || 
                                                       (edge.sourceHandle && edge.sourceHandle.endsWith('-main')) ||
                                                       edge.sourceHandle === 'intent-fallback';
                            
                            if (edge.target === connection.target && isValidSourceHandle) {
                                // Check if edge is marked as parallel
                                if (edge.data?.isParallel === true) return true;
                                
                                // Check if parent node is a parallel parent
                                const parentNode = this.tempNode.find(n => n.id === edge.source);
                                if (parentNode && parentNode.data.type === 'model' && parentNode.data.isParallel === true) {
                                    return true;
                                }
                                
                                // Check if parent is messageInput with multiple model outputs
                                if (parentNode && parentNode.data.type === 'messageInput') {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === 'output'
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                
                                // Check if parent is intent with multiple model outputs from main action handles
                                if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                    // Check if this specific main action handle has multiple connections to AI Models
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                
                                // Check if parent is intent fallback with multiple model outputs
                                if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle === 'intent-fallback') {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === 'intent-fallback'
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                
                                // Check if parent is smart intent main action with multiple model outputs
                                if (parentNode && parentNode.data.type === 'smartIntent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                
                                // Check if parent is smart intent fallback with multiple model outputs
                                if (parentNode && parentNode.data.type === 'smartIntent' && edge.sourceHandle === 'smart-intent-fallback') {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === 'smart-intent-fallback'
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                            }
                            return false;
                        });

                        if (targetHasParallelParent) {
                            return {
                                isValid: false,
                                message: 'Cannot connect to this AI Model node because it is already a child of a parallel parent node'
                            };
                        }
                        
                        // If target is AI Model and not in use, allow the connection (creating parallel branch)
                        return { isValid: true };
                    }
                    
                    // **Check 2: If source node is a child of a parallel parent**
                    // Check if this source node receives input from a parallel parent (AI Model, messageInput, or intent)
                    const hasParallelParent = this.tempEdge.some(edge => {
                        // Check for 'output' handle (AI Model, messageInput) OR main action handles (Intent) OR fallback handle
                        const isValidSourceHandle = edge.sourceHandle === 'output' || 
                                                   (edge.sourceHandle && edge.sourceHandle.endsWith('-main')) ||
                                                   edge.sourceHandle === 'intent-fallback';
                        
                        if (edge.target === connection.source && isValidSourceHandle) {
                            // Check if the edge has isParallel data
                            if (edge.data?.isParallel === true) {
                                return true;
                            }
                            // Or check if the parent node has isParallel = true (AI Model only)
                            const parentNode = this.tempNode.find(n => n.id === edge.source);
                            if (parentNode && parentNode.data.type === 'model' && parentNode.data.isParallel === true) {
                                return true;
                            }
                            // Or check if parent is messageInput with multiple model outputs (parallel mode)
                            if (parentNode && parentNode.data.type === 'messageInput') {
                                const parentOutputs = this.tempEdge.filter(e =>
                                    e.source === parentNode.id && e.sourceHandle === 'output'
                                );
                                if (parentOutputs.length >= 2) {
                                    const allToModels = parentOutputs.every(e => {
                                        const t = this.tempNode.find(n => n.id === e.target);
                                        return t && t.data.type === 'model';
                                    });
                                    if (allToModels) return true;
                                }
                            }
                            // Or check if parent is intent with multiple model outputs from main action handles (parallel mode)
                            if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                // Check if this specific main action handle has multiple connections to AI Models
                                const parentOutputs = this.tempEdge.filter(e =>
                                    e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                );
                                if (parentOutputs.length >= 2) {
                                    const allToModels = parentOutputs.every(e => {
                                        const t = this.tempNode.find(n => n.id === e.target);
                                        return t && t.data.type === 'model';
                                    });
                                    if (allToModels) return true;
                                }
                            }
                            // Or check if parent is intent fallback with multiple model outputs (parallel mode)
                            if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle === 'intent-fallback') {
                                const parentOutputs = this.tempEdge.filter(e =>
                                    e.source === parentNode.id && e.sourceHandle === 'intent-fallback'
                                );
                                if (parentOutputs.length >= 2) {
                                    const allToModels = parentOutputs.every(e => {
                                        const t = this.tempNode.find(n => n.id === e.target);
                                        return t && t.data.type === 'model';
                                    });
                                    if (allToModels) return true;
                                }
                            }
                            // Or check if parent is smart intent main action with multiple model outputs (parallel mode)
                            if (parentNode && parentNode.data.type === 'smartIntent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                const parentOutputs = this.tempEdge.filter(e =>
                                    e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                );
                                if (parentOutputs.length >= 2) {
                                    const allToModels = parentOutputs.every(e => {
                                        const t = this.tempNode.find(n => n.id === e.target);
                                        return t && t.data.type === 'model';
                                    });
                                    if (allToModels) return true;
                                }
                            }
                            // Or check if parent is smart intent fallback with multiple model outputs (parallel mode)
                            if (parentNode && parentNode.data.type === 'smartIntent' && edge.sourceHandle === 'smart-intent-fallback') {
                                const parentOutputs = this.tempEdge.filter(e =>
                                    e.source === parentNode.id && e.sourceHandle === 'smart-intent-fallback'
                                );
                                if (parentOutputs.length >= 2) {
                                    const allToModels = parentOutputs.every(e => {
                                        const t = this.tempNode.find(n => n.id === e.target);
                                        return t && t.data.type === 'model';
                                    });
                                    if (allToModels) return true;
                                }
                            }
                        }
                        return false;
                    });
                    
                    if (hasParallelParent) {
                        // This node is a child of parallel execution, can only connect to Merge node
                        if (targetType !== 'merge') {
                            return {
                                isValid: false,
                                message: 'AI Model nodes in parallel execution (child of parallel parent) can only connect to Merge node.'
                            };
                        }
                        // If target is Merge, allow the connection
                        return { isValid: true };
                    }
                    
                    // Check if there are existing output connections from this model node
                    const existingOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === 'output'
                    );

                    // **Check if this source AI Model is a child of a parallel parent FIRST**
                    // This check should happen BEFORE checking existing outputs
                    const sourceIsChildOfParallelParent = this.tempEdge.some(edge => {
                        // Check for 'output' handle (AI Model, messageInput) OR main action handles (Intent) OR fallback handle
                        const isValidSourceHandle = edge.sourceHandle === 'output' || 
                                                   (edge.sourceHandle && edge.sourceHandle.endsWith('-main')) ||
                                                   edge.sourceHandle === 'intent-fallback';
                        
                        if (edge.target === connection.source && isValidSourceHandle) {
                            // Check if the edge has isParallel data
                            if (edge.data?.isParallel === true) {
                                return true;
                            }
                            // Or check if the parent node has isParallel = true (AI Model only)
                            const parentNode = this.tempNode.find(n => n.id === edge.source);
                            if (parentNode && parentNode.data.type === 'model' && parentNode.data.isParallel === true) {
                                return true;
                            }
                            // Or check if parent is messageInput with multiple model outputs (parallel mode)
                            if (parentNode && parentNode.data.type === 'messageInput') {
                                const parentOutputs = this.tempEdge.filter(e =>
                                    e.source === parentNode.id && e.sourceHandle === 'output'
                                );
                                if (parentOutputs.length >= 2) {
                                    const allToModels = parentOutputs.every(e => {
                                        const t = this.tempNode.find(n => n.id === e.target);
                                        return t && t.data.type === 'model';
                                    });
                                    if (allToModels) return true;
                                }
                            }
                            // Or check if parent is intent with multiple model outputs from main action handles (parallel mode)
                            if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                // Check if this specific main action handle has multiple connections to AI Models
                                const parentOutputs = this.tempEdge.filter(e =>
                                    e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                );
                                if (parentOutputs.length >= 2) {
                                    const allToModels = parentOutputs.every(e => {
                                        const t = this.tempNode.find(n => n.id === e.target);
                                        return t && t.data.type === 'model';
                                    });
                                    if (allToModels) return true;
                                }
                            }
                            // Or check if parent is intent fallback with multiple model outputs (parallel mode)
                            if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle === 'intent-fallback') {
                                const parentOutputs = this.tempEdge.filter(e =>
                                    e.source === parentNode.id && e.sourceHandle === 'intent-fallback'
                                );
                                if (parentOutputs.length >= 2) {
                                    const allToModels = parentOutputs.every(e => {
                                        const t = this.tempNode.find(n => n.id === e.target);
                                        return t && t.data.type === 'model';
                                    });
                                    if (allToModels) return true;
                                }
                            }
                            // Or check if parent is smart intent main action with multiple model outputs (parallel mode)
                            if (parentNode && parentNode.data.type === 'smartIntent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                const parentOutputs = this.tempEdge.filter(e =>
                                    e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                );
                                if (parentOutputs.length >= 2) {
                                    const allToModels = parentOutputs.every(e => {
                                        const t = this.tempNode.find(n => n.id === e.target);
                                        return t && t.data.type === 'model';
                                    });
                                    if (allToModels) return true;
                                }
                            }
                            // Or check if parent is smart intent fallback with multiple model outputs (parallel mode)
                            if (parentNode && parentNode.data.type === 'smartIntent' && edge.sourceHandle === 'smart-intent-fallback') {
                                const parentOutputs = this.tempEdge.filter(e =>
                                    e.source === parentNode.id && e.sourceHandle === 'smart-intent-fallback'
                                );
                                if (parentOutputs.length >= 2) {
                                    const allToModels = parentOutputs.every(e => {
                                        const t = this.tempNode.find(n => n.id === e.target);
                                        return t && t.data.type === 'model';
                                    });
                                    if (allToModels) return true;
                                }
                            }
                        }
                        return false;
                    });
                    
                    if (sourceIsChildOfParallelParent) {
                        // This source node is a child of parallel execution, can only connect to Merge node
                        if (targetType !== 'merge') {
                            return {
                                isValid: false,
                                message: 'AI Model nodes in parallel execution (child of parallel parent) can only connect to Merge node.'
                            };
                        }
                        // If target is Merge, allow the connection
                        return { isValid: true };
                    }

                    // If there are existing connections, all must be to model nodes for parallel execution
                    if (existingOutputs.length > 0) {
                        // Check if all existing connections are to model nodes
                        const allTargetsAreModels = existingOutputs.every(edge => {
                            const existingTargetNode = this.tempNode.find(node => node.id === edge.target);
                            return existingTargetNode && existingTargetNode.data.type === 'model';
                        });

                        if (allTargetsAreModels) {
                            // **NEW: Check if trying to add more parallel connections when descendants exist**
                            if (targetType === 'model') {
                                // Check 1: If any existing AI Model child has descendants
                                const hasChildWithDescendants = existingOutputs.some(edge => {
                                    const childNode = this.tempNode.find(n => n.id === edge.target);
                                    if (childNode && childNode.data.type === 'model') {
                                        return hasDescendants(edge.target, 'output');
                                    }
                                    return false;
                                });
                                
                                if (hasChildWithDescendants) {
                                    return {
                                        isValid: false,
                                        message: 'Cannot add more parallel connections: AI Model already has descendants through existing child AI Models. Parallel mode is only allowed if child AI Models have no further connections.'
                                    };
                                }
                                
                                // Check 2: If the target AI Model (new connection) has descendants
                                const targetHasDescendants = hasDescendants(connection.target, 'output');
                                if (targetHasDescendants) {
                                    return {
                                        isValid: false,
                                        message: 'Cannot add more parallel connections: The target AI Model already has descendants. Parallel mode is only allowed if child AI Models have no further connections.'
                                    };
                                }
                            }
                            
                            // Parallel mode: only allow connection to model nodes or merge node
                            if (targetType !== 'model' && targetType !== 'merge') {
                                return {
                                    isValid: false,
                                    message: 'AI Model node in parallel mode can only connect to other AI Model nodes or Merge node'
                                };
                            }
                            
                            // If connecting to another model (not merge), apply layer validation
                            if (targetType === 'model') {
                                // Check if target AI Model is already a parallel parent
                                if (targetNode.data.isParallel === true) {
                                    return {
                                        isValid: false,
                                        message: 'Cannot connect to this AI Model node because it is already a parallel parent node'
                                    };
                                }

                                // Check if target AI Model is already a child of a parallel parent
                                const targetHasParallelParent = this.tempEdge.some(edge => {
                                    // Check for 'output' handle (AI Model, messageInput) OR main action handles (Intent) OR fallback handle
                                    const isValidSourceHandle = edge.sourceHandle === 'output' || 
                                                               (edge.sourceHandle && edge.sourceHandle.endsWith('-main')) ||
                                                               edge.sourceHandle === 'intent-fallback';
                                    
                                    if (edge.target === connection.target && isValidSourceHandle) {
                                        // Check if edge is marked as parallel
                                        if (edge.data?.isParallel === true) return true;
                                        
                                        // Check if parent node is a parallel parent
                                        const parentNode = this.tempNode.find(n => n.id === edge.source);
                                        if (parentNode && parentNode.data.type === 'model' && parentNode.data.isParallel === true) {
                                            return true;
                                        }
                                        
                                        // Check if parent is messageInput with multiple model outputs
                                        if (parentNode && parentNode.data.type === 'messageInput') {
                                            const parentOutputs = this.tempEdge.filter(e =>
                                                e.source === parentNode.id && e.sourceHandle === 'output'
                                            );
                                            if (parentOutputs.length >= 2) {
                                                const allToModels = parentOutputs.every(e => {
                                                    const t = this.tempNode.find(n => n.id === e.target);
                                                    return t && t.data.type === 'model';
                                                });
                                                if (allToModels) return true;
                                            }
                                        }
                                        
                                        // Check if parent is intent with multiple model outputs from main action handles
                                        if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                            // Check if this specific main action handle has multiple connections to AI Models
                                            const parentOutputs = this.tempEdge.filter(e =>
                                                e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                            );
                                            if (parentOutputs.length >= 2) {
                                                const allToModels = parentOutputs.every(e => {
                                                    const t = this.tempNode.find(n => n.id === e.target);
                                                    return t && t.data.type === 'model';
                                                });
                                                if (allToModels) return true;
                                            }
                                        }
                                        
                                        // Check if parent is intent fallback with multiple model outputs
                                        if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle === 'intent-fallback') {
                                            const parentOutputs = this.tempEdge.filter(e =>
                                                e.source === parentNode.id && e.sourceHandle === 'intent-fallback'
                                            );
                                            if (parentOutputs.length >= 2) {
                                                const allToModels = parentOutputs.every(e => {
                                                    const t = this.tempNode.find(n => n.id === e.target);
                                                    return t && t.data.type === 'model';
                                                });
                                                if (allToModels) return true;
                                            }
                                        }
                                        
                                        // Check if parent is smart intent main action with multiple model outputs
                                        if (parentNode && parentNode.data.type === 'smartIntent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                            const parentOutputs = this.tempEdge.filter(e =>
                                                e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                            );
                                            if (parentOutputs.length >= 2) {
                                                const allToModels = parentOutputs.every(e => {
                                                    const t = this.tempNode.find(n => n.id === e.target);
                                                    return t && t.data.type === 'model';
                                                });
                                                if (allToModels) return true;
                                            }
                                        }
                                        
                                        // Check if parent is smart intent fallback with multiple model outputs
                                        if (parentNode && parentNode.data.type === 'smartIntent' && edge.sourceHandle === 'smart-intent-fallback') {
                                            const parentOutputs = this.tempEdge.filter(e =>
                                                e.source === parentNode.id && e.sourceHandle === 'smart-intent-fallback'
                                            );
                                            if (parentOutputs.length >= 2) {
                                                const allToModels = parentOutputs.every(e => {
                                                    const t = this.tempNode.find(n => n.id === e.target);
                                                    return t && t.data.type === 'model';
                                                });
                                                if (allToModels) return true;
                                            }
                                        }
                                    }
                                    return false;
                                });

                                if (targetHasParallelParent) {
                                    return {
                                        isValid: false,
                                        message: 'Cannot connect to this AI Model node because it is already a child of a parallel parent node'
                                    };
                                }
                                
                                // Check if source and target share a common parent (same parallel layer)
                                // Get all parents of the source node (nodes that connect TO source with output handle)
                                const sourceParents = this.tempEdge
                                    .filter(edge => edge.target === connection.source && edge.sourceHandle === 'output')
                                    .map(edge => edge.source);
                                
                                // Check if any of source's parents also connect to the target
                                // We need to check existing edges plus consider this will be a new connection
                                const wouldBeInSameLayer = sourceParents.some(parentId => {
                                    // Check if this parent has an edge to the target node
                                    return this.tempEdge.some(edge => 
                                        edge.source === parentId && 
                                        edge.target === connection.target && 
                                        edge.sourceHandle === 'output'
                                    );
                                });
                                
                                if (wouldBeInSameLayer) {
                                    return {
                                        isValid: false,
                                        message: 'Cannot connect AI Model nodes in the same parallel layer. Nodes from the same parent cannot connect to each other.'
                                    };
                                }
                                
                                // Additional check: Prevent circular reference (layer ที่ต่ำกว่าย้อนกลับมา layer ก่อนหน้า)
                                // Check if the target model already has a path back to the source
                                const wouldCreateCycle = this.checkModelCircularPath(connection.target, connection.source);
                                if (wouldCreateCycle) {
                                    return {
                                        isValid: false,
                                        message: 'Cannot connect back to previous layers. This would create a circular reference between AI Model nodes.'
                                    };
                                }
                            }
                            // Merge node is always allowed in parallel mode - no additional checks needed
                        } else {
                            // Mixed mode detected - not allowed
                            return {
                                isValid: false,
                                message: 'AI Model node already has a connection to a non-model node. Cannot add parallel model connections.'
                            };
                        }
                    } else {
                        // No existing connections - but still need to check if this is a child of parallel parent
                        // Check if this source node receives input from a parallel parent (AI Model, messageInput, or intent)
                        const hasParallelParentNoExisting = this.tempEdge.some(edge => {
                            // Check for 'output' handle (AI Model, messageInput) OR main action handles (Intent) OR fallback handle
                            const isValidSourceHandle = edge.sourceHandle === 'output' || 
                                                       (edge.sourceHandle && edge.sourceHandle.endsWith('-main')) ||
                                                       edge.sourceHandle === 'intent-fallback';
                            
                            if (edge.target === connection.source && isValidSourceHandle) {
                                // Check if the edge has isParallel data
                                if (edge.data?.isParallel === true) {
                                    return true;
                                }
                                // Or check if the parent node has isParallel = true (AI Model only)
                                const parentNode = this.tempNode.find(n => n.id === edge.source);
                                if (parentNode && parentNode.data.type === 'model' && parentNode.data.isParallel === true) {
                                    return true;
                                }
                                // Or check if parent is messageInput with multiple model outputs (parallel mode)
                                if (parentNode && parentNode.data.type === 'messageInput') {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === 'output'
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                // Or check if parent is intent with multiple model outputs from main action handles (parallel mode)
                                if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                    console.log('Checking intent parent for parallel outputs');
                                    // Check if this specific main action handle has multiple connections to AI Models
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                // Or check if parent is intent fallback with multiple model outputs (parallel mode)
                                if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle === 'intent-fallback') {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === 'intent-fallback'
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                // Or check if parent is smart intent main action with multiple model outputs (parallel mode)
                                if (parentNode && parentNode.data.type === 'smartIntent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                                // Or check if parent is smart intent fallback with multiple model outputs (parallel mode)
                                if (parentNode && parentNode.data.type === 'smartIntent' && edge.sourceHandle === 'smart-intent-fallback') {
                                    const parentOutputs = this.tempEdge.filter(e =>
                                        e.source === parentNode.id && e.sourceHandle === 'smart-intent-fallback'
                                    );
                                    if (parentOutputs.length >= 2) {
                                        const allToModels = parentOutputs.every(e => {
                                            const t = this.tempNode.find(n => n.id === e.target);
                                            return t && t.data.type === 'model';
                                        });
                                        if (allToModels) return true;
                                    }
                                }
                            }
                            return false;
                        });
                        
                        console.log('hasParallelParentNoExisting:', hasParallelParentNoExisting);
                        if (hasParallelParentNoExisting) {
                            // This node is a child of parallel execution, can only connect to Merge node
                            if (targetType !== 'merge') {
                                return {
                                    isValid: false,
                                    message: 'AI Model nodes in parallel execution (child of parallel parent) can only connect to Merge node.'
                                };
                            }
                            // If target is Merge, allow the connection
                            return { isValid: true };
                        }
                        
                        // No parallel parent - allow connection to allowed targets
                        const allowedTargets = ['model', 'intent', 'response', 'email', 'smartIntent', 'advancedRouter', 'jsonCondition', 'merge'];
                        if (!allowedTargets.includes(targetType)) {
                            return {
                                isValid: false,
                                message: 'AI Model node can only connect to AI Model, Intent, Response, or Merge nodes'
                            };
                        }

                        // If connecting to a non-model node, check that this will be the only connection
                        if (targetType !== 'model' && existingOutputs.length > 0) {
                            return {
                                isValid: false,
                                message: 'AI Model node can only have one output connection to non-model nodes'
                            };
                        }
                        
                        // If connecting to a model node (first connection), also check for circular path and same layer
                        if (targetType === 'model') {
                            // First check if target is a child of parallel parent (ห้ามทุกกรณี)
                            const targetIsChildOfParallelParent = this.tempEdge.some(edge => {
                                // Check for 'output' handle (AI Model, messageInput) OR main action handles (Intent) OR fallback handle
                                const isValidSourceHandle = edge.sourceHandle === 'output' || 
                                                           (edge.sourceHandle && edge.sourceHandle.endsWith('-main')) ||
                                                           edge.sourceHandle === 'intent-fallback';
                                
                                if (edge.target === connection.target && isValidSourceHandle) {
                                    // Check if edge is marked as parallel
                                    if (edge.data?.isParallel === true) return true;
                                    
                                    // Check if parent node is a parallel parent
                                    const parentNode = this.tempNode.find(n => n.id === edge.source);
                                    if (parentNode && parentNode.data.type === 'model' && parentNode.data.isParallel === true) {
                                        return true;
                                    }
                                    
                                    // Check if parent is messageInput with multiple model outputs
                                    if (parentNode && parentNode.data.type === 'messageInput') {
                                        const parentOutputs = this.tempEdge.filter(e =>
                                            e.source === parentNode.id && e.sourceHandle === 'output'
                                        );
                                        if (parentOutputs.length >= 2) {
                                            const allToModels = parentOutputs.every(e => {
                                                const t = this.tempNode.find(n => n.id === e.target);
                                                return t && t.data.type === 'model';
                                            });
                                            if (allToModels) return true;
                                        }
                                    }
                                    
                                    // Check if parent is intent with multiple model outputs from main action handles
                                    if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                        const parentOutputs = this.tempEdge.filter(e =>
                                            e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                        );
                                        if (parentOutputs.length >= 2) {
                                            const allToModels = parentOutputs.every(e => {
                                                const t = this.tempNode.find(n => n.id === e.target);
                                                return t && t.data.type === 'model';
                                            });
                                            if (allToModels) return true;
                                        }
                                    }
                                    
                                    // Check if parent is intent fallback with multiple model outputs
                                    if (parentNode && parentNode.data.type === 'intent' && edge.sourceHandle === 'intent-fallback') {
                                        const parentOutputs = this.tempEdge.filter(e =>
                                            e.source === parentNode.id && e.sourceHandle === 'intent-fallback'
                                        );
                                        if (parentOutputs.length >= 2) {
                                            const allToModels = parentOutputs.every(e => {
                                                const t = this.tempNode.find(n => n.id === e.target);
                                                return t && t.data.type === 'model';
                                            });
                                            if (allToModels) return true;
                                        }
                                    }
                                    
                                    // Check if parent is smart intent main action with multiple model outputs
                                    if (parentNode && parentNode.data.type === 'smartIntent' && edge.sourceHandle && edge.sourceHandle.endsWith('-main')) {
                                        const parentOutputs = this.tempEdge.filter(e =>
                                            e.source === parentNode.id && e.sourceHandle === edge.sourceHandle
                                        );
                                        if (parentOutputs.length >= 2) {
                                            const allToModels = parentOutputs.every(e => {
                                                const t = this.tempNode.find(n => n.id === e.target);
                                                return t && t.data.type === 'model';
                                            });
                                            if (allToModels) return true;
                                        }
                                    }
                                    
                                    // Check if parent is smart intent fallback with multiple model outputs
                                    if (parentNode && parentNode.data.type === 'smartIntent' && edge.sourceHandle === 'smart-intent-fallback') {
                                        const parentOutputs = this.tempEdge.filter(e =>
                                            e.source === parentNode.id && e.sourceHandle === 'smart-intent-fallback'
                                        );
                                        if (parentOutputs.length >= 2) {
                                            const allToModels = parentOutputs.every(e => {
                                                const t = this.tempNode.find(n => n.id === e.target);
                                                return t && t.data.type === 'model';
                                            });
                                            if (allToModels) return true;
                                        }
                                    }
                                }
                                return false;
                            });
                            
                            if (targetIsChildOfParallelParent) {
                                return {
                                    isValid: false,
                                    message: 'Cannot connect to this AI Model node because it is already a child of a parallel parent node'
                                };
                            }
                            
                            // Check if source and target share a common parent (same parallel layer)
                            const sourceParents = this.tempEdge
                                .filter(edge => edge.target === connection.source && edge.sourceHandle === 'output')
                                .map(edge => edge.source);
                            
                            const wouldBeInSameLayer = sourceParents.some(parentId => {
                                return this.tempEdge.some(edge => 
                                    edge.source === parentId && 
                                    edge.target === connection.target && 
                                    edge.sourceHandle === 'output'
                                );
                            });
                            
                            if (wouldBeInSameLayer) {
                                return {
                                    isValid: false,
                                    message: 'Cannot connect AI Model nodes in the same parallel layer. Nodes from the same parent cannot connect to each other.'
                                };
                            }
                            
                            // Check for circular path (ห้าม layer ต่ำกว่าย้อนกลับ)
                            const wouldCreateCycle = this.checkModelCircularPath(connection.target, connection.source);
                            if (wouldCreateCycle) {
                                return {
                                    isValid: false,
                                    message: 'Cannot connect back to previous layers. This would create a circular reference between AI Model nodes.'
                                };
                            }
                        }
                        // Merge node is always allowed as first connection - no additional checks needed
                    }
                }
            }

            if (sourceType === 'orchestrator') {
                if (sourceHandle === 'agents-bottom') {
                    const allowedTargets = ['agent'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Orchestrator agents handle can only connect to Agent nodes'
                        };
                    }
                } else {
                    // serviceFallback / apiRequest ถูกถอดออกแล้ว — เป็นสตริงตาย ไม่มี node จริงรองรับ
                    const allowedTargets = ['intent', 'smartIntent', 'router', 'advancedRouter', 'model', 'loanInterest', 'interestOptimizer', 'interestOptimizerV2', 'loanInterestV2', 'contactAgent', 'apiTrigger', 'email', 'response', 'orchestrator', 'flowOutput', 'jsonCondition', 'condition', 'switch', 'delay', 'merge'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Orchestrator output can only connect to response, action, routing, or processing nodes'
                        };
                    }
                }
            }

            if (sourceType === 'agent') {
                if (sourceHandle === 'tools-bottom' || sourceHandle === 'output-bottom') {
                    const allowedTargets = ['tool', 'documents', 'dynamicKnowledge', 'databaseIntegration', 'apiknowledge', 'products', 'sharepoint', 'flowTool'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Agent tools handle can only connect to knowledge, Tool, or Flow Tool nodes'
                        };
                    }
                } else {
                    const allowedTargets = ['response', 'model', 'orchestrator', 'agent', 'apiTrigger', 'contactAgent', 'email', 'merge', 'jsonCondition', 'condition', 'switch', 'delay', 'flowOutput'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Agent output can only connect to response, action, routing, or processing nodes'
                        };
                    }
                }
            }

            if (sourceType === 'flowTool') {
                return {
                    isValid: false,
                    message: 'Flow Tool nodes cannot send connections. They can only receive connections from Agent nodes'
                };
            }

            if (sourceType === 'flowInput') {
                const allowedTargets = ['orchestrator', 'agent', 'model', 'intent', 'smartIntent', 'advancedRouter', 'jsonCondition'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'Flow Input can only connect to routing or processing nodes'
                    };
                }
            }

            if (sourceType === 'tool' || sourceType === 'flowOutput') {
                return {
                    isValid: false,
                    message: sourceType === 'tool' ? 'Tool nodes cannot send connections. They can only receive connections from Agent nodes' : `${this.getNodeTypeLabel(sourceType)} does not have an output connection`
                };
            }

            if(sourceType === 'email') {
                const allowedTargets = ['model', 'response', 'contactAgent', 'apiTrigger', 'email', 'intent', 'smartIntent', 'advancedRouter', 'jsonCondition'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: "Email node can't connect to this node type"
                    };
                }

            }

            // API Trigger node handle validation
            if (sourceType === 'apiTrigger') {
                const allowedTargets = ['model', 'response', 'contactAgent', 'apiTrigger', 'smartIntent', 'advancedRouter', 'jsonCondition'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'API Trigger node can only connect to AI Model, Response nodes, API Trigger nodes or Contact Agent nodes'
                    };
                }
            }
            if (sourceType === 'contactAgent') {
                const allowedTargets = ['model', 'response', 'contactAgent', 'apiTrigger', 'intent', 'smartIntent', 'advancedRouter', 'jsonCondition'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'Contact Agent node can only connect to AI Model, Response nodes, API Trigger nodes or Contact Agent nodes'
                    };
                }
            }

            // Condition node handle validation
            if (sourceType === 'condition') {
                const validHandles = ['output-true', 'output-false'];
                const isValidHandle = !sourceHandle || validHandles.includes(sourceHandle);

                if (!isValidHandle) {
                    return {
                        isValid: false,
                        message: 'Invalid output handle for Condition node'
                    };
                }
            }

            // Documents/Knowledge nodes should only connect to AI models or processing nodes
            if (['documents', 'databaseIntegration', 'products', 'apiknowledge', 'sharepoint'].includes(sourceType)) {
                const allowedTargets = ['model', 'agent'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: `${this.getNodeTypeLabel(sourceType)} can only connect to AI Model or Agent nodes`
                    };
                }
            }

            // S15-P1 — Knowledge Graph / Waterfall Search ต่อได้เฉพาะ AI Model (ยังไม่รองรับใต้ Agent — spec MVP Limitations)
            if (['knowledgeGraph', 'nexaWaterfallSearch'].includes(sourceType) && targetType !== 'model') {
                return {
                    isValid: false,
                    message: `${this.getNodeTypeLabel(sourceType)} can only connect to AI Model nodes`
                };
            }

            return { isValid: true };
        },
        
        // Check for circular path specifically for model-to-model connections
        // This prevents both: 1) cycles, and 2) lower layers connecting back to upper layers
        checkModelCircularPath(targetNodeId, sourceNodeId) {
            // First check: Does target have a path forward to source? (would create cycle)
            const hasCycleForward = (startNodeId, goalNodeId, visited = new Set()) => {
                if (startNodeId === goalNodeId) return true;
                if (visited.has(startNodeId)) return false;
                
                visited.add(startNodeId);
                
                const outgoingEdges = this.tempEdge.filter(edge => 
                    edge.source === startNodeId && edge.sourceHandle === 'output'
                );
                
                for (const edge of outgoingEdges) {
                    const targetNode = this.tempNode.find(node => node.id === edge.target);
                    if (targetNode && targetNode.data.type === 'model') {
                        if (hasCycleForward(edge.target, goalNodeId, visited)) {
                            return true;
                        }
                    }
                }
                
                return false;
            };
            
            // Second check: Is target an ancestor of source? (would connect lower layer back to upper layer)
            const isAncestor = (potentialAncestorId, descendantId, visited = new Set()) => {
                if (potentialAncestorId === descendantId) return true;
                if (visited.has(descendantId)) return false;
                
                visited.add(descendantId);
                
                // Get all incoming edges to descendant (its parents)
                const incomingEdges = this.tempEdge.filter(edge => 
                    edge.target === descendantId && edge.sourceHandle === 'output'
                );
                
                for (const edge of incomingEdges) {
                    const sourceNode = this.tempNode.find(node => node.id === edge.source);
                    if (sourceNode && sourceNode.data.type === 'model') {
                        if (isAncestor(potentialAncestorId, edge.source, visited)) {
                            return true;
                        }
                    }
                }
                
                return false;
            };
            
            // Check both conditions:
            // 1. Would create a cycle? (target -> ... -> source)
            if (hasCycleForward(targetNodeId, sourceNodeId)) {
                return true;
            }
            
            // 2. Is target an ancestor of source? (connecting back to upper layer)
            if (isAncestor(targetNodeId, sourceNodeId)) {
                return true;
            }
            
            return false;
        },
        
        // Check for circular references
        checkCircularReference(connection) {
            // Create a temporary edge list with the new connection
            const tempEdges = [...this.tempEdge, {
                source: connection.source,
                target: connection.target
            }];

            // Use DFS to detect cycles
            const visited = new Set();
            const recursionStack = new Set();

            const hasCycle = (nodeId) => {
                if (recursionStack.has(nodeId)) {
                    return true; // Cycle detected
                }
                if (visited.has(nodeId)) {
                    return false; // Already processed
                }

                visited.add(nodeId);
                recursionStack.add(nodeId);

                // Find all outgoing edges from this node
                const outgoingEdges = tempEdges.filter(edge => edge.source === nodeId);

                for (const edge of outgoingEdges) {
                    if (hasCycle(edge.target)) {
                        return true;
                    }
                }

                recursionStack.delete(nodeId);
                return false;
            };

            // Check for cycles starting from any node
            for (const node of this.tempNode) {
                if (!visited.has(node.id) && hasCycle(node.id)) {
                    return {
                        isValid: false,
                        message: 'This connection would create a circular reference in the flow'
                    };
                }
            }

            return { isValid: true };
        },
        
        /**
         * Validate that AI Model node doesn't already have input from a parallel parent
         * This prevents cases like Intent A and Fallback connecting to the same AI Model
         * @param {Object} sourceNode - The source node
         * @param {Object} targetNode - The target AI Model node
         * @param {Object} connection - The connection being made
         * @returns {Object} - Validation result
         */
        validateNoExistingParallelParentInput(sourceNode, targetNode, connection) {
            // Get all existing input edges to the target AI Model
            const existingInputs = this.tempEdge.filter(edge => 
                edge.target === connection.target && edge.targetHandle === 'input'
            );
            
            // If target AI Model has no existing inputs, allow the connection
            if (existingInputs.length === 0) {
                return { isValid: true };
            }
            
            // Check if any existing input comes from a parallel parent (or potential parallel parent)
            for (const existingEdge of existingInputs) {
                const existingSourceNode = this.tempNode.find(n => n.id === existingEdge.source);
                if (!existingSourceNode) continue;
                
                // Check if the existing source can be a parallel parent
                if (this.canBeParallelParent(existingSourceNode)) {
                    // If the new connection is from the SAME source node but DIFFERENT handle, block it
                    // (e.g., Intent A main action and Fallback trying to connect to same AI Model)
                    if (existingEdge.source === connection.source && existingEdge.sourceHandle !== connection.sourceHandle) {
                        return {
                            isValid: false,
                            message: 'Cannot connect to this AI Model node. It already has a connection from a different output of the same node. Each AI Model should only receive input from one output path.'
                        };
                    }
                    
                    // If the new connection is from a DIFFERENT parallel parent node, block it
                    // (e.g., Message Input and Intent both trying to connect to same AI Model)
                    if (existingEdge.source !== connection.source) {
                        return {
                            isValid: false,
                            message: 'Cannot connect to this AI Model node. It already has a connection from another parallel-capable node. Each AI Model should only receive input from one source.'
                        };
                    }
                }
            }
            
            // Also check the reverse: if the source is a parallel parent and trying to connect to an AI Model
            // that already has input from ANY other source
            const sourceType = sourceNode.data.type;
            if (sourceType === 'intent' || sourceType === 'smartIntent' || sourceType === 'advancedRouter') {
                // For intent/smart intent nodes, check if target already has input from different node
                const hasInputFromDifferentNode = existingInputs.some(edge => edge.source !== connection.source);
                if (hasInputFromDifferentNode) {
                    return {
                        isValid: false,
                        message: 'Cannot connect to this AI Model node. It already has a connection from another source. Each AI Model should only receive input from one source.'
                    };
                }
            }
            
            return { isValid: true };
        },
        
        // Validate input connection limits
        validateInputLimit(sourceNode, targetNode, connection) {

            const targetType = targetNode.data.type;

            // Count existing input connections to target node
            const existingInputs = this.tempEdge.filter(edge =>
                edge.target === connection.target && edge.targetHandle === 'input'
            ).length;

            if (targetType === 'documents' || targetType === 'products') {
                const existingModelBottomInputs = this.tempEdge.filter(edge => edge.source == sourceNode.id
                    && edge.targetNode.data.type === targetType
                    && edge.sourceHandle == 'output-bottom').length;

                // If there is already one connection from an AI Model output-bottom handle to documents node
                if (existingModelBottomInputs >= 1) {
                    return {
                        isValid: false,
                        message: `AI Model can only connect to one ${targetType === 'documents' ? 'Documents' : 'Products'} node from output-bottom handle`
                    };
                }
            }

            // Most nodes should only have one input connection
            const singleInputNodes = [
                'delay', 'apiTrigger', 'contactAgent',
                'guardrailAI', 'intentAI', 'documents', 'databaseIntegration', 'products', 'jsonCondition'
            ];

            if (singleInputNodes.includes(targetType) && existingInputs >= 1) {
                return {
                    isValid: false,
                    message: `${this.getNodeTypeLabel(targetType)} node can only have one input connection`
                };
            }

            // Special cases for nodes that can have multiple inputs
            if (targetType === 'switch' && existingInputs >= 3) {
                return {
                    isValid: false,
                    message: 'Switch node can have maximum 3 input connections'
                };
            }

            if (targetType === 'condition' && existingInputs >= 2) {
                return {
                    isValid: false,
                    message: 'Condition node can have maximum 2 input connections'
                };
            }

            return { isValid: true };
        },
        // Validate output connection limits
        validateOutputLimit(sourceNode, targetNode, connection) {
            const sourceType = sourceNode.data.type;
            const sourceHandle = connection.sourceHandle;

            // Count existing output connections from source node
            const existingOutputs = this.tempEdge.filter(edge =>
                edge.source === connection.source
            ).length;

            // messageInput node output validation
            if (sourceType === 'messageInput') {
                // Check if existing connections are all to AI Model nodes (parallel mode)
                const existingModelConnections = this.tempEdge.filter(edge => {
                    if (edge.source === connection.source) {
                        const target = this.tempNode.find(n => n.id === edge.target);
                        return target && target.data.type === 'model';
                    }
                    return false;
                });
                // If already has connections to model nodes (parallel mode), only allow more model connections
                if (existingModelConnections.length > 0) {
                    if (targetNode.data.type !== 'model') {
                        return {
                            isValid: false,
                            message: 'Message Input in parallel mode can only connect to AI Model nodes'
                        };
                    }
                    // Allow multiple connections to AI Models
                } else {
                    // Normal mode: only allow one connection (unless the new connection is also to AI Model)
                    if (existingOutputs >= 1 && targetNode.data.type !== 'model') {
                        return {
                            isValid: false,
                            message: 'Message Input node can only have one output connection (or multiple to AI Model nodes for parallel execution)'
                        };
                    }
                }
            }

            // Intent node - limit each handle to one connection
            if (sourceType === 'intent') {
                // Check fallback handle - allow multiple connections to AI Model nodes (parallel mode)
                if (sourceHandle === 'intent-fallback') {
                    const existingFallbackOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === 'intent-fallback'
                    );

                    // Allow multiple connections to AI Model nodes (parallel mode)
                    if (targetNode.data.type === 'model') {
                        // No restriction for connecting to AI Models
                        return { isValid: true };
                    }

                    // For non-AI Model targets, limit to one connection
                    if (existingFallbackOutputs.length >= 1) {
                        return {
                            isValid: false,
                            message: 'Intent fallback can only have one output connection (or multiple to AI Model nodes for parallel execution)'
                        };
                    }
                }
                // Check main action handles (format: {intent.id}-main)
                else if (sourceHandle && sourceHandle.endsWith('-main')) {
                    const existingMainOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    ).length;

                    // Allow multiple connections to AI Model nodes (parallel mode)
                    if (targetNode.data.type === 'model') {
                        // No restriction for connecting to AI Models
                        return { isValid: true };
                    }

                    // For non-AI Model targets, limit to one connection
                    if (existingMainOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Intent main action can only have one output connection (or multiple to AI Model nodes for parallel execution)'
                        };
                    }
                }
                // Check parallel action handles (format: {intent.id}-{parallel.id})
                else if (sourceHandle && sourceHandle !== 'output-bottom' && sourceHandle !== 'input') {
                    const existingParallelOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    ).length;

                    if (existingParallelOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Each intent action can only have one output connection'
                        };
                    }
                }
                else if (sourceHandle && sourceHandle == 'output-bottom') {
                    const existingBottomOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    ).length;

                    if (existingBottomOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Intent can only have one Intent AI connection'
                        };
                    }
                }
            }
            if (sourceType === 'smartIntent') {
                // Check fallback handle - allow multiple connections to AI Model nodes (parallel mode)
                if (sourceHandle === 'smart-intent-fallback') {
                    const existingFallbackOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === 'smart-intent-fallback'
                    );

                    // Allow multiple connections to AI Model nodes (parallel mode)
                    if (targetNode.data.type === 'model') {
                        // No restriction for connecting to AI Models
                        return { isValid: true };
                    }

                    // For non-AI Model targets, limit to one connection
                    if (existingFallbackOutputs.length >= 1) {
                        return {
                            isValid: false,
                            message: 'Smart Intent fallback can only have one output connection (or multiple to AI Model nodes for parallel execution)'
                        };
                    }
                }
                // Check main action handles (format: {smartIntent.id}-main)
                else if (sourceHandle && sourceHandle.endsWith('-main')) {
                    const existingMainOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    );

                    // Allow multiple connections to AI Model nodes (parallel mode)
                    if (targetNode.data.type === 'model') {
                        // No restriction for connecting to AI Models
                        return { isValid: true };
                    }

                    // For non-AI Model targets, limit to one connection
                    if (existingMainOutputs.length >= 1) {
                        return {
                            isValid: false,
                            message: 'Smart Intent main action can only have one output connection (or multiple to AI Model nodes for parallel execution)'
                        };
                    }
                }
                else if (sourceHandle && sourceHandle == 'output-bottom') {
                    const existingBottomOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    ).length;

                    if (existingBottomOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Smart intent can only have one Intent AI connection'
                        };
                    }
                }
            }
            if (sourceType === 'advancedRouter') {
                if (sourceHandle === 'output-bottom') {
                    const existingBottomOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === 'output-bottom'
                    ).length;
                    if (existingBottomOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Advanced Router can only have one Intent AI connection'
                        };
                    }
                }
                else if (sourceHandle && sourceHandle.startsWith('advrouter-')) {
                    const existingOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    );

                    // Allow multiple connections to AI Model nodes (parallel mode)
                    if (targetNode.data.type === 'model') {
                        return { isValid: true };
                    }

                    if (existingOutputs.length >= 1) {
                        return {
                            isValid: false,
                            message: 'Each Advanced Router output can only have one connection (or multiple to AI Model nodes for parallel execution)'
                        };
                    }
                }
            }

            // General rule for all other source handles on the right side (Position.Right)
            // This covers: model, api, condition, switch, delay, webhook, email, apiTrigger, contactAgent, etc.
            if (sourceHandle && sourceHandle !== 'output-bottom' && sourceHandle !== 'input') {
                const existingHandleOutputs = this.tempEdge.filter(edge =>
                    edge.source === connection.source && edge.sourceHandle === sourceHandle
                ).length;

                // Check specific handle types that should be limited to one connection
                const limitedHandles = [
                    'output', // Standard output handle for most nodes
                    'output-success', // Success output for some nodes
                    'output-true', // Condition true output
                    'output-false', // Condition false output
                    'output-match', // JSON Condition match output
                    'output-fallback', // JSON Condition fallback output
                    'switch-default' // Switch default output
                ];

                // Check if it's a switch output handle or other limited handles
                const isSwitchOutput = sourceHandle.startsWith('switch-output-');
                const isLimitedHandle = limitedHandles.includes(sourceHandle);

                if ((isSwitchOutput || isLimitedHandle) && existingHandleOutputs >= 1) {
                    let nodeName = this.getNodeTypeLabel(sourceType);
                    let handleName = sourceHandle;
                    
                    // Special case: Allow AI Model node to connect to multiple AI Model nodes in parallel
                    if (sourceType === 'model' && sourceHandle === 'output' && targetNode.data.type === 'model') {
                        // Allow multiple connections from model to model nodes
                        return { isValid: true };
                    }
                    
                    // Special case: Allow messageInput node to connect to multiple AI Model nodes in parallel
                    if (sourceType === 'messageInput' && sourceHandle === 'output' && targetNode.data.type === 'model') {
                        // Allow multiple connections from messageInput to model nodes
                        return { isValid: true };
                    }
                    
                    // Special case: Allow Intent main action handles to connect to multiple AI Model nodes in parallel
                    if (sourceType === 'intent' && sourceHandle && sourceHandle.endsWith('-main') && targetNode.data.type === 'model') {
                        // Allow multiple connections from intent main action to model nodes
                        return { isValid: true };
                    }
                    
                    // Special case: Allow Intent fallback handle to connect to multiple AI Model nodes in parallel
                    if (sourceType === 'intent' && sourceHandle === 'intent-fallback' && targetNode.data.type === 'model') {
                        // Allow multiple connections from intent fallback to model nodes
                        return { isValid: true };
                    }
                    
                    // Special case: Allow Smart Intent main action handles to connect to multiple AI Model nodes in parallel
                    if (sourceType === 'smartIntent' && sourceHandle && sourceHandle.endsWith('-main') && targetNode.data.type === 'model') {
                        // Allow multiple connections from smart intent main action to model nodes
                        return { isValid: true };
                    }
                    
                    // Special case: Allow Smart Intent fallback handle to connect to multiple AI Model nodes in parallel
                    if (sourceType === 'smartIntent' && sourceHandle === 'smart-intent-fallback' && targetNode.data.type === 'model') {
                        // Allow multiple connections from smart intent fallback to model nodes
                        return { isValid: true };
                    }
                    
                    if (isSwitchOutput) {
                        handleName = 'switch output';
                    } else if (sourceHandle === 'output-true') {
                        handleName = 'true output';
                    } else if (sourceHandle === 'output-false') {
                        handleName = 'false output';
                    } else if (sourceHandle === 'switch-default') {
                        handleName = 'default output';
                    } else if (sourceHandle === 'output' || sourceHandle === 'output-success') {
                        handleName = 'output';
                    }

                    return {
                        isValid: false,
                        message: `${nodeName} ${handleName} can only have one output connection`
                    };
                }
            }

            return { isValid: true };
        },
        // Helper function to apply default values to a node based on its type
        applyNodeDefaults(node) {
            const defaultBasePrompt = `Find the Intent from the message of users.
#Condition for Response
1. If the user asks a question related to or similar in meaning to the #Group Intent below. You must response 1. Otherwise, 0`;

            // Apply comprehensive defaults based on openNodeInspector logic
            node.data = {
                ...node.data,
                // Common properties
                color: node.data.color || '#1976D2',
                placeholderText: node.data.placeholderText || '',
                required: node.data.required || false,
                selectedModel: Array.isArray(node.data.selectedModel)
                    ? node.data.selectedModel
                    : (node.data.selectedModel ? [node.data.selectedModel] : []),
                systemPrompt: node.data.systemPrompt || '',
                temperature: node.data.temperature || null,
                maxTokens: node.data.maxTokens || null,
                
                // AI Model Configuration properties
                provider: node.data.provider || null,
                model: node.data.model || null,
                useBaseModelPrompt: node.data.useBaseModelPrompt !== undefined ? node.data.useBaseModelPrompt : false,
                orchestratorName: node.data.orchestratorName || 'New Orchestrator',
                confidenceThreshold: node.data.confidenceThreshold !== undefined ? node.data.confidenceThreshold : 0.7,
                enableMultiAgent: node.data.enableMultiAgent !== undefined ? node.data.enableMultiAgent : false,
                multiAgentStrategy: node.data.multiAgentStrategy || 'pipeline',
                enableCache: node.data.enableCache !== undefined ? node.data.enableCache : false,
                cacheTTLMinutes: node.data.cacheTTLMinutes || 1440,
                cacheScope: node.data.cacheScope || 'flow',
                enableSemanticCache: node.data.enableSemanticCache !== undefined ? node.data.enableSemanticCache : false,
                similarityThreshold: node.data.similarityThreshold !== undefined ? node.data.similarityThreshold : 0.95,
                enableTransactionId: node.data.enableTransactionId !== undefined ? node.data.enableTransactionId : false,
                enableInputMessageReplace: node.data.enableInputMessageReplace !== undefined ? node.data.enableInputMessageReplace : false,
                enableVoicePromptReplace: node.data.enableVoicePromptReplace !== undefined ? node.data.enableVoicePromptReplace : false,
                enableLanguageReplace: node.data.enableLanguageReplace !== undefined ? node.data.enableLanguageReplace : false,
                gender: node.data.gender || 'male',
                typingSpeed: node.data.typingSpeed || null,
                maxInputLength: node.data.maxInputLength || 200000,
                agentName: node.data.agentName || 'New Agent',
                agentDescription: node.data.agentDescription || '',
                personaPrompt: node.data.personaPrompt || '',
                selectedTools: Array.isArray(node.data.selectedTools) ? node.data.selectedTools : [],
                enableAgentToolDecision: node.data.enableAgentToolDecision !== undefined ? node.data.enableAgentToolDecision : false,
                useCustomAIModel: node.data.useCustomAIModel !== undefined ? node.data.useCustomAIModel : false,
                agentProvider: node.data.agentProvider || null,
                agentModel: node.data.agentModel || null,
                agentTemperature: node.data.agentTemperature !== undefined ? node.data.agentTemperature : 0,
                agentMaxTokens: node.data.agentMaxTokens || null,
                agentTopP: node.data.agentTopP !== undefined ? node.data.agentTopP : 1,
                agentFrequencyPenalty: node.data.agentFrequencyPenalty || null,
                agentPresencePenalty: node.data.agentPresencePenalty || null,
                agentStopSequence: node.data.agentStopSequence || null,
                agentStreamResponse: node.data.agentStreamResponse !== undefined ? node.data.agentStreamResponse : false,
                agentJsonResponse: node.data.agentJsonResponse !== undefined ? node.data.agentJsonResponse : false,
                agentGender: node.data.agentGender || 'male',
                agentTypingSpeed: node.data.agentTypingSpeed || null,
                agentMaxInputLength: node.data.agentMaxInputLength || 200000,
                agentMemoryTurn: node.data.agentMemoryTurn || null,
                agentMemoryDuration: node.data.agentMemoryDuration || null,
                agentShortTermMemory: node.data.agentShortTermMemory || 200,
                agentLongTermMemory: node.data.agentLongTermMemory !== undefined ? node.data.agentLongTermMemory : false,
                agentEnableTime: node.data.agentEnableTime !== undefined ? node.data.agentEnableTime : false,
                agentTimeZone: node.data.agentTimeZone || null,
                agentEnableTransactionId: node.data.agentEnableTransactionId !== undefined ? node.data.agentEnableTransactionId : false,
                knowledgeConnections: {
                    ...this.createDefaultKnowledgeConnections(),
                    ...(node.data.knowledgeConnections || {})
                },
                masterToolId: node.data.masterToolId || null,
                toolName: node.data.toolName || 'New Tool',
                toolDescription: node.data.toolDescription || '',
                toolType: node.data.toolType || 'master',
                endpoint: node.data.endpoint || '',
                method: node.data.method || 'GET',
                body: node.data.body || '',
                textContent: node.data.textContent || '',
                flowToolName: node.data.flowToolName || 'New Flow Tool',
                flowToolDescription: node.data.flowToolDescription || '',
                internalNodes: Array.isArray(node.data.internalNodes) ? node.data.internalNodes : [],
                internalEdges: Array.isArray(node.data.internalEdges) ? node.data.internalEdges : [],
                internalNodeCount: node.data.internalNodeCount || this.getInternalProcessingNodeCount(node.data.internalNodes || []),
                showWarning: node.data.showWarning !== undefined ? node.data.showWarning : node.data.type === 'flowTool',
                
                // Parallel execution settings
                isParallel: node.data.isParallel !== undefined ? node.data.isParallel : false,
                
                gender: node.data.gender || 'male',
                typingSpeed: node.data.typingSpeed || null,
                maxInputLength: node.data.maxInputLength || 200000,
                streamResponse: node.data.streamResponse !== undefined ? node.data.streamResponse : false,
                jsonResponse: node.data.jsonResponse !== undefined ? node.data.jsonResponse : false,
                enableTime: node.data.enableTime || null,
                timeZone: node.data.timeZone || null,
                memoryTurn: node.data.memoryTurn || null,
                memoryDuration: node.data.memoryDuration || null,
                shortTermMemory: node.data.shortTermMemory || null,
                longTermMemory: node.data.longTermMemory !== undefined ? node.data.longTermMemory : false,
                isChainMassage: node.data.isChainMassage !== undefined ? node.data.isChainMassage : false,
                lenChainMassage: node.data.lenChainMassage || null,
                timeSecondChainMassage: node.data.timeSecondChainMassage || null,
                topP: node.data.topP || null,
                frequencyPenalty: node.data.frequencyPenalty || null,
                presencePenalty: node.data.presencePenalty || null,
                stopSequence: node.data.stopSequence || null,
                reasoningEffort: node.data.reasoningEffort || null,
                intentaiReasoningEffort: node.data.intentaiReasoningEffort || null,
                loadingBar: node.data.loadingBar !== undefined ? node.data.loadingBar : false,
                loadingBarDuration: node.data.loadingBarDuration || 0,
                monitorLineUserId: node.data.monitorLineUserId || null,
                monitorSocialID: node.data.monitorSocialID || null,
                showLineName: node.data.showLineName !== undefined ? node.data.showLineName : false,
                forbiddenWords: node.data.forbiddenWords || [''],
                
                // Intent specific defaults
                intentGroupName: node.data.intentGroupName || '',
                examples: node.data.examples || '',
                
                // Intent AI properties with comprehensive defaults
                intentaiModel: Array.isArray(node.data.intentaiModel)
                    ? (node.data.intentaiModel.length > 0 ? node.data.intentaiModel[0] : null)
                    : (node.data.intentaiModel || (Array.isArray(node.data.intentModel) ? (node.data.intentModel.length > 0 ? node.data.intentModel[0] : null) : node.data.intentModel) || null),
                intentaiProvider: node.data.intentaiProvider || node.data.provider || '',
                intentaiGender: node.data.intentaiGender || node.data.gender || 'male',
                intentaiTypingSpeed: node.data.intentaiTypingSpeed || node.data.typingSpeed || 0,
                intentaiMaxInputLength: node.data.intentaiMaxInputLength || node.data.maxInputLength || 4000,
                intentaiStreamResponse: node.data.intentaiStreamResponse !== undefined ? node.data.intentaiStreamResponse : (node.data.streamResponse !== undefined ? node.data.streamResponse : true),
                intentaiJsonResponse: node.data.intentaiJsonResponse !== undefined ? node.data.intentaiJsonResponse : (node.data.jsonResponse !== undefined ? node.data.jsonResponse : false),
                intentaiEnableTime: node.data.intentaiEnableTime !== undefined ? node.data.intentaiEnableTime : (node.data.enableTime !== undefined ? node.data.enableTime : false),
                intentaiTimeZone: node.data.intentaiTimeZone || node.data.timeZone || '',
                intentaiMemoryTurn: node.data.intentaiMemoryTurn || node.data.memoryTurn || 5,
                intentaiMemoryDuration: node.data.intentaiMemoryDuration || node.data.memoryDuration || 60,
                intentaiShortTermMemory: node.data.intentaiShortTermMemory || node.data.shortTermMemory || 3,
                intentaiLongTermMemory: node.data.intentaiLongTermMemory !== undefined ? node.data.intentaiLongTermMemory : (node.data.longTermMemory !== undefined ? node.data.longTermMemory : false),
                intentaiIsChainMassage: node.data.intentaiIsChainMassage !== undefined ? node.data.intentaiIsChainMassage : (node.data.isChainMassage !== undefined ? node.data.isChainMassage : false),
                intentaiLenChainMassage: node.data.intentaiLenChainMassage || node.data.lenChainMassage || 5,
                intentaiTimeSecondChainMassage: node.data.intentaiTimeSecondChainMassage || node.data.timeSecondChainMassage || 30,
                intentaiTemperature: node.data.intentaiTemperature || node.data.temperature || 0.7,
                intentaiTopP: node.data.intentaiTopP || node.data.topP || 1.0,
                intentaiFrequencyPenalty: node.data.intentaiFrequencyPenalty || node.data.frequencyPenalty || 0,
                intentaiPresencePenalty: node.data.intentaiPresencePenalty || node.data.presencePenalty || 0,
                intentaiMaxTokens: node.data.intentaiMaxTokens || node.data.maxTokens || 1000,
                intentaiSystemPrompt: node.data.intentaiSystemPrompt || node.data.intentSystemPrompt || '',
                intentaiExamples: node.data.intentaiExamples || node.data.intentExamples || '',
                intentaiConfidence: node.data.intentaiConfidence || node.data.intentConfidence || 75,
                intentaiFallbackIntent: node.data.intentaiFallbackIntent || node.data.fallbackIntent || 'unknown',
                intentaiShowLineName: node.data.intentaiShowLineName !== undefined ? node.data.intentaiShowLineName : (node.data.showLineName !== undefined ? node.data.showLineName : false),
                intentaiLoadingBar: node.data.intentaiLoadingBar !== undefined ? node.data.intentaiLoadingBar : (node.data.loadingBar !== undefined ? node.data.loadingBar : false),
                intentaiLoadingBarDuration: node.data.intentaiLoadingBarDuration || node.data.loadingBarDuration || 0,
                intentaiMonitorLineUserId: node.data.intentaiMonitorLineUserId || node.data.monitorLineUserId || null,
                intentaiMonitorSocialID: node.data.intentaiMonitorSocialID || node.data.monitorSocialID || null,
                intentaiForbiddenWords: node.data.intentaiForbiddenWords || node.data.forbiddenWords || [''],
                
                // Guardrail AI properties
                guardrailType: node.data.guardrailType || 'content_safety',
                guardrailModel: node.data.guardrailModel || 'gpt-4',
                guardrailPrompt: node.data.guardrailPrompt || '',
                safetyThreshold: node.data.safetyThreshold || 80,
                enableLogging: node.data.enableLogging !== undefined ? node.data.enableLogging : true,
                
                // API Trigger properties
                apiUrl: node.data.apiUrl || '',
                httpMethod: node.data.httpMethod || 'POST',
                headers: node.data.type === 'tool' ? (Array.isArray(node.data.headers) ? node.data.headers : []) : (node.data.headers || ''),
                textResponse: node.data.textResponse || '',
                jsonValidate: node.data.jsonValidate || '{\n   "Name":"Value"\n}',
                modelJsonValidator: node.data.modelJsonValidator || null,
                listHeadersAPITrigger: node.data.listHeadersAPITrigger || [{ key: '', value: '' }],
                listValidation: node.data.listValidation || [{ key: '', typeValidation: '', numberOfCharacter: '', regExp: '', promptMessage: '' }],
                
                // Contact Agent properties
                departmentName: node.data.departmentName || '',
                contactAgentMessage: node.data.contactAgentMessage || '',
                handoffMessage: node.data.handoffMessage || '',
                botDisableTime: node.data.botDisableTime || null,
                disableBot: node.data.disableBot !== undefined ? node.data.disableBot : true,
                
                // Switch properties
                switchName: node.data.switchName || '',
                
                // JSON Condition properties
                jsonConditionName: node.data.jsonConditionName || 'New JSON Condition',
                matchMode: node.data.matchMode || 'all',
                conditions: Array.isArray(node.data.conditions) ? node.data.conditions : [],
                matchLabel: node.data.matchLabel || 'Match',
                fallbackLabel: node.data.fallbackLabel || 'Fallback',

                // Condition properties
                conditionType: node.data.conditionType || 'value_comparison',
                leftOperand: node.data.leftOperand || '',
                operator: node.data.operator || 'equals',
                rightOperand: node.data.rightOperand || '',
                conditionExpression: node.data.conditionExpression || '',
                trueLabel: node.data.trueLabel || 'True',
                falseLabel: node.data.falseLabel || 'False',
                minValue: node.data.minValue || 0,
                maxValue: node.data.maxValue || 100,
                expectedValue: node.data.expectedValue !== undefined ? node.data.expectedValue : true,
                
                // Delay properties
                delayType: node.data.delayType || 'fixed',
                delayValue: node.data.delayValue || 5,
                delayUnit: node.data.delayUnit || 'seconds',
                randomMin: node.data.randomMin || 1,
                randomMax: node.data.randomMax || 10,
                delayMessage: node.data.delayMessage || '',
                showProgress: node.data.showProgress !== undefined ? node.data.showProgress : true,
                delayExpression: node.data.delayExpression || '',
                
                // Documents properties
                document: node.data.document || [],
                selectedDocumentData: node.data.selectedDocumentData || [],
                enableSpecificDataSelection: node.data.enableSpecificDataSelection !== undefined ? node.data.enableSpecificDataSelection : false,
                documentVectorName: node.data.documentVectorName || '',
                textReplacement: node.data.textReplacement || [],
                topNChunks: node.data.topNChunks || 5,
                cosineSimilarity: node.data.cosineSimilarity || 0.4,
                
                // Database Integration properties
                tableName: node.data.tableName || null,
                sqlExtractionModel: node.data.sqlExtractionModel || null,
                promptSqlNoData: node.data.promptSqlNoData || '',

                // Knowledge Graph / Waterfall Search properties (S15-P1) — สร้าง object/array ใหม่เสมอ:
                // onDrop สร้าง node ด้วย { ...nodeTemplate.data } (shallow) — ถ้าไม่ normalize ตรงนี้ node 2 ตัวจาก palette จะแชร์ kgRetrievalConfig / graphTiers ก้อนเดียวกัน
                knowledgeGraphIds: Array.isArray(node.data.knowledgeGraphIds) ? [...node.data.knowledgeGraphIds] : [],
                kgRetrievalConfig: normalizeKgRetrievalConfig(node.data.kgRetrievalConfig),
                graphTiers: (Array.isArray(node.data.graphTiers) ? node.data.graphTiers : []).map(t => ({ ...normalizeWaterfallTier(t), _key: t?._key || this.generateGuid() })),

                // Products properties
                productGroupList: node.data.productGroupList || [],
                productTopNChunks: node.data.productTopNChunks || 5,
                productCosineSimilarity: node.data.productCosineSimilarity || 0.4,
                
                // Knowledge API properties
                knowledgeApiUrl: node.data.knowledgeApiUrl || '',
                knowledgeHttpMethod: node.data.knowledgeHttpMethod || 'GET',
                listHeaders: node.data.listHeaders || [{ key: '', value: '' }],
                listParams: node.data.listParams || [],
                knowledgeRequestBody: this.methodAllowsRequestBody(node.data.knowledgeHttpMethod || 'GET') ? (node.data.knowledgeRequestBody || '') : '',
                knowledgeModelJsonValidator: node.data.knowledgeModelJsonValidator || null,
                responseDataPath: node.data.responseDataPath || '',
                
                // SharePoint properties
                sharepoint: node.data.sharepoint || {
                    siteUrl: '',
                    tenantId: '',
                    clientId: '',
                    clientSecret: '',
                    folderPath: '',
                    filename: ''
                },
                
                // Email properties
                emailProvider: node.data.emailProvider || 'mailgun',
                toEmail: node.data.toEmail || '',
                ccEmails: node.data.ccEmails || [],
                bccEmails: node.data.bccEmails || [],
                subject: node.data.subject || '',
                emailTemplate: node.data.emailTemplate || 'custom',
                emailBody: node.data.emailBody || '',
                isHtml: node.data.isHtml !== undefined ? node.data.isHtml : false,
                useCentralConfig: node.data.useCentralConfig !== undefined ? node.data.useCentralConfig : true,
                mailConfig: this.createEmptyMailConfig(node.data.mailConfig),
                showMailConfigSecret: {
                    password: false,
                    smtpOAuth2TenantId: false,
                    smtpOAuth2ClientId: false,
                    smtpOAuth2ClientSecret: false,
                    mailgunApiKey: false,
                },
                
                // Merge properties
                mergeStrategy: node.data.mergeStrategy || 'concatenate',
                mergePrompt: node.data.mergePrompt || '',
                mergeSeparator: node.data.mergeSeparator || 'hr',
                mergeCustomSeparator: node.data.mergeCustomSeparator || '',
                mergeIncludeLabels: node.data.mergeIncludeLabels !== undefined ? node.data.mergeIncludeLabels : true,
                mergeLabelFormat: node.data.mergeLabelFormat || '',
                mergeOutputFormat: node.data.mergeOutputFormat || 'text',
                summaryModel: node.data.summaryModel || null,
                summaryPrompt: node.data.summaryPrompt || 'Summarize the following AI responses into a single coherent response:\n\n{responses}'
            };

            // Type-specific defaults
            if (node.data.type === 'intent') {
                if (!node.data.intents || node.data.intents.length === 0) {
                    node.data.intents = [
                        {
                            id: `intent-${this.generateGuid()}`,
                            name: 'Intent Name',
                            priority: 0,
                            prompt: '',
                            basePrompt: defaultBasePrompt,
                            enableBasePrompt: false,
                            messages: [
                                { id: `msg-${this.generateGuid()}`, text: '' }
                            ],
                            mainAction: { id: `main-${this.generateGuid()}`, label: 'Main Action' },
                            parallelActions: []
                        }
                    ];
                }
                if (!node.data.actions || node.data.actions.length === 0) {
                    node.data.actions = [
                        {
                            id: `action-${this.generateGuid()}`,
                            label: 'Action 1',
                            parallels: [
                                { id: `parallel-${this.generateGuid()}`, label: 'Parallel Action 1' }
                            ]
                        }
                    ];
                }
                node.data.outputs = node.data.outputs || [];
            }

            if (node.data.type === 'smartIntent') {
                if (!node.data.smartIntents || node.data.smartIntents.length === 0) {
                    node.data.smartIntents = [
                        {
                            id: `smart-intent-${this.generateGuid()}`,
                            name: 'Smart Intent Name',
                            priority: 0,
                            prompt: '',
                            basePrompt: defaultBasePrompt,
                            enableBasePrompt: false,
                            useMemory: false,
                            messages: [
                                { id: `msg-${this.generateGuid()}`, text: '' }
                            ],
                            mainAction: {
                                id: `main-${this.generateGuid()}`,
                                label: 'Action',
                                useAI: true,
                                provider: null,
                                model: null,
                                gender: 'male',
                                typingSpeed: null,
                                maxInputLength: 200000,
                                streamResponse: false,
                                jsonResponse: false,
                                enableTime: null,
                                timeZone: null,
                                temperature: null,
                                topP: null,
                                frequencyPenalty: null,
                                presencePenalty: null,
                                maxTokens: null,
                                stopSequence: null,
                                memoryTurn: null,
                                memoryDuration: null,
                                shortTermMemory: null,
                                longTermMemory: false,
                                isChainMassage: false,
                                lenChainMassage: null,
                                timeSecondChainMassage: null,
                                forbiddenWords: [''],
                                // Knowledge Configuration
                                knowledgeType: '',
                                // Documents properties
                                document: [],
                                topNChunks: 5,
                                cosineSimilarity: 0.4,
                                // Database Integration properties
                                tableName: null,
                                sqlExtractionModel: null,
                                promptSqlNoData: '',
                                // Products properties
                                productGroupList: [],
                                productTopNChunks: 5,
                                productCosineSimilarity: 0.4,
                                // API Knowledge properties
                                knowledgeApiUrl: '',
                                knowledgeHttpMethod: 'GET',
                                listParams: [],
                                knowledgeResponseDataPath: ''
                            }
                        }
                    ];
                }
                if (!node.data.smartIntentFallback) {
                    node.data.smartIntentFallback = {
                        useAI: false,
                        provider: null,
                        model: null,
                        gender: 'male',
                        typingSpeed: null,
                        maxInputLength: 200000,
                        streamResponse: false,
                        jsonResponse: false,
                        enableTime: null,
                        timeZone: null,
                        temperature: null,
                        topP: null,
                        frequencyPenalty: null,
                        presencePenalty: null,
                        maxTokens: null,
                        stopSequence: null,
                        memoryTurn: null,
                        memoryDuration: null,
                        shortTermMemory: null,
                        longTermMemory: false,
                        isChainMassage: false,
                        lenChainMassage: null,
                        timeSecondChainMassage: null,
                        forbiddenWords: ['']
                    };
                }
                node.data.smartIntentGroupName = node.data.smartIntentGroupName || '';
            }

            if (node.data.type === 'switch') {
                if (!node.data.switchOutputs || node.data.switchOutputs.length === 0) {
                    node.data.switchOutputs = [
                        {
                            id: `switch-output-${this.generateGuid()}`,
                            name: 'Output 1',
                            condition: ''
                        },
                        {
                            id: `switch-output-${this.generateGuid()}`,
                            name: 'Output 2',
                            condition: ''
                        }
                    ];
                }
                node.data.switchName = node.data.switchName || 'Multi-Path';
            }

            if (node.data.type === 'jsonCondition') {
                node.data.jsonConditionName = node.data.jsonConditionName || 'New JSON Condition';
                node.data.matchMode = node.data.matchMode || 'all';
                node.data.conditions = Array.isArray(node.data.conditions) ? node.data.conditions : [];
                node.data.matchLabel = node.data.matchLabel || 'Match';
                node.data.fallbackLabel = node.data.fallbackLabel || 'Fallback';
            }

            return node;
        },

        // Calculate center position of current viewport
        getCenterPosition() {
            try {
                // Get the VueFlow instance
                const vueFlow = this.$refs.vueFlow;
                if (!vueFlow) {
                    console.warn('VueFlow ref not found, using fallback position');
                    return { x: 400, y: 200 }; // Fallback
                }

                // Try multiple methods to get viewport information
                let viewport, dimensions;
                
                // Method 1: Direct access to VueFlow methods
                if (typeof vueFlow.getViewport === 'function') {
                    viewport = vueFlow.getViewport();
                }
                
                if (typeof vueFlow.getDimensions === 'function') {
                    dimensions = vueFlow.getDimensions();
                }

                // Method 2: Access through VueFlow store/state if direct methods don't work
                if (!viewport && vueFlow.viewport) {
                    viewport = vueFlow.viewport;
                }

                if (!dimensions && vueFlow.dimensions) {
                    dimensions = vueFlow.dimensions;
                }

                // Method 3: Access through VueFlow internal state
                if (!viewport || !dimensions) {
                    const flowContainer = vueFlow.$el || vueFlow.$refs?.wrapper || document.querySelector('.vue-flow');
                    if (flowContainer) {
                        const rect = flowContainer.getBoundingClientRect();
                        dimensions = dimensions || { width: rect.width, height: rect.height };
                        
                        // If we still don't have viewport, try to get it from the flow
                        if (!viewport) {
                            // Get transform from the flow viewport
                            const viewportElement = flowContainer.querySelector('.vue-flow__viewport');
                            if (viewportElement) {
                                const transform = viewportElement.style.transform;
                                const match = transform.match(/translate\(([^)]+)\) scale\(([^)]+)\)/);
                                if (match) {
                                    const [, translate, scale] = match;
                                    const [x, y] = translate.split(',').map(v => parseFloat(v.replace('px', '')));
                                    viewport = { x, y, zoom: parseFloat(scale) };
                                }
                            }
                        }
                    }
                }

                // If we still don't have complete viewport info, use fallback
                if (!viewport || !dimensions) {
                    return { x: 400, y: 200 };
                }

                
                // Calculate center position in flow coordinates
                const centerX = (-viewport.x + dimensions.width / 2) / viewport.zoom;
                const centerY = (-viewport.y + dimensions.height / 2) / viewport.zoom;
                
                // Default node size (approximate)
                const nodeWidth = 200;  // Average node width
                const nodeHeight = 100; // Average node height
                
                // Adjust position so that center of node is at calculated center
                const adjustedX = centerX - (nodeWidth / 2);
                const adjustedY = centerY - (nodeHeight / 2);
                
                return { x: adjustedX, y: adjustedY };
            } catch (error) {
                return { x: 400, y: 200 }; // Fallback
            }
        },

        // New methods for node management
        addNewNode(nodeData) {
            if (this.isDebugMode) return;
            // Check if trying to add messageInput node when one already exists
            if (nodeData.data.type === 'messageInput') {
                const existingMessageInput = this.tempNode.find(node => node.data.type === 'messageInput');
                if (existingMessageInput) {
                    this.showError(this.$t('_messageInputExists'));
                    return;
                }
            }

            const newNode = {
                ...nodeData,
                id: this.generateGuid(),
                position: this.getCenterPosition() // Use dynamic center position
            };

            // Apply comprehensive defaults
            this.applyNodeDefaults(newNode);

            this.tempNode.push(newNode);

            // Save state to history
            this.saveStateToHistory(`Add ${nodeData.data.label} Node`);

            this.$nextTick(() => {
                const getTempNode = this.tempNode.find(node => node.id === newNode.id);
                if (getTempNode) {
                    this.checkNodeValidation(getTempNode);
                    
                    // Keep selection state in sync with the same inspector initialization path used for node clicks.
                    this.selectedNodes = [getTempNode];
                    this.openNodeInspector(getTempNode);
                    
                    // Trigger updateNodeProperty for critical properties to ensure proper initialization
                    // This ensures the node data is properly registered and validated
                    if (getTempNode.data.type === 'model' || getTempNode.data.type === 'intentAI') {
                        if (getTempNode.data.selectedModel) {
                            this.updateNodeProperty('selectedModel', getTempNode.data.selectedModel);
                        }
                    }
                }
                
                // Focus and select the new node
                this.focusAndSelectNode(newNode.id);
            });
            // Show success message
            this.showSuccess(this.$t('_addedToFlow', { label: nodeData.data.label }));

        },
        // Helper function to focus and select a newly added node
        focusAndSelectNode(nodeId) {
            this.$nextTick(() => {
                // Find the new node
                const newNode = this.tempNode.find(node => node.id === nodeId);
                if (newNode) {
                    // First, clear all existing selections by setting selected to false
                    this.tempNode.forEach(node => {
                        if (node.selected) {
                            node.selected = false;
                        }
                    });

                    // Clear edges selection too
                    this.tempEdge.forEach(edge => {
                        if (edge.selected) {
                            edge.selected = false;
                        }
                    });

                    // Force update the arrays to trigger reactivity
                    this.tempNode = [...this.tempNode];
                    this.tempEdge = [...this.tempEdge];

                    // Wait a bit then properly select the node
                    setTimeout(() => {
                        // Find the node again after array update
                        const nodeToSelect = this.tempNode.find(node => node.id === nodeId);
                        if (nodeToSelect) {
                            // Set the node as selected
                            nodeToSelect.selected = true;
                            
                            // Update the selection state
                            this.selectedNode = nodeToSelect;
                            this.selectedNodes = [nodeToSelect];
                            
                            // Force another reactivity update to trigger visual selection
                            this.tempNode = [...this.tempNode];
                            
                            // Trigger the selection change event manually to ensure VueFlow updates
                            this.$nextTick(() => {
                                // Call onNodeClick to ensure all selection logic is properly executed
                                this.onNodeClick({ node: nodeToSelect });
                                
                                // Focus the view on the new node with smooth animation
                                setTimeout(() => {
                                    if (this.$refs.vueFlow) {
                                        try {
                                            this.$refs.vueFlow.fitView({
                                                nodes: [nodeToSelect],
                                                padding: 0.3,
                                                duration: 500,
                                                minZoom: 0.8,
                                                maxZoom: 1.5
                                            });
                                        } catch (error) {
                                            console.warn('Failed to focus on node:', error);
                                        }
                                    }
                                }, 100);
                            });
                        }
                    }, 50);
                }
            });
        },
        handleDeleteNode(nodeId) {
            if (this.isDebugMode) return;
            const nodeToDelete = this.tempNode.find(node => node.id === nodeId);
            const nodeName = nodeToDelete ? nodeToDelete.data.label || 'Node' : 'Node';

            // Find all edges that will be removed
            const edgesToRemove = this.tempEdge.filter(edge =>
                edge.source === nodeId || edge.target === nodeId
            );

            // Find all affected nodes (nodes that can be parallel parents and Merge nodes)
            const affectedParentNodes = new Set();
            const affectedMergeNodes = new Set();
            const affectedAgentToolDecisionNodeIds = new Set();
            
            edgesToRemove.forEach(edge => {
                const agentNodeId = this.getAgentToolDecisionOwnerIdFromEdge(edge);
                if (agentNodeId) {
                    affectedAgentToolDecisionNodeIds.add(agentNodeId);
                }

                // Check if source is parallel parent (and not being deleted)
                if (edge.source !== nodeId) {
                    const sourceNode = this.tempNode.find(n => n.id === edge.source);
                    if (sourceNode && this.canBeParallelParent(sourceNode)) {
                        affectedParentNodes.add(edge.source);
                    }
                }
                
                // Check if target is Merge node (and not being deleted)
                if (edge.target !== nodeId) {
                    const targetNode = this.tempNode.find(n => n.id === edge.target);
                    if (targetNode && targetNode.data.type === 'merge') {
                        affectedMergeNodes.add(edge.target);
                    }
                }
            });

            // Remove the node
            this.tempNode = this.tempNode.filter(node => node.id !== nodeId);

            // Remove all edges connected to this node
            this.tempEdge = this.tempEdge.filter(edge =>
                edge.source !== nodeId && edge.target !== nodeId
            );

            if (affectedAgentToolDecisionNodeIds.size > 0) {
                this.syncAgentToolDecisionAvailability(Array.from(affectedAgentToolDecisionNodeIds));
            }

            // Check each affected parent node to update parallel mode
            affectedParentNodes.forEach(parentNodeId => {
                this.checkAndUpdateParallelMode(parentNodeId);
            });

            // Trigger validation for affected Merge nodes
            if (affectedMergeNodes.size > 0) {
                this.$nextTick(() => {
                    this.checkAffectedNodesValidation(Array.from(affectedMergeNodes));
                });
            }

            // Save state to history
            this.saveStateToHistory(`Delete ${nodeName}`);

            // Show success message
            this.showSuccess(this.$t('_nodeDeleted'));

            // Close inspector panel if the deleted node was selected
            if (this.selectedNode && this.selectedNode.id === nodeId) {
                this.selectedNodes = [];
                this.selectedNode = null;
                this.closeInspector();
            }
        },
        handleKeyDown(event) {
            if (this.isDebugMode) return;
            // Check for Undo shortcut (Ctrl+Z)
            if (event.ctrlKey && event.code === 'KeyZ' && !event.shiftKey && !this.isViewMode) {
                event.preventDefault();
                this.undo();
            }
            // Check for Redo shortcuts (Ctrl+Y or Ctrl+Shift+Z)
            else if ((event.ctrlKey && event.code === 'KeyY') || 
                     (event.ctrlKey && event.shiftKey && event.code === 'KeyZ') && !this.isViewMode) {
                event.preventDefault();
                this.redo();
            }
            // Check for Copy shortcut (Ctrl+C)
            else if (event.ctrlKey && event.code === 'KeyC' && !this.isViewMode && this.selectedNodes.length > 0) {
                event.preventDefault();
                this.handleCopySelectedNodes();
            }
            // Check for Paste shortcut (Ctrl+V)
            else if (event.ctrlKey && event.code === 'KeyV' && !this.isViewMode && this.copiedNodes.length > 0) {
                event.preventDefault();
                this.handlePasteNodes();
            }
            // Check if Delete key is pressed and we're not in view mode
            else if (event.key === 'Delete' && !this.isViewMode && this.selectedNodes.length > 0) {
                event.preventDefault();
                this.handleDeleteSelectedNodes();
            }
            // Check if Ctrl+D is pressed for duplication (using event.code for keyboard layout independence)
            else if (event.ctrlKey && event.code === 'KeyD' && !this.isViewMode && this.selectedNodes.length > 0) {
                event.preventDefault();
                this.handleDuplicateSelectedNodes();
            }
            // Alternative: Also support Ctrl+Shift+D for duplication (fallback)
            else if (event.ctrlKey && event.shiftKey && event.code === 'KeyD' && !this.isViewMode && this.selectedNodes.length > 0) {
                event.preventDefault();
                this.handleDuplicateSelectedNodes();
            }
            // Fallback: Use event.key for older browsers or special cases
            else if (event.ctrlKey && event.key.toLowerCase() === 'd' && !this.isViewMode && this.selectedNodes.length > 0) {
                event.preventDefault();
                this.handleDuplicateSelectedNodes();
            }
        },
        focusFlowContainer() {
            // Ensure the flow container can receive keyboard events
            this.$nextTick(() => {
                if (this.$refs.flowContainer) {
                    this.$refs.flowContainer.focus();
                }
            });
        },
        handleDeleteSelectedNodes() {
            if (this.selectedNodes.length === 0) {
                return;
            }

            // Collect all node IDs to delete
            const nodeIdsToDelete = this.selectedNodes.map(node => node.id);
            const nodeCount = nodeIdsToDelete.length;
            
            // Find all edges that will be removed
            const edgesToRemove = this.tempEdge.filter(edge =>
                nodeIdsToDelete.includes(edge.source) || nodeIdsToDelete.includes(edge.target)
            );

            // Find all affected nodes (nodes that can be parallel parents and Merge nodes)
            const affectedParentNodes = new Set();
            const affectedMergeNodes = new Set();
            
            edgesToRemove.forEach(edge => {
                // Check source node (if it's not being deleted)
                if (!nodeIdsToDelete.includes(edge.source)) {
                    const sourceNode = this.tempNode.find(n => n.id === edge.source);
                    if (sourceNode && this.canBeParallelParent(sourceNode)) {
                        affectedParentNodes.add(edge.source);
                    }
                }
                
                // Check target node (if it's not being deleted)
                if (!nodeIdsToDelete.includes(edge.target)) {
                    const targetNode = this.tempNode.find(n => n.id === edge.target);
                    if (targetNode && targetNode.data.type === 'merge') {
                        affectedMergeNodes.add(edge.target);
                    }
                }
            });

            // Remove all selected nodes
            this.tempNode = this.tempNode.filter(node => !nodeIdsToDelete.includes(node.id));

            // Remove all edges connected to any of the deleted nodes
            this.tempEdge = this.tempEdge.filter(edge =>
                !nodeIdsToDelete.includes(edge.source) && !nodeIdsToDelete.includes(edge.target)
            );

            // Check each affected parent node to update parallel mode
            affectedParentNodes.forEach(parentNodeId => {
                this.checkAndUpdateParallelMode(parentNodeId);
            });

            // Trigger validation for affected Merge nodes
            if (affectedMergeNodes.size > 0) {
                this.$nextTick(() => {
                    this.checkAffectedNodesValidation(Array.from(affectedMergeNodes));
                });
            }

            // Save state to history
            this.saveStateToHistory(`Delete ${nodeCount} Node${nodeCount > 1 ? 's' : ''}`);

            // Show success message
            this.showSuccess(this.$t('_nodesDeleted', { n: nodeCount }));

            // Clear selection and close inspector
            this.selectedNodes = [];
            this.selectedNode = null;
            this.closeInspector();
        },
        handleDuplicateSelectedNodes() {
            if (this.selectedNodes.length === 0) {
                return;
            }
            
            // Check if any selected node is messageInput type
            const hasMessageInputNode = this.selectedNodes.some(node => node.data.type === 'messageInput');
            if (hasMessageInputNode) {
                this.showError(this.$t('_cannotDuplicateMessageInput'));
                return;
            }
            
            console.log('Duplicating nodes:', this.selectedNodes);
            const duplicatedNodes = [];
            const oldToNewNodeIds = new Map(); // Map old node IDs to new node IDs
            const offsetX = 60; // Horizontal offset for duplicated nodes
            const offsetY = 60; // Vertical offset for duplicated nodes

            // Create duplicated nodes
            this.selectedNodes.forEach(originalNode => {
                const newNodeId = this.generateGuid();

                // Deep clone the node data to avoid reference issues
                let stgData = JSON.stringify(originalNode.data);
                stgData = stgData.replaceAll(originalNode.id, newNodeId);

                oldToNewNodeIds.set(originalNode.id, newNodeId);
                const clonedData = JSON.parse(stgData);
                
                // Update label if it exists
                if (clonedData.label) {
                    clonedData.label = clonedData.label.replace(' (Copy)', '') + ' (Copy)';
                }

                const duplicatedNode = {
                    ...originalNode,
                    id: newNodeId,
                    position: {
                        x: originalNode.position.x + offsetX,
                        y: originalNode.position.y + offsetY
                    },
                    selected: false, // Don't select the duplicated nodes initially
                    data: clonedData
                };

                duplicatedNodes.push(duplicatedNode);
            });

            // Add duplicated nodes to the flow
            this.tempNode.push(...duplicatedNodes);

            // Save state to history
            const nodeCount = duplicatedNodes.length;
            this.saveStateToHistory(`Duplicate ${nodeCount} Node${nodeCount > 1 ? 's' : ''}`);

            // Find and duplicate edges between the selected nodes only
            const selectedNodeIds = this.selectedNodes.map(node => node.id);
            const edgesToDuplicate = this.tempEdge.filter(edge => 
                selectedNodeIds.includes(edge.source) && selectedNodeIds.includes(edge.target)
            );

/*
            const duplicatedEdges = edgesToDuplicate.map(originalEdge => ({
                ...originalEdge,
                id: this.generateUniqueId(),
                source: oldToNewNodeIds.get(originalEdge.source),
                target: oldToNewNodeIds.get(originalEdge.target)
            }));

            // Add duplicated edges to the flow
            this.tempEdge.push(...duplicatedEdges);
*/
            // Show success message
            this.showSuccess(this.$t('_nodesDuplicated', { n: nodeCount }));

            // Clear current selection first
            this.selectedNodes = [];
            this.selectedNode = null;

            // Select the duplicated nodes after a brief delay to ensure they're rendered
            this.$nextTick(() => {
                // Update the selected state of duplicated nodes
                duplicatedNodes.forEach(node => {
                    const nodeInFlow = this.tempNode.find(n => n.id === node.id);
                    if (nodeInFlow) {
                        nodeInFlow.selected = true;
                    }
                });
                
                this.selectedNodes = duplicatedNodes;
                
                // If only one node was duplicated, open it in inspector
                if (duplicatedNodes.length === 1) {
                    this.selectedNode = duplicatedNodes[0];
                    this.openNodeInspector(duplicatedNodes[0]);
                }
            });
        },
        handleCopySelectedNodes() {
            if (this.selectedNodes.length === 0) {
                return;
            }
            this.copiedNodes = [];
            
            
            // Deep clone the selected nodes data
            this.copiedNodes = this.selectedNodes.map(node => {
                // Deep clone the node data to avoid reference issues
                let stgData = JSON.stringify(node.data);
                const clonedData = JSON.parse(stgData);
                
                return {
                    ...node,
                    data: clonedData
                };
            });
            
            // Also copy edges between the selected nodes
            const selectedNodeIds = this.selectedNodes.map(node => node.id);
            
            // Show success message
            const nodeCount = this.copiedNodes.length;
            this.showSuccess(this.$t('_nodesCopied', { n: nodeCount }));
        },
        handlePasteNodes() {
            if (this.copiedNodes.length === 0) {
                return;
            }
            
            // Check if any copied node is messageInput type and if one already exists
            const hasMessageInputNode = this.copiedNodes.some(node => node.data.type === 'messageInput');
            if (hasMessageInputNode) {
                const existingMessageInput = this.tempNode.find(node => node.data.type === 'messageInput');
                if (existingMessageInput) {
                    this.showError(this.$t('_cannotPasteMessageInput'));
                    return;
                }
            }
            
            const pastedNodes = [];
            const oldToNewNodeIds = new Map(); // Map old node IDs to new node IDs
            
            // Get center position for pasting
            const centerPosition = this.getCenterPosition();
            
            // Calculate bounding box of copied nodes to maintain relative positions
            let minX = Infinity, minY = Infinity;
            this.copiedNodes.forEach(node => {
                minX = Math.min(minX, node.position.x);
                minY = Math.min(minY, node.position.y);
            });
            
            // Create pasted nodes
            this.copiedNodes.forEach(originalNode => {
                const newNodeId = this.generateGuid();
                
                // Deep clone the node data to avoid reference issues
                let stgData = JSON.stringify(originalNode.data);
                stgData = stgData.replaceAll(originalNode.id, newNodeId);
                
                oldToNewNodeIds.set(originalNode.id, newNodeId);
                const clonedData = JSON.parse(stgData);
                
                // Update label if it exists to indicate it's a copy
                if (clonedData.label) {
                    clonedData.label = clonedData.label.replace(' (Copy)', '') + ' (Copy)';
                }
                
                // Calculate new position relative to center with maintained relative positions
                const relativeX = originalNode.position.x - minX;
                const relativeY = originalNode.position.y - minY;
                
                const pastedNode = {
                    ...originalNode,
                    id: newNodeId,
                    position: {
                        x: centerPosition.x + relativeX,
                        y: centerPosition.y + relativeY
                    },
                    selected: false, // Don't select the pasted nodes initially
                    data: clonedData
                };
                
                pastedNodes.push(pastedNode);
            });
            
            // Add pasted nodes to the flow
            this.tempNode.push(...pastedNodes);
            // Save state to history
            const nodeCount = pastedNodes.length;
            this.saveStateToHistory(`Paste ${nodeCount} Node${nodeCount > 1 ? 's' : ''}`);
            
            // Show success message
            this.showSuccess(this.$t('_nodesPasted', { n: nodeCount }));
            
            // Clear current selection first
            this.selectedNodes = [];
            this.selectedNode = null;
        },
        handleRemoveOutgoingEdge(edgeId) {
            // Find edge details before removing for history
            const edgeToRemove = this.tempEdge.find(edge => edge.id === edgeId);
            
            // Remove the specific edge by ID
            this.tempEdge = this.tempEdge.filter(edge => edge.id !== edgeId);
            
            // After removal, check and update parallel mode for source node
            if (edgeToRemove) {
                const sourceNode = this.tempNode.find(node => node.id === edgeToRemove.source);
                if (sourceNode && this.canBeParallelParent(sourceNode)) {
                    this.checkAndUpdateParallelMode(sourceNode.id);
                }
                
                // Save to history
                const targetNode = this.tempNode.find(node => node.id === edgeToRemove.target);
                const sourceName = sourceNode ? sourceNode.data.label || 'Node' : 'Node';
                const targetName = targetNode ? targetNode.data.label || 'Node' : 'Node';
                this.saveStateToHistory(`Disconnect ${sourceName} → ${targetName}`);
            } else {
                this.saveStateToHistory('Remove Connection');
            }
        },
        handleNodeDataUpdate(nodeId, newData) {
            if (this.isDebugMode) return;
            const nodeIndex = this.tempNode.findIndex(node => node.id === nodeId);
            if (nodeIndex !== -1) {
                this.tempNode = [
                    ...this.tempNode.slice(0, nodeIndex),
                    {
                        ...this.tempNode[nodeIndex],
                        data: { ...this.tempNode[nodeIndex].data, ...newData }
                    },
                    ...this.tempNode.slice(nodeIndex + 1)
                ];
            }
        },
        onDrop(event) {
            event.preventDefault();
            if (this.isDebugMode) return;
            const nodeData = event.dataTransfer.getData('application/reactflow');
            if (nodeData) {
                const nodeTemplate = JSON.parse(nodeData);

                // Get VueFlow instance
                const vueFlowInstance = this.$refs.vueFlow;
                if (vueFlowInstance) {
                    let position;

                    // Try to use screenToFlowPosition if available (newer versions)
                    if (typeof vueFlowInstance.screenToFlowPosition === 'function') {
                        const flowPosition = vueFlowInstance.screenToFlowPosition({
                            x: event.clientX,
                            y: event.clientY
                        });
                        position = {
                            x: flowPosition.x - 75,
                            y: flowPosition.y - 25
                        };
                    }
                    // Fallback to manual calculation
                    else {
                        const flowElement = vueFlowInstance.$el.querySelector('.vue-flow__viewport') || vueFlowInstance.$el;
                        const flowBounds = flowElement.getBoundingClientRect();

                        // Get current viewport transform
                        const viewport = vueFlowInstance.getViewport ? vueFlowInstance.getViewport() : { x: 0, y: 0, zoom: 1 };

                        // Calculate the position relative to the flow viewport
                        const relativeX = event.clientX - flowBounds.left;
                        const relativeY = event.clientY - flowBounds.top;

                        // Convert screen coordinates to flow coordinates accounting for zoom and pan
                        position = {
                            x: (relativeX - viewport.x) / viewport.zoom - 75,
                            y: (relativeY - viewport.y) / viewport.zoom - 25
                        };
                    }

                    const newNode = {
                        id: this.generateGuid(),
                        type: 'flow-node',
                        position: position,
                        data: { ...nodeTemplate.data }
                    };

                    // Check if trying to add messageInput node when one already exists
                    if (newNode.data.type === 'messageInput') {
                        const existingMessageInput = this.tempNode.find(node => node.data.type === 'messageInput');
                        if (existingMessageInput) {
                            this.showError(this.$t('_messageInputExists'));
                            return;
                        }
                    }

                    // Apply comprehensive defaults
                    this.applyNodeDefaults(newNode);

                    this.tempNode.push(newNode);
                    this.$nextTick(() => {
                        const getTempNode = this.tempNode.find(node => node.id === newNode.id);
                        if (getTempNode) {
                            this.checkNodeValidation(getTempNode);
                        }
                        
                        // Focus and select the new node
                        this.focusAndSelectNode(newNode.id);
                    });
                    // Show success message
                    this.showSuccess(this.$t('_addedToFlow', { label: nodeTemplate.data.label }));
                } else {
                    console.error('VueFlow instance not available');
                }
            }
        },
        // Update SaveFlow method to use new targets structure
        async SaveFlow() {
            try {
                // this.dialog = true;

                // Check if there are any nodes in the flow has data.warningShow = true
                // if (this.tempNode.some(node => node.data.showWarning)) {
                //     this.showError('Please resolve all warnings before saving the flow');
                //     return;
                // }


                if (!this.tempNode || this.tempNode.length === 0) {
                    this.showError(this.$t('_cannotSaveEmpty'));
                    return;
                }

                // Convert to flat structure with targets
                const flatNodes = this.convertToFlatStructure();

                const flowData = {
                    id: this.flowId || null,
                    name: this.flowName || 'Untitled Flow',
                    flowDetails: flatNodes
                };

                // Validate flow structure
                const validationResult = this.validateFlatFlow(flowData);
                if (!validationResult.isValid) {
                    this.showError(this.$t('_flowValidationFailed', { errors: validationResult.errors.join(', ') }));
                    return;
                }

                // Choose the appropriate API method
                const apiMethod = this.flowId ? 'UpdateFlow' : 'CreateFlow';

                // Save to API
                const response = await this.saveFlowToAPI(flowData, apiMethod);

                if (response.success) {
                    // Update flow metadata
                    this.flowId = response.flowId || this.flowId;
                    this.flowName = flowData.name;
                    this.clearHistory();
                    this.showSuccess(this.isEditMode ? 'Flow updated successfully' : 'Flow saved successfully');
                    this.successDialog = true; // Show success dialog

                    this.$router.replace({ path: '/FlowCreator', query: { id: this.flowId } });
                    this.isEditMode = true;
                    // Automatically hide success dialog after 2 seconds
                    setTimeout(() => {
                        this.successDialog = false;
                    }, 2000);
                } else {
                    throw new Error(response.message || 'Failed to save flow');
                }

            } catch (error) {
                console.error('Error saving flow:', error);
                this.showError(this.$t('_failedSaveFlow', { msg: error.message }));
            } finally {
                this.dialog = false;
            }
        },

        // ============================================
        // DEBUG MODE METHODS
        // ============================================
        toggleDebugMode() {
            if (this.isDebugMode) {
                this.exitDebugMode();
            } else {
                this.enterDebugMode();
            }
        },
        generateDebugUserId() {
            return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c =>
                (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
            );
        },
        enterDebugMode() {
            this.isDebugMode = true;
            this.debugStatus = 'ready';
            this.selectedNodes = [];
            this.selectedNode = null;
            this.tempNode.forEach(n => { n.selected = false; });
            this.closeInspector();
            this.debugSteps = [];
            this.debugNodeStates = {};
            this.debugEdgeStates = {};
            this.debugSessionId = '';
            this.debugMessage = '';
            this.debugError = '';
            this.debugCurrentNodeId = '';
            this.debugPreviousNodeId = '';
            this.debugPausedNodeName = '';
            this.debugStepCount = 0;
            this.debugTotalDuration = 0;
            this.debugVariables = {};
            this.debugChangedVariables = new Set();
            this.debugPatchVariables = {};
            this.debugOverrideNextNodeId = '';
            this.debugOverrideNextStepIndex = null;
            this.debugStepsBeforeSetNext = null;
            this.debugHoveredNodeId = '';
            this.pruneUnsupportedDebugBreakpoints();
            this.debugUserId = this.generateDebugUserId();
            this.tempNode.forEach(n => { this.debugNodeStates[n.id] = 'idle'; });
            this.tempEdge.forEach(e => { e.animated = false; });
        },
        exitDebugMode() {
            if (this.debugStatus === 'running' || this.debugStatus === 'paused') {
                this.stopDebugExecution();
            }
            this.isDebugMode = false;
            this.debugStatus = 'ready';
            this.debugSteps = [];
            this.debugNodeStates = {};
            this.debugEdgeStates = {};
            this.debugSessionId = '';
            this.debugCurrentNodeId = '';
            this.debugError = '';
            this.debugVariables = {};
            this.debugChangedVariables = new Set();
            this.debugLastBotReply = '';
            this.debugSseReader = null;
            this.debugPatchVariables = {};
            this.debugOverrideNextNodeId = '';
            this.debugOverrideNextStepIndex = null;
            this.debugStepsBeforeSetNext = null;
            this.debugHoveredNodeId = '';
            this.debugUserId = '';
            this.debugKeepMemory = false;
            this.tempEdge.forEach(e => { e.animated = false; });
        },
        onDebugKeepMemoryChange(val) {
            this.debugKeepMemory = val;
            if (val) {
                this.debugUserId = this.generateDebugUserId();
            }
        },
        async startDebugExecution() {
            if (!this.debugMessage.trim()) return;
            if (this.debugStatus === 'running') return;
            if (!this.debugKeepMemory) {
                this.debugUserId = this.generateDebugUserId();
            }
            this.debugStatus = 'running';
            this.debugSteps = [];
            this.debugStepCount = 0;
            this.debugTotalDuration = 0;
            this.debugError = '';
            this.debugCurrentNodeId = '';
            this.debugPreviousNodeId = '';
            this.debugPausedNodeName = '';
            this.debugVariables = {};
            this.debugChangedVariables = new Set();
            this.debugLastBotReply = '';
            this.debugPatchVariables = {};
            this.debugOverrideNextNodeId = '';
            this.debugOverrideNextStepIndex = null;
            this.debugStepsBeforeSetNext = null;
            this.debugHoveredNodeId = '';
            const nodeStates = {};
            this.tempNode.forEach(n => { nodeStates[n.id] = 'idle'; });
            this.debugNodeStates = nodeStates;
            this.debugEdgeStates = {};
            this.tempEdge.forEach(e => { e.animated = false; });
            const flatNodes = this.convertToFlatStructure();
            const flowNodes = flatNodes.map(n => ({
                Id: n.id,
                NodeType: n.nodeType,
                Position: JSON.stringify(n.position),
                Data: JSON.stringify(n.data),
                Targets: JSON.stringify(n.targets),
            }));
            const request = {
                FlowNodes: flowNodes,
                Message: this.debugMessage.trim(),
                Breakpoints: this.getSupportedDebugBreakpointIds(),
                Variables: {},
                DebugUserId: this.debugUserId,
            };
            try {
                const response = await this.$call.DebugFlowSSE(request);
                if (!response.ok) {
                    this.debugStatus = 'error';
                    this.debugError = `HTTP ${response.status}: ${response.statusText}`;
                    return;
                }
                const reader = response.body.getReader();
                this.debugSseReader = reader;
                const decoder = new TextDecoder();
                let buffer = '';
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';
                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            const jsonStr = line.substring(6).trim();
                            if (jsonStr) {
                                try {
                                    const event = JSON.parse(jsonStr);
                                    this.handleDebugEvent(event);
                                } catch (e) {
                                    console.warn('Debug SSE parse error:', e, jsonStr);
                                }
                            }
                        }
                    }
                }
                if (this.debugStatus === 'running') {
                    this.debugStatus = 'completed';
                }
            } catch (err) {
                if (err.name === 'AbortError') {
                    this.debugStatus = 'stopped';
                } else {
                    console.error('Debug SSE error:', err);
                    this.debugStatus = 'error';
                    this.debugError = err.message;
                }
            }
        },
        handleDebugEvent(event) {
            const type = event.EventType || event.eventType;
            const nodeId = event.NodeId || event.nodeId;
            const nodeName = event.NodeName || event.nodeName;
            const nodeType = event.NodeType || event.nodeType;
            switch (type) {
                case 'session_start':
                    this.debugSessionId = event.SessionId || event.sessionId;
                    break;
                case 'node_start':
                    if (nodeType === 'aiParallel') {
                        this.ensureAiParallelSummaryStep({
                            summaryNodeId: nodeId,
                            sourceNodeId: this.getAiParallelSourceNodeId(nodeId),
                            nodeName: nodeName || 'AI Parallel Execution',
                            stepIndex: event.StepIndex || event.stepIndex,
                            startTime: event.StartTime || event.startTime,
                            input: event.Input || event.input,
                        });
                        break;
                    }
                    this.debugPreviousNodeId = this.debugCurrentNodeId;
                    this.debugCurrentNodeId = nodeId;
                    this.debugNodeStates = { ...this.debugNodeStates, [nodeId]: 'running' };
                    this.markExecutionEdge(this.debugPreviousNodeId, nodeId, 'active');
                    this.debugSteps = [...this.debugSteps, {
                        eventType: type, nodeId, nodeName: nodeName || nodeType, nodeType,
                        stepIndex: event.StepIndex || event.stepIndex,
                        startTime: event.StartTime || event.startTime,
                    }];
                    this.debugStepCount = this.getVisibleDebugStepCount();
                    this.focusOnNode(nodeId);
                    break;
                case 'node_complete':
                    if (nodeType === 'aiParallel') {
                        const contextVariables = event.ContextVariables || event.contextVariables || {};
                        const output = event.Output || event.output || {};
                        const parallelResults = output.parallelResults || output.ParallelResults || contextVariables.parallelResults || contextVariables.ParallelResults || [];
                        const nextNodeId = event.NextNodeId || event.nextNodeId;
                        const summaryIdx = this.ensureAiParallelSummaryStep({
                            summaryNodeId: nodeId,
                            sourceNodeId: this.getAiParallelSourceNodeId(nodeId),
                            nodeName: nodeName || 'AI Parallel Execution',
                            stepIndex: event.StepIndex || event.stepIndex,
                        });
                        const updated = [...this.debugSteps];
                        const existingStep = summaryIdx >= 0 ? updated[summaryIdx] : {};
                        const existingOutput = existingStep.output || {};
                        const parallelModelLogs = existingOutput.parallelModelLogs || existingStep.parallelModelLogs || [];

                        updated[summaryIdx] = {
                            ...existingStep,
                            eventType: type, nodeId, nodeName: nodeName || 'AI Parallel Execution', nodeType,
                            stepIndex: event.StepIndex || event.stepIndex,
                            durationMs: event.DurationMs || event.durationMs,
                            output: { ...existingOutput, ...output, parallelModelLogs, parallelResults, parallelResultsCount: Array.isArray(parallelResults) ? parallelResults.length : 0 },
                            outputPath: event.OutputPath || event.outputPath,
                            nextNodeId,
                            contextVariables,
                            success: event.Success !== undefined ? event.Success : event.success,
                            isAiParallelSummary: true,
                            sourceNodeId: existingStep.sourceNodeId || this.getAiParallelSourceNodeId(nodeId),
                            parallelModelLogs,
                        };
                        this.debugSteps = updated;
                        this.debugStepCount = this.getVisibleDebugStepCount();

                        if (nextNodeId && Array.isArray(parallelResults)) {
                            parallelResults.forEach(result => {
                                const resultNodeId = result.NodeId || result.nodeId;
                                if (resultNodeId) this.markEdgeBetween(resultNodeId, nextNodeId, 'active');
                            });
                        }

                        if (Object.keys(contextVariables).length) {
                            const prevVarKeys = Object.keys(this.debugVariables);
                            const changed = new Set();
                            for (const key of Object.keys(contextVariables)) {
                                if (!prevVarKeys.includes(key) || JSON.stringify(this.debugVariables[key]) !== JSON.stringify(contextVariables[key])) {
                                    changed.add(key);
                                }
                            }
                            this.debugVariables = { ...contextVariables };
                            this.debugChangedVariables = changed;
                        }
                        break;
                    }
                    this.debugNodeStates = { ...this.debugNodeStates, [nodeId]: 'success' };
                    this.markExecutionEdge(this.debugPreviousNodeId, nodeId, 'completed');
                    {
                        const existingIdx = this.findDebugStepIndex(nodeId, event.StepIndex || event.stepIndex, ['node_start', 'breakpoint_hit']);
                        if (existingIdx >= 0) {
                            const updated = [...this.debugSteps];
                            updated[existingIdx] = { ...updated[existingIdx], eventType: type,
                                durationMs: event.DurationMs || event.durationMs, output: event.Output || event.output,
                                outputPath: event.OutputPath || event.outputPath, gptReply: event.GptReply || event.gptReply,
                                nextNodeId: event.NextNodeId || event.nextNodeId,
                                contextVariables: event.ContextVariables || event.contextVariables,
                                success: event.Success !== undefined ? event.Success : event.success,
                            };
                            this.debugSteps = updated;
                        } else {
                            this.debugSteps = [...this.debugSteps, {
                                eventType: type, nodeId, nodeName: nodeName || nodeType, nodeType,
                                stepIndex: event.StepIndex || event.stepIndex,
                                durationMs: event.DurationMs || event.durationMs, output: event.Output || event.output,
                                outputPath: event.OutputPath || event.outputPath, gptReply: event.GptReply || event.gptReply,
                                nextNodeId: event.NextNodeId || event.nextNodeId,
                                contextVariables: event.ContextVariables || event.contextVariables,
                                success: event.Success !== undefined ? event.Success : event.success,
                            }];
                        }
                    }
                    if (event.ContextVariables || event.contextVariables) {
                        const vars = event.ContextVariables || event.contextVariables;
                        const prevVarKeys = Object.keys(this.debugVariables);
                        const changed = new Set();
                        for (const key of Object.keys(vars)) {
                            if (!prevVarKeys.includes(key) || JSON.stringify(this.debugVariables[key]) !== JSON.stringify(vars[key])) {
                                changed.add(key);
                            }
                        }
                        this.debugVariables = { ...vars };
                        this.debugChangedVariables = changed;
                    }
                    break;
                case 'node_error':
                    this.debugStatus = 'error';
                    this.debugError = this.getDebugEventErrorMessage(event, 'Node execution failed');
                    this.debugNodeStates = { ...this.debugNodeStates, [nodeId]: 'error' };
                    {
                        const existingIdx = this.findDebugStepIndex(nodeId, event.StepIndex || event.stepIndex, ['node_start', 'breakpoint_hit']);
                        if (existingIdx >= 0) {
                            const updated = [...this.debugSteps];
                            updated[existingIdx] = { ...updated[existingIdx], eventType: type,
                                errorMessage: event.ErrorMessage || event.errorMessage, success: false };
                            this.debugSteps = updated;
                        } else {
                            this.debugSteps = [...this.debugSteps, {
                                eventType: type, nodeId, nodeName: nodeName || nodeType, nodeType,
                                stepIndex: event.StepIndex || event.stepIndex,
                                errorMessage: event.ErrorMessage || event.errorMessage, success: false,
                            }];
                        }
                    }
                    break;
                case 'breakpoint_hit':
                    this.debugStatus = 'paused';
                    this.debugCurrentNodeId = nodeId;
                    this.debugPausedNodeName = nodeName || nodeType || '';
                    this.debugNodeStates = { ...this.debugNodeStates, [nodeId]: 'paused' };
                    {
                        const stepIndex = event.StepIndex || event.stepIndex;
                        const parentNodeId = event.ParentNodeId || event.parentNodeId;
                        const isParallelModelStep = nodeType === 'model' && !!parentNodeId;
                        if (isParallelModelStep) {
                            this.markEdgeBetween(parentNodeId, nodeId, 'active');
                            this.upsertAiParallelModelLog(event, 'paused');
                            this.focusOnNode(nodeId);
                            break;
                        }
                        const existingTypes = isParallelModelStep ? ['agent_start', 'breakpoint_hit'] : ['node_start', 'breakpoint_hit'];
                        const existingIdx = this.findDebugStepIndex(nodeId, stepIndex, existingTypes);
                        const pauseStep = {
                            eventType: type, nodeId, nodeName: nodeName || nodeType, nodeType, stepIndex,
                            isSubStep: false,
                            isParallelModelStep,
                        };
                        if (isParallelModelStep) {
                            pauseStep.parentNodeId = parentNodeId;
                            pauseStep.agentName = event.AgentName || event.agentName || nodeName;
                        }
                        if (existingIdx >= 0) {
                            const updated = [...this.debugSteps];
                            updated[existingIdx] = { ...updated[existingIdx], ...pauseStep };
                            this.debugSteps = updated;
                        } else {
                            this.debugSteps = [...this.debugSteps, pauseStep];
                        }
                    }
                    this.focusOnNode(nodeId);
                    break;
                case 'agent_start':
                    {
                        const parentNodeId = event.ParentNodeId || event.parentNodeId;
                        const agentName = event.AgentName || event.agentName || nodeName;
                        const agentNodeType = nodeType || 'agent';
                        const isParallelModelStep = agentNodeType === 'model' && !!parentNodeId;
                        if (isParallelModelStep) {
                            this.debugPreviousNodeId = parentNodeId;
                            this.debugCurrentNodeId = nodeId;
                            this.debugNodeStates = { ...this.debugNodeStates, [nodeId]: 'running' };
                            this.markEdgeBetween(parentNodeId, nodeId, 'active');
                            this.upsertAiParallelModelLog(event, 'running');
                            this.focusOnNode(nodeId);
                            break;
                        }
                        this.debugNodeStates = { ...this.debugNodeStates, [nodeId]: 'running' };
                        this.markEdgeBetween(parentNodeId, nodeId, 'active');
                        this.debugSteps = [...this.debugSteps, {
                            eventType: type, nodeId, nodeName: agentName, nodeType: agentNodeType,
                            parentNodeId, agentName, stepIndex: event.StepIndex || event.stepIndex,
                            startTime: event.StartTime || event.startTime, isSubStep: !isParallelModelStep,
                            isParallelModelStep,
                        }];
                        this.debugStepCount = this.getVisibleDebugStepCount();
                        this.focusOnNode(nodeId);
                    }
                    break;
                case 'tool_start':
                    {
                        const parentAgentId = event.ParentNodeId || event.parentNodeId;
                        this.debugNodeStates = { ...this.debugNodeStates, [nodeId]: 'running' };
                        if (parentAgentId) this.markEdgeBetween(parentAgentId, nodeId, 'active');
                    }
                    break;
                case 'tool_complete':
                    {
                        const parentAgentId = event.ParentNodeId || event.parentNodeId;
                        const toolSuccess = event.Success !== undefined ? event.Success : event.success;
                        this.debugNodeStates = { ...this.debugNodeStates, [nodeId]: toolSuccess ? 'success' : 'error' };
                        if (parentAgentId) this.markEdgeBetween(parentAgentId, nodeId, 'completed');
                    }
                    break;
                case 'agent_complete':
                    {
                        const agentName = event.AgentName || event.agentName || nodeName;
                        const agentSuccess = event.Success !== undefined ? event.Success : event.success;
                        const parentNodeId = event.ParentNodeId || event.parentNodeId;
                        const agentNodeType = nodeType || 'agent';
                        const isParallelModelStep = agentNodeType === 'model' && !!parentNodeId;
                        const output = event.Output || event.output || {};
                        this.debugNodeStates = { ...this.debugNodeStates, [nodeId]: agentSuccess ? 'success' : 'error' };
                        if (parentNodeId) this.markEdgeBetween(parentNodeId, nodeId, 'completed');
                        if (isParallelModelStep) {
                            this.upsertAiParallelModelLog(event, agentSuccess ? 'success' : 'error');
                            break;
                        }
                        const existingIdx = this.debugSteps.findLastIndex(s => s.nodeId === nodeId && ['agent_start', 'breakpoint_hit'].includes(s.eventType) && s.agentName === agentName);
                        if (existingIdx >= 0) {
                            const updated = [...this.debugSteps];
                            updated[existingIdx] = { ...updated[existingIdx], eventType: type,
                                nodeType: agentNodeType, isSubStep: !isParallelModelStep, isParallelModelStep,
                                durationMs: event.DurationMs || event.durationMs,
                                success: event.Success !== undefined ? event.Success : event.success,
                                gptReply: event.GptReply || event.gptReply, output: output,
                                errorMessage: event.ErrorMessage || event.errorMessage,
                            };
                            this.debugSteps = updated;
                        } else {
                            this.debugSteps = [...this.debugSteps, {
                                eventType: type, nodeId, nodeName: agentName, nodeType: agentNodeType,
                                parentNodeId: event.ParentNodeId || event.parentNodeId, agentName, stepIndex: event.StepIndex || event.stepIndex,
                                isSubStep: !isParallelModelStep, isParallelModelStep,
                                durationMs: event.DurationMs || event.durationMs,
                                success: event.Success !== undefined ? event.Success : event.success,
                                gptReply: event.GptReply || event.gptReply, output: output,
                                errorMessage: event.ErrorMessage || event.errorMessage,
                            }];
                        }
                        this.debugStepCount = this.getVisibleDebugStepCount();
                    }
                    break;
                case 'flow_complete':
                    this.debugStatus = 'completed';
                    this.debugTotalDuration = event.TotalDurationMs || event.totalDurationMs || 0;
                    this.debugStepCount = Math.max(event.TotalSteps || event.totalSteps || 0, this.getVisibleDebugStepCount(), this.debugStepCount);
                    {
                        const finalReply = event.GptReply || event.gptReply;
                        if (finalReply) { this.debugLastBotReply = finalReply; }
                        else if (!this.debugLastBotReply) {
                            for (let i = this.debugSteps.length - 1; i >= 0; i--) {
                                if (this.debugSteps[i].gptReply) { this.debugLastBotReply = this.debugSteps[i].gptReply; break; }
                            }
                        }
                    }
                    break;
                case 'flow_error':
                    this.debugStatus = 'error';
                    this.debugError = this.getDebugEventErrorMessage(event, this.debugError || 'Flow execution failed');
                    break;
                case 'variables_patched':
                    if (event.ContextVariables || event.contextVariables) {
                        this.debugVariables = { ...(event.ContextVariables || event.contextVariables) };
                    }
                    break;
                case 'set_next_applied':
                    {
                        const targetId = event.NextNodeId || event.nextNodeId;
                        const visualTargetId = targetId && typeof targetId === 'string' && targetId.endsWith('-parallel') ? this.getAiParallelSourceNodeId(targetId) : targetId;
                        if (this.debugStepsBeforeSetNext) {
                            this.trimDebugStepsForSetNext(targetId, this.debugOverrideNextStepIndex, false);
                            this.debugStepsBeforeSetNext = null;
                        }
                        const resetStates = {};
                        this.tempNode.forEach(n => { resetStates[n.id] = 'idle'; });
                        if (visualTargetId) { resetStates[visualTargetId] = 'running'; }
                        this.debugNodeStates = resetStates;
                        this.debugEdgeStates = {};
                        this.tempEdge.forEach(e => { e.animated = false; });
                        this.debugOverrideNextNodeId = '';
                        this.debugOverrideNextStepIndex = null;
                        if (visualTargetId) { this.focusOnNode(visualTargetId); }
                    }
                    break;
            }
        },
        getAiParallelSourceNodeId(parallelNodeId) {
            if (!parallelNodeId || typeof parallelNodeId !== 'string') return '';
            return parallelNodeId.endsWith('-parallel') ? parallelNodeId.slice(0, -'-parallel'.length) : '';
        },
        getAiParallelSummaryNodeId(parentNodeId, fallbackNodeId = '') {
            if (fallbackNodeId && fallbackNodeId.endsWith('-parallel')) return fallbackNodeId;
            return parentNodeId ? `${parentNodeId}-parallel` : fallbackNodeId;
        },
        getDebugSetNextTargetName() {
            if (!this.debugOverrideNextNodeId) return '';
            if (typeof this.debugOverrideNextNodeId === 'string' && this.debugOverrideNextNodeId.endsWith('-parallel')) return 'AI Parallel Execution';
            return this.tempNode.find(n => n.id === this.debugOverrideNextNodeId)?.data?.label || this.debugOverrideNextNodeId;
        },
        ensureAiParallelSummaryStep({ summaryNodeId, sourceNodeId = '', nodeName = 'AI Parallel Execution', stepIndex = null, startTime = null, input = null }) {
            if (!summaryNodeId) return -1;
            const existingIdx = this.debugSteps.findLastIndex(step => step.nodeId === summaryNodeId && step.nodeType === 'aiParallel');
            const resolvedSourceNodeId = sourceNodeId || this.getAiParallelSourceNodeId(summaryNodeId);
            if (existingIdx >= 0) {
                const updated = [...this.debugSteps];
                updated[existingIdx] = {
                    ...updated[existingIdx],
                    nodeName: updated[existingIdx].nodeName || nodeName,
                    sourceNodeId: updated[existingIdx].sourceNodeId || resolvedSourceNodeId,
                    stepIndex: updated[existingIdx].stepIndex ?? stepIndex,
                    startTime: updated[existingIdx].startTime || startTime,
                    input: updated[existingIdx].input || input,
                };
                this.debugSteps = updated;
                return existingIdx;
            }

            this.debugSteps = [...this.debugSteps, {
                eventType: 'node_start',
                nodeId: summaryNodeId,
                nodeName,
                nodeType: 'aiParallel',
                stepIndex,
                startTime,
                input,
                output: { parallelModelLogs: [] },
                parallelModelLogs: [],
                sourceNodeId: resolvedSourceNodeId,
                isAiParallelSummary: true,
            }];
            this.debugStepCount = this.getVisibleDebugStepCount();
            return this.debugSteps.length - 1;
        },
        upsertAiParallelModelLog(event, status) {
            const parentNodeId = event.ParentNodeId || event.parentNodeId;
            const nodeId = event.NodeId || event.nodeId;
            if (!parentNodeId || !nodeId) return;

            const summaryNodeId = this.getAiParallelSummaryNodeId(parentNodeId);
            const summaryIdx = this.ensureAiParallelSummaryStep({
                summaryNodeId,
                sourceNodeId: parentNodeId,
                nodeName: 'AI Parallel Execution',
                stepIndex: event.StepIndex || event.stepIndex,
                startTime: event.StartTime || event.startTime,
            });
            if (summaryIdx < 0) return;

            const agentName = event.AgentName || event.agentName || event.NodeName || event.nodeName || 'AI Model';
            const stepIndex = event.StepIndex || event.stepIndex;
            const updated = [...this.debugSteps];
            const summaryStep = updated[summaryIdx];
            const output = { ...(summaryStep.output || {}) };
            const logs = [...(output.parallelModelLogs || summaryStep.parallelModelLogs || [])];
            const existingLogIdx = logs.findLastIndex(log => log.nodeId === nodeId && (stepIndex == null || log.stepIndex == null || log.stepIndex === stepIndex));
            const eventOutput = event.Output || event.output;
            const logPatch = {
                nodeId,
                nodeName: event.NodeName || event.nodeName || agentName,
                nodeType: event.NodeType || event.nodeType || 'model',
                parentNodeId,
                agentName,
                stepIndex,
                status,
                eventType: event.EventType || event.eventType,
                startTime: event.StartTime || event.startTime,
                endTime: event.EndTime || event.endTime,
                durationMs: event.DurationMs || event.durationMs,
                output: eventOutput,
                outputPath: event.OutputPath || event.outputPath,
                gptReply: event.GptReply || event.gptReply,
                success: event.Success !== undefined ? event.Success : event.success,
                errorMessage: event.ErrorMessage || event.errorMessage,
            };

            if (existingLogIdx >= 0) {
                logs[existingLogIdx] = { ...logs[existingLogIdx], ...logPatch };
            } else {
                logs.push(logPatch);
            }

            output.parallelModelLogs = logs;
            const summaryEventType = status === 'paused' ? 'breakpoint_hit' : summaryStep.eventType === 'node_complete' ? 'node_complete' : 'node_start';
            updated[summaryIdx] = {
                ...summaryStep,
                eventType: summaryEventType,
                output,
                parallelModelLogs: logs,
                success: summaryStep.success ?? true,
            };
            this.debugSteps = updated;
            this.debugStepCount = this.getVisibleDebugStepCount();
        },
        findDebugStepIndex(nodeId, stepIndex, eventTypes = null) {
            for (let i = this.debugSteps.length - 1; i >= 0; i--) {
                const step = this.debugSteps[i];
                if (step.nodeId !== nodeId) continue;
                if (Array.isArray(eventTypes) && !eventTypes.includes(step.eventType)) continue;
                if (stepIndex != null && step.stepIndex != null && step.stepIndex !== stepIndex) continue;
                return i;
            }
            return -1;
        },
        trimDebugStepsForSetNext(targetId, targetStepIndex = null, useFallback = true) {
            if (!this.debugSteps.length) return;
            let trimIndex = -1;
            if (targetId) {
                trimIndex = this.findDebugStepIndex(targetId, targetStepIndex, null);
            }
            if (trimIndex < 0 && useFallback) {
                trimIndex = this.debugSteps.findLastIndex(step => step.eventType === 'breakpoint_hit');
            }
            if (trimIndex >= 0) {
                this.debugSteps = this.debugSteps.slice(0, trimIndex);
                this.debugStepCount = this.getVisibleDebugStepCount();
            }
        },
        getVisibleDebugStepCount() {
            return this.debugSteps.filter(step => !step.isSubStep).length;
        },
        getDebugEventErrorMessage(event, fallback = 'Flow execution failed') {
            const directMessage = event?.ErrorMessage || event?.errorMessage || event?.Message || event?.message;
            if (directMessage) return directMessage;

            for (let i = this.debugSteps.length - 1; i >= 0; i--) {
                const step = this.debugSteps[i];
                if (step?.errorMessage) return step.errorMessage;
            }

            return fallback;
        },
        findDebugOutputPathForTransition(sourceNodeId, targetNodeId) {
            if (!sourceNodeId || !targetNodeId) return null;
            const previousStep = [...this.debugSteps].reverse().find(step =>
                step.nodeId === sourceNodeId && step.eventType === 'node_complete' && step.nextNodeId === targetNodeId && step.outputPath
            );
            return previousStep?.outputPath || null;
        },
        markExecutionEdge(sourceNodeId, targetNodeId, state) {
            const outputPath = this.findDebugOutputPathForTransition(sourceNodeId, targetNodeId);
            this.markEdgeBetween(sourceNodeId, targetNodeId, state, outputPath);
        },
        markEdgeBetween(sourceNodeId, targetNodeId, state, sourceHandle = null) {
            const newEdgeStates = { ...this.debugEdgeStates };
            let matchedExactEdge = false;
            this.tempEdge.forEach(edge => {
                const matchesNodePair = edge.target === targetNodeId && (!sourceNodeId || edge.source === sourceNodeId);
                const matchesHandle = !sourceHandle || edge.sourceHandle === sourceHandle;
                if (matchesNodePair && matchesHandle) {
                    newEdgeStates[edge.id] = state;
                    edge.animated = (state === 'active');
                    if (sourceHandle) { matchedExactEdge = true; }
                }
            });
            if (sourceHandle && !matchedExactEdge) {
                this.tempEdge.forEach(edge => {
                    if (edge.target === targetNodeId && (!sourceNodeId || edge.source === sourceNodeId)) {
                        newEdgeStates[edge.id] = state;
                        edge.animated = (state === 'active');
                    }
                });
            }
            this.debugEdgeStates = newEdgeStates;
        },
        async resumeDebugExecution() {
            if (!this.debugSessionId || this.debugStatus !== 'paused') return;
            this.debugStatus = 'running';
            this.debugPausedNodeName = '';
            this.debugHoveredNodeId = '';
            if (this.debugCurrentNodeId) {
                this.debugNodeStates = { ...this.debugNodeStates, [this.debugCurrentNodeId]: 'running' };
            }
            try {
                const payload = { Breakpoints: this.getSupportedDebugBreakpointIds() };
                if (Object.keys(this.debugPatchVariables).length > 0) {
                    payload.PatchVariables = { ...this.debugPatchVariables };
                }
                if (this.debugOverrideNextNodeId) {
                    payload.OverrideNextNodeId = this.debugOverrideNextNodeId;
                }
                this.$call.DebugResume(this.debugSessionId, payload, () => {
                    this.debugPatchVariables = {};
                    this.debugOverrideNextNodeId = '';
                }, (err) => { console.error('Resume error:', err); });
            } catch (err) { console.error('Resume failed:', err); }
        },
        async stopDebugExecution() {
            if (!this.debugSessionId) { this.debugStatus = 'stopped'; return; }
            try {
                if (this.debugSseReader) { try { this.debugSseReader.cancel(); } catch (e) {} this.debugSseReader = null; }
                this.$call.DebugStop(this.debugSessionId, () => { this.debugStatus = 'stopped'; },
                    (err) => { console.error('Stop error:', err); this.debugStatus = 'stopped'; });
            } catch (err) { console.error('Stop failed:', err); this.debugStatus = 'stopped'; }
        },
        toggleDebugBreakpoint(nodeId) {
            const node = this.tempNode.find(n => n.id === nodeId);
            if (node?.data?.type === 'intentAI') return;
            const newBps = new Set(this.debugBreakpoints);
            if (newBps.has(nodeId)) { newBps.delete(nodeId); } else { newBps.add(nodeId); }
            this.debugBreakpoints = newBps;
        },
        getSupportedDebugBreakpointIds() {
            return Array.from(this.debugBreakpoints).filter(nodeId => {
                const node = this.tempNode.find(n => n.id === nodeId);
                return node?.data?.type !== 'intentAI';
            });
        },
        pruneUnsupportedDebugBreakpoints() {
            const supportedBreakpointIds = this.getSupportedDebugBreakpointIds();
            if (supportedBreakpointIds.length !== this.debugBreakpoints.size) {
                this.debugBreakpoints = new Set(supportedBreakpointIds);
            }
        },
        clearAllBreakpoints() {
            this.debugBreakpoints = new Set();
        },
        focusOnNode(nodeId) {
            if (!nodeId || !this.$refs.vueFlow) return;
            try {
                const node = this.tempNode.find(n => n.id === nodeId);
                if (node && node.position) {
                    const vf = this.$refs.vueFlow;
                    if (vf.setCenter) {
                        vf.setCenter(node.position.x + 60, node.position.y + 50, { duration: 400, zoom: 1.5 });
                    }
                }
            } catch (e) { console.warn('focusOnNode error:', e); }
        },
        onDebugPatchVariable({ key, value }) {
            this.debugPatchVariables = { ...this.debugPatchVariables, [key]: value };
        },
        onDebugSetNext(target) {
            const nodeId = typeof target === 'object' ? target?.nodeId : target;
            const stepIndex = typeof target === 'object' ? target?.stepIndex : null;
            const focusNodeId = typeof target === 'object' ? (target?.focusNodeId || target?.sourceNodeId) : null;
            if (this.debugOverrideNextNodeId === nodeId) {
                this.restoreDebugStepsBeforeSetNext();
                this.debugOverrideNextNodeId = '';
                this.debugOverrideNextStepIndex = null;
                return;
            }
            if (nodeId === this.debugCurrentNodeId) { return; }
            if (!this.debugStepsBeforeSetNext) {
                this.debugStepsBeforeSetNext = [...this.debugSteps];
            }
            this.debugOverrideNextNodeId = nodeId;
            this.debugOverrideNextStepIndex = stepIndex ?? null;
            this.trimDebugStepsForSetNext(nodeId, this.debugOverrideNextStepIndex, false);
            this.focusOnNode(focusNodeId || (nodeId && typeof nodeId === 'string' && nodeId.endsWith('-parallel') ? this.getAiParallelSourceNodeId(nodeId) : nodeId));
        },
        onDebugCancelSetNext() {
            this.restoreDebugStepsBeforeSetNext();
            this.debugOverrideNextNodeId = '';
            this.debugOverrideNextStepIndex = null;
        },
        restoreDebugStepsBeforeSetNext() {
            if (this.debugStepsBeforeSetNext) {
                this.debugSteps = this.debugStepsBeforeSetNext;
                this.debugStepsBeforeSetNext = null;
                this.debugStepCount = this.getVisibleDebugStepCount();
            }
        },
        // ============================================
        // END DEBUG MODE METHODS
        // ============================================

        convertToFlatStructure() {
            const flatNodes = [];

            // First pass: create all nodes without targets
            this.tempNode.forEach(node => {
                const flatNode = {
                    id: node.id,
                    nodeType: node.data.type,
                    position: node.position,
                    data: this.extractNodeData(node),
                    targets: []
                };
                flatNodes.push(flatNode);
            });

            // Second pass: populate targets based on edges
            this.tempEdge.forEach(edge => {
                const sourceNode = flatNodes.find(node => node.id === edge.source);
                if (sourceNode) {
                    const targetInfo = {
                        nodeId: edge.target,
                        sourceHandle: edge.sourceHandle || null
                    };
                    sourceNode.targets.push(targetInfo);
                }
            });

            return flatNodes;
        },
        convertFromFlatStructure(flatNodes) {
            const flowNodes = [];
            const flowEdges = [];

            flatNodes.forEach(flatNode => {
                // Parse string data back to objects (handle both string and object for backward compatibility)
                const position = typeof flatNode.position === 'string'
                    ? JSON.parse(flatNode.position)
                    : flatNode.position;

                const data = typeof flatNode.data === 'string'
                    ? JSON.parse(flatNode.data)
                    : flatNode.data;

                const targets = typeof flatNode.targets === 'string'
                    ? JSON.parse(flatNode.targets)
                    : (flatNode.targets || []);

                // Create flow node
                const expandedData = this.expandNodeData(flatNode.nodeType, data);

                const flowNode = {
                    id: flatNode.id,
                    type: 'flow-node',
                    position: position,
                    data: {
                        type: flatNode.nodeType,
                        label: data.label,
                        ...expandedData
                    },
                    selected: false
                };
                flowNodes.push(flowNode);

                // Create edges from targets
                if (targets && Array.isArray(targets)) {
                    targets.forEach((target, index) => {
                        const edge = {
                            id: `e${flatNode.id}-${target.nodeId}-${this.generateGuid()}`,
                            source: flatNode.id,
                            target: target.nodeId,
                            sourceHandle: target.sourceHandle || null,
                            targetHandle: 'input',
                            type: 'flow-edge',
                            markerEnd: MarkerType.ArrowClosed,
                            animated: false,
                            showButton: false,
                            data: {
                                // Set isParallel based on source node's isParallel property
                                // Only AI Model nodes have isParallel property
                                // messageInput and intent nodes create parallel flow but don't have isParallel property themselves
                                isParallel: (flatNode.nodeType === 'model' && expandedData.isParallel === true) ||
                                           (flatNode.nodeType === 'messageInput' && targets.length >= 2 && targets.every(t => {
                                               const targetNode = flatNodes.find(fn => fn.id === t.nodeId);
                                               return targetNode && targetNode.nodeType === 'model';
                                           })) ||
                                           (flatNode.nodeType === 'intent' && target.sourceHandle && target.sourceHandle.endsWith('-main') && 
                                               targets.filter(t => t.sourceHandle && t.sourceHandle.endsWith('-main')).length >= 2 && 
                                               targets.filter(t => t.sourceHandle && t.sourceHandle.endsWith('-main')).every(t => {
                                                   const targetNode = flatNodes.find(fn => fn.id === t.nodeId);
                                                   return targetNode && targetNode.nodeType === 'model';
                                               }))
                            }
                        };

                        flowEdges.push(edge);
                    });
                }
            });

            return { nodes: flowNodes, edges: flowEdges };
        },

        findRootNodes() {
            const allNodeIds = new Set(this.tempNode.map(node => node.id));
            const targetNodeIds = new Set();

            // Collect all target node IDs from edges
            this.tempEdge.forEach(edge => {
                targetNodeIds.add(edge.target);
            });

            // Root nodes are those that are not targets of any edge
            const rootNodes = Array.from(allNodeIds).filter(nodeId => !targetNodeIds.has(nodeId));

            return rootNodes;
        },

        // Recursively build nested node structure
        buildNestedNode(nodeId, nodeMap, processedNodes) {
            if (processedNodes.has(nodeId)) {
                return null; // Avoid circular references
            }

            processedNodes.add(nodeId);
            const node = nodeMap.get(nodeId);

            if (!node) {
                return null;
            }

            // Find all outgoing edges from this node
            const outgoingEdges = this.tempEdge.filter(edge => edge.source === nodeId);

            // Group edges by source handle for intent nodes with multiple outputs
            const edgeGroups = this.groupEdgesBySourceHandle(outgoingEdges, node);

            // Build target nodes
            node.target = edgeGroups.map(group => {
                const targetNodes = group.edges.map(edge => {
                    const targetNode = this.buildNestedNode(edge.target, nodeMap, new Set(processedNodes));
                    if (targetNode) {
                        // Add source information to target node
                        targetNode.source = group.sourceHandle ? `${nodeId}.${group.sourceHandle}` : nodeId;
                    }
                    return targetNode;
                }).filter(targetNode => targetNode !== null);

                return targetNodes;
            }).flat();

            return node;
        },
        // Group edges by source handle (for nodes with multiple outputs like intent, switch)
        groupEdgesBySourceHandle(edges, sourceNode) {
            if (sourceNode.nodeType === 'intent' || sourceNode.nodeType === 'switch') {
                // Group by source handle
                const groups = new Map();

                edges.forEach(edge => {
                    const sourceHandle = edge.sourceHandle || 'default';
                    if (!groups.has(sourceHandle)) {
                        groups.set(sourceHandle, {
                            sourceHandle: sourceHandle === 'default' ? null : sourceHandle,
                            edges: []
                        });
                    }
                    groups.get(sourceHandle).edges.push(edge);
                });

                return Array.from(groups.values());
            } else {
                // Single group for nodes with single output
                return [{
                    sourceHandle: null,
                    edges: edges
                }];
            }
        },
        // Extract node-specific data based on node type
        extractNodeData(node) {
            const baseData = {
                // label: node.data.label
            };

            switch (node.data.type) {
                case 'messageInput':
                    return {
                        ...baseData,
                        modelDisplayId: [
                            ...(Array.isArray(node.data.selectedModel)
                                ? node.data.selectedModel
                                : (node.data.selectedModel ? [node.data.selectedModel] : [])),
                            
                        ],
                        unableToAccessModel: [
                            ...(Array.isArray(this.unableToAccessModel)
                                ? this.unableToAccessModel
                                : (this.unableToAccessModel ? [this.unableToAccessModel] : []))
                        ]
                    };

                case 'model':
                    return {
                        ...baseData,
                        // Basic model configuration
                        platform: node.data.platform,
                        provider: node.data.provider,
                        model: node.data.model,
                        apiVersion: node.data.apiVersion,
                        modelDisplayId: node.data.selectedModel,
                        systemPrompt: node.data.systemPrompt,
                        useBaseModelPrompt: node.data.useBaseModelPrompt,

                        // Parallel execution settings
                        isParallel: node.data.isParallel || false,

                        // AI Parameters
                        temperature: node.data.temperature,
                        maxTokens: node.data.maxTokens,
                        topP: node.data.topP,
                        frequencyPenalty: node.data.frequencyPenalty,
                        presencePenalty: node.data.presencePenalty,
                        stopSequence: node.data.stopSequence,

                        // Behavior Settings
                        gender: node.data.gender,
                        typingSpeed: node.data.typingSpeed,
                        maxInputLength: node.data.maxInputLength,

                        // Response Options
                        streamResponse: node.data.streamResponse,
                        jsonResponse: node.data.jsonResponse,
                        enableTime: node.data.enableTime,
                        timeZone: node.data.timeZone,

                        // Memory Settings (convert memoryDuration to negative like ModelManagement.vue)
                        memoryTurn: node.data.memoryTurn,
                        memoryDuration: node.data.memoryDuration > 0
                            ? -node.data.memoryDuration
                            : node.data.memoryDuration,
                        shortTermMemory: node.data.shortTermMemory,
                        longTermMemory: node.data.longTermMemory,
                        isChainMassage: node.data.isChainMassage,
                        lenChainMassage: node.data.lenChainMassage,
                        timeSecondChainMassage: node.data.timeSecondChainMassage,

                        // Line Configuration
                        loadingBar: node.data.loadingBar,
                        loadingBarDuration: node.data.loadingBarDuration,
                        showLineName: node.data.showLineName,
                        monitorLineUserId: node.data.monitorLineUserId,
                        monitorSocialID: node.data.monitorSocialID,
                        forbiddenWords: node.data.forbiddenWords
                    };

                case 'orchestrator':
                    return {
                        ...baseData,
                        orchestratorName: node.data.orchestratorName,
                        provider: node.data.provider,
                        model: node.data.model,
                        modelId: node.data.modelId,
                        modelDisplayId: node.data.modelDisplayId,
                        temperature: node.data.temperature,
                        maxTokens: node.data.maxTokens,
                        topP: node.data.topP,
                        frequencyPenalty: node.data.frequencyPenalty,
                        presencePenalty: node.data.presencePenalty,
                        stopSequence: node.data.stopSequence,
                        streamResponse: node.data.streamResponse,
                        jsonResponse: node.data.jsonResponse,
                        memoryTurn: node.data.memoryTurn,
                        memoryDuration: node.data.memoryDuration,
                        shortTermMemory: node.data.shortTermMemory,
                        longTermMemory: node.data.longTermMemory,
                        systemPrompt: node.data.systemPrompt,
                        guardrailPrompt: node.data.guardrailPrompt,
                        confidenceThreshold: node.data.confidenceThreshold,
                        enableMultiAgent: node.data.enableMultiAgent,
                        multiAgentStrategy: node.data.multiAgentStrategy,
                        enableCache: node.data.enableCache,
                        cacheTTLMinutes: node.data.cacheTTLMinutes,
                        cacheScope: node.data.cacheScope,
                        enableSemanticCache: node.data.enableSemanticCache,
                        similarityThreshold: node.data.similarityThreshold,
                        showLineName: node.data.showLineName,
                        loadingBar: node.data.loadingBar,
                        loadingBarDuration: node.data.loadingBarDuration,
                        monitorLineUserId: node.data.monitorLineUserId,
                        monitorSocialID: node.data.monitorSocialID,
                        forbiddenWords: node.data.forbiddenWords,
                        gender: node.data.gender,
                        typingSpeed: node.data.typingSpeed,
                        maxInputLength: node.data.maxInputLength,
                        enableTime: node.data.enableTime,
                        timeZone: node.data.timeZone,
                        enableTransactionId: node.data.enableTransactionId,
                        enableInputMessageReplace: node.data.enableInputMessageReplace,
                        enableVoicePromptReplace: node.data.enableVoicePromptReplace,
                        enableLanguageReplace: node.data.enableLanguageReplace
                    };

                case 'agent':
                    return {
                        ...baseData,
                        agentName: node.data.agentName,
                        agentDescription: node.data.agentDescription,
                        personaPrompt: node.data.personaPrompt,
                        selectedTools: node.data.selectedTools || [],
                        enableAgentToolDecision: node.data.enableAgentToolDecision,
                        useCustomAIModel: node.data.useCustomAIModel,
                        agentProvider: node.data.agentProvider,
                        agentModel: node.data.agentModel,
                        agentTemperature: node.data.agentTemperature,
                        agentMaxTokens: node.data.agentMaxTokens,
                        agentTopP: node.data.agentTopP,
                        agentFrequencyPenalty: node.data.agentFrequencyPenalty,
                        agentPresencePenalty: node.data.agentPresencePenalty,
                        agentStopSequence: node.data.agentStopSequence,
                        agentStreamResponse: node.data.agentStreamResponse,
                        agentJsonResponse: node.data.agentJsonResponse,
                        agentGender: node.data.agentGender,
                        agentTypingSpeed: node.data.agentTypingSpeed,
                        agentMaxInputLength: node.data.agentMaxInputLength,
                        agentMemoryTurn: node.data.agentMemoryTurn,
                        agentMemoryDuration: node.data.agentMemoryDuration,
                        agentShortTermMemory: node.data.agentShortTermMemory,
                        agentLongTermMemory: node.data.agentLongTermMemory,
                        agentEnableTime: node.data.agentEnableTime,
                        agentTimeZone: node.data.agentTimeZone,
                        agentEnableTransactionId: node.data.agentEnableTransactionId,
                        knowledgeConnections: node.data.knowledgeConnections || this.createDefaultKnowledgeConnections()
                    };

                case 'tool':
                    return {
                        ...baseData,
                        toolName: node.data.toolName,
                        toolDescription: node.data.toolDescription,
                        toolType: node.data.toolType,
                        masterToolId: node.data.masterToolId,
                        endpoint: node.data.endpoint,
                        method: node.data.method,
                        headers: node.data.headers || [],
                        body: node.data.body,
                        textContent: node.data.textContent
                    };

                case 'flowTool':
                    return {
                        ...baseData,
                        flowToolName: node.data.flowToolName,
                        flowToolDescription: node.data.flowToolDescription,
                        internalNodes: node.data.internalNodes || [],
                        internalEdges: node.data.internalEdges || [],
                        internalNodeCount: this.getInternalProcessingNodeCount(node.data.internalNodes || []),
                        showWarning: !this.validateInternalFlow(node.data.internalNodes || [], node.data.internalEdges || [])
                    };

                case 'flowInput':
                    return {
                        ...baseData,
                        label: node.data.label || 'Flow Input',
                        isFixed: node.data.isFixed !== undefined ? node.data.isFixed : true
                    };

                case 'flowOutput':
                    return {
                        ...baseData,
                        label: node.data.label || 'Flow Output',
                        outputTemplate: node.data.outputTemplate || '{currentResponse}',
                        isFixed: node.data.isFixed !== undefined ? node.data.isFixed : true
                    };

                case 'intent':
                    return {
                        ...baseData,
                        intentGroupName: node.data.intentGroupName,
                        intents: node.data.intents || []
                    };

                case 'smartIntent':
                    return {
                        ...baseData,
                        smartIntentGroupName: node.data.smartIntentGroupName,
                        smartIntents: node.data.smartIntents || [],
                        smartIntentFallback: node.data.smartIntentFallback || {}
                    };

                case 'advancedRouter':
                    return {
                        ...baseData,
                        advancedRouterName: node.data.advancedRouterName,
                        advancedRouterPrompt: node.data.advancedRouterPrompt,
                        advancedRouterConfidenceThreshold: node.data.advancedRouterConfidenceThreshold != null ? node.data.advancedRouterConfidenceThreshold : 0.5,
                        advancedRouterMaxDepth: node.data.advancedRouterMaxDepth || 2,
                        advancedRouterOutputs: node.data.advancedRouterOutputs || [],
                        customSchemaEnabled: !!node.data.customSchemaEnabled,
                        schemaFieldMapping: node.data.schemaFieldMapping || {
                            intentField: 'capability_intent',
                            confidenceField: 'confidence',
                            subIntentsField: 'intents'
                        }
                    };

                case 'jsonCondition':
                    return {
                        ...baseData,
                        jsonConditionName: node.data.jsonConditionName,
                        matchMode: node.data.matchMode,
                        conditions: node.data.conditions || [],
                        matchLabel: node.data.matchLabel,
                        fallbackLabel: node.data.fallbackLabel
                    };

                case 'switch':
                    return {
                        ...baseData,
                        switchName: node.data.switchName,
                        switchOutputs: node.data.switchOutputs || []
                    };

                case 'condition':
                    return {
                        ...baseData,
                        conditionType: node.data.conditionType,
                        leftOperand: node.data.leftOperand,
                        operator: node.data.operator,
                        rightOperand: node.data.rightOperand,
                        conditionExpression: node.data.conditionExpression,
                        trueLabel: node.data.trueLabel,
                        falseLabel: node.data.falseLabel,
                        minValue: node.data.minValue,
                        maxValue: node.data.maxValue,
                        expectedValue: node.data.expectedValue
                    };

                case 'delay':
                    return {
                        ...baseData,
                        delayType: node.data.delayType,
                        delayValue: node.data.delayValue,
                        delayUnit: node.data.delayUnit,
                        randomMin: node.data.randomMin,
                        randomMax: node.data.randomMax,
                        delayMessage: node.data.delayMessage,
                        showProgress: node.data.showProgress,
                        delayExpression: node.data.delayExpression
                    };

                case 'apiTrigger':
                    return {
                        ...baseData,
                        apiUrl: node.data.apiUrl,
                        httpMethod: node.data.httpMethod || 'GET',
                        textResponse: node.data.textResponse,
                        jsonValidate: node.data.jsonValidate,
                        modelJsonValidator: node.data.modelJsonValidator,
                        listHeadersAPITrigger: node.data.listHeadersAPITrigger,
                        listValidation: node.data.listValidation
                    };

                case 'contactAgent':
                    return {
                        ...baseData,
                        contactAgentMessage: node.data.contactAgentMessage,
                        botDisableTime: node.data.botDisableTime,
                    };

                case 'guardrailAI':
                    return {
                        ...baseData,
                        guardrailModel: node.data.guardrailModel,
                        guardrailPrompt: node.data.guardrailPrompt,
                        safetyThreshold: node.data.safetyThreshold,
                        enableLogging: node.data.enableLogging
                    };

                case 'intentAI':
                    return {
                        ...baseData,
                        // Basic model configuration
                        intentaiPlatform: node.data.intentaiPlatform,
                        intentaiProvider: node.data.intentaiProvider,
                        intentaiModel: node.data.intentaiModel,
                        intentAiModelApiVersion: node.data.intentAiModelApiVersion,
                        intentaiSystemPrompt: node.data.intentaiSystemPrompt,
                        intentaiExamples: node.data.intentaiExamples,
                        intentaiConfidence: node.data.intentaiConfidence,
                        intentaiFallbackIntent: node.data.intentaiFallbackIntent,

                        // AI Parameters
                        intentaiTemperature: node.data.intentaiTemperature,
                        intentaiMaxTokens: node.data.intentaiMaxTokens,
                        intentaiTopP: node.data.intentaiTopP,
                        intentaiFrequencyPenalty: node.data.intentaiFrequencyPenalty,
                        intentaiPresencePenalty: node.data.intentaiPresencePenalty,
                        intentaiStopSequence: node.data.intentaiStopSequence,

                        // Behavior Settings
                        intentaiGender: node.data.intentaiGender,
                        intentaiTypingSpeed: node.data.intentaiTypingSpeed,
                        intentaiMaxInputLength: node.data.intentaiMaxInputLength,

                        // Response Options
                        intentaiStreamResponse: node.data.intentaiStreamResponse,
                        intentaiEnableTime: node.data.intentaiEnableTime,
                        intentaiTimeZone: node.data.intentaiTimeZone,
                        intentaiJsonResponse: node.data.intentaiJsonResponse,
                        

                        // Memory Settings (convert memoryDuration to negative like ModelManagement.vue)
                        intentaiMemoryTurn: node.data.intentaiMemoryTurn,
                        intentaiMemoryDuration: node.data.intentaiMemoryDuration > 0
                            ? -node.data.intentaiMemoryDuration
                            : node.data.intentaiMemoryDuration,
                        intentaiShortTermMemory: node.data.intentaiShortTermMemory,
                        intentaiLongTermMemory: node.data.intentaiLongTermMemory,
                        intentaiIsChainMassage: node.data.intentaiIsChainMassage,
                        intentaiLenChainMassage: node.data.intentaiLenChainMassage,
                        intentaiTimeSecondChainMassage: node.data.intentaiTimeSecondChainMassage,

                        // Line Configuration
                        intentaiShowLineName: node.data.intentaiShowLineName,
                        intentaiLoadingBar: node.data.intentaiLoadingBar,
                        intentaiLoadingBarDuration: node.data.intentaiLoadingBarDuration,
                        intentaiMonitorLineUserId: node.data.intentaiMonitorLineUserId,
                        intentaiMonitorSocialID: node.data.intentaiMonitorSocialID,
                        intentaiForbiddenWords: node.data.intentaiForbiddenWords
                    };

                case 'documents':
                    return {
                        ...baseData,
                        document: node.data.document,
                        textReplacement: node.data.textReplacement,
                        topNChunks: node.data.topNChunks,
                        cosineSimilarity: node.data.cosineSimilarity,
                        selectedDocumentData: node.data.selectedDocumentData || [],
                        enableSpecificDataSelection: node.data.enableSpecificDataSelection || false,
                        documentVectorName: ''
                    };

                case 'databaseIntegration':
                    return {
                        ...baseData,
                        tableName: node.data.tableName,
                        sqlExtractionModel: node.data.sqlExtractionModel,
                        promptSqlNoData: node.data.promptSqlNoData
                    };

                // S15-P1 — ห้ามพึ่ง default (baseData ว่าง = ข้อมูลหายตอน Save · G1)
                case 'knowledgeGraph':
                    return {
                        ...baseData,
                        // ค่าเป็น graphKey · เรียงตามที่ผู้ใช้เลือก (ชื่อ field ตาม ajt · D3)
                        knowledgeGraphIds: Array.isArray(node.data.knowledgeGraphIds) ? node.data.knowledgeGraphIds.filter(Boolean) : [],
                        // serialize ครบ 10 คีย์ PascalCase เสมอแม้ยังไม่เลือกกราฟ (ajt :15597-15611)
                        kgRetrievalConfig: normalizeKgRetrievalConfig(node.data.kgRetrievalConfig)
                    };

                case 'nexaWaterfallSearch':
                    return {
                        ...baseData,
                        // เพดาน 10 · normalize ตัด _key runtime ทิ้ง + แปลงตัวเลขจาก v-text-field ให้เป็น number
                        // ไม่ส่ง citationPrompt แล้ว (ตัดช่องออก 2026-10-20) ⇒ backend ได้ null ⇒ ใช้กติกามาตรฐาน · ค่าที่เคยแก้ไว้หายตอนบันทึกครั้งถัดไป
                        graphTiers: (Array.isArray(node.data.graphTiers) ? node.data.graphTiers : []).slice(0, WF_MAX_TIERS).map(normalizeWaterfallTier)
                    };

                case 'products':
                    return {
                        ...baseData,
                        productGroupList: Array.isArray(node.data.productGroupList)
                            ? node.data.productGroupList.map(item => ({
                                id: item.id,
                                name: item.name
                            }))
                            : [],
                        productTopNChunks: node.data.productTopNChunks,
                        productCosineSimilarity: node.data.productCosineSimilarity
                    };

                case 'apiknowledge': {
                    const knowledgeHttpMethod = node.data.knowledgeHttpMethod || 'GET';
                    const tokenHttpMethod = node.data.tokenHttpMethod || 'POST';

                    return {
                        ...baseData,
                        knowledgeApiUrl: node.data.knowledgeApiUrl,
                        knowledgeHttpMethod,
                        knowledgeRequestBody: this.methodAllowsRequestBody(knowledgeHttpMethod) ? (node.data.knowledgeRequestBody || '') : '',
                        knowledgeModelJsonValidator: node.data.knowledgeModelJsonValidator || null,
                        responseDataPath: node.data.responseDataPath,
                        listHeaders: node.data.listHeaders || [],
                        listParams: node.data.listParams || [],
                        enableToken: node.data.enableToken || false,
                        tokenAuthMode: node.data.tokenAuthMode || 'new',
                        tokenName: node.data.tokenName || '',
                        useExistingTokenName: node.data.useExistingTokenName || '',
                        tokenUrl: node.data.tokenUrl || '',
                        tokenHttpMethod,
                        tokenRequestBody: this.methodAllowsRequestBody(tokenHttpMethod) ? (node.data.tokenRequestBody || '') : '',
                        tokenHeaders: node.data.tokenHeaders || [],
                        tokenParams: node.data.tokenParams || [],
                        tokenResponsePath: node.data.tokenResponsePath || 'access_token',
                    };
                }

                case 'email':
                    return {
                        ...baseData,
                        emailProvider: node.data.emailProvider || 'mailgun',
                        toEmail: node.data.toEmail || '',
                        ccEmails: node.data.ccEmails || [],
                        bccEmails: node.data.bccEmails || [],
                        subject: node.data.subject || '',
                        emailTemplate: node.data.emailTemplate || 'custom',
                        emailBody: node.data.emailBody || '',
                        isHtml: node.data.isHtml !== undefined ? node.data.isHtml : false,
                        useCentralConfig: node.data.useCentralConfig !== undefined ? node.data.useCentralConfig : true,
                        mailConfig: node.data.mailConfig || null,
                    };

                case 'sharepoint':
                    return {
                        ...baseData,
                        sharepoint: {
                            siteUrl: node.data.sharepoint?.siteUrl || '',
                            tenantId: node.data.sharepoint?.tenantId || '',
                            clientId: node.data.sharepoint?.clientId || '',
                            clientSecret: node.data.sharepoint?.clientSecret || '',
                            folderPath: node.data.sharepoint?.folderPath || '',
                            filename: node.data.sharepoint?.filename || ''
                        }
                    };

                case 'merge':
                    return {
                        ...baseData,
                        mergeOutputFormat: node.data.mergeOutputFormat || 'text',
                        mergePromptUsage: node.data.mergePromptUsage || 'system',
                        mergeSeparator: node.data.mergeSeparator || 'hr',
                        mergeCustomSeparator: node.data.mergeCustomSeparator || '',
                        mergeIncludeLabels: node.data.mergeIncludeLabels !== undefined ? node.data.mergeIncludeLabels : true,
                        mergeLabelFormat: node.data.mergeLabelFormat || ''
                    };

                default:
                    return baseData;
            }
        },

        // Validate nested flow structure
        validateNestedFlow(flowData) {
            const errors = [];

            // Check for required flow properties
            if (!flowData.name || flowData.name.trim() === '') {
                errors.push('Flow name is required');
            }

            // Check for at least one root node
            if (!flowData.nodes || flowData.nodes.length === 0) {
                errors.push('Flow must contain at least one root node');
            }

            // Validate nested structure
            const allNodeIds = new Set();
            const validateNode = (node, path = '') => {
                if (!node || !node.id) {
                    errors.push(`Node at path ${path} is missing ID`);
                    return;
                }

                if (allNodeIds.has(node.id)) {
                    errors.push(`Duplicate node ID found: ${node.id} at path ${path}`);
                    return;
                }

                allNodeIds.add(node.id);

                if (!node.nodeType) {
                    errors.push(`Node ${node.id} is missing nodeType`);
                }

                // Validate node-specific data
                // this.validateNodeData(node, errors);

                // Recursively validate target nodes
                if (node.target && Array.isArray(node.target)) {
                    node.target.forEach((targetNode, index) => {
                        validateNode(targetNode, `${path}.target[${index}]`);
                    });
                }
            };

            flowData.nodes.forEach((rootNode, index) => {
                validateNode(rootNode, `nodes[${index}]`);
            });

            return {
                isValid: errors.length === 0,
                errors
            };
        },

        // Validate specific node data
        validateNodeData(node, errors) {
            switch (node.nodeType) {
                case 'messageInput':
                    const messageInputModels = Array.isArray(node.data.modelDisplayId)
                        ? node.data.modelDisplayId
                        : (node.data.modelDisplayId ? [node.data.modelDisplayId] : []);
                    if (!messageInputModels.length) {
                        errors.push(`Message Input node (${node.id}) requires at least one selected model`);
                    }
                    break;
                case 'model':
                    if (!node.data.model) {
                        errors.push(`AI Model node (${node.id}) requires a selected model`);
                    }
                    break;
                
                case 'intentAI':
                    if (!node.data.intentaiModel) {
                        errors.push(`Intent AI node (${node.id}) requires a selected model`);
                    }
                    break;

                case 'intent':
                    if (!node.data.intents || node.data.intents.length === 0) {
                        errors.push(`Intent node (${node.id}) requires at least one intent`);
                    }
                    break;

                case 'switch':
                    if (!node.data.switchOutputs || node.data.switchOutputs.length < 2) {
                        errors.push(`Switch node (${node.id}) requires at least two output paths`);
                    }
                    break;

                case 'jsonCondition':
                    if (!node.data.conditions || !Array.isArray(node.data.conditions) || node.data.conditions.length === 0) {
                        errors.push(`JSON Condition node (${node.id}) requires at least one rule`);
                    }
                    break;

                case 'apiTrigger':
                    if (!node.data.apiUrl) {
                        errors.push(`API Trigger node (${node.id}) requires API URL`);
                    }
                    break;

                case 'apiknowledge':
                    if (!node.data.knowledgeApiUrl) {
                        errors.push(`Knowledge API node (${node.id}) requires API URL`);
                    }
                    if (node.data.enableToken) {
                        if (node.data.tokenAuthMode === 'new') {
                            if (!node.data.tokenName || node.data.tokenName.trim() === '') {
                                errors.push(`Knowledge API node (${node.id}) requires Token Name when using Get New Token`);
                            }
                            if (!node.data.tokenUrl || node.data.tokenUrl.trim() === '') {
                                errors.push(`Knowledge API node (${node.id}) requires Token URL when using Get New Token`);
                            }
                        } else if (node.data.tokenAuthMode === 'existing') {
                            if (!node.data.useExistingTokenName || node.data.useExistingTokenName.trim() === '') {
                                errors.push(`Knowledge API node (${node.id}) must select an existing token`);
                            }
                        }
                    }
                    break;

                case 'email':
                    if (!node.data.toEmail || node.data.toEmail.trim() === '') {
                        errors.push(`Email node (${node.id}) requires a recipient email address`);
                    } else {
                        // Basic email validation
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (!emailRegex.test(node.data.toEmail.trim())) {
                            errors.push(`Email node (${node.id}) has invalid recipient email format`);
                        }
                    }

                    if (!node.data.subject || node.data.subject.trim() === '') {
                        errors.push(`Email node (${node.id}) requires a subject`);
                    }

                    if (node.data.emailTemplate === 'custom' && (!node.data.emailBody || node.data.emailBody.trim() === '')) {
                        errors.push(`Email node (${node.id}) requires email content when using custom template`);
                    }

                    // Custom mail config validation
                    if (node.data.useCentralConfig === false) {
                        const mc = node.data.mailConfig || {};
                        const method = (mc.emailSendingMethod || '').toUpperCase();
                        if (!method) {
                            errors.push(`Email node (${node.id}) custom config requires Email Sending Method`);
                        } else if (method === 'SMTP') {
                            if (!mc.smtpHost || mc.smtpHost.trim() === '') errors.push(`Email node (${node.id}) custom SMTP requires Host`);
                            if (!mc.smtpPort) errors.push(`Email node (${node.id}) custom SMTP requires Port`);
                            if (!mc.emailHost || mc.emailHost.trim() === '') errors.push(`Email node (${node.id}) custom SMTP requires From email`);
                            if (!mc.hostName || mc.hostName.trim() === '') errors.push(`Email node (${node.id}) custom SMTP requires Display Name`);
                            const auth = (mc.smtpAuthMethod || '').toLowerCase();
                            if (auth === 'oauth2') {
                                if (!mc.smtpOAuth2TenantId) errors.push(`Email node (${node.id}) OAuth2 requires Tenant ID`);
                                if (!mc.smtpOAuth2ClientId) errors.push(`Email node (${node.id}) OAuth2 requires Client ID`);
                                if (!mc.smtpOAuth2ClientSecret) errors.push(`Email node (${node.id}) OAuth2 requires Client Secret`);
                            } else if (!mc.password) {
                                errors.push(`Email node (${node.id}) Basic auth requires Password`);
                            }
                        } else if (method === 'MAILGUN') {
                            if (!mc.mailgunApiUrl) errors.push(`Email node (${node.id}) custom Mailgun requires API URL`);
                            if (!mc.mailgunApiKey) errors.push(`Email node (${node.id}) custom Mailgun requires API Key`);
                            if (!mc.mailFrom) errors.push(`Email node (${node.id}) custom Mailgun requires From email`);
                        }
                    }
                    break;

                case 'sharepoint':
                    if (!node.data.sharepoint || 
                        !node.data.sharepoint.siteUrl || node.data.sharepoint.siteUrl.trim() === '' ||
                        !node.data.sharepoint.tenantId || node.data.sharepoint.tenantId.trim() === '' ||
                        !node.data.sharepoint.clientId || node.data.sharepoint.clientId.trim() === '' ||
                        !node.data.sharepoint.clientSecret || node.data.sharepoint.clientSecret.trim() === '' ||
                        !node.data.sharepoint.folderPath || node.data.sharepoint.folderPath.trim() === '' ||
                        !node.data.sharepoint.filename || node.data.sharepoint.filename.trim() === '') {
                        errors.push(`SharePoint node (${node.id}) requires all configuration fields: Site URL, Tenant ID, Client ID, Client Secret, Folder Path, and Filename`);
                    }
                    break;
            }
        },
        // Expand node data back to full structure
        expandNodeData(nodeType, data) {
            const expanded = { ...data };

            switch (nodeType) {
                case 'messageInput':
                    expanded.selectedModel = Array.isArray(data.modelDisplayId)
                        ? data.modelDisplayId
                        : (data.modelDisplayId ? [data.modelDisplayId] : []);
                    break;
                case 'model':
                    // Basic model configuration
                    expanded.selectedModel = data.modelDisplayId;
                    expanded.platform = data.platform || null;
                    expanded.provider = data.provider || null;
                    expanded.model = data.model || null;
                    expanded.apiVersion = data.apiVersion || null;
                    expanded.systemPrompt = data.systemPrompt || '';
                    expanded.useBaseModelPrompt = data.useBaseModelPrompt !== undefined ? data.useBaseModelPrompt : false;

                    // Parallel execution settings
                    expanded.isParallel = data.isParallel !== undefined ? data.isParallel : false;

                    // AI Parameters
                    expanded.temperature = data.temperature || null;
                    expanded.maxTokens = data.maxTokens || null;
                    expanded.topP = data.topP || null;
                    expanded.frequencyPenalty = data.frequencyPenalty || null;
                    expanded.presencePenalty = data.presencePenalty || null;
                    expanded.stopSequence = data.stopSequence || null;

                    // Behavior Settings
                    expanded.gender = data.gender || 'male';
                    expanded.typingSpeed = data.typingSpeed || null;
                    expanded.maxInputLength = data.maxInputLength || 200000;

                    // Response Options
                    expanded.streamResponse = data.streamResponse !== undefined ? data.streamResponse : false;
                    expanded.jsonResponse = data.jsonResponse !== undefined ? data.jsonResponse : false;
                    expanded.enableTime = data.enableTime || false;
                    expanded.timeZone = data.timeZone || null;

                    // Memory Settings (convert memoryDuration from negative like ModelManagement.vue)
                    expanded.memoryTurn = data.memoryTurn || null;
                    expanded.memoryDuration = data.memoryDuration < 0
                        ? Math.abs(data.memoryDuration)
                        : data.memoryDuration;
                    expanded.shortTermMemory = data.shortTermMemory || null;
                    expanded.longTermMemory = data.longTermMemory !== undefined ? data.longTermMemory : false;
                    expanded.isChainMassage = data.isChainMassage !== undefined ? data.isChainMassage : false;
                    expanded.lenChainMassage = data.lenChainMassage || null;
                    expanded.timeSecondChainMassage = data.timeSecondChainMassage || null;

                    // Line Configuration
                    expanded.loadingBar = data.loadingBar !== undefined ? data.loadingBar : false;
                    expanded.loadingBarDuration = data.loadingBarDuration || 0;
                    expanded.showLineName = data.showLineName !== undefined ? data.showLineName : false;
                    expanded.monitorLineUserId = data.monitorLineUserId || null;
                    expanded.monitorSocialID = data.monitorSocialID || null;
                    expanded.forbiddenWords = data.forbiddenWords || [''];
                    break;
                case 'orchestrator':
                    expanded.label = data.label || 'Orchestrator';
                    expanded.orchestratorName = data.orchestratorName || 'New Orchestrator';
                    expanded.provider = data.provider || null;
                    expanded.model = data.model || null;
                    expanded.modelId = data.modelId || 0;
                    expanded.modelDisplayId = data.modelDisplayId || null;
                    expanded.temperature = data.temperature !== undefined ? data.temperature : 0;
                    expanded.maxTokens = data.maxTokens || null;
                    expanded.topP = data.topP !== undefined ? data.topP : 1;
                    expanded.frequencyPenalty = data.frequencyPenalty || null;
                    expanded.presencePenalty = data.presencePenalty || null;
                    expanded.stopSequence = data.stopSequence || null;
                    expanded.streamResponse = data.streamResponse !== undefined ? data.streamResponse : false;
                    expanded.jsonResponse = data.jsonResponse !== undefined ? data.jsonResponse : false;
                    expanded.memoryTurn = data.memoryTurn || null;
                    expanded.memoryDuration = data.memoryDuration || null;
                    expanded.shortTermMemory = data.shortTermMemory || 200;
                    expanded.longTermMemory = data.longTermMemory !== undefined ? data.longTermMemory : false;
                    expanded.systemPrompt = data.systemPrompt || '';
                    expanded.guardrailPrompt = data.guardrailPrompt || '';
                    expanded.confidenceThreshold = data.confidenceThreshold !== undefined ? data.confidenceThreshold : 0.7;
                    expanded.enableMultiAgent = data.enableMultiAgent !== undefined ? data.enableMultiAgent : false;
                    expanded.multiAgentStrategy = data.multiAgentStrategy || 'pipeline';
                    expanded.enableCache = data.enableCache !== undefined ? data.enableCache : false;
                    expanded.cacheTTLMinutes = data.cacheTTLMinutes || 1440;
                    expanded.cacheScope = data.cacheScope || 'flow';
                    expanded.enableSemanticCache = data.enableSemanticCache !== undefined ? data.enableSemanticCache : false;
                    expanded.similarityThreshold = data.similarityThreshold !== undefined ? data.similarityThreshold : 0.95;
                    expanded.showLineName = data.showLineName !== undefined ? data.showLineName : false;
                    expanded.loadingBar = data.loadingBar !== undefined ? data.loadingBar : false;
                    expanded.loadingBarDuration = data.loadingBarDuration || 0;
                    expanded.monitorLineUserId = data.monitorLineUserId || null;
                    expanded.monitorSocialID = data.monitorSocialID || null;
                    expanded.forbiddenWords = data.forbiddenWords || [''];
                    expanded.enableTime = data.enableTime !== undefined ? data.enableTime : false;
                    expanded.timeZone = data.timeZone || null;
                    expanded.enableTransactionId = data.enableTransactionId !== undefined ? data.enableTransactionId : false;
                    expanded.enableInputMessageReplace = data.enableInputMessageReplace !== undefined ? data.enableInputMessageReplace : false;
                    expanded.enableVoicePromptReplace = data.enableVoicePromptReplace !== undefined ? data.enableVoicePromptReplace : false;
                    expanded.enableLanguageReplace = data.enableLanguageReplace !== undefined ? data.enableLanguageReplace : false;
                    expanded.gender = data.gender || 'male';
                    expanded.typingSpeed = data.typingSpeed || null;
                    expanded.maxInputLength = data.maxInputLength || 200000;
                    break;
                case 'agent':
                    expanded.label = data.label || 'Agent';
                    expanded.agentName = data.agentName || 'New Agent';
                    expanded.agentDescription = data.agentDescription || '';
                    expanded.personaPrompt = data.personaPrompt || '';
                    expanded.selectedTools = Array.isArray(data.selectedTools) ? data.selectedTools : [];
                    expanded.enableAgentToolDecision = data.enableAgentToolDecision !== undefined ? data.enableAgentToolDecision : false;
                    expanded.useCustomAIModel = data.useCustomAIModel !== undefined ? data.useCustomAIModel : false;
                    expanded.agentProvider = data.agentProvider || null;
                    expanded.agentModel = data.agentModel || null;
                    expanded.agentTemperature = data.agentTemperature !== undefined ? data.agentTemperature : 0;
                    expanded.agentMaxTokens = data.agentMaxTokens || null;
                    expanded.agentTopP = data.agentTopP !== undefined ? data.agentTopP : 1;
                    expanded.agentFrequencyPenalty = data.agentFrequencyPenalty || null;
                    expanded.agentPresencePenalty = data.agentPresencePenalty || null;
                    expanded.agentStopSequence = data.agentStopSequence || null;
                    expanded.agentStreamResponse = data.agentStreamResponse !== undefined ? data.agentStreamResponse : false;
                    expanded.agentJsonResponse = data.agentJsonResponse !== undefined ? data.agentJsonResponse : false;
                    expanded.agentGender = data.agentGender || 'male';
                    expanded.agentTypingSpeed = data.agentTypingSpeed || null;
                    expanded.agentMaxInputLength = data.agentMaxInputLength || 200000;
                    expanded.agentMemoryTurn = data.agentMemoryTurn || null;
                    expanded.agentMemoryDuration = data.agentMemoryDuration || null;
                    expanded.agentShortTermMemory = data.agentShortTermMemory || 200;
                    expanded.agentLongTermMemory = data.agentLongTermMemory !== undefined ? data.agentLongTermMemory : false;
                    expanded.agentEnableTime = data.agentEnableTime !== undefined ? data.agentEnableTime : false;
                    expanded.agentTimeZone = data.agentTimeZone || null;
                    expanded.agentEnableTransactionId = data.agentEnableTransactionId !== undefined ? data.agentEnableTransactionId : false;
                    expanded.knowledgeConnections = {
                        ...this.createDefaultKnowledgeConnections(),
                        ...(data.knowledgeConnections || {})
                    };
                    break;
                case 'tool':
                    expanded.label = data.label || 'Tool';
                    expanded.toolName = data.toolName || 'New Tool';
                    expanded.toolDescription = data.toolDescription || '';
                    expanded.toolType = data.toolType || 'master';
                    expanded.masterToolId = data.masterToolId || null;
                    expanded.endpoint = data.endpoint || '';
                    expanded.method = data.method || 'GET';
                    expanded.headers = Array.isArray(data.headers) ? data.headers : [];
                    expanded.body = data.body || '';
                    expanded.textContent = data.textContent || '';
                    break;
                case 'flowTool':
                    expanded.label = data.label || 'Flow Tool';
                    expanded.flowToolName = data.flowToolName || 'New Flow Tool';
                    expanded.flowToolDescription = data.flowToolDescription || '';
                    expanded.internalNodes = Array.isArray(data.internalNodes) ? data.internalNodes : [];
                    expanded.internalEdges = Array.isArray(data.internalEdges) ? data.internalEdges : [];
                    expanded.internalNodeCount = this.getInternalProcessingNodeCount(expanded.internalNodes);
                    expanded.showWarning = !this.validateInternalFlow(expanded.internalNodes, expanded.internalEdges);
                    break;
                case 'flowInput':
                    expanded.label = data.label || 'Flow Input';
                    expanded.isFixed = data.isFixed !== undefined ? data.isFixed : true;
                    break;
                case 'flowOutput':
                    expanded.label = data.label || 'Flow Output';
                    expanded.outputTemplate = data.outputTemplate || '{currentResponse}';
                    expanded.isFixed = data.isFixed !== undefined ? data.isFixed : true;
                    break;
                case 'intentAI':
                    // Basic intentAI configuration
                    expanded.intentaiPlatform = data.intentaiPlatform || data.platform || null;
                    expanded.intentaiProvider = data.intentaiProvider || data.provider || null;
                    // Handle intentaiModel - extract single value from array if needed
                    expanded.intentaiModel = Array.isArray(data.intentaiModel)
                        ? (data.intentaiModel.length > 0 ? data.intentaiModel[0] : null)
                        : (data.intentaiModel || data.model || null);
                    expanded.intentAiModelApiVersion = data.intentAiModelApiVersion || null;
                    expanded.intentaiSystemPrompt = data.intentaiSystemPrompt || data.intentSystemPrompt || '';
                    expanded.intentaiExamples = data.intentaiExamples || data.intentExamples || '';
                    expanded.intentaiConfidence = data.intentaiConfidence || data.intentConfidence || 75;
                    expanded.intentaiFallbackIntent = data.intentaiFallbackIntent || data.fallbackIntent || 'unknown';

                    // AI Parameters
                    expanded.intentaiTemperature = data.intentaiTemperature || data.temperature || null;
                    expanded.intentaiMaxTokens = data.intentaiMaxTokens || data.maxTokens || null;
                    expanded.intentaiTopP = data.intentaiTopP || data.topP || null;
                    expanded.intentaiFrequencyPenalty = data.intentaiFrequencyPenalty || data.frequencyPenalty || null;
                    expanded.intentaiPresencePenalty = data.intentaiPresencePenalty || data.presencePenalty || null;
                    expanded.intentaiStopSequence = data.intentaiStopSequence || data.stopSequence || null;

                    // Behavior Settings
                    expanded.intentaiGender = data.intentaiGender || data.gender || 'male';
                    expanded.intentaiTypingSpeed = data.intentaiTypingSpeed || data.typingSpeed || null;
                    expanded.intentaiMaxInputLength = data.intentaiMaxInputLength || data.maxInputLength || 200000;

                    // Response Options
                    expanded.intentaiStreamResponse = data.intentaiStreamResponse !== undefined ? data.intentaiStreamResponse : (data.streamResponse !== undefined ? data.streamResponse : false);
                    expanded.intentaiJsonResponse = data.intentaiJsonResponse !== undefined ? data.intentaiJsonResponse : (data.jsonResponse !== undefined ? data.jsonResponse : false);
                    expanded.intentaiEnableTime = data.intentaiEnableTime !== undefined ? data.intentaiEnableTime : (data.enableTime || false);
                    expanded.intentaiTimeZone = data.intentaiTimeZone || data.timeZone || null;

                    // Memory Settings (convert memoryDuration from negative like ModelManagement.vue)
                    expanded.intentaiMemoryTurn = data.intentaiMemoryTurn || data.memoryTurn || 5;
                    expanded.intentaiMemoryDuration = data.intentaiMemoryDuration < 0
                        ? Math.abs(data.intentaiMemoryDuration)
                        : (data.memoryDuration < 0 ? Math.abs(data.memoryDuration) : data.intentaiMemoryDuration || data.memoryDuration);
                    expanded.intentaiShortTermMemory = data.intentaiShortTermMemory || data.shortTermMemory || null;
                    expanded.intentaiLongTermMemory = data.intentaiLongTermMemory !== undefined ? data.intentaiLongTermMemory : (data.longTermMemory !== undefined ? data.longTermMemory : false);
                    expanded.intentaiIsChainMassage = data.intentaiIsChainMassage !== undefined ? data.intentaiIsChainMassage : (data.isChainMassage !== undefined ? data.isChainMassage : false);
                    expanded.intentaiLenChainMassage = data.intentaiLenChainMassage || data.lenChainMassage || null;
                    expanded.intentaiTimeSecondChainMassage = data.intentaiTimeSecondChainMassage || data.timeSecondChainMassage || null;

                    // Line Configuration
                    expanded.intentaiShowLineName = data.intentaiShowLineName !== undefined ? data.intentaiShowLineName : (data.showLineName !== undefined ? data.showLineName : false);
                    expanded.intentaiLoadingBar = data.intentaiLoadingBar !== undefined ? data.intentaiLoadingBar : (data.loadingBar !== undefined ? data.loadingBar : false);
                    expanded.intentaiLoadingBarDuration = data.intentaiLoadingBarDuration || data.loadingBarDuration || 0;
                    expanded.intentaiMonitorLineUserId = data.intentaiMonitorLineUserId || data.monitorLineUserId || null;
                    expanded.intentaiMonitorSocialID = data.intentaiMonitorSocialID || data.monitorSocialID || null;
                    expanded.intentaiForbiddenWords = data.intentaiForbiddenWords || data.forbiddenWords || [''];
                    break;
                case 'intent':
                    // Ensure required arrays exist
                    expanded.intents = data.intents || [];
                    expanded.actions = data.actions || [];
                    expanded.outputs = data.outputs || [];
                    break;
                case 'smartIntent':
                    // Ensure all smartIntent properties are properly loaded
                    expanded.smartIntentGroupName = data.smartIntentGroupName || '';
                    expanded.smartIntents = data.smartIntents || [];
                    
                    // Process each smartIntent to ensure forbiddenWords have empty item at the end
                    if (expanded.smartIntents && expanded.smartIntents.length > 0) {
                        expanded.smartIntents.forEach(smartIntent => {
                            if (smartIntent.mainAction) {
                                if (smartIntent.mainAction.platform === undefined) {
                                    smartIntent.mainAction.platform = null;
                                }
                                // Ensure apiVersion is restored
                                if (smartIntent.mainAction.apiVersion === undefined) {
                                    smartIntent.mainAction.apiVersion = null;
                                }

                                // Ensure forbiddenWords array exists and has empty item at the end
                                if (smartIntent.mainAction.forbiddenWords && Array.isArray(smartIntent.mainAction.forbiddenWords)) {
                                    // Only add empty item if the last item is not already empty
                                    const lastItem = smartIntent.mainAction.forbiddenWords[smartIntent.mainAction.forbiddenWords.length - 1];
                                    if (lastItem !== '') {
                                        smartIntent.mainAction.forbiddenWords.push('');
                                    }
                                } else {
                                    smartIntent.mainAction.forbiddenWords = [''];
                                }
                            }
                        });
                    }
                    
                    expanded.smartIntentFallback = data.smartIntentFallback || {
                        useAI: false,
                        platform: null,
                        provider: null,
                        model: null,
                        apiVersion: null,
                        gender: 'male',
                        typingSpeed: null,
                        maxInputLength: 200000,
                        streamResponse: false,
                        jsonResponse: false,
                        enableTime: null,
                        timeZone: null,
                        temperature: null,
                        topP: null,
                        frequencyPenalty: null,
                        presencePenalty: null,
                        maxTokens: null,
                        stopSequence: null,
                        memoryTurn: null,
                        memoryDuration: null,
                        shortTermMemory: null,
                        longTermMemory: false,
                        isChainMassage: false,
                        lenChainMassage: null,
                        timeSecondChainMassage: null,
                        forbiddenWords: ['']
                    };
                    
                    // Ensure fallback apiVersion is restored
                    if (expanded.smartIntentFallback.platform === undefined) {
                        expanded.smartIntentFallback.platform = null;
                    }
                    if (expanded.smartIntentFallback.apiVersion === undefined) {
                        expanded.smartIntentFallback.apiVersion = null;
                    }
                    
                    // Ensure fallback forbiddenWords has empty item at the end
                    if (expanded.smartIntentFallback.forbiddenWords && Array.isArray(expanded.smartIntentFallback.forbiddenWords)) {
                        const lastItem = expanded.smartIntentFallback.forbiddenWords[expanded.smartIntentFallback.forbiddenWords.length - 1];
                        if (lastItem !== '') {
                            expanded.smartIntentFallback.forbiddenWords.push('');
                        }
                    } else {
                        expanded.smartIntentFallback.forbiddenWords = [''];
                    }
                    break;
                case 'advancedRouter':
                    expanded.advancedRouterName = data.advancedRouterName || '';
                    expanded.advancedRouterPrompt = data.advancedRouterPrompt || '';
                    expanded.advancedRouterConfidenceThreshold = data.advancedRouterConfidenceThreshold != null ? data.advancedRouterConfidenceThreshold : 0.5;
                    expanded.advancedRouterMaxDepth = data.advancedRouterMaxDepth || 2;
                    expanded.advancedRouterOutputs = Array.isArray(data.advancedRouterOutputs) ? data.advancedRouterOutputs : [];
                    expanded.customSchemaEnabled = !!data.customSchemaEnabled;
                    expanded.schemaFieldMapping = data.schemaFieldMapping || {
                        intentField: 'capability_intent',
                        confidenceField: 'confidence',
                        subIntentsField: 'intents'
                    };
                    break;
                case 'jsonCondition':
                    expanded.jsonConditionName = data.jsonConditionName || 'New JSON Condition';
                    expanded.matchMode = data.matchMode || 'all';
                    expanded.conditions = Array.isArray(data.conditions) ? data.conditions : [];
                    expanded.matchLabel = data.matchLabel || 'Match';
                    expanded.fallbackLabel = data.fallbackLabel || 'Fallback';
                    break;
                case 'contactAgent':
                    // Ensure all contact agent properties are properly loaded
                    expanded.departmentName = data.departmentName || '';
                    expanded.contactAgentMessage = data.contactAgentMessage || '';
                    expanded.handoffMessage = data.handoffMessage || '';
                    expanded.botDisableTime = data.botDisableTime || null;
                    expanded.disableBot = data.disableBot !== undefined ? data.disableBot : true;
                    break;
                case 'apiTrigger':
                    // Ensure all API trigger properties are properly loaded
                    expanded.apiUrl = data.apiUrl || '';
                    expanded.httpMethod = data.httpMethod || 'GET';
                    expanded.headers = data.headers || '';
                    expanded.textResponse = data.textResponse || '';
                    expanded.jsonValidate = data.jsonValidate || '{\n   "Name":"Value"\n}';
                    expanded.modelJsonValidator = data.modelJsonValidator || null;
                    expanded.listHeadersAPITrigger = data.listHeadersAPITrigger || [
                        {
                            key: '',
                            value: '',
                        },
                    ];
                    expanded.listValidation = data.listValidation || [
                        {
                            key: '',
                            typeValidation: '',
                            numberOfCharacter: '',
                            regExp: '',
                            promptMessage: '',
                        },
                    ];
                    break;
                case 'products':
                    // Ensure all products node properties are properly loaded
                    expanded.productGroupList = data.productGroupList || [];
                    expanded.productTopNChunks = data.productTopNChunks || 5;
                    expanded.productCosineSimilarity = data.productCosineSimilarity || 0.4;
                    break;
                case 'documents':
                    // Ensure all documents node properties are properly loaded
                    expanded.document = data.document || [];
                    expanded.textReplacement = data.textReplacement || [];
                    expanded.topNChunks = data.topNChunks || 5;
                    expanded.cosineSimilarity = data.cosineSimilarity || 0.4;
                    expanded.selectedDocumentData = data.selectedDocumentData || [];
                    expanded.enableSpecificDataSelection = data.enableSpecificDataSelection || false;
                    expanded.documentVectorName = '';
                    break;
                case 'apiknowledge':
                    // Ensure all apiknowledge node properties are properly loaded
                    expanded.knowledgeApiUrl = data.knowledgeApiUrl || '';
                    expanded.knowledgeHttpMethod = data.knowledgeHttpMethod || 'GET';
                    expanded.knowledgeRequestBody = this.methodAllowsRequestBody(expanded.knowledgeHttpMethod) ? (data.knowledgeRequestBody || '') : '';
                    expanded.knowledgeModelJsonValidator = data.knowledgeModelJsonValidator || null;
                    expanded.responseDataPath = data.responseDataPath || 'data';
                    expanded.listHeaders = data.listHeaders || [];
                    expanded.listParams = data.listParams || [];
                    expanded.description = data.description || '';
                    // Token properties
                    expanded.enableToken = data.enableToken || false;
                    expanded.tokenAuthMode = data.tokenAuthMode || 'new';
                    expanded.tokenName = data.tokenName || '';
                    expanded.useExistingTokenName = data.useExistingTokenName || '';
                    expanded.tokenUrl = data.tokenUrl || '';
                    expanded.tokenHttpMethod = data.tokenHttpMethod || 'POST';
                    expanded.tokenRequestBody = this.methodAllowsRequestBody(expanded.tokenHttpMethod) ? (data.tokenRequestBody || '') : '';
                    expanded.tokenHeaders = data.tokenHeaders || [];
                    expanded.tokenParams = data.tokenParams || [];
                    expanded.tokenResponsePath = data.tokenResponsePath || 'access_token';
                    break;
                case 'email':
                    // Ensure all email node properties are properly loaded
                    expanded.emailProvider = data.emailProvider || 'mailgun';
                    expanded.toEmail = data.toEmail || '';
                    expanded.ccEmails = data.ccEmails || [];
                    expanded.bccEmails = data.bccEmails || [];
                    expanded.subject = data.subject || '';
                    expanded.emailTemplate = data.emailTemplate || 'custom';
                    expanded.emailBody = data.emailBody || '';
                    expanded.isHtml = data.isHtml !== undefined ? data.isHtml : false;
                    expanded.useCentralConfig = data.useCentralConfig !== undefined ? data.useCentralConfig : true;
                    expanded.mailConfig = this.createEmptyMailConfig(data.mailConfig);
                    break;
                case 'sharepoint':
                    // Ensure all SharePoint node properties are properly loaded
                    expanded.sharepoint = data.sharepoint || {
                        siteUrl: '',
                        tenantId: '',
                        clientId: '',
                        clientSecret: '',
                        folderPath: '',
                        filename: ''
                    };
                    break;
                case 'knowledgeGraph':
                    // S15-P1 — normalize ให้ครบ 10 คีย์ · label เติมกลับเพราะ baseData ไม่ save label (pattern เดียวกับ agent/tool ข้างบน)
                    expanded.label = data.label || this.getNodeTypeLabel(nodeType);
                    expanded.knowledgeGraphIds = Array.isArray(data.knowledgeGraphIds) ? data.knowledgeGraphIds.filter(Boolean) : [];
                    expanded.kgRetrievalConfig = normalizeKgRetrievalConfig(data.kgRetrievalConfig);
                    break;
                case 'nexaWaterfallSearch':
                    expanded.label = data.label || this.getNodeTypeLabel(nodeType);
                    expanded.graphTiers = (Array.isArray(data.graphTiers) ? data.graphTiers : []).slice(0, WF_MAX_TIERS).map(normalizeWaterfallTier);
                    break;
            }
            return expanded;
        },
        validateFlatFlow(flowData) {
            const errors = [];

            // Check for required flow properties
            if (!flowData.name || flowData.name.trim() === '') {
                errors.push('Flow name is required');
            }

            // Check for at least one node
            if (!flowData.flowDetails || flowData.flowDetails.length === 0) {
                errors.push('Flow must contain at least one node');
            }

            // Validate nodes
            const nodeIds = new Set();
            flowData.flowDetails.forEach((node, index) => {
                if (!node.id) {
                    errors.push(`Node ${index + 1} is missing ID`);
                }

                if (nodeIds.has(node.id)) {
                    errors.push(`Duplicate node ID found: ${node.id}`);
                }
                nodeIds.add(node.id);

                if (!node.nodeType) {
                    errors.push(`Node ${node.id} is missing nodeType`);
                }

                if (!node.position) {
                    errors.push(`Node ${node.id} is missing position`);
                }

                // Validate node-specific data
                // this.validateNodeData(node, errors);
            });

            // Validate target references
            const allNodeIds = new Set(flowData.flowDetails.map(node => node.id));
            flowData.flowDetails.forEach(node => {
                if (node.targets && Array.isArray(node.targets)) {
                    node.targets.forEach((target, targetIndex) => {
                        if (!target.nodeId) {
                            errors.push(`Node ${node.id} target ${targetIndex + 1} is missing nodeId`);
                        } else if (!allNodeIds.has(target.nodeId)) {
                            errors.push(`Node ${node.id} references non-existent target node: ${target.nodeId}`);
                        }
                    });
                }
            });

            return {
                isValid: errors.length === 0,
                errors
            };
        },

        // API call to save flow
        async saveFlowToAPI(flowData, apiMethod = 'CreateFlow') {
            return new Promise((resolve, reject) => {
                const apiFlowData = {
                    ...flowData,
                    flowDetails: flowData.flowDetails.map(node => ({
                        ...node,
                        position: JSON.stringify(node.position),
                        data: JSON.stringify(node.data),
                        targets: JSON.stringify(node.targets)
                    }))
                };
                apiFlowData.flowId = this.flowId;
                this.$call[apiMethod](
                    apiFlowData,
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            resolve({
                                success: true,
                                flowId: dataResponse.data?.flowId || dataResponse.data?.id,
                                message: dataResponse.message
                            });
                        } else {
                            resolve({
                                success: false,
                                message: dataResponse.message || 'Unknown error occurred'
                            });
                        }
                    },
                    (errorResponse) => {
                        reject(new Error(errorResponse.message || 'API call failed'));
                    }
                );
            });
        },

        // Validate flow structure
        validateFlow(flowData) {
            const errors = [];

            // Check for required flow properties
            if (!flowData.name || flowData.name.trim() === '') {
                errors.push('Flow name is required');
            }

            // Check for at least one node
            if (!flowData.nodes || flowData.nodes.length === 0) {
                errors.push('Flow must contain at least one node');
            }

            // Validate nodes
            flowData.nodes.forEach((node, index) => {
                if (!node.id) {
                    errors.push(`Node ${index + 1} is missing ID`);
                }
                if (!node.data || !node.data.type) {
                    errors.push(`Node ${index + 1} is missing type`);
                }
                if (!node.position) {
                    errors.push(`Node ${index + 1} is missing position`);
                }

                // Validate specific node types
                switch (node.data.type) {
                    case 'model':
                        if (!node.data.selectedModel) {
                            errors.push(`AI Model node (${node.id}) requires a selected model`);
                        }
                        break;
                    case 'intent':
                        if (!node.data.intents || node.data.intents.length === 0) {
                            errors.push(`Intent node (${node.id}) requires at least one intent`);
                        }
                        break;
                    case 'switch':
                        if (!node.data.switchOutputs || node.data.switchOutputs.length < 2) {
                            errors.push(`Switch node (${node.id}) requires at least two output paths`);
                        }
                        break;
                    case 'jsonCondition':
                        if (!node.data.conditions || !Array.isArray(node.data.conditions) || node.data.conditions.length === 0) {
                            errors.push(`JSON Condition node (${node.id}) requires at least one rule`);
                        }
                        break;
                    case 'apiTrigger':
                        if (!node.data.apiUrl) {
                            errors.push(`API Trigger node (${node.id}) requires API URL`);
                        }
                        break;
                }
            });

            // Validate edges
            flowData.edges.forEach((edge, index) => {
                if (!edge.source || !edge.target) {
                    errors.push(`Edge ${index + 1} is missing source or target`);
                }

                // Check if source and target nodes exist
                const sourceExists = flowData.nodes.some(node => node.id === edge.source);
                const targetExists = flowData.nodes.some(node => node.id === edge.target);

                if (!sourceExists) {
                    errors.push(`Edge ${index + 1} references non-existent source node: ${edge.source}`);
                }
                if (!targetExists) {
                    errors.push(`Edge ${index + 1} references non-existent target node: ${edge.target}`);
                }
            });

            return {
                isValid: errors.length === 0,
                errors
            };
        },

        // Quick save function for auto-save
        async quickSave() {
            if (this.flowId) {
                // Only auto-save if flow already exists
                await this.SaveFlow();
            }
        },

        // Show success message
        showSuccess(message) {
            this.snackbarMessage = message;
            this.snackBarColor = 'success';
            this.showSnackbar = true;
        },

        // Show error message
        showError(message) {
            this.snackbarMessage = message;
            this.snackBarColor = 'error';
            this.showSnackbar = true;
            console.error(message);
        },

        // Show warning message
        showWarning(message) {
            this.snackbarMessage = message;
            this.snackBarColor = 'warning';
            this.showSnackbar = true;
        },

        // Show info message
        showInfo(message) {
            this.snackbarMessage = message;
            this.snackBarColor = 'info';
            this.showSnackbar = true;
        },
        // Load flow data for editing
        async loadFlowForEdit(flowId) {
            try {
                this.dialog = true;

                const response = await this.loadFlowFromAPI(flowId);

                if (response.success) {
                    const flowData = response.data;
                    // Convert flow data to editor format
                    if (flowData.flowDetails && Array.isArray(flowData.flowDetails)) {
                        const { nodes, edges } = this.convertFromFlatStructure(flowData.flowDetails);
                        this.tempNode = nodes;
                        this.tempEdge = edges;
                    } else {
                        // Fallback for old format
                        this.tempNode = flowData.nodes || [];
                        this.tempEdge = flowData.edges || [];
                    }
                    let messageInputTemp = this.tempNode.find(n => n.data && n.data.type === 'messageInput');
                    if (messageInputTemp) {
                        // Only filter models if uniqueAiModelOptions is available (to prevent losing models on reload)
                        if (this.uniqueAiModelOptions && this.uniqueAiModelOptions.length > 0) {
                            this.unableToAccessModel = messageInputTemp.data.selectedModel;
                            let tempUniqueModels = this.uniqueAiModelOptions.map(option => option.value);
                            this.unableToAccessModel = this.unableToAccessModel.filter(model => !tempUniqueModels.includes(model));
                            this.tempNode = this.tempNode.map(node => {
                                if (node.data && node.data.type === 'messageInput') {
                                    let filteredModels = node.data.selectedModel.filter(model => !this.unableToAccessModel.includes(model));
                                    return {
                                        ...node,
                                        data: {
                                            ...node.data,
                                            selectedModel: filteredModels
                                        }
                                    };
                                }
                                return node;
                            });
                        } else {
                            // If uniqueAiModelOptions is not loaded yet, keep original selectedModel values
                            // They will be filtered later when models are loaded
                            console.warn('AI Model options not loaded yet, keeping original selectedModel values');
                        }
                    }
                    // Update flow metadata
                    this.flowId = flowData.id;
                    this.flowName = flowData.name;
                    this.flowCreatedAt = flowData.createDate;

                    this.showSuccess(this.$t('_flowLoadedNamed', { name: flowData.name }));

                    // Fit view after loading nodes
                    this.$nextTick(() => {
                        // Check validation for all nodes after loading
                        this.checkAllNodesValidation();

                        setTimeout(() => {
                            // Fit view to show all nodes if in edit or view mode
                            if ((this.isEditMode || this.isViewMode) && this.$refs.vueFlow && this.tempNode.length > 0) {
                                try {
                                    this.$refs.vueFlow.fitView({
                                        padding: 0.2,
                                        minZoom: 0.5,
                                        maxZoom: 1.5,
                                        duration: 800,
                                        includeHiddenNodes: false
                                    });
                                } catch (error) {
                                    console.warn('Failed to fit view:', error);
                                }
                            }
                        }, 200);
                    });

                } else {
                    throw new Error(response.message || 'Failed to load flow');
                }

            } catch (error) {
                console.error('Error loading flow for edit:', error);
                this.showError(this.$t('_failedLoadFlow', { msg: error.message }));

                // Redirect back to flow list on error
                this.$router.push('/FlowList');
            } finally {
                this.dialog = false;
                this.$nextTick(() => {
                    setTimeout(() => {
                        // Allow time for UI to stabilize
                    }, 300);
                });
            }
        },
        async handleImportedData(importDataString) {
            try {
                const flowData = JSON.parse(importDataString);

                // Validate imported data
                if (!flowData.name || !flowData.flowDetails) {
                    throw new Error('Invalid flow structure');
                }

                // Convert imported data to editor format
                if (flowData.flowDetails && Array.isArray(flowData.flowDetails)) {
                    const { nodes, edges } = this.convertFromFlatStructure(flowData.flowDetails);
                    this.tempNode = nodes;
                    this.tempEdge = edges;
                } else {
                    this.tempNode = flowData.nodes || [];
                    this.tempEdge = flowData.edges || [];
                }

                // Set flow metadata
                this.flowId = null; // New flow from import
                this.flowName = `${flowData.name} (Imported)`;
                this.isEditMode = true; // Allow editing of imported flow

                this.showSuccess(this.$t('_flowImportedNamed', { name: flowData.name }));

                // Clear the import data from URL
                this.$router.replace({ path: '/FlowCreator' });

                this.$nextTick(() => {
                    setTimeout(() => {
                        // Allow time for UI to stabilize
                    }, 300);
                });
                this.$nextTick(() => {
                    setTimeout(() => {
                        // Fit view after importing nodes
                        if (this.$refs.vueFlow && this.tempNode.length > 0) {
                            try {
                                this.$refs.vueFlow.fitView({
                                    padding: 0.1,
                                    minZoom: 0.5,
                                    maxZoom: 1.5,
                                    duration: 800,
                                    includeHiddenNodes: false
                                });
                            } catch (error) {
                                console.warn('Failed to fit view after import:', error);
                            }
                        }
                    }, 200);
                });

            } catch (error) {
                console.error('Error handling imported data:', error);
                this.showError(this.$t('_failedImportFlowData'));
                this.$router.push('/FlowList');
            }
        },
        // Set view-only mode
        setViewOnlyMode() {
            this.$nextTick(() => {
                if (this.$refs.vueFlow) {
                    const flow = this.$refs.vueFlow;
                    // Lock drag and connect interactions, but allow selection to view node details
                    flow.nodesDraggable = false;
                    flow.nodesConnectable = false;
                    flow.elementsSelectable = true; // Allow selection to view node details in inspector
                    this.interactiveControls = false;
                }
            });
        },

        // Load flow with nested structure support
        async LoadFlow(flowId) {
            try {
                this.dialog = true;

                const response = await this.loadFlowFromAPI(flowId);

                if (response.success) {
                    const flowData = response.data;

                    // Check if it's flat structure (version 2.0+) or old nested structure
                    if (flowData.metadata?.version && parseFloat(flowData.metadata.version) >= 2.0) {
                        // Check if it uses targets or source format
                        const firstNode = flowData.nodes[0];
                        if (firstNode && firstNode.targets !== undefined) {
                            // New targets format
                            const { nodes, edges } = this.convertFromFlatStructure(flowData.nodes);
                            this.tempNode = nodes;
                            this.tempEdge = edges;
                        } else if (firstNode && firstNode.source !== undefined) {
                            // Old source format - convert to new format
                            const { nodes, edges } = this.convertFromSourceFormat(flowData.nodes);
                            this.tempNode = nodes;
                            this.tempEdge = edges;
                        } else {
                            throw new Error('Invalid flow data format');
                        }
                    } else {
                        // Load old nested structure directly (backward compatibility)
                        this.tempNode = flowData.nodes.map(node => ({
                            ...node,
                            selected: false
                        }));
                        this.tempEdge = flowData.edges || [];
                    }

                    // Update flow metadata
                    this.flowId = flowData.id;
                    this.flowName = flowData.name;
                    this.flowCreatedAt = flowData.metadata?.createdAt;

                    // Restore viewport if available
                    if (flowData.metadata?.viewport && this.$refs.vueFlow) {
                        setTimeout(() => {
                            this.$refs.vueFlow.setViewport(flowData.metadata.viewport);
                        }, 300);
                    }

                    // Check validation for all nodes after loading
                    this.$nextTick(() => {
                        this.checkAllNodesValidation();
                    });

                    this.showSuccess(this.$t('_flowLoaded'));
                } else {
                    throw new Error(response.message || 'Failed to load flow');
                }

            } catch (error) {
                console.error('Error loading flow:', error);
                this.showError(this.$t('_failedLoadFlow', { msg: error.message }));
            } finally {
                this.dialog = false;
            }
        },
        async loadFlowFromAPI(flowId) {
            return new Promise((resolve, reject) => {
                this.$call.GetFlowById(
                    flowId,
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            resolve({
                                success: true,
                                data: dataResponse.data
                            });
                        } else {
                            resolve({
                                success: false,
                                message: dataResponse.message || 'Failed to load flow'
                            });
                        }
                    },
                    (errorResponse) => {
                        reject(new Error(errorResponse.message || 'API call failed'));
                    }
                );
            });
        },
        // Helper method to convert old source format to new targets format
        convertFromSourceFormat(sourceNodes) {
            const flowNodes = [];
            const flowEdges = [];

            sourceNodes.forEach(sourceNode => {
                // Create flow node
                const flowNode = {
                    id: sourceNode.id,
                    type: 'flow-node',
                    position: sourceNode.position,
                    data: {
                        type: sourceNode.nodeType,
                        label: sourceNode.data.label,
                        ...this.expandNodeData(sourceNode.nodeType, sourceNode.data)
                    },
                    selected: false
                };

                flowNodes.push(flowNode);

                // Create edge if source exists
                if (sourceNode.source) {
                    const edge = {
                        id: `e${sourceNode.source}-${sourceNode.id}-${Date.now()}`,
                        source: sourceNode.source,
                        target: sourceNode.id,
                        sourceHandle: sourceNode.sourceHandle || null,
                        targetHandle: 'input',
                        type: 'flow-edge',
                        markerEnd: 'arrowclosed',
                        animated: false,
                        showButton: false
                    };

                    flowEdges.push(edge);
                }
            });

            return { nodes: flowNodes, edges: flowEdges };
        },


        clearFlow() {
            this.tempNode = [];
            this.tempEdge = [];
            
            // Save state to history
            this.saveStateToHistory('Clear Flow');
            
            this.showSuccess(this.$t('_flowCleared'));
        },
        // Methods for managing intent group, intents, and their actions
        addInspectorIntent() {
            if (!this.nodeInspector.intents) {
                this.nodeInspector.intents = [];
            }
            const defaultBasePrompt = `Find the Intent from the message of users.
#Condition for Response
1. If the user asks a question related to or similar in meaning to the #Group Intent below. You must response 1. Otherwise, 0`;

            const newIntent = {
                id: `intent-${this.generateGuid()}`, // Use GUID
                name: `Intent Name ${this.nodeInspector.intents.length + 1}`,
                priority: 0, // Default priority
                prompt: '',
                basePrompt: defaultBasePrompt, // Use default base prompt
                enableBasePrompt: false, // Disable base prompt by default
                messages: [
                    { id: `msg-${this.generateGuid()}`, text: '' } // Use GUID
                ],
                mainAction: {
                    id: `main-${this.generateGuid()}`, // Use GUID
                    label: 'Main Action'
                },
                parallelActions: []
            };
            this.nodeInspector.intents.push(newIntent);
            this.updateIntentData();
        },
        removeInspectorIntent(index) {
            if (this.nodeInspector.intents && this.nodeInspector.intents.length > 1) {
                const intentToRemove = this.nodeInspector.intents[index];

                // Collect all source handles related to this intent (main action + parallel actions)
                const sourceHandlesToRemove = [];

                // Add main action source handle
                sourceHandlesToRemove.push(`${intentToRemove.id}-main`);

                // Add parallel actions source handles
                if (intentToRemove.parallelActions) {
                    intentToRemove.parallelActions.forEach(parallel => {
                        sourceHandlesToRemove.push(`${intentToRemove.id}-${parallel.id}`);
                    });
                }

                // Remove all edges connected to this intent's actions
                this.tempEdge = this.tempEdge.filter(edge =>
                    !(edge.source === this.selectedNode.id && sourceHandlesToRemove.includes(edge.sourceHandle))
                );

                // Remove the intent
                this.nodeInspector.intents.splice(index, 1);
                this.updateIntentData();
            }
        },
        addInspectorParallelAction(intentIndex) {
            const intent = this.nodeInspector.intents[intentIndex];
            if (!intent.parallelActions) {
                intent.parallelActions = [];
            }
            const newParallel = {
                id: `parallel-${this.generateGuid()}`,
                label: `Parallel Action ${intent.parallelActions.length + 1}`
            };
            intent.parallelActions.push(newParallel);
            this.updateIntentData();
        },
        removeInspectorParallelAction(intentIndex, parallelIndex) {
            const intent = this.nodeInspector.intents[intentIndex];
            if (intent.parallelActions && intent.parallelActions.length > parallelIndex) {
                const parallelToRemove = intent.parallelActions[parallelIndex];
                const sourceHandleToRemove = `${intent.id}-${parallelToRemove.id}`;

                // Remove edges connected to this parallel action
                this.tempEdge = this.tempEdge.filter(edge =>
                    !(edge.source === this.selectedNode.id && edge.sourceHandle === sourceHandleToRemove)
                );

                // Remove the parallel action
                intent.parallelActions.splice(parallelIndex, 1);
                this.updateIntentData();
            }
        },
        addInspectorIntentMessage(intentIndex) {
            const intent = this.nodeInspector.intents[intentIndex];
            if (!intent.messages) {
                intent.messages = [];
            }
            const newMessage = {
                id: `msg-${this.generateGuid()}`,
                text: ''
            };
            intent.messages.push(newMessage);
            this.updateIntentData();
        },
        removeInspectorIntentMessage(intentIndex, messageIndex) {
            const intent = this.nodeInspector.intents[intentIndex];
            if (intent.messages && intent.messages.length > 1) {
                intent.messages.splice(messageIndex, 1);
                this.updateIntentData();
            }
        },
        convertIntentsToActions(intents) {
            const actions = [];
            intents.forEach((intent, intentIndex) => {
                // Add main action for each intent
                actions.push({
                    id: `${intent.id}-main`,
                    label: `${intent.mainAction.label}`,
                    type: 'main',
                    intentId: intent.id,
                });

                // Add parallel actions for each intent
                intent.parallelActions.forEach((parallel, parallelIndex) => {
                    actions.push({
                        id: `${intent.id}-${parallel.id}`,
                        label: `${parallel.label}`,
                        type: 'parallel',
                        intentId: intent.id,
                    });
                });
            });
            return actions;
        },
        convertIntentsToOutputs(intents) {
            const outputs = [];
            intents.forEach((intent, intentIndex) => {
                // Add main action output for each intent
                outputs.push({
                    id: `${intent.id}-main`,
                    label: `${intent.mainAction.label}`,
                    type: 'main',
                    intentId: intent.id,
                });

                // Add parallel outputs for each intent
                intent.parallelActions.forEach((parallel, parallelIndex) => {
                    outputs.push({
                        id: `${intent.id}-${parallel.id}`,
                        label: `${parallel.label}`,
                        type: 'parallel',
                        intentId: intent.id,
                    });
                });
            });
            return outputs;
        },
        // Methods for managing intent actions and parallels (Legacy - keep for compatibility)
        addInspectorAction() {
            if (!this.nodeInspector.actions) {
                this.nodeInspector.actions = [];
            }
            const newAction = {
                id: `action-${this.generateGuid()}`,
                label: `Action ${this.nodeInspector.actions.length + 1}`,
                parallels: [
                    { id: `parallel-${this.generateGuid()}`, label: 'Parallel Action 1' }
                ]
            };
            this.nodeInspector.actions.push(newAction);
        },
        removeInspectorAction(index) {
            if (this.nodeInspector.actions && this.nodeInspector.actions.length > 1) {
                this.nodeInspector.actions.splice(index, 1);
            }
        },
        addInspectorParallel(actionIndex) {
            if (!this.nodeInspector.actions[actionIndex].parallels) {
                this.nodeInspector.actions[actionIndex].parallels = [];
            }
            const action = this.nodeInspector.actions[actionIndex];
            const newParallel = {
                id: `parallel-${this.generateGuid()}`,
                label: `Parallel Action ${action.parallels.length + 1}`
            };
            action.parallels.push(newParallel);
        },
        removeInspectorParallel(actionIndex, parallelIndex) {
            const action = this.nodeInspector.actions[actionIndex];
            if (action.parallels && action.parallels.length > 1) {
                action.parallels.splice(parallelIndex, 1);
            }
        },

        // Switch methods
        addInspectorSwitchOutput() {
            if (!this.nodeInspector.switchOutputs) {
                this.nodeInspector.switchOutputs = [];
            }
            const newOutput = {
                id: `switch-output-${this.generateGuid()}`,
                name: `Output ${this.nodeInspector.switchOutputs.length + 1}`,
                condition: '',
                description: '',
                // Simple condition builder properties
                conditionMode: 'simple',
                conditionType: 'text_contains',
                sourceField: 'input.text',
                searchValue: '',
                compareValue: '',
                caseSensitive: false,
                operator: 'gt',
                expectedValue: true,
                regexPattern: '',
                regexFlags: '',
                lengthOperator: 'gt',
                lengthValue: 0,
                lengthValueMax: 100
            };
            this.nodeInspector.switchOutputs.push(newOutput);
            this.updateSwitchData();
        },
        removeInspectorSwitchOutput(index) {
            if (this.nodeInspector.switchOutputs && this.nodeInspector.switchOutputs.length > 2) {
                this.nodeInspector.switchOutputs.splice(index, 1);
                this.updateSwitchData();
            }
        },
        updateSwitchData() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'switch') return;

            this.updateNodeProperty('switchOutputs', this.nodeInspector.switchOutputs);
            this.updateNodeProperty('switchName', this.nodeInspector.switchName);
        },

        updateJsonConditionData() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'jsonCondition') return;

            const conditions = JSON.parse(JSON.stringify(this.nodeInspector.jsonConditions || []));
            this.updateNodeProperty('jsonConditionName', this.nodeInspector.jsonConditionName);
            this.updateNodeProperty('matchMode', this.nodeInspector.matchMode || 'all');
            this.updateNodeProperty('conditions', conditions);
            this.updateNodeProperty('matchLabel', this.nodeInspector.matchLabel || 'Match');
            this.updateNodeProperty('fallbackLabel', this.nodeInspector.fallbackLabel || 'Fallback');
            this.checkNodeValidation(this.selectedNode);
        },
        addJsonConditionRule() {
            if (!Array.isArray(this.nodeInspector.jsonConditions)) {
                this.nodeInspector.jsonConditions = [];
            }

            this.nodeInspector.jsonConditions.push({
                id: `jc-${this.generateGuid()}`,
                jsonPath: '',
                operator: '',
                compareValue: ''
            });
            this.jsonConditionExpandedPanel = this.nodeInspector.jsonConditions.length - 1;
            this.updateJsonConditionData();
        },
        removeJsonConditionRule(index) {
            if (Array.isArray(this.nodeInspector.jsonConditions) && this.nodeInspector.jsonConditions.length > 1) {
                this.nodeInspector.jsonConditions.splice(index, 1);
                this.jsonConditionExpandedPanel = Math.max(0, Math.min(index, this.nodeInspector.jsonConditions.length - 1));
                this.updateJsonConditionData();
            }
        },
        getJsonConditionOperatorProps(item) {
            const rawItem = item && item.raw ? item.raw : item;
            return rawItem && rawItem.header ? { disabled: true } : {};
        },
        jsonConditionNeedsCompareValue(operatorType) {
            return ![
                'is_empty',
                'is_not_empty',
                'array_empty',
                'array_not_empty',
                'exists',
                'not_exists',
                'is_true',
                'is_false',
                'array_all_unique'
            ].includes(operatorType);
        },
        getJsonConditionOperatorLabel(operatorType) {
            return getJsonConditionOperatorToken(operatorType);
        },
        getJsonConditionRuleSummary(condition) {
            const summaryParts = [];
            const jsonPath = typeof condition?.jsonPath === 'string' ? condition.jsonPath.trim() : '';
            const operatorLabel = this.getJsonConditionOperatorLabel(condition?.operator);
            const compareValue = typeof condition?.compareValue === 'string' ? condition.compareValue.trim() : '';

            if (jsonPath) {
                summaryParts.push(jsonPath);
            }

            if (operatorLabel) {
                summaryParts.push(operatorLabel);
            }

            if (this.jsonConditionNeedsCompareValue(condition?.operator) && compareValue) {
                summaryParts.push(compareValue);
            }

            return summaryParts.length ? summaryParts.join(' ') : '(empty)';
        },

        // Smart Intent Methods
        addInspectorSmartIntent() {
            if (!this.nodeInspector.smartIntents) {
                this.nodeInspector.smartIntents = [];
            }
            const defaultBasePrompt = `Find the Intent from the message of users.
#Condition for Response
1. If the user asks a question related to or similar in meaning to the #Group Intent below. You must response 1. Otherwise, 0`;

            const newSmartIntent = {
                id: `smart-intent-${this.generateGuid()}`,
                name: `Smart Intent ${this.nodeInspector.smartIntents.length + 1}`,
                priority: 0,
                prompt: '',
                basePrompt: defaultBasePrompt,
                enableBasePrompt: false,
                useMemory: false,
                messages: [
                    { id: `msg-${this.generateGuid()}`, text: '' }
                ],
                mainAction: {
                    id: `main-${this.generateGuid()}`,
                    label: 'Action',
                    useAI: false,
                    // Basic Information - match AI Model defaults
                    provider: null,
                    model: null,
                    gender: 'male',
                    typingSpeed: null,
                    maxInputLength: 200000,
                    // Response Options - match AI Model defaults
                    streamResponse: false,
                    jsonResponse: false,
                    enableTime: false,
                    timeZone: null,
                    // AI Parameters - match AI Model defaults
                    temperature: null,
                    topP: null,
                    frequencyPenalty: null,
                    presencePenalty: null,
                    maxTokens: null,
                    stopSequence: null,
                    // Output Control - match AI Model defaults
                    streamingEnabled: true,
                    // Line Configuration - match AI Model defaults
                    lineFlexMessage: false,
                    // System Prompt - match AI Model defaults
                    systemPrompt: '',
                    // Forbidden Words - match AI Model defaults
                    forbiddenWords: [''],
                    // Memory Settings - match AI Model defaults
                    memoryTurn: null,
                    memoryDuration: null,
                    shortTermMemory: null,
                    longTermMemory: false,
                    // Multi-turn Input Configuration - match AI Model defaults
                    isChainMassage: false,
                    lenChainMassage: null,
                    timeSecondChainMassage: null,
                    // Knowledge Configuration
                    knowledgeType: '',
                    // Knowledge Sources Enable Flags
                    enableDocuments: false,
                    enableDatabaseIntegration: false,
                    enableProducts: false,
                    enableApiKnowledge: false,
                    // Documents properties
                    document: [],
                    topNChunks: 5,
                    cosineSimilarity: 0.5,
                    // Database Integration properties
                    tableName: null,
                    sqlExtractionModel: null,
                    promptSqlNoData: '',
                    // Products properties
                    productGroupList: [],
                    productTopNChunks: 5,
                    productCosineSimilarity: 0.5,
                    // API Knowledge properties
                    knowledgeApiUrl: '',
                    knowledgeHttpMethod: 'GET',
                    listHeaders: [],
                    listParams: [],
                    knowledgeRequestBody: '',
                    knowledgeResponseDataPath: ''
                }
            };
            this.nodeInspector.smartIntents.push(newSmartIntent);
            this.updateSmartIntentData();
        },

        removeInspectorSmartIntent(index) {
            if (this.nodeInspector.smartIntents && this.nodeInspector.smartIntents.length > 1) {
                const smartIntentToRemove = this.nodeInspector.smartIntents[index];

                // Collect source handles related to this smart intent
                const sourcesToRemove = [];
                sourcesToRemove.push(`${smartIntentToRemove.id}-main`);

                // Remove edges connected to these source handles
                this.tempEdge = this.tempEdge.filter(edge => 
                    !sourcesToRemove.includes(edge.sourceHandle)
                );

                // Remove the smart intent
                this.nodeInspector.smartIntents.splice(index, 1);
                this.updateSmartIntentData();
            }
        },

        addInspectorSmartIntentMessage(smartIntentIndex) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            const newMessage = {
                id: `msg-${this.generateGuid()}`,
                text: ''
            };
            smartIntent.messages.push(newMessage);
            this.updateSmartIntentData();
        },

        removeInspectorSmartIntentMessage(smartIntentIndex, messageIndex) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (smartIntent.messages && smartIntent.messages.length > 1) {
                smartIntent.messages.splice(messageIndex, 1);
                this.updateSmartIntentData();
            }
        },

        updateSmartIntentData() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'smartIntent') return;

            this.updateNodeProperty('smartIntents', this.nodeInspector.smartIntents);
            this.updateNodeProperty('smartIntentGroupName', this.nodeInspector.smartIntentGroupName);

            // Trigger validation check for real-time feedback
            this.checkNodeValidation(this.selectedNode);
        },

        updateAdvancedRouterData() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'advancedRouter') return;

            this.updateNodeProperty('advancedRouterName', this.nodeInspector.advancedRouterName);
            this.updateNodeProperty('advancedRouterPrompt', this.nodeInspector.advancedRouterPrompt);
            this.updateNodeProperty('advancedRouterConfidenceThreshold', this.nodeInspector.advancedRouterConfidenceThreshold);
            this.updateNodeProperty('advancedRouterMaxDepth', this.nodeInspector.advancedRouterMaxDepth);
            this.updateNodeProperty('advancedRouterOutputs', JSON.parse(JSON.stringify(this.nodeInspector.advancedRouterOutputs || [])));

            this.updateNodeProperty('customSchemaEnabled', !!this.nodeInspector.advancedRouterCustomSchemaEnabled);
            this.updateNodeProperty('schemaFieldMapping', {
                intentField: this.nodeInspector.advancedRouterIntentField || 'capability_intent',
                confidenceField: this.nodeInspector.advancedRouterConfidenceField || 'confidence',
                subIntentsField: this.nodeInspector.advancedRouterSubIntentsField || 'intents'
            });

            this.checkNodeValidation(this.selectedNode);
        },
        buildAdvancedRouterIntent(name = '') {
            return {
                id: `advrouter-output-${this.generateGuid()}`,
                intentName: name,
                label: name,
                description: '',
                subIntents: []
            };
        },
        addAdvancedRouterOutput() {
            if (!Array.isArray(this.nodeInspector.advancedRouterOutputs)) {
                this.nodeInspector.advancedRouterOutputs = [];
            }
            this.nodeInspector.advancedRouterOutputs.push(this.buildAdvancedRouterIntent(`Intent ${this.nodeInspector.advancedRouterOutputs.length + 1}`));
            this.updateAdvancedRouterData();
        },
        removeAdvancedRouterOutput(outputIndex) {
            if (!Array.isArray(this.nodeInspector.advancedRouterOutputs)) return;
            this.nodeInspector.advancedRouterOutputs.splice(outputIndex, 1);
            this.updateAdvancedRouterData();
        },
        addAdvancedRouterSubIntent(outputIndex, subIndex, sub2Index) {
            const outputs = this.nodeInspector.advancedRouterOutputs || [];
            const parent = outputs[outputIndex];
            if (!parent) return;
            if (!Array.isArray(parent.subIntents)) parent.subIntents = [];

            if (subIndex == null) {
                parent.subIntents.push(this.buildAdvancedRouterIntent());
            } else {
                const sub = parent.subIntents[subIndex];
                if (!sub) return;
                if (!Array.isArray(sub.subIntents)) sub.subIntents = [];
                if (sub2Index == null) {
                    sub.subIntents.push(this.buildAdvancedRouterIntent());
                } else {
                    const sub2 = sub.subIntents[sub2Index];
                    if (!sub2) return;
                    if (!Array.isArray(sub2.subIntents)) sub2.subIntents = [];
                    sub2.subIntents.push(this.buildAdvancedRouterIntent());
                }
            }
            this.updateAdvancedRouterData();
        },
        removeAdvancedRouterSubIntent(outputIndex, subIndex, sub2Index, sub3Index) {
            const outputs = this.nodeInspector.advancedRouterOutputs || [];
            const parent = outputs[outputIndex];
            if (!parent || !Array.isArray(parent.subIntents)) return;

            if (sub2Index == null) {
                parent.subIntents.splice(subIndex, 1);
            } else {
                const sub = parent.subIntents[subIndex];
                if (!sub || !Array.isArray(sub.subIntents)) return;
                if (sub3Index == null) {
                    sub.subIntents.splice(sub2Index, 1);
                } else {
                    const sub2 = sub.subIntents[sub2Index];
                    if (!sub2 || !Array.isArray(sub2.subIntents)) return;
                    sub2.subIntents.splice(sub3Index, 1);
                }
            }
            this.updateAdvancedRouterData();
        },

        generateAdvancedRouterPrompt() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'advancedRouter') return;

            const outputs = this.nodeInspector.advancedRouterOutputs || [];
            if (outputs.length === 0) {
                this.showWarning(this.$t('_addIntentBeforeGenerate'));
                return;
            }

            const maxDepth = this.nodeInspector.advancedRouterMaxDepth || 2;
            this.advRouterGenerateIntents = this._flattenAdvancedRouterOutputs(outputs, 1, '', maxDepth);

            const descriptions = {};
            this.advRouterGenerateIntents.forEach(row => {
                descriptions[row.path] = row.existingDescription || '';
            });

            const intentaiNode = this._findConnectedIntentAINode(this.selectedNode);
            const intentaiData = intentaiNode?.data || {};
            const platform = intentaiData.intentaiPlatform || null;
            const provider = intentaiData.intentaiProvider || null;
            const model = intentaiData.intentaiModel || null;

            this.advRouterGenerateForm = {
                platform,
                provider,
                model,
                descriptions,
                additionalInstructions: '',
            };
            this.advRouterGenerateError = '';
            this.advRouterGenerateLoading = false;
            this.advRouterGenerateDialog = true;
        },

        _flattenAdvancedRouterOutputs(outputs, level, parentPath, maxDepth) {
            const rows = [];
            if (!Array.isArray(outputs)) return rows;
            outputs.forEach((intent, idx) => {
                if (!intent) return;
                const path = parentPath ? `${parentPath}.${idx}` : String(idx);
                rows.push({
                    path,
                    level,
                    name: (intent.intentName || '').trim(),
                    existingDescription: (intent.description || '').trim(),
                });
                if (level < maxDepth && Array.isArray(intent.subIntents) && intent.subIntents.length > 0) {
                    rows.push(...this._flattenAdvancedRouterOutputs(intent.subIntents, level + 1, path, maxDepth));
                }
            });
            return rows;
        },

        _findConnectedIntentAINode(advancedRouterNode) {
            if (!advancedRouterNode || !Array.isArray(this.tempEdge) || !Array.isArray(this.tempNode)) return null;
            const edge = this.tempEdge.find(e =>
                e.source === advancedRouterNode.id && e.sourceHandle === 'output-bottom');
            if (!edge) return null;
            return this.tempNode.find(n => n.id === edge.target && n.data?.type === 'intentAI') || null;
        },

        advRouterLevelColor(level) {
            const palette = { 1: 'purple', 2: 'indigo', 3: 'deep-purple', 4: 'pink' };
            return palette[level] || 'grey';
        },

        onAdvRouterGenerateModelChange(value) {
            this.advRouterGenerateForm.model = value;
            const selectedModel = this.findModelOption(value, this.advRouterGenerateForm.platform, this.advRouterGenerateForm.provider);
            this.advRouterGenerateForm.platform = selectedModel?.platform || null;
            this.advRouterGenerateForm.provider = selectedModel?.provider || null;
        },

        _buildAdvancedRouterGeneratePayload() {
            const maxDepth = this.nodeInspector.advancedRouterMaxDepth || 2;
            const descriptions = this.advRouterGenerateForm.descriptions || {};

            const buildIntentTree = (outputs, level, parentPath) => {
                if (!Array.isArray(outputs)) return [];
                return outputs
                    .map((intent, idx) => {
                        if (!intent || !(intent.intentName || '').trim()) return null;
                        const path = parentPath ? `${parentPath}.${idx}` : String(idx);
                        const description = (descriptions[path] || '').trim();
                        const children = level < maxDepth && Array.isArray(intent.subIntents) && intent.subIntents.length > 0
                            ? buildIntentTree(intent.subIntents, level + 1, path)
                            : [];
                        return {
                            name: intent.intentName.trim(),
                            description,
                            subIntents: children,
                        };
                    })
                    .filter(Boolean);
            };

            return {
                intentTree: buildIntentTree(this.nodeInspector.advancedRouterOutputs || [], 1, ''),
                modelConfig: {
                    platform: this.advRouterGenerateForm.platform,
                    provider: this.advRouterGenerateForm.provider,
                    model: this.advRouterGenerateForm.model,
                    temperature: 0.2,
                    maxTokens: null,
                },
                intentField: (this.nodeInspector.advancedRouterIntentField || '').trim(),
                confidenceField: (this.nodeInspector.advancedRouterConfidenceField || '').trim(),
                subIntentsField: (this.nodeInspector.advancedRouterSubIntentsField || '').trim(),
                confidenceThreshold: this.nodeInspector.advancedRouterConfidenceThreshold ?? 0.5,
                maxDepth,
                additionalInstructions: (this.advRouterGenerateForm.additionalInstructions || '').trim(),
            };
        },

        confirmAdvancedRouterGenerate() {
            if (!this.advRouterGenerateForm.model) {
                this.advRouterGenerateError = 'Please select a model before generating.';
                return;
            }
            const payload = this._buildAdvancedRouterGeneratePayload();
            if (!payload.intentTree.length) {
                this.advRouterGenerateError = 'Add at least one intent with a name before generating.';
                return;
            }

            this.advRouterGenerateError = '';
            this.advRouterGenerateLoading = true;
            this.$call.GenerateAdvancedRouterPrompt(
                payload,
                (response) => {
                    this.advRouterGenerateLoading = false;
                    if (response.status === 0 && response.data?.prompt) {
                        this.nodeInspector.advancedRouterPrompt = response.data.prompt;
                        this.updateAdvancedRouterData();
                        this.showSuccess(this.$t('_classifierGenerated'));
                        this.advRouterGenerateDialog = false;
                    } else {
                        this.advRouterGenerateError = response.message || 'Failed to generate prompt.';
                    }
                },
                (err) => {
                    this.advRouterGenerateLoading = false;
                    this.advRouterGenerateError = err?.response?.data?.message || err?.message || 'Failed to generate prompt.';
                },
            );
        },

        cancelAdvancedRouterGenerate() {
            if (this.advRouterGenerateLoading) return;
            this.advRouterGenerateDialog = false;
            this.advRouterGenerateError = '';
        },

        openAdvancedRouterTest() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'advancedRouter') return;

            const prompt = (this.nodeInspector.advancedRouterPrompt || '').trim();
            if (!prompt) {
                this.showWarning(this.$t('_writePromptBeforeTest'));
                return;
            }
            const outputs = this.nodeInspector.advancedRouterOutputs || [];
            if (outputs.length === 0) {
                this.showWarning(this.$t('_addIntentBeforeTest'));
                return;
            }

            const intentaiNode = this._findConnectedIntentAINode(this.selectedNode);
            const data = intentaiNode?.data;
            if (data && data.intentaiModel) {
                const ddm = Array.isArray(this.listDDModel)
                    ? this.listDDModel.find(e => e.modelName === data.intentaiModel)
                    : null;
                // intentaiModel stores the registry modelName (e.g. "Azure|gpt-5.4" or "gpt-5.4|gpt-5.4").
                // Display only the trailing model segment so the chip stays readable.
                const raw = data.intentaiModel || '';
                const modelDisplay = ddm?.modelDisplay
                    || (raw.includes('|') ? raw.split('|').slice(-1)[0] : raw);
                this.advRouterTestModelInfo = {
                    model: data.intentaiModel,
                    modelDisplay,
                    provider: data.intentaiProvider || ddm?.provider || null,
                    platform: data.intentaiPlatform || ddm?.platform || null,
                    apiVersion: data.intentaiApiVersion || ddm?.apiVersion || null,
                    temperature: data.intentaiTemperature ?? 0,
                };
            } else {
                this.advRouterTestModelInfo = null;
            }

            this.advRouterTestForm = { message: '' };
            this.advRouterTestResult = null;
            this.advRouterTestError = '';
            this.advRouterTestRawCollapsed = false;
            this.advRouterTestLoading = false;
            this.advRouterTestDialog = true;
        },

        runAdvancedRouterTest() {
            if (!this.advRouterTestModelInfo) {
                this.advRouterTestError = 'Connect an Intent AI node before testing.';
                return;
            }
            const message = (this.advRouterTestForm.message || '').trim();
            if (!message) {
                this.advRouterTestError = 'Enter a user message to test.';
                return;
            }

            const maxDepth = this.nodeInspector.advancedRouterMaxDepth || 2;
            const buildIntentTree = (outputs, level, parentPath) => {
                if (!Array.isArray(outputs)) return [];
                return outputs
                    .map((intent, idx) => {
                        if (!intent || !(intent.intentName || '').trim()) return null;
                        const path = parentPath ? `${parentPath}.${idx}` : String(idx);
                        const children = level < maxDepth && Array.isArray(intent.subIntents) && intent.subIntents.length > 0
                            ? buildIntentTree(intent.subIntents, level + 1, path)
                            : [];
                        return {
                            name: intent.intentName.trim(),
                            description: (intent.description || '').trim(),
                            subIntents: children,
                        };
                    })
                    .filter(Boolean);
            };

            const payload = {
                testMessage: message,
                prompt: (this.nodeInspector.advancedRouterPrompt || '').trim(),
                intentTree: buildIntentTree(this.nodeInspector.advancedRouterOutputs || [], 1, ''),
                modelConfig: {
                    platform: this.advRouterTestModelInfo.platform,
                    provider: this.advRouterTestModelInfo.provider,
                    model: this.advRouterTestModelInfo.model,
                    apiVersion: this.advRouterTestModelInfo.apiVersion,
                    temperature: this.advRouterTestModelInfo.temperature ?? 0,
                    maxTokens: null,
                },
                intentField: (this.nodeInspector.advancedRouterIntentField || '').trim(),
                confidenceField: (this.nodeInspector.advancedRouterConfidenceField || '').trim(),
                subIntentsField: (this.nodeInspector.advancedRouterSubIntentsField || '').trim(),
                customSchemaEnabled: !!this.nodeInspector.advancedRouterCustomSchemaEnabled,
                confidenceThreshold: this.nodeInspector.advancedRouterConfidenceThreshold ?? 0.5,
                maxDepth,
            };

            if (!payload.intentTree.length) {
                this.advRouterTestError = 'Add at least one intent with a name before testing.';
                return;
            }

            this.advRouterTestError = '';
            this.advRouterTestLoading = true;
            this.advRouterTestResult = null;
            this.$call.TestAdvancedRouterPrompt(
                payload,
                (response) => {
                    this.advRouterTestLoading = false;
                    if (response.status === 0 && response.data) {
                        this.advRouterTestResult = response.data;
                    } else {
                        this.advRouterTestError = response.message || 'Test failed.';
                    }
                },
                (err) => {
                    this.advRouterTestLoading = false;
                    this.advRouterTestError = err?.response?.data?.message || err?.message || 'Test failed.';
                },
            );
        },

        closeAdvancedRouterTest() {
            if (this.advRouterTestLoading) return;
            this.advRouterTestDialog = false;
            this.advRouterTestError = '';
        },

        onAdvRouterTestKeydown(event) {
            // Enter → run; Shift+Enter → newline.
            // Skip during IME composition (Thai/CJK input) so segment commits don't fire submit.
            if (event.key === 'Enter' && !event.shiftKey && !event.isComposing && event.keyCode !== 229) {
                event.preventDefault();
                if (this.advRouterTestModelInfo
                    && !this.advRouterTestLoading
                    && (this.advRouterTestForm.message || '').trim()) {
                    this.runAdvancedRouterTest();
                }
            }
        },

        openIntentRouterTest() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'intent') return;
            const intents = this._buildIntentRouterTestIntents(this.nodeInspector.intents || []);
            if (!intents.length) {
                this.showWarning(this.$t('_addIntentBeforeTest'));
                return;
            }

            const intentaiNode = this._findConnectedIntentAINode(this.selectedNode);
            this.intentRouterTestModelInfo = this._buildNodeTestModelInfo(intentaiNode?.data, 'intentAI');
            this.intentRouterTestForm = { message: '' };
            this.intentRouterTestResult = null;
            this.intentRouterTestError = '';
            this.intentRouterTestRawCollapsed = false;
            this.intentRouterTestLoading = false;
            this.intentRouterTestDialog = true;
        },

        runIntentRouterTest() {
            if (!this.intentRouterTestModelInfo) {
                this.intentRouterTestError = 'Connect an Intent AI node before testing.';
                return;
            }
            const message = (this.intentRouterTestForm.message || '').trim();
            if (!message) {
                this.intentRouterTestError = 'Enter a user message to test.';
                return;
            }
            const payload = {
                testMessage: message,
                intents: this._buildIntentRouterTestIntents(this.nodeInspector.intents || []),
                modelConfig: this._buildNodeTestModelConfig(this.intentRouterTestModelInfo),
            };
            if (!payload.intents.length) {
                this.intentRouterTestError = 'Add at least one intent with a name before testing.';
                return;
            }

            this.intentRouterTestError = '';
            this.intentRouterTestLoading = true;
            this.intentRouterTestResult = null;
            this.$call.TestIntentRouterPrompt(
                payload,
                (response) => {
                    this.intentRouterTestLoading = false;
                    const data = response?.data || response?.Data;
                    const status = response?.status ?? response?.Status;
                    if (status === 0 && data) {
                        this.intentRouterTestResult = data;
                    } else {
                        this.intentRouterTestError = response?.message || response?.Message || 'Test failed.';
                    }
                },
                (err) => {
                    this.intentRouterTestLoading = false;
                    this.intentRouterTestError = err?.response?.data?.message || err?.response?.data || err?.message || 'Test failed.';
                },
            );
        },

        closeIntentRouterTest() {
            if (this.intentRouterTestLoading) return;
            this.intentRouterTestDialog = false;
            this.intentRouterTestError = '';
        },

        onIntentRouterTestKeydown(event) {
            if (event.key === 'Enter' && !event.shiftKey && !event.isComposing && event.keyCode !== 229) {
                event.preventDefault();
                if (this.intentRouterTestModelInfo
                    && !this.intentRouterTestLoading
                    && (this.intentRouterTestForm.message || '').trim()) {
                    this.runIntentRouterTest();
                }
            }
        },

        openAIModelTest() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'model') return;
            this.aiModelTestModelInfo = this._buildNodeTestModelInfo(this.selectedNode.data, 'model');
            this.aiModelTestForm = {
                systemMessage: this.nodeInspector.systemPrompt || '',
                userMessage: '',
            };
            this.aiModelTestResult = null;
            this.aiModelTestError = '';
            this.aiModelTestRawCollapsed = false;
            this.aiModelTestLoading = false;
            this.aiModelTestDialog = true;
        },

        runAIModelTest() {
            if (!this.aiModelTestModelInfo) {
                this.aiModelTestError = 'Select a model before testing.';
                return;
            }
            const userMessage = (this.aiModelTestForm.userMessage || '').trim();
            if (!userMessage) {
                this.aiModelTestError = 'Enter a user message to test.';
                return;
            }
            const payload = {
                userMessage,
                systemMessage: this.aiModelTestForm.systemMessage || '',
                modelConfig: this._buildNodeTestModelConfig(this.aiModelTestModelInfo),
            };

            this.aiModelTestError = '';
            this.aiModelTestLoading = true;
            this.aiModelTestResult = null;
            this.$call.TestAIModelPrompt(
                payload,
                (response) => {
                    this.aiModelTestLoading = false;
                    const data = response?.data || response?.Data;
                    const status = response?.status ?? response?.Status;
                    if (status === 0 && data) {
                        this.aiModelTestResult = data;
                    } else {
                        this.aiModelTestError = response?.message || response?.Message || 'Test failed.';
                    }
                },
                (err) => {
                    this.aiModelTestLoading = false;
                    this.aiModelTestError = err?.response?.data?.message || err?.response?.data || err?.message || 'Test failed.';
                },
            );
        },

        closeAIModelTest() {
            if (this.aiModelTestLoading) return;
            this.aiModelTestDialog = false;
            this.aiModelTestError = '';
        },

        extractImageUrl(text) {
            if (!text) return null;
            const trimmed = text.trim();
            const mdMatch = trimmed.match(/^!\[.*?\]\((https?:\/\/.+?)\)$/i);
            if (mdMatch) return mdMatch[1];
            if (/^https?:\/\/.+\.(png|jpg|jpeg|gif|webp|svg|bmp)(\?.*)?$/i.test(trimmed)) return trimmed;
            return null;
        },

        isImageUrl(text) {
            return this.extractImageUrl(text) !== null;
        },

        onAIModelTestKeydown(event) {
            if (event.key === 'Enter' && !event.shiftKey && !event.isComposing && event.keyCode !== 229) {
                event.preventDefault();
                if (this.aiModelTestModelInfo
                    && !this.aiModelTestLoading
                    && (this.aiModelTestForm.userMessage || '').trim()) {
                    this.runAIModelTest();
                }
            }
        },

        _buildNodeTestModelInfo(data, type) {
            if (!data) return null;
            const model = type === 'intentAI' ? data.intentaiModel : data.model;
            if (!model) return null;
            const ddm = Array.isArray(this.listDDModel)
                ? this.listDDModel.find(e => e.modelName === model)
                : null;
            const raw = model || '';
            const modelDisplay = ddm?.modelDisplay || (raw.includes('|') ? raw.split('|').slice(-1)[0] : raw);
            return {
                model,
                modelDisplay,
                provider: type === 'intentAI' ? (data.intentaiProvider || ddm?.provider || null) : (data.provider || ddm?.provider || null),
                platform: type === 'intentAI' ? (data.intentaiPlatform || ddm?.platform || null) : (data.platform || ddm?.platform || null),
                apiVersion: type === 'intentAI'
                    ? (data.intentAiModelApiVersion || data.intentaiApiVersion || ddm?.apiVersion || null)
                    : (data.apiVersion || ddm?.apiVersion || null),
                temperature: type === 'intentAI' ? (data.intentaiTemperature ?? 0) : (data.temperature ?? 0),
                maxTokens: type === 'intentAI' ? (data.intentaiMaxTokens ?? null) : (data.maxTokens ?? null),
                topP: type === 'intentAI' ? (data.intentaiTopP ?? 1) : (data.topP ?? 1),
                frequencyPenalty: type === 'intentAI' ? (data.intentaiFrequencyPenalty ?? 0) : (data.frequencyPenalty ?? 0),
                presencePenalty: type === 'intentAI' ? (data.intentaiPresencePenalty ?? 0) : (data.presencePenalty ?? 0),
                stop: type === 'intentAI' ? (data.intentaiStopSequence || null) : (data.stopSequence || null),
                reasoningEffort: type === 'intentAI' ? (data.intentaiReasoningEffort || null) : (data.reasoningEffort || null),
                jsonResponse: type === 'intentAI' ? (data.intentaiJsonResponse ?? false) : (data.jsonResponse ?? false),
            };
        },

        _buildNodeTestModelConfig(modelInfo) {
            return {
                platform: modelInfo.platform,
                provider: modelInfo.provider,
                model: modelInfo.model,
                apiVersion: modelInfo.apiVersion,
                temperature: modelInfo.temperature ?? 0,
                maxTokens: modelInfo.maxTokens ?? null,
                topP: modelInfo.topP ?? 1,
                frequencyPenalty: modelInfo.frequencyPenalty ?? 0,
                presencePenalty: modelInfo.presencePenalty ?? 0,
                stop: modelInfo.stop || null,
                reasoningEffort: modelInfo.reasoningEffort || null,
                jsonResponse: !!modelInfo.jsonResponse,
            };
        },

        _buildIntentRouterTestIntents(intents) {
            if (!Array.isArray(intents)) return [];
            return intents
                .filter(intent => intent && (intent.name || '').trim())
                .map(intent => ({
                    id: intent.id,
                    name: intent.name.trim(),
                    prompt: intent.prompt || '',
                    basePrompt: intent.basePrompt || '',
                    priority: intent.priority ?? null,
                    mainActionLabel: intent.mainAction?.label || 'Main Action',
                    messages: Array.isArray(intent.messages)
                        ? intent.messages.map(message => message?.text || '').filter(text => text.trim())
                        : [],
                }));
        },

        intentRouterScoreColor(score) {
            if (score?.isFallback) return score.score === 1 ? 'warning' : 'grey';
            return score?.score === 1 ? 'success' : 'grey';
        },

        handleOpenNodeTest(nodeId) {
            const node = Array.isArray(this.tempNode)
                ? this.tempNode.find(n => n.id === nodeId)
                : null;
            if (!node) return;

            if (!this.selectedNode || this.selectedNode.id !== nodeId) {
                this.openNodeInspector(node);
            }
            this.$nextTick(() => {
                if (node.data?.type === 'advancedRouter') this.openAdvancedRouterTest();
                if (node.data?.type === 'intent') this.openIntentRouterTest();
                if (node.data?.type === 'model') this.openAIModelTest();
            });
        },

        handleOpenAdvancedRouterTest(nodeId) {
            const node = Array.isArray(this.tempNode)
                ? this.tempNode.find(n => n.id === nodeId)
                : null;
            if (!node || node.data?.type !== 'advancedRouter') return;

            // Ensure the inspector is populated for this node before opening the dialog —
            // openAdvancedRouterTest reads from nodeInspector.
            if (!this.selectedNode || this.selectedNode.id !== nodeId) {
                this.openNodeInspector(node);
            }
            this.$nextTick(() => this.openAdvancedRouterTest());
        },

        advRouterTestScoreBarClass(confidence) {
            const threshold = this.nodeInspector.advancedRouterConfidenceThreshold ?? 0.5;
            if (confidence >= threshold) return 'tp-score-bar--ok';
            if (confidence >= threshold * 0.6) return 'tp-score-bar--mid';
            return 'tp-score-bar--low';
        },

        updateSmartIntentProviderChange(smartIntentIndex, newProvider) {
            // Clear model when provider changes
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction.model = null;
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction.apiVersion = null;
            this.updateSmartIntentData();
        },

        updateSmartIntentModelChange(smartIntentIndex, newModel) {
            // Auto-assign apiVersion when model changes
            this.$nextTick(() => {
                const selectedModel = this.listDDModel.find(m => m.modelName === newModel);
                if (selectedModel) {
                    this.nodeInspector.smartIntents[smartIntentIndex].mainAction.apiVersion = selectedModel.apiVersion;
                }
                this.updateSmartIntentData();
            });
        },

        updateSmartIntentFallbackData() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'smartIntent') return;

            // Initialize fallback if it doesn't exist
            if (!this.nodeInspector.smartIntentFallback) {
                this.nodeInspector.smartIntentFallback = {
                    useAI: false,
                    provider: '',
                    model: '',
                    apiVersion: null,
                    // Basic Information
                    gender: 'male',
                    typingSpeed: null,
                    maxInputLength: 200000,
                    // Response Options
                    streamingEnabled: false,
                    enableTime: false,
                    timeZone: null,
                    // AI Parameters
                    temperature: 0.7,
                    topP: 1.0,
                    frequencyPenalty: 0.0,
                    presencePenalty: 0.0,
                    maxTokens: 150,
                    stopSequence: '',
                    // Memory Settings
                    memoryTurn: null,
                    memoryDuration: null,
                    shortTermMemory: null,
                    longTermMemory: false,
                    // Multi-turn Input Configuration
                    isChainMassage: false,
                    lenChainMassage: null,
                    timeSecondChainMassage: null,
                    // Line Configuration
                    showLineName: false,
                    loadingBar: false,
                    loadingBarDuration: 0,
                    monitorLineUserId: null,
                    monitorSocialID: null,
                    lineFlexMessage: false,
                    // System Prompt
                    systemPrompt: '',
                    // Forbidden Words
                    forbiddenWords: ['']
                };
            }
            
            this.updateNodeProperty('smartIntentFallback', this.nodeInspector.smartIntentFallback);
            
            // Trigger validation check for real-time feedback
            this.checkNodeValidation(this.selectedNode);
        },
        
        updateSmartIntentFallbackProviderChange(newProvider) {
            // Clear model when provider changes
            this.nodeInspector.smartIntentFallback.model = '';
            this.nodeInspector.smartIntentFallback.apiVersion = null;
            this.updateSmartIntentFallbackData();
        },

        updateSmartIntentFallbackModelChange(newModel) {
            // Auto-assign apiVersion when model changes
            this.$nextTick(() => {
                const selectedModel = this.listDDModel.find(m => m.modelName === newModel);
                if (selectedModel) {
                    this.nodeInspector.smartIntentFallback.apiVersion = selectedModel.apiVersion;
                }
                this.updateSmartIntentFallbackData();
            });
        },

        // Smart Intent Knowledge API Header Management Methods
        addSmartIntentKnowledgeAPIHeader(smartIntentIndex) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction.listHeaders) {
                smartIntent.mainAction.listHeaders = [];
            }
            
            const newHeader = {
                id: `header-${this.generateGuid()}`,
                key: '',
                value: ''
            };
            
            smartIntent.mainAction.listHeaders.push(newHeader);
            this.updateSmartIntentData();
        },

        removeSmartIntentKnowledgeAPIHeader(smartIntentIndex, headerIndex) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (smartIntent.mainAction.listHeaders && smartIntent.mainAction.listHeaders.length > 0) {
                smartIntent.mainAction.listHeaders.splice(headerIndex, 1);
                this.updateSmartIntentData();
            }
        },

        // Smart Intent Fallback Knowledge API Header Management Methods
        addSmartIntentFallbackKnowledgeAPIHeader() {
            if (!this.nodeInspector.smartIntentFallback.listHeaders) {
                this.nodeInspector.smartIntentFallback.listHeaders = [];
            }
            
            const newHeader = {
                id: `header-${this.generateGuid()}`,
                key: '',
                value: ''
            };
            
            this.nodeInspector.smartIntentFallback.listHeaders.push(newHeader);
            this.updateSmartIntentFallbackData();
        },

        removeSmartIntentFallbackKnowledgeAPIHeader(headerIndex) {
            if (this.nodeInspector.smartIntentFallback.listHeaders && this.nodeInspector.smartIntentFallback.listHeaders.length > 0) {
                this.nodeInspector.smartIntentFallback.listHeaders.splice(headerIndex, 1);
                this.updateSmartIntentFallbackData();
            }
        },

        getModelsByProvider(provider, platform) {
            if (!provider) return [];
            
            // Filter models by the specific provider and optionally by platform
            return this.listDDModel.filter((model) => {
                const providerMatch = model.providerName === provider || model.provider === provider;
                if (!providerMatch) return false;
                if (platform) return model.platform === platform;
                return true;
            });
        },
        // Document data methods
        getDocumentData() {
            let self = this;
            // Only fetch data if specific data selection is enabled
            if (!self.nodeInspector.enableSpecificDataSelection) {
                self.resetDocumentDataTable();
                return;
            }

            if (self.nodeInspector.documentVectorName == "" || self.nodeInspector.documentVectorName == null) {
                return;
            }

            this.loadingDocumentDataTable = true;
            self.resetDocumentDataTable();

            let temp = {
                page: 1,
                perPage: 1000,
                name: self.nodeInspector.documentVectorName,
                sortDesc: false,
                sortBy: null,
            };

            self.$call.GetTableVector(
                temp,
                (dataResponse) => {
                    if (dataResponse.status == 0) {
                        self.documentDataTable = dataResponse.data.table.data || [];
                        self.documentDataTotalTable = dataResponse.data.table.total || 0;
                        self.showDocumentDataTable = true;

                        // Initialize selection state for each item
                        self.documentDataTable.forEach((element) => {
                            element.selected = false;
                            // Check if item is already selected
                            if (self.nodeInspector.selectedDocumentData) {
                                const alreadySelected = self.nodeInspector.selectedDocumentData.find(
                                    (selected) => selected.id == element.id
                                );
                                if (alreadySelected) {
                                    element.selected = true;
                                }
                            }
                        });
                    } else {
                        self.documentDataTable = [];
                        self.documentDataTotalTable = 0;
                        self.showDocumentDataTable = false;
                    }
                    self.loadingDocumentDataTable = false;
                },
                (errorResource) => {
                    console.error('Error fetching document data:', errorResource);
                    self.documentDataTable = [];
                    self.documentDataTotalTable = 0;
                    self.showDocumentDataTable = false;
                    self.loadingDocumentDataTable = false;
                }
            );
        },
        selectDocumentData(flag, id, textORG) {
            let self = this;

            if (!self.nodeInspector.selectedDocumentData) {
                self.nodeInspector.selectedDocumentData = [];
            }

            if (flag) {
                // Add to selection
                self.nodeInspector.selectedDocumentData.push({
                    id: id,
                    textORG: textORG
                });

                // Update node property
                self.updateNodeProperty('selectedDocumentData', self.nodeInspector.selectedDocumentData);
            } else {
                // Remove from selection
                if (self.nodeInspector.selectedDocumentData.findIndex(e => e.id == id) > -1) {
                    self.nodeInspector.selectedDocumentData.splice(
                        self.nodeInspector.selectedDocumentData.findIndex(e => e.id == id),
                        1
                    );
                    self.updateNodeProperty('selectedDocumentData', self.nodeInspector.selectedDocumentData);
                }
            }

            // Update UI selection state
            self.documentDataTable.forEach((element) => {
                if (element.id == id) {
                    element.selected = flag;
                }
            });
        },
        deleteSelectedDocumentData(id) {
            let self = this;
            if (self.nodeInspector.selectedDocumentData && self.nodeInspector.selectedDocumentData.length > 0) {
                // Remove from selected data
                self.nodeInspector.selectedDocumentData = self.nodeInspector.selectedDocumentData.filter(item => item.id !== id);
                self.updateNodeProperty('selectedDocumentData', self.nodeInspector.selectedDocumentData);

                // Update table selection state
                self.documentDataTable.forEach((element) => {
                    if (element.id == id) {
                        element.selected = false;
                    }
                });
            }
        },
        onSpecificDataSelectionChange(enabled) {
            let self = this;

            // Update node property
            self.updateNodeProperty('enableSpecificDataSelection', enabled);

            if (enabled) {
                // When enabled, auto-expand and clear document vector selection
                self.documentDataExpanded = true;

                // Clear document vector selection
                self.nodeInspector.documentVectorName = '';
                self.updateNodeProperty('documentVectorName', '');

                // Reset document data table
                self.resetDocumentDataTable();
            } else {
                // When disabled, reset document data and clear selection
                self.resetDocumentDataTable();
                if (self.nodeInspector.selectedDocumentData) {
                    self.nodeInspector.selectedDocumentData = [];
                    self.updateNodeProperty('selectedDocumentData', []);
                }
                self.documentDataExpanded = false;
            }
        },
        resetDocumentDataAndSelection() {
            // Reset table data
            this.resetDocumentDataTable();

            // Reset document vector selection and clear selected data if necessary
            if (this.nodeInspector.documentVectorName) {
                this.nodeInspector.documentVectorName = '';
                this.updateNodeProperty('documentVectorName', '');
            }

            // Clear selected document data if necessary
            if (this.nodeInspector.selectedDocumentData && this.nodeInspector.selectedDocumentData.length > 0) {
                this.nodeInspector.selectedDocumentData = [];
                this.updateNodeProperty('selectedDocumentData', []);
            }
        },
        resetDocumentDataTable() {
            this.documentDataTable = [];
            this.documentDataTotalTable = 0;
            this.showDocumentDataTable = false;
        },
        formatText(text) {
            if (!text) return '';
            return text.replace(/ /g, "&nbsp;");
        },
        generateConditionPreview(output) {
            if (!output.conditionType) return '';

            const source = output.sourceField || 'input.text';

            switch (output.conditionType) {
                case 'text_contains':
                    const containsMethod = output.caseSensitive ? 'includes' : 'toLowerCase().includes';
                    const searchValue = output.caseSensitive ? `'${output.searchValue}'` : `'${(output.searchValue || '').toLowerCase()}'`;
                    return `${source}.${containsMethod}(${searchValue})`;

                case 'text_equals':
                    const equalsMethod = output.caseSensitive ? '===' : 'toLowerCase() ===';
                    const compareValue = output.caseSensitive ? `'${output.compareValue}'` : `'${(output.compareValue || '').toLowerCase()}'`;
                    return `${source}.${equalsMethod} ${compareValue}`;

                case 'number_comparison':
                    const operators = { gt: '>', lt: '<', eq: '===', gte: '>=', lte: '<=', ne: '!==' };
                    const op = operators[output.operator] || '>';
                    return `${source} ${op} ${output.compareValue || 0}`;

                case 'boolean_check':
                    return `${source} === ${output.expectedValue}`;

                case 'regex_match':
                    const flags = output.regexFlags ? `'${output.regexFlags}'` : '';
                    return `new RegExp('${output.regexPattern}'${flags ? ', ' + flags : ''}).test(${source})`;

                case 'length_check':
                    const lengthOp = { gt: '>', lt: '<', eq: '===', between: 'between' }[output.lengthOperator] || '>';
                    if (output.lengthOperator === 'between') {
                        return `${source}.length >= ${output.lengthValue || 0} && ${source}.length <= ${output.lengthValueMax || 100}`;
                    }
                    return `${source}.length ${lengthOp} ${output.lengthValue || 0}`;

                default:
                    return '';
            }
        },
        insertConditionHelper(outputIndex, helperText) {
            if (!this.nodeInspector.switchOutputs[outputIndex]) return;

            const currentCondition = this.nodeInspector.switchOutputs[outputIndex].condition || '';
            const newCondition = currentCondition + (currentCondition ? ' && ' : '') + helperText;

            this.nodeInspector.switchOutputs[outputIndex].condition = newCondition;
            this.updateSwitchData();
        },


        // Save/Load UI Methods
        openSaveDialog() {
            this.saveDialog = true;
        },

        closeSaveDialog() {
            this.saveDialog = false;
        },

        // Load from Model methods
        async openLoadFromModelDialog() {
            this.loadFromModelDialog = true;
        },

        async openSmartIntentLoadFromModelDialog(smartIntentIndex = null) {
            this.currentLoadingSmartIntentIndex = smartIntentIndex; // Store which intent/fallback is being loaded
            this.smartIntentLoadFromModelDialog = true;
        },

        closeLoadFromModelDialog() {
            this.loadFromModelDialog = false;
            this.selectedModelToLoad = null;
            this.modelConfigPreview = null;
        },

        closeSmartIntentLoadFromModelDialog() {
            this.smartIntentLoadFromModelDialog = false;
            this.selectedSmartIntentModelToLoad = null;
            this.smartIntentModelConfigPreview = null;
            this.currentLoadingSmartIntentIndex = null;
        },

        async confirmLoadFromModel() {
            if (!this.selectedModelToLoad) return;

            this.loadingModelConfig = true;
            try {
                // Call API to get model configuration
                this.$call.GetGPTModelById(
                    this.selectedModelToLoad,
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            const modelConfig = dataResponse.data;
                            // Check if this is intentAI node
                            const isIntentAI = this.selectedNode && this.selectedNode.data.type === 'intentAI';
                            
                            this.applyLoadedModelSelection(modelConfig, isIntentAI);
                            this.$nextTick(() => {
                                this.loadOtherProperties(modelConfig);
                            });
                        } else {
                            this.showError(this.$t('_failedLoadModelConfig'));
                            this.loadingModelConfig = false;
                        }
                    },
                    (error) => {
                        console.error('API Error:', error);
                        this.showError(this.$t('_errorLoadingModelConfig'));
                        this.loadingModelConfig = false;
                    }
                );
            } catch (error) {
                console.error('Exception:', error);
                this.showError(this.$t('_errorLoadingModelConfig'));
                this.loadingModelConfig = false;
            }
        },

        loadOtherProperties(modelConfig) {
            console.log('Loaded model configuration:', modelConfig);
            // Check if this is intentAI node
            const isIntentAI = this.selectedNode && this.selectedNode.data.type === 'intentAI';
            // Load configuration into nodeInspector with proper reactive updates
            if (isIntentAI) {
                this.nodeInspector.intentaiGender = (modelConfig.gender === "m" ? 'male' : 'female') || 'male';
                this.nodeInspector.intentaiTemperature = modelConfig.temperature || null;
                this.nodeInspector.intentaiMaxTokens = modelConfig.max_tokens || null;
                this.nodeInspector.intentaiTopP = modelConfig.top_p || null;
                this.nodeInspector.intentaiFrequencyPenalty = modelConfig.frequency_penalty || null;
                this.nodeInspector.intentaiPresencePenalty = modelConfig.presence_penalty || null;
                this.nodeInspector.intentaiSystemPrompt = modelConfig.promptTemplate || '';
                this.nodeInspector.intentaiTypingSpeed = modelConfig.typingSpeed || null;
                this.nodeInspector.intentaiMaxInputLength = modelConfig.maximumChatInput || 200000;
                this.nodeInspector.intentaiStreamResponse = modelConfig.isStream !== undefined ? modelConfig.isStream : false;
                this.nodeInspector.intentaiJsonResponse = modelConfig.jsonResponse !== undefined ? modelConfig.jsonResponse : false;
                this.nodeInspector.intentaiEnableTime = modelConfig.timeZone ? true : false;
                this.nodeInspector.intentaiTimeZone = modelConfig.timeZone || null;
                this.nodeInspector.intentaiMemoryTurn = modelConfig.historyCount || null;
                this.nodeInspector.intentaiMemoryDuration = modelConfig.historyMinute < 0
                    ? Math.abs(modelConfig.historyMinute)
                    : modelConfig.historyMinute;
                this.nodeInspector.intentaiShortTermMemory = modelConfig.len_ShortMemory || null;
                this.nodeInspector.intentaiLongTermMemory = modelConfig.isLongTerm_Memory !== undefined ? modelConfig.isLongTerm_Memory : false;
                this.nodeInspector.intentaiIsChainMassage = modelConfig.isChainMassage !== undefined ? modelConfig.isChainMassage : false;
                this.nodeInspector.intentaiLenChainMassage = modelConfig.lenChainMassage || null;
                this.nodeInspector.intentaiTimeSecondChainMassage = modelConfig.timeSecondChainMassage || null;
                this.nodeInspector.intentaiShowLineName = modelConfig.isShowNameUserMonitor !== undefined ? modelConfig.isShowNameUserMonitor : false;
                this.nodeInspector.intentaiLoadingBar = modelConfig.loadingBarFlag !== undefined ? modelConfig.loadingBarFlag : false;
                this.nodeInspector.intentaiLoadingBarDuration = modelConfig.loadingBarDuration || 0;
                this.nodeInspector.intentaiMonitorLineUserId = modelConfig.monitorLineUserID || null;
                this.nodeInspector.intentaiMonitorSocialID = modelConfig.monitorSocialID || null;
                this.nodeInspector.intentaiStopSequence = modelConfig.stop || null;
                // Handle forbidden words for intentAI
                if (modelConfig.forbiddenWord) {
                    try {
                        this.nodeInspector.intentaiForbiddenWords = JSON.parse(modelConfig.forbiddenWord);
                        this.nodeInspector.intentaiForbiddenWords.push('');
                    } catch (e) {
                        this.nodeInspector.intentaiForbiddenWords = [''];
                    }
                } else {
                    this.nodeInspector.intentaiForbiddenWords = [''];
                }
            } else {
                this.nodeInspector.gender = (modelConfig.gender === "m" ? 'male' : 'female') || 'male';
                this.nodeInspector.temperature = modelConfig.temperature || null;
                this.nodeInspector.maxTokens = modelConfig.max_tokens || null;
                this.nodeInspector.topP = modelConfig.top_p || null;
                this.nodeInspector.frequencyPenalty = modelConfig.frequency_penalty || null;
                this.nodeInspector.presencePenalty = modelConfig.presence_penalty || null;
                this.nodeInspector.systemPrompt = modelConfig.promptTemplate || '';
                this.nodeInspector.typingSpeed = modelConfig.typingSpeed || null;
                this.nodeInspector.maxInputLength = modelConfig.maximumChatInput || 200000;
                this.nodeInspector.streamResponse = modelConfig.isStream !== undefined ? modelConfig.isStream : false;
                this.nodeInspector.jsonResponse = false;
                this.nodeInspector.enableTime = modelConfig.timeZone ? true : false;
                this.nodeInspector.timeZone = modelConfig.timeZone || null;
                this.nodeInspector.memoryTurn = modelConfig.historyCount || null;
                this.nodeInspector.memoryDuration = modelConfig.historyMinute < 0
                    ? Math.abs(modelConfig.historyMinute)
                    : modelConfig.historyMinute;
                this.nodeInspector.shortTermMemory = modelConfig.len_ShortMemory || null;
                this.nodeInspector.longTermMemory = modelConfig.isLongTerm_Memory !== undefined ? modelConfig.isLongTerm_Memory : false;
                this.nodeInspector.isChainMassage = modelConfig.isChainMassage !== undefined ? modelConfig.isChainMassage : false;
                this.nodeInspector.lenChainMassage = modelConfig.lenChainMassage || null;
                this.nodeInspector.timeSecondChainMassage = modelConfig.timeSecondChainMassage || null;
                this.nodeInspector.showLineName = modelConfig.isShowNameUserMonitor !== undefined ? modelConfig.isShowNameUserMonitor : false;
                this.nodeInspector.loadingBar = modelConfig.loadingBarFlag !== undefined ? modelConfig.loadingBarFlag : false;
                this.nodeInspector.loadingBarDuration = modelConfig.loadingBarDuration || 0;
                this.nodeInspector.monitorLineUserId = modelConfig.monitorLineUserID || null;
                this.nodeInspector.monitorSocialID = modelConfig.monitorSocialID || null;
                this.nodeInspector.stopSequence = modelConfig.stop || null;
                // Handle forbidden words for AI Model
                if (modelConfig.forbiddenWord) {
                    try {
                        this.nodeInspector.forbiddenWords = JSON.parse(modelConfig.forbiddenWord);
                        this.nodeInspector.forbiddenWords.push('');
                    } catch (e) {
                        this.nodeInspector.forbiddenWords = [''];
                    }
                } else {
                    this.nodeInspector.forbiddenWords = [''];
                }
            }
            
            // Common properties for both node types
            this.nodeInspector.loadingBar = modelConfig.loadingBarFlag !== undefined ? modelConfig.loadingBarFlag : false;
            this.nodeInspector.loadingBarDuration = modelConfig.loadingBarDuration || 0;
            this.nodeInspector.showLineName = modelConfig.isShowNameUserMonitor !== undefined ? modelConfig.isShowNameUserMonitor : false;
            this.nodeInspector.monitorLineUserId = modelConfig.monitorLineUserID || null;
            this.nodeInspector.monitorSocialID = modelConfig.monitorSocialID || null;
            

            // Handle forbidden words
            if (modelConfig.forbiddenWord) {
                try {
                    this.nodeInspector.forbiddenWords = JSON.parse(modelConfig.forbiddenWord);
                    this.nodeInspector.forbiddenWords.push('');
                } catch (e) {
                    this.nodeInspector.forbiddenWords = [''];
                }
            } else {
                this.nodeInspector.forbiddenWords = [''];
            }

            // Update node data immediately and force UI refresh
            this.updateAllNodeProperties();
            this.$forceUpdate();

            this.closeLoadFromModelDialog();
            this.showSuccess(this.$t('_loadedConfigFrom', { name: modelConfig.modelName || this.$t('_selectedModel') }));
            this.loadingModelConfig = false;
        },

        updateAllNodeProperties() {
            if (!this.selectedNode) return;

            // Update all properties to sync with the node data
            Object.keys(this.nodeInspector).forEach(key => {
                this.updateNodeProperty(key, this.nodeInspector[key]);
            });

            // Force Vue to update the UI
            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },

        getModelTypeColor(provider) {
            const colors = {
                'OpenAI': '#10a37f',
                'Anthropic': '#d97757',
                'Google': '#4285f4',
                'Azure': '#0078d4',
                'AWS': '#ff9900'
            };
            return colors[provider] || '#666666';
        },

        // Smart Intent Config Loading Methods
        async loadSmartIntentModelConfigPreview(modelId = null) {
            const targetModelId = modelId || this.selectedSmartIntentModelToLoad;
            if (!targetModelId) {
                this.smartIntentModelConfigPreview = null;
                return;
            }

            this.loadingSmartIntentModelConfig = true;
            try {
                // Use the same method as AI Model node
                this.$call.GetGPTModelById(
                    targetModelId,
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            const modelConfig = dataResponse.data;
                            // Map to preview format like AI Model node
                            this.smartIntentModelConfigPreview = {
                                provider: modelConfig.provider || 'N/A',
                                model: modelConfig.model || 'N/A',
                                temperature: modelConfig.temperature ?? 'N/A',
                                maxTokens: modelConfig.max_tokens ?? 'N/A',
                                topP: modelConfig.top_p ?? 'N/A',
                                frequencyPenalty: modelConfig.frequency_penalty ?? 'N/A',
                                presencePenalty: modelConfig.presence_penalty ?? 'N/A',
                                systemPrompt: modelConfig.promptTemplate ? (modelConfig.promptTemplate.length > 100 ? modelConfig.promptTemplate.substring(0, 100) + '...' : modelConfig.promptTemplate) : 'N/A',
                                // Keep original data for applying config
                                originalData: modelConfig
                            };
                        } else {
                            this.showError(this.$t('_failedLoadModelConfigMsg', { msg: dataResponse.message }));
                            this.smartIntentModelConfigPreview = null;
                        }
                        this.loadingSmartIntentModelConfig = false;
                    },
                    (error) => {
                        console.error('Error loading Smart Intent model config:', error);
                        this.showError(this.$t('_failedLoadModelConfigRetry'));
                        this.smartIntentModelConfigPreview = null;
                        this.loadingSmartIntentModelConfig = false;
                    }
                );
            } catch (error) {
                console.error('Error loading Smart Intent model config:', error);
                this.showError(this.$t('_failedLoadModelConfigRetry'));
                this.smartIntentModelConfigPreview = null;
                this.loadingSmartIntentModelConfig = false;
            }
        },

        confirmSmartIntentLoadFromModel() {
            if (!this.smartIntentModelConfigPreview || !this.smartIntentModelConfigPreview.originalData) {
                this.showError(this.$t('_noModelConfigToLoad'));
                return;
            }

            const modelConfig = this.smartIntentModelConfigPreview.originalData;

            // Apply configuration based on current selection
            if (this.currentLoadingSmartIntentIndex === 'fallback') {
                // Apply to Fallback directly (fallback doesn't have mainAction)
                this.applyConfigToTarget(modelConfig, this.nodeInspector.smartIntentFallback);
                this.showSuccess(this.$t('_loadedConfigToFallback', { name: modelConfig.modelName || this.$t('_selectedModel') }));
            } else if (this.currentLoadingSmartIntentIndex !== null) {
                // Apply to specific Smart Intent's mainAction
                const smartIntent = this.nodeInspector.smartIntents[this.currentLoadingSmartIntentIndex];
                if (smartIntent && smartIntent.mainAction) {
                    this.applyConfigToTarget(modelConfig, smartIntent.mainAction);
                    this.showSuccess(this.$t('_loadedConfigToTarget', { name: modelConfig.modelName || this.$t('_selectedModel'), target: smartIntent.name || 'Smart Intent' }));
                }
            } else {
                this.showError(this.$t('_noTargetSelected'));
                return;
            }

            // Force update nodeInspector to sync with data changes
            this.updateSmartIntentData();
            this.updateSmartIntentFallbackData();
            
            // Ensure selectedNode.data is synced with nodeInspector
            if (this.selectedNode && this.selectedNode.data) {
                this.selectedNode.data.smartIntents = JSON.parse(JSON.stringify(this.nodeInspector.smartIntents));
                this.selectedNode.data.smartIntentFallback = JSON.parse(JSON.stringify(this.nodeInspector.smartIntentFallback));
            }
            
            // Force re-sync nodeInspector from selectedNode.data
            this.syncNodeInspectorFromSelectedNode();

            // Update node data and force UI refresh
            this.updateSmartIntentNodeProperties();
            this.$forceUpdate();

            this.closeSmartIntentLoadFromModelDialog();
            this.smartIntentLoadFromModelDialog = false;
            this.loadingSmartIntentModelConfig = false;
        },

        applyConfigToTarget(modelConfig, target) {
            // Basic AI Parameters
            target.provider = modelConfig.provider || null;
            target.model = modelConfig.model || null;
            
            // Auto-assign apiVersion from listDDModel based on the loaded model
            if (modelConfig.model) {
                const selectedModel = this.listDDModel.find(m => m.modelName === modelConfig.model);
                if (selectedModel) {
                    target.apiVersion = selectedModel.apiVersion;
                }
            }

            target.systemPrompt = modelConfig.promptTemplate || '';
            target.temperature = modelConfig.temperature || null;
            target.maxTokens = modelConfig.max_tokens || null;
            target.topP = modelConfig.top_p || null;
            target.presencePenalty = modelConfig.presence_penalty || null;
            target.frequencyPenalty = modelConfig.frequency_penalty || null;
            target.streamingEnabled = modelConfig.isStream !== undefined ? modelConfig.isStream : true;
            target.lineFlexMessage = modelConfig.lineFlexMessage !== undefined ? modelConfig.lineFlexMessage : false;
            
            // Basic Information fields that are specific to Smart Intent mainAction
            if (target.hasOwnProperty('typingSpeed') || target.hasOwnProperty('maxInputLength')) {
                target.typingSpeed = modelConfig.typingSpeed || null;
                target.maxInputLength = modelConfig.maximumChatInput || 200000;
            }
            
            // Response Options fields that are specific to Smart Intent mainAction
            if (target.hasOwnProperty('enableTime') || target.hasOwnProperty('timeZone')) {
                target.enableTime = modelConfig.timeZone ? true : false;
                target.timeZone = modelConfig.timeZone || null;
            }
            
            // Memory Settings fields that are specific to Smart Intent mainAction
            if (target.hasOwnProperty('memoryTurn') || target.hasOwnProperty('memoryDuration') || target.hasOwnProperty('shortTermMemory') || target.hasOwnProperty('longTermMemory')) {
                target.memoryTurn = modelConfig.historyCount || null;
                target.memoryDuration = modelConfig.historyMinute ? Math.abs(modelConfig.historyMinute) : null;
                target.shortTermMemory = modelConfig.len_ShortMemory || null;
                target.longTermMemory = modelConfig.isLongTerm_Memory !== undefined ? modelConfig.isLongTerm_Memory : false;
            }
            
            // Multi-turn Input Configuration fields that are specific to Smart Intent mainAction
            if (target.hasOwnProperty('isChainMassage') || target.hasOwnProperty('lenChainMassage') || target.hasOwnProperty('timeSecondChainMassage')) {
                target.isChainMassage = modelConfig.isChainMassage !== undefined ? modelConfig.isChainMassage : false;
                target.lenChainMassage = modelConfig.lenChainMassage || null;
                target.timeSecondChainMassage = modelConfig.timeSecondChainMassage || null;
            }
            
            target.streamResponse = modelConfig.isStream !== undefined ? modelConfig.isStream : false;
            target.jsonResponse = modelConfig.jsonResponse !== undefined ? modelConfig.jsonResponse : false;
            target.stopSequence = modelConfig.stop || null;
            
            // Advanced configuration - map from AI Model node format
            target.loadingBar = modelConfig.loadingBarFlag !== undefined ? modelConfig.loadingBarFlag : false;
            target.loadingBarDuration = modelConfig.loadingBarDuration || null;
            target.showLineName = modelConfig.isShowNameUserMonitor !== undefined ? modelConfig.isShowNameUserMonitor : false;
            target.monitorLineUserId = modelConfig.monitorLineUserID || null;
            target.monitorSocialID = modelConfig.monitorSocialID || null;
            
            // Set forbidden words if available
            if (modelConfig.forbiddenWord) {
                try {
                    target.forbiddenWords = JSON.parse(modelConfig.forbiddenWord) || [''];
                    target.forbiddenWords.push('');
                } catch (e) {
                    target.forbiddenWords = [''];
                }
            } else {
                target.forbiddenWords = [''];
            }
            
            // Ensure forbidden words is always an array
            if (!Array.isArray(target.forbiddenWords)) {
                target.forbiddenWords = [''];
            }
        },

        updateSmartIntentNodeProperties() {
            if (!this.selectedNode || this.selectedNode.type !== 'smartIntent') return;

            // Update Smart Intent properties to sync with the node data
            if (this.selectedNode.data.smartIntents) {
                this.selectedNode.data.smartIntents = [...this.nodeInspector.smartIntents];
            }
            if (this.selectedNode.data.smartIntentFallback) {
                this.selectedNode.data.smartIntentFallback = { ...this.nodeInspector.smartIntentFallback };
            }

            // Force Vue to update the UI
            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },

        getLoadConfigTitle() {
            if (this.currentLoadingSmartIntentIndex === 'fallback') {
                return 'Fallback';
            } else if (this.currentLoadingSmartIntentIndex !== null) {
                const smartIntent = this.nodeInspector.smartIntents[this.currentLoadingSmartIntentIndex];
                return smartIntent ? (smartIntent.name || `Smart Intent ${this.currentLoadingSmartIntentIndex + 1}`) : 'Smart Intent';
            }
            return 'Smart Intent';
        },

        syncNodeInspectorFromSelectedNode() {
            if (!this.selectedNode) return;
            
            // Sync Smart Intent data back to nodeInspector
            if (this.selectedNode.data.type === 'smartIntent') {
                if (this.selectedNode.data.smartIntents) {
                    this.nodeInspector.smartIntents = JSON.parse(JSON.stringify(this.selectedNode.data.smartIntents));
                    
                    // Ensure each smart intent has all required mainAction properties
                    this.nodeInspector.smartIntents.forEach(smartIntent => {
                        if (!smartIntent.mainAction) {
                            smartIntent.mainAction = {};
                        }
                        // Set defaults for missing properties
                        const defaults = {
                            useAI: false,
                            provider: null,
                            model: null,
                            // Basic Information
                            typingSpeed: null,
                            maxInputLength: 200000,
                            // Response Options
                            enableTime: false,
                            timeZone: null,
                            // AI Parameters
                            temperature: null,
                            topP: null,
                            frequencyPenalty: null,
                            presencePenalty: null,
                            maxTokens: null,
                            stopSequence: null,
                            // Output Control
                            streamingEnabled: true,
                            // Line Configuration
                            lineFlexMessage: false,
                            // System Prompt
                            systemPrompt: '',
                            // Forbidden Words
                            forbiddenWords: [''],
                            // Memory Settings
                            memoryTurn: null,
                            memoryDuration: null,
                            shortTermMemory: null,
                            longTermMemory: false,
                            // Multi-turn Input Configuration
                            isChainMassage: false,
                            lenChainMassage: null,
                            timeSecondChainMassage: null,
                            // Knowledge Configuration
                            knowledgeType: '',
                            // Knowledge Sources Enable Flags
                            enableDocuments: false,
                            enableDatabaseIntegration: false,
                            enableProducts: false,
                            enableApiKnowledge: false,
                            // Documents properties
                            document: [],
                            topNChunks: 5,
                            cosineSimilarity: 0.5,
                            // Database Integration properties
                            tableName: null,
                            sqlExtractionModel: null,
                            promptSqlNoData: '',
                            // Products properties
                            productGroupList: [],
                            productTopNChunks: 5,
                            productCosineSimilarity: 0.5,
                            // API Knowledge properties
                            knowledgeApiUrl: '',
                            knowledgeHttpMethod: 'GET',
                            listHeaders: [],
                            listParams: [],
                            knowledgeRequestBody: '',
                            knowledgeResponseDataPath: ''
                        };
                        Object.keys(defaults).forEach(key => {
                            if (smartIntent.mainAction[key] === undefined) {
                                smartIntent.mainAction[key] = defaults[key];
                            }
                        });
                        // Ensure forbiddenWords is always an array
                        if (!Array.isArray(smartIntent.mainAction.forbiddenWords)) {
                            smartIntent.mainAction.forbiddenWords = [''];
                        }
                    });
                }
                
                if (this.selectedNode.data.smartIntentFallback) {
                    this.nodeInspector.smartIntentFallback = JSON.parse(JSON.stringify(this.selectedNode.data.smartIntentFallback));
                    
                    // Ensure fallback has all required properties
                    const fallbackDefaults = {
                        useAI: false,
                        provider: '',
                        model: '',
                        temperature: 0.7,
                        maxTokens: 150,
                        topP: 1.0,
                        presencePenalty: 0.0,
                        frequencyPenalty: 0.0,
                        streamingEnabled: true,
                        lineFlexMessage: false,
                        systemPrompt: '',
                        forbiddenWords: [''],
                        // Additional fields from AI Model node
                        typingSpeed: 50,
                        maxInputLength: 2000,
                        responseOption: 'text',
                        enableMemory: false,
                        memorySize: 10,
                        stopSequence: '',
                        seedValue: null,
                        enableSafetyFilter: false,
                        enableContentFilter: false
                    };
                    Object.keys(fallbackDefaults).forEach(key => {
                        if (this.nodeInspector.smartIntentFallback[key] === undefined) {
                            this.nodeInspector.smartIntentFallback[key] = fallbackDefaults[key];
                        }
                    });
                    // Ensure forbiddenWords is always an array
                    if (!Array.isArray(this.nodeInspector.smartIntentFallback.forbiddenWords)) {
                        this.nodeInspector.smartIntentFallback.forbiddenWords = [''];
                    }
                }
                
                if (this.selectedNode.data.smartIntentGroupName) {
                    this.nodeInspector.smartIntentGroupName = this.selectedNode.data.smartIntentGroupName;
                }
            }
        },

        async loadModelConfigPreview(modelId) {
            try {
                this.$call.GetGPTModelById(
                    modelId,
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            const modelConfig = dataResponse.data;
                            this.modelConfigPreview = {
                                provider: modelConfig.provider || 'N/A',
                                model: modelConfig.model || 'N/A',
                                temperature: modelConfig.temperature ?? 'N/A',
                                maxTokens: modelConfig.max_tokens ?? 'N/A',
                                topP: modelConfig.top_p ?? 'N/A',
                                frequencyPenalty: modelConfig.frequency_penalty ?? 'N/A',
                                presencePenalty: modelConfig.presence_penalty ?? 'N/A',
                                systemPrompt: modelConfig.promptTemplate ? (modelConfig.promptTemplate.length > 100 ? modelConfig.promptTemplate.substring(0, 100) + '...' : modelConfig.promptTemplate) : 'N/A'
                            };
                        }
                    },
                    (error) => {
                        console.error('Error loading model config preview:', error);
                    }
                );
            } catch (error) {
                console.error('Error loading model config preview:', error);
            }
        },

        async confirmSave() {
            this.saving = true;
            try {
                // Call the existing SaveFlow method
                await this.SaveFlow();

                this.closeSaveDialog();

            } catch (error) {
                console.error('Error saving flow:', error);
            } finally {
                this.saving = false;
            }
        },

        async performSave() {
            if (!this.saveFormValid) return;

            this.saving = true;
            try {
                // Update flow metadata
                this.flowName = this.saveFlowData.name;

                // Call the existing SaveFlow method
                await this.SaveFlow();

                this.closeSaveDialog();

            } catch (error) {
                console.error('Error saving flow:', error);
            } finally {
                this.saving = false;
            }
        },

        // Update exportFlow to use targets structure
        exportFlow() {
            try {
                const flatNodes = this.convertToFlatStructure();

                const flowData = {
                    name: this.flowName,
                    nodes: flatNodes,
                    metadata: {
                        exportedAt: new Date().toISOString(),
                        nodeCount: this.tempNode.length,
                        edgeCount: this.tempEdge.length,
                    }
                };

                const dataStr = JSON.stringify(flowData, null, 2);
                const dataBlob = new Blob([dataStr], { type: 'application/json' });

                const link = document.createElement('a');
                link.href = URL.createObjectURL(dataBlob);
                link.download = `Flow_Config_${this.flowName.replace(/\s+/g, '_')}.json`;
                link.click();

                this.showSuccess(this.$t('_flowExported'));
            } catch (error) {
                this.showError(this.$t('_failedExportFlow'));
            }
        },

        // Update importFlow to handle targets structure
        importFlow() {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.json';
            input.onchange = (e) => {
                const file = e.target.files[0];
                if (!file) return;

                const reader = new FileReader();
                reader.onload = (e) => {
                    try {
                        const flowData = JSON.parse(e.target.result);
                        
                        // Validate flow data structure
                        if (!flowData.nodes) {
                            throw new Error('Invalid flow file format');
                        }

                        // Check format type
                        const firstNode = flowData.nodes[0];
                        let nodes, edges;
                        if (firstNode && firstNode.targets !== undefined) {
                            // New targets format
                            const result = this.convertFromFlatStructure(flowData.nodes);
                            nodes = result.nodes;
                            edges = result.edges;
                        } else if (firstNode && firstNode.source !== undefined) {
                            // Old source format
                            const result = this.convertFromSourceFormat(flowData.nodes);
                            nodes = result.nodes;
                            edges = result.edges;
                        } else if (flowData.edges) {
                            // Very old format with separate edges
                            nodes = flowData.nodes;
                            edges = flowData.edges || [];
                        } else {
                            throw new Error('Unsupported flow file format');
                        }

                        // Generate new IDs for all nodes and update references
                        const idMap = new Map();
                        nodes.forEach(node => {
                            const oldId = node.id;
                            const newId = this.generateGuid();
                            idMap.set(oldId, newId);
                            node.id = newId;
                        });

                        // Update edge sources and targets
                        edges.forEach(edge => {
                            if (idMap.has(edge.source)) {
                                edge.source = idMap.get(edge.source);
                            }
                            if (idMap.has(edge.target)) {
                                edge.target = idMap.get(edge.target);
                            }
                        });

                        // Update node data targets if they exist
                        nodes.forEach(node => {
                            if (node.data && node.data.targets && Array.isArray(node.data.targets)) {
                                node.data.targets = node.data.targets.map(targetId => 
                                    idMap.has(targetId) ? idMap.get(targetId) : targetId
                                );
                            }
                            // Also check for any other references in node data
                            if (node.data) {
                                const dataStr = JSON.stringify(node.data);
                                const updatedDataStr = dataStr.replace(/("target":\s*"[^"]*")/g, (match, targetPart) => {
                                    const targetId = targetPart.match(/"target":\s*"([^"]*)"/)[1];
                                    if (idMap.has(targetId)) {
                                        return `"target":"${idMap.get(targetId)}"`;
                                    }
                                    return match;
                                });
                                if (dataStr !== updatedDataStr) {
                                    node.data = JSON.parse(updatedDataStr);
                                }
                            }
                        });

                        this.tempNode = nodes;
                        this.tempEdge = edges;

                        this.flowName = flowData.name || 'Imported Flow';

                        this.showSuccess(this.$t('_flowImported'));
                        this.$nextTick(() => {
                            // Check validation for all nodes after importing
                            this.checkAllNodesValidation();

                            setTimeout(() => {
                                // Fit view after importing nodes
                                if (this.$refs.vueFlow && this.tempNode.length > 0) {
                                    try {
                                        this.$refs.vueFlow.fitView({
                                            padding: 0.1,
                                            minZoom: 0.5,
                                            maxZoom: 1.5,
                                            duration: 800,
                                            includeHiddenNodes: false
                                        });
                                    } catch (error) {
                                        console.warn('Failed to fit view after import:', error);
                                    }
                                }
                            }, 200);
                        });

                    } catch (error) {
                        this.showError(this.$t('_failedImportInvalidFormat'));
                    }
                };
                reader.readAsText(file);
            };
            input.click();
        },

        // Drag and Drop Methods for JSON Editor
        onDragStart(event, text) {
            event.dataTransfer.setData("text/plain", text);
            event.dataTransfer.effectAllowed = "copy";
        },

        onJsonDragOver(event) {
            event.preventDefault();
            event.stopPropagation();

            const ta = this.getTextareaElement(event.target);
            if (ta) ta.classList.add("drag-over");
        },

        onJsonDragLeave(event) {
            event.stopPropagation();

            const ta = this.getTextareaElement(event.target);
            if (ta) ta.classList.remove("drag-over");
        },

        onJsonDrop(event) {
            event.preventDefault();
            event.stopPropagation(); // ป้องกันการทำงานซ้ำซ้อน

            const text = event.dataTransfer.getData("text/plain");
            if (!text) return;

            // หา textarea element ที่แท้จริง
            const ta = this.getTextareaElement(event.target);
            if (!ta) return;

            ta.classList.remove("drag-over");

            // คำนวณตำแหน่งการแทรกข้อความจากตำแหน่งของเมาส์
            const caretPosition = this.getCaretPositionFromPoint(ta, event.clientX, event.clientY);

            // แทรกข้อความที่ตำแหน่งที่คำนวณได้
            const value = ta.value || "";
            const newValue = value.slice(0, caretPosition) + text + value.slice(caretPosition);

            // อัพเดต textarea และ model
            ta.value = newValue;
            this.nodeInspector.jsonValidate = newValue;
            this.updateNodeProperty("jsonValidate", newValue);

            // เลื่อน caret หลังข้อความที่แทรก
            const newPos = caretPosition + text.length;
            this.$nextTick(() => {
                ta.setSelectionRange(newPos, newPos);
                ta.focus();
            });
        },

        // Drag & drop for Knowledge API JSON
        onKnowledgeJsonDrop(event) {
            event.preventDefault();
            event.stopPropagation();

            const text = event.dataTransfer.getData("text/plain");
            if (!text) return;

            // หา textarea element ที่แท้จริง
            const ta = this.getTextareaElement(event.target);
            if (!ta) return;

            ta.classList.remove("drag-over");

            // คำนวณตำแหน่งการแทรกข้อความจากตำแหน่งของเมาส์
            const caretPosition = this.getCaretPositionFromPoint(ta, event.clientX, event.clientY);

            // แทรกข้อความที่ตำแหน่งที่คำนวณได้
            const value = ta.value || "";
            const newValue = value.slice(0, caretPosition) + text + value.slice(caretPosition);

            // อัพเดต textarea และ model
            ta.value = newValue;
            this.nodeInspector.knowledgeRequestBody = newValue;
            this.updateNodeProperty("knowledgeRequestBody", newValue);

            // เลื่อน caret หลังข้อความที่แทรก
            const newPos = caretPosition + text.length;
            this.$nextTick(() => {
                ta.setSelectionRange(newPos, newPos);
                ta.focus();
            });
        },
        getCaretPositionFromPoint(textarea, clientX, clientY) {
            // สำหรับเบราว์เซอร์สมัยใหม่ที่รองรับ caretPositionFromPoint/caretRangeFromPoint
            if (document.caretPositionFromPoint) {
                const position = document.caretPositionFromPoint(clientX, clientY);
                if (position && position.offsetNode === textarea.firstChild) {
                    return position.offset;
                }
            } else if (document.caretRangeFromPoint) {
                const range = document.caretRangeFromPoint(clientX, clientY);
                if (range && range.startContainer === textarea.firstChild) {
                    return range.startOffset;
                }
            }

            // ถ้าไม่สามารถคำนวณตำแหน่งได้ ให้ใช้ตำแหน่งปัจจุบัน หรือท้ายข้อความ
            return textarea.selectionStart || textarea.value.length;
        },

        // หา <textarea> จริงๆ ใน v-textarea
        getTextareaElement(target) {
            if (target.tagName === "TEXTAREA") return target;
            const container = target.closest(".json-textarea-container");
            return container && container.querySelector("textarea");
        },

        // Click to insert variable
        insertVariable(text) {
            const textarea = this.$refs.jsonTextarea?.$el?.querySelector('textarea');

            if (textarea) {
                const start = textarea.selectionStart || 0;
                const end = textarea.selectionEnd || 0;
                const currentValue = textarea.value || '';

                const newValue =
                    currentValue.substring(0, start) +
                    text +
                    currentValue.substring(end);

                // อัพเดต model
                this.nodeInspector.jsonValidate = newValue;
                this.updateNodeProperty('jsonValidate', newValue);

                // ตั้งตำแหน่ง cursor
                this.$nextTick(() => {
                    textarea.setSelectionRange(start + text.length, start + text.length);
                    textarea.focus();
                });
            }
        },

        // Click to insert variable for Knowledge API
        insertKnowledgeVariable(text) {
            const textarea = this.$refs.knowledgeJsonTextarea?.$el?.querySelector('textarea');

            if (textarea) {
                const start = textarea.selectionStart || 0;
                const end = textarea.selectionEnd || 0;
                const currentValue = textarea.value || '';

                const newValue =
                    currentValue.substring(0, start) +
                    text +
                    currentValue.substring(end);

                // อัพเดต model
                this.nodeInspector.knowledgeRequestBody = newValue;
                this.updateNodeProperty('knowledgeRequestBody', newValue);

                // ตั้งตำแหน่ง cursor
                this.$nextTick(() => {
                    textarea.setSelectionRange(start + text.length, start + text.length);
                    textarea.focus();
                });
            }
        },

        // Mask placeholders like {userMessage} → bare sentinel so the
        // surrounding "..." in source still parses as a valid JSON string,
        // pretty-print, then restore placeholders verbatim.
        _formatJsonWithPlaceholders(raw) {
            const placeholders = [];
            const masked = raw.replace(/\{[^{}\s"]+\}/g, (match) => {
                const idx = placeholders.length;
                placeholders.push(match);
                return `__FUSION_PH_${idx}__`;
            });
            const formatted = JSON.stringify(JSON.parse(masked), null, 2);
            return formatted.replace(/__FUSION_PH_(\d+)__/g, (_, i) => placeholders[Number(i)]);
        },

        // Pretty-print JSON in API Knowledge request body.
        beautifyKnowledgeJson() {
            const raw = (this.nodeInspector.knowledgeRequestBody || '').trim();
            if (!raw) {
                this.showWarning(this.$t('_requestBodyEmpty'));
                return;
            }
            let formatted;
            try { formatted = this._formatJsonWithPlaceholders(raw); }
            catch (e) {
                this.showError(this.$t('_invalidJsonBeautify'));
                return;
            }
            this.nodeInspector.knowledgeRequestBody = formatted;
            this.updateNodeProperty('knowledgeRequestBody', formatted);
            this.showSuccess(this.$t('_jsonFormatted'));
        },

        // Pretty-print JSON in API Trigger validation schema.
        beautifyTriggerJson() {
            const raw = (this.nodeInspector.jsonValidate || '').trim();
            if (!raw) {
                this.showWarning(this.$t('_jsonEmpty'));
                return;
            }
            let formatted;
            try { formatted = this._formatJsonWithPlaceholders(raw); }
            catch (e) {
                this.showError(this.$t('_invalidJsonBeautify'));
                return;
            }
            this.nodeInspector.jsonValidate = formatted;
            this.updateNodeProperty('jsonValidate', formatted);
            this.showSuccess(this.$t('_jsonFormatted'));
        },
        getValidationErrors() {
            const errors = [];

            if (!this.tempNode.length) {
                errors.push('Flow must contain at least one node');
            }

            const isolatedNodes = this.tempNode.filter(node => node.data.showWarning != false);
            
            if (isolatedNodes.length) {
                isolatedNodes.forEach(element => {
                    errors.push(`${element.id} isolated node found`);
                });
            }

            return errors;
        },

        formatDate(dateString) {
            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now - date;
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

            if (diffDays === 0) return 'Today';
            if (diffDays === 1) return 'Yesterday';
            if (diffDays < 7) return `${diffDays} days ago`;

            return date.toLocaleDateString();
        },

        handleBeforeUnload(event) {
            // Skip browser confirmation if we're already navigating away via custom dialog
            if (this.isNavigatingAway) {
                return;
            }
            
            // Show warning for page refresh, close tab, or navigate away from the page
            if (!this.isViewMode) {
                const message = 'Are you sure you want to leave? Any unsaved changes will be lost.';
                
                // Modern browsers - this will trigger the browser's confirmation dialog
                event.preventDefault();
                event.returnValue = message;
                
                // For older browsers
                return message;
            }
        },

        // Custom confirmation dialog methods
        showExitConfirmation(callback) {
            if (!this.isViewMode && (this.canUndo || this.canRedo)) {
                this.pendingNavigation = callback;
                this.confirmExitDialog = true;
                return false; // Prevent immediate navigation
            } else {
                callback(); // Proceed with navigation if in view mode
                return true;
            }
        },

        confirmExit() {
            this.confirmExitDialog = false;
            this.isNavigatingAway = true; // Set flag to prevent browser confirmation
            
            if (this.pendingNavigation) {
                // Add a small delay to ensure the flag is set before navigation
                this.$nextTick(() => {
                    this.pendingNavigation();
                    this.pendingNavigation = null;
                });
            }
        },

        cancelExit() {
            this.confirmExitDialog = false;
            this.pendingNavigation = null;
        },

        // Prompt Template Methods
        showPromptTemplate() {
            this.promptTemplateDialog = true;
        },
        
        showSmartIntentPromptTemplate(smartIntentIndex) {
            this.smartIntentCurrentIndex = smartIntentIndex;
            // Reset any existing group selection and search query for fresh start
            this.smartIntentGroupPromptTemplate = null;
            this.smartIntentTemplateSearchQuery = '';
            this.smartIntentPromptTemplateDialog = true;
        },

        setSelectSmartIntentPromptTemplate(template) {
            if (template && template.prompt && this.smartIntentCurrentIndex !== null) {
                const smartIntent = this.nodeInspector.smartIntents[this.smartIntentCurrentIndex];
                if (smartIntent && smartIntent.mainAction) {
                    // Insert template content into Smart Intent system prompt
                    const currentPrompt = smartIntent.mainAction.systemPrompt || '';
                    const templatePrompt = template.prompt;

                    // Add template content (could append or replace based on preference)
                    smartIntent.mainAction.systemPrompt = currentPrompt + (currentPrompt ? '\n\n' : '') + templatePrompt;
                    this.updateSmartIntentData();
                }

                // Close template dialog
                this.smartIntentPromptTemplateDialog = false;
                this.smartIntentCurrentIndex = null; // Reset
            }
        },

        showSmartIntentFallbackPromptTemplate() {
            // Reset any existing group selection and search query for fresh start
            this.smartIntentFallbackGroupPromptTemplate = null;
            this.smartIntentFallbackTemplateSearchQuery = '';
            this.smartIntentFallbackPromptTemplateDialog = true;
        },

        setSelectSmartIntentFallbackPromptTemplate(template) {
            if (template && template.prompt) {
                // Insert template content into Smart Intent Fallback system prompt
                const currentPrompt = this.nodeInspector.smartIntentFallback.systemPrompt || '';
                const templatePrompt = template.prompt;

                // Add template content (could append or replace based on preference)
                this.nodeInspector.smartIntentFallback.systemPrompt = currentPrompt + (currentPrompt ? '\n\n' : '') + templatePrompt;
                this.updateSmartIntentFallbackData();

                // Close template dialog
                this.smartIntentFallbackPromptTemplateDialog = false;
            }
        },

        setSelectPromptTemplate(template) {
            if (template && template.prompt) {
                // Only for regular AI Model nodes
                const currentPrompt = this.nodeInspector.systemPrompt || '';
                const templatePrompt = template.prompt;

                // Add template content (could append or replace based on preference)
                this.nodeInspector.systemPrompt = currentPrompt + (currentPrompt ? '\n\n' : '') + templatePrompt;
                this.updateNodeProperty('systemPrompt', this.nodeInspector.systemPrompt);

                // Close template dialog
                this.promptTemplateDialog = false;
            }
        },
        
        truncatePrompt(prompt) {
            if (!prompt) return '';
            return prompt.length > 100 ? prompt.substring(0, 100) + '...' : prompt;
        },

        onContextMenu(event) {
            if (this.filteredPromptTemplates.length > 0) {
                event.preventDefault();
                this.x = event.clientX;
                this.y = event.clientY;
                this.showMenu = true;
            }
        },

        // IntentAI Prompt Template Methods
        showIntentaiPromptTemplate() {
            this.intentaiPromptTemplateDialog = true;
        },

        setSelectIntentaiPromptTemplate(template) {
            if (template && template.prompt) {
                // Insert template content into intentAI system prompt
                const currentPrompt = this.nodeInspector.intentaiSystemPrompt || '';
                const templatePrompt = template.prompt;

                // Add template content (could append or replace based on preference)
                this.nodeInspector.intentaiSystemPrompt = currentPrompt + (currentPrompt ? '\n\n' : '') + templatePrompt;
                this.updateNodeProperty('intentaiSystemPrompt', this.nodeInspector.intentaiSystemPrompt);

                // Close template dialog
                this.intentaiPromptTemplateDialog = false;
            }
        },

        onIntentaiContextMenu(event) {
            if (this.filteredIntentaiPromptTemplates.length > 0) {
                event.preventDefault();
                this.x = event.clientX;
                this.y = event.clientY;
                this.showIntentaiMenu = true;
            }
        },

        // Input validation functions
        isNumberpositive: function (evt) {
            evt = evt || window.event;
            let charCode = evt.which ? evt.which : evt.keyCode;
            // Block minus sign (45) and any non-digit characters, but allow control keys (0-31)
            if ((charCode > 31 && (charCode < 48 || charCode > 57)) || charCode === 45) {
                evt.preventDefault();
            } else {
                return true;
            }
        },

        maxNumberpositive: function (evt) {
            evt = evt || window.event;
            let charCode = evt.which ? evt.which : evt.keyCode;

            // อนุญาตเฉพาะตัวเลข 0-9 เท่านั้น (บล็อกเครื่องหมายลบและตัวอื่นๆ)
            if (charCode < 48 || charCode > 57) {
                evt.preventDefault();
                return false;
            }

            return true;
        },

        // Function สำหรับตรวจสอบและปรับค่าเมื่อ unfocus หรือกด enter
        validateAndCorrectValue: function (evt, min = 1, max = 200000) {
            let value = parseInt(evt.target.value) || 0;

            if (value < min) {
                evt.target.value = min;
            } else if (value > max) {
                evt.target.value = max;
            }

            // อัปเดตค่าในโมเดล
            const fieldName = evt.target.getAttribute('data-field');
            if (fieldName && this.nodeInspector[fieldName] !== undefined) {
                this.nodeInspector[fieldName] = parseInt(evt.target.value);
                this.updateNodeProperty(fieldName, parseInt(evt.target.value));
            }
        },

        // === Email Node — Mail Config helpers ===
        createEmptyMailConfig(existing) {
            const def = {
                emailSendingMethod: 'SMTP',
                smtpProvider: 'Office365',
                smtpAuthMethod: 'OAuth2',
                smtpHost: 'smtp.office365.com',
                smtpPort: 587,
                smtpEnableSsl: true,
                smtpTimeout: 30000,
                emailHost: '',
                hostName: '',
                password: '',
                smtpOAuth2TenantId: '',
                smtpOAuth2ClientId: '',
                smtpOAuth2ClientSecret: '',
                smtpOAuth2Provider: 'AzureAD',
                mailgunApiUrl: '',
                mailgunApiKey: '',
                mailFrom: '',
            };
            return Object.assign({}, def, existing || {});
        },
        isEncryptedSecret(value) {
            return typeof value === 'string' && value.startsWith('enc:');
        },
        toggleMailConfigSource(useCentral) {
            this.nodeInspector.useCentralConfig = useCentral;
            this.updateNodeProperty('useCentralConfig', useCentral);
            if (!useCentral && (!this.nodeInspector.mailConfig || !this.nodeInspector.mailConfig.emailSendingMethod)) {
                // Pre-fill from legacy emailProvider when first switching to custom
                const seed = {};
                if (this.nodeInspector.emailProvider === 'mailgun') seed.emailSendingMethod = 'Mailgun';
                else if (this.nodeInspector.emailProvider === 'smtp') seed.emailSendingMethod = 'SMTP';
                this.nodeInspector.mailConfig = this.createEmptyMailConfig(Object.assign({}, this.nodeInspector.mailConfig || {}, seed));
                this.updateNodeProperty('mailConfig', this.nodeInspector.mailConfig);
            }
            this.emailTestResult = null;
        },
        updateMailConfigProperty(key, value) {
            if (!this.nodeInspector.mailConfig) {
                this.nodeInspector.mailConfig = this.createEmptyMailConfig();
            }
            this.nodeInspector.mailConfig[key] = value;
            this.updateNodeProperty('mailConfig', Object.assign({}, this.nodeInspector.mailConfig));
        },
        onSmtpProviderChange(provider) {
            if (!this.nodeInspector.mailConfig) {
                this.nodeInspector.mailConfig = this.createEmptyMailConfig();
            }
            const mc = this.nodeInspector.mailConfig;
            mc.smtpProvider = provider;
            // Provider presets — Gmail/Office365 lock host/port/ssl + nudge auth method to the supported one
            if (provider === 'Gmail') {
                mc.smtpHost = 'smtp.gmail.com';
                mc.smtpPort = 587;
                mc.smtpEnableSsl = true;
                mc.smtpAuthMethod = 'Basic';
            } else if (provider === 'Office365') {
                mc.smtpHost = 'smtp.office365.com';
                mc.smtpPort = 587;
                mc.smtpEnableSsl = true;
                if (mc.smtpAuthMethod !== 'OAuth2' && mc.smtpAuthMethod !== 'Basic') {
                    mc.smtpAuthMethod = 'OAuth2';
                }
            }
            // Provider === 'Other' — leave existing host/port/ssl/auth as-is so user can edit freely
            this.nodeInspector.mailConfig = Object.assign({}, mc);
            this.updateNodeProperty('mailConfig', this.nodeInspector.mailConfig);
            this.emailTestResult = null;
        },
        onMailConfigSecretChange(key, value) {
            // When user types into a previously encrypted field, replace with the new plain value.
            this.updateMailConfigProperty(key, value);
        },
        testEmailNodeConnection() {
            const self = this;
            self.emailTestResult = null;
            self.emailTestLoading = true;
            const payload = {
                UseCentralConfig: !!self.nodeInspector.useCentralConfig,
                MailConfig: self.nodeInspector.useCentralConfig ? null : (self.nodeInspector.mailConfig || null),
            };
            self.$call.TestEmailConnection(payload,
                (resp) => {
                    self.emailTestLoading = false;
                    const data = resp && (resp.Data || resp.data) || {};
                    self.emailTestResult = {
                        success: !!data.Success || !!data.success,
                        message: data.Message || data.message || 'No response',
                        elapsedMs: data.ElapsedMs != null ? data.ElapsedMs : data.elapsedMs,
                    };
                },
                (err) => {
                    self.emailTestLoading = false;
                    self.emailTestResult = {
                        success: false,
                        message: (err && (err.message || err.toString())) || 'Request failed',
                        elapsedMs: null,
                    };
                }
            );
        },

        // Email list management methods
        addEmailToList(listType) {
            if(listType == 'ccEmails') {
                if (!this.nodeInspector.ccEmails) {
                    this.nodeInspector.ccEmails = [];
                }
                this.nodeInspector.ccEmails.push('');
            }
            else {
                if (!this.nodeInspector.bccEmails) {
                    this.nodeInspector.bccEmails = [];
                }
                this.nodeInspector.bccEmails.push('');
            }
            this.updateEmailList(listType);
        },

        removeEmailFromList(listType, index) {
            if (this.nodeInspector[listType]) {
                this.nodeInspector[listType].splice(index, 1);
                this.updateEmailList(listType);
            }
        },

        updateEmailList(listType) {
            if(listType == 'ccEmails') {
                if (!this.nodeInspector.ccEmails) {
                    this.nodeInspector.ccEmails = [];
                    this.updateNodeProperty(listType, []);
                    return;
                }
                const filteredEmails = this.nodeInspector.ccEmails.filter(email => email && email.trim() !== '');
                this.nodeInspector.ccEmails = [...this.nodeInspector.ccEmails]; // trigger reactivity
                this.updateNodeProperty(listType, this.nodeInspector.ccEmails);
            }
            else {
                if (!this.nodeInspector.bccEmails) {
                    this.nodeInspector.bccEmails = [];
                    this.updateNodeProperty(listType, []);
                    return;
                }
                const filteredEmails = this.nodeInspector.bccEmails.filter(email => email && email.trim() !== '');
                this.nodeInspector.bccEmails = [...this.nodeInspector.bccEmails]; // trigger reactivity
                this.updateNodeProperty(listType, this.nodeInspector.bccEmails);
            }
        }
    },
    async mounted() {
        try {
            // Load models first and wait for completion
            await Promise.all([
                this.loadGPTModels(),
                this.GetAllDDModelManagement(),
                this.loadAgentTools(),
                this.loadKnowledgeGraphs() // S15-P1 — resolve เสมอ ไม่ทำให้ catch ข้างล่างเด้ง toast
            ]);
        } catch (error) {
            console.warn('Failed to load models:', error);
            // Show error message to user
            this.showError(this.$t('_failedLoadAiModels'));
        }
        
        // Then check route mode after models are loaded
        await this.checkRouteMode();

        // Initialize history after loading
        this.initializeHistory();

        // Check validation for all nodes after loading
        this.$nextTick(() => {
            this.checkAllNodesValidation();
        });

        this.$nextTick(() => {
            if (this.$refs.vueFlow) {
                const flow = this.$refs.vueFlow;
                // Set default unlocked state
                flow.nodesDraggable = true;
                flow.nodesConnectable = true;

                // flow.elementsSelectable = true;
                // flow.zoomOnScroll = true;
                // flow.zoomOnDoubleClick = true;
                // flow.panOnDrag = true;

                // Ensure interactive controls are enabled by default (unlocked state)
                this.interactiveControls = true;
                if (this.isViewMode) {
                    this.setViewOnlyMode();
                }
            }

            // Focus the flow container so it can receive keyboard events
            if (this.$refs.flowContainer) {
                this.$refs.flowContainer.focus();
            }
        });
        
        window.addEventListener('beforeunload', this.handleBeforeUnload);
    },
    beforeRouteLeave(to, from, next) {
        if (!this.isViewMode && !this.isNavigatingAway) {
            // Use custom confirmation dialog instead of browser confirm
            this.showExitConfirmation(() => {
                this.isNavigatingAway = true; // Set flag to prevent handleBeforeUnload from triggering
                next();
            });
        } else {
            next();
        }
    },

    beforeUnmount() {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        
        // Clear property update timer
        if (this.propertyUpdateTimer) {
            clearTimeout(this.propertyUpdateTimer);
        }
    }
}
</script>

<style scoped>
.card2 {
    height: 100%;
    border-radius: 16px !important;
    margin-bottom: 2rem;
    padding-top: 1rem;
    position: relative;
}

.flow-container {
    height: 100%;
    width: 100%;
    border: 1px solid rgba(0, 0, 0, 0.1);
    overflow: hidden;
    border-radius: 12px;
    outline: none; /* Remove focus outline */
}

.basic-flow {
    height: 100%;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    position: relative;
}

.debug-readonly-flow {
    border-color: rgba(107, 114, 128, 0.15);
    background: #fcfcfd;
}

.debug-readonly-flow .basic-flow {
    background: linear-gradient(135deg, #fafbfc 0%, #f0f2f5 100%);
}

.debug-readonly-flow :deep(.vue-flow__pane),
.debug-readonly-flow :deep(.vue-flow__node),
.debug-readonly-flow :deep(.vue-flow__edge) {
    cursor: default !important;
}

.debug-readonly-flow :deep(.vue-flow__node) {
    filter: grayscale(0.18) saturate(0.82) opacity(0.82) brightness(1.04);
}

.debug-readonly-flow :deep(.vue-flow__node.has-breakpoint-node),
.debug-readonly-flow :deep(.vue-flow__node.debug-success-node) {
    filter: none;
}

.debug-readonly-flow :deep(.debug-breakpoint-toggle) {
    cursor: pointer !important;
}

.debug-readonly-flow :deep(.delete-button),
.debug-readonly-flow :deep(.adv-router-test-pill),
.debug-readonly-flow :deep(.vue-flow__selection),
.debug-readonly-flow :deep(.vue-flow__nodesselection-rect) {
    display: none !important;
}

.debug-readonly-flow :deep(.vue-flow__background-pattern) {
    opacity: 0.35;
}

/* Header improvements */
:deep(.v-toolbar) {
    border-radius: 12px !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12) !important;
}

:deep(.vue-flow__edge) {
    stroke-width: 2;
}

:deep(.vue-flow__edge-path) {
    stroke: #1976d2;
}

:deep(.vue-flow__edge-text) {
    font-size: 12px;
    font-weight: 500;
    fill: #1976d2;
}

:deep(.vue-flow__background) {
    background: transparent !important;
}

:deep(.vue-flow__controls) {
    bottom: 5px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 3px;
    gap: 4px;
}

:deep(.vue-flow__controls-button) {
    background: transparent;
    border: none;
    border-radius: 8px;
    margin: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

:deep(.vue-flow__controls-button:hover) {
    background: rgba(59, 130, 246, 0.1);
    transform: scale(1.05);
}

:deep(.vue-flow__controls-button:active) {
    transform: scale(0.95);
}

/* Enhanced edge hover effects */
:deep(.edge-hovered .vue-flow__edge-path) {
    transition: all 0.3s ease;
    stroke: #ffc400 !important;
    filter: drop-shadow(0 0 5px #ffc400) drop-shadow(0 0 10px #ffc400);
}

/* Node selection effects */
/* :deep(.vue-flow__node.selected) {
        box-shadow: 0 0 0 2px #3b82f6, 0 0 0 4px rgba(59, 130, 246, 0.2);
    } */

/* Responsive design */
@media (max-width: 768px) {
    .flow-container {
        height: 400px;
        margin: 0.5rem;
    }

    :deep(.vue-flow__controls) {
        bottom: 10px;
        left: 10px;
    }
}

/* Loading state */
.flow-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    background: rgba(255, 255, 255, 0.9);
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
}

.custom-controls-demo {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 10;
    background: white;
    padding: 10px 15px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    gap: 15px;
}

/* Model Selector Styles */
.model-option {
    padding: 8px 0;
}

.model-option-selected {
    background-color: #e0e0e0 !important;
    border-radius: 8px;
}

.model-icon-small {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.model-chip-selected {
    background-color: #e0e0e0 !important;
}

/* Hover effect on selected model chips in autocomplete */
.model-chip-hover:hover {
    background-color: #e0e0e0 !important;
}

/* Floating Inspector Panel */
.floating-inspector {
    position: fixed;
    top: 25px;
    right: 10px;
    width: 420px;
    max-height: calc(100vh - 80px);
    background: rgba(255, 255, 255, 0.5);
    border-radius: 20px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 32px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    border: 1px solid rgba(0, 0, 0, 0.06);
    backdrop-filter: blur(10px);
    animation: slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    overflow: hidden;
}

.floating-inspector.inspector-expanded {
    position: fixed;
    top: 20px;
    left: 40%;
    right: 20px;
    bottom: 0px;
    width: auto;
    max-height: none;
    z-index: 1001;
    border-radius: 24px;
}

/* When inside the internal flow dialog, keep the inspector above the dialog
   overlay but below Vuetify menu overlays so dropdowns can expand over it. */
.floating-inspector--internal {
    z-index: 2405;
}

.floating-inspector--internal.inspector-expanded {
    z-index: 2405;
}

.inspector-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 28px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 20px 20px 0 0;
    position: relative;
    overflow: hidden;
}

.inspector-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
    pointer-events: none;
}

.inspector-title {
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 1.1rem;
    z-index: 1;
    position: relative;
}

.inspector-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    z-index: 1;
    position: relative;
}

.inspector-controls .v-btn {
    background: rgba(255, 255, 255, 0.15) !important;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    transition: all 0.2s ease !important;
}

.inspector-controls .v-btn:hover {
    background: rgba(255, 255, 255, 0.25) !important;
    transform: scale(1.05);
}

.inspector-content {
    flex: 1;
    overflow-y: auto;
    padding: 0px;
    background-color: white;
}

/* Form Sections */
.section-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.add-btn {
    height: 36px !important;
    font-size: 0.8rem !important;
    font-weight: 600 !important;
    border-radius: 10px !important;
    text-transform: none !important;
    letter-spacing: 0.3px !important;
}

/* Advanced Router - Intent Tree */
.adv-router-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 12px;
    margin-bottom: 10px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.adv-router-card:hover {
    border-color: #d1d5db;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
.adv-router-card--l1 { border-left: 3px solid #a855f7; }
.adv-router-card--l2 { border-left: 3px solid #6366f1; }
.adv-router-card--l3 { border-left: 3px solid #7c3aed; }
.adv-router-card--l4 { border-left: 3px solid #ec4899; }

.adv-router-card-row {
    display: flex;
    align-items: center;
    gap: 8px;
}
.adv-router-depth-chip {
    flex-shrink: 0;
    font-weight: 600 !important;
    letter-spacing: 0.5px !important;
    min-width: 32px;
    justify-content: center;
}
.adv-router-delete-btn {
    flex-shrink: 0;
}
.adv-router-description {
    margin-top: 8px;
}

.adv-router-children {
    margin-top: 12px;
    padding-left: 12px;
    border-left: 2px dashed #e5e7eb;
}
.adv-router-children-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}
.adv-router-children-label {
    font-size: 0.75rem;
    color: #6b7280;
    font-weight: 500;
}

.adv-router-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 24px 16px;
    background: #fafafa;
    border-radius: 10px;
    border: 1px dashed #e5e7eb;
    margin-bottom: 12px;
    text-align: center;
    line-height: 1.4;
}

/* ===== Generate Classifier Prompt dialog ===== */
.generate-prompt-dialog {
    border-radius: 16px !important;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(15, 23, 42, 0.18) !important;
    background: #ffffff;
}

.gp-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 20px 24px !important;
    background: linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%);
    border-bottom: none;
}

.gp-header-icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.18);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.gp-header-text {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
    min-width: 0;
}

.gp-title {
    font-size: 18px;
    font-weight: 600;
    color: #ffffff;
    margin: 0;
    letter-spacing: 0.1px;
}

.gp-subtitle {
    font-size: 12.5px;
    color: rgba(255, 255, 255, 0.82);
    margin-top: 2px;
    font-weight: 400;
}

.gp-content {
    padding: 24px !important;
    background: #fafbfc;
}

.gp-section {
    margin-bottom: 22px;
}
.gp-section:last-of-type {
    margin-bottom: 0;
}

.gp-section-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 6px;
}

.gp-section-label {
    font-size: 13px;
    font-weight: 600;
    color: #334155;
    text-transform: none;
    letter-spacing: 0.1px;
    margin: 0 0 8px 0;
    padding: 0;
}

.gp-section-header .gp-section-label {
    margin-bottom: 0;
}

.gp-optional {
    font-size: 11px;
    font-weight: 500;
    color: #94a3b8;
    margin-left: 6px;
    text-transform: lowercase;
}

.gp-section-hint {
    font-size: 11.5px;
    color: #94a3b8;
}

.gp-section-help {
    font-size: 12.5px;
    color: #64748b;
    margin: 0 0 12px 0;
    line-height: 1.5;
}

.gp-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 20px 16px;
    background: #ffffff;
    border-radius: 10px;
    border: 1px dashed #e2e8f0;
    font-size: 12.5px;
    color: #94a3b8;
}

.gp-intents-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.gp-intent-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 12px 14px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.gp-intent-card:hover {
    border-color: #cbd5e1;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}
.gp-intent-card--l1 { border-left: 3px solid #8B5CF6; }
.gp-intent-card--l2 { border-left: 3px solid #6366F1; }
.gp-intent-card--l3 { border-left: 3px solid #7C3AED; }
.gp-intent-card--l4 { border-left: 3px solid #EC4899; }

.gp-intent-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.gp-level-chip {
    flex-shrink: 0;
    font-weight: 600 !important;
    letter-spacing: 0.4px !important;
    min-width: 30px;
    height: 20px !important;
    justify-content: center;
}

.gp-intent-name {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.gp-intent-input :deep(.v-field),
.gp-textarea :deep(.v-field) {
    border-radius: 8px;
    background: #ffffff;
    font-size: 13px;
}

.gp-intent-input :deep(.v-field--focused),
.gp-textarea :deep(.v-field--focused) {
    border-color: #6366F1;
}

.gp-alert {
    border-radius: 10px !important;
    font-size: 12.5px;
}

.gp-actions {
    background: #ffffff;
    border-top: 1px solid #f1f5f9;
    padding: 14px 24px !important;
    gap: 8px;
}

.gp-btn-cancel {
    text-transform: none !important;
    font-weight: 500 !important;
    color: #64748b !important;
    letter-spacing: 0 !important;
    height: 40px !important;
    padding: 0 16px !important;
    border-radius: 8px !important;
}

.gp-btn-generate {
    text-transform: none !important;
    font-weight: 600 !important;
    letter-spacing: 0.1px !important;
    height: 40px !important;
    padding: 0 20px !important;
    border-radius: 8px !important;
    background: linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%) !important;
    color: #ffffff !important;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.35) !important;
}
.gp-btn-generate:hover:not(.v-btn--disabled) {
    box-shadow: 0 6px 18px rgba(99, 102, 241, 0.5) !important;
}
.gp-btn-generate.v-btn--disabled {
    background: #e2e8f0 !important;
    color: #94a3b8 !important;
    box-shadow: none !important;
}

/* Running-light wrapper for Generate with AI — same chase-light technique as Run Test,
   using the dialog's existing indigo/purple palette so it reads as part of the same family. */
.gp-btn-generate-wrap {
    position: relative;
    display: inline-block;
    border-radius: 10px;
    isolation: isolate;
}

/* While loading, the button is disabled to block re-clicks, but we override the default
   disabled "gray pill" styling so the gradient + text remain visible (the running-light
   border is doing the progress signalling, no need for a spinner overlay). */
.gp-btn-generate-wrap--loading > .gp-btn-generate.v-btn--disabled {
    background: linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%) !important;
    color: #ffffff !important;
    opacity: 0.85;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3) !important;
}

.gp-btn-generate-wrap--loading::before,
.gp-btn-generate-wrap--loading::after {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 10px;
    pointer-events: none;
    z-index: 0;
}

.gp-btn-generate-wrap--loading::before {
    padding: 2px;
    background: conic-gradient(from var(--tp-angle),
        rgba(99, 102, 241, 0) 0deg,
        rgba(99, 102, 241, 0) 280deg,
        rgba(99, 102, 241, 0.3) 305deg,
        #6366F1 335deg,
        #8B5CF6 355deg,
        rgba(139, 92, 246, 0.3) 25deg,
        rgba(99, 102, 241, 0) 50deg,
        rgba(99, 102, 241, 0) 360deg);
    -webkit-mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    animation: tp-edge-run 1.6s linear infinite;
}

.gp-btn-generate-wrap--loading::after {
    inset: -5px;
    border-radius: 12px;
    background: conic-gradient(from var(--tp-angle),
        rgba(99, 102, 241, 0) 0deg,
        rgba(99, 102, 241, 0) 290deg,
        rgba(99, 102, 241, 0.55) 335deg,
        rgba(139, 92, 246, 0.7) 355deg,
        rgba(99, 102, 241, 0) 30deg,
        rgba(99, 102, 241, 0) 360deg);
    filter: blur(7px);
    opacity: 0.65;
    animation: tp-edge-run 1.6s linear infinite;
}

.gp-btn-generate-wrap > .v-btn {
    position: relative;
    z-index: 1;
}

/* === Test Classifier Prompt Dialog (teal/cyan palette) === */
.test-prompt-dialog {
    border-radius: 16px !important;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(15, 118, 110, 0.18) !important;
}

.tp-header {
    background: linear-gradient(135deg, #0d9488 0%, #06b6d4 100%);
    color: #ffffff;
    padding: 20px 24px !important;
    display: flex !important;
    align-items: center;
    gap: 14px;
}

.tp-header-icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.18);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border: 1px solid rgba(255, 255, 255, 0.25);
}

.tp-header-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.tp-title {
    font-size: 17px;
    font-weight: 700;
    letter-spacing: 0.2px;
    margin: 0;
    color: #ffffff;
}

.tp-subtitle {
    font-size: 12.5px;
    color: rgba(255, 255, 255, 0.85);
    font-weight: 400;
}

.tp-content {
    background: #f8fafc;
    padding: 22px 24px 18px !important;
}

.tp-model-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    margin-bottom: 18px;
    font-size: 13px;
}

.tp-model-info-label {
    color: #64748b;
    font-weight: 500;
}

.tp-model-info-name {
    color: #0f172a;
    font-weight: 600;
}

.tp-model-info-chip {
    margin-left: auto;
    font-weight: 600 !important;
    letter-spacing: 0.3px !important;
}

.tp-section {
    margin-bottom: 18px;
}

.tp-section-label {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #475569;
    margin: 0 0 8px;
}

.tp-section-label--lg {
    font-size: 13px;
    letter-spacing: 0.5px;
}

.tp-section-label--sm {
    font-size: 11px;
    letter-spacing: 0.5px;
}

.tp-textarea :deep(.v-field) {
    border-radius: 10px;
    background: #ffffff;
}

.tp-alert {
    border-radius: 10px !important;
    font-size: 12.5px;
}

.tp-results {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 16px 18px;
    margin-top: 6px;
}

.tp-results-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    gap: 12px;
    flex-wrap: wrap;
}

.tp-results-meta {
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
    font-size: 12px;
    color: #64748b;
}

.tp-meta-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
}

.tp-meta-sub {
    color: #94a3b8;
    margin-left: 2px;
}

.tp-match-card {
    padding: 14px 16px;
    border-radius: 10px;
    margin-bottom: 14px;
}

.tp-match-card--success {
    background: linear-gradient(135deg, #ecfdf5 0%, #f0fdfa 100%);
    border: 1px solid #99f6e4;
}

.tp-match-card--fallback {
    background: linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%);
    border: 1px solid #bfdbfe;
}

.tp-match-card--intent-fallback {
    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
    border: 1px solid #fcd34d;
}

.tp-fallback-icon {
    color: #3b82f6 !important;
}

.tp-intent-fallback-icon {
    color: #d97706 !important;
}

.tp-match-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
}

.tp-match-title {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
}

.tp-confidence-chip {
    margin-left: auto !important;
    font-weight: 700 !important;
    letter-spacing: 0.3px !important;
}

.tp-match-path {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
    margin-bottom: 8px;
}

.tp-path-chip {
    font-weight: 600 !important;
    letter-spacing: 0.2px !important;
}

.tp-path-arrow {
    color: #94a3b8;
}

.tp-match-footnote {
    font-size: 11.5px;
    color: #64748b;
    letter-spacing: 0.2px;
}

.tp-scores {
    margin-bottom: 14px;
}

.tp-scores-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
}

.tp-scores-hint {
    font-size: 11px;
    color: #94a3b8;
    font-weight: 500;
}

.tp-scores-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.tp-score-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid transparent;
    transition: border-color 0.15s;
}

.tp-score-row--matched {
    background: #ecfdf5;
    border-color: #6ee7b7;
}

.tp-score-level {
    flex-shrink: 0;
    font-weight: 700 !important;
    letter-spacing: 0.3px !important;
}

.tp-score-path {
    flex: 0 0 auto;
    min-width: 160px;
    font-size: 12.5px;
    color: #1e293b;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tp-score-bar-wrap {
    flex: 1 1 auto;
    height: 6px;
    background: #e2e8f0;
    border-radius: 3px;
    overflow: hidden;
    min-width: 60px;
}

.tp-score-bar {
    height: 100%;
    border-radius: 3px;
    transition: width 0.3s;
}

.tp-score-bar--ok {
    background: linear-gradient(90deg, #10b981, #34d399);
}

.tp-score-bar--mid {
    background: linear-gradient(90deg, #f59e0b, #fbbf24);
}

.tp-score-bar--low {
    background: linear-gradient(90deg, #ef4444, #f87171);
}

.tp-score-pct {
    font-size: 12px;
    font-weight: 600;
    color: #334155;
    min-width: 38px;
    text-align: right;
    flex-shrink: 0;
}

.tp-intent-tree {
    margin-bottom: 14px;
}

.tp-tree-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.tp-tree-row {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 9px 10px;
    background: #f8fafc;
    border: 1px solid transparent;
    border-radius: 8px;
}

.tp-tree-row--matched {
    background: #ecfdf5;
    border-color: #6ee7b7;
}

.tp-tree-row--fallback {
    background: #fffbeb;
    border-color: #fcd34d;
}

.tp-tree-main {
    flex: 1 1 auto;
    min-width: 0;
}

.tp-tree-name {
    font-size: 12.5px;
    font-weight: 700;
    color: #1e293b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tp-tree-route,
.tp-tree-error {
    font-size: 11px;
    color: #64748b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tp-tree-error {
    color: #b91c1c;
}

.tp-binary-score-chip {
    min-width: 34px;
    justify-content: center;
    font-weight: 800 !important;
}

.tp-model-response-card {
    padding-bottom: 12px;
}

.tp-model-response-header {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-bottom: 8px;
}

.tp-model-response-body {
    padding: 12px 14px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    color: #0f172a;
    font-size: 13px;
    line-height: 1.55;
    white-space: pre-wrap;
    word-break: break-word;
}

.tp-raw {
    border-top: 1px solid #f1f5f9;
    padding-top: 12px;
}

.tp-raw-header {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    user-select: none;
    color: #64748b;
}

.tp-raw-header:hover {
    color: #0d9488;
}

.tp-raw-body {
    margin-top: 8px;
    padding: 12px 14px;
    background: #0f172a;
    color: #cbd5e1;
    font-family: ui-monospace, "JetBrains Mono", "DejaVu Sans Mono", "Cascadia Code", monospace;
    font-size: 11.5px;
    line-height: 1.55;
    border-radius: 8px;
    max-height: 240px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-word;
}

.tp-actions {
    background: #ffffff;
    border-top: 1px solid #f1f5f9;
    padding: 14px 24px !important;
    gap: 8px;
}

.tp-btn-cancel {
    text-transform: none !important;
    font-weight: 500 !important;
    color: #64748b !important;
    letter-spacing: 0 !important;
    height: 40px !important;
    padding: 0 16px !important;
    border-radius: 8px !important;
}

.tp-btn-run {
    text-transform: none !important;
    font-weight: 600 !important;
    letter-spacing: 0.1px !important;
    height: 40px !important;
    padding: 0 20px !important;
    border-radius: 8px !important;
    background: linear-gradient(135deg, #0d9488 0%, #06b6d4 100%) !important;
    color: #ffffff !important;
    box-shadow: 0 4px 12px rgba(13, 148, 136, 0.35) !important;
    position: relative;
}
.tp-btn-run:hover:not(.v-btn--disabled) {
    box-shadow: 0 6px 18px rgba(13, 148, 136, 0.5) !important;
}
.tp-btn-run.v-btn--disabled {
    background: #e2e8f0 !important;
    color: #94a3b8 !important;
    box-shadow: none !important;
}

/* Running-light wrapper — a thin streak of purple/indigo travels along the button's rectangular edge
   (chase-light look). Achieved with a rotating conic-gradient masked to show only a border-thick ring. */
@property --tp-angle {
    syntax: '<angle>';
    inherits: false;
    initial-value: 0deg;
}

.tp-btn-run-wrap {
    position: relative;
    display: inline-block;
    border-radius: 10px;
    isolation: isolate;
}

/* Keep the teal gradient + text visible while loading — the running-light border
   already signals progress, so we hide the v-btn spinner overlay and the disabled
   gray pill. */
.tp-btn-run-wrap--loading > .tp-btn-run.v-btn--disabled {
    background: linear-gradient(135deg, #0d9488 0%, #06b6d4 100%) !important;
    color: #ffffff !important;
    opacity: 0.85;
    box-shadow: 0 4px 12px rgba(13, 148, 136, 0.3) !important;
}

.tp-btn-run-wrap--loading::before,
.tp-btn-run-wrap--loading::after {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 10px;
    pointer-events: none;
    z-index: 0;
}

/* Crisp streak running along the edge — the bright wedge of the conic-gradient,
   clipped to a 2px ring via mask-composite: exclude. */
.tp-btn-run-wrap--loading::before {
    padding: 2px;
    background: conic-gradient(from var(--tp-angle),
        rgba(139, 92, 246, 0) 0deg,
        rgba(139, 92, 246, 0) 280deg,
        rgba(139, 92, 246, 0.3) 305deg,
        #8B5CF6 335deg,
        #6366F1 355deg,
        rgba(99, 102, 241, 0.3) 25deg,
        rgba(139, 92, 246, 0) 50deg,
        rgba(139, 92, 246, 0) 360deg);
    -webkit-mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    animation: tp-edge-run 1.6s linear infinite;
}

/* Soft halo behind the streak — same conic angle, blurred, gives the trailing glow.
   No mask so the glow is allowed to bloom outward a few px from the edge. */
.tp-btn-run-wrap--loading::after {
    inset: -5px;
    border-radius: 12px;
    background: conic-gradient(from var(--tp-angle),
        rgba(139, 92, 246, 0) 0deg,
        rgba(139, 92, 246, 0) 290deg,
        rgba(139, 92, 246, 0.55) 335deg,
        rgba(99, 102, 241, 0.7) 355deg,
        rgba(139, 92, 246, 0) 30deg,
        rgba(139, 92, 246, 0) 360deg);
    filter: blur(7px);
    opacity: 0.65;
    animation: tp-edge-run 1.6s linear infinite;
}

.tp-btn-run-wrap > .v-btn {
    position: relative;
    z-index: 1;
}

@keyframes tp-edge-run {
    to { --tp-angle: 360deg; }
}

.tp-section-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 4px;
}

.tp-section-row .tp-section-label {
    margin-bottom: 0;
}

.tp-section-hint {
    font-size: 11px;
    color: #94a3b8;
    font-weight: 500;
    letter-spacing: 0.2px;
}

.header-chip {
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 1px !important;
    border-radius: 16px !important;
    padding: 12px 20px !important;
    min-height: 48px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    background: linear-gradient(135deg, var(--v-theme-primary) 0%, var(--v-theme-primary-darken-1) 100%) !important;
    border: 2px solid rgba(255, 255, 255, 0.2) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    position: relative;
    overflow: hidden;
    color: white !important;
}

.header-chip::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
}

.header-chip:hover::before {
    left: 100%;
}

.header-chip:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2) !important;
}

.header-chip .v-icon {
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

/* Color-specific gradients for different node types */
.header-chip.bg-blue {
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
}

.header-chip.bg-green {
    background: linear-gradient(135deg, #10b981 0%, #047857 100%) !important;
}

.header-chip.bg-teal {
    background: linear-gradient(135deg, #14b8a6 0%, #0f766e 100%) !important;
}

.header-chip.bg-orange {
    background: linear-gradient(135deg, #f97316 0%, #ea580c 100%) !important;
}

.header-chip.bg-purple {
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
}

.header-chip.bg-red {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
}

.header-chip.bg-indigo {
    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%) !important;
}

.header-chip.bg-amber {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
}

.header-chip.bg-pink {
    background: linear-gradient(135deg, #ec4899 0%, #db2777 100%) !important;
}

/* Node Type Badge */
.node-type-badge {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
    padding: 8px 0;
}

.node-type-badge .v-chip {
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-radius: 12px;
}

/* Agent Architect inspector cards */
.agent-architect-inspector {
    padding: 26px 16px 32px;
    background: #ffffff;
}

.aa-config-hero {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 26px 24px;
    border-radius: 16px;
    background: linear-gradient(135deg, #f7fbff 0%, #e5f4ff 100%);
    border: 1px solid #cfe6ff;
    margin-bottom: 24px;
}

.aa-agent-hero {
    color: #009688;
}

.aa-tool-hero {
    color: #ffb300;
}

.aa-flow-tool-hero {
    color: #3f51b5;
}

.aa-orchestrator-hero {
    color: #e91e63;
    background: linear-gradient(135deg, #f8fbff 0%, #e5f0ff 100%);
    border-color: #cfe0fb;
}

.aa-hero-title {
    font-size: 20px;
    font-weight: 700;
    line-height: 1.2;
    margin-bottom: 12px;
}

.aa-hero-subtitle {
    color: #4b5563;
    font-size: 14px;
    line-height: 1.35;
}

.aa-field :deep(.v-field) {
    border-radius: 9px !important;
    background: #ffffff !important;
}

.aa-field :deep(.v-field__outline) {
    color: #d0d4dc !important;
}

.aa-field :deep(.v-label) {
    font-weight: 600;
    color: #6b7280;
}

.aa-field-hint {
    color: #777;
    font-size: 12px;
    line-height: 1.2;
    padding-left: 16px;
    letter-spacing: 0.2px;
}

.aa-switch-card {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 10px 12px;
    align-items: center;
    padding: 12px;
    border: 1px solid #e2e2e2;
    border-radius: 12px;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.aa-switch-card-active {
    border-color: rgba(0, 150, 136, 0.35);
    background: #f0fdfa;
}

.aa-switch-main,
.aa-panel-title-row {
    display: flex;
    align-items: center;
    min-width: 0;
}

.aa-switch-text {
    min-width: 0;
}

.aa-switch-icon-svg {
    display: block;
    width: 17px;
    height: 17px;
    flex-shrink: 0;
}

.aa-switch-title,
.aa-panel-title,
.aa-internal-title {
    color: #111827;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.2;
}

.aa-switch-subtitle,
.aa-panel-subtitle,
.aa-internal-subtitle {
    color: #4b5563;
    font-size: 12px;
    line-height: 1.25;
    margin-top: 2px;
}

.aa-switch-help {
    grid-column: 1 / -1;
    color: #795548;
    font-size: 12px;
    line-height: 1.55;
}

.aa-switch-warning {
    border-radius: 4px !important;
    color: #ff7a00 !important;
}

.aa-switch-warning :deep(.v-alert__content) {
    font-size: 12px;
    line-height: 1.55;
}

.aa-flat-panels {
    border-radius: 12px;
}

.aa-flat-panel {
    border-radius: 12px !important;
    border: 1px solid #e5e7eb !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
    overflow: hidden;
}

.aa-flat-panel-title {
    min-height: 64px !important;
    padding: 12px 20px !important;
}

.aa-panel-avatar {
    box-shadow: 0 6px 14px rgba(0, 150, 136, 0.28);
    margin-right: 14px;
}

.aa-flat-panel-content {
    background: #ffffff;
}

.aa-knowledge-card {
    padding: 12px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fbfdff;
}

.aa-knowledge-title {
    font-weight: 700;
    color: #111827;
    font-size: 13px;
    margin-bottom: 8px;
}

.aa-nested-config {
    border: 1px solid #d8f3ef;
    background: #f8fffe;
    border-radius: 12px;
    padding: 14px;
    margin-bottom: 4px;
}

.aa-section-heading {
    color: #1976d2;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
}

.aa-info-alert {
    border-radius: 4px !important;
    color: #1682ff !important;
}

.aa-warning-alert {
    border-radius: 4px !important;
    color: #ff7a00 !important;
}

.aa-internal-flow-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 12px;
    border: 1px solid #222;
    border-radius: 2px;
    background: #ffffff;
}

.aa-internal-status {
    font-weight: 600 !important;
}

.aa-edit-internal-flow-btn {
    border-radius: 4px !important;
    text-transform: uppercase !important;
    letter-spacing: 1.2px !important;
    font-weight: 700 !important;
    box-shadow: 0 2px 5px rgba(63, 81, 181, 0.35) !important;
}

/* Flow Tool internal editor */
.flow-tool-editor-shell {
    position: relative;
    width: 100vw;
    height: 100vh;
    overflow: hidden;
    background: #e8f3ff;
}

.internal-flow-toolbar {
    position: absolute;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 35;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 14px;
    border-radius: 12px;
    color: #ffffff;
    background: linear-gradient(135deg, #4f46e5 0%, #6d5dfc 100%);
    box-shadow: 0 8px 22px rgba(79, 70, 229, 0.35);
}

.internal-flow-toolbar-title {
    display: flex;
    align-items: center;
    font-weight: 700;
    min-width: 220px;
}

.internal-flow-status-chip {
    font-weight: 700 !important;
}

.internal-flow-save-btn {
    color: #1f2937 !important;
    font-weight: 700 !important;
}

.internal-flow-canvas-wrap {
    position: absolute;
    inset: 0;
}

.internal-flow-canvas {
    width: 100%;
    height: 100%;
    background-color: #e8f3ff;
}

.internal-flow-info-banner {
    position: absolute;
    top: 88px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 30;
    display: flex;
    align-items: center;
    gap: 12px;
    width: min(620px, calc(100vw - 420px));
    min-height: 54px;
    padding: 12px 18px;
    border-radius: 4px;
    background: rgba(187, 222, 251, 0.72);
    color: #1e88e5;
    font-size: 13px;
    line-height: 1.45;
}


.internal-flow-node-panel {
    top: 96px !important;
    left: 6px !important;
    height: calc(100vh - 120px) !important;
    max-height: calc(100vh - 120px) !important;
    z-index: 40 !important;
}

.internal-flow-node-panel :deep(.panel-card) {
    height: 100%;
    overflow: hidden;
}

.internal-flow-node-panel :deep(.panel-content) {
    max-height: calc(100vh - 235px);
    overflow-y: auto;
}

.internal-flow-node-panel :deep(.panel-header) {
    background: linear-gradient(135deg, #6d5dfc 0%, #5e50d8 100%);
}

/* Modern Input Styles */
.modern-input :deep(.v-field) {
    border-radius: 14px !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
}

.modern-input :deep(.v-field--focused) {
    transform: translateY(-1px);
}

.modern-input :deep(.v-field__outline) {
    --v-field-border-opacity: 0.15;
}

.modern-input :deep(.v-field--focused .v-field__outline) {
    --v-field-border-opacity: 1;
    color: rgb(102, 126, 234) !important;
    --v-field-border-width: 2px;
}

.modern-input :deep(.v-label) {
    font-weight: 500;
}
/* Modern Switch */
.modern-switch :deep(.v-switch__track) {
    border-radius: 20px !important;
    background: #e5e7eb !important;
    transition: all 0.3s ease !important;
}

.modern-switch :deep(.v-switch--inset .v-switch__track) {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

.modern-switch :deep(.v-switch__thumb) {
    border-radius: 50% !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.modern-switch :deep(.v-switch__thumb):hover {
    transform: scale(1.1);
}

/* Slider Styles */
.slider-container {
    padding: 16px 0;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.slider-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 12px;
    display: block;
}

.modern-slider :deep(.v-slider-track__fill) {
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%) !important;
}

.modern-slider :deep(.v-slider-thumb__surface) {
    background: white !important;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
    border: 3px solid #667eea !important;
}

.modern-slider :deep(.v-slider-thumb__surface):hover {
    transform: scale(1.2);
}

/* Intent Cards */
.intents-section {
    padding-right: 4px;
}

.intent-card-inner {
    border-radius: 16px !important;
    border: 1px solid rgba(0, 0, 0, 0.06) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
    overflow: hidden;
}

.intent-card-inner:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08) !important;
    transform: translateY(-2px);
}

.intent-card-inner :deep(.v-card-text) {
    background: transparent !important;
}

/* Scrollbar Styles */
.inspector-content::-webkit-scrollbar,
.intents-section::-webkit-scrollbar {
    width: 6px;
}

.inspector-content::-webkit-scrollbar-track,
.intents-section::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
}

.inspector-content::-webkit-scrollbar-thumb,
.intents-section::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
}

.inspector-content::-webkit-scrollbar-thumb:hover,
.intents-section::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
}

/* Animation for smooth transitions */
@keyframes slideInRight {
    from {
        transform: translateX(100%) scale(0.9);
        opacity: 0;
    }

    to {
        transform: translateX(0) scale(1);
        opacity: 1;
    }
}

/* Add button animations */
.add-btn {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.add-btn:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
}

/* Improved scrollbar */
.inspector-content::-webkit-scrollbar,
.intents-section::-webkit-scrollbar {
    width: 8px;
}

.inspector-content::-webkit-scrollbar-track,
.intents-section::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.03);
    border-radius: 4px;
}

.inspector-content::-webkit-scrollbar-thumb,
.intents-section::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 4px;
    border: 2px solid transparent;
    background-clip: content-box;
}

.inspector-content::-webkit-scrollbar-thumb:hover,
.intents-section::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
    background-clip: content-box;
}

/* Mobile Responsive for Inspector */
@media (max-width: 768px) {
    .floating-inspector {
        width: calc(100vw - 20px);
        right: 10px;
        left: 10px;
        width: auto;
    }

    .floating-inspector.inspector-expanded {
        width: calc(100vw - 20px);
        left: 10px;
        right: 10px;
        top: 10px;
        bottom: 10px;
    }

    .inspector-header {
        padding: 20px;
    }

    .inspector-content {
        padding: 20px;
    }
}

/* Intent expansion panel styling */
.intent-expansion {
    border-radius: 8px !important;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
}

.intent-expansion .v-expansion-panel {
    border-radius: 8px !important;
}

.intent-expansion .v-expansion-panel-title {
    padding: 16px 20px !important;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
    min-height: 56px !important;
}

.intent-expansion .v-expansion-panel-title:hover {
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%) !important;
}

.intent-expansion .v-expansion-panel-text {
    background: #ffffff !important;
}

.intent-content {
    background: #ffffff;
    border-radius: 0 0 8px 8px;
}

/* Smart Intent card styling */
.smart-intent-card {
    background: #fcfaf8 !important;
    border: 1px solid rgba(37, 99, 235, 0.1) !important;
    border-radius: 12px !important;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.08) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    overflow: hidden !important;
}

.smart-intent-card:hover {
    border-color: rgba(37, 99, 235, 0.2) !important;
    box-shadow: 0 8px 24px rgba(37, 99, 235, 0.12) !important;
    transform: translateY(-2px) !important;
}

.smart-intent-card .v-card-title {
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
    color: white !important;
    padding: 16px 20px !important;
    font-size: 1.1rem !important;
    font-weight: 600 !important;
    letter-spacing: 0.5px !important;
}

.smart-intent-card .v-card-text {
    padding: 20px !important;
    background: #ffffff !important;
}

/* Smart Intent expansion panel styling */
.smart-intent-expansion {
    padding: 0 !important;
    border-radius: 8px !important;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
}

.smart-intent-expansion .v-expansion-panel {
    border-radius: 8px !important;
}

.smart-intent-expansion .v-expansion-panel-title {
    padding: 16px 20px !important;
    background: #e8f3ff !important;
    border-bottom: 1px solid #4e46e528 !important;
    min-height: 56px !important;
}

.smart-intent-expansion .v-expansion-panel-title:hover {
    background: #c7e1ff !important;
}

.smart-intent-expansion .v-expansion-panel-text {
    background: #ffffff !important;
}
.smart-intent-expansion :deep(.v-expansion-panel-text__wrapper) {
    padding: 0 5px 0 5px !important;
}

.smart-intent-content {
    background: #ffffff;
    border-radius: 0 0 8px 8px;
}

/* Fallback AI Config expansion panel styling */
.fallback-ai-config-expansion :deep(.v-expansion-panel-text .v-expansion-panel-text__wrapper) {
    padding: 0 5px 0 5px !important;
}
.fallback-ai-config-expansion {
    padding: 0 !important;
    border-radius: 8px !important;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(255, 152, 0, 0.12) !important;
    margin-top: 8px !important;
}

.fallback-ai-config-expansion .v-expansion-panel {
    border-radius: 8px !important;
    border: 1px solid rgba(255, 152, 0, 0.2) !important;
}

.fallback-ai-config-title {
    padding: 16px 20px !important;
    background: linear-gradient(135deg, #fff3e0, #ffe0b2) !important;
    border-bottom: 1px solid rgba(255, 152, 0, 0.3) !important;
    min-height: 56px !important;
    transition: all 0.3s ease !important;
}

.fallback-ai-config-title:hover {
    background: linear-gradient(135deg, #ffe0b2, #ffcc80) !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 152, 0, 0.15) !important;
}

.fallback-ai-config-expansion .v-expansion-panel-text {
    background: #ffffff !important;
    border-radius: 0 0 8px 8px !important;
}

.fallback-ai-config-expansion :deep(.v-expansion-panel-text__wrapper) {
    padding: 16px 20px !important;
}

/* AI Main Config expansion panel styling */
.ai-main-config-expansion {
    padding: 0 !important;
    border-radius: 8px !important;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(33, 150, 243, 0.12) !important;
    margin-top: 8px !important;
}

.ai-main-config-expansion .v-expansion-panel {
    border-radius: 8px !important;
    border: 1px solid rgba(33, 150, 243, 0.2) !important;
}
.ai-main-config-expansion :deep(.v-expansion-panel .v-expansion-panel-text .v-expansion-panel-text__wrapper) {
    padding: 0 5px 0 5px !important;
}

.ai-config-title {
    padding: 16px 20px !important;
    background: linear-gradient(135deg, #e3f2fd, #bbdefb) !important;
    border-bottom: 1px solid rgba(33, 150, 243, 0.3) !important;
    min-height: 56px !important;
    transition: all 0.3s ease !important;
}

.ai-config-title:hover {
    background: linear-gradient(135deg, #bbdefb, #90caf9) !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.15) !important;
}

.ai-main-config-expansion .v-expansion-panel-text {
    background: #ffffff !important;
    border-radius: 0 0 8px 8px !important;
}

.ai-main-config-expansion :deep(.v-expansion-panel-text__wrapper) {
    padding: 16px 20px !important;
}

/* Switch output expansion panel styling */
.switch-output-expansion {
    border-radius: 8px !important;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
}

.switch-output-expansion .v-expansion-panel {
    border-radius: 8px !important;
}

.switch-output-expansion .v-expansion-panel-title {
    padding: 16px 20px !important;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
    min-height: 56px !important;
}

.switch-output-expansion .v-expansion-panel-title:hover {
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%) !important;
}

.switch-output-expansion .v-expansion-panel-text {
    background: #ffffff !important;
}

.switch-output-content {
    background: #ffffff;
    border-radius: 0 0 8px 8px;
}

.switch-outputs-section {
    max-height: 500px;
    padding-right: 4px;
}

.switch-output-card {
    border-radius: 16px !important;
    border: 1px solid rgba(0, 0, 0, 0.06) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
    overflow: hidden;
}

.switch-output-card:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08) !important;
    transform: translateY(-2px);
}

.switch-output-card .v-card-text {
    background: transparent !important;
}

/* Selection box styling */
:deep(.vue-flow__selection) {
    background: rgba(102, 126, 234, 0.08);
    border: 1.5px solid #667eea;
    border-radius: 4px;
}

:deep(.vue-flow__nodesselection-rect) {
    background: transparent;
    border: none;
    box-shadow: none;
}

/* Multi-Selection Indicator */
.floating-multi-selection {
    position: absolute;
    top: 30px;
    right: 20px;
    z-index: 1000;
    max-width: 280px;
}

.multi-selection-card {
    border-radius: 12px !important;
    backdrop-filter: blur(10px);
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.multi-selection-card .v-card-text {
    background: transparent;
}

/* Condition Configuration Styles */
.condition-builder {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid rgba(0, 0, 0, 0.06);
}

.row-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.condition-builder .modern-input {
    background: white;
    border-radius: 8px;
}

.condition-builder .modern-input :deep(.v-field) {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.json-condition-operator-header {
    min-height: 38px !important;
    padding-inline: 20px !important;
    pointer-events: none;
    opacity: 1 !important;
}

.json-condition-operator-header :deep(.v-list-item-title) {
    color: #777777;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0;
}

:global(.json-condition-operator-menu .v-list-item:not(.json-condition-operator-header)) {
    min-height: 48px;
}

.json-condition-form-title,
.json-condition-section-title {
    color: #1565c0;
    font-size: 0.95rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.json-condition-section-header {
    justify-content: space-between;
    margin-bottom: 12px;
}

.json-condition-add-rule-btn {
    min-width: auto !important;
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
    color: #00897b !important;
    font-size: 0.875rem !important;
    font-weight: 700 !important;
    letter-spacing: 0.03em;
    text-transform: uppercase !important;
    box-shadow: none !important;
}

.json-condition-add-rule-btn:hover {
    background: transparent !important;
    color: #00796b !important;
}

.json-condition-input :deep(.v-field) {
    min-height: 20px;
    border-radius: 16px !important;
    background: #ffffff !important;
    box-shadow: none !important;
}

.json-condition-input :deep(.v-field__outline) {
    --v-field-border-opacity: 0.2;
}

.json-condition-input :deep(.v-field--focused .v-field__outline) {
    color: #4f6dff !important;
}

.json-condition-input :deep(.v-field__field) {
    align-items: stretch;
}

.json-condition-input :deep(.v-field__input) {
    min-height: 50px;
    padding-top: 16px;
    padding-bottom: 8px;
    line-height: 1.3;
    align-items: center;
}

.json-condition-input :deep(.v-field__input),
.json-condition-input :deep(.v-select__selection-text),
.json-condition-input :deep(input) {
    font-size: 0.95rem;
    line-height: 1.3;
}

.json-condition-rule-panels {
    background: transparent;
}

.json-condition-rule-panel {
    margin-bottom: 14px;
    border-radius: 12px !important;
    border: 1px solid rgba(15, 23, 42, 0.08) !important;
    background: #ffffff !important;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06) !important;
    overflow: hidden;
}

.json-condition-rule-title {
    min-height: 68px !important;
    padding: 18px 22px !important;
    background: #ffffff !important;
}

.json-condition-rule-title:hover {
    background: #fafafa !important;
}

.json-condition-rule-title :deep(.v-expansion-panel-title__icon) {
    color: #1f2937;
}

.json-condition-rule-heading {
    font-size: 1.05rem;
    font-weight: 400;
    color: #111827;
}

.json-condition-rule-panel :deep(.v-expansion-panel-text__wrapper) {
    padding: 0 22px 20px !important;
}

.json-condition-rule-body {
    padding-top: 4px;
}

.json-condition-rule-actions {
    display: flex;
    justify-content: flex-end;
}

.json-condition-remove-rule-btn {
    min-width: auto !important;
    padding-inline: 0 !important;
    text-transform: none !important;
    font-weight: 600 !important;
}

:global(.json-condition-match-mode-menu .v-list-item) {
    min-height: 56px;
}

:global(.json-condition-match-mode-menu .v-list-item--active) {
    background: #ececec !important;
}

.json-condition-output-section {
    background: #ffffff;
    border-radius: 16px;
    padding: 18px;
    border: 1px solid rgba(139, 92, 246, 0.12);
    box-shadow: 0 6px 18px rgba(139, 92, 246, 0.06);
}

.json-condition-output-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
}

.output-labels-section {
    background: linear-gradient(135deg, #fef7ff 0%, #f3e8ff 100%);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid rgba(168, 85, 247, 0.1);
}

.output-labels-section .section-title {
    color: #7c3aed;
    font-weight: 600;
}

.gap-2 {
    gap: 8px;
}

.d-flex.gap-2 {
    display: flex;
}

@media (max-width: 768px) {
    .json-condition-output-grid {
        grid-template-columns: 1fr;
    }
}

/* Condition type specific styling */
.condition-builder.text-contains {
    background: linear-gradient(135deg, #fef3e2 0%, #fed7aa 100%);
    border-color: rgba(251, 146, 60, 0.2);
}

.condition-builder.number-range {
    background: linear-gradient(135deg, #ecfccb 0%, #d9f99d 100%);
    border-color: rgba(132, 204, 22, 0.2);
}

.condition-builder.boolean-check {
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    border-color: rgba(59, 130, 246, 0.2);
}

.condition-builder.custom-expression {
    background: linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%);
    border-color: rgba(236, 72, 153, 0.2);
}

/* Delay Configuration Styles */
.delay-config-section {
    background: linear-gradient(135deg, #fef3f4 0%, #fde2e4 100%);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid rgba(244, 63, 94, 0.1);
}

.delay-config-section .modern-input {
    background: white;
    border-radius: 8px;
}

.delay-config-section .modern-input :deep(.v-field) {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.duration-preview {
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    border-radius: 12px;
    padding: 12px;
    border: 1px solid rgba(14, 165, 233, 0.1);
}

.duration-preview .v-alert {
    background: transparent !important;
}

/* Delay type specific styling */
.delay-config-section.fixed-delay {
    background: linear-gradient(135deg, #ecfccb 0%, #d9f99d 100%);
    border-color: rgba(132, 204, 22, 0.2);
}

.delay-config-section.random-delay {
    background: linear-gradient(135deg, #fef3e2 0%, #fed7aa 100%);
    border-color: rgba(251, 146, 60, 0.2);
}

.delay-config-section.variable-delay {
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    border-color: rgba(59, 130, 246, 0.2);
}

/* Switch Condition Builder Styles */
.condition-builder-section {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid rgba(0, 0, 0, 0.06);
}

.condition-section-title {
    color: #475569;
    font-weight: 600;
    font-size: 0.875rem;
    margin: 0;
}

.condition-mode-toggle {
    border-radius: 8px !important;
}

.condition-mode-toggle .v-btn {
    border-radius: 6px !important;
    text-transform: none !important;
    font-weight: 500 !important;
}

.simple-condition-builder,
.advanced-condition-builder {
    background: white;
    border-radius: 10px;
    padding: 12px;
    border: 1px solid rgba(0, 0, 0, 0.04);
    margin-top: 12px;
}

.condition-fields {
    background: linear-gradient(135deg, #fefcfb 0%, #fef7f0 100%);
    border-radius: 8px;
    padding: 12px;
    border: 1px solid rgba(251, 146, 60, 0.1);
    margin-top: 8px;
}

.condition-preview {
    background: rgba(59, 130, 246, 0.1) !important;
    color: #1e40af !important;
    padding: 4px 8px !important;
    border-radius: 4px !important;
    font-family: 'Fira Code', monospace !important;
    font-size: 0.75rem !important;
    word-break: break-all;
}

.condition-hints {
    background: linear-gradient(135deg, #fefcfb 0%, #fef7f0 100%);
    border-radius: 8px;
    padding: 8px;
    border: 1px solid rgba(251, 146, 60, 0.1);
}

.hint-chip {
    cursor: pointer !important;
    transition: all 0.2s ease !important;
    font-family: 'Fira Code', monospace !important;
    font-size: 0.75rem !important;
}

.hint-chip:hover {
    background: rgba(59, 130, 246, 0.1) !important;
    color: #1e40af !important;
}

.gap-2 {
    gap: 8px;
}

/* Switch output specific styling */
.switch-output-expansion .condition-builder-section {
    margin-bottom: 0;
}

.switch-output-expansion .condition-fields .modern-input {
    background: white;
    border-radius: 6px;
}

.switch-output-expansion .condition-fields .modern-input :deep(.v-field) {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* Toolbar Styles */
.flow-toolbar {
    position: absolute;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1000;
    width: 600px;
    max-width: 80%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: column;
    padding: 16px 24px;
    backdrop-filter: blur(10px);
    background: rgba(255, 255, 255, 0.3);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-radius: 16px;
}

.flow-toolbar.flow-toolbar-debug {
    width: 450px;
}

.toolbar-left {
    display: flex;
    align-items: center;
    gap: 20px;
}

.flow-status-chip {
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 1px !important;
    border-radius: 12px !important;
    padding: 8px 16px !important;
    min-height: 40px !important;
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.2) !important;
}

.flow-info {
    display: flex;
    flex-direction: row;
    gap: 4px;
}

.confirmation-content {
    padding: 8px 0;
}

.flow-name-display {
    display: flex;
    align-items: center;
    padding: 20px;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 12px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    margin-bottom: 20px;
}

.flow-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin-top: 4px;
}

.stats-summary {
    display: flex;
    justify-content: space-around;
    padding: 16px;
    background: white;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    margin-bottom: 16px;
}

.stat-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
}

.stat-item .v-icon {
    opacity: 0.8;
}

.flow-name {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1a202c;
    margin: 0;
}

.flow-status {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.75rem;
    color: #f59e0b;
    font-weight: 500;
}

.flow-status.saved {
    color: green;
}

.toolbar-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-top: 3px;
    padding: 0px 12px;
}

.btn-text {
    margin-left: 4px;
    font-size: 0.8rem;
}

.lock-btn {
    border-radius: 10px !important;
    text-transform: none !important;
    font-weight: 500 !important;
    transition: all 0.2s ease !important;
}

.more-actions-btn {
    border-radius: 50% !important;
    background: transparent !important;
}

/* Dialog Styles */
.save-dialog,
.load-dialog,
.load-model-dialog {
    border-radius: 20px !important;
    overflow: hidden;
}

.dialog-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 24px !important;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.dialog-header .header-content {
    flex: 1;
}

.dialog-header .text-h5 {
    font-size: 1.25rem !important;
}

.header-content {
    display: flex;
    align-items: center;
}

.dialog-content {
    padding: 28px !important;
    background: #fafafa;
}

.dialog-actions {
    padding: 20px 28px !important;
    background: white;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
}

/* Header Actions */
.header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.load-model-btn {
    transition: all 0.2s ease;
}

.load-model-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Load Model Dialog Specific */
.load-model-content {
    min-height: 200px;
}

.model-selection-section {
    margin-bottom: 20px;
}

.model-list-item {
    padding: 12px 16px;
    border-radius: 8px;
    margin: 4px 0;
}

.model-list-item:hover {
    background-color: #f8fafc;
}

.config-preview-section {
    margin-top: 20px;
}

.config-preview-card {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
}

.preview-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
}

.preview-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f3f4f6;
}

.preview-item:last-child {
    border-bottom: none;
}

.preview-label {
    font-weight: 500;
    color: #6b7280;
    font-size: 0.875rem;
}

.preview-value {
    font-weight: 600;
    color: #374151;
    font-size: 0.875rem;
    text-align: right;
}

.model-option-subtitle {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    color: #94a3b8;
    font-size: 12px;
    font-weight: 500;
}

.model-option-meta {
    display: inline-flex;
    align-items: center;
    min-width: 0;
    max-width: 45%;
}

.model-option-meta-icon {
    flex: 0 0 auto;
    color: #64748b;
    margin-right: 4px;
}

.model-option-meta-value {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.model-option-meta-separator {
    flex: 0 0 auto;
    color: #cbd5e1;
}

.model-selection-title {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.model-provider-details {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
}

.model-provider-detail {
    display: flex;
    align-items: center;
    min-width: 0;
    padding: 10px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #f8fafc;
}

.model-provider-label {
    color: #64748b;
    font-size: 12px;
    font-weight: 500;
    margin-right: 8px;
    white-space: nowrap;
}

.model-provider-value {
    color: #1f2937;
    font-size: 13px;
    font-weight: 600;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

@media (max-width: 600px) {
    .model-provider-details {
        grid-template-columns: 1fr;
    }
}

/* Form Sections */
.form-section {
    background: white;
    padding: 20px;
    margin-bottom: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.section-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
}

/* Stats Grid */
.stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
}

.stat-card {
    border-radius: 8px !important;
}

.stat-number {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1;
}

.stat-label {
    font-size: 0.75rem;
    opacity: 0.8;
    margin-top: 4px;
}

/* Flow List */
.search-section {
    padding: 0 4px;
}

.flow-list {
    max-height: 400px;
    overflow-y: auto;
    padding: 4px;
}

.flow-item {
    margin-bottom: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 12px !important;
}

.flow-item:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
    transform: translateY(-1px);
}

.flow-item.selected {
    border-color: #667eea !important;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2) !important;
}

.flow-icon {
    background: rgba(102, 126, 234, 0.1);
    border-radius: 8px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.flow-title {
    font-size: 1rem;
    font-weight: 600;
    color: #1a202c;
}

.flow-meta {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 2px;
}

.flow-description {
    font-size: 0.85rem;
    color: #4b5563;
    line-height: 1.4;
    margin-top: 8px;
}

.flow-stats {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.flow-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.no-flows {
    text-align: center;
    padding: 40px 20px;
}

/* Tags Section */
.tags-section {
    margin-top: 8px;
}

/* Alert Styles */
.alert-title {
    font-weight: 600;
    margin-bottom: 4px;
}

/* Modern Input Overrides */
.modern-input :deep(.v-field) {
    border-radius: 10px !important;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
}

/* Responsive Design */
@media (max-width: 768px) {
    .flow-toolbar {
        flex-direction: column;
        gap: 12px;
        padding: 12px 16px;
    }

    .toolbar-left {
        width: 100%;
        justify-content: center;
    }

    .toolbar-actions {
        width: 100%;
        justify-content: center;
    }

    .stats-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .btn-text {
        display: none;
    }
}

@media (max-width: 480px) {
    .stats-grid {
        grid-template-columns: 1fr;
    }

    .flow-stats {
        flex-direction: row;
        gap: 8px;
    }
}

/* Scrollbar for flow list */
.flow-list::-webkit-scrollbar {
    width: 6px;
}

.flow-list::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
}

.flow-list::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.3);
    border-radius: 3px;
}

.flow-list::-webkit-scrollbar-thumb:hover {
    background: rgba(102, 126, 234, 0.5);
}

.floating-back-btn {
    position: absolute;
    top: 60px;
    left: 20px;
    z-index: 2000;
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08) !important;
    border-radius: 12px !important;
    border: 1px solid rgba(255, 255, 255, 0.2);
    height: 44px !important;
    padding: 0 16px !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    overflow: hidden;
}

.floating-back-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.1), transparent);
    transition: left 0.5s ease;
}

.floating-back-btn:hover::before {
    left: 100%;
}

.floating-back-btn:hover {
    background: rgba(255, 255, 255, 1) !important;
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.floating-back-btn:active {
    transform: translateY(-1px) scale(0.98);
}

.floating-back-btn .v-icon {
    margin-right: 8px;
    transition: transform 0.2s ease;
}

.floating-back-btn:hover .v-icon {
    transform: translateX(-2px);
}

.back-text {
    font-size: 0.875rem;
    font-weight: 600;
    color: #667eea;
    letter-spacing: 0.3px;
    transition: color 0.2s ease;
}

.floating-back-btn:hover .back-text {
    color: #5a67d8;
}

.view-mode-btn {
    background-color: rgb(255, 224, 121);
    height: 40px;
    color: rgb(186, 109, 9);
    border-radius: 8px;
}

/* API Trigger Styles */
.api-section {
    background: rgba(248, 249, 250, 0.5);
    border-radius: 12px;
    padding: 20px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    margin-bottom: 16px;
}

.section-header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
}

.section-title {
    font-weight: 600;
    font-size: 1rem;
    color: #1976d2;
    margin-bottom: 12px;
}

.header-row,
.validation-row {
    background: white;
    border-radius: 8px;
    padding: 12px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    margin-bottom: 8px;
}

.headers-section,
.validation-section {
    background: rgba(248, 249, 250, 0.3);
    border-radius: 8px;
    padding: 12px;
    border: 1px solid rgba(0, 0, 0, 0.04);
}

.add-btn {
    margin-top: 8px;
    border: 1px dashed #1976d2 !important;
    background: rgba(25, 118, 210, 0.04) !important;
    color: #1976d2 !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;
}

.add-btn:hover {
    background: rgba(25, 118, 210, 0.08) !important;
    border-color: #1976d2 !important;
}

/* Responsive Design for API Trigger */
@media (max-width: 768px) {
    .api-section {
        padding: 16px;
    }

    .header-row,
    .validation-row {
        padding: 8px;
    }

    .section-title {
        font-size: 0.9rem;
    }

    .headers-section,
    .validation-section {
        padding: 8px;
    }
}

/* Enhanced JSON Editor Container */
.json-editor-container {
    background: white;
    border-radius: 12px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

/* Variables Panel Header */
.json-variables-header {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding: 12px 16px;
}

.variables-header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.variables-title {
    display: flex;
    align-items: center;
    font-weight: 600;
    color: #374151;
    flex: 1;
    justify-content: space-between;
}

.category-filter-btn {
    height: 28px !important;
    min-width: auto !important;
    padding: 0 8px !important;
    border-radius: 6px !important;
    font-size: 0.75rem !important;
    color: #6b7280 !important;
    transition: all 0.2s ease !important;
}

.category-filter-btn.filter-active {
    background: rgba(79, 70, 229, 0.1) !important;
    color: #4f46e5 !important;
}

.category-filter-btn:hover {
    background: rgba(0, 0, 0, 0.04) !important;
}

.expand-btn {
    height: 28px !important;
    width: 28px !important;
    border-radius: 6px !important;
    color: #6b7280 !important;
}

.expand-btn:hover {
    background: rgba(0, 0, 0, 0.04) !important;
    color: #374151 !important;
}

/* Category Menu */
.category-menu {
    border-radius: 8px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    border: 1px solid rgba(0, 0, 0, 0.06) !important;
    min-width: 120px;
}

.category-menu .v-list-item {
    min-height: 36px !important;
    padding: 0 12px !important;
    font-size: 0.875rem !important;
}

.category-menu .v-list-item:hover {
    background: rgba(79, 70, 229, 0.04) !important;
}

.category-menu .v-list-item--active {
    background: rgba(79, 70, 229, 0.1) !important;
    color: #4f46e5 !important;
}

/* Variables Content */
.json-variables-content {
    padding: 16px;
    background: #fafbfc;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

/* Compact Grid Layout */
.variables-quick-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(130px, 130px));
    gap: 8px;
    max-height: 160px;
    overflow-y: auto;
    padding: 2px;
}

.variable-chip-compact {
    height: 32px !important;
    padding: 0 8px !important;
    border-radius: 8px !important;
    cursor: grab !important;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    font-size: 0.75rem !important;
    border: 1px solid rgba(0, 0, 0, 0.06) !important;
    user-select: none;
}

.variable-chip-compact:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    border-color: rgba(0, 0, 0, 0.1) !important;
}

.variable-chip-compact:active {
    cursor: grabbing !important;
    transform: scale(0.98) !important;
}

.variable-label {
    flex: 1;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0 4px;
}

.drag-handle {
    opacity: 0.4;
    transition: opacity 0.2s ease;
    position: absolute;
    right: 12px;
}

.variable-chip-compact:hover .drag-handle {
    opacity: 0.8;
}

.variable-key {
    font-size: 0.75rem !important;
    color: #6b7280 !important;
    font-family: 'Fira Code', monospace !important;
}

.variable-actions {
    display: flex;
    align-items: center;
    gap: 4px;
}

.insert-btn {
    opacity: 0;
    transition: opacity 0.2s ease;
}

.variable-list-item:hover .insert-btn {
    opacity: 1;
}

/* JSON Textarea Container */
.json-textarea-container {
    position: relative;
}

.modern-json-editor {
    border-radius: 0 !important;
    background: white !important;
}

.modern-json-editor :deep(.v-field) {
    border-radius: 0 !important;
    font-family: 'Fira Code', 'Fira Mono', Inconsolata, 'DejaVu Sans Mono', Courier, monospace !important;
    font-size: 13px !important;
    line-height: 1.5 !important;
    border: none !important;
    box-shadow: none !important;
}

.modern-json-editor :deep(.v-field__outline) {
    display: none !important;
}

.modern-json-editor :deep(textarea) {
    border: 2px solid transparent !important;
    transition: all 0.2s ease !important;
    background: white !important;
    min-height: 120px !important;
}

.modern-json-editor :deep(textarea:focus) {
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1) !important;
}

.modern-json-editor :deep(textarea.drag-over) {
    border-color: #10b981 !important;
    background-color: rgba(16, 185, 129, 0.05) !important;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1) !important;
}

.modern-json-editor :deep(textarea:hover) {
    cursor: text;
}

.modern-json-editor :deep(textarea.drag-over) {
    cursor: copy !important;
}

/* Insert Helper */
.insert-helper {
    position: absolute;
    bottom: 8px;
    right: 12px;
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    padding: 4px 8px;
    border-radius: 6px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    pointer-events: none;
}

/* Footer variant: sits below the editor instead of overlaying it */
.insert-helper-footer {
    display: flex;
    align-items: center;
    margin-top: 6px;
    padding: 4px 2px;
    color: #6b7280;
}

.helper-text {
    font-size: 0.7rem;
    color: #6b7280;
    font-weight: 500;
}

/* Variables toggle chip — acts as expand/collapse button */
.variables-toggle-chip {
    cursor: pointer;
    transition: background-color 0.15s ease, transform 0.15s ease;
}

.variables-toggle-chip:hover {
    transform: translateY(-1px);
}

.variables-toggle-chip.is-open {
    background: rgba(79, 70, 229, 0.18) !important;
}

/* Beautify button in JSON header */
.beautify-btn {
    text-transform: none;
    letter-spacing: 0;
    color: #4f46e5;
    min-width: 0;
    padding: 0 8px;
}

.beautify-btn:hover {
    background: rgba(79, 70, 229, 0.08);
}

/* Time Field Container */
.time-field-container {
    max-width: 300px;
}

.time-unit {
    font-size: 0.875rem;
    font-weight: 500;
}

.info-icon {
    opacity: 0.7;
    transition: opacity 0.2s ease;
}

.info-icon:hover {
    opacity: 1;
}

/* Animation for smooth transitions */
.v-enter-active,
.v-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

/* Scrollbar Styling */
.variables-quick-grid::-webkit-scrollbar {
    width: 4px;
}

.variables-quick-grid::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.04);
    border-radius: 2px;
}

.variables-quick-grid::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
}

.variables-quick-grid::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
}

/* New CSS classes to replace inline styles */
.flow-main-container {
    height: 100%;
    width: 100%;
    position: relative;
}

.flow-container-fluid {
    height: 100%;
    width: 100%;
    padding: 10px;
}

.card2 {
    margin-top: 1rem;
    padding: 0;
}

.flow-name-input {
    width: 300px;
}

.flow-actions {
    display: flex;
    gap: 6px;
    align-items: center;
}

/* Undo/Redo Container */
.undo-redo-container {
    position: absolute;
    top: 60px;
    left: 130px;
    display: flex;
    align-items: center;
    gap: 4px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 10px;
    padding: 4px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    z-index: 1201;
    backdrop-filter: blur(10px);
}

.undo-btn, .redo-btn {
    height: 32px !important;
    width: 32px !important;
    min-width: 32px !important;
    border-radius: 8px !important;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.undo-btn:hover:not(:disabled), .redo-btn:hover:not(:disabled) {
    background: rgba(25, 118, 210, 0.08) !important;
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.15) !important;
}

.undo-btn:disabled, .redo-btn:disabled {
    opacity: 0.4 !important;
    cursor: not-allowed !important;
}

.undo-btn:active:not(:disabled), .redo-btn:active:not(:disabled) {
    transform: scale(0.95);
}

.undo-btn .v-icon, .redo-btn .v-icon {
    transition: all 0.2s ease;
}

.undo-btn:hover:not(:disabled) .v-icon, .redo-btn:hover:not(:disabled) .v-icon {
    color: #1976d2 !important;
}

.menu-list {
    border-radius: 15px;
    box-shadow: 3px 4px 10px rgba(0, 0, 0, 0.2);
}

.menu-divider {
    border: none;
    border-bottom: 1px solid #d5d5d5;
    margin: 6px 8px;
}

.save-btn {
    height: 40px;
    width: 100px;
    border-radius: 12px;
}

.debug-toggle-btn {
    height: 40px;
    width: 60px;
    color: #2771c4 !important;
    background: #fdfefe !important;
    border-radius: 12px !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}
/* Debug mode active — pulsing glow */
.debug-toggle-btn.bg-warning {
    color: #ffffff !important;
    background: #fb8c00 !important;
    animation: debug-pulse 2s ease-in-out infinite;
    box-shadow: 0 0 12px rgba(255, 152, 0, 0.5), 0 0 24px rgba(255, 152, 0, 0.2) !important;
}
@keyframes debug-pulse {
    0%, 100% { box-shadow: 0 0 12px rgba(255, 152, 0, 0.5), 0 0 24px rgba(255, 152, 0, 0.2); }
    50% { box-shadow: 0 0 20px rgba(255, 152, 0, 0.7), 0 0 36px rgba(255, 152, 0, 0.35); }
}

.node-panel {
    max-height: 100%;
}

/* Animation for smooth transitions */
.v-enter-active,
.v-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

/* Priority Number Styling */
.priority-number {
    font-size: 0.8rem;
    color: #7018ff;
    font-weight: 500;
    background: rgba(219, 176, 255, 0.349);
    padding: 2px 6px;
    border-radius: 4px;
    min-width: 20px;
    text-align: center;
}

/* Success Dialog Styles */
.success-dialog {
    border-radius: 20px !important;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
}

.success-dialog-header {
    background: linear-gradient(135deg, #10b943 0%, #047834 100%);
    color: white;
    padding: 24px !important;
    text-align: center;
    border-bottom: none;
}

.success-header-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.success-icon {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    padding: 12px;
    animation: successPulse 2s infinite;
}

@keyframes successPulse {
    0% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
    }

    50% {
        transform: scale(1.05);
        box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
    }

    100% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
    }
}

.success-title {
    font-size: 1.5rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
}

.success-dialog-content {
    padding: 32px 24px !important;
    text-align: center;
    background: #fafafa;
}

.success-message {
    font-size: 1.1rem;
    font-weight: 600;
    color: #047857;
    margin-bottom: 24px;
    line-height: 1.5;
}

.success-details {
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: white;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid rgba(16, 185, 129, 0.1);
}

.success-detail-item {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    color: #374151;
    font-weight: 500;
}

.success-detail-item .v-icon {
    color: #10b981;
}

.success-dialog-actions {
    padding: 20px 24px !important;
    background: white;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.success-ok-btn {
    height: 44px !important;
    padding: 0 24px !important;
    border-radius: 12px !important;
    font-weight: 600 !important;
    text-transform: none !important;
    background: linear-gradient(135deg, #10b943 0%, #047834 100%);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.success-ok-btn:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4) !important;
}

.success-ok-btn:active {
    transform: translateY(0) !important;
}

/* Animation for dialog entrance */
.v-dialog-transition-enter-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-dialog-transition-enter-from {
    transform: scale(0.8);
    opacity: 0;
}

/* Mobile responsive */
@media (max-width: 480px) {
    .success-dialog {
        margin: 16px;
    }

    .success-dialog-header {
        padding: 20px !important;
    }

    .success-dialog-content {
        padding: 24px 20px !important;
    }

    .success-title {
        font-size: 1.25rem;
    }

    .success-message {
        font-size: 1rem;
    }

    .success-details {
        padding: 12px;
    }

    .success-detail-item {
        font-size: 0.8rem;
    }
}

/* Confirmation Dialog Styles */
.confirmation-dialog {
    border-radius: 16px !important;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2) !important;
}

.confirmation-dialog .dialog-header {
    background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
    padding: 24px !important;
    border-bottom: 1px solid rgba(255, 183, 77, 0.2);
}

.confirmation-dialog .header-content {
    flex: 1;
}

.confirmation-dialog .text-h5 {
    color: #e65100;
    font-weight: 600;
}

.confirmation-dialog .dialog-content {
    padding: 24px !important;
    background: #fafafa;
}

.confirmation-content {
    text-align: center;
}

.confirmation-content .text-body-1 {
    color: #424242;
    font-size: 16px;
    line-height: 1.5;
    margin-bottom: 16px;
}

.confirmation-dialog .dialog-actions {
    background: white;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    padding: 20px 24px !important;
}

.confirmation-dialog .v-btn {
    border-radius: 8px;
    text-transform: none;
    font-weight: 500;
    padding: 0 20px;
    height: 40px;
}

.confirmation-dialog .v-btn--outlined {
    border-width: 2px;
}

.confirmation-dialog .v-btn--elevated {
    box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3) !important;
}

.confirmation-dialog .v-btn--elevated:hover {
    box-shadow: 0 6px 16px rgba(255, 152, 0, 0.4) !important;
    transform: translateY(-1px);
}

.confirmation-dialog .v-btn--outlined:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.2);
}

/* Alert styling in confirmation */
.confirmation-dialog .v-alert {
    border-radius: 12px;
    background: rgba(255, 193, 7, 0.1) !important;
    border: 1px solid rgba(255, 193, 7, 0.3);
}

.confirmation-dialog .v-alert .v-alert__content {
    color: #e65100;
    font-size: 14px;
}

/* Animation for dialog entrance */
.v-dialog-transition-enter-active .confirmation-dialog {
    animation: confirmationSlideIn 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

@keyframes confirmationSlideIn {
    from {
        opacity: 0;
        transform: translateY(-30px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

/* Mobile responsive for confirmation dialog */
@media (max-width: 480px) {
    .confirmation-dialog .dialog-header {
        padding: 20px !important;
    }
    
    .confirmation-dialog .dialog-content {
        padding: 20px !important;
    }
    
    .confirmation-dialog .dialog-actions {
        padding: 16px !important;
        flex-direction: column-reverse;
        gap: 12px;
    }
    
    .confirmation-dialog .v-btn {
        width: 100%;
        margin: 0 !important;
    }
    
    .confirmation-dialog .v-spacer {
        display: none;
    }
}

/* Invalid connection state */
:deep(.vue-flow__handle.invalid-connection) {
    background-color: #ef4444 !important;
    border-color: #dc2626 !important;
    cursor: not-allowed !important;
}

/* Valid connection state */
:deep(.vue-flow__handle.valid-connection) {
    background-color: #10b981 !important;
    border-color: #059669 !important;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.3) !important;
}

/* Connection preview line */
:deep(.vue-flow__connection-line) {
    stroke: #6366f1;
    stroke-width: 2;
    stroke-dasharray: 5, 5;
    animation: dash 1s linear infinite;
}

@keyframes dash {
    to {
        stroke-dashoffset: -10;
    }
}

/* Invalid connection preview */
:deep(.vue-flow__connection-line.invalid) {
    stroke: #ef4444;
    stroke-dasharray: 3, 3;
}

/* Success animation for valid connections */
@keyframes connectionSuccess {
    0% {
        stroke-width: 2;
    }

    50% {
        stroke-width: 4;
    }

    100% {
        stroke-width: 2;
    }
}

:deep(.vue-flow__edge.connection-success .vue-flow__edge-path) {
    animation: connectionSuccess 0.5s ease-in-out;
}

/* Selected Document Data Styles */
.selected-document-data {
    background: rgba(25, 118, 210, 0.04);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid rgba(25, 118, 210, 0.1);
}

.selected-document-data-chip {
    margin: 2px !important;
    font-weight: 500 !important;
    display: inline-flex !important;
    align-items: center !important;
    max-width: 250px !important;
}

.selected-document-data-chip .chip-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
}

.selected-document-data-chip :deep(.v-chip__content) {
    display: flex !important;
    align-items: center !important;
    max-width: 100% !important;
    overflow: hidden !important;
}

.selected-document-data-chip :deep(.v-chip__close) {
    flex-shrink: 0 !important;
    margin-left: 4px !important;
    order: 2 !important;
}

.selected-document-data-chip :deep(.v-chip__content > span) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
}

.custom-divider {
    border: none;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    margin: 0px 0px 20px 0px;
    width: 100%;
}

/* Enhanced Configuration Panel Styles */
.config-header {
    background: linear-gradient(135deg, #f8f9ff 0%, #e3f2fd 100%);
    border-radius: 16px;
    padding: 24px;
    border: 1px solid rgba(25, 118, 210, 0.1);
    margin-bottom: 24px;
}

.config-panels {
    border: none !important;
    gap: 16px;
}

.config-panels :deep(.v-expansion-panel::after) {
    display: none !important;
}

.panel-item {
    border-radius: 16px !important;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
    border: 1px solid rgba(0, 0, 0, 0.08) !important;
    transition: all 0.3s ease;
}


.panel-title-custom {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding: 0 24px !important;
}

.panel-icon-wrapper {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #1976D2, #1565C0);
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
}

.panel-title {
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.2;
}

.panel-subtitle {
    font-size: 13px;
    color: #666;
    margin-top: 2px;
    line-height: 1.3;
}

.panel-content-wrapper {
    background: #ffffff;
}

.panel-content {
    background: #ffffff;
}

.config-section {
    position: relative;
}

.section-label {
    font-size: 14px;
    font-weight: 600;
    color: #1976D2;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 16px;
    position: relative;
    padding-left: 12px;
}

.section-label::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 16px;
    background: linear-gradient(135deg, #1976D2, #1565C0);
    border-radius: 2px;
}

.config-input {
    margin-bottom: 0px;
}

.config-input :deep(.v-field) {
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
}

.config-input :deep(.v-field:hover) {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.config-input :deep(.v-field--focused) {
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

/* Enhanced Option Cards */
.option-cards-row {
    gap: 16px;
}

.option-card {
    border-radius: 16px !important;
    transition: all 0.3s ease;
    border: 2px solid rgba(0, 0, 0, 0.08) !important;
    background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
}

.option-card:hover {
    border-color: rgba(25, 118, 210, 0.3) !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12) !important;
    transform: translateY(-2px);
}

.option-card.option-active {
    border-color: rgba(25, 118, 210, 0.6) !important;
    background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
    box-shadow: 0 4px 16px rgba(25, 118, 210, 0.2) !important;
}

.option-title {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.2;
}

.option-description {
    font-size: 13px;
    color: #666;
    margin-top: 4px;
    line-height: 1.4;
}

/* Memory Toggle Card */
.memory-toggle-card {
    border-radius: 12px !important;
    transition: all 0.3s ease;
}

.memory-toggle-card.toggle-active {
    border-color: rgba(76, 175, 80, 0.5) !important;
    background: linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%);
}

.memory-label {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
}

.memory-description {
    font-size: 12px;
    color: #666;
    margin-top: 2px;
}

/* Switch Option Compact Styles */
.switch-option-compact {
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    background: #ffffff;
    transition: all 0.3s ease;
}

.switch-option-compact:hover {
    border-color: rgba(25, 118, 210, 0.3);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.switch-active-bg {
    background: linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.02) 100%) !important;
    border-color: rgba(25, 118, 210, 0.3) !important;
}

.flag-active-purple {
    background: linear-gradient(135deg, rgba(103, 58, 183, 0.07) 0%, rgba(103, 58, 183, 0.03) 100%) !important;
    border-color: rgba(103, 58, 183, 0.3) !important;
}

.flag-active-orange {
    background: linear-gradient(135deg, rgba(230, 74, 25, 0.07) 0%, rgba(230, 74, 25, 0.03) 100%) !important;
    border-color: rgba(230, 74, 25, 0.3) !important;
}

.flag-active-teal {
    background: linear-gradient(135deg, rgba(0, 150, 136, 0.07) 0%, rgba(0, 150, 136, 0.03) 100%) !important;
    border-color: rgba(0, 150, 136, 0.3) !important;
}

.flag-snippet-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f5f3ff;
    border: 1px solid #e9d8fd;
    border-radius: 8px;
    padding: 6px 12px;
    margin-top: 6px;
}

.flag-snippet-code {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 13px;
    color: #6d28d9;
    font-weight: 500;
}

.multi-agent-card {
    border-color: rgba(236, 72, 153, 0.25) !important;
}

/* Compact inline switch styles */
.switch-option-inline {
    display: inline-block;
}

.switch-active-bg-compact {
    background: linear-gradient(135deg, rgba(94, 53, 177, 0.08) 0%, rgba(94, 53, 177, 0.04) 100%) !important;
    border: 1px solid rgba(94, 53, 177, 0.2) !important;
    border-radius: 8px !important;
}

.switch-title-compact {
    font-size: 12px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.1;
    white-space: nowrap;
}

.switch-description-compact {
    font-size: 10px;
    color: #666;
    line-height: 1.2;
    white-space: nowrap;
}

/* Action buttons container responsive layout */
.action-buttons-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.base-prompt-toggle-section {
    width: 100%;
}

.base-prompt-toggle-section .v-card {
    border: 1px solid rgba(58, 183, 73, 0.2);
    transition: all 0.2s ease;
}

.base-prompt-toggle-section .v-card:hover {
    border-color: rgba(35, 140, 49, 0.4);
    box-shadow: 0 2px 8px rgba(58, 183, 77, 0.1);
}

.load-model-button-wrapper {
    display: flex;
    justify-content: flex-end;
}

/* Responsive adjustments for action buttons */
@media (max-width: 600px) {
    .load-model-button-wrapper {
        justify-content: stretch;
    }
    
    .load-model-btn {
        width: 100%;
    }
    
    .switch-title-compact {
        font-size: 11px;
    }
    
    .switch-description-compact {
        font-size: 9px;
    }
    
    .toggle-text-container {
        min-width: 120px;
    }
}

@media (max-width: 400px) {
    .base-prompt-toggle-wrapper .switch-option-inline > div {
        flex-direction: column;
        align-items: flex-start;
        padding: 8px 12px;
    }
    
    .toggle-text-container {
        margin: 4px 0;
        min-width: auto;
    }
    
    .switch-title-compact,
    .switch-description-compact {
        white-space: normal;
    }
}

.switch-title {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.2;
}

.switch-description {
    font-size: 12px;
    color: #666;
    margin-top: 2px;
    line-height: 1.3;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .config-header {
        padding: 16px;
        margin-bottom: 16px;
    }

    .panel-content {
        padding: 16px !important;
    }

    .panel-title-custom {
        padding: 0 16px !important;
    }

    .option-cards-row {
        gap: 12px;
    }

    /* Smart Intent responsive styles */
    .smart-intent-card .v-card-title {
        padding: 12px 16px !important;
        font-size: 1rem !important;
    }

    .smart-intent-card .v-card-text {
        padding: 16px !important;
    }

    .smart-intent-expansion .v-expansion-panel-title {
        padding: 12px 16px !important;
        min-height: 48px !important;
    }
}

/* Line Configuration Specific Styles */
.line-option-card {
    border-radius: 12px !important;
    transition: all 0.3s ease;
}

.line-option-card.option-active {
    border-color: rgba(76, 175, 80, 0.5) !important;
    background: linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%);
}

/* System Prompt Specific Styles */
.prompt-section {
    position: relative;
}

.prompt-card {
    border-radius: 16px !important;
    overflow: hidden;
    border: 2px solid rgba(0, 0, 0, 0.08) !important;
    transition: all 0.3s ease;
}

.prompt-card:hover {
    border-color: rgba(25, 118, 210, 0.3) !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12) !important;
}

.prompt-textarea :deep(.v-field) {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
}

.prompt-textarea :deep(.v-field__field) {
    padding: 0px !important;
}

.prompt-textarea :deep(.v-field__input) {
    font-family: 'Roboto Mono', monospace !important;
    font-size: 14px !important;
    line-height: 1.6 !important;
}

.tips-card {
    border-radius: 12px !important;
    border: 1px solid rgba(96, 125, 139, 0.2) !important;
}

.tips-card ul {
    margin: 0;
    padding-left: 16px;
}

.tips-card li {
    margin-bottom: 4px;
}

.tips-card li:last-child {
    margin-bottom: 0;
}

/* Prompt Template Styles */
.prompt-template-container {
    padding: 0.5rem 0;
}

.prompt-editor-card {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    transition: all 0.2s ease;
}

.prompt-editor-card:hover {
    border-color: rgba(25, 118, 210, 0.5);
    box-shadow: 0 1px 3px rgba(227, 6, 6, 0.05);
}

.prompt-textarea {
    font-family: 'Roboto Mono', monospace;
    line-height: 1.5;
    border: none !important;
    box-shadow: none !important;
    padding: 0px 5px !important;
    font-size: 14px;
    min-height: 300px !important;
}

.prompt-editor-card {
    border: 1px solid rgba(0, 0, 0, 0.12) !important;
    box-shadow: none !important;
    transition: all 0.2s ease;
    overflow: hidden;
}

.prompt-editor-card:hover {
    border-color: rgba(25, 118, 210, 0.5) !important;
}

.prompt-templates-card {
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    transition: all 0.3s ease;
}

.template-list-item {
    border-radius: 4px;
    margin-bottom: 2px;
    transition: all 0.2s ease;
}

.template-list-item:hover {
    background-color: rgba(25, 118, 210, 0.04);
}

/* Selection Info Styles */
.selection-info {
    position: absolute;
    bottom: 20px;
    left: 160px;
    z-index: 1000;
    display: flex;
    align-items: flex-start;
    pointer-events: none;
}

.selection-info .v-chip {
    pointer-events: auto;
    margin: 0;
}

.prompt-template-list {
    max-height: 500px;
    overflow-y: auto;
}

.template-list-all {
    max-height: 400px;
    overflow-y: auto;
}

/* Knowledge Sources Styles */
.knowledge-sources-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

/* SharePoint Configuration Subsection Styles */
.subsection {
    background: rgba(248, 250, 252, 0.6);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
    transition: all 0.2s ease;
}

.subsection-header {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding-bottom: 8px;
}

.subsection-title {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
    letter-spacing: 0.025em;
}

.subsection .modern-input:last-child {
    margin-bottom: 0 !important;
}

/* Color coding for different subsections */
.subsection:nth-child(2) {
    background: rgba(25, 118, 210, 0.02);
    border-color: rgba(25, 118, 210, 0.1);
}

.subsection:nth-child(3) {
    background: rgba(76, 175, 80, 0.02);
    border-color: rgba(76, 175, 80, 0.1);
}

.subsection:nth-child(4) {
    background: rgba(33, 150, 243, 0.02);
    border-color: rgba(33, 150, 243, 0.1);
}

.knowledge-source-card {
    background-color: white;
    transition: all 0.2s ease;
}

.knowledge-source-card .border {
    border: 1px solid #e0e0e0 !important;
    transition: border-color 0.2s ease, background-color 0.2s ease;
}

.knowledge-source-card .border:hover {
    border-color: #1976d2 !important;
}

.knowledge-source-frame {
    display: flex; 
    align-items: center; 
    justify-content: space-between;
    flex-direction: column;
    padding: 5px;
}
.knowledge-source-title {
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 5px;
}
.knowledge-source-config {
    width: 100%;
    padding: 5px;
}

/* View Mode Disabled State - prevents interaction with form elements */
.view-mode-disabled {
    pointer-events: none;
    opacity: 0.85;
}

.view-mode-disabled :deep(.v-field) {
    background-color: #f5f5f5 !important;
}

.view-mode-disabled :deep(.v-checkbox),
.view-mode-disabled :deep(.v-switch) {
    pointer-events: none;
}
</style>
